// ===========================================
// BUSTED! BLACKJACK - PIXI.JS GAME TABLE V1.0
// ===========================================
// Production-ready PixiJS game table rendering.
// Matches existing React-based GameTable UX.
// ===========================================

// CRITICAL: Register non-eval GL-program generator BEFORE any pixi.js code runs.
// This allows CSP to block 'unsafe-eval' without breaking the WebGL renderer.
// Must be imported before Application/Container/etc. (side-effect install).
import 'pixi.js/unsafe-eval';

import { useEffect, useRef, useCallback, useState, useImperativeHandle, forwardRef, useMemo } from 'react';
import { Application, Container, Sprite, Graphics, Text, TextStyle, Assets, Texture, Rectangle } from 'pixi.js';
import type { Card as CardType, DealerState, HandState, SidebetType, ScorecardHandResult, ScorecardSidebetResult, ScorecardInsuranceResult, ChipBreakdown } from '@busted-blackjack/shared';
import { PixiControlPanel, ControlPanelPosition, getStoredPosition } from './PixiControlPanel';
import { PixiInsurancePanel } from './PixiInsurancePanel';
import { PixiHowToBetModal } from './PixiHowToBetModal';
import { PixiAvatarCountdownRing } from './PixiAvatarCountdownRing';
import { LoadingVideo } from '../../LoadingVideo';
import { useGameStateStore } from '../../../stores/gameStateStore';
import { useSocketStore } from '../../../stores/socketStore';
import { recordAnimationFailure } from '../../../stores/adminLogStore';
import { getAnimationDuration, getStaggerValue } from '../../../stores/animationConfigStore';
import { diagLog } from '../../../utils/diagnosticLogger';

// Registry imports - shared positioning, rotation, and sprite utilities
import {
  // Constants
  KITTY_BASE_RADIAL_OFFSET,
  KITTY_PAIR_GAP as REGISTRY_KITTY_PAIR_GAP,
  CARD_OVERLAP as REGISTRY_CARD_OVERLAP,
  CARD_WIDTH as REGISTRY_CARD_WIDTH,
  CARD_HEIGHT as REGISTRY_CARD_HEIGHT,
  CHIP_SIZES as REGISTRY_CHIP_SIZES,
  BEAM_ROTATIONS,
  BEAM_HIDDEN_ROTATION,
  DEFAULT_SEAT_CONFIG as REGISTRY_DEFAULT_SEAT_CONFIG,
  DEFAULT_CHIP_CONFIG as REGISTRY_DEFAULT_CHIP_CONFIG,
  DEFAULT_INSURANCE_CHIP_CONFIG,
  SEAT_BASE_DIMENSIONS,
  KITTY_CHIP_SIZE,
  KITTY_VERTICAL_OFFSET,
  // Rotation utilities
  degreesToRadians,
  radiansToDegrees,
  applyRadialOffset,
  applyTangentOffset as registryApplyTangentOffset,
  localToWorld,
  getBeamRotationForSeat,
  getBeamHiddenRotation,
  normalizeAngle,
  shortestAngleDiff,
  // Position calculations
  calculateKittyPosition,
  calculateInsuranceKittyPosition,
  calculateKittyCenteringOffset as registryCalculateKittyCenteringOffset,
  applyKittyCenteringOffset,
  calculateCardCenteredOffset,
  calculatePlayerCardPosition,
  calculateDealerCardPosition,
  calculateSeatDimensions,
  calculateAdjustedSeatPosition,
  calculateScoreboxWidth,
  calculateOutcomeLabelPosition,
  calculateKittyChipYOffset as registryCalculateKittyChipYOffset,
  kittyLocalToWorld,
  calculateAvatarPosition,
  calculateAvatarPositionRaw,
  // Sprite utilities
  createKittyDisplay as registryCreateKittyDisplay,
  updateKittyLayout as registryUpdateKittyLayout,
  addChipToKitty as registryAddChipToKitty,
  repositionKittyChips as registryRepositionKittyChips,
  removeChipFromKitty,
  getScaledCardDimensions,
  getChipSize,
  getOutcomeDisplay,
  createOutcomeLabel as registryCreateOutcomeLabel,
  // Types
  type KittyDisplay,
  type KittyChipData as RegistryKittyChipData,
  type ChipCounterConfig as RegistryChipCounterConfig,
  type ChipBorderConfig as RegistryChipBorderConfig,
  type OutcomeLabelConfig as RegistryOutcomeLabelConfig,
} from './registry';

// Layout store for animation endpoints
import {
  getKittyPosition,
  getTrayChipPosition,
  getStagedBetPosition,
  getEndpointPosition,
} from '../../../stores/layoutStore';
import { BlackjackEndpointIds } from '@busted-blackjack/shared';

// Chip utilities
import { breakdownChips, capChipsForAnimation } from '../../../utils/chipAnimations';

// Pixi-native sound engine
import { playTableSound, getOutcomeSoundKey } from './audio/tableAudio';

// Asset imports
import tableImageUrl from '../../../assets/table2.png';
import cardBackUrl from '../../../assets/cards/card_back.svg';
import shoeImageUrl from '../../../assets/shoe.png';
import trayImageUrl from '../../../assets/tray2.png';

// Face card imports
import aceHeartsUrl from '../../../assets/cards/AH_face.svg';
import aceDiamondsUrl from '../../../assets/cards/AD_face.svg';
import aceClubsUrl from '../../../assets/cards/AC_face.svg';
import aceSpadesUrl from '../../../assets/cards/AS_face.svg';
import jackHeartsUrl from '../../../assets/cards/jack-hearts.svg';
import jackDiamondsUrl from '../../../assets/cards/jack-diamonds.svg';
import jackClubsUrl from '../../../assets/cards/jack-clubs.svg';
import jackSpadesUrl from '../../../assets/cards/jack-spades.svg';
import queenHeartsUrl from '../../../assets/cards/queen-hearts.svg';
import queenDiamondsUrl from '../../../assets/cards/queen-diamonds.svg';
import queenClubsUrl from '../../../assets/cards/queen-clubs.svg';
import queenSpadesUrl from '../../../assets/cards/queen-spades.svg';
import kingHeartsUrl from '../../../assets/cards/king-hearts.svg';
import kingDiamondsUrl from '../../../assets/cards/king-diamonds.svg';
import kingClubsUrl from '../../../assets/cards/king-clubs.svg';
import kingSpadesUrl from '../../../assets/cards/king-spades.svg';

// Chip imports
import chip1Url from '../../../assets/cosmetics/chip-sets/default/1.png';
import chip5Url from '../../../assets/cosmetics/chip-sets/default/5.png';
import chip25Url from '../../../assets/cosmetics/chip-sets/default/25.png';
import chip100Url from '../../../assets/cosmetics/chip-sets/default/100.png';
import chip500Url from '../../../assets/cosmetics/chip-sets/default/500.png';
import chip1000Url from '../../../assets/cosmetics/chip-sets/default/1000.png';
import chip5000Url from '../../../assets/cosmetics/chip-sets/default/5000.png';
import chip10000Url from '../../../assets/cosmetics/chip-sets/default/10000.png';

// Sidebet imports
import sidebet1Url from '../../../assets/cosmetics/sidebets/1.png';
import sidebet2Url from '../../../assets/cosmetics/sidebets/2.png';
import sidebet3Url from '../../../assets/cosmetics/sidebets/3.png';
import sidebet4Url from '../../../assets/cosmetics/sidebets/4.png';
import sidebet5Url from '../../../assets/cosmetics/sidebets/5.png';
import sidebet6Url from '../../../assets/cosmetics/sidebets/6.png';
import sidebet7Url from '../../../assets/cosmetics/sidebets/7.png';
import sidebet8Url from '../../../assets/cosmetics/sidebets/8.png';
import sidebet9Url from '../../../assets/cosmetics/sidebets/9.png';
import sidebet10Url from '../../../assets/cosmetics/sidebets/10.png';
import sidebet11Url from '../../../assets/cosmetics/sidebets/11.png';
import sidebet12Url from '../../../assets/cosmetics/sidebets/12.png';

// Insurance badge import
import insuranceBadgeUrl from '../../../assets/insurance.png';

// Beam mask (clips beam to table edge)
import beamMaskUrl from '../../../assets/beam-mask.png';

// ===========================================
// ASSET MAPS
// ===========================================

const CHIP_URLS: Record<number, string> = {
  1: chip1Url,
  5: chip5Url,
  25: chip25Url,
  100: chip100Url,
  500: chip500Url,
  1000: chip1000Url,
  5000: chip5000Url,
  10000: chip10000Url,
};

const SIDEBET_URLS: string[] = [
  sidebet1Url,
  sidebet2Url,
  sidebet3Url,
  sidebet4Url,
  sidebet5Url,
  sidebet6Url,
  sidebet7Url,
  sidebet8Url,
  sidebet9Url,
  sidebet10Url,
  sidebet11Url,
  sidebet12Url,
];

const FACE_CARD_URLS: Record<string, Record<string, string>> = {
  A: { hearts: aceHeartsUrl, diamonds: aceDiamondsUrl, clubs: aceClubsUrl, spades: aceSpadesUrl },
  J: { hearts: jackHeartsUrl, diamonds: jackDiamondsUrl, clubs: jackClubsUrl, spades: jackSpadesUrl },
  Q: { hearts: queenHeartsUrl, diamonds: queenDiamondsUrl, clubs: queenClubsUrl, spades: queenSpadesUrl },
  K: { hearts: kingHeartsUrl, diamonds: kingDiamondsUrl, clubs: kingClubsUrl, spades: kingSpadesUrl },
};

// ===========================================
// CONFIGURATION
// ===========================================

// Table image dimensions (the base for all positioning)
const TABLE_IMAGE_WIDTH = 1244;
const TABLE_IMAGE_HEIGHT = 794;

// UI elements positioned relative to table
const UI_BELOW_TABLE_MARGIN = 0; // Space below table for chip rail and staged bet
const UI_HORIZONTAL_MARGIN = 28; // Small margin on sides for content that extends past table

// Canvas dimensions calculated from content bounds
const CANVAS_WIDTH = TABLE_IMAGE_WIDTH + (UI_HORIZONTAL_MARGIN * 2);
const CANVAS_HEIGHT = TABLE_IMAGE_HEIGHT + UI_BELOW_TABLE_MARGIN;

// ===========================================
// STAGE Z-ORDER (single source of truth)
// ===========================================
// Explicit z-index for app.stage top-level layers.
// With sortableChildren=true, these values determine draw order
// regardless of addChild() order (fixing the panel re-add bug).
const STAGE_Z = {
  gameContent: 0,
  uiLayer: 10,
  controlPanel: 100,
  scorecardOverlay: 200,  // above control panel (for the relocated scorecard)
  insurancePanel: 300,
  howToBet: 400,
  sidebetBackdrop: 500,
  sidebetModal: 501,
} as const;

// Scale dimensions match canvas (entire canvas fits in viewport)
const SCALE_WIDTH = CANVAS_WIDTH;
const SCALE_HEIGHT = CANVAS_HEIGHT;

const CARD_WIDTH = 74;
const CARD_HEIGHT = 105;
const CARD_OVERLAP = 18;
const CHIP_SIZE = 36;

// ===========================================
// FILMSTRIP ARCHITECTURE
// ===========================================
// Each seat has a "filmstrip" container that holds cards for all 4 possible hands.
// Cards are positioned at fixed LOCAL offsets within the filmstrip (cell 0, 1, 2, 3).
// Navigation slides the filmstrip to center the viewed hand in the seat viewport.
// This unifies split/non-split handling - there's always a 4-cell filmstrip,
// just usually only cell 0 has cards.
const FILMSTRIP_CELL_WIDTH = 288; // Doubled from 144 for more spacing between split hands

// ===========================================
// DYNAMIC IRIS MASK FOR SPLIT HANDS
// ===========================================
// Only enabled when hands > 1 (split occurred)
// Expands/contracts to hug the active hand's cards

// ===========================================
// IRIS MASK SYSTEM
// ===========================================
// Dynamic clipping mask for split hand carousel.
// Uses asymmetric left/right edges that animate independently
// during carousel transitions to create an iris effect.

// Mask constants
const MASK_CORNER_RADIUS = 8;  // Rounded corners for mask
const MASK_DEBUG_ENABLED = false; // Set to true to show red debug borders for masks

// Mask state type - tracks all four edges for elastic clipping
interface MaskState {
  leftEdge: number;       // Left edge position (negative = left of center)
  rightEdge: number;      // Right edge position (positive = right of center)
  topEdge: number;        // Top edge position (negative = above center)
  bottomEdge: number;     // Bottom edge position (positive = below center)
  seatLeftEdge: number;   // Seat's left edge (minimum left bound)
  seatRightEdge: number;  // Seat's right edge (minimum right bound)
  seatTopEdge: number;    // Seat's top edge (minimum top bound)
  seatBottomEdge: number; // Seat's bottom edge (minimum bottom bound)
  isEnabled: boolean;     // Whether mask is actively clipping
  isAnimating: boolean;   // Whether mask is currently animating
  animationId?: number;   // For canceling animations
}

/**
 * Get the actual bounds of a hand's cards in viewport local coordinates.
 * Accounts for card rotations and actual sprite positions.
 *
 * @param seatPosition - The seat position
 * @param handIndex - The hand index (0, 1, 2, 3)
 * @param filmstripX - Current filmstrip X offset (to transform to viewport coords)
 * @returns { left, right, top, bottom } bounds relative to viewport center, or null if no cards found
 */
function getHandBounds(
  seatPosition: number,
  handIndex: number,
  filmstripX: number = 0
): { left: number; right: number; top: number; bottom: number } | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let foundCards = false;
  const cardDetails: string[] = [];

  // Find all cards for this hand from MODULE_CARD_STATES
  MODULE_CARD_STATES.forEach((cardState, key) => {
    // Key format: "seatPosition-handIndex-cardIndex"
    const [seatStr, handStr] = key.split('-');
    if (parseInt(seatStr) === seatPosition && parseInt(handStr) === handIndex) {
      const sprite = cardState.sprite;
      if (sprite && !sprite.destroyed) {
        foundCards = true;

        // Get sprite's local position in filmstrip coordinates
        const localX = sprite.x;
        const localY = sprite.y;

        // Calculate card's edges accounting for rotation
        // Card is anchored at center (0.5, 0.5)
        const cardWidth = sprite.width;
        const cardHeight = sprite.height;
        const rotation = sprite.rotation;

        // Calculate rotated bounding box corners
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const hw = cardWidth / 2;
        const hh = cardHeight / 2;

        // Four corners relative to sprite center
        const corners = [
          { x: -hw * cos - (-hh) * sin, y: -hw * sin + (-hh) * cos },
          { x: hw * cos - (-hh) * sin, y: hw * sin + (-hh) * cos },
          { x: hw * cos - hh * sin, y: hw * sin + hh * cos },
          { x: -hw * cos - hh * sin, y: -hw * sin + hh * cos },
        ];

        // Find min/max of corners, add sprite position and filmstrip offset
        for (const corner of corners) {
          const worldX = localX + corner.x + filmstripX;
          const worldY = localY + corner.y; // Y doesn't need filmstrip offset
          minX = Math.min(minX, worldX);
          maxX = Math.max(maxX, worldX);
          minY = Math.min(minY, worldY);
          maxY = Math.max(maxY, worldY);
        }
        cardDetails.push(`${key}: pos=(${localX.toFixed(0)},${localY.toFixed(0)})`);
      }
    }
  });

  console.log(`[BOUNDS] getHandBounds(seat=${seatPosition}, hand=${handIndex}): ` +
    `x=[${minX.toFixed(0)}, ${maxX.toFixed(0)}], y=[${minY.toFixed(0)}, ${maxY.toFixed(0)}]`);

  if (!foundCards) {
    return null;
  }

  return { left: minX, right: maxX, top: minY, bottom: maxY };
}

/**
 * Redraw a seat mask with all four edges (elastic bounds).
 * Mask hugs the cards on all sides.
 */
function redrawMaskElastic(
  mask: Graphics,
  leftEdge: number,
  rightEdge: number,
  topEdge: number,
  bottomEdge: number
): void {
  if (!mask || mask.destroyed) return;
  try {
    const width = rightEdge - leftEdge;
    const height = bottomEdge - topEdge;
    mask.clear();
    mask.roundRect(leftEdge, topEdge, width, height, MASK_CORNER_RADIUS);
    mask.fill({ color: 0xffffff });
  } catch (e) {
    console.warn('[MASK] Failed to redraw elastic mask:', e);
  }
}

/**
 * Update debug border with all four edges.
 */
function updateMaskDebugBorderElastic(
  debugBorder: Graphics | undefined,
  leftEdge: number,
  rightEdge: number,
  topEdge: number,
  bottomEdge: number
): void {
  if (!MASK_DEBUG_ENABLED || !debugBorder || debugBorder.destroyed) return;
  try {
    const width = rightEdge - leftEdge;
    const height = bottomEdge - topEdge;
    debugBorder.clear();
    debugBorder.roundRect(leftEdge, topEdge, width, height, MASK_CORNER_RADIUS);
    debugBorder.stroke({ color: 0xff0000, width: 3 });
  } catch (e) {
    console.warn('[MASK] Failed to update elastic debug border:', e);
  }
}

/**
 * Enable the elastic mask for a seat viewport. Called when split occurs (hands > 1).
 * Initial mask is set to seat bounds (all four edges at seat border).
 */
function enableSeatMask(
  scene: {
    seatCardViewports: Map<number, Container>;
    seatCardMasks: Map<number, Graphics>;
    seatMaskState: Map<number, MaskState>;
    seatMaskDebugBorders: Map<number, Graphics>
  },
  seatPosition: number,
  seatWidth: number,
  seatHeight: number
): void {
  const viewport = scene.seatCardViewports.get(seatPosition);
  const mask = scene.seatCardMasks.get(seatPosition);

  if (!viewport || viewport.destroyed || !mask || mask.destroyed) {
    console.warn(`[MASK] Cannot enable mask for seat ${seatPosition}: viewport or mask missing`);
    return;
  }

  // Initial edges at seat boundaries (all four sides)
  const leftEdge = -seatWidth / 2;
  const rightEdge = seatWidth / 2;
  const topEdge = -seatHeight / 2;
  const bottomEdge = seatHeight / 2;

  // Redraw mask at seat bounds
  redrawMaskElastic(mask, leftEdge, rightEdge, topEdge, bottomEdge);
  mask.visible = true; // Required in PixiJS v8
  viewport.mask = mask;

  // Create/update debug border
  if (MASK_DEBUG_ENABLED) {
    let debugBorder = scene.seatMaskDebugBorders.get(seatPosition);
    const parentContainer = viewport.parent;
    if (!debugBorder || debugBorder.destroyed) {
      debugBorder = new Graphics();
      debugBorder.label = `maskDebugBorder-${seatPosition}`;
      debugBorder.zIndex = 9999;
      if (parentContainer) {
        parentContainer.addChild(debugBorder);
      }
      scene.seatMaskDebugBorders.set(seatPosition, debugBorder);
    }
    debugBorder.x = viewport.x;
    debugBorder.y = viewport.y;
    debugBorder.rotation = viewport.rotation;
    debugBorder.visible = true;
    updateMaskDebugBorderElastic(debugBorder, leftEdge, rightEdge, topEdge, bottomEdge);
  }

  scene.seatMaskState.set(seatPosition, {
    leftEdge,
    rightEdge,
    topEdge,
    bottomEdge,
    seatLeftEdge: leftEdge,
    seatRightEdge: rightEdge,
    seatTopEdge: topEdge,
    seatBottomEdge: bottomEdge,
    isEnabled: true,
    isAnimating: false,
  });

  console.log(`[MASK] Enabled for seat ${seatPosition}, bounds=[${leftEdge.toFixed(0)}, ${rightEdge.toFixed(0)}, ${topEdge.toFixed(0)}, ${bottomEdge.toFixed(0)}]`);
}

/**
 * Disable the iris mask for a seat viewport. Called when round ends.
 */
function disableSeatMask(
  scene: {
    seatCardViewports: Map<number, Container>;
    seatCardMasks: Map<number, Graphics>;
    seatMaskState: Map<number, MaskState>;
    seatMaskDebugBorders: Map<number, Graphics>
  },
  seatPosition: number
): void {
  const viewport = scene.seatCardViewports.get(seatPosition);
  if (!viewport || viewport.destroyed) return;

  const mask = scene.seatCardMasks.get(seatPosition);
  if (mask && !mask.destroyed) {
    mask.visible = false;
  }
  viewport.mask = null;

  // Hide debug border
  const debugBorder = scene.seatMaskDebugBorders.get(seatPosition);
  if (debugBorder && !debugBorder.destroyed) {
    debugBorder.visible = false;
  }

  const state = scene.seatMaskState.get(seatPosition);
  if (state) {
    state.isEnabled = false;
    state.isAnimating = false;
  }

  console.log(`[MASK] Disabled for seat ${seatPosition}`);
}

/**
 * Elastic mask adjustment - syncs all four mask edges to match actual hand bounds.
 * Can both EXPAND (when cards spread out) and CONTRACT (when cards tighten up).
 * Used after card deals, nudges, and straightening to keep the mask hugging the cards.
 */
function adjustMaskToHandBounds(
  scene: {
    seatCardMasks: Map<number, Graphics>;
    seatMaskState: Map<number, MaskState>;
    seatMaskDebugBorders: Map<number, Graphics>;
  },
  seatPosition: number,
  handIndex: number
): void {
  const mask = scene.seatCardMasks.get(seatPosition);
  const state = scene.seatMaskState.get(seatPosition);

  if (!mask || mask.destroyed || !state || !state.isEnabled) {
    return;
  }

  // Get actual hand bounds in viewport coordinates
  // filmstripX = -handIndex * FILMSTRIP_CELL_WIDTH centers this hand in the viewport
  const filmstripX = -handIndex * FILMSTRIP_CELL_WIDTH;
  const bounds = getHandBounds(seatPosition, handIndex, filmstripX);
  if (!bounds) {
    return;
  }

  // Clamp to seat edges (mask can't be smaller than seat border)
  const newLeftEdge = Math.min(bounds.left, state.seatLeftEdge);
  const newRightEdge = Math.max(bounds.right, state.seatRightEdge);
  const newTopEdge = Math.min(bounds.top, state.seatTopEdge);
  const newBottomEdge = Math.max(bounds.bottom, state.seatBottomEdge);

  // Only redraw if any edge changed
  const changed = newLeftEdge !== state.leftEdge || newRightEdge !== state.rightEdge ||
                  newTopEdge !== state.topEdge || newBottomEdge !== state.bottomEdge;

  if (changed) {
    console.log(`[MASK] Elastic sync: x=[${state.leftEdge.toFixed(0)},${state.rightEdge.toFixed(0)}]→[${newLeftEdge.toFixed(0)},${newRightEdge.toFixed(0)}] y=[${state.topEdge.toFixed(0)},${state.bottomEdge.toFixed(0)}]→[${newTopEdge.toFixed(0)},${newBottomEdge.toFixed(0)}]`);

    state.leftEdge = newLeftEdge;
    state.rightEdge = newRightEdge;
    state.topEdge = newTopEdge;
    state.bottomEdge = newBottomEdge;

    redrawMaskElastic(mask, newLeftEdge, newRightEdge, newTopEdge, newBottomEdge);

    const debugBorder = scene.seatMaskDebugBorders.get(seatPosition);
    if (MASK_DEBUG_ENABLED && debugBorder && !debugBorder.destroyed) {
      updateMaskDebugBorderElastic(debugBorder, newLeftEdge, newRightEdge, newTopEdge, newBottomEdge);
    }
  }
}

// Deal animation start position (matches top shoe card)
const DEAL_START_OFFSET = { x: 18, y: 8 }; // Offset from SHOE_POSITION
const DEAL_START_ROTATION = 95; // Degrees (matches top shoe card rotation)
const SHOE_CARD_SCALE = 53 / 74; // Shoe card size (53x75) vs dealt card size (74x105)
const SHOE_CARD_ROTATION_VARIANCE = 4; // Random rotation ±4 degrees for shoe stack cards
const SHOE_CARD_Y_VARIANCE = 4; // Random Y offset 0 to +4 pixels for shoe stack cards
const OPPONENT_CARD_SCALE = 110 / 117; // Opponent cards at 110% of BASE scale

/** Apply opponent card scaling to all relevant CardConfig properties */
function applyOpponentCardScale(config: CardConfig): CardConfig {
  return {
    ...config,
    scale: config.scale * OPPONENT_CARD_SCALE,
    cornerFontSize: config.cornerFontSize * OPPONENT_CARD_SCALE,
    cornerSuitSize: config.cornerSuitSize * OPPONENT_CARD_SCALE,
    centerSuitSize: config.centerSuitSize * OPPONENT_CARD_SCALE,
    cornerGap: config.cornerGap * OPPONENT_CARD_SCALE,
  };
}

/** Generate random rotation for shoe stack cards (±SHOE_CARD_ROTATION_VARIANCE degrees) */
function generateShoeCardRotation(): number {
  // Random value between -VARIANCE and +VARIANCE
  return (Math.random() * 2 - 1) * SHOE_CARD_ROTATION_VARIANCE;
}

/** Generate random Y offset for shoe stack cards (0 to +SHOE_CARD_Y_VARIANCE pixels) */
function generateShoeCardRandomY(): number {
  return Math.random() * SHOE_CARD_Y_VARIANCE;
}

// ===========================================
// MODULE-LEVEL STATE (survives HMR and remounts)
// ===========================================
// Map: "seat-handIndex-cardIndex" -> CardSpriteState (e.g., "3-0-0", "dealer-0-1")
const MODULE_CARD_STATES = new Map<string, CardSpriteState>();
// Track which seats/dealer have been straightened
const MODULE_STRAIGHTENED = new Map<string, boolean>();
// Registry for outcome labels - stored in animation layer, persist across re-renders
// Key: "seat-handIndex", Value: { wrapper, outcome, state }
interface OutcomeLabelState {
  wrapper: Container;
  outcome: string;
  state: 'animating-in' | 'static' | 'animating-out';
}
const MODULE_OUTCOME_LABELS = new Map<string, OutcomeLabelState>();
// Track locked scorebox widths - width locks when hand resolves, unlocks when 2nd card of new round is dealt
// Key: "seatPosition-handIndex", Value: locked width in pixels
const MODULE_SCOREBOX_LOCKS = new Map<string, number>();
// Track how many cards have started dealing animation per seat/hand
// Key: "seatPosition-handIndex", Value: number of cards that have started animating
// Used to prevent scorebox from showing cards before their deal animation starts
const MODULE_DEALT_CARD_COUNTS = new Map<string, number>();
// Persisted rail chip rotations by denomination - survives re-renders and HMR
const MODULE_RAIL_CHIP_ROTATIONS = new Map<number, number>();
// Cache last displayed scorebox text per seat - persists until rake animation clears it
// Key: "seatPosition", Value: last displayed score text (e.g., "18", "08/18")
const MODULE_SCOREBOX_CACHE = new Map<string, string>();
// Track which seats have rake in progress - prevents scorebox recalculation during rake
// When a seat is in this set, createSeatContainer respects the cached "--" instead of recalculating
const MODULE_RAKE_SEATS_IN_PROGRESS = new Set<number>();
// Track which seats have scorebox wipe animation in progress
// When a seat is in this set, createSeatContainer respects the cached value instead of recalculating
const MODULE_SCOREBOX_WIPE_IN_PROGRESS = new Set<number>();
// Track which seats have been raked this round - prevents duplicate rake processing
// Cleared when first card is dealt to a seat (indicates new round for that seat)
const MODULE_RAKED_SEATS_THIS_ROUND = new Set<number>();
// Track Text objects created/destroyed for leak diagnosis
let MODULE_TEXT_CREATED_COUNT = 0;
let MODULE_TEXT_DESTROYED_COUNT = 0;

// ===========================================
// SCOREBOX HEIGHT MONITORING (DEBUG)
// ===========================================
// Track scorebox containers and their heights to detect shrinking
interface ScoreboxMonitorState {
  container: Container;
  lastHeight: number;
  lastScaleY: number;
  lastBoundsHeight: number;
}
const MODULE_SCOREBOX_MONITORS = new Map<string, ScoreboxMonitorState>();

/**
 * Register a scorebox container for height monitoring.
 * Call this after creating/positioning a scorebox.
 */
function monitorScoreboxHeight(key: string, container: Container, context: string): void {
  const bounds = container.getLocalBounds();
  const state: ScoreboxMonitorState = {
    container,
    lastHeight: container.height,
    lastScaleY: container.scale.y,
    lastBoundsHeight: bounds.height,
  };
  MODULE_SCOREBOX_MONITORS.set(key, state);
  console.log(`[SCOREBOX MONITOR] REGISTERED ${key} - height=${container.height.toFixed(2)}, scaleY=${container.scale.y.toFixed(3)}, bounds.h=${bounds.height.toFixed(2)} [${context}]`);
}

/**
 * Check all monitored scoreboxes for height changes.
 * Call this periodically (e.g., in animation loop or effect).
 */
function checkScoreboxHeights(): void {
  MODULE_SCOREBOX_MONITORS.forEach((state, key) => {
    if (state.container.destroyed) {
      MODULE_SCOREBOX_MONITORS.delete(key);
      return;
    }

    const currentHeight = state.container.height;
    const currentScaleY = state.container.scale.y;
    const bounds = state.container.getLocalBounds();
    const currentBoundsHeight = bounds.height;

    // Check for any changes
    const heightChanged = Math.abs(currentHeight - state.lastHeight) > 0.01;
    const scaleChanged = Math.abs(currentScaleY - state.lastScaleY) > 0.001;
    const boundsChanged = Math.abs(currentBoundsHeight - state.lastBoundsHeight) > 0.01;

    if (heightChanged || scaleChanged || boundsChanged) {
      console.log(`[SCOREBOX MONITOR] CHANGE DETECTED ${key}:`);
      console.log(`  height: ${state.lastHeight.toFixed(2)} → ${currentHeight.toFixed(2)} (Δ${(currentHeight - state.lastHeight).toFixed(2)})`);
      console.log(`  scaleY: ${state.lastScaleY.toFixed(3)} → ${currentScaleY.toFixed(3)} (Δ${(currentScaleY - state.lastScaleY).toFixed(3)})`);
      console.log(`  bounds.h: ${state.lastBoundsHeight.toFixed(2)} → ${currentBoundsHeight.toFixed(2)} (Δ${(currentBoundsHeight - state.lastBoundsHeight).toFixed(2)})`);
      console.log(`  Call stack:`, new Error().stack);

      // Update tracked values
      state.lastHeight = currentHeight;
      state.lastScaleY = currentScaleY;
      state.lastBoundsHeight = currentBoundsHeight;
    }
  });
}

// Expose scorebox monitoring to window for manual debugging
// Usage: window.checkScoreboxHeights() - check all monitored scoreboxes for changes
// Usage: window.listScoreboxMonitors() - list all currently monitored scoreboxes with their dimensions
(window as unknown as {
  checkScoreboxHeights?: () => void;
  listScoreboxMonitors?: () => void;
}).checkScoreboxHeights = checkScoreboxHeights;
(window as unknown as {
  checkScoreboxHeights?: () => void;
  listScoreboxMonitors?: () => void;
}).listScoreboxMonitors = () => {
  console.log(`[SCOREBOX MONITOR] Currently tracking ${MODULE_SCOREBOX_MONITORS.size} scoreboxes:`);
  MODULE_SCOREBOX_MONITORS.forEach((state, key) => {
    if (state.container.destroyed) {
      console.log(`  ${key}: DESTROYED`);
    } else {
      const bounds = state.container.getLocalBounds();
      console.log(`  ${key}: height=${state.container.height.toFixed(2)}, scaleY=${state.container.scale.y.toFixed(3)}, bounds.h=${bounds.height.toFixed(2)}`);
    }
  });
};

// Base font size for score width measurements (cached at preload)
const SCOREBOX_BASE_FONT_SIZE = 24;
// Cache for score text widths at base font size - populated at preload
// Key: padded score string ("00", "01", ..., "30"), Value: width at base font size
const MODULE_SCORE_WIDTH_CACHE = new Map<string, number>();

/**
 * Get cached score text width, scaled to target font size.
 * Falls back to estimated width if cache miss (shouldn't happen after preload).
 */
function getScoreTextWidth(score: number, targetFontSize: number): number {
  const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();
  const scoreStr = padScore(score);
  const cachedWidth = MODULE_SCORE_WIDTH_CACHE.get(scoreStr);
  if (cachedWidth !== undefined) {
    return cachedWidth * (targetFontSize / SCOREBOX_BASE_FONT_SIZE);
  }
  // Fallback: estimate 2-digit width (shouldn't happen after preload)
  console.warn(`[getScoreTextWidth] Cache miss for score "${scoreStr}", using estimate`);
  return targetFontSize * 1.2; // Rough estimate for 2 chars
}

// ===========================================
// DIAGNOSTIC: Scene State Logging
// ===========================================
// Comprehensive diagnostic logging for debugging race conditions
// between split hand round end and new round start.

interface SceneDiagnostic {
  timestamp: string;
  trigger: string;
  shoeCardSprites: { index: number; exists: boolean; destroyed: boolean; x: number; y: number }[];
  moduleCardStatesCount: number;
  moduleCardStatesKeys: string[];
  rakeInProgress: boolean;
  splitHandStates: { seat: number; totalHands: number; viewedHandIndex: number }[];
  maskStates: { seat: number; isEnabled: boolean; isAnimating: boolean }[];
  animationLayerChildren: number;
  cardsLayerChildren: number;
  tableLayerChildren: number;
}

function logSceneDiagnostic(
  scene: {
    shoeCardSprites: Sprite[];
    rakeInProgress: boolean;
    splitHandState: Map<number, { totalHands: number; viewedHandIndex: number }>;
    seatMaskState: Map<number, MaskState>;
    animationLayer?: Container | null;
    cardsLayer?: Container | null;
    tableLayer?: Container | null;
  } | null,
  trigger: string
): void {
  if (!scene) {
    console.log(`[SCENE DIAG] ${trigger}: scene is NULL`);
    return;
  }

  const diag: SceneDiagnostic = {
    timestamp: new Date().toISOString(),
    trigger,
    shoeCardSprites: scene.shoeCardSprites.map((s, i) => ({
      index: i,
      exists: !!s,
      destroyed: s?.destroyed ?? true,
      x: s?.x ?? NaN,
      y: s?.y ?? NaN,
    })),
    moduleCardStatesCount: MODULE_CARD_STATES.size,
    moduleCardStatesKeys: Array.from(MODULE_CARD_STATES.keys()).slice(0, 20), // First 20 keys
    rakeInProgress: scene.rakeInProgress,
    splitHandStates: Array.from(scene.splitHandState.entries()).map(([seat, state]) => ({
      seat,
      totalHands: state.totalHands,
      viewedHandIndex: state.viewedHandIndex,
    })),
    maskStates: Array.from(scene.seatMaskState.entries()).map(([seat, state]) => ({
      seat,
      isEnabled: state.isEnabled,
      isAnimating: state.isAnimating,
    })),
    animationLayerChildren: scene.animationLayer?.children.length ?? -1,
    cardsLayerChildren: scene.cardsLayer?.children.length ?? -1,
    tableLayerChildren: scene.tableLayer?.children.length ?? -1,
  };

  console.log(`[SCENE DIAG] ${trigger}:`, JSON.stringify(diag, null, 2));
}

// ===========================================
// CRITICAL: MODULE-LEVEL ANIMATION STATE
// ===========================================
// NOTE: MODULE_CLEAR_TABLE_PROMISE, MODULE_HOLE_CARD_REVEAL_PROMISE, and
// MODULE_DEALER_STRAIGHTEN_PROMISE have been migrated to AnimationOrchestrator.
// The orchestrator uses animateSingleton() for deduplication and automatic cleanup.
// See: docs/BUG_REGISTRY.md - BUG-003: Stale MODULE_* State Between Rounds

// ===========================================
// IN-FLIGHT REGISTRY (Animation-safe reconciliation)
// ===========================================
// Tracks elements currently being animated. Reconciliation skips these
// to prevent duplicates, position jumps, and visual glitches on ALT+TAB.
// Keys follow same format as MODULE_CARD_STATES: "seatPos-handIdx-cardIdx" or "dealer-0-cardIdx"
const MODULE_ELEMENTS_IN_FLIGHT = new Set<string>();

// Tracks chip sprites currently being animated. Reconciliation skips these
// to prevent mid-flight chip animations from being destroyed.
const MODULE_CHIP_SPRITES_IN_FLIGHT = new Set<Sprite>();

// ===========================================
// SERVER SCORE HISTORY (Event-driven scorebox)
// ===========================================
// Stores server-provided scores indexed by card count for animation-synced display
// Key: "seatPosition-handIndex", Value: Map of cardCount → { score, isSoft }
// This allows us to show the correct score as each card animation completes
interface ScoreSnapshot {
  score: number;
  isSoft: boolean;
}
const MODULE_SCORE_HISTORY = new Map<string, Map<number, ScoreSnapshot>>();

// Store the latest score for each seat-hand (for resolved/final display)
const MODULE_LATEST_SCORE = new Map<string, ScoreSnapshot>();

// ===========================================
// DEALER BUTTON STATE (Module-level for smooth animation)
// ===========================================
// Current visual seat of dealer button (0 = hidden, 1-7 = visible)
let MODULE_DEALER_BUTTON_CURRENT_SEAT = 0;
// Pending visual seat - set when prop changes, animated to during clearTable
let MODULE_DEALER_BUTTON_PENDING_SEAT: number | null = null;

/**
 * Record a score snapshot from SCORE_UPDATED event
 * Called when the server sends a score update after a card is dealt
 */
export function recordScoreSnapshot(
  seatPosition: number,
  handIndex: number,
  cardCount: number,
  score: number,
  isSoft: boolean
): void {
  const key = `${seatPosition}-${handIndex}`;

  // Get or create the history map for this seat-hand
  let history = MODULE_SCORE_HISTORY.get(key);
  if (!history) {
    history = new Map();
    MODULE_SCORE_HISTORY.set(key, history);
  }

  // Store the score for this card count
  history.set(cardCount, { score, isSoft });

  // Also store as latest
  MODULE_LATEST_SCORE.set(key, { score, isSoft });

  console.log(`[SCORE HISTORY] seat=${seatPosition} hand=${handIndex} cardCount=${cardCount} score=${score} isSoft=${isSoft}`);
}

/**
 * Get the score for a specific card count (for animation-synced display)
 */
export function getScoreForCardCount(
  seatPosition: number,
  handIndex: number,
  cardCount: number
): ScoreSnapshot | undefined {
  const key = `${seatPosition}-${handIndex}`;
  const history = MODULE_SCORE_HISTORY.get(key);
  return history?.get(cardCount);
}

/**
 * Get the latest/final score for a seat-hand
 */
export function getLatestScore(
  seatPosition: number,
  handIndex: number
): ScoreSnapshot | undefined {
  const key = `${seatPosition}-${handIndex}`;
  return MODULE_LATEST_SCORE.get(key);
}

/**
 * Clear score history for a seat-hand (called on new round)
 */
export function clearScoreHistory(seatPosition: number, handIndex: number): void {
  const key = `${seatPosition}-${handIndex}`;
  MODULE_SCORE_HISTORY.delete(key);
  MODULE_LATEST_SCORE.delete(key);
}

/**
 * Clear all per-round state (called on DEALING_STARTED)
 * Prevents memory leaks from accumulating data across many rounds
 */
export function clearAllScoreHistory(): void {
  MODULE_SCORE_HISTORY.clear();
  MODULE_LATEST_SCORE.clear();
  MODULE_DEALT_CARD_COUNTS.clear();
  MODULE_SCOREBOX_LOCKS.clear();
  MODULE_SCOREBOX_CACHE.clear();
  // Clear straightened state for new round
  MODULE_STRAIGHTENED.clear();
  // Clear hole card revealed flag for new round (prevents duplicate reveals)
  MODULE_HOLE_CARD_REVEALED = false;
  // Transition orchestrator to DEALING phase - this also clears stale animations
  // The orchestrator manages singleton promises (clear-table, hole-card-reveal, dealer-straighten)
  getAnimationOrchestrator().setPhase('DEALING');
  console.log('[PixiGameTableV1] Cleared per-round state for new dealing phase');
}

/**
 * Force reset clearTable animation state. Use this on component mount
 * to handle cases where HMR or previous errors left the animation stuck.
 * NOTE: Now handled by AnimationOrchestrator - this is a no-op for backwards compatibility.
 */
export function resetClearTablePromise(): void {
  // Orchestrator handles animation deduplication and cleanup automatically
  // This function is kept for backwards compatibility but does nothing now
  console.log('[PixiGameTableV1] resetClearTablePromise called (orchestrator handles this)');
}

/**
 * Clear all module-level state synchronously. Call on reconnection
 * to prevent stale sprites/labels from persisting. This mimics what
 * F5 refresh does by resetting all module Maps.
 */
export function clearModuleState(): void {
  console.log('[PixiGameTableV1] Clearing all module state (reconnection reset)');

  // Reset the animation orchestrator first
  resetAnimationOrchestrator();

  // Destroy any existing sprites before clearing Maps
  MODULE_CARD_STATES.forEach((state) => {
    if (state.sprite && !state.sprite.destroyed) state.sprite.destroy();
  });
  MODULE_OUTCOME_LABELS.forEach((state) => {
    if (state.wrapper && !state.wrapper.destroyed) state.wrapper.destroy();
  });
  // Destroy in-flight chip sprites (prevents orphaned chips during resync)
  const inFlightCount = MODULE_CHIP_SPRITES_IN_FLIGHT.size;
  MODULE_CHIP_SPRITES_IN_FLIGHT.forEach((sprite) => {
    if (!sprite.destroyed) {
      sprite.parent?.removeChild(sprite);
      sprite.destroy();
    }
  });
  if (inFlightCount > 0) {
    console.log(`[clearModuleState] Destroyed ${inFlightCount} in-flight chip sprites`);
  }

  // Clear all Maps
  MODULE_CARD_STATES.clear();
  MODULE_STRAIGHTENED.clear();
  MODULE_OUTCOME_LABELS.clear();
  MODULE_SCOREBOX_LOCKS.clear();
  MODULE_DEALT_CARD_COUNTS.clear();
  MODULE_SCOREBOX_CACHE.clear();
  MODULE_SCORE_HISTORY.clear();
  MODULE_LATEST_SCORE.clear();
  MODULE_ELEMENTS_IN_FLIGHT.clear();
  MODULE_CHIP_SPRITES_IN_FLIGHT.clear();
  MODULE_RAKE_SEATS_IN_PROGRESS.clear();
  MODULE_RAKED_SEATS_THIS_ROUND.clear();
  MODULE_SCOREBOX_MONITORS.clear();
  // Reset text tracking counters
  MODULE_TEXT_CREATED_COUNT = 0;
  MODULE_TEXT_DESTROYED_COUNT = 0;
  // NOTE: MODULE_*_PROMISE variables are now managed by AnimationOrchestrator
  // resetAnimationOrchestrator() above handles clearing those
  MODULE_HOLE_CARD_REVEALED = false;
}

// ===========================================
// WEBGL MEMORY DIAGNOSTICS
// ===========================================
// Helper to log texture count at key moments for debugging memory leaks
// Reference is set when app is created, used by logTextureCount
let _diagnosticAppRef: { current: Application | null } = { current: null };

/**
 * Log current WebGL texture count and memory stats
 * Call at key moments: rake start/end, deal start, etc.
 */
export function logTextureCount(context: string): void {
  const app = _diagnosticAppRef.current;
  if (!app || !app.renderer || (app.renderer as unknown as { destroyed?: boolean }).destroyed) {
    console.log(`[TEXTURE DIAG] ${context}: App not available`);
    return;
  }

  // Get texture count from PixiJS
  const texGC = (app.renderer as unknown as { textureGC?: { count?: number } }).textureGC;
  const textureCount = texGC?.count ?? 'N/A';

  // Get JS heap memory (Chrome only)
  const perfMemory = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number } }).memory;
  const heapInfo = perfMemory
    ? ` | JS Heap: ${(perfMemory.usedJSHeapSize / 1024 / 1024).toFixed(1)}MB`
    : '';

  // Get sprite counts
  const cardCount = MODULE_CARD_STATES.size;
  const labelCount = MODULE_OUTCOME_LABELS.size;
  const textBalance = MODULE_TEXT_CREATED_COUNT - MODULE_TEXT_DESTROYED_COUNT;

  console.log(`[TEXTURE DIAG] ${context}: textures=${textureCount} | cards=${cardCount} | labels=${labelCount} | textBalance=${textBalance} (created=${MODULE_TEXT_CREATED_COUNT}, destroyed=${MODULE_TEXT_DESTROYED_COUNT})${heapInfo}`);
}

// ===========================================
// ANIMATION ORCHESTRATOR
// ===========================================
// Single source of truth for animation state.
// Replaces scattered MODULE_* promises and refs.
// See: docs/ANIMATION_ARCHITECTURE_REFACTOR.md
//
// Principles:
// 1. SIMPLE - Clear phases, explicit state
// 2. RESILIENT - Graceful handling of edge cases
// 3. OBSERVABLE - Easy to debug via logs
// ===========================================

/** Animation phases - explicit state machine */
export type AnimationPhase =
  | 'IDLE'           // No round active, waiting
  | 'BETTING'        // Accepting bets, may have rake animations
  | 'DEALING'        // Cards being dealt
  | 'PLAYER_TURNS'   // Players taking actions
  | 'DEALER_TURN'    // Dealer playing
  | 'RESOLVING'      // Payouts and outcomes
  | 'ROUND_END';     // Cleanup before next round

/** What's currently animating */
interface ActiveAnimation {
  id: string;           // Unique identifier
  type: string;         // e.g., 'CARD_DEAL', 'CHIP_MOVE', 'RAKE'
  startTime: number;    // For timeout detection
  promise: Promise<void>;
}

/**
 * AnimationOrchestrator - Single point of control for all animations
 *
 * Usage:
 *   const orchestrator = getAnimationOrchestrator();
 *   await orchestrator.animate('bet-chips-seat-1', 'CHIP_MOVE', chipAnimation());
 *   orchestrator.setPhase('DEALING');
 */
class AnimationOrchestrator {
  private phase: AnimationPhase = 'IDLE';
  private activeAnimations: Map<string, ActiveAnimation> = new Map();
  private roundNumber: number = 0;

  /** Get current phase */
  getPhase(): AnimationPhase {
    return this.phase;
  }

  /** Get current round number */
  getRound(): number {
    return this.roundNumber;
  }

  /** Transition to a new phase */
  setPhase(newPhase: AnimationPhase): void {
    const oldPhase = this.phase;
    this.phase = newPhase;
    console.log(`[Orchestrator] Phase: ${oldPhase} → ${newPhase} (round ${this.roundNumber})`);

    // Clear stale animations on phase change (resilience)
    if (newPhase === 'BETTING' || newPhase === 'IDLE') {
      this.clearStaleAnimations();
    }
  }

  /** Start a new round - resets all state */
  startRound(roundNumber: number): void {
    this.roundNumber = roundNumber;
    this.activeAnimations.clear();
    this.setPhase('BETTING');
    console.log(`[Orchestrator] Round ${roundNumber} started`);
  }

  /** End the current round */
  endRound(): void {
    this.setPhase('ROUND_END');
    this.activeAnimations.clear();
    console.log(`[Orchestrator] Round ${this.roundNumber} ended`);
  }

  /** Reset everything - call on reconnect or error recovery */
  reset(): void {
    console.log(`[Orchestrator] RESET - clearing all state`);
    this.phase = 'IDLE';
    this.activeAnimations.clear();
    this.roundNumber = 0;
  }

  /**
   * Run an animation with tracking.
   * Returns when animation completes.
   * Handles errors gracefully (logs and continues).
   */
  async animate(id: string, type: string, animation: Promise<void>): Promise<void> {
    // If same animation already running, wait for it (idempotent)
    const existing = this.activeAnimations.get(id);
    if (existing) {
      console.log(`[Orchestrator] Animation ${id} already running, waiting...`);
      return existing.promise;
    }

    // Track the animation
    const startTime = Date.now();
    const tracked: ActiveAnimation = {
      id,
      type,
      startTime,
      promise: animation,
    };
    this.activeAnimations.set(id, tracked);

    console.log(`[Orchestrator] START: ${type} (${id}) - ${this.activeAnimations.size} active`);

    // Diagnostic: Log animation start (extract seat/hand from id if present)
    const seatMatch = id.match(/seat-(\d+)/);
    const handMatch = id.match(/hand-(\d+)/);
    diagLog.animStart({
      id,
      type,
      seat: seatMatch ? parseInt(seatMatch[1]) : undefined,
      hand: handMatch ? parseInt(handMatch[1]) : undefined,
    });

    try {
      await animation;
    } catch (error) {
      // Log but don't throw - resilience
      console.error(`[Orchestrator] Animation failed: ${id}`, error);
    } finally {
      const durationMs = Date.now() - startTime;
      this.activeAnimations.delete(id);
      console.log(`[Orchestrator] END: ${type} (${id}) - ${this.activeAnimations.size} active`);

      // Diagnostic: Log animation end
      diagLog.animEnd({
        id,
        type,
        durationMs,
        seat: seatMatch ? parseInt(seatMatch[1]) : undefined,
        hand: handMatch ? parseInt(handMatch[1]) : undefined,
      });
    }
  }

  /** Check if any animations are running */
  isAnimating(): boolean {
    return this.activeAnimations.size > 0;
  }

  /** Check if a specific animation is running */
  isRunning(id: string): boolean {
    return this.activeAnimations.has(id);
  }

  /** Wait for all current animations to complete */
  async waitForAll(): Promise<void> {
    if (this.activeAnimations.size === 0) return;

    console.log(`[Orchestrator] Waiting for ${this.activeAnimations.size} animations...`);
    const promises = Array.from(this.activeAnimations.values()).map(a => a.promise);
    await Promise.all(promises);
    console.log(`[Orchestrator] All animations complete`);
  }

  /** Wait for animations of a specific type */
  async waitForType(type: string): Promise<void> {
    const matching = Array.from(this.activeAnimations.values())
      .filter(a => a.type === type)
      .map(a => a.promise);

    if (matching.length === 0) return;

    console.log(`[Orchestrator] Waiting for ${matching.length} ${type} animations...`);
    await Promise.all(matching);
  }

  /**
   * Wait for animations of a specific type with timeout.
   * Returns true if animations completed, false if timeout triggered.
   * This replaces Promise.race patterns for resilience.
   */
  async waitForTypeWithTimeout(type: string, timeoutMs: number = 2000): Promise<boolean> {
    const matching = Array.from(this.activeAnimations.values())
      .filter(a => a.type === type);

    if (matching.length === 0) return true;

    console.log(`[Orchestrator] Waiting for ${matching.length} ${type} animations (timeout: ${timeoutMs}ms)...`);
    const startTime = performance.now();

    const result = await Promise.race([
      Promise.all(matching.map(a => a.promise)).then(() => true),
      new Promise<false>(resolve => setTimeout(() => {
        console.warn(`[Orchestrator] ${type} wait TIMEOUT after ${timeoutMs}ms`);
        // Diagnostic: Log each timed-out animation
        for (const anim of matching) {
          if (this.activeAnimations.has(anim.id)) {
            diagLog.animTimeout({ id: anim.id, type: anim.type, waitedMs: timeoutMs });
            this.activeAnimations.delete(anim.id);
          }
        }
        resolve(false);
      }, timeoutMs))
    ]);

    const duration = performance.now() - startTime;
    if (result && duration > 10) {
      console.log(`[Orchestrator] ${type} animations complete (waited ${duration.toFixed(0)}ms)`);
    }
    return result;
  }

  /** Get count of active animations of a specific type */
  getActiveCountByType(type: string): number {
    return Array.from(this.activeAnimations.values())
      .filter(a => a.type === type).length;
  }

  /** Wait for animations whose ID starts with prefix (for per-seat serialization) */
  async waitForIdPrefix(prefix: string): Promise<void> {
    const matching = Array.from(this.activeAnimations.values())
      .filter(a => a.id.startsWith(prefix))
      .map(a => a.promise);

    if (matching.length === 0) return;

    console.log(`[Orchestrator] Waiting for ${matching.length} animations with prefix "${prefix}"...`);
    await Promise.all(matching);
  }

  /** Wait for animations whose ID starts with prefix, with timeout */
  async waitForIdPrefixWithTimeout(prefix: string, timeoutMs: number = 2000): Promise<boolean> {
    const matching = Array.from(this.activeAnimations.values())
      .filter(a => a.id.startsWith(prefix));

    if (matching.length === 0) return true;

    console.log(`[Orchestrator] Waiting for ${matching.length} "${prefix}" animations (timeout: ${timeoutMs}ms)...`);

    const result = await Promise.race([
      Promise.all(matching.map(a => a.promise)).then(() => true),
      new Promise<false>(resolve => setTimeout(() => {
        console.warn(`[Orchestrator] ${prefix} wait TIMEOUT after ${timeoutMs}ms`);
        for (const anim of matching) {
          if (this.activeAnimations.has(anim.id)) {
            this.activeAnimations.delete(anim.id);
          }
        }
        resolve(false);
      }, timeoutMs))
    ]);

    return result;
  }

  /** Get promise for a specific animation ID (or undefined if not running) */
  getAnimation(id: string): Promise<void> | undefined {
    return this.activeAnimations.get(id)?.promise;
  }

  /** Clear animations running longer than timeout (resilience) */
  private clearStaleAnimations(timeoutMs: number = 10000): void {
    const now = Date.now();
    let cleared = 0;

    for (const [id, anim] of this.activeAnimations) {
      if (now - anim.startTime > timeoutMs) {
        console.warn(`[Orchestrator] Clearing stale animation: ${id} (${anim.type})`);
        this.activeAnimations.delete(id);
        cleared++;
      }
    }

    if (cleared > 0) {
      console.warn(`[Orchestrator] Cleared ${cleared} stale animations`);
    }
  }

  /** Get debug info */
  getDebugInfo(): { phase: AnimationPhase; round: number; activeCount: number; active: string[] } {
    return {
      phase: this.phase,
      round: this.roundNumber,
      activeCount: this.activeAnimations.size,
      active: Array.from(this.activeAnimations.keys()),
    };
  }

  /**
   * Run a singleton animation with synchronous registration.
   * CRITICAL: Registers the animation BEFORE the async function body executes.
   * This ensures mutual exclusion works correctly for operations that wait on each other.
   *
   * Use this for singleton operations like:
   * - revealHoleCard (waits for straightenDealerCards)
   * - straightenDealerCards (waits for revealHoleCard)
   * - clearTable
   *
   * @param id - Unique animation ID
   * @param type - Animation type for logging/filtering
   * @param fn - Async function to execute (NOT a pre-created Promise)
   * @returns Promise that resolves when animation completes
   */
  animateSingleton(id: string, type: string, fn: () => Promise<void>): Promise<void> {
    // If same animation already running, return existing promise (idempotent)
    const existing = this.activeAnimations.get(id);
    if (existing) {
      console.log(`[Orchestrator] ${id} already running, reusing existing promise`);
      return existing.promise;
    }

    // Create deferred promise for tracking
    let resolvePromise: () => void;
    let rejectPromise: (e: unknown) => void;
    const trackedPromise = new Promise<void>((res, rej) => {
      resolvePromise = res;
      rejectPromise = rej;
    });

    // SYNCHRONOUS: Register immediately before fn() executes
    const tracked: ActiveAnimation = {
      id,
      type,
      startTime: Date.now(),
      promise: trackedPromise,
    };
    this.activeAnimations.set(id, tracked);
    console.log(`[Orchestrator] SINGLETON START: ${type} (${id}) - ${this.activeAnimations.size} active`);

    // ASYNC: Now execute the function (after registration)
    fn()
      .then(() => {
        this.activeAnimations.delete(id);
        console.log(`[Orchestrator] SINGLETON END: ${type} (${id}) - ${this.activeAnimations.size} active`);
        resolvePromise!();
      })
      .catch((error) => {
        this.activeAnimations.delete(id);
        console.error(`[Orchestrator] SINGLETON FAILED: ${id}`, error);
        rejectPromise!(error);
      });

    return trackedPromise;
  }
}

// Singleton instance
let MODULE_ORCHESTRATOR: AnimationOrchestrator | null = null;

/** Get the animation orchestrator (creates if needed) */
export function getAnimationOrchestrator(): AnimationOrchestrator {
  if (!MODULE_ORCHESTRATOR) {
    MODULE_ORCHESTRATOR = new AnimationOrchestrator();
  }
  return MODULE_ORCHESTRATOR;
}

/** Reset the orchestrator - call on reconnect */
export function resetAnimationOrchestrator(): void {
  if (MODULE_ORCHESTRATOR) {
    MODULE_ORCHESTRATOR.reset();
  }
}

// ===========================================
// HOLE CARD REVEAL TRACKING (LEGACY - TO BE MIGRATED)
// ===========================================
// TODO: Migrate to AnimationOrchestrator
// Separate flag for tracking if hole card has been revealed this round
// This is distinct from MODULE_STRAIGHTENED which tracks card positioning
let MODULE_HOLE_CARD_REVEALED = false;

// ===========================================
// DIAGNOSTIC LOGGING (Stress Test Support)
// ===========================================
let MODULE_ROUND_COUNTER = 0;
const DIAGNOSTIC_INTERVAL = 5; // Log every N rounds (set to 5 for stress testing)

// ===========================================
// AVATAR LOAD RETRY TRACKING
// ===========================================
// Track failed avatar loads for per-round retry (max 8 attempts per avatar)
// Key: avatar URL, Value: retry attempts count
const MODULE_FAILED_AVATARS = new Map<string, number>();
const AVATAR_RETRY_CAP = 8;

/**
 * Get diagnostic info about module-level state sizes.
 * Call this to check for memory leaks during stress testing.
 */
export function getModuleDiagnostics(): {
  roundCount: number;
  mapSizes: Record<string, number>;
  heapUsedMB: number | null;
} {
  const mapSizes: Record<string, number> = {
    CARD_STATES: MODULE_CARD_STATES.size,
    STRAIGHTENED: MODULE_STRAIGHTENED.size,
    OUTCOME_LABELS: MODULE_OUTCOME_LABELS.size,
    SCOREBOX_LOCKS: MODULE_SCOREBOX_LOCKS.size,
    DEALT_CARD_COUNTS: MODULE_DEALT_CARD_COUNTS.size,
    RAIL_CHIP_ROTATIONS: MODULE_RAIL_CHIP_ROTATIONS.size,
    SCOREBOX_CACHE: MODULE_SCOREBOX_CACHE.size,
    SCORE_HISTORY: MODULE_SCORE_HISTORY.size,
    LATEST_SCORE: MODULE_LATEST_SCORE.size,
  };

  // Get JS heap size if available (Chrome only)
  let heapUsedMB: number | null = null;
  if ((performance as unknown as { memory?: { usedJSHeapSize: number } }).memory) {
    heapUsedMB = Math.round((performance as unknown as { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize / 1024 / 1024);
  }

  return {
    roundCount: MODULE_ROUND_COUNTER,
    mapSizes,
    heapUsedMB,
  };
}

/**
 * Increment round counter and log diagnostics every N rounds.
 * Call this at the start of each dealing phase.
 */
export function incrementRoundAndLogDiagnostics(): void {
  MODULE_ROUND_COUNTER++;

  if (MODULE_ROUND_COUNTER % DIAGNOSTIC_INTERVAL === 0) {
    const diag = getModuleDiagnostics();
    console.log(`[STRESS-TEST] ========== ROUND ${diag.roundCount} DIAGNOSTICS ==========`);
    console.log(`[STRESS-TEST] Heap: ${diag.heapUsedMB ?? 'N/A'} MB`);
    console.log('[STRESS-TEST] Module Map Sizes:', diag.mapSizes);

    // Flag any maps that seem too large (potential leaks)
    const warnings: string[] = [];
    if (diag.mapSizes.CARD_STATES > 50) warnings.push(`CARD_STATES=${diag.mapSizes.CARD_STATES} (expected <20)`);
    if (diag.mapSizes.STRAIGHTENED > 20) warnings.push(`STRAIGHTENED=${diag.mapSizes.STRAIGHTENED} (expected <10)`);
    if (diag.mapSizes.OUTCOME_LABELS > 20) warnings.push(`OUTCOME_LABELS=${diag.mapSizes.OUTCOME_LABELS} (expected <10)`);
    if (diag.mapSizes.SCORE_HISTORY > 50) warnings.push(`SCORE_HISTORY=${diag.mapSizes.SCORE_HISTORY} (expected <20)`);
    if (diag.mapSizes.OUTCOME_ANIMATION_PROMISES > 10) warnings.push(`OUTCOME_ANIMATION_PROMISES=${diag.mapSizes.OUTCOME_ANIMATION_PROMISES} (expected <5)`);

    if (warnings.length > 0) {
      console.warn('[STRESS-TEST] ⚠️ POTENTIAL LEAKS:', warnings);
    } else {
      console.log('[STRESS-TEST] ✅ All map sizes normal');
    }
    console.log('[STRESS-TEST] ================================================');
  }
}

// ===========================================
// OUTCOME LABEL ANIMATION
// ===========================================
// NOTE: MODULE_OUTCOME_ANIMATION_PROMISES and related functions were removed.
// They were dead code - defined but never called.
// Outcome label animations are now tracked via the AnimationOrchestrator if needed.

// CardSpriteState interface (forward declaration for module-level map)
interface CardSpriteState {
  sprite: Container;
  randomness: { offsetX: number; offsetY: number; rotation: number };
  baseRotation: number;
  baseX: number;
  baseY: number;
  isDealer: boolean;
  card: CardType; // Store card data for re-rendering when cardConfig changes
}

// Suit symbols and colors
const SUIT_SYMBOLS: Record<string, string> = {
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
  spades: '\u2660',
};

const SUIT_COLORS: Record<string, number> = {
  hearts: 0xef4444,
  diamonds: 0xef4444,
  clubs: 0x111827,
  spades: 0x111827,
};

// ===========================================
// SEAT POSITIONING (POLAR COORDINATES)
// ===========================================

// Table center positioned relative to table image (centered horizontally, ~22% from top of table)
const TABLE_CENTER = { x: CANVAS_WIDTH / 2, y: TABLE_IMAGE_HEIGHT * 0.22 };

// Polar radius scaling factor - controls how far seats are from center
// Based on table height for consistent proportions
const SEAT_RADIUS_SCALE = TABLE_IMAGE_HEIGHT * 0.70;

const SEAT_POLAR_POSITIONS = [
  { visualPos: 1, angle: 170, radius: 0.41, rotation: 90 },
  { visualPos: 2, angle: 144, radius: 0.47, rotation: 60 },
  { visualPos: 3, angle: 120, radius: 0.48, rotation: 30 },
  { visualPos: 4, angle: 90, radius: 0.46, rotation: 0 },
  { visualPos: 5, angle: 60, radius: 0.48, rotation: -30 },
  { visualPos: 6, angle: 36, radius: 0.47, rotation: -60 },
  { visualPos: 7, angle: 10, radius: 0.41, rotation: -90 },
];

function polarToCartesian(angle: number, radius: number): { x: number; y: number } {
  const radians = degreesToRadians(angle);
  return {
    x: TABLE_CENTER.x + radius * SEAT_RADIUS_SCALE * Math.cos(radians),
    y: TABLE_CENTER.y + radius * SEAT_RADIUS_SCALE * Math.sin(radians),
  };
}

const SEAT_POSITIONS = SEAT_POLAR_POSITIONS.map((seat) => {
  const { x, y } = polarToCartesian(seat.angle, seat.radius);
  return { visualPos: seat.visualPos, x, y, rotation: seat.rotation };
});

// Positions relative to canvas center (which aligns with table center horizontally)
const DEALER_POSITION = { x: CANVAS_WIDTH / 2, y: 130 };
const SHOE_POSITION = { x: CANVAS_WIDTH / 2 - 280, y: 80 };
const KITTY_POSITION = { x: CANVAS_WIDTH / 2 + 190, y: 100 }; // Tray position

// Sidebet icon size (used in animations)
const SIDEBET_ICON_SIZE = 40;

// Sidebet border wipe duration (ms) - matches outcomeLabelFly for synchronized exit
const SIDEBET_BORDER_WIPE_DURATION = 300;

/**
 * Wipe out a sidebet outcome border with a vertical downward wipe effect.
 * Uses a mask that moves downward to hide the border from top to bottom,
 * then redraws the border with gold color.
 */
function fadeOutSidebetBorder(
  borderRing: Graphics,
  iconSize: number,
  onComplete?: () => void
): void {
  const startTime = performance.now();
  const duration = SIDEBET_BORDER_WIPE_DURATION;

  // Border ring radius
  const radius = iconSize / 2 + 3;
  const borderWidth = 8; // Outcome border width (green/red is width 8)

  // Create a mask for the wipe effect
  const wipeMask = new Graphics();
  const maskSize = (radius + borderWidth) * 2 + 4;
  const maskTop = -radius - borderWidth - 2;

  // Initial mask: covers entire border
  wipeMask.rect(-maskSize / 2, maskTop, maskSize, maskSize);
  wipeMask.fill({ color: 0xffffff });

  // Get parent container to add mask
  const parent = borderRing.parent;
  if (parent) {
    parent.addChild(wipeMask);
    wipeMask.x = borderRing.x;
    wipeMask.y = borderRing.y;
    borderRing.mask = wipeMask;
  }

  const animate = () => {
    if (borderRing.destroyed) {
      wipeMask.destroy();
      onComplete?.();
      return;
    }

    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Ease in cubic for accelerating wipe (matches outcome label easeInCubic)
    const easedProgress = progress * progress * progress;

    // Move mask top edge down while keeping bottom edge fixed (wipe down)
    const newTop = maskTop + (maskSize * easedProgress);
    const currentHeight = maskSize * (1 - easedProgress);
    wipeMask.clear();
    wipeMask.rect(-maskSize / 2, newTop, maskSize, currentHeight);
    wipeMask.fill({ color: 0xffffff });

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      // Wipe complete - remove mask and redraw with gold
      borderRing.mask = null;
      wipeMask.destroy();

      borderRing.clear();
      borderRing.circle(0, 0, radius);
      borderRing.stroke({ color: 0xd4a54a, width: 3 }); // Gold (default)
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

// ===========================================
// SIDEBET POSITION CONFIGURATION
// ===========================================
// Sidebet icons are positioned in an arch ABOVE the main bet (kitty)
// Positions are relative to the seat, with Y offset measured from kitty position
// (more negative = further toward dealer)

const SIDEBET_CONFIG = {
  // Base Y offset from seat center (same direction as kitty, but further toward dealer)
  // Kitty is at -134, sidebets are above that
  baseRadiusOffset: -217,  // Further toward dealer than kitty (-134)

  // Horizontal positions for the 3 sidebet slots (relative to seat center)
  // Forms an arch: sides are lower, center is higher
  slotPositions: [
    { localX: -45, localY: -5 },   // Slot 0: Left
    { localX: 0, localY: -33 },    // Slot 1: Center (higher for arch)
    { localX: 45, localY: -5 },    // Slot 2: Right
  ],

  // Icon appearance
  iconSize: SIDEBET_ICON_SIZE,
  iconSpacing: 35,  // Horizontal spacing between icons

  // Dynamic positioning based on active count:
  // 1 sidebet: centered (slot 1 position)
  // 2 sidebets: left and right (slot 0 and 2 positions)
  // 3 sidebets: all three positions
  getPositionsForCount: (count: number): number[] => {
    switch (count) {
      case 1: return [1];           // Center only
      case 2: return [0, 2];        // Left and right
      case 3: return [0, 1, 2];     // All three
      default: return [];
    }
  },
};

// Sidebet type to texture index mapping
const SIDEBET_TYPE_TO_INDEX: Record<string, number> = {
  'perfect-pairs': 1,
  'twenty-one-plus-three': 2,
  'top-three': 3,
  'lucky-ladies': 4,
  'royal-match': 5,
  'lucky-lucky': 6,
  'blazing-sevens': 7,
  'busted-bet': 8,
  'over-under-13': 9,
  'over-under-13-over': 9,     // Same icon for over variant
  'over-under-13-under': 9,    // Same icon for under variant
  'over-under-13-exactly': 9,  // Same icon for exactly variant
  'mega-hand': 10,
  'straight': 11,
  'flush': 12,
};

// ===========================================
// TURN INDICATOR BEAM CONFIGURATION
// ===========================================
// Beam emanates from dealer position, points to active player's seat
// Rotation angles are in degrees (0 = up, 90 = right, 180 = down)
const BEAM_CONFIG = {
  // Per-seat rotation angles (visual positions 1-7)
  rotations: {
    1: 259,      // Far left
    2: 234.5,    // Left
    3: 210.5,    // Inner left
    4: 180,      // Center (directly down)
    5: 149.5,    // Inner right
    6: 125.5,    // Right
    7: 101,      // Far right
  } as Record<number, number>,
  width: 18,           // Arc width in degrees
  color: 0x22C55E,     // Green (#22C55E)
  opacity: 0.3,        // 30% opacity
  radius: 600,         // How far the beam extends from origin
  // Origin is at dealer position (will be set dynamically based on canvas)
  // Spring animation parameters
  spring: {
    stiffness: 0.08,   // Lower = slower/smoother
    damping: 0.75,     // Higher = less oscillation
  },
  fadeSpeed: 0.1,      // Alpha change per frame for show/hide
};

// Chip denominations for display (up to 10k)
const CHIP_DENOMS = [1, 5, 25, 100, 500, 1000, 5000, 10000];
const CHIPS_PER_STACK = 8;

// ===========================================
// TYPES
// ===========================================

interface PlayerState {
  seatPosition: number;
  displayName: string;
  avatarUrl?: string;
  hands: HandState[];
  currentHandIndex: number;
  insuranceBet?: number;
  confirmedBet?: number;
}

/** Per-seat position configuration */
export interface SeatConfig {
  x: number;             // X offset
  y: number;             // Y offset
  rot: number;           // Rotation offset in degrees
  scale: number;         // Scale factor (50-150, default 100)
}

/** Per-seat chip stack configuration */
export interface ChipStackConfig {
  radiusOffset: number;  // Offset along radius direction
  x: number;             // X offset
  y: number;             // Y offset
  scale: number;         // Scale factor (50-150, default 100)
}

/** Default seat config */
const DEFAULT_SEAT_CONFIG: SeatConfig = { x: 0, y: 0, rot: 0, scale: 100 };
const DEFAULT_CHIP_CONFIG: ChipStackConfig = { radiusOffset: 0, x: 0, y: 0, scale: 100 };

/** Scorebox configuration */
export interface ScoreboxConfig {
  scale: number;    // Box scale factor (50-200, default 100) - does NOT affect font size
  fontSize: number; // Font size in pixels (8-24, default 12)
  x: number;        // X offset from default position
  y: number;        // Y offset from default position
  minWidth?: number; // Minimum width in pixels (prevents shrinking below this, like React's 35px)
}

/** Outcome label configuration */
export interface OutcomeLabelConfig {
  scale: number;      // Scale factor (50-200, default 100)
  fontSize: number;   // Font size in pixels (8-24, default 12)
  x: number;          // X offset from seat left edge (default 0)
  fontFamily: string; // Font family (default 'Arial')
}

const DEFAULT_SCOREBOX_CONFIG: ScoreboxConfig = { scale: 100, fontSize: 12, x: 0, y: 0 };
const DEFAULT_OUTCOME_CONFIG: OutcomeLabelConfig = { scale: 100, fontSize: 12, x: 0, fontFamily: 'Arial' };

// Base font size for pre-rendered outcome label textures
// Runtime scaling: spriteScale = (config.fontSize / OUTCOME_BASE_FONT_SIZE)
const OUTCOME_BASE_FONT_SIZE = 24;

/** Chip stack border configuration */
export interface ChipBorderConfig {
  opacity: number;   // 0-100, opacity percentage
  thickness: number; // 0-10, border width in pixels
  color: string;     // Hex color string like "#ffffff"
}

const DEFAULT_CHIP_BORDER_CONFIG: ChipBorderConfig = { opacity: 0, thickness: 2, color: '#ffffff' };

/** Chip counter configuration */
export interface ChipCounterConfig {
  show: boolean;           // Whether to show the counter
  pillOpacity: number;     // 0-100, background pill opacity
  fontColor: string;       // Hex color string like "#FB923C"
  fontSize: number;        // Font size in pixels
  x?: number;              // X offset from default position
  y?: number;              // Y offset from default position
}

const DEFAULT_CHIP_COUNTER_CONFIG: ChipCounterConfig = { show: false, pillOpacity: 80, fontColor: '#FB923C', fontSize: 12, x: 0, y: 0 };

/** Kitty stack position configuration */
export interface KittyConfig {
  x: number;  // X offset from default position
  y: number;  // Y offset from default position
  rotation: number;  // Rotation in degrees (chip stack only, not counter)
}

const DEFAULT_KITTY_CONFIG: KittyConfig = { x: 0, y: 0, rotation: 0 };

/** Per-card offset configuration for shoe cards */
export interface ShoeCardOffsets {
  0: { x: number; y: number; rotation: number; scale: number };
  1: { x: number; y: number; rotation: number; scale: number };
  2: { x: number; y: number; rotation: number; scale: number };
}

const DEFAULT_SHOE_CARD_OFFSETS: ShoeCardOffsets = {
  0: { x: 0, y: 0, rotation: 0, scale: 100 },
  1: { x: 0, y: 0, rotation: 0, scale: 100 },
  2: { x: 0, y: 0, rotation: 0, scale: 100 },
};

/** Dealer button configuration */
export interface DealerButtonConfig {
  x: number;         // X offset from default position
  y: number;         // Y offset from default position
  size: number;      // Size percentage (100 = default 24px)
  bgColor: string;   // Background color hex
  fontColor: string; // Font color hex
  borderColor: string; // Border color hex
  borderWidth: number; // Border width in pixels
}

/** Chip rail position configuration */
export interface ChipRailConfig {
  x: number;       // X offset from center
  y: number;       // Y offset from default position
  scale: number;   // Scale percentage (100 = default)
  spacing: number; // Horizontal spacing between chips
}

const DEFAULT_CHIP_RAIL_CONFIG: ChipRailConfig = { x: 0, y: 0, scale: 100, spacing: 85 };

/** Staged bet display configuration */
export interface StagedBetConfig {
  x: number;            // X offset from center
  y: number;            // Y offset from default position
  scale: number;        // Scale percentage (100 = default)
  width: number;        // Box width in pixels
  height: number;       // Box height in pixels
  fontSize: number;     // Amount font size
  borderThickness: number; // Border width
  clearFontSize: number;   // CLEAR button font size
  buildYourBetFontSize?: number; // "Build your bet." placeholder font size
}

const DEFAULT_STAGED_BET_CONFIG: StagedBetConfig = {
  x: 0, y: 0, scale: 100,
  width: 200, height: 64, fontSize: 18, borderThickness: 6, clearFontSize: 10, buildYourBetFontSize: 22
};

/** Card rendering configuration */
export interface CardConfig {
  scale: number;           // Overall card scale percentage (100 = default)
  cornerFontSize: number;  // Corner rank font size in pixels
  cornerSuitSize: number;  // Corner suit symbol size in pixels
  centerSuitSize: number;  // Center suit symbol size in pixels
  borderThickness: number; // Card border width in pixels
  seatCardsY: number;      // Cards Y offset within seats (local coords)
  dealerCardsX: number;    // Cards X offset for dealer (local coords)
  dealerCardsY: number;    // Cards Y offset for dealer (local coords)
  cornerGap: number;       // Vertical gap between rank and suit in corners
}

const DEFAULT_CARD_CONFIG: CardConfig = {
  scale: 117,
  cornerFontSize: 20,
  cornerSuitSize: 24,
  centerSuitSize: 70,
  borderThickness: 1,
  seatCardsY: -5,
  dealerCardsX: 0,
  dealerCardsY: 0,
  cornerGap: -6,
};

/** Dealer persona for display name and avatar */
interface DealerPersona {
  name: string;
  lastName: string;
  avatarUrl: string;
  gender: 'male' | 'female';
}

interface PixiGameTableV1Props {
  dealer: DealerState;
  /** Dealer persona with name and avatar URL */
  dealerPersona?: DealerPersona | null;
  players: Map<number, PlayerState>;
  mySeatPosition: number | null;
  currentTurnSeat: number | null;
  currentHandIndex: number;
  onSeatClick?: (seatPosition: number) => void;
  /** Scale factor for seat radius (0-200, default 100) */
  seatRadiusScale?: number;
  /** X,Y offset for the entire seat group */
  seatGroupOffset?: { x: number; y: number };
  /** Scale factor for seat box size (50-150, default 100) */
  seatSizeScale?: number;
  /** Shoe position, rotation, and scale */
  shoeConfig?: { x: number; y: number; rotation: number; scale: number };
  /** Tray position, rotation, and scale */
  trayConfig?: { x: number; y: number; rotation: number; scale: number };
  /** Shoe cards (fanned cards behind shoe) position, rotation, and scale as a group */
  shoeCardsConfig?: { x: number; y: number; rotation: number; scale: number };
  /** Per-seat position configs (keyed by seat 1-7) */
  seatConfigs?: Record<number, SeatConfig>;
  /** Dealer scorebox X,Y offset from default position */
  dealerScoreboxOffset?: { x: number; y: number };
  /** Per-seat bet chip stack configs (keyed by seat 1-7) */
  betChipConfigs?: Record<number, ChipStackConfig>;
  /** Per-seat insurance chip stack configs (keyed by seat 1-7) */
  insuranceChipConfigs?: Record<number, ChipStackConfig>;
  /** Scorebox display configuration */
  scoreboxConfig?: ScoreboxConfig;
  /** Outcome label display configuration */
  outcomeLabelConfig?: OutcomeLabelConfig;
  /** Chip stack border configuration */
  chipBorderConfig?: ChipBorderConfig;
  /** Chip counter configuration */
  chipCounterConfig?: ChipCounterConfig;
  /** Kitty stack position configuration */
  kittyConfig?: KittyConfig;
  /** Sidebet icon position configuration */
  sidebetIconConfig?: {
    baseRadiusOffset: number;  // Y offset from seat center (negative = toward dealer)
    scale: number;             // Icon scale percentage
    slot0: { x: number; y: number }; // Left position
    slot1: { x: number; y: number }; // Center position
    slot2: { x: number; y: number }; // Right position
  };
  /** Opponent-specific kitty stack position configuration (defaults to kittyConfig if not provided) */
  opponentKittyConfig?: KittyConfig;
  /** Per-seat opponent kitty offsets (additive on top of opponentKittyConfig) */
  opponentKittyConfigs?: Record<number, { radiusOffset: number; x: number; y: number; scale: number }>;
  /** Opponent-specific sidebet icon position configuration (defaults to sidebetIconConfig if not provided) */
  opponentSidebetIconConfig?: {
    baseRadiusOffset: number;
    scale: number;
    slot0: { x: number; y: number };
    slot1: { x: number; y: number };
    slot2: { x: number; y: number };
  };
  /** Per-seat sidebet icon offsets (additive on top of base config) */
  sidebetIconConfigs?: Record<number, { radiusOffset: number; x: number; y: number }>;
  /** Per-card offsets for shoe cards (0, 1, 2) */
  shoeCardOffsets?: ShoeCardOffsets;
  /** Dealer button configuration */
  dealerButtonConfig?: DealerButtonConfig;
  /** Position of dealer button (visual seat 1-7, or 0 to hide) */
  dealerButtonSeat?: number;
  // ===========================================
  // UI ELEMENT PROPS (moved from React overlays)
  // ===========================================
  /** Callback when a chip in the rail is clicked */
  onChipClick?: (denomination: number) => void;
  /** Callback when clear bet button is clicked */
  onClearBet?: () => void;
  /** Current staged bet amount */
  stagedAmount?: number;
  /** Array of staged chip denominations */
  stagedChips?: number[];
  /** Displayed counter amount (may differ from stagedAmount during animations) */
  displayedAmount?: number;
  /** User's current balance */
  balance?: number;
  /** Callback when a chip animation should start (for counter updates) */
  onChipAnimationStart?: (denomination: number, direction: 'in' | 'out') => void;
  /** Callback when a chip animation completes (for counter updates) */
  onChipAnimationEnd?: (denomination: number, direction: 'in' | 'out') => void;
  /** Chip rail position/scale configuration */
  chipRailConfig?: ChipRailConfig;
  /** Staged bet display configuration */
  stagedBetConfig?: StagedBetConfig;
  /** Tray chips (dealer chip rail) position offset relative to tray */
  trayChipsOffset?: { x: number; y: number };
  /** Card rendering configuration */
  cardConfig?: CardConfig;
  /** Callback when player clicks their own seat to straighten cards */
  onStraightenCards?: (seatPosition: number) => void;
  /** Show/hide bet chip stacks at seats (DEV control) */
  showBetChips?: boolean;
  /** Show/hide insurance chip stacks at seats (DEV control) */
  showInsuranceChips?: boolean;
  /** Show/hide static hand labels at seats (DEV control) */
  showHandLabels?: boolean;
  /** Hand label global position config (DEV control) */
  handLabelConfig?: { x: number; y: number };
  /** Per-seat hand label position configs (DEV control) */
  handLabelConfigs?: Record<number, { x: number; y: number }>;
  /** Populate table with all elements for layout testing (DEV control) */
  devPopulateTable?: boolean;
  /** Per-seat card hand position configs (DEV control) */
  perSeatCardConfigs?: Record<number, { x: number; y: number; radiusOffset: number }>;
  /** Which seat is showing a dev test hand (null = none) */
  devCardSeat?: number | null;
  /** Left corner card config (scorekeeper) */
  leftCornerCardConfig?: { x: number; y: number; rotation: number; scale: number };
  /** Right corner card config (modal replacement) */
  rightCornerCardConfig?: { x: number; y: number; rotation: number; scale: number };
  /** Per-denomination Y offsets for chip rail chips */
  chipYOffsets?: Record<number, number>;
  /** Per-denomination X offsets for chip rail chips */
  chipXOffsets?: Record<number, number>;
  /** Per-denomination Y offsets for chip rail labels */
  chipLabelYOffsets?: Record<number, number>;
  /** Pill opacity for chip rail labels (0-100, 0 = no pill) */
  chipLabelPillOpacity?: number;
  /** Username Y offset within seat box */
  usernameY?: number;
  /** Current header height in pixels (for accurate scale calculation) */
  headerHeight?: number;
  /** Available width for the canvas (excluding header and control panel) */
  availableWidth?: number;
  /** Available height for the canvas (excluding header and control panel) */
  availableHeight?: number;
  // ===========================================
  // PIXI CONTROL PANEL PROPS
  // ===========================================
  /** Enable PixiJS control panel (replaces React control panel) */
  usePixiControlPanel?: boolean;
  /** Table ID for control panel position persistence */
  tableId?: string;
  /** Control panel action handlers */
  onHit?: () => void;
  onStand?: () => void;
  onDouble?: () => void;
  onSplit?: () => void;
  onSurrender?: () => void;
  onBet?: () => void;
  onAutobet?: () => void;
  /** Insurance action handlers */
  onInsuranceAccept?: (amount: number) => void;
  onInsuranceDecline?: () => void;
  /** How To Bet modal close handler */
  onHowToBetClose?: (dontShowAgain: boolean) => void;
  /** Control panel button text sizes */
  panelTextSizes?: {
    mainButtonsVert?: number;
    mainButtonsHoriz?: number;
    sideButtonsVert?: number;
    sideButtonsHoriz?: number;
  };
  /** Turn indicator beam configuration */
  beamConfig?: {
    color: string;    // Hex color e.g., '#22C55E'
    opacity: number;  // 0-100
    width: number;    // Beam width in degrees
    rotations?: Record<number, number>;  // Per-seat rotation angles (visual positions 1-7)
  };
  /** DEV: Force beam visible for positioning adjustments */
  devShowBeam?: boolean;
  /** DEV: Which visual seat (1-7) to point the dev beam at */
  devBeamSeat?: number;
  /** Current kitty amount (main bet) for sidebet percentage calculations */
  kittyAmount?: number;
  /** Callback when a sidebet is placed. rounds: 1, 5, 10, or -1 for infinite/ongoing */
  onSidebetPlace?: (sidebetType: string, amount: number, rounds: number) => void;
  /** Callback when a sidebet is terminated/removed by the user */
  onSidebetRemove?: (sidebetType: string) => void;
  /** Callback when user clicks on their own sidebet icon on the table */
  onSidebetIconClick?: (sidebetType: string) => void;
  /** Socket connection state - disables bet button when not connected */
  isConnected?: boolean;
  /** Callback when control panel position changes (for mirroring SidePanel) */
  onControlPanelPositionChange?: (position: ControlPanelPosition) => void;
  /** BUDDY subscription active - shows DP/TC HUD when true */
  buddyActive?: boolean;
  /** Deck penetration percentage (0-100) for BUDDY HUD */
  penetration?: number;
  /** True count value for BUDDY HUD */
  trueCount?: number;
  /** BUDDY HUD config (positions, sizes, fonts) */
  buddyHudConfig?: {
    dpX: number;
    tcX: number;
    y: number;
    barY: number; // Y offset for bar/ticks only (relative to container y)
    barWidth: number;
    barHeight: number;
    labelFontSize: number;
    valueFontSize: number;
    tickLabelFontSize: number;
  };
}

export interface PixiGameTableV1Ref {
  // Card animations
  // duration is optional - server-provided timing from event.duration (undefined = use default)
  dealCardToSeat: (seatPosition: number, card: CardType, cardIndex: number, handIndex?: number, duration?: number) => Promise<void>;
  dealCardToDealer: (card: CardType, cardIndex: number, duration?: number) => Promise<void>;
  revealHoleCard: (flipDuration?: number) => Promise<void>;
  clearTable: () => Promise<void>;
  rakeCardsForSeat: (seatPosition: number) => Promise<void>;
  /** Rake ONLY dealer cards (used in new multiplayer architecture - fires after all player rakes) */
  rakeDealerCards: () => Promise<void>;
  /** Straighten all cards for a specific seat (animate to neat, aligned position) */
  straightenSeatCards: (seatPosition: number, handIndex?: number) => Promise<void>;
  /** Straighten dealer cards (animate to neat, aligned position) */
  straightenDealerCards: () => Promise<void>;

  // Chip animations - using layout endpoints
  /** Animate chip from player's rail to kitty (bet placement) */
  animateRailToKitty: (seatPosition: number, denomination: number, duration?: number) => Promise<void>;
  /** Animate chips from opponent's avatar to their kitty (opponent bet placement - for Pixi table) */
  animateOpponentBetToKitty: (seatPosition: number, amount: number, betChips: number[]) => Promise<void>;
  /** Animate chip from player's kitty to rail (win/push - chips return to player). For split hands, pass handIndex. */
  animateChipFromKitty: (seatPosition: number, denomination: number, handIndex?: number, zIndex?: number, duration?: number) => Promise<void>;
  /** Animate chip from player's kitty to dealer tray (loss - dealer collects). For split hands, pass handIndex. */
  animateChipToDealer: (seatPosition: number, denomination: number, handIndex?: number, zIndex?: number, duration?: number) => Promise<void>;
  /** Animate chip from dealer tray to player's kitty (payout - dealer pays player). For split hands, pass handIndex. */
  animateChipFromDealer: (seatPosition: number, denomination: number, handIndex?: number, duration?: number) => Promise<void>;
  /** Animate surrender - FULL chip stack flies to BOTH rail AND dealer simultaneously (visual split) */
  animateSurrenderChips: (seatPosition: number, denominations: number[]) => Promise<void>;
  /** Clear kitty display for a seat or all seats (round reset). For split hands, pass handIndex. */
  clearKitty: (seatPosition?: number, handIndex?: number) => void;
  /** Recenter split kitties after one is removed (e.g., mid-round bust). Animates remaining kitties to fill gap. */
  recenterSplitKitties: (seatPosition: number) => void;
  /** Get all chip denominations currently in a seat's kitty. For split hands, pass handIndex. */
  getKittyChips: (seatPosition: number, handIndex?: number) => number[];

  // Opponent chip animations (Pixi-based - for multiplayer)
  /** Animate chips from dealer tray to opponent's kitty (payout landing) */
  animatePayoutToOpponentKitty: (seatPosition: number, chips: number[]) => Promise<void>;
  /** Animate all chips from opponent's kitty to their avatar (win/push collection) */
  animateOpponentKittyToAvatar: (seatPosition: number, chips: number[]) => Promise<void>;
  /** Animate chips from opponent's kitty to dealer tray (loss collection) */
  animateOpponentLossToDealer: (seatPosition: number, chips: number[]) => Promise<void>;
  /** Animate opponent double down - chips from avatar to EXISTING kitty */
  animateOpponentDoubleDownChips: (seatPosition: number, chips: number[]) => Promise<void>;
  /** Animate opponent surrender - simultaneous: half to dealer, half to avatar */
  animateOpponentSurrenderChips: (seatPosition: number, lossChips: number[], refundChips: number[]) => Promise<void>;

  // Outcome label - IMPERATIVE (bypasses React render cycle)
  /**
   * Show outcome label for a seat. Returns promise that resolves when fly-in animation completes.
   * This is IMPERATIVE - call directly, don't wait for React to render.
   */
  showOutcomeLabel: (seatPosition: number, handIndex: number, outcome: string) => Promise<void>;

  // Sidebet animations
  /** Animate chip from player's rail to sidebet icon (sidebet placement) */
  animateSidebetPlacement: (seatPosition: number, sidebetType: string, amount: number, rounds: number, duration?: number, stagger?: number) => Promise<void>;
  /** Show win glow on sidebet icon after resolution */
  showSidebetWin: (seatPosition: number, sidebetType: string, winType?: string) => void;
  /** Show loss dim on sidebet icon after resolution */
  showSidebetLoss: (seatPosition: number, sidebetType: string) => void;
  /** Animate sidebet win payout: dealer tray → sidebet icon → player rail. chipBreakdown is server-authoritative. */
  animateSidebetWin: (seatPosition: number, sidebetType: string, amount: number, chipBreakdown: ChipBreakdown, duration?: number) => Promise<void>;
  /** Animate sidebet loss: sidebet icon → dealer tray */
  animateSidebetLoss: (seatPosition: number, sidebetType: string, amount: number, duration?: number, stagger?: number) => Promise<void>;
  /** Clear sidebet visual states (call after payout animations) */
  clearSidebetState: (sidebetType: string) => void;
  /** Clear ALL active sidebet visuals - call when new round starts */
  clearAllSidebetVisuals: () => void;
  /** Restore configured sidebet visuals after clearing - shows green indicators for active configs */
  restoreConfiguredSidebetVisuals: (configs: Map<string, { percentage: number; rounds: number }>) => void;

  // Per-seat sidebet icon management
  /** Create a sidebet icon at a player seat (returns slot index assigned, or -1 if full) */
  addSidebetToSeat: (seatPosition: number, sidebetType: string, amount: number, rounds: number) => number;
  /** Remove a sidebet icon from a player seat */
  removeSidebetFromSeat: (seatPosition: number, sidebetType: string) => void;
  /** Update the badge on an existing sidebet icon (rounds remaining) */
  updateSidebetBadge: (seatPosition: number, sidebetType: string, newRounds: number) => void;
  /** Mark a sidebet for removal during next card rake */
  markSidebetForRemoval: (seatPosition: number, sidebetType: string) => void;
  /** Remove all sidebets marked for removal (call during card rake) */
  removeExpiredSidebets: () => void;
  /** Clear all sidebet icons from a player seat */
  clearSidebetsFromSeat: (seatPosition: number) => void;
  /** Clear ALL visuals for a player seat (cards, kitty, sidebets) - used when player leaves */
  clearSeatVisuals: (seatPosition: number) => Promise<void>;
  /** Get the current sidebet icon position for animations (returns null if not found) */
  getSidebetIconPosition: (seatPosition: number, sidebetType: string) => { x: number; y: number } | null;

  // Health check - verifies and repairs PixiJS scene integrity
  /**
   * Verify PixiJS scene elements exist and are valid.
   * Call during rake phase each round to catch and fix scene corruption.
   * Returns a report of what was checked and any repairs made.
   */
  ensurePixiSceneHealth: () => {
    checked: number;
    fixed: number;
    issues: string[];
  };

  /**
   * Animate the dealer button to a new visual seat position.
   * Call this when buttonPosition changes (e.g., after ROUND_STARTED).
   * @param targetVisualSeat Visual seat position (1-7) to move the button to
   */
  moveDealerButton: (targetVisualSeat: number) => Promise<void>;

  /**
   * Sync dealt card counts and scores for mid-hand join.
   * When joining a table with an active game, cards are already dealt but
   * MODULE_DEALT_CARD_COUNTS and MODULE_SCORE_HISTORY are empty. This initializes
   * them so scoreboxes display correctly and control panel enables.
   */
  syncDealtCounts: (
    seatCardCounts: Record<number, number[]>,
    dealerCardCount: number,
    seatScores?: Record<number, Array<{ score: number; isSoft: boolean }>>,
    dealerScore?: { score: number; isSoft: boolean }
  ) => void;

  /**
   * Full visual resync after keyed state refresh.
   * Creates card sprites for all cards in the current zustand state.
   * Call this after accepting a keyed resync to render cards that were wiped on tab-away.
   * Unlike reconciliation (which refuses to create cards due to potential staleness),
   * this method trusts the state is fresh from the server.
   */
  fullVisualResync: () => void;

  /**
   * Show the sidebet modal for the given sidebet type.
   * Called from SidePanel when user clicks on a sidebet in the list.
   */
  showSidebetModal: (sidebetType: string) => void;

  // BUDDY HUD (DP and TC indicators)
  /** Show or hide the BUDDY HUD based on subscription status */
  setBuddyHUDVisible: (visible: boolean) => void;
  /** Update the BUDDY HUD with current penetration and true count values */
  updateBuddyHUD: (penetration: number, trueCount: number) => void;

  // Insurance Panel
  /** Show the insurance panel with cost and optional deadline */
  showInsurancePanel: (cost: number, deadline: number | null) => void;
  /** Hide the insurance panel */
  hideInsurancePanel: () => void;

  // How To Bet Modal
  /** Show the How To Bet tutorial modal */
  showHowToBetModal: () => void;
  /** Hide the How To Bet tutorial modal */
  hideHowToBetModal: () => void;

  /** Set the insurance recommendation highlighting */
  setInsuranceRecommendation: (rec: 'ACCEPT' | 'DECLINE' | null) => void;
  /** Set whether player can afford insurance */
  setInsuranceCanAfford: (canAfford: boolean) => void;

  // Insurance Badges (per-seat indicator)
  /** Show insurance pending badge at a seat */
  showInsuranceBadge: (seatPosition: number) => void;
  /** Hide insurance pending badge at a seat */
  hideInsuranceBadge: (seatPosition: number) => void;
  /** Hide all insurance badges */
  hideAllInsuranceBadges: () => void;

  // Avatar Countdown Rings (for betting and turn timers)
  /** Show countdown ring around avatar at a seat */
  showCountdownRing: (seatPosition: number, deadline: number, totalDuration?: number) => void;
  /** Hide countdown ring at a seat */
  hideCountdownRing: (seatPosition: number) => void;
  /** Hide all countdown rings */
  hideAllCountdownRings: () => void;

  // Insurance Chip Animations
  /** Animate chip from rail to insurance kitty (accepting insurance) */
  animateRailToInsuranceKitty: (seatPosition: number, denomination: number) => Promise<void>;
  /** Animate insurance win: chips from insurance kitty to rail */
  animateInsuranceWin: (seatPosition: number, insuranceBet: number, payoutAmount: number) => Promise<void>;
  /** Animate insurance loss: chips from insurance kitty to dealer */
  animateInsuranceLoss: (seatPosition: number, amount: number) => Promise<void>;
  /** Clear insurance kitty for a seat */
  clearInsuranceKitty: (seatPosition: number) => void;

  // Auto-play recovery (for ALT+TAB fix)
  /** Force retry autoplay after tab visibility change */
  retryAutoPlay: () => void;
  /** Get the current computed recommendation (for post-resync autoplay) */
  getComputedRecommendation: () => { action: string; isDeviation: boolean } | null;

  // Split Hand Support
  /**
   * Set up split hand mode for a seat.
   * Creates carousel navigation state and prepares for multiple hands.
   * @param seatPosition - The player's seat position
   * @param totalHands - Number of hands after split (2, 3, 4, etc.)
   */
  setSplitHandCount: (seatPosition: number, totalHands: number) => void;

  /**
   * Navigate carousel to show a specific hand.
   * Cards and kitty for other hands will be hidden/offset.
   * @param seatPosition - The player's seat position
   * @param handIndex - Hand to show (0-based)
   * @param pendingOutcome - Optional outcome to show for the target hand (animates in sync with hand label)
   * @returns Promise that resolves when carousel animation completes
   */
  navigateToHand: (seatPosition: number, handIndex: number, pendingOutcome?: string) => Promise<void>;

  /**
   * Get the currently viewed hand index for a seat.
   * @param seatPosition - The player's seat position
   * @returns Current hand index (0 if no split)
   */
  getCurrentViewedHand: (seatPosition: number) => number;

  /**
   * Get total number of hands for a seat.
   * @param seatPosition - The player's seat position
   * @returns Total hands (1 if no split)
   */
  getTotalHands: (seatPosition: number) => number;

  /**
   * Instantly reset filmstrip to cell 1 (hand index 0) without animation.
   * Used after rake completes to prepare for next round.
   * @param seatPosition - The player's seat position
   */
  instantResetFilmstrip: (seatPosition: number) => void;

  /**
   * Animate split action - chips fly from rail to new split hand's kitty.
   * Also creates the new hand's kitty container and initializes carousel if needed.
   * @param seatPosition - The player's seat position
   * @param originalHandIndex - The hand being split (usually 0)
   * @param newHandIndex - The new hand being created
   * @param betChips - Chip denominations to animate
   * @returns Promise that resolves when animation completes
   */
  animateSplitAction: (
    seatPosition: number,
    originalHandIndex: number,
    newHandIndex: number,
    betChips: number[]
  ) => Promise<void>;

  /**
   * Reset split hand state for a seat (after round completes).
   * Clears carousel navigation and resets to single-hand mode.
   * @param seatPosition - The player's seat position
   */
  resetSplitHandState: (seatPosition: number) => void;

  /**
   * Animate the second card separating to a new split hand position.
   * Called after HAND_SPLIT to visually move the split card to its new hand.
   * @param seatPosition - The player's seat position
   * @param originalHandIndex - The hand being split (source)
   * @param newHandIndex - The new hand (destination)
   * @param duration - Animation duration in ms (default 300)
   */
  animateSplitCardSeparation: (
    seatPosition: number,
    originalHandIndex: number,
    newHandIndex: number,
    duration?: number
  ) => Promise<void>;

  /**
   * Show or hide the active hand indicator (glow/highlight).
   * Call when turn starts to highlight which split hand is active.
   * @param seatPosition - The player's seat position
   * @param handIndex - Hand to highlight, or null to clear all highlights
   */
  setActiveHandIndicator: (seatPosition: number, handIndex: number | null) => void;

  // DEV CONTROLS: Populate Table
  /**
   * Populate table with all visual elements for layout testing.
   * Creates cards, chip stacks, sidebet icons, split hands, insurance kitty.
   */
  populateTableForDev: () => void;
  /**
   * Clear all dev population elements from the table.
   */
  clearDevPopulation: () => void;

  // ===========================================
  // SCORECARD (Round Results Display)
  // ===========================================
  /**
   * Flip the scorecard to show results (front) or hide (back).
   * @param toFront - true to show results, false to show card back
   * @param duration - total flip duration in ms (default 400)
   */
  flipScorecard: (toFront: boolean, duration?: number) => Promise<void>;
  /**
   * Update the scorecard content with server-driven round results.
   * Call this before flipping to front.
   */
  updateScorecardContent: (
    hands: ScorecardHandResult[],
    sidebets: ScorecardSidebetResult[],
    insurance: ScorecardInsuranceResult | undefined,
    totalNetResult: number
  ) => void;
  /**
   * Reset the scorecard to card-back state (no animation).
   */
  resetScorecard: () => void;

  // Avatar retry (for failed loads)
  /**
   * Retry loading failed avatars with cache-busting.
   * Call on round-start to self-heal transient failures.
   * Respects retry cap (8 attempts) to avoid endless errors.
   */
  retryFailedAvatars: () => Promise<void>;

  // Cleanup
  /** Immediately destroy PixiJS application and remove canvas from DOM */
  destroy: () => void;
}

// ===========================================
// ANIMATION HELPERS
// ===========================================

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInCubic = (t: number) => t * t * t;
const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const linear = (t: number) => t;

// ===========================================
// CARD RANDOMNESS GENERATION
// ===========================================
// Generates natural-looking random rotation and offset for dealt cards.
// 10% chance of "sloppy" card (±36° rotation, ±36px offset)
// 90% chance of "normal" card (±6° rotation, ±6px offset)

interface CardRandomness {
  rotation: number;  // Random rotation in radians
  offsetX: number;   // X offset in world coordinates
  offsetY: number;   // Y offset in world coordinates
}

function generateCardRandomness(seatRotationDegrees: number): CardRandomness {
  // Y = random(1-10), Z = 6
  // If Y === 1: X = 36 (sloppy card, 10% chance)
  // Else: X = 6 (normal subtle variation)
  const Y = Math.floor(Math.random() * 10) + 1; // 1-10
  const Z = 6;
  const X = Y === 1 ? Z * Z : Z; // 36 or 6

  const direction = Math.random() < 0.5 ? 1 : -1; // CW or CCW
  const rotationDegrees = direction * Math.random() * X; // 0 to X degrees
  const rawOffsetY = (Math.random() * 2 - 1) * X; // -X to +X pixels (in viewer coordinates)

  // Transform offset to account for seat rotation (same logic as React Card.tsx)
  const radians = degreesToRadians(seatRotationDegrees);
  const offsetX = -rawOffsetY * Math.sin(radians);
  const offsetY = rawOffsetY * Math.cos(radians);

  return {
    rotation: degreesToRadians(rotationDegrees),
    offsetX,
    offsetY,
  };
}

/**
 * Generate randomness for card placement in LOCAL filmstrip coordinates.
 * Unlike generateCardRandomness, this does NOT transform for seat rotation
 * since the filmstrip container is already rotated.
 *
 * In local coords:
 * - X is along the card row (horizontal spread)
 * - Y is perpendicular to card row (toward/away from dealer)
 */
function generateCardRandomnessLocal(): CardRandomness {
  // Y = random(1-10), Z = 6
  // If Y === 1: X = 36 (sloppy card, 10% chance)
  // Else: X = 6 (normal subtle variation)
  const Y = Math.floor(Math.random() * 10) + 1; // 1-10
  const Z = 6;
  const X = Y === 1 ? Z * Z : Z; // 36 or 6

  const direction = Math.random() < 0.5 ? 1 : -1; // CW or CCW
  const rotationDegrees = direction * Math.random() * X; // 0 to X degrees

  // In local coords, offset is perpendicular to card row (Y axis)
  const offsetY = (Math.random() * 2 - 1) * X; // -X to +X pixels perpendicular to row
  const offsetX = 0; // No horizontal offset

  return {
    rotation: degreesToRadians(rotationDegrees),
    offsetX,
    offsetY,
  };
}

/**
 * Get or create the filmstrip container for a seat.
 * The filmstrip is a horizontal strip containing cards for all 4 possible hands.
 * Cards are positioned at LOCAL offsets within the filmstrip (cell 0, 1, 2, 3).
 * The filmstrip slides to show the currently viewed hand.
 *
 * Architecture:
 * - seatCardViewports: Positioned at seat center, rotated with seat, masked to seat bounds
 * - seatCardContents: The filmstrip inside viewport, slides for navigation
 *
 * @param scene - The scene ref
 * @param seatPosition - The seat position (1-7)
 * @param seatX - Seat center X in world coordinates
 * @param seatY - Seat center Y in world coordinates
 * @param seatRotationRad - Seat rotation in radians
 * @param cardsLayer - Layer to add the filmstrip to
 * @param seatWidth - Scaled seat width for masking
 * @param seatHeight - Scaled seat height for masking
 * @returns The filmstrip container (seatCardContents entry)
 */
function getOrCreateFilmstrip(
  scene: {
    seatCardViewports: Map<number, Container>;
    seatCardContents: Map<number, Container>;
    seatCardMasks: Map<number, Graphics>;
    seatMaskState: Map<number, MaskState>;
    seatMaskDebugBorders: Map<number, Graphics>;
  },
  seatPosition: number,
  seatX: number,
  seatY: number,
  seatRotationRad: number,
  cardsLayer: Container,
  seatWidth: number,
  seatHeight: number
): Container {
  // Check if filmstrip already exists
  let filmstrip = scene.seatCardContents.get(seatPosition);
  if (filmstrip && !filmstrip.destroyed) {
    return filmstrip;
  }

  // Create viewport container (positioned at seat, masked to seat bounds)
  const viewport = new Container();
  viewport.x = seatX;
  viewport.y = seatY;
  viewport.rotation = seatRotationRad;
  viewport.sortableChildren = true; // For card zIndex ordering
  cardsLayer.addChild(viewport);
  scene.seatCardViewports.set(seatPosition, viewport);

  // Create mask for split hand clipping - but DON'T apply it yet (only enable when hands > 1)
  // Mask starts at seat bounds (elastic mask will expand to hug cards when enabled)
  const viewportMask = new Graphics();
  const seatHalfWidth = seatWidth / 2;
  const seatHalfHeight = seatHeight / 2;
  viewportMask.roundRect(-seatHalfWidth, -seatHalfHeight, seatWidth, seatHeight, MASK_CORNER_RADIUS);
  viewportMask.fill({ color: 0xffffff });
  // Hidden by default - enableSeatMask() will set visible=true when mask is applied
  // (In PixiJS v8, mask must be visible to work as a mask)
  viewportMask.visible = false;
  viewport.addChild(viewportMask);
  scene.seatCardMasks.set(seatPosition, viewportMask);
  // NOTE: Mask is NOT applied by default - only enable when split happens (hands > 1)
  // viewport.mask = viewportMask; // REMOVED - use enableSeatMask() when split occurs

  // Initialize mask state as disabled (all four edges at seat boundaries)
  scene.seatMaskState.set(seatPosition, {
    leftEdge: -seatHalfWidth,
    rightEdge: seatHalfWidth,
    topEdge: -seatHalfHeight,
    bottomEdge: seatHalfHeight,
    seatLeftEdge: -seatHalfWidth,
    seatRightEdge: seatHalfWidth,
    seatTopEdge: -seatHalfHeight,
    seatBottomEdge: seatHalfHeight,
    isEnabled: false,
    isAnimating: false,
  });

  // DEBUG: Add red border to visualize mask bounds (added to parent, not viewport, to avoid clipping)
  // Hidden by default - shown when mask is enabled
  const debugBorder = new Graphics();
  debugBorder.roundRect(-seatHalfWidth, -seatHalfHeight, seatWidth, seatHeight, MASK_CORNER_RADIUS);
  debugBorder.stroke({ color: 0xff0000, width: 3 }); // Red border, 3px thick
  debugBorder.x = seatX;
  debugBorder.y = seatY;
  debugBorder.rotation = seatRotationRad;
  debugBorder.zIndex = 9999; // On top
  debugBorder.visible = false; // Hidden until mask is enabled
  cardsLayer.addChild(debugBorder);
  scene.seatMaskDebugBorders.set(seatPosition, debugBorder);

  // Create filmstrip container inside viewport (slides for navigation)
  filmstrip = new Container();
  filmstrip.x = 0; // Starts centered (hand 0 visible)
  filmstrip.y = 0;
  filmstrip.sortableChildren = true; // For card zIndex ordering
  viewport.addChild(filmstrip);
  scene.seatCardContents.set(seatPosition, filmstrip);

  console.log(`[FILMSTRIP] Created filmstrip for seat ${seatPosition} at (${seatX.toFixed(0)}, ${seatY.toFixed(0)}), rot=${(seatRotationRad * 180 / Math.PI).toFixed(1)}°, mask=${seatWidth}x${seatHeight}, maskEnabled=false`);

  return filmstrip;
}

/**
 * Animate scorebox wipe during carousel navigation.
 * Old score slides out horizontally, "--" slides in from opposite direction.
 * Direction matches carousel: carousel LEFT → wipe LEFT, carousel RIGHT → wipe RIGHT.
 *
 * @param scoreboxLayer - The layer containing scoreboxes
 * @param seatPosition - The seat position
 * @param wipeDirection - 'left', 'right', 'up', or 'down'
 *   - Horizontal (left/right): matches carousel direction during split hand navigation
 *   - Vertical (down): used during rake to transition score to "--"
 * @param duration - Total wipe duration
 * @param newScore - The score to show at the end (optional, "--" if not provided)
 */
function animateScoreboxWipe(
  scoreboxLayer: Container | null,
  seatPosition: number,
  wipeDirection: 'left' | 'right' | 'up' | 'down',
  duration: number,
  newScore?: string
): void {
  if (!scoreboxLayer || scoreboxLayer.destroyed) {
    console.log(`[SCOREBOX WIPE] ABORT: scoreboxLayer invalid`);
    return;
  }

  const scoreboxLabel = `scorebox-${seatPosition}`;
  const scorebox = scoreboxLayer.children.find(c => c.label === scoreboxLabel) as Container | undefined;
  if (!scorebox || scorebox.destroyed) {
    console.log(`[SCOREBOX WIPE] ABORT: scorebox not found for seat ${seatPosition}, children labels: ${scoreboxLayer.children.map(c => c.label).join(', ')}`);
    return;
  }

  // Find the text element (it's after the background Graphics)
  const scoreText = scorebox.children.find(c => c instanceof Text) as Text | undefined;
  if (!scoreText || scoreText.destroyed) {
    console.log(`[SCOREBOX WIPE] ABORT: scoreText not found in scorebox for seat ${seatPosition}`);
    return;
  }

  // Mark wipe in progress - prevents createSeatContainer from overwriting during re-renders
  MODULE_SCOREBOX_WIPE_IN_PROGRESS.add(seatPosition);

  // Store original position and text
  const originalX = scoreText.x;
  const originalY = scoreText.y;
  const originalText = scoreText.text;
  const boxWidth = (scorebox as Container & { actualWidth?: number }).actualWidth ?? 35;
  // Get actual box height from bounds (accounts for scale)
  const scoreboxBounds = scorebox.getLocalBounds();
  const boxHeight = scoreboxBounds.height;

  // Lock bounds to prevent container from resizing during animation
  // Store original boundsArea to restore later
  const originalBoundsArea = scorebox.boundsArea;
  scorebox.boundsArea = new Rectangle(0, 0, boxWidth, boxHeight);

  // Determine if horizontal or vertical wipe
  const isHorizontal = wipeDirection === 'left' || wipeDirection === 'right';

  // Calculate slide distance (text should fully exit the box)
  const slideDistance = isHorizontal
    ? boxWidth + scoreText.width
    : boxHeight + scoreText.height;

  // Direction multiplier based on wipe direction
  // left = -x, right = +x, up = -y, down = +y
  let exitDirection: number;
  if (wipeDirection === 'left') exitDirection = -1;
  else if (wipeDirection === 'right') exitDirection = 1;
  else if (wipeDirection === 'up') exitDirection = -1;
  else exitDirection = 1; // down

  const enterDirection = -exitDirection; // Opposite direction

  // Create the "--" text for the transition
  const dashText = new Text({
    text: '--',
    style: scoreText.style.clone()
  });
  dashText.anchor.set(0.5, 0.5);

  // Position dashText offscreen (above for down wipe, opposite side for horizontal)
  if (isHorizontal) {
    dashText.x = originalX + (slideDistance * enterDirection);
    dashText.y = originalY;
  } else {
    dashText.x = originalX;
    dashText.y = originalY + (slideDistance * enterDirection); // Above the box for 'down' wipe
  }
  scorebox.addChild(dashText);

  // Create mask to clip text at scorebox boundaries (like outcome label clips at seat border)
  const mask = new Graphics();
  mask.rect(0, 0, boxWidth, boxHeight);
  mask.fill({ color: 0xffffff });
  scorebox.addChild(mask);

  // Store original mask (if any) to restore later
  const originalMask = scorebox.mask;
  scorebox.mask = mask;

  // Set cache to "--" immediately so any re-renders during animation use "--"
  MODULE_SCOREBOX_CACHE.set(`${seatPosition}`, '--');

  console.log(`[SCOREBOX WIPE DEBUG] Setup: scoreText="${scoreText.text}" at (${originalX}, ${originalY}), dashText starts at y=${dashText.y}, direction=${wipeDirection}, duration=${duration}ms`);

  const startTime = performance.now();

  const animateFrame = () => {
    if (scorebox.destroyed || scoreText.destroyed) {
      if (!dashText.destroyed) dashText.destroy();
      if (!mask.destroyed) mask.destroy();
      // Note: slideFilmstripToHand will clear MODULE_SCOREBOX_WIPE_IN_PROGRESS at navigation end
      // For RAKE, MODULE_RAKE_SEATS_IN_PROGRESS protects the scorebox
      return;
    }

    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    // Ease out for smooth deceleration
    const eased = 1 - Math.pow(1 - progress, 2);

    if (isHorizontal) {
      // Old text exits horizontally (clipped at edge)
      scoreText.x = originalX + (slideDistance * exitDirection * eased);
      // "--" enters from opposite side
      dashText.x = originalX + (slideDistance * enterDirection * (1 - eased));
    } else {
      // Old text exits vertically DOWN (clipped at bottom edge)
      scoreText.y = originalY + (slideDistance * exitDirection * eased);
      // "--" enters from TOP
      dashText.y = originalY + (slideDistance * enterDirection * (1 - eased));
    }

    // DEBUG: Check for height changes during animation
    checkScoreboxHeights();

    if (progress < 1) {
      requestAnimationFrame(animateFrame);
    } else {
      // Animation complete - set final state
      // IMPORTANT: Change text BEFORE resetting position to avoid flash of old score
      scoreText.text = '--';
      scoreText.x = originalX;
      scoreText.y = originalY;
      // Cache already set at animation start

      // Restore original mask, boundsArea, and clean up
      scorebox.mask = originalMask;
      scorebox.boundsArea = originalBoundsArea;
      if (!mask.destroyed) {
        scorebox.removeChild(mask);
        mask.destroy();
      }
      if (!dashText.destroyed) {
        scorebox.removeChild(dashText);
        dashText.destroy();
      }

      console.log(`[SCOREBOX WIPE] Seat ${seatPosition} wipe complete, showing "--"`);
    }
  };

  console.log(`[SCOREBOX WIPE] Seat ${seatPosition} wiping ${wipeDirection}, "${originalText}" → "--" (${duration}ms)`);
  requestAnimationFrame(animateFrame);
}

/**
 * Slide the filmstrip to show a specific hand.
 * The filmstrip moves so the target hand's cell is centered in the viewport.
 * Also triggers scorebox wipe animation when navigating between hands with cards.
 *
 * @param scene - The scene ref (must include scoreboxLayer for wipe animation)
 * @param seatPosition - The seat position
 * @param targetHandIndex - The hand index to show (0-3)
 * @param animate - Whether to animate the slide (default true)
 * @param duration - Animation duration in ms (default 300)
 */
function slideFilmstripToHand(
  scene: {
    seatCardContents: Map<number, Container>;
    scoreboxLayer?: Container | null;
    seatCardMasks: Map<number, Graphics>;
    seatMaskState: Map<number, MaskState>;
    seatMaskDebugBorders: Map<number, Graphics>;
  },
  seatPosition: number,
  targetHandIndex: number,
  animate: boolean = true,
  duration: number = 600,
  irisConfig?: {
    targetHandCardCount: number;
    scaleFactor: number;
    seatScale: number;
  }
): Promise<void> {
  return new Promise((resolve) => {
    const filmstrip = scene.seatCardContents.get(seatPosition);
    if (!filmstrip || filmstrip.destroyed) {
      resolve();
      return;
    }

    // Target X position: negative offset to center the target hand
    // Hand 0 at x=0, Hand 1 at x=-144, Hand 2 at x=-288, etc.
    const targetX = -targetHandIndex * FILMSTRIP_CELL_WIDTH;

    if (!animate || Math.abs(filmstrip.x - targetX) < 1) {
      // Snap immediately
      filmstrip.x = targetX;
      console.log(`[FILMSTRIP] Seat ${seatPosition} snapped to hand ${targetHandIndex} (x=${targetX})`);
      resolve();
      return;
    }

    // Sound: slide at animation start (skip if snapping)
    playTableSound('slide');

    // Calculate current hand index from filmstrip position
    const currentHandIndex = Math.round(-filmstrip.x / FILMSTRIP_CELL_WIDTH);

    // Determine wipe direction based on navigation direction
    // targetHandIndex > currentHandIndex → carousel moves LEFT → wipe LEFT
    // targetHandIndex < currentHandIndex → carousel moves RIGHT → wipe RIGHT
    const wipeDirection: 'left' | 'right' = targetHandIndex > currentHandIndex ? 'left' : 'right';

    // Capture start position early (needed for both filmstrip and iris animation)
    const startX = filmstrip.x;

    // Trigger scorebox wipe animation (duration = navigation_duration / 2)
    // The wipe completes at halfway point showing "--", then at navigation end the new score snaps in
    console.log(`[FILMSTRIP DEBUG] scene.scoreboxLayer=${!!scene.scoreboxLayer}, wipeDirection=${wipeDirection}, duration=${duration}`);
    if (scene.scoreboxLayer) {
      animateScoreboxWipe(scene.scoreboxLayer, seatPosition, wipeDirection, duration / 2);
    } else {
      console.log(`[FILMSTRIP DEBUG] scoreboxLayer is null/undefined, skipping wipe animation`);
    }

    // === ASYMMETRIC IRIS ANIMATION SETUP ===
    // Animate left/right edges independently based on carousel direction:
    // - DEPARTING edge (where current hand exits):
    //   Phase 1 (0-50%): Contract to seat edge
    //   Phase 2 (50-100%): Expand for arriving hand if needed
    // - ARRIVING edge (where next hand enters):
    //   Linear interpolation to target, clamped to never clip departing cards
    const maskState = scene.seatMaskState.get(seatPosition);
    const mask = scene.seatCardMasks.get(seatPosition);
    const debugBorder = scene.seatMaskDebugBorders.get(seatPosition);
    const irisEnabled = !!(maskState?.isEnabled && mask && !mask.destroyed && irisConfig);

    // Asymmetric edge animation state
    let irisSeatScale = 1;
    let startLeftEdge = 0;
    let startRightEdge = 0;
    let seatLeftEdge = 0;
    let seatRightEdge = 0;
    let departingHandBounds: { left: number; right: number; top: number; bottom: number } | null = null;
    let arrivingHandBounds: { left: number; right: number; top: number; bottom: number } | null = null;
    // Direction: 'left' means carousel moves left, right edge is departing
    const isMovingLeft = targetHandIndex > currentHandIndex;

    if (irisEnabled && irisConfig && maskState) {
      irisSeatScale = irisConfig.seatScale;
      startLeftEdge = maskState.leftEdge;
      startRightEdge = maskState.rightEdge;
      seatLeftEdge = maskState.seatLeftEdge;
      seatRightEdge = maskState.seatRightEdge;

      // Get bounds for departing hand (currently visible) and arriving hand (at destination)
      departingHandBounds = getHandBounds(seatPosition, currentHandIndex, startX);
      arrivingHandBounds = getHandBounds(seatPosition, targetHandIndex, targetX);

      console.log(`[IRIS] Asymmetric carousel: direction=${isMovingLeft ? 'left' : 'right'}, ` +
        `seatEdges=[${seatLeftEdge.toFixed(0)}, ${seatRightEdge.toFixed(0)}], ` +
        `startEdges=[${startLeftEdge.toFixed(0)}, ${startRightEdge.toFixed(0)}], ` +
        `departing=${departingHandBounds ? `[${departingHandBounds.left.toFixed(0)}, ${departingHandBounds.right.toFixed(0)}]` : 'null'}, ` +
        `arriving=${arrivingHandBounds ? `[${arrivingHandBounds.left.toFixed(0)}, ${arrivingHandBounds.right.toFixed(0)}]` : 'null'}`);

      // PRE-EXPAND top/bottom edges to accommodate BOTH hands during carousel
      // This prevents clipping of arriving hand's cards before mask adjusts at completion
      const departingTop = departingHandBounds?.top ?? maskState.seatTopEdge;
      const departingBottom = departingHandBounds?.bottom ?? maskState.seatBottomEdge;
      const arrivingTop = arrivingHandBounds?.top ?? maskState.seatTopEdge;
      const arrivingBottom = arrivingHandBounds?.bottom ?? maskState.seatBottomEdge;

      // Union of both hands' vertical bounds (min for top = more negative = higher up)
      const expandedTopEdge = Math.min(departingTop, arrivingTop, maskState.seatTopEdge);
      const expandedBottomEdge = Math.max(departingBottom, arrivingBottom, maskState.seatBottomEdge);

      if (expandedTopEdge !== maskState.topEdge || expandedBottomEdge !== maskState.bottomEdge) {
        maskState.topEdge = expandedTopEdge;
        maskState.bottomEdge = expandedBottomEdge;
        redrawMaskElastic(mask, maskState.leftEdge, maskState.rightEdge, expandedTopEdge, expandedBottomEdge);
        if (MASK_DEBUG_ENABLED && debugBorder && !debugBorder.destroyed) {
          updateMaskDebugBorderElastic(debugBorder, maskState.leftEdge, maskState.rightEdge, expandedTopEdge, expandedBottomEdge);
        }
        console.log(`[IRIS] Pre-expanded top/bottom: [${expandedTopEdge.toFixed(0)}, ${expandedBottomEdge.toFixed(0)}]`);
      }
    }

    // Animate the slide
    const startTime = performance.now();

    console.log(`[FILMSTRIP] Seat ${seatPosition} sliding from x=${startX.toFixed(0)} to x=${targetX} (hand ${targetHandIndex}, wipe ${wipeDirection})`);

    const animateFrame = () => {
      if (filmstrip.destroyed) {
        resolve();
        return;
      }

      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);

      filmstrip.x = startX + (targetX - startX) * eased;

      // === ASYMMETRIC IRIS ANIMATION ===
      // Animate left/right edges independently based on carousel direction
      if (irisEnabled && maskState && mask && !mask.destroyed) {
        let currentLeftEdge: number;
        let currentRightEdge: number;

        // Determine target edges based on arriving hand bounds (or seat bounds if no arriving bounds)
        const arrivingLeft = arrivingHandBounds?.left ?? seatLeftEdge;
        const arrivingRight = arrivingHandBounds?.right ?? seatRightEdge;

        // Calculate final target edges (at least seat bounds, expanded for arriving hand)
        const targetLeftEdge = Math.min(seatLeftEdge, arrivingLeft);
        const targetRightEdge = Math.max(seatRightEdge, arrivingRight);

        // Get departing hand bounds for clamping (to never clip outgoing cards)
        const departingLeft = departingHandBounds?.left ?? seatLeftEdge;
        const departingRight = departingHandBounds?.right ?? seatRightEdge;

        // Clamp fade: departing hand is fully visible at start, off-screen by end
        // Fade the clamp influence to 0 as animation progresses
        const clampInfluence = 1 - progress;

        if (isMovingLeft) {
          // Carousel moves LEFT: right edge is DEPARTING, left edge is ARRIVING
          // DEPARTING (right) edge: two-phase animation
          if (progress < 0.5) {
            // Phase 1: Contract to seat edge
            const contractProgress = progress / 0.5;
            const contractEased = 1 - Math.pow(1 - contractProgress, 2);
            currentRightEdge = startRightEdge + (seatRightEdge - startRightEdge) * contractEased;
          } else {
            // Phase 2: Expand to arriving hand if needed
            const expandProgress = (progress - 0.5) / 0.5;
            const expandEased = 1 - Math.pow(1 - expandProgress, 2);
            currentRightEdge = seatRightEdge + (targetRightEdge - seatRightEdge) * expandEased;
          }

          // ARRIVING (left) edge: linear interpolation to target
          const rawLeftEdge = startLeftEdge + (targetLeftEdge - startLeftEdge) * progress;
          // Clamp fades out: at start, don't clip departing hand; at end, no clamp
          const clampedLeftEdge = Math.min(rawLeftEdge, departingLeft);
          currentLeftEdge = rawLeftEdge + (clampedLeftEdge - rawLeftEdge) * clampInfluence;
        } else {
          // Carousel moves RIGHT: left edge is DEPARTING, right edge is ARRIVING
          // DEPARTING (left) edge: two-phase animation
          if (progress < 0.5) {
            // Phase 1: Contract to seat edge
            const contractProgress = progress / 0.5;
            const contractEased = 1 - Math.pow(1 - contractProgress, 2);
            currentLeftEdge = startLeftEdge + (seatLeftEdge - startLeftEdge) * contractEased;
          } else {
            // Phase 2: Expand to arriving hand if needed
            const expandProgress = (progress - 0.5) / 0.5;
            const expandEased = 1 - Math.pow(1 - expandProgress, 2);
            currentLeftEdge = seatLeftEdge + (targetLeftEdge - seatLeftEdge) * expandEased;
          }

          // ARRIVING (right) edge: linear interpolation to target
          const rawRightEdge = startRightEdge + (targetRightEdge - startRightEdge) * progress;
          // Clamp fades out: at start, don't clip departing hand; at end, no clamp
          const clampedRightEdge = Math.max(rawRightEdge, departingRight);
          currentRightEdge = rawRightEdge + (clampedRightEdge - rawRightEdge) * clampInfluence;
        }

        // Update mask state and redraw (top/bottom stay at seat boundaries during carousel)
        maskState.leftEdge = currentLeftEdge;
        maskState.rightEdge = currentRightEdge;
        redrawMaskElastic(mask, currentLeftEdge, currentRightEdge, maskState.topEdge, maskState.bottomEdge);

        if (MASK_DEBUG_ENABLED && debugBorder && !debugBorder.destroyed) {
          updateMaskDebugBorderElastic(debugBorder, currentLeftEdge, currentRightEdge, maskState.topEdge, maskState.bottomEdge);
        }
      }

      if (progress < 1) {
        requestAnimationFrame(animateFrame);
      } else {
        filmstrip.x = targetX; // Snap to exact position

        // Snap scorebox to target hand's score
        if (scene.scoreboxLayer) {
          const scoreboxLabel = `scorebox-${seatPosition}`;
          const scorebox = scene.scoreboxLayer.children.find(c => c.label === scoreboxLabel) as Container | undefined;
          if (scorebox && !scorebox.destroyed) {
            const scoreText = scorebox.children.find(c => c instanceof Text) as Text | undefined;
            if (scoreText && !scoreText.destroyed) {
              // Get the score for the target hand
              const targetScore = getLatestScore(seatPosition, targetHandIndex);
              if (targetScore) {
                const { score, isSoft } = targetScore;
                const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();
                // Soft hands show both values (e.g., "08/18"), unless 21
                let newScoreText: string;
                if (isSoft && score < 21) {
                  const lowValue = score - 10;
                  newScoreText = `${padScore(lowValue)}/${padScore(score)}`;
                } else {
                  newScoreText = padScore(score);
                }
                scoreText.text = newScoreText;
                MODULE_SCOREBOX_CACHE.set(`${seatPosition}`, newScoreText);
                console.log(`[FILMSTRIP] Scorebox snapped to hand ${targetHandIndex} score: "${newScoreText}"`);
              }
            }
          }
        }

        // Clear wipe-in-progress flag now that navigation is complete
        MODULE_SCOREBOX_WIPE_IN_PROGRESS.delete(seatPosition);

        if (irisEnabled && maskState) {
          // Sync all four edges (including top/bottom) to the arriving hand's actual bounds
          // During carousel, only left/right were animated; now sync top/bottom too
          adjustMaskToHandBounds(scene, seatPosition, targetHandIndex);
          console.log(`[IRIS] Carousel complete: mask edges synced to hand ${targetHandIndex}`);
        }

        console.log(`[FILMSTRIP] Seat ${seatPosition} slide complete (x=${targetX})`);
        resolve();
      }
    };

    requestAnimationFrame(animateFrame);
  });
}

// ===========================================
// RAIL-TO-KITTY CHIP ANIMATION TIMING
// ===========================================
// Shared timing constants for chip animations from rail to kitty.
// Used by both initial bet (animateRailToKitty) and split (animateSplitAction).
const RAIL_TO_KITTY_TIMING = {
  /** Time between chip animation starts (ms) - matches DEFAULT_STAGGER_CONFIG.betConfirmChip */
  stagger: 80,
  /** Duration of each chip flight animation (ms) - matches chipFlight default */
  duration: 200,
} as const;

/**
 * Configuration for a single chip in a batch rail-to-kitty animation.
 */
interface RailToKittyChipConfig {
  /** The chip sprite to animate */
  sprite: Sprite;
  /** Chip denomination (for kitty stacking/tracking) */
  denomination: number;
  /** Start position (rail) in animation layer coordinates */
  startPos: { x: number; y: number };
  /** End position (kitty) in animation layer coordinates */
  endPos: { x: number; y: number };
  /** Start size (rail chip size) */
  startSize: number;
  /** End size (kitty chip size) */
  endSize: number;
  /** Called when this chip lands - sprite is NOT destroyed, caller must handle */
  onLand?: (denomination: number, sprite: Sprite) => void;
}

/**
 * Configuration for batch rail-to-kitty animation.
 */
interface RailToKittyBatchConfig {
  /** The layer to animate chips in */
  animationLayer: Container;
  /** Array of chip configurations */
  chips: RailToKittyChipConfig[];
  /** Optional duration override (default: RAIL_TO_KITTY_TIMING.duration) */
  duration?: number;
  /** Optional stagger override (default: RAIL_TO_KITTY_TIMING.stagger) */
  stagger?: number;
  /** Debug label for logging */
  debugLabel?: string;
}

/**
 * Batch animate chips from rail to kitty with stagger timing.
 * Uses animateChipFlight for each chip with consistent behavior.
 *
 * @param config - Batch animation configuration
 * @returns Promise that resolves when all chips complete
 */
function animateChipBatchRailToKitty(config: RailToKittyBatchConfig): Promise<void> {
  const {
    animationLayer,
    chips,
    duration = RAIL_TO_KITTY_TIMING.duration,
    stagger = RAIL_TO_KITTY_TIMING.stagger,
    debugLabel = 'batch-rail-to-kitty',
  } = config;

  if (chips.length === 0) {
    return Promise.resolve();
  }

  // Create staggered promises for parallel execution
  const chipPromises = chips.map((chipConfig, index) => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        animateChipFlight({
          sprite: chipConfig.sprite,
          animationLayer,
          startPos: chipConfig.startPos,
          endPos: chipConfig.endPos,
          startSize: chipConfig.startSize,
          endSize: chipConfig.endSize,
          duration,
          zIndex: 2000 + index,
          destroyOnComplete: false, // Caller handles adding to kitty
          debugLabel: `${debugLabel}-${chipConfig.denomination}`,
          onComplete: () => {
            // Sound: split additional-bet chip arrives in the (split) player kitty
            playTableSound('chipLand');
            chipConfig.onLand?.(chipConfig.denomination, chipConfig.sprite);
            resolve();
          },
        });
      }, index * stagger);
    });
  });

  return Promise.all(chipPromises).then(() => {});
}

// ===========================================
// SPLIT HAND KITTY CENTERING
// ===========================================
// When multiple hands exist (after split), all kitties should be
// centered as a group based on actual pill widths with consistent padding.
const SPLIT_KITTY_SPACING = 144; // Legacy fixed spacing - kept for fallback
const SPLIT_KITTY_PILL_PADDING = 2; // Gap between pill edges (px)

/**
 * Calculate the tangent offset for a kitty to center it with N total kitties.
 * LEGACY: Uses fixed spacing. Prefer calculateDynamicKittyOffsets for pill-based spacing.
 * @param handIndex - The hand index (0 = main, 1-3 = splits)
 * @param totalHands - Total number of hands (1-4)
 * @returns Tangent offset from base kitty position
 */
function calculateCenteredKittyOffset(handIndex: number, totalHands: number): number {
  if (totalHands <= 1) return 0;
  // Center formula: offset = (i - (N-1)/2) * spacing
  return (handIndex - (totalHands - 1) / 2) * SPLIT_KITTY_SPACING;
}

/**
 * Get the width of a kitty's counter pill.
 * @param pill - The pill Graphics object
 * @returns Width of the pill, or default minimum if not measurable
 */
function getKittyPillWidth(pill: Graphics | null | undefined): number {
  if (!pill || pill.destroyed) return 40; // Minimum default width
  // Use the pill's local bounds to get actual rendered width
  const bounds = pill.getLocalBounds();
  return Math.max(bounds.width, 40); // Ensure minimum width
}

/**
 * Calculate dynamic tangent offsets for all kitties based on actual pill widths.
 * Pills are positioned with SPLIT_KITTY_PILL_PADDING gap between edges.
 * The group is centered around the base kitty position.
 * @param pillWidths - Array of pill widths for each hand (index 0 = main, 1+ = splits)
 * @param padding - Gap between pill edges (default: SPLIT_KITTY_PILL_PADDING)
 * @returns Array of tangent offsets for each hand
 */
function calculateDynamicKittyOffsets(pillWidths: number[], padding: number = SPLIT_KITTY_PILL_PADDING): number[] {
  if (pillWidths.length <= 1) return [0];

  // Calculate total width: sum of pill widths + gaps between them
  const totalPillWidth = pillWidths.reduce((sum, w) => sum + w, 0);
  const totalGaps = (pillWidths.length - 1) * padding;
  const totalWidth = totalPillWidth + totalGaps;

  // Calculate center positions for each pill
  // Start from left edge of group, place each pill's center
  const offsets: number[] = [];
  let currentX = -totalWidth / 2; // Start at left edge of centered group

  for (let i = 0; i < pillWidths.length; i++) {
    const pillWidth = pillWidths[i];
    const pillCenter = currentX + pillWidth / 2;
    offsets.push(pillCenter);
    currentX += pillWidth + padding; // Move to next pill's left edge
  }

  return offsets;
}

function animateSprite(
  sprite: Container,
  target: { x?: number; y?: number; rotation?: number; alpha?: number; scaleX?: number; scaleY?: number },
  options: { duration: number; easing?: (t: number) => number; onComplete?: () => void; debugName?: string }
) {
  // Track if animation has completed (to prevent double-calling onComplete)
  let resolved = false;
  const debugName = options.debugName || 'sprite';

  // Helper to snap sprite to final position (if it still exists)
  const snapToFinal = () => {
    if (sprite && !sprite.destroyed) {
      if (target.x !== undefined) sprite.x = target.x;
      if (target.y !== undefined) sprite.y = target.y;
      if (target.rotation !== undefined) sprite.rotation = target.rotation;
      if (target.alpha !== undefined) sprite.alpha = target.alpha;
      if (target.scaleX !== undefined) sprite.scale.x = target.scaleX;
      if (target.scaleY !== undefined) sprite.scale.y = target.scaleY;
    }
  };

  // Unified finalize function - ensures onComplete is called exactly once
  const finalize = (reason: 'complete' | 'timeout' | 'destroyed' | 'tab-hidden') => {
    if (resolved) return;
    resolved = true;
    clearTimeout(safetyTimeout);

    // Snap to final position
    snapToFinal();

    // Log failures to admin panel (not 'complete' which is normal)
    if (reason !== 'complete') {
      recordAnimationFailure({
        fileName: 'PixiGameTableV1.tsx',
        functionName: 'animateSprite',
        reason: reason === 'tab-hidden' ? 'timeout' : reason,
        animationType: 'sprite-animation',
        targetDescription: debugName,
        expectedDuration: options.duration,
      });
    }

    options.onComplete?.();
  };

  // GUARANTEED RESOLUTION: Safety timeout ensures animation always completes
  // Even if requestAnimationFrame never fires, sprite is destroyed, or errors occur
  const safetyTimeout = setTimeout(() => {
    if (!resolved) {
      finalize('timeout');
    }
  }, options.duration + 500); // Animation duration + 500ms buffer

  // Early exit: Tab hidden at start - snap immediately
  if (document.hidden) {
    finalize('tab-hidden');
    return;
  }

  // Early exit: Sprite already destroyed
  if (!sprite || sprite.destroyed) {
    finalize('destroyed');
    return;
  }

  // Capture start values
  const startX = sprite.x;
  const startY = sprite.y;
  const startRotation = sprite.rotation;
  const startAlpha = sprite.alpha;
  const startScaleX = sprite.scale.x;
  const startScaleY = sprite.scale.y;

  const deltaX = target.x !== undefined ? target.x - startX : 0;
  const deltaY = target.y !== undefined ? target.y - startY : 0;
  const deltaRotation = target.rotation !== undefined ? target.rotation - startRotation : 0;
  const deltaAlpha = target.alpha !== undefined ? target.alpha - startAlpha : 0;
  const deltaScaleX = target.scaleX !== undefined ? target.scaleX - startScaleX : 0;
  const deltaScaleY = target.scaleY !== undefined ? target.scaleY - startScaleY : 0;

  const startTime = performance.now();
  const easing = options.easing || easeOutCubic;

  const animate = () => {
    // Already resolved (by timeout, visibility change, etc.)
    if (resolved) return;

    // Sprite destroyed mid-animation
    if (!sprite || sprite.destroyed) {
      finalize('destroyed');
      return;
    }

    // Tab hidden mid-animation - snap to final
    if (document.hidden) {
      finalize('tab-hidden');
      return;
    }

    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / options.duration, 1);
    const eased = easing(progress);

    // Update sprite position
    if (target.x !== undefined) sprite.x = startX + deltaX * eased;
    if (target.y !== undefined) sprite.y = startY + deltaY * eased;
    if (target.rotation !== undefined) sprite.rotation = startRotation + deltaRotation * eased;
    if (target.alpha !== undefined) sprite.alpha = startAlpha + deltaAlpha * eased;
    if (target.scaleX !== undefined) sprite.scale.x = startScaleX + deltaScaleX * eased;
    if (target.scaleY !== undefined) sprite.scale.y = startScaleY + deltaScaleY * eased;

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      finalize('complete');
    }
  };

  requestAnimationFrame(animate);
}

// ===========================================
// CHIP FLIGHT ANIMATION (REUSABLE REGISTRY)
// ===========================================
// Single source of truth for animating chips between any two points.
// Used by both player and opponent flows to ensure consistent behavior.

interface ChipFlightConfig {
  sprite: Sprite;
  animationLayer: Container;
  startPos: { x: number; y: number };
  endPos: { x: number; y: number };
  startSize: number;
  endSize: number;
  duration: number;
  zIndex?: number;
  startRotation?: number;  // If undefined, uses sprite's current rotation
  endRotation?: number;    // If undefined, generates random rotation
  onProgress?: (progress: number) => void; // Called each frame with progress 0-1
  onComplete?: () => void; // Called after animation, before sprite cleanup
  destroyOnComplete?: boolean; // Default true - destroy sprite when done
  debugLabel?: string;     // For logging/debugging
}

/**
 * Animates a chip sprite from one position to another with size and rotation interpolation.
 * Handles in-flight tracking, guaranteed resolution, and proper cleanup.
 *
 * @returns Promise that resolves when animation completes (or times out)
 */
function animateChipFlight(config: ChipFlightConfig): Promise<void> {
  const {
    sprite,
    animationLayer,
    startPos,
    endPos,
    startSize,
    endSize,
    duration,
    zIndex = 2000,
    startRotation = sprite.rotation,
    endRotation = Math.random() * Math.PI * 2,
    onProgress,
    onComplete,
    destroyOnComplete = true,
    debugLabel = 'chip-flight',
  } = config;

  return new Promise((resolve) => {
    // Early exit: invalid inputs
    if (!sprite || sprite.destroyed || !animationLayer) {
      onComplete?.();
      resolve();
      return;
    }

    // Position sprite at start
    sprite.x = startPos.x;
    sprite.y = startPos.y;
    sprite.width = startSize;
    sprite.height = startSize;
    sprite.rotation = startRotation;
    sprite.zIndex = zIndex;

    // Add to animation layer and track in-flight
    if (!sprite.parent) {
      animationLayer.addChild(sprite);
    }
    MODULE_CHIP_SPRITES_IN_FLIGHT.add(sprite);

    // Track resolution state
    let resolved = false;

    // Snap to final state helper
    const snapToFinal = () => {
      if (sprite && !sprite.destroyed) {
        sprite.x = endPos.x;
        sprite.y = endPos.y;
        sprite.width = endSize;
        sprite.height = endSize;
        sprite.rotation = endRotation;
      }
    };

    // Unified finalize function
    const finalize = (reason: 'complete' | 'timeout' | 'destroyed' | 'tab-hidden') => {
      if (resolved) return;
      resolved = true;
      clearTimeout(safetyTimeout);

      // Snap to final position
      snapToFinal();

      // Log failures
      if (reason !== 'complete') {
        recordAnimationFailure({
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateChipFlight',
          reason: reason === 'tab-hidden' ? 'timeout' : reason,
          animationType: debugLabel,
          targetDescription: `${startPos.x.toFixed(0)},${startPos.y.toFixed(0)} → ${endPos.x.toFixed(0)},${endPos.y.toFixed(0)}`,
          expectedDuration: duration,
        });
      }

      // Remove from in-flight tracking
      MODULE_CHIP_SPRITES_IN_FLIGHT.delete(sprite);

      // Call completion callback (for kitty cleanup, etc.)
      onComplete?.();

      // Cleanup sprite
      if (destroyOnComplete && sprite && !sprite.destroyed) {
        animationLayer.removeChild(sprite);
        sprite.destroy();
      }

      resolve();
    };

    // Safety timeout
    const safetyTimeout = setTimeout(() => {
      if (!resolved) finalize('timeout');
    }, duration + 500);

    // Early exit: tab hidden
    if (document.hidden) {
      finalize('tab-hidden');
      return;
    }

    const startTime = performance.now();

    const animate = () => {
      if (resolved) return;

      if (!sprite || sprite.destroyed) {
        finalize('destroyed');
        return;
      }

      if (document.hidden) {
        finalize('tab-hidden');
        return;
      }

      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      // Interpolate position
      sprite.x = startPos.x + (endPos.x - startPos.x) * eased;
      sprite.y = startPos.y + (endPos.y - startPos.y) * eased;

      // Interpolate size
      const currentSize = startSize + (endSize - startSize) * eased;
      sprite.width = currentSize;
      sprite.height = currentSize;

      // Interpolate rotation
      sprite.rotation = startRotation + (endRotation - startRotation) * eased;

      // Notify progress callback
      onProgress?.(progress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        finalize('complete');
      }
    };

    requestAnimationFrame(animate);
  });
}

// ===========================================
// RAKE ANIMATION HELPERS
// ===========================================

/** Rake animation configuration - timing values from config store */
const RAKE_CONFIG = {
  controlPointPull: 1.1,  // Control point is pulled 10% past dealer center
  controlPointY: 0.35,    // Control point Y at 35% of canvas height
  destinationYOffset: -200, // 200px above dealer position
  // Timing values - read from config at runtime
  get duration() {
    return getAnimationDuration('cardsCleared', undefined, {
      fileName: 'PixiGameTableV1.tsx',
      functionName: 'RAKE_CONFIG.duration',
      eventType: 'CARDS_CLEARED',
    });
  },
  get staggerDelay() {
    return getStaggerValue('cardRake', undefined, {
      fileName: 'PixiGameTableV1.tsx',
      functionName: 'RAKE_CONFIG.staggerDelay',
      eventType: 'CARDS_CLEARED',
    });
  },
};

/** Quadratic Bezier interpolation */
function quadraticBezier(
  p0: { x: number; y: number },
  p1: { x: number; y: number },  // Control point
  p2: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const oneMinusT = 1 - t;
  return {
    x: oneMinusT * oneMinusT * p0.x + 2 * oneMinusT * t * p1.x + t * t * p2.x,
    y: oneMinusT * oneMinusT * p0.y + 2 * oneMinusT * t * p1.y + t * t * p2.y,
  };
}

/** Calculate arc control point for rake animation based on card start position */
function calculateRakeControlPoint(
  startX: number,
  startY: number,
  dealerX: number
): { x: number; y: number } {
  // Control point X: pulled toward dealer center (and 10% past it)
  const controlX = startX + (dealerX - startX) * RAKE_CONFIG.controlPointPull;
  // Control point Y: 35% from top of canvas
  const controlY = CANVAS_HEIGHT * RAKE_CONFIG.controlPointY;
  return { x: controlX, y: controlY };
}

/** Animate a sprite along a Bezier arc with rotation interpolation */
function animateSpriteArc(
  sprite: Container,
  start: { x: number; y: number; rotation: number },
  end: { x: number; y: number },
  control: { x: number; y: number },
  options: { duration: number; easing?: (t: number) => number; onComplete?: () => void; debugName?: string }
) {
  // GUARANTEED RESOLUTION: Track if we've already resolved
  let resolved = false;
  const debugName = options.debugName || 'arc-sprite';

  // GUARANTEED RESOLUTION: Snap sprite to final position (safe even if destroyed)
  const snapToFinal = () => {
    if (sprite && !sprite.destroyed) {
      sprite.x = end.x;
      sprite.y = end.y;
      sprite.rotation = 0; // Cards straighten to 0 during arc
    }
  };

  // GUARANTEED RESOLUTION: Single point of completion - always called exactly once
  const finalize = (reason: 'complete' | 'timeout' | 'destroyed' | 'tab-hidden') => {
    if (resolved) return; // Already finalized
    resolved = true;

    // Clear safety timeout
    clearTimeout(safetyTimeout);

    // Remove visibility handler
    document.removeEventListener('visibilitychange', visibilityHandler);

    // Always snap to final position (safe no-op if sprite destroyed)
    snapToFinal();

    // Log failures (not normal completions)
    if (reason !== 'complete') {
      recordAnimationFailure({
        fileName: 'PixiGameTableV1.tsx',
        functionName: 'animateSpriteArc',
        reason: reason === 'tab-hidden' ? 'timeout' : reason,
        animationType: 'arc-animation',
        targetDescription: debugName,
        expectedDuration: options.duration,
      });
    }

    // ALWAYS call onComplete to resolve promises
    options.onComplete?.();
  };

  // GUARANTEED RESOLUTION: Safety timeout - if animation hasn't resolved after duration + buffer, force it
  const safetyTimeout = setTimeout(() => {
    if (!resolved) {
      finalize('timeout');
    }
  }, options.duration + 500); // 500ms buffer for slow frames

  // VISIBILITY CHANGE HANDLER: When tab becomes visible, check if sprite was destroyed
  const visibilityHandler = () => {
    if (!document.hidden && !resolved) {
      if (!sprite || sprite.destroyed) {
        finalize('destroyed');
      }
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  // FAST PATH: Skip animation when tab is hidden
  if (document.hidden) {
    finalize('tab-hidden');
    return;
  }

  // FAST PATH: Skip animation if sprite already destroyed
  if (!sprite || sprite.destroyed) {
    finalize('destroyed');
    return;
  }

  const startTime = performance.now();
  const easing = options.easing || easeInOutCubic;
  const startRotation = start.rotation;

  const animate = () => {
    // Already finalized (e.g., by safety timeout or visibility handler)
    if (resolved) return;

    // Check if sprite was destroyed during animation
    if (!sprite || sprite.destroyed) {
      finalize('destroyed');
      return;
    }

    // MID-ANIMATION TAB SWITCH: Snap to final if tab becomes hidden
    if (document.hidden) {
      finalize('tab-hidden');
      return;
    }

    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / options.duration, 1);
    const eased = easing(progress);

    // Interpolate position along Bezier curve
    const pos = quadraticBezier(
      { x: start.x, y: start.y },
      control,
      end,
      eased
    );
    sprite.x = pos.x;
    sprite.y = pos.y;

    // Interpolate rotation toward 0 (cards straighten during flight)
    sprite.rotation = startRotation * (1 - eased);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      finalize('complete');
    }
  };

  requestAnimationFrame(animate);
}

// ===========================================
// SPRITE FACTORIES
// ===========================================

function createChipSprite(
  denomination: number,
  textures: Map<string, Texture>,
  x: number = 0,
  y: number = 0
): Sprite | null {
  const texture = textures.get(`chip-${denomination}`);
  if (!texture) return null;

  const sprite = new Sprite(texture);
  sprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
  sprite.width = CHIP_SIZE;
  sprite.height = CHIP_SIZE;
  sprite.anchor.set(0.5);
  sprite.x = x;
  sprite.y = y;
  return sprite;
}

function createCardSprite(
  card: CardType,
  textures: Map<string, Texture>,
  x: number = 0,
  y: number = 0,
  rotation: number = 0,
  config: CardConfig = DEFAULT_CARD_CONFIG
): Container {
  const container = new Container();
  container.x = x;
  container.y = y;
  container.rotation = degreesToRadians(rotation);
  // Don't intercept pointer events - let clicks pass through to seats below
  container.eventMode = 'none';

  // Apply scale from config
  const scaleFactor = config.scale / 100;
  const cardWidth = CARD_WIDTH * scaleFactor;
  const cardHeight = CARD_HEIGHT * scaleFactor;

  if (!card.faceUp) {
    const backTexture = textures.get('cardBack');
    if (backTexture) {
      // Card shadow for depth (matching face-up cards)
      const shadow = new Graphics();
      shadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, 8 * scaleFactor);
      shadow.fill({ color: 0x000000, alpha: 0.3 });
      container.addChild(shadow);

      // White background to fill any gaps at corners
      const bg = new Graphics();
      bg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
      bg.fill({ color: 0xfefefe });
      container.addChild(bg);

      // Rounded rectangle mask for card back (matching face-up card corners)
      const mask = new Graphics();
      mask.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
      mask.fill({ color: 0xffffff });
      container.addChild(mask);

      const back = new Sprite(backTexture);
      back.width = cardWidth;
      back.height = cardHeight;
      back.anchor.set(0.5);
      back.mask = mask;
      container.addChild(back);

      // Border overlay to cover any sharp corners (matching face-up cards)
      const border = new Graphics();
      border.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
      border.stroke({ color: 0x374151, width: config.borderThickness });
      container.addChild(border);
    }
    return container;
  }

  // Card shadow for depth
  const shadow = new Graphics();
  shadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, 8 * scaleFactor);
  shadow.fill({ color: 0x000000, alpha: 0.3 });
  container.addChild(shadow);

  // Card background - crisp white with dark border
  const bg = new Graphics();
  bg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
  bg.fill({ color: 0xfefefe }); // Slightly off-white for realism
  bg.stroke({ color: 0x374151, width: config.borderThickness }); // Configurable border
  container.addChild(bg);

  const isFaceCard = ['A', 'J', 'Q', 'K'].includes(card.rank);

  if (isFaceCard) {
    const textureKey = `${card.rank}-${card.suit}`;
    const faceTexture = textures.get(textureKey);
    if (faceTexture) {
      const faceSprite = new Sprite(faceTexture);
      faceSprite.width = 66 * scaleFactor;
      faceSprite.height = 94 * scaleFactor;
      faceSprite.anchor.set(0.5);
      container.addChild(faceSprite);
    }
  } else {
    // Center symbol for number cards
    const centerStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: config.centerSuitSize,
      fill: SUIT_COLORS[card.suit],
    });
    const centerText = new Text({ text: SUIT_SYMBOLS[card.suit], style: centerStyle });
    centerText.anchor.set(0.5);
    centerText.y = -4 * scaleFactor;
    container.addChild(centerText);
  }

  // Corner rank style
  const cornerRankStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: config.cornerFontSize,
    fontWeight: 'bold',
    fill: SUIT_COLORS[card.suit],
  });

  // Corner suit style (can be different size)
  const cornerSuitStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: config.cornerSuitSize,
    fontWeight: 'bold',
    fill: SUIT_COLORS[card.suit],
  });

  // Top-left corner (tight to edge): rank on top, suit below
  const rankText = new Text({ text: card.rank, style: cornerRankStyle });
  rankText.x = -cardWidth / 2 + 2 * scaleFactor;
  rankText.y = -cardHeight / 2 + 1 * scaleFactor;
  container.addChild(rankText);

  const suitText = new Text({ text: SUIT_SYMBOLS[card.suit], style: cornerSuitStyle });
  suitText.x = -cardWidth / 2 + 2 * scaleFactor;
  suitText.y = -cardHeight / 2 + 1 * scaleFactor + config.cornerFontSize + config.cornerGap;
  container.addChild(suitText);

  // Bottom-right corner (rotated 180): rank at corner, suit toward center
  const rankText2 = new Text({ text: card.rank, style: cornerRankStyle });
  rankText2.x = cardWidth / 2 - 2 * scaleFactor;
  rankText2.y = cardHeight / 2 - 1 * scaleFactor;
  rankText2.anchor.set(0, 0);
  rankText2.rotation = Math.PI;
  container.addChild(rankText2);

  const suitText2 = new Text({ text: SUIT_SYMBOLS[card.suit], style: cornerSuitStyle });
  suitText2.x = cardWidth / 2 - 2 * scaleFactor;
  suitText2.y = cardHeight / 2 - 1 * scaleFactor - config.cornerFontSize - config.cornerGap;
  suitText2.anchor.set(0, 0);
  suitText2.rotation = Math.PI;
  container.addChild(suitText2);

  return container;
}

// ===========================================
// CHIP STACK RENDERING
// ===========================================

function createChipStack(
  amount: number,
  textures: Map<string, Texture>,
  x: number = 0,
  y: number = 0
): Container {
  const container = new Container();
  container.x = x;
  container.y = y;

  if (amount <= 0) return container;

  // Break amount into chip denominations (greedy algorithm)
  const denoms = [5000, 1000, 500, 100, 25, 5, 1];
  const chips: number[] = [];
  let remaining = amount;

  for (const denom of denoms) {
    while (remaining >= denom && chips.length < 10) { // Max 10 chips visual
      chips.push(denom);
      remaining -= denom;
    }
  }

  // Stack chips vertically with slight offset
  chips.forEach((denom, i) => {
    const chipSprite = createChipSprite(denom, textures, 0, -i * 3);
    if (chipSprite) {
      // Random rotation for natural chip appearance (0-359 degrees)
      chipSprite.rotation = Math.random() * 2 * Math.PI;
      container.addChild(chipSprite);
    }
  });

  // Amount label below stack
  if (amount > 0) {
    const labelStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 11,
      fontWeight: 'bold',
      fill: 0xffffff,
    });
    const label = new Text({ text: amount.toString(), style: labelStyle });
    label.anchor.set(0.5, 0);
    label.y = 8;
    container.addChild(label);
  }

  return container;
}

/**
 * Creates a display of chip stacks for static visualization.
 * Shows ONE chip per denomination, stacked vertically (highest value at top).
 * This represents the available denominations, not actual chip counts.
 */
function createSeatChipDisplay(
  textures: Map<string, Texture>,
  x: number = 0,
  y: number = 0,
  chipSize: number = 28,
  _chipsPerDenom: number = 1, // Ignored - always 1 chip per denom for static display
  verticalOffset: number = 8, // Vertical offset between denomination layers
  _fanOffset: number = 0, // Ignored - no horizontal fanning for static display
  borderConfig?: ChipBorderConfig, // Optional border configuration
  counterConfig?: ChipCounterConfig, // Optional counter configuration
  displayAmount?: number // Amount to display in counter (if configured)
): Container {
  const container = new Container();
  container.x = x;
  container.y = y;

  // Stack denominations vertically, smallest at bottom, largest at top
  // Sort ascending so we can place smallest first at the bottom
  const sortedDenoms = [...CHIP_DENOMS].sort((a, b) => a - b);

  // Calculate total stack height
  const totalHeight = (sortedDenoms.length - 1) * verticalOffset + chipSize;

  // Draw border if configured
  if (borderConfig && borderConfig.opacity > 0 && borderConfig.thickness > 0) {
    const border = new Graphics();
    const padding = 4; // Extra padding around chips
    const borderWidth = chipSize + padding * 2;
    const borderHeight = totalHeight + padding * 2;

    // Parse hex color to number
    const colorNum = parseInt(borderConfig.color.replace('#', ''), 16);

    border.roundRect(-borderWidth / 2, -borderHeight / 2, borderWidth, borderHeight, 6);
    border.stroke({
      color: colorNum,
      width: borderConfig.thickness,
      alpha: borderConfig.opacity / 100
    });
    border.zIndex = -1; // Behind chips
    container.addChild(border);
  }

  sortedDenoms.forEach((denom, denomIndex) => {
    const texture = textures.get(`chip-${denom}`);
    if (!texture) return;

    // Y position: smallest (index 0) at top (negative Y), largest at bottom (positive Y)
    // This creates a stack where largest chips form the base and smallest are on top
    const chipY = totalHeight / 2 - denomIndex * verticalOffset - chipSize / 2;

    const chip = new Sprite(texture);
    chip.width = chipSize;
    chip.height = chipSize;
    chip.anchor.set(0.5);
    chip.x = 0; // Centered, no horizontal offset
    chip.y = chipY;
    // Random rotation for natural chip appearance (0-359 degrees)
    chip.rotation = Math.random() * 2 * Math.PI;
    chip.zIndex = sortedDenoms.length - 1 - denomIndex; // Smallest denominations render on top
    container.addChild(chip);
  });

  // Add counter below chip stack if configured
  if (counterConfig && counterConfig.show && displayAmount !== undefined) {
    const counterY = totalHeight / 2 + 10; // Below the chip stack

    // Counter pill background (if opacity > 0)
    if (counterConfig.pillOpacity > 0) {
      const pillPadding = { x: 6, y: 3 };
      const pillWidth = chipSize + pillPadding.x * 2;
      const pillHeight = counterConfig.fontSize + pillPadding.y * 2;

      const pill = new Graphics();
      pill.roundRect(-pillWidth / 2, counterY - pillPadding.y, pillWidth, pillHeight, 8);
      pill.fill({ color: 0x000000, alpha: counterConfig.pillOpacity / 100 });
      pill.zIndex = 50;
      container.addChild(pill);
    }

    // Counter text
    const fontColor = parseInt(counterConfig.fontColor.replace('#', ''), 16);
    const counterStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: counterConfig.fontSize,
      fontWeight: 'bold',
      fill: fontColor,
    });

    // Format amount with commas
    const formattedAmount = displayAmount.toLocaleString();
    const counterText = new Text({ text: formattedAmount, style: counterStyle });
    counterText.anchor.set(0.5, 0);
    counterText.x = 0;
    counterText.y = counterY;
    counterText.zIndex = 51;
    container.addChild(counterText);
  }

  // Sort children by zIndex for proper layering
  container.sortableChildren = true;

  return container;
}

// ===========================================
// KITTY DISPLAY SYSTEM
// ===========================================
// Creates and manages persistent kitty displays that show chips bet into the pot.
// Chips animate to kitty and remain visible, stacking vertically.
// Counter upticks as each chip lands.

/** Kitty display configuration - matches DEV CONTROLS styling */
const KITTY_CONFIG = {
  chipSize: 28,           // Same as kittyMedium
  verticalOffset: 8,      // Vertical spacing between chip denominations
  borderPadding: 4,       // Padding around chips for border
  borderRadius: 6,        // Border corner radius
  borderColor: 0xffffff,  // White border (matches DEV CONTROLS)
  borderThickness: 2,     // Border width
  borderOpacity: 0.3,     // Subtle visibility
  counterFontSize: 12,    // Counter text size
  counterFontColor: 0xfb923c, // Orange (matches DEV CONTROLS)
  counterPillOpacity: 0.8, // Pill background alpha
  counterOffsetY: 10,     // Gap between chips and counter
};

/**
 * Creates an empty kitty display container.
 * The container starts visible immediately (chips will be added to it).
 * @param counterConfig - Counter configuration from DEV CONTROLS (optional)
 */
function createKittyDisplay(
  textures: Map<string, Texture>,
  counterConfig?: ChipCounterConfig
): {
  container: Container;
  chipsWrapper: Container;  // Wrapper for chips/borders - can be rotated independently
  borderContainer: Container;  // Container for circular chip borders
  pill: Graphics;
  counterText: Text;
  chipContainer: Container;
} {
  const container = new Container();
  container.sortableChildren = true;
  container.visible = true; // Visible immediately - chips will populate it
  container.label = 'kittyContainer'; // Label to prevent orphan cleanup from destroying it

  // Chips wrapper - holds borderContainer and chipContainer
  // This wrapper can be rotated independently without affecting pill/counterText
  const chipsWrapper = new Container();
  chipsWrapper.sortableChildren = true;
  chipsWrapper.zIndex = 10;  // Below counter elements
  container.addChild(chipsWrapper);

  // Border container (holds circular borders behind chips)
  const borderContainer = new Container();
  borderContainer.sortableChildren = true;
  borderContainer.zIndex = 5;  // Behind chips (zIndex 10)
  chipsWrapper.addChild(borderContainer);

  // Chip container (holds the actual chip sprites)
  const chipContainer = new Container();
  chipContainer.sortableChildren = true;
  chipContainer.zIndex = 10;  // Above borders
  chipsWrapper.addChild(chipContainer);

  // Counter pill background - direct child of main container (not in wrapper)
  const pill = new Graphics();
  pill.zIndex = 50;
  container.addChild(pill);

  // Use DEV CONTROLS font size if provided, otherwise use default
  const fontSize = counterConfig?.fontSize ?? KITTY_CONFIG.counterFontSize;
  const fontColor = counterConfig?.fontColor ?? `#${KITTY_CONFIG.counterFontColor.toString(16).padStart(6, '0')}`;

  // Counter text - centered both horizontally and vertically
  // Direct child of main container (not in wrapper)
  const counterStyle = new TextStyle({
    fontFamily: 'Arial, sans-serif',
    fontSize: fontSize,
    fontWeight: 'bold',
    fill: fontColor,
  });
  const counterText = new Text({ text: '', style: counterStyle }); // Start empty, not '0'
  counterText.anchor.set(0.5, 0.5); // Center anchor for vertical centering in pill
  counterText.zIndex = 51;
  counterText.visible = false; // Hidden until chips are added
  container.addChild(counterText);

  return { container, chipsWrapper, borderContainer, pill, counterText, chipContainer };
}

/**
 * Updates the kitty counter position based on current chip count.
 * Stack is bottom-justified - chips grow upward, counter stays at fixed position below.
 * Note: Rectangular border removed - each chip now has its own circular border.
 * @param counterOffset - Optional x,y offset for counter position (from DEV CONTROLS)
 */
function updateKittyLayout(
  pill: Graphics,
  counterText: Text,
  chipCount: number,
  total: number,
  counterOffset: { x: number; y: number } = { x: 0, y: 0 },
  isOpponentKitty: boolean = false
): void {
  // DEFENSIVE CHECK: Skip if elements are destroyed (can happen during tab switch/resync)
  if (!pill || pill.destroyed || !counterText || counterText.destroyed) {
    return;
  }

  // OPPONENT KITTIES: Skip counter updates entirely - opponents don't show chip counters
  if (isOpponentKitty) {
    counterText.visible = false;
    pill.clear();
    return;
  }

  const { chipSize } = KITTY_CONFIG;
  const { counterPillOpacity, counterOffsetY } = KITTY_CONFIG;

  // Counter position is FIXED below the stack origin (doesn't move as stack grows)
  // Apply DEV CONTROLS offset
  const counterX = counterOffset.x;
  const counterY = counterOffsetY + counterOffset.y;

  // Show/hide counter based on chip count
  if (chipCount > 0) {
    counterText.visible = true;
    counterText.text = total.toLocaleString();

    // Measure text width to size pill dynamically - check destroyed again after text assignment
    if (counterText.destroyed) return;
    const textWidth = counterText.width;
    const pillPadding = { x: 5, y: 3 };
    const pillWidth = Math.max(chipSize, textWidth) + pillPadding.x * 2;
    const pillHeight = counterText.style.fontSize + pillPadding.y * 2;

    // Redraw pill sized to text, apply x offset
    pill.clear();
    pill.roundRect(-pillWidth / 2 + counterX, counterY - pillPadding.y, pillWidth, pillHeight, 8);
    pill.fill({ color: 0x000000, alpha: counterPillOpacity });

    // Center text within pill, apply x offset
    counterText.x = counterX;
    counterText.y = counterY - pillPadding.y + pillHeight / 2;
  } else {
    // Hide counter when no chips
    counterText.visible = false;
    pill.clear();
  }
}

// ===========================================
// KITTY CENTERING HELPERS
// ===========================================
// When both main bet and insurance kitty exist, they should be
// centered as a pair over the seat's kitty position.

/** Gap between main kitty counter right edge and insurance counter left edge - matches SPLIT_KITTY_PILL_PADDING */
const KITTY_PAIR_GAP = 2;

/**
 * Estimate counter pill width for a given amount.
 * Based on font size and typical text width.
 */
function estimateCounterPillWidth(amount: number, fontSize: number = KITTY_CONFIG.counterFontSize): number {
  const text = amount.toLocaleString();
  // Approximate character width at given font size (monospace-ish for numbers)
  const charWidth = fontSize * 0.6;
  const textWidth = text.length * charWidth;
  const pillPadding = 5 * 2; // x padding on each side
  return Math.max(KITTY_CONFIG.chipSize, textWidth) + pillPadding;
}

/**
 * Calculate tangent offset for main/insurance kitty positioning.
 *
 * Uses a centerline approach:
 * - Main bet is on the LEFT, right-justified to centerline with padding
 * - Insurance is on the RIGHT, left-justified to centerline with padding
 * - Each stack only depends on its OWN width, not the other's
 * - As pills grow, they grow OUTWARD from center
 *
 * @param amount - Bet amount (for counter width estimation)
 * @param padding - Padding from centerline (insuranceChipConfigs.x value)
 * @param isMainKitty - If true, returns offset for main kitty (negative = left); if false, for insurance
 * @param hasInsurance - Whether insurance exists (main kitty only shifts when insurance is present)
 */
function calculateKittyCenteringOffset(
  amount: number,
  padding: number,
  isMainKitty: boolean,
  hasInsurance: boolean = true
): number {
  // Main kitty only shifts when insurance exists
  if (isMainKitty && !hasInsurance) return 0;

  const pillWidth = estimateCounterPillWidth(amount);

  // Main bet: right edge at -padding, so center at -(padding + pillWidth/2)
  // Insurance: left edge at +padding, so center at +(padding + pillWidth/2)
  const offset = padding + pillWidth / 2;

  return isMainKitty ? -offset : offset;
}

/**
 * Apply tangent offset to a world position based on seat rotation.
 * Tangent direction is perpendicular to radial (left/right from seat's perspective).
 * @param pos - Current position { x, y }
 * @param offset - Offset along tangent (positive = right, negative = left)
 * @param rotRad - Seat rotation in radians
 */
function applyTangentOffset(
  pos: { x: number; y: number },
  offset: number,
  rotRad: number
): { x: number; y: number } {
  return {
    x: pos.x + offset * Math.cos(rotRad),
    y: pos.y + offset * Math.sin(rotRad),
  };
}

// Sidebet counter styling (matches kitty counter)
const SIDEBET_COUNTER_CONFIG = {
  fontSize: 12,                 // Same as KITTY_CONFIG.counterFontSize
  fontColor: 0xfb923c,          // Orange - same as KITTY_CONFIG.counterFontColor
  pillOpacity: 0.8,             // Same as KITTY_CONFIG.counterPillOpacity
  offsetY: 13,                  // Gap below sidebet icon
};

// ===========================================
// BUDDY HUD CONFIGURATION
// ===========================================
// Deck Penetration (DP) and True Count (TC) indicators
// Only visible to BUDDY subscribers
// ===========================================

const BUDDY_HUD_CONFIG = {
  // Positioning (relative to canvas) - at very top above dealer
  dpX: 250,                     // DP indicator X position (left side)
  tcX: 1065,                    // TC indicator X position (right side)
  y: 26,                        // Y position for both (near top of table)
  barY: 6,                      // Y offset for bar/ticks relative to container

  // Bar dimensions
  barWidth: 189,
  barHeight: 4,
  barRadius: 0,                 // No rounded corners (crisp edges at tick marks)

  // Colors
  barBgColor: 0xAAAAAA,         // Light grey background (matches ticks)
  barBgAlpha: 0.9,

  // 4-Zone color scheme (worst to best)
  // Zone 1 (Worst): TC -5 to -2.5, DP 0-25%
  zone1Color: 0xFF4444,         // Red
  // Zone 2: TC -2.5 to 0, DP 25-50%
  zone2Color: 0xFFA500,         // Orange
  // Zone 3: TC 0 to +2.5, DP 50-75%
  zone3Color: 0xFFDD00,         // Yellow
  // Zone 4 (Best): TC +2.5 to +5, DP 75-100%
  zone4Color: 0x44FF44,         // Green

  // Legacy colors (kept for compatibility)
  dpFillColor: 0x22c55e,        // Green for penetration (deprecated - using zones now)
  tcPositiveColor: 0x22c55e,    // Green for positive TC (player advantage)
  tcNegativeColor: 0xef4444,    // Red for negative TC (house advantage)
  tcNeutralColor: 0xfbbf24,     // Yellow for neutral TC

  // Text styling - matching reference (yellow/gold labels, green values)
  labelFontSize: 36,
  labelColor: 0xf59e0b,         // Yellow/gold for "DP" and "TC" labels
  valueFontSize: 16,
  valueColor: 0x22c55e,         // Green for value text

  // TC range
  tcMin: -5,
  tcMax: 5,

  // Tick marks
  tickColor: 0xAAAAAA,          // Light grey tick marks
  tickHeight: 6,
  tickLabelFontSize: 12,
  tickLabelColor: 0xAAAAAA,     // Light grey tick labels
};

interface BuddyHudConfigParam {
  dpX: number;
  tcX: number;
  y: number;
  barWidth: number;
  barHeight: number;
  labelFontSize: number;
  valueFontSize: number;
  tickLabelFontSize: number;
}

/**
 * Creates the BUDDY HUD elements (DP and TC indicators).
 * Returns all the graphics elements that need to be stored in scene.buddyHUD.
 */
function createBuddyHUD(configOverride?: BuddyHudConfigParam): {
  container: Container;
  dpContainer: Container;
  dpLabel: Text;
  dpValue: Text;
  dpBarBg: Graphics;
  dpBarFill: Graphics;
  dpTickMarks: Graphics;
  dpTickLabels: Text[];
  tcContainer: Container;
  tcLabel: Text;
  tcValue: Text;
  tcBarBg: Graphics;
  tcIndicator: Graphics;
  tcTickMarks: Graphics;
  tcTickLabels: Text[];
} {
  // Merge override config with defaults
  const config = {
    ...BUDDY_HUD_CONFIG,
    ...(configOverride ?? {}),
  };
  const container = new Container();
  container.visible = false; // Hidden by default, shown for BUDDY subscribers

  // ===========================================
  // DECK PENETRATION (DP) - Left side
  // ===========================================
  const dpContainer = new Container();
  dpContainer.x = config.dpX;
  dpContainer.y = config.y;

  // DP Label (yellow/gold, left of bar)
  const dpLabel = new Text({
    text: 'DP',
    style: {
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: config.labelFontSize,
      fontWeight: '900',
      fill: config.labelColor,
    }
  });
  dpLabel.anchor.set(1, 0.5);
  dpLabel.x = -config.barWidth / 2 - 8;
  dpLabel.y = 0;
  dpContainer.addChild(dpLabel);

  // DP Value (green, right of bar)
  const dpValue = new Text({
    text: '0%',
    style: {
      fontFamily: 'Arial, sans-serif',
      fontSize: config.valueFontSize,
      fontWeight: 'bold',
      fill: config.valueColor,
    }
  });
  dpValue.anchor.set(0, 0.5);
  dpValue.x = config.barWidth / 2 + 8;
  dpValue.y = 0;
  dpContainer.addChild(dpValue);

  // DP Bar Background (no rounded corners - starts exactly at 0, ends exactly at 100)
  const dpBarBg = new Graphics();
  dpBarBg.rect(-config.barWidth / 2, -config.barHeight / 2, config.barWidth, config.barHeight);
  dpBarBg.fill({ color: config.barBgColor, alpha: config.barBgAlpha });
  dpContainer.addChild(dpBarBg);

  // DP Bar Fill (will be updated based on penetration)
  const dpBarFill = new Graphics();
  dpContainer.addChild(dpBarFill);

  // DP Tick Marks with labels (0, 25, 50, 75, 100)
  const dpTickMarks = new Graphics();
  const dpTickLabels: Text[] = [];
  const dpTicks = [0, 25, 50, 75, 100];
  dpTicks.forEach(tick => {
    const tickX = -config.barWidth / 2 + (tick / 100) * config.barWidth;
    dpTickMarks.moveTo(tickX, -config.barHeight / 2 - 2);
    dpTickMarks.lineTo(tickX, -config.barHeight / 2 - 2 - config.tickHeight);
    dpTickMarks.stroke({ color: config.tickColor, width: 1 });

    // Tick label
    const tickLabel = new Text({
      text: tick.toString(),
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: config.tickLabelFontSize,
        fill: config.tickLabelColor,
      }
    });
    tickLabel.anchor.set(0.5, 1);
    tickLabel.x = tickX;
    tickLabel.y = -config.barHeight / 2 - config.tickHeight - 4;
    dpContainer.addChild(tickLabel);
    dpTickLabels.push(tickLabel);
  });
  dpContainer.addChild(dpTickMarks);

  container.addChild(dpContainer);

  // ===========================================
  // TRUE COUNT (TC) - Right side
  // ===========================================
  const tcContainer = new Container();
  tcContainer.x = config.tcX;
  tcContainer.y = config.y;

  // TC Label (yellow/gold, left of bar)
  const tcLabel = new Text({
    text: 'TC',
    style: {
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: config.labelFontSize,
      fontWeight: '900',
      fill: config.labelColor,
    }
  });
  tcLabel.anchor.set(1, 0.5);
  tcLabel.x = -config.barWidth / 2 - 8;
  tcLabel.y = 0;
  tcContainer.addChild(tcLabel);

  // TC Value (green, right of bar)
  const tcValue = new Text({
    text: '0',
    style: {
      fontFamily: 'Arial, sans-serif',
      fontSize: config.valueFontSize,
      fontWeight: 'bold',
      fill: config.valueColor,
    }
  });
  tcValue.anchor.set(0, 0.5);
  tcValue.x = config.barWidth / 2 + 8;
  tcValue.y = 0;
  tcContainer.addChild(tcValue);

  // TC Bar Background (no rounded corners - starts exactly at -5, ends exactly at +5)
  const tcBarBg = new Graphics();
  tcBarBg.rect(-config.barWidth / 2, -config.barHeight / 2, config.barWidth, config.barHeight);
  tcBarBg.fill({ color: config.barBgColor, alpha: config.barBgAlpha });
  tcContainer.addChild(tcBarBg);

  // TC Indicator (positioned based on current TC)
  const tcIndicator = new Graphics();
  tcContainer.addChild(tcIndicator);

  // TC Tick Marks with labels (-5, -2.5, 0, +2.5, +5)
  const tcTickMarks = new Graphics();
  const tcTickLabels: Text[] = [];
  const tcTicks = [-5, -2.5, 0, 2.5, 5];
  tcTicks.forEach(tick => {
    const tickX = -config.barWidth / 2 + ((tick - config.tcMin) / (config.tcMax - config.tcMin)) * config.barWidth;
    tcTickMarks.moveTo(tickX, -config.barHeight / 2 - 2);
    tcTickMarks.lineTo(tickX, -config.barHeight / 2 - 2 - config.tickHeight);
    tcTickMarks.stroke({ color: config.tickColor, width: 1 });

    // Tick label (format: -5, -2.5, 0, +2.5, +5)
    const labelText = tick === 0 ? '0' : (tick > 0 ? `+${tick}` : tick.toString());
    const tickLabel = new Text({
      text: labelText,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: config.tickLabelFontSize,
        fill: config.tickLabelColor,
      }
    });
    tickLabel.anchor.set(0.5, 1);
    tickLabel.x = tickX;
    tickLabel.y = -config.barHeight / 2 - config.tickHeight - 4;
    tcContainer.addChild(tickLabel);
    tcTickLabels.push(tickLabel);
  });
  tcContainer.addChild(tcTickMarks);

  container.addChild(tcContainer);

  return {
    container,
    dpContainer,
    dpLabel,
    dpValue,
    dpBarBg,
    dpBarFill,
    dpTickMarks,
    dpTickLabels,
    tcContainer,
    tcLabel,
    tcValue,
    tcBarBg,
    tcIndicator,
    tcTickMarks,
    tcTickLabels,
  };
}

/**
 * Gets the zone color based on value and type.
 * DP zones: 0-25% (zone1), 25-50% (zone2), 50-75% (zone3), 75-100% (zone4)
 * TC zones: -5 to -2.5 (zone1), -2.5 to 0 (zone2), 0 to +2.5 (zone3), +2.5 to +5 (zone4)
 */
function getZoneColor(value: number, type: 'dp' | 'tc'): number {
  const config = BUDDY_HUD_CONFIG;

  if (type === 'dp') {
    // DP: 0-100%
    if (value < 25) return config.zone1Color;      // 0-25%: Red (worst)
    if (value < 50) return config.zone2Color;      // 25-50%: Orange
    if (value < 75) return config.zone3Color;      // 50-75%: Yellow
    return config.zone4Color;                       // 75-100%: Green (best)
  } else {
    // TC: -5 to +5
    if (value < -2.5) return config.zone1Color;    // -5 to -2.5: Red (worst)
    if (value < 0) return config.zone2Color;       // -2.5 to 0: Orange
    if (value < 2.5) return config.zone3Color;     // 0 to +2.5: Yellow
    return config.zone4Color;                       // +2.5 to +5: Green (best)
  }
}

/**
 * Updates the BUDDY HUD with current penetration and true count values.
 */
function updateBuddyHUDValues(
  dpBarFill: Graphics,
  dpValue: Text,
  tcIndicator: Graphics,
  tcValue: Text,
  penetration: number,
  trueCount: number
): void {
  const config = BUDDY_HUD_CONFIG;

  // ===========================================
  // Update Deck Penetration
  // ===========================================
  const dpPercent = Math.max(0, Math.min(100, penetration));
  const dpColor = getZoneColor(dpPercent, 'dp');

  // Update value text with zone color
  dpValue.text = `${dpPercent.toFixed(1)}%`;
  dpValue.style.fill = dpColor;

  // Redraw fill bar with zone-based color
  dpBarFill.clear();
  const fillWidth = (dpPercent / 100) * config.barWidth;
  if (fillWidth > 0) {
    dpBarFill.roundRect(
      -config.barWidth / 2,
      -config.barHeight / 2,
      fillWidth,
      config.barHeight,
      config.barRadius
    );
    dpBarFill.fill({ color: dpColor });
  }

  // ===========================================
  // Update True Count
  // ===========================================
  const tcClamped = Math.max(config.tcMin, Math.min(config.tcMax, trueCount));

  // Color based on TC zone (4-zone system)
  const indicatorColor = getZoneColor(trueCount, 'tc');

  // Format TC value with sign and zone color
  const tcSign = trueCount >= 0 ? '+' : '';
  tcValue.text = `${tcSign}${trueCount.toFixed(1)}`;
  tcValue.style.fill = indicatorColor;

  // Redraw indicator at TC position
  tcIndicator.clear();
  const tcPosition = ((tcClamped - config.tcMin) / (config.tcMax - config.tcMin)) * config.barWidth;
  const indicatorX = -config.barWidth / 2 + tcPosition;
  const indicatorRadius = 5;

  // Draw indicator circle
  tcIndicator.circle(indicatorX, 0, indicatorRadius);
  tcIndicator.fill({ color: indicatorColor });
  tcIndicator.stroke({ color: 0xffffff, width: 1.5 });
}

/**
 * Updates the sidebet counter pill size based on current total.
 * Styled to match the kitty counter (pill background, orange text).
 * Note: Position is managed by repositionSidebetIcons - this only handles pill sizing and visibility.
 */
function updateSidebetCounterLayout(
  pill: Graphics,
  counterText: Text,
  total: number,
  _iconSize: number  // Kept for API compatibility
): void {
  const { pillOpacity } = SIDEBET_COUNTER_CONFIG;

  if (total > 0) {
    counterText.visible = true;
    counterText.text = total.toLocaleString();

    // Measure text width to size pill dynamically
    const textWidth = counterText.width;
    const pillPadding = { x: 6, y: 2 };
    const pillWidth = textWidth + pillPadding.x * 2;
    const pillHeight = (counterText.style.fontSize as number) + pillPadding.y * 2;

    // Redraw pill centered on its position (position set by repositionSidebetIcons)
    // Pill and counter are at the same position, so draw pill centered around (0,0)
    pill.clear();
    pill.roundRect(-pillWidth / 2, -pillHeight / 2, pillWidth, pillHeight, 6);
    pill.fill({ color: 0x000000, alpha: pillOpacity });

    // Don't set counter x/y - position is managed by repositionSidebetIcons
    // Counter anchor is (0.5, 0.5) so it centers on its position, same as pill
  } else {
    // Hide counter when no chips
    counterText.visible = false;
    pill.clear();
  }
}

/**
 * Kitty chip data - tracks one visual sprite per denomination with count.
 * Max 8 visual chips (one per denomination).
 * Each chip has a circular border that sits behind the entire chip stack.
 */
interface KittyChipData {
  sprite: Sprite;
  border: Graphics;  // Circular border behind the chip
  count: number;
}

/**
 * Adds a chip to the kitty display. Returns whether a NEW sprite was added.
 * If denomination already exists, returns false (just increment count).
 * Max 8 visual chips (one per denomination), bottom-justified stacking.
 * Each chip gets a circular border that sits behind the entire chip stack.
 */
function addChipToKitty(
  chipContainer: Container,
  borderContainer: Container,
  denomination: number,
  chipSprite: Sprite,
  kittyChipMap: Map<number, KittyChipData>,
  borderConfig: ChipBorderConfig
): boolean {
  const { chipSize, verticalOffset } = KITTY_CONFIG;

  // Check if this denomination already exists in the kitty
  const existingData = kittyChipMap.get(denomination);
  if (existingData) {
    // Denomination exists - increment count, destroy the incoming sprite
    existingData.count++;
    chipSprite.destroy();
    return false;
  }

  // New denomination - add sprite to kitty
  (chipSprite as unknown as { denom: number }).denom = denomination;
  chipSprite.width = chipSize;
  chipSprite.height = chipSize;
  chipSprite.anchor.set(0.5);
  chipSprite.x = 0;
  // Random rotation for natural chip landing appearance (0-359 degrees)
  chipSprite.rotation = Math.random() * 2 * Math.PI;
  chipContainer.addChild(chipSprite);

  // Create circular border for this chip (sits behind all chips)
  const border = new Graphics();
  const borderColor = parseInt(borderConfig.color.replace('#', ''), 16);
  border.circle(0, 0, chipSize / 2);
  border.stroke({
    color: borderColor,
    width: borderConfig.thickness,
    alpha: borderConfig.opacity / 100,
  });
  border.x = 0;
  borderContainer.addChild(border);

  // Track in map
  kittyChipMap.set(denomination, { sprite: chipSprite, border, count: 1 });

  // Reposition ALL chips and borders based on sorted denomination order
  repositionKittyChips(chipContainer, borderContainer, kittyChipMap);

  return true;
}

/**
 * Repositions all chips and their borders in the kitty based on sorted denomination order.
 * Bottom-justified: smallest at bottom (y=0), largest at top (negative Y).
 * Borders are positioned at the same Y but have lower zIndex (behind all chips).
 */
function repositionKittyChips(
  chipContainer: Container,
  borderContainer: Container,
  kittyChipMap: Map<number, KittyChipData>
): void {
  const { verticalOffset } = KITTY_CONFIG;

  // Get sorted denominations (ascending - smallest first at bottom)
  const sortedDenoms = [...kittyChipMap.keys()].sort((a, b) => a - b);

  // Position each chip and its border based on index in sorted order
  // Chips have zIndex 100+, borders have zIndex 0+ (always behind chips)
  sortedDenoms.forEach((denom, index) => {
    const data = kittyChipMap.get(denom);
    if (data) {
      const yPos = -index * verticalOffset;
      // Chip position and zIndex (above borders)
      data.sprite.y = yPos;
      data.sprite.zIndex = 100 + index;
      // Border position and zIndex (behind all chips)
      data.border.y = yPos;
      data.border.zIndex = index;
    }
  });
}

/**
 * Calculate the expected Y offset for a denomination in the kitty.
 * Used to animate chips to their correct position in the stack.
 * Returns the local Y offset within the kitty container.
 */
function calculateKittyChipYOffset(
  denomination: number,
  existingDenominations: number[]
): number {
  const { verticalOffset } = KITTY_CONFIG;

  // Build sorted list including the new denomination
  const allDenoms = [...new Set([...existingDenominations, denomination])].sort((a, b) => a - b);

  // Find index of this denomination in sorted order
  const index = allDenoms.indexOf(denomination);

  // Position: smallest at bottom (y=0), larger denominations go up (negative Y)
  return -index * verticalOffset;
}

// ===========================================
// SCORE BOX RENDERING
// ===========================================

/** Creates a score box showing the numerical hand value - always solid black
 * Returns container with actualWidth property for width locking
 */
function createScoreBox(
  score: number,
  isSoft: boolean,
  isBlackjack?: boolean,
  isBusted?: boolean,
  scoreboxConfig: ScoreboxConfig = DEFAULT_SCOREBOX_CONFIG,
  displayTextOverride?: string,  // Optional override for custom display (e.g., "--" for empty)
  minWidth?: number  // Optional minimum width (for width locking after resolution)
): Container & { actualWidth: number } {
  const container = new Container() as Container & { actualWidth: number };
  container.actualWidth = 0;

  // Determine display text - always solid black background with white text
  // Scores < 10 are zero-padded (e.g., "8" → "08")
  let displayText = displayTextOverride || '';

  // Helper to zero-pad single digit scores
  const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();

  if (!displayTextOverride) {
    if (isBlackjack) {
      displayText = '21';
    } else if (score > 0) {
      // Show score (soft hands show both values)
      if (isSoft && score <= 21) {
        displayText = `${padScore(score - 10)}/${padScore(score)}`;
      } else {
        displayText = padScore(score);
      }
    }
  }

  if (!displayText) return container;

  const fontSize = scoreboxConfig.fontSize;
  const boxScale = scoreboxConfig.scale / 100;

  // Text style - white bold text (font size NOT affected by box scale)
  const textStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: fontSize,
    fontWeight: 'bold',
    fill: 0xffffff,
  });
  const text = new Text({ text: displayText, style: textStyle });

  // Fixed height 20px, min width 12px (expands for soft hands like "11/21")
  // If minWidth provided, use it as floor to prevent shrinking
  const calculatedWidth = Math.max(12, text.width) * boxScale;
  const boxWidth = minWidth ? Math.max(minWidth, calculatedWidth) : calculatedWidth;
  const boxHeight = 20 * boxScale;

  // Store actual width for locking
  container.actualWidth = boxWidth;

  // Background - solid black, no border
  // Custom shape: bottom-right corner square, others rounded
  const bg = new Graphics();
  const radius = 4;
  bg.moveTo(radius, 0);                               // start after top-left curve
  bg.lineTo(boxWidth - radius, 0);                    // top edge
  bg.arcTo(boxWidth, 0, boxWidth, radius, radius);    // top-right (rounded)
  bg.lineTo(boxWidth, boxHeight);                     // right edge to bottom-right (square)
  bg.lineTo(radius, boxHeight);                       // bottom edge
  bg.arcTo(0, boxHeight, 0, boxHeight - radius, radius); // bottom-left (rounded)
  bg.lineTo(0, radius);                               // left edge
  bg.arcTo(0, 0, radius, 0, radius);                  // top-left (rounded)
  bg.fill({ color: 0x000000 });
  container.addChild(bg);

  // Center text in scaled box
  text.anchor.set(0.5, 0.5);
  text.x = boxWidth / 2;
  text.y = boxHeight / 2;
  container.addChild(text);

  return container;
}

/** Creates an outcome label (WINNER!, LOSER!, etc.) using pre-loaded Sprite textures */
function createOutcomeLabel(
  outcome: string,
  outcomeLabelConfig: OutcomeLabelConfig = DEFAULT_OUTCOME_CONFIG,
  textures?: Map<string, Texture>
): Container {
  const container = new Container();

  // Determine background color based on outcome
  let bgColor = 0x1f2937;
  const outcomeLower = outcome.toLowerCase();

  switch (outcomeLower) {
    case 'win':
      bgColor = 0x22c55e;
      break;
    case 'blackjack':
      bgColor = 0xfbbf24;
      break;
    case 'lose':
    case 'bust':
      bgColor = 0xef4444;
      break;
    case 'push':
      bgColor = 0x6b7280;
      break;
    case 'surrender':
      bgColor = 0x9ca3af;
      break;
  }

  const fontSize = outcomeLabelConfig.fontSize;
  const containerScale = outcomeLabelConfig.scale / 100;
  const fontScale = fontSize / OUTCOME_BASE_FONT_SIZE;

  // Try to use pre-loaded texture as Sprite
  const texture = textures?.get(`outcome-${outcomeLower}`);
  let textDisplay: Sprite | Text;

  if (texture) {
    textDisplay = new Sprite(texture);
    textDisplay.scale.set(fontScale);
  } else {
    // Fallback to Text if texture not found (shouldn't happen after preload)
    console.warn(`[createOutcomeLabel] Pre-loaded texture not found for outcome-${outcomeLower}, falling back to Text`);
    MODULE_TEXT_CREATED_COUNT++;

    // Determine display text for fallback
    let displayText = '';
    switch (outcomeLower) {
      case 'win': displayText = 'WINNER!'; break;
      case 'blackjack': displayText = 'BLACKJACK!'; break;
      case 'lose': displayText = 'LOSER!'; break;
      case 'bust': displayText = 'BUSTED!'; break;
      case 'push': displayText = 'PUSH'; break;
      case 'surrender': displayText = 'SURRENDER'; break;
      default: displayText = outcome.toUpperCase();
    }

    const textColor = outcomeLower === 'blackjack' ? 0x000000 : 0xffffff;
    const textStyle = new TextStyle({
      fontFamily: outcomeLabelConfig.fontFamily,
      fontSize: fontSize,
      fontWeight: 'bold',
      fill: textColor,
    });
    textDisplay = new Text({ text: displayText, style: textStyle });
  }

  // Background - sized based on text display dimensions
  const padding = 6 * containerScale;
  const leftPadding = 3;
  const boxWidth = leftPadding + textDisplay.width + padding;
  const boxHeight = textDisplay.height + padding;

  const bg = new Graphics();
  const radius = 4 * containerScale;
  // Custom shape: only top-right corner rounded
  bg.moveTo(0, 0);                                    // top-left (square)
  bg.lineTo(boxWidth - radius, 0);                    // top edge
  bg.arcTo(boxWidth, 0, boxWidth, radius, radius);    // top-right (rounded)
  bg.lineTo(boxWidth, boxHeight);                     // right edge
  bg.lineTo(0, boxHeight);                            // bottom edge
  bg.lineTo(0, 0);                                    // left edge back to start
  bg.fill({ color: bgColor });
  container.addChild(bg);

  textDisplay.x = leftPadding; // left justified with 3px padding
  textDisplay.y = padding / 2;
  container.addChild(textDisplay);

  // Apply overall scale
  container.scale.set(containerScale);

  return container;
}

// ===========================================
// HAND LABEL FLY-OUT ANIMATION
// ===========================================

/**
 * Animate a hand label flying out upward with clipping at the seat border.
 * FOLLOWS THE SAME PATTERN AS OUTCOME LABEL:
 * - Wrapper positioned at SEAT CENTER
 * - Label position converted to LOCAL COORDINATES once
 * - Animation purely in local coordinates
 * - Mask from seat border bottom to below label
 *
 * @param params.label - The hand label Text element (currently in world coords)
 * @param params.bg - The background Graphics element (optional)
 * @param params.seatData - Seat position data (x, y, rotation in degrees)
 * @param params.scaledSeatWidth - Seat width in pixels
 * @param params.scaledSeatHeight - Seat height in pixels
 * @param params.outcomeLabelLayer - The layer to add the masked wrapper to
 * @param params.duration - Animation duration in ms (default 300)
 * @param params.onComplete - Callback when animation completes
 * @param params.debugLabel - Label for debug logging
 */
function animateHandLabelFlyOut(params: {
  label: Text | Sprite;
  bg: Graphics | null;
  seatData: { x: number; y: number; rotation: number };
  scaledSeatWidth: number;
  scaledSeatHeight: number;
  outcomeLabelLayer: Container;
  duration?: number;
  onComplete?: () => void;
  debugLabel?: string;
  /** If provided, reuse existing wrapper (label/bg already in local coords) */
  existingWrapper?: Container;
}): void {
  const {
    label,
    bg,
    seatData,
    scaledSeatWidth,
    scaledSeatHeight,
    outcomeLabelLayer,
    duration = 300,
    onComplete,
    debugLabel = 'unknown',
    existingWrapper
  } = params;

  const seatRotRad = degreesToRadians(seatData.rotation);
  const flyUpDistance = -60;

  // === CASE 1: Existing wrapper provided (from Navigation Phase 2) ===
  // Label and bg are already inside wrapper with local coordinates
  if (existingWrapper && !existingWrapper.destroyed) {
    console.log(`[HAND LABEL FLY-OUT] ${debugLabel}: Using existing wrapper, label already in local coords`);

    // Label and bg are already positioned in local coords from fly-in
    // Just animate them upward
    const labelStartY = label.y;
    animateSprite(label, { y: labelStartY + flyUpDistance }, {
      duration,
      easing: easeInCubic,
      onComplete: () => {
        // Clean up wrapper
        outcomeLabelLayer.removeChild(existingWrapper);
        existingWrapper.destroy({ children: true });
        onComplete?.();
      }
    });

    if (bg && !bg.destroyed) {
      const bgStartY = bg.y;
      animateSprite(bg, { y: bgStartY + flyUpDistance }, {
        duration,
        easing: easeInCubic,
      });
    }
    return;
  }

  // === CASE 2: No existing wrapper (initial split Hand1, rake) ===
  // Create wrapper and convert world coords to local

  // === STEP 1: Create wrapper at SEAT CENTER (same as outcome label pattern) ===
  const handLabelWrapper = new Container();
  handLabelWrapper.sortableChildren = true; // Enable zIndex sorting so text renders above background
  handLabelWrapper.x = seatData.x;
  handLabelWrapper.y = seatData.y;
  handLabelWrapper.rotation = seatRotRad;

  // === STEP 2: Create mask (TOP edge at seat border bottom, extends DOWN) ===
  // For fly-OUT, we clip on LEFT (label exits upward and left-ward)
  const maskTopY = scaledSeatHeight / 2; // Seat border bottom in local coords
  const maskBottomY = scaledSeatHeight / 2 + 60; // Extend 60px below seat
  const maskHeight = maskBottomY - maskTopY;
  const maskLeftX = -scaledSeatWidth / 2 + 40; // Clip 40px from left edge
  const maskRightX = scaledSeatWidth / 2; // Full right side
  const maskWidth = maskRightX - maskLeftX;

  const handLabelMask = new Graphics();
  handLabelMask.roundRect(maskLeftX, maskTopY, maskWidth, maskHeight, 8);
  handLabelMask.fill({ color: 0xffffff });
  handLabelWrapper.addChild(handLabelMask);
  handLabelWrapper.mask = handLabelMask;


  // === STEP 3: Convert label world position to LOCAL coordinates ===
  const labelWorldX = label.x;
  const labelWorldY = label.y;
  const dx = labelWorldX - seatData.x;
  const dy = labelWorldY - seatData.y;
  const cosR = Math.cos(-seatRotRad);
  const sinR = Math.sin(-seatRotRad);
  const labelLocalX = dx * cosR - dy * sinR;
  const labelLocalY = dx * sinR + dy * cosR;

  console.log(`[HAND LABEL FLY-OUT] ${debugLabel}: Creating new wrapper, labelWorld=(${labelWorldX.toFixed(1)}, ${labelWorldY.toFixed(1)}), labelLocal=(${labelLocalX.toFixed(1)}, ${labelLocalY.toFixed(1)})`);

  // === STEP 4: Add wrapper to layer, then re-parent label ===
  outcomeLabelLayer.addChild(handLabelWrapper);

  // Re-parent bg FIRST, then label (so label renders on top)
  if (bg && !bg.destroyed) {
    // Background has its OWN world position (different from label due to padding)
    const bgWorldX = bg.x;
    const bgWorldY = bg.y;
    const bgDx = bgWorldX - seatData.x;
    const bgDy = bgWorldY - seatData.y;
    const bgLocalX = bgDx * cosR - bgDy * sinR;
    const bgLocalY = bgDx * sinR + bgDy * cosR;

    outcomeLabelLayer.removeChild(bg);
    bg.x = bgLocalX;
    bg.y = bgLocalY;
    bg.rotation = 0;
    bg.zIndex = 0;
    handLabelWrapper.addChild(bg);
  }

  // Re-parent label
  outcomeLabelLayer.removeChild(label);
  label.x = labelLocalX;
  label.y = labelLocalY;
  label.rotation = 0;
  label.zIndex = 1;
  handLabelWrapper.addChild(label);

  // === STEP 5: Animate upward in LOCAL coordinates ===
  animateSprite(label, { y: labelLocalY + flyUpDistance }, {
    duration,
    easing: easeInCubic,
    onComplete: () => {
      outcomeLabelLayer.removeChild(handLabelWrapper);
      handLabelWrapper.destroy({ children: true });
      MODULE_TEXT_DESTROYED_COUNT++; // Track hand label destruction
      onComplete?.();
    }
  });

  if (bg && !bg.destroyed) {
    const bgTargetY = bg.y + flyUpDistance;
    animateSprite(bg, { y: bgTargetY }, {
      duration,
      easing: easeInCubic,
    });
  }
}

// ===========================================
// SEAT CONTAINER RENDERING
// ===========================================

function createSeatContainer(
  seatPosition: number,
  x: number,
  y: number,
  rotation: number,
  player: PlayerState | undefined,
  isCurrentUser: boolean,
  isPlayerTurn: boolean,
  textures: Map<string, Texture>,
  currentHandIndex: number,
  onSeatClick?: (seatPosition: number) => void,
  sizeScale: number = 100,
  betChipConfig: ChipStackConfig = DEFAULT_CHIP_CONFIG,
  insuranceChipConfig: ChipStackConfig = DEFAULT_CHIP_CONFIG,
  scoreboxConfig: ScoreboxConfig = DEFAULT_SCOREBOX_CONFIG,
  outcomeLabelConfig: OutcomeLabelConfig = DEFAULT_OUTCOME_CONFIG,
  chipBorderConfig: ChipBorderConfig = DEFAULT_CHIP_BORDER_CONFIG,
  chipCounterConfig: ChipCounterConfig = DEFAULT_CHIP_COUNTER_CONFIG,
  kittyConfig: KittyConfig = DEFAULT_KITTY_CONFIG,
  cardConfig: CardConfig = DEFAULT_CARD_CONFIG,
  showBetChips: boolean = false, // Show/hide bet chip stacks (DEV control, default hidden)
  showInsuranceChips: boolean = false, // Show/hide insurance chip stacks (DEV control, default hidden)
  usernameYOffset: number = 0, // Y offset for username position
  outcomeLabelLayer: Container | null = null, // Layer for persistent outcome labels
  scoreboxLayer: Container | null = null, // Layer for scoreboxes (above outcome labels)
  highlightLayer: Container | null = null, // Layer for turn highlight borders (above scoreboxes)
  avatarLayer: Container | null = null // Layer for avatars (above highlight borders)
): Container {
  const container = new Container();
  container.x = x;
  container.y = y;
  container.rotation = degreesToRadians(rotation);

  // Base size scaled by sizeScale
  const width = Math.round(144 * (sizeScale / 100));
  const height = Math.round(168 * (sizeScale / 100));

  // Seat box background
  const box = new Graphics();
  if (!player) {
    // Empty seat - needs a fill for click detection
    box.roundRect(-width / 2, -height / 2, width, height, 8);
    box.fill({ color: 0x166534, alpha: 0.3 }); // Visible fill for click area
    box.stroke({ color: 0x6b7280, width: 2, alpha: 0.5 });

    // Add box FIRST so it's clickable
    container.addChild(box);

    // "Join" text
    const joinStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
    });
    const joinText = new Text({ text: 'Join', style: joinStyle });
    joinText.anchor.set(0.5);
    joinText.alpha = 0.8;
    container.addChild(joinText);

    // Make the box interactive (not the container)
    box.eventMode = 'static';
    box.cursor = 'pointer';
    box.interactive = true;

    // Add click handler directly to the box
    if (onSeatClick) {
      box.on('pointerdown', () => {
        console.log('[PixiGameTableV1] Seat clicked:', seatPosition);
        onSeatClick(seatPosition);
      });
    }

    // Add bet chip stacks (kitty) above seat - position matches actual in-game kitty
    if (showBetChips) {
      const betChipScale = betChipConfig.scale / 100;
      // Use same positioning formula as actual kitty: KITTY_BASE_RADIAL_OFFSET base + radiusOffset + config offsets
      const kittyLocalX = betChipConfig.x + kittyConfig.x;
      const kittyLocalY = KITTY_BASE_RADIAL_OFFSET + betChipConfig.radiusOffset + betChipConfig.y + kittyConfig.y;
      const betChips = createSeatChipDisplay(
        textures,
        kittyLocalX,
        kittyLocalY,
        Math.round(28 * betChipScale), // chip size (medium)
        3,  // 3 chips per denomination (fanned)
        Math.round(8 * betChipScale),  // vertical offset between denominations
        Math.round(5 * betChipScale),  // horizontal fan offset
        chipBorderConfig,
        chipCounterConfig,
        100 // Mock amount for empty seat display
      );
      container.addChild(betChips);
    }

    // Add insurance chip stacks - position matches actual in-game insurance kitty
    if (showInsuranceChips) {
      const insChipScale = insuranceChipConfig.scale / 100;
      // Insurance at SAME radial level as main bet (KITTY_BASE_RADIAL_OFFSET), offset tangentially
      // In local coords: Y = radial, X = tangent
      // insuranceChipConfig.x = tangential offset (left/right)
      // insuranceChipConfig.y = additional radial offset
      const insLocalX = insuranceChipConfig.x + kittyConfig.x;
      const insLocalY = KITTY_BASE_RADIAL_OFFSET + insuranceChipConfig.radiusOffset + insuranceChipConfig.y + kittyConfig.y;
      const insuranceChips = createSeatChipDisplay(
        textures,
        insLocalX,
        insLocalY,
        Math.round(20 * insChipScale), // smaller chips
        2,  // 2 chips per denomination
        Math.round(6 * insChipScale),  // tighter vertical stacking
        Math.round(4 * insChipScale),  // tighter horizontal fan
        chipBorderConfig,
        chipCounterConfig, // Add chip counter for insurance
        100 // Mock amount for empty seat display
      );
      container.addChild(insuranceChips);
    }

    // Return early for empty seats
    return container;
  } else {
    // Enable z-index sorting for this container
    container.sortableChildren = true;

    // Occupied seat
    const borderColor = isCurrentUser ? 0xeab308 : 0x6b7280;
    const borderAlpha = isCurrentUser ? 0.7 : 0.5;
    const bgAlpha = isCurrentUser ? 0.4 : 0.3;

    box.roundRect(-width / 2, -height / 2, width, height, 8);
    box.fill({ color: 0x166534, alpha: bgAlpha });
    box.stroke({ color: borderColor, width: 3, alpha: borderAlpha });
    box.zIndex = -2; // Behind outcome label (-1), scorebox (0), and gold turn border (1)
    container.addChild(box);

    // Make occupied seat clickable for current user (for card straightening)
    console.log('[createSeatContainer] Seat', seatPosition, 'isCurrentUser:', isCurrentUser, 'onSeatClick:', !!onSeatClick);
    if (isCurrentUser && onSeatClick) {
      console.log('[createSeatContainer] Setting up click handler for seat', seatPosition);
      box.eventMode = 'static';
      box.cursor = 'pointer';
      box.interactive = true;
      box.on('pointerdown', () => {
        console.log('[PixiGameTableV1] Own seat clicked for straightening:', seatPosition);
        onSeatClick(seatPosition);
      });
    }

    // ALSO make the entire container clickable as backup (cards might be covering the box)
    if (isCurrentUser && onSeatClick) {
      container.eventMode = 'static';
      container.interactive = true;
      container.on('pointerdown', () => {
        console.log('[PixiGameTableV1] Seat CONTAINER clicked:', seatPosition);
        onSeatClick(seatPosition);
      });
    }

    // Highlight border - rendered in highlightLayer (above scoreboxes and outcome labels)
    // Style changes based on turn
    const highlight = new Graphics();
    highlight.roundRect(-width / 2, -height / 2, width, height, 8);
    if (isPlayerTurn) {
      highlight.stroke({ color: 0xfacc15, width: 4, alpha: 1 });
    } else {
      highlight.stroke({ color: 0xf9d700, width: 2, alpha: 0.8 });
    }

    if (highlightLayer) {
      // Position in highlightLayer using global coordinates
      highlight.x = x;
      highlight.y = y;
      highlight.rotation = degreesToRadians(rotation);
      highlightLayer.addChild(highlight);
    } else {
      // Fallback: add to seat container if no highlightLayer
      container.addChild(highlight);
    }

    // Avatar - positioned at bottom-right corner (matching React version)
    // Rendered in avatarLayer (above highlight borders)
    // Current user: 48px, opponents: 40px (83% of 48)
    const avatarSize = isCurrentUser ? 48 : 40;

    // Create avatar container
    const avatarContainer = new Container();

    // Check if we have a loaded avatar texture
    const avatarTextureKey = player.avatarUrl ? `avatar-${player.avatarUrl}` : null;
    const avatarTexture = avatarTextureKey ? textures.get(avatarTextureKey) : null;

    if (avatarTexture) {
      // Use avatar image with circular mask
      const avatarSprite = new Sprite(avatarTexture);
      avatarSprite.anchor.set(0.5);
      avatarSprite.width = avatarSize;
      avatarSprite.height = avatarSize;

      // Create circular mask
      const avatarMask = new Graphics();
      avatarMask.circle(0, 0, avatarSize / 2);
      avatarMask.fill({ color: 0xffffff });
      avatarSprite.mask = avatarMask;

      // Border ring
      const avatarBorder = new Graphics();
      avatarBorder.circle(0, 0, avatarSize / 2);
      avatarBorder.stroke({ color: isCurrentUser ? 0xffffff : 0xfdba74, width: 2 });

      avatarContainer.addChild(avatarMask);
      avatarContainer.addChild(avatarSprite);
      avatarContainer.addChild(avatarBorder);
    } else {
      // Fallback: initials on colored background
      const avatarBg = new Graphics();
      avatarBg.circle(0, 0, avatarSize / 2);
      avatarBg.fill({ color: isCurrentUser ? 0xeab308 : 0x4b5563 });
      avatarBg.stroke({ color: isCurrentUser ? 0xffffff : 0xfdba74, width: 2 });

      // Avatar initials (first 3 letters like React version)
      const initials = player.displayName.substring(0, 3).toUpperCase();
      const avatarTextStyle = new TextStyle({
        fontFamily: 'Arial',
        fontSize: isCurrentUser ? 14 : 12,
        fontWeight: 'bold',
        fill: isCurrentUser ? 0x000000 : 0xffffff,
      });
      const avatarText = new Text({ text: initials, style: avatarTextStyle });
      avatarText.anchor.set(0.5);

      avatarContainer.addChild(avatarBg);
      avatarContainer.addChild(avatarText);
    }

    if (avatarLayer) {
      // Position in avatarLayer using global coordinates
      // Uses registry function for consistent avatar positioning across all animations
      const avatarPos = calculateAvatarPositionRaw(x, y, width, height, rotation);
      avatarContainer.x = avatarPos.x;
      avatarContainer.y = avatarPos.y;
      avatarContainer.rotation = degreesToRadians(rotation);
      avatarLayer.addChild(avatarContainer);
    } else {
      // Fallback: add to seat container if no avatarLayer
      avatarContainer.x = width / 2;
      avatarContainer.y = height / 2;
      container.addChild(avatarContainer);
    }

    // Username at top of seat (inside the seat box)
    const displayName = player.displayName || `P${seatPosition}`;
    const nameStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 11,
      fill: 0xffffff,
      fontWeight: isPlayerTurn ? 'bold' : 'normal',
    });
    const nameText = new Text({ text: displayName, style: nameStyle });
    nameText.anchor.set(0.5, 0);
    nameText.y = -height / 2 + 6 + usernameYOffset;
    nameText.zIndex = 4;
    container.addChild(nameText);

    // NOTE: Cards are NOT rendered here. The animation layer handles all card rendering
    // from deal to rake. This avoids duplicate cards and simplifies the architecture.
    // Static card rendering was a holdover from the React/DOM clone-based system.

    // Score box - ALWAYS visible (persistent element), shows "--" when no cards
    // Use player's own currentHandIndex, not the global prop (which is for the current user only)
    // Default to 0 when currentHandIndex is undefined (e.g., during phase=complete table_state)
    const handIndex = player.currentHandIndex ?? 0;
    const hand = player.hands[handIndex];

    // Check if hand is resolved first - if resolved, show all cards (not filtered)
    // Also consider busted hands as resolved (isBusted is set before outcome)
    // Only consider hand resolved when it has an actual outcome or is busted
    // Do NOT use score >= 21 here - the card animation must complete first before showing all cards
    const isResolved = !!hand?.outcome || !!hand?.isBusted;

    // Only count cards that have started deal animation
    // This prevents scorebox from showing new cards before animation starts
    // Exception: when resolved, show all cards (animations have completed by resolution time)
    const dealtKey = `${seatPosition}-${handIndex}`;
    const dealtCardCount = MODULE_DEALT_CARD_COUNTS.get(dealtKey) ?? 0;
    const visibleCards = hand
      ? (isResolved ? hand.cards : hand.cards.slice(0, dealtCardCount))
      : [];
    const hasCards = visibleCards.length > 0;

    // AUDIT: Scorebox calculation logging for accuracy verification
    // Format: [SCOREBOX AUDIT] seat=N hand=N cards=[ranks] score=X display="XX" resolved=bool cached=bool
    if (hand && hand.cards.length > 0) {
      console.log(`[SCOREBOX DIAG] seat=${seatPosition} hand=${handIndex}: totalCards=${hand.cards.length} dealtCount=${dealtCardCount} visibleCards=${visibleCards.length} isResolved=${isResolved} outcome=${hand.outcome ?? 'none'} isBusted=${hand.isBusted ?? false}`);
    }

    // Width lock key is per-seat only (not per-hand) since lock persists across rounds
    const scoreboxLockKey = `${seatPosition}`;
    const scoreboxCacheKey = `${seatPosition}`;

    // Format score display using server-provided scores from SCORE_UPDATED events
    // - After resolution (outcome exists): show only final score from getLatestScore
    // - During play: use getScoreForCardCount to match animation timing
    // - Soft hands show both values (e.g., "08/18")
    // - Scores < 10 are zero-padded (e.g., "8" → "08")
    // - When no server score, use cached score (persists until rake clears it)
    // - When rake is in progress, use cached "--" and skip recalculation
    let scoreDisplayText = '--';

    // If rake is in progress for this seat, ALWAYS show "--" and skip recalculation
    // This prevents re-renders during rake from showing the old score
    if (MODULE_RAKE_SEATS_IN_PROGRESS.has(seatPosition)) {
      scoreDisplayText = '--';
      console.log(`[SCOREBOX AUDIT] seat=${seatPosition} RAKE IN PROGRESS - forcing "--"`);
    } else if (MODULE_SCOREBOX_WIPE_IN_PROGRESS.has(seatPosition)) {
      // Wipe animation in progress - use cached value to prevent overwrite during re-renders
      const cachedScore = MODULE_SCOREBOX_CACHE.get(`${seatPosition}`);
      scoreDisplayText = cachedScore ?? '--';
      console.log(`[SCOREBOX AUDIT] seat=${seatPosition} WIPE IN PROGRESS - using cached: "${scoreDisplayText}"`);
    } else if (hasCards) {
      // Helper to zero-pad single digit scores
      const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();

      // Get server-provided score from history cache
      // For resolved hands, use the latest score; for in-progress, match card count
      const serverScore = isResolved
        ? getLatestScore(seatPosition, handIndex)
        : getScoreForCardCount(seatPosition, handIndex, visibleCards.length);

      if (serverScore) {
        const { score, isSoft } = serverScore;

        // After resolution, show only the final score
        if (isResolved) {
          scoreDisplayText = padScore(score);
        } else if (isSoft && score < 21) {
          // During play, soft hands show both values (e.g., "08/18")
          // Never show "11/21" - game forces action on 21
          const lowValue = score - 10;
          scoreDisplayText = `${padScore(lowValue)}/${padScore(score)}`;
        } else {
          scoreDisplayText = padScore(score);
        }

        // AUDIT: Log scorebox using server-provided score
        const cardRanks = visibleCards.map(c => c.rank).join(',');
        console.log(`[SCOREBOX AUDIT] seat=${seatPosition} hand=${handIndex} cards=[${cardRanks}] serverScore=${score} display="${scoreDisplayText}" isSoft=${isSoft} resolved=${isResolved}`);

        // Cache the score - persists until rake animation clears it
        MODULE_SCOREBOX_CACHE.set(scoreboxCacheKey, scoreDisplayText);
      } else {
        // No server score available - use cached value or show "--"
        // Server should always send SCORE_UPDATED after each card, so this is a fallback
        const cachedScore = MODULE_SCOREBOX_CACHE.get(scoreboxCacheKey);
        if (cachedScore) {
          scoreDisplayText = cachedScore;
          console.log(`[SCOREBOX AUDIT] seat=${seatPosition} hand=${handIndex} NO SERVER SCORE - using cached: "${cachedScore}"`);
        } else {
          console.log(`[SCOREBOX AUDIT] seat=${seatPosition} hand=${handIndex} NO SERVER SCORE - showing "--"`);
        }
      }

      // Width lock management:
      // - Lock width when hand is resolved (prevents shrinking during resolution)
      // - Unlock when new round's 2nd card is dealt (cards >= 2 AND no outcome)
      // - Lock persists through betting phase (no cards) until new round deals 2nd card
      if (isResolved) {
        // Will lock after createScoreBox returns actualWidth
      } else if (visibleCards.length >= 2) {
        // New round with 2+ cards and no outcome - clear the lock
        MODULE_SCOREBOX_LOCKS.delete(scoreboxLockKey);
      }
      // Note: Do NOT clear lock when no cards - lock persists through betting phase
    } else {
      // No cards - use cached score if available (persists until rake clears it)
      const cachedScore = MODULE_SCOREBOX_CACHE.get(scoreboxCacheKey);
      if (cachedScore) {
        scoreDisplayText = cachedScore;
        console.log(`[SCOREBOX AUDIT] seat=${seatPosition} hand=${handIndex} CACHED display="${cachedScore}"`);
      }
    }

    // Get locked width if exists (prevents shrinking after resolution)
    const lockedWidth = MODULE_SCOREBOX_LOCKS.get(scoreboxLockKey);

    // Effective minWidth: use the larger of config minWidth and locked width
    // - Config minWidth is a static floor (e.g., 35px to match React)
    // - Locked width is captured at resolution and persists until new round
    const configMinWidth = scoreboxConfig.minWidth ?? 0;
    const effectiveMinWidth = Math.max(configMinWidth, lockedWidth ?? 0) || undefined;

    // Score box - bottom-left corner of seat
    // Rendered in scoreboxLayer (above outcome labels) instead of seat container
    // Shows "--" when no cards (persistent scorebox like /table-demo)
    // SKIP creation if wipe animation is in progress (preserved scorebox is already in layer)
    if (!MODULE_SCOREBOX_WIPE_IN_PROGRESS.has(seatPosition)) {
      const scoreBox = createScoreBox(
        0, false, false, false, scoreboxConfig,
        scoreDisplayText,  // Always use pre-formatted display text
        effectiveMinWidth  // Pass effective min width (combines config + locking)
      );
      // Name the scorebox so showOutcomeLabel can find it
      scoreBox.label = `scorebox-${seatPosition}`;

      // Lock width when hand is resolved
      if (isResolved && scoreBox.actualWidth > 0) {
        MODULE_SCOREBOX_LOCKS.set(scoreboxLockKey, scoreBox.actualWidth);
      }

      const scoreBounds = scoreBox.getLocalBounds();

      if (scoreboxLayer) {
        // Position in scoreboxLayer using global coordinates
        // Use SCALED dimensions so scorebox position tracks the corner of the scaled seat
        const localX = -width / 2 + scoreboxConfig.x;
        const localY = height / 2 - scoreBounds.height + scoreboxConfig.y;
        const rotRad = degreesToRadians(rotation);
        const cos = Math.cos(rotRad);
        const sin = Math.sin(rotRad);
        // Transform local to global coordinates
        scoreBox.x = x + localX * cos - localY * sin;
        scoreBox.y = y + localX * sin + localY * cos;
        scoreBox.rotation = rotRad;
        scoreboxLayer.addChild(scoreBox);
        // Monitor for height changes (DEBUG)
        monitorScoreboxHeight(`seat-${seatPosition}`, scoreBox, `createSeatContainer layer`);
      } else {
        // Fallback: add to seat container if no scoreboxLayer
        scoreBox.x = -width / 2 + scoreboxConfig.x;
        scoreBox.y = height / 2 - scoreBounds.height + scoreboxConfig.y;
        container.addChild(scoreBox);
        // Monitor for height changes (DEBUG)
        monitorScoreboxHeight(`seat-${seatPosition}`, scoreBox, `createSeatContainer fallback`);
      }
    }
    // else: wipe animation in progress, preserved scorebox is already in scoreboxLayer

    // OUTCOME LABELS: Now handled imperatively via showOutcomeLabel() method
    // This avoids race conditions with React's render cycle.
    // The imperative method is called by useGameEvents when chip resolution events fire,
    // ensuring proper Order of Operations: outcome label animates BEFORE chips resolve.
    //
    // The old React-driven code here was removed because it created duplicate labels:
    // 1. Imperative showOutcomeLabel() creates label on HAND_BUSTED event
    // 2. React re-renders and this code would create ANOTHER label
    //
    // Position updates for existing labels are handled in the imperative method.

    // Chip stack (bet) above seat - actual bet visualization
    // NOTE: Static chip stack removed - bet chips are now handled by the animated kitty system
    // (see animateRailToKitty, clearKitty, etc.)
    // The old code created a static display that didn't animate and conflicted with the kitty.
    const totalBet = hand?.bet || 0;

    // Add bet chip stacks (kitty) above seat - position matches actual in-game kitty (DEV ONLY)
    if (showBetChips) {
      const betChipScale2 = betChipConfig.scale / 100;
      // Use same positioning formula as actual kitty: KITTY_BASE_RADIAL_OFFSET base + radiusOffset + config offsets
      const kittyLocalX2 = betChipConfig.x + kittyConfig.x;
      const kittyLocalY2 = KITTY_BASE_RADIAL_OFFSET + betChipConfig.radiusOffset + betChipConfig.y + kittyConfig.y;
      const betChips = createSeatChipDisplay(
        textures,
        kittyLocalX2,
        kittyLocalY2,
        Math.round(28 * betChipScale2), // chip size (medium)
        3,  // 3 chips per denomination (fanned)
        Math.round(8 * betChipScale2),  // vertical offset between denominations
        Math.round(5 * betChipScale2),  // horizontal fan offset
        chipBorderConfig,
        chipCounterConfig,
        totalBet > 0 ? totalBet : 100 // Show actual bet or mock amount
      );
      betChips.zIndex = 8;
      container.addChild(betChips);
    }

    // Add insurance chip stacks - position matches actual in-game insurance kitty (DEV ONLY)
    if (showInsuranceChips) {
      const insChipScale2 = insuranceChipConfig.scale / 100;
      const insuranceAmount = player?.insuranceBet || 0;
      // Always show mock stack when DEV toggle is on (for positioning), or real amount if betting
      const displayAmount = insuranceAmount > 0 ? insuranceAmount : 5000; // Mock $5000 for DEV
      // Insurance at SAME radial level as main bet (KITTY_BASE_RADIAL_OFFSET), offset tangentially
      // insuranceChipConfig.x = tangential offset (left/right)
      // insuranceChipConfig.y = additional radial offset
      const insLocalX2 = insuranceChipConfig.x + kittyConfig.x;
      const insLocalY2 = KITTY_BASE_RADIAL_OFFSET + insuranceChipConfig.radiusOffset + insuranceChipConfig.y + kittyConfig.y;
      const insuranceChips = createSeatChipDisplay(
        textures,
        insLocalX2,
        insLocalY2,
        Math.round(20 * insChipScale2), // smaller chips
        2,  // 2 chips per denomination
        Math.round(6 * insChipScale2),  // tighter vertical stacking
        Math.round(4 * insChipScale2),  // tighter horizontal fan
        chipBorderConfig,
        chipCounterConfig, // Always show counter for DEV positioning
        displayAmount
      );
      insuranceChips.zIndex = 10;
      container.addChild(insuranceChips);
    }
  }

  return container;
}

// ===========================================
// DEALER BUTTON RENDERING
// ===========================================

const DEFAULT_DEALER_BUTTON_CONFIG: DealerButtonConfig = {
  x: 0,
  y: 0,
  size: 100,
  bgColor: '#FFFFFF',
  fontColor: '#111827',
  borderColor: '#111827',
  borderWidth: 2,
};

function createDealerButton(config: DealerButtonConfig = DEFAULT_DEALER_BUTTON_CONFIG): Container {
  const container = new Container();

  // Calculate scaled size (base is 24px)
  const baseSize = 24;
  const size = Math.round(baseSize * (config.size / 100));

  // Parse colors
  const bgColor = parseInt(config.bgColor.replace('#', ''), 16);
  const fontColor = parseInt(config.fontColor.replace('#', ''), 16);
  const borderColor = parseInt(config.borderColor.replace('#', ''), 16);

  // Draw circle background with border
  const circle = new Graphics();
  circle.circle(0, 0, size / 2);
  circle.fill({ color: bgColor });
  circle.stroke({ color: borderColor, width: config.borderWidth });
  container.addChild(circle);

  // Add "B" text
  const textStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: Math.round(12 * (config.size / 100)),
    fontWeight: 'bold',
    fill: fontColor,
  });
  const buttonText = new Text({ text: 'B', style: textStyle });
  buttonText.anchor.set(0.5);
  container.addChild(buttonText);

  return container;
}

// ===========================================
// UI BUTTON FACTORIES (migrated from React)
// ===========================================

// UI positions relative to table dimensions
// Staged bet overlaps bottom of table image, chip rail is below table in margin area
const UI_STAGED_BET_POS = { x: CANVAS_WIDTH / 2, y: TABLE_IMAGE_HEIGHT - 34 }; // 34px above table bottom
const UI_CHIP_RAIL_POS = { x: CANVAS_WIDTH / 2, y: TABLE_IMAGE_HEIGHT + 26 }; // 26px below table

// Chip denominations and their colors for the rail
const CHIP_RAIL_DENOMS = [1, 5, 25, 100, 500, 1000, 5000, 10000];
const CHIP_RAIL_COLORS: Record<number, number> = {
  1: 0xd1d5db,    // Gray-300
  5: 0xef4444,    // Red-500
  25: 0x22c55e,   // Green-500
  100: 0x111827,  // Gray-900
  500: 0x9333ea,  // Purple-600
  1000: 0xf97316, // Orange-500
  5000: 0xb45309, // Amber-700
  10000: 0xeab308,
};

/** All denominations for chip display (matching React version) */
const ALL_DENOMINATIONS = [1, 5, 25, 100, 500, 1000, 5000, 10000];
/** Maximum visible chips per denomination in staged display */
const MAX_VISIBLE_CHIPS = 3;
/** Vertical offset between stacked chips (pixels) */
const CHIP_STACK_OFFSET = 4;
/** Chip sizes matching React version */
const CHIP_SIZES = {
  rail: 48,         // Rail chip size (w-12 h-12) - player's balance
  staged: 24,       // Staged chip size (w-6 h-6) - bet reference
  kittyMedium: 28,  // Kitty chip for current player (w-7 h-7)
  kittySmall: 20,   // Kitty chip for opponents (w-5 h-5)
  dealerPile: 20,   // Dealer tray chip size (w-5 h-5)
};
/**
 * Get animation timing from config store.
 * Client-only animations (no server pacing) pass undefined as event duration,
 * so this always reads from the cached config loaded on table join.
 */
function getClientAnimationTiming(key: 'railToStaged' | 'stagedToRail' | 'stagedToKitty' | 'chipSwapDelay' | 'chipSwapOut' | 'chipSwapIn' | 'chipSwapInDelay' | 'railToInsuranceKitty' | 'insuranceKittyShift' | 'clearChipStagger' | 'buildYourBetPulse' | 'cardFlipInFlight' | 'cardNudge' | 'chipLandingScale' | 'chipFadeOut' | 'cardStraighten' | 'outcomeLabelFly', functionName: string): number {
  return getAnimationDuration(key, undefined, {
    fileName: 'PixiGameTableV1.tsx',
    functionName,
    eventType: 'CLIENT_UI', // Not a server event
  });
}

/** Initial chips per denomination in the pool (expands dynamically as needed) */
const CHIP_POOL_INITIAL_SIZE = 10;

/**
 * Persistent chip sprite with metadata.
 * These sprites live throughout the game session - no creation/destruction during animations.
 */
interface PersistentChip {
  sprite: Sprite;
  denomination: number;
  state: 'pool' | 'staged' | 'animating';
}

/** Staged bet box layout constants (non-configurable values) */
const STAGED_BET_LAYOUT = {
  padding: 8,             // Inner padding (constant)
  chipSize: 24,           // Chip size in staged area
  chipOverlap: -12,       // Horizontal overlap between stacks (-space-x-3)
};

/**
 * Calculate the target position for a flying chip landing in the staged bet area.
 * Returns the global position where the chip should land based on its denomination
 * and the current state of the staged chips array.
 */
function getStagedChipTargetPosition(
  denom: number,
  stagedChips: number[],
  stagedBetX: number,
  stagedBetY: number,
  stagedBetScale: number,
  boxWidth: number,
  boxBorderWidth: number
): { x: number; y: number } {
  const { padding, chipSize, chipOverlap } = STAGED_BET_LAYOUT;

  // Calculate the X offset for this denomination's stack
  let chipStackX = 0;
  for (const d of ALL_DENOMINATIONS) {
    if (d === denom) break;
    const count = stagedChips.filter(c => c === d).length;
    if (count > 0) {
      chipStackX += chipSize + chipOverlap;
    }
  }

  // Calculate the current count for this denomination (before adding the new chip)
  const currentCount = stagedChips.filter(c => c === denom).length;
  // The new chip will be at the top of the stack
  const newCount = Math.min(currentCount + 1, MAX_VISIBLE_CHIPS);

  // Calculate Y position (top of the new stack)
  const stackHeight = chipSize + (newCount - 1) * CHIP_STACK_OFFSET;
  const topOffset = -stackHeight / 2;
  // The newest chip is at the top (index newCount - 1)
  // chipY = topOffset (for the top chip)
  // But chips have anchor (0.5, 0), so center is at chipY + chipSize/2
  // Flying chip has anchor (0.5, 0.5), so it should target the center
  const chipCenterY = topOffset + chipSize / 2;

  // Calculate final position including container offset (left-justified with constant padding)
  const containerOffsetX = -boxWidth / 2 + boxBorderWidth + padding + chipSize / 2;

  const x = stagedBetX + (containerOffsetX + chipStackX) * stagedBetScale;
  const y = stagedBetY + chipCenterY * stagedBetScale;

  return { x, y };
}

/**
 * Calculate the source position for a chip animating FROM the staged bet area.
 * Used for CLEAR animation - calculates where the chip starts based on its
 * position in the stack (by index within same-denomination chips).
 */
function getStagedChipSourcePosition(
  denom: number,
  chipIndexInDenom: number,
  totalChipsOfDenom: number,
  stagedChips: number[],
  stagedBetX: number,
  stagedBetY: number,
  stagedBetScale: number,
  boxWidth: number,
  boxBorderWidth: number
): { x: number; y: number } {
  const { padding, chipSize, chipOverlap } = STAGED_BET_LAYOUT;

  // Calculate the X offset for this denomination's stack
  let chipStackX = 0;
  for (const d of ALL_DENOMINATIONS) {
    if (d === denom) break;
    const count = stagedChips.filter(c => c === d).length;
    if (count > 0) {
      chipStackX += chipSize + chipOverlap;
    }
  }

  // Calculate Y position based on chip index in the stack
  const visibleCount = Math.min(totalChipsOfDenom, MAX_VISIBLE_CHIPS);
  const stackHeight = chipSize + (visibleCount - 1) * CHIP_STACK_OFFSET;
  const topOffset = -stackHeight / 2;

  // chipIndexInDenom is 0 for the first chip of this denom to animate (bottom of stack visually)
  // But we want chips to animate from top to bottom, so invert the index
  const stackIndex = Math.min(chipIndexInDenom, visibleCount - 1);
  // In createStagedBetDisplay: chipY = topOffset + (count - 1 - i) * CHIP_STACK_OFFSET
  // where i=0 is bottom chip (highest stackIdx), i=count-1 is top chip (lowest stackIdx)
  // For animation, let's start from the top chip (most recent)
  const visualIndex = visibleCount - 1 - stackIndex;
  const chipY = topOffset + visualIndex * CHIP_STACK_OFFSET;
  const chipCenterY = chipY + chipSize / 2;

  // Calculate final position including container offset (left-justified with constant padding)
  const containerOffsetX = -boxWidth / 2 + boxBorderWidth + padding + chipSize / 2;

  const x = stagedBetX + (containerOffsetX + chipStackX) * stagedBetScale;
  const y = stagedBetY + chipCenterY * stagedBetScale;

  return { x, y };
}

/**
 * Get an available chip from the pool for a given denomination.
 * If pool is exhausted, creates a new chip dynamically (no hard limit).
 */
function getChipFromPool(
  chipPool: Map<number, PersistentChip[]>,
  denomination: number,
  textures?: Map<string, Texture>,
  uiLayer?: Container | null
): PersistentChip | null {
  const pool = chipPool.get(denomination);
  if (!pool) return null;

  const available = pool.find(chip => chip.state === 'pool');
  if (available) {
    available.state = 'animating';
    return available;
  }

  // Pool exhausted - dynamically create a new chip if we have the dependencies
  if (textures && uiLayer) {
    const chipTexture = textures.get(`chip-${denomination}`);
    if (chipTexture) {
      const sprite = new Sprite(chipTexture);
      sprite.anchor.set(0.5);
      sprite.visible = false;
      sprite.width = CHIP_SIZES.staged;
      sprite.height = CHIP_SIZES.staged;
      uiLayer.addChild(sprite);

      const newChip: PersistentChip = {
        sprite,
        denomination,
        state: 'animating',
      };
      pool.push(newChip);
      return newChip;
    }
  }

  console.warn(`[ChipPool] Could not create chip for denomination ${denomination}`);
  return null;
}

/**
 * Return a chip to the pool (hide it and mark as available).
 */
function returnChipToPool(chip: PersistentChip): void {
  chip.state = 'pool';
  chip.sprite.visible = false;
}

/**
 * Calculate the position for a chip in the staged area based on its denomination
 * and stack index. Used for both animation targets and static positioning.
 *
 * Returns both position and z-index for proper layering.
 */
function calculateStagedChipPosition(
  denom: number,
  stackIndex: number,  // 0 = bottom of stack, higher = toward top
  totalInStack: number,
  stagedBetX: number,
  stagedBetY: number,
  stagedBetScale: number,
  currentStagedDenoms: number[],  // Denominations currently staged (for X offset calculation)
  boxWidth: number,
  boxBorderWidth: number
): { x: number; y: number; zIndex: number } {
  const { padding, chipSize, chipOverlap } = STAGED_BET_LAYOUT;

  // Calculate the X offset for this denomination's stack
  // Also count how many denomination stacks are before this one (for z-index)
  let chipStackX = 0;
  let denomStackIndex = 0;
  for (const d of ALL_DENOMINATIONS) {
    if (d === denom) break;
    // Check if this denomination has any chips staged
    if (currentStagedDenoms.includes(d)) {
      chipStackX += chipSize + chipOverlap;
      denomStackIndex++;
    }
  }

  // Calculate Y position based on stack index
  const visibleCount = Math.min(totalInStack, MAX_VISIBLE_CHIPS);
  const stackHeight = chipSize + (visibleCount - 1) * CHIP_STACK_OFFSET;
  const topOffset = -stackHeight / 2;

  // stackIndex 0 is bottom (rendered first), higher indices are on top
  // In the visual stack: i=0 draws at bottom, i=count-1 draws at top
  // chipY = topOffset + (count - 1 - i) * CHIP_STACK_OFFSET
  const visualIndex = Math.min(stackIndex, visibleCount - 1);
  const chipY = topOffset + (visibleCount - 1 - visualIndex) * CHIP_STACK_OFFSET;
  const chipCenterY = chipY + chipSize / 2;

  // Calculate final position including container offset (left-justified with constant padding)
  const containerOffsetX = -boxWidth / 2 + boxBorderWidth + padding + chipSize / 2;

  const x = stagedBetX + (containerOffsetX + chipStackX) * stagedBetScale;
  const y = stagedBetY + chipCenterY * stagedBetScale;

  // Z-index: higher denomination stacks render on top (overlap to the right)
  // Within a stack, higher stackIndex renders on top
  // Use denomStackIndex * 10 + stackIndex to ensure proper layering
  const zIndex = denomStackIndex * 10 + stackIndex;

  return { x, y, zIndex };
}

/**
 * Compute the diff between two chip arrays (by denomination counts).
 * Returns which chips were removed and which were added.
 */
function chipDiff(oldChips: number[], newChips: number[]): { removed: number[]; added: number[] } {
  const oldCounts = new Map<number, number>();
  const newCounts = new Map<number, number>();
  for (const c of oldChips) oldCounts.set(c, (oldCounts.get(c) || 0) + 1);
  for (const c of newChips) newCounts.set(c, (newCounts.get(c) || 0) + 1);

  const removed: number[] = [];
  const added: number[] = [];

  const allDenoms = new Set([...oldCounts.keys(), ...newCounts.keys()]);
  for (const denom of allDenoms) {
    const diff = (newCounts.get(denom) || 0) - (oldCounts.get(denom) || 0);
    if (diff < 0) for (let i = 0; i < -diff; i++) removed.push(denom);
    if (diff > 0) for (let i = 0; i < diff; i++) added.push(denom);
  }
  return { removed, added };
}

/** Creates the staged bet display box (matches React StagedChipsDisplay styling) */
function createStagedBetDisplay(
  amount: number,
  displayedAmount: number,
  stagedChips: number[],
  textures: Map<string, Texture>,
  onClear?: () => void,
  alreadyBet: boolean = false,
  config: StagedBetConfig = DEFAULT_STAGED_BET_CONFIG
): Container {
  const container = new Container();

  const width = config.width;
  const height = config.height;
  const borderWidth = config.borderThickness;
  const clearButtonWidth = 32;
  const chipSize = 24; // Matches React w-6 h-6 staged chips

  // Get color based on amount (matches React getAmountColors)
  const getAmountColor = (amt: number): number => {
    if (amt === 0) return 0x9ca3af; // gray-400
    if (amt < 5) return 0xd1d5db; // gray-300
    if (amt < 25) return 0xef4444; // red-500
    if (amt < 100) return 0x22c55e; // green-500
    if (amt < 500) return 0x6b7280; // gray-500
    if (amt < 1000) return 0x9333ea; // purple-500
    if (amt < 5000) return 0xf97316; // orange-500
    if (amt < 10000) return 0xb45309; // amber-700
    return 0xf59e0b; // amber-500
  };

  const amountColor = getAmountColor(amount);
  const showClear = amount > 0 && onClear;

  // Background box with thick border
  const bg = new Graphics();
  const drawBg = (borderColor: number, borderAlpha: number) => {
    bg.clear();
    bg.roundRect(-width / 2, -height / 2, width, height, 8);
    bg.fill({ color: 0x000000 });
    bg.stroke({ color: borderColor, width: borderWidth, alpha: borderAlpha });
  };
  drawBg(amount > 0 ? amountColor : 0x000000, amount > 0 ? 1 : 0);
  container.addChild(bg);

  // NOTE: Removed "Bet Placed" display - staged bet is for the NEXT round,
  // so players should always be able to view and modify their staged bet during gameplay
  if (amount === 0) {
    // Show "Build your bet." with GOLDEN PULSING animation (border and text)
    // Pulses from gray-400 (#9ca3af) to amber-400 (#fbbf24)
    const GRAY_COLOR = 0x9ca3af;
    const GOLD_COLOR = 0xfbbf24;
    const PULSE_DURATION = getClientAnimationTiming('buildYourBetPulse', 'createStagedBetBox');

    const textStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: config.buildYourBetFontSize ?? 28,
      fill: GRAY_COLOR,
    });
    const placeholderText = new Text({ text: 'Build your bet.', style: textStyle });
    placeholderText.anchor.set(0.5);
    container.addChild(placeholderText);

    // Color interpolation helper
    const lerpColor = (color1: number, color2: number, t: number): number => {
      const r1 = (color1 >> 16) & 0xff;
      const g1 = (color1 >> 8) & 0xff;
      const b1 = color1 & 0xff;
      const r2 = (color2 >> 16) & 0xff;
      const g2 = (color2 >> 8) & 0xff;
      const b2 = color2 & 0xff;
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      return (r << 16) | (g << 8) | b;
    };

    // Pulsing animation loop
    const startTime = performance.now();
    const animatePulse = () => {
      if (container.destroyed) return; // Stop if container was removed

      const elapsed = (performance.now() - startTime) % PULSE_DURATION;
      const progress = elapsed / PULSE_DURATION;
      // Ease-in-out: use sine wave for smooth pulse (0 -> 1 -> 0)
      const t = (Math.sin(progress * Math.PI * 2 - Math.PI / 2) + 1) / 2;

      const currentColor = lerpColor(GRAY_COLOR, GOLD_COLOR, t);

      // Update text color
      placeholderText.style.fill = currentColor;

      // Update border color
      drawBg(currentColor, 1);

      requestAnimationFrame(animatePulse);
    };
    requestAnimationFrame(animatePulse);
  } else {
    // Padding for both chips and amount (same value for symmetry)
    const padding = 8;

    // Create chip stacks container (left side)
    const chipsContainer = new Container();
    let chipStackX = 0;
    const chipOverlap = -12; // Horizontal overlap between denomination stacks (matches React -space-x-3 = -0.75rem = -12px)

    // Render chip stacks for each denomination that has chips
    for (const denom of ALL_DENOMINATIONS) {
      const count = Math.min(stagedChips.filter(c => c === denom).length, MAX_VISIBLE_CHIPS);
      if (count === 0) continue;

      const stackContainer = new Container();
      stackContainer.x = chipStackX;

      // Calculate stack height and center offset
      const stackHeight = chipSize + (count - 1) * CHIP_STACK_OFFSET;
      const topOffset = -stackHeight / 2;

      // Render each chip in the stack (bottom to top)
      for (let i = 0; i < count; i++) {
        const chipY = topOffset + (count - 1 - i) * CHIP_STACK_OFFSET;
        const chipTexture = textures.get(`chip-${denom}`);

        if (chipTexture) {
          const chipSprite = new Sprite(chipTexture);
          chipSprite.width = chipSize;
          chipSprite.height = chipSize;
          chipSprite.anchor.set(0.5, 0);
          chipSprite.y = chipY;
          // Random rotation for natural chip appearance (0-359 degrees)
          chipSprite.rotation = Math.random() * 2 * Math.PI;
          stackContainer.addChild(chipSprite);
        } else {
          // Fallback: draw a colored circle
          const chipGraphic = new Graphics();
          chipGraphic.circle(0, chipY + chipSize / 2, chipSize / 2);
          chipGraphic.fill({ color: CHIP_RAIL_COLORS[denom] || 0xffffff });
          chipGraphic.stroke({ color: 0xffffff, width: 1 });
          stackContainer.addChild(chipGraphic);
        }
      }

      chipsContainer.addChild(stackContainer);
      chipStackX += chipSize + chipOverlap;
    }

    // Position chips container to the left (left-justified with padding from inner border)
    // Chips are center-anchored, so add chipSize/2 to get left edge at padding
    if (chipsContainer.children.length > 0) {
      chipsContainer.x = -width / 2 + borderWidth + padding + chipSize / 2;
      container.addChild(chipsContainer);
    }

    // Show amount text on the right (right-justified with padding)
    const symbolStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: config.fontSize,
      fontWeight: 'normal',
      fill: amountColor,
    });
    const amountStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: config.fontSize,
      fontWeight: 'bold',
      fill: amountColor,
    });

    const amountTextContainer = new Container();
    const symbolText = new Text({ text: 'BB ', style: symbolStyle });
    // Use displayedAmount for the counter (animated value)
    const numberText = new Text({ text: displayedAmount.toLocaleString(), style: amountStyle });
    numberText.x = symbolText.width;

    amountTextContainer.addChild(symbolText);
    amountTextContainer.addChild(numberText);
    amountTextContainer.pivot.y = amountTextContainer.height / 2;

    // Position amount text: right-justified with same padding (before CLEAR button if visible)
    // Right edge of text area = box right - border - padding - CLEAR button (if visible)
    const textRightEdge = width / 2 - borderWidth - padding - (showClear ? clearButtonWidth : 0);
    amountTextContainer.x = textRightEdge - amountTextContainer.width;

    container.addChild(amountTextContainer);

    // Vertical CLEAR button on right edge (matches React version)
    if (showClear) {
      const clearBtn = new Container();
      clearBtn.eventMode = 'static';
      clearBtn.cursor = 'pointer';
      clearBtn.x = width / 2 - clearButtonWidth / 2;

      // Clear button background (rounded on right side only)
      const clearBg = new Graphics();
      clearBg.roundRect(-clearButtonWidth / 2, -height / 2, clearButtonWidth, height, 8);
      clearBg.fill({ color: amountColor });
      clearBtn.addChild(clearBg);

      // Vertical "CLEAR" text
      const clearStyle = new TextStyle({
        fontFamily: 'Arial',
        fontSize: config.clearFontSize,
        fontWeight: 'bold',
        fill: 0xffffff,
        letterSpacing: 1,
      });
      const clearText = new Text({ text: 'CLEAR', style: clearStyle });
      clearText.anchor.set(0.5);
      clearText.rotation = Math.PI / 2; // Rotate 90 degrees for vertical text
      clearBtn.addChild(clearText);

      clearBtn.on('pointerdown', (e) => {
        e.stopPropagation();
        onClear();
      });

      container.addChild(clearBtn);
    }
  }

  return container;
}

/**
 * Creates the staged bet frame (background, amount text, clear button) WITHOUT chips.
 * Chips are managed separately by the persistent chip system.
 * Returns individual components for independent updates.
 */
function createStagedBetFrame(
  amount: number,
  onClear?: () => void,
  config: StagedBetConfig = DEFAULT_STAGED_BET_CONFIG
): {
  container: Container;
  background: Graphics;
  amountText: Container;
  clearButton: Container | null;
  updateAmount: (newAmount: number, displayedAmount: number) => void;
} {
  const container = new Container();

  const width = config.width;
  const height = config.height;
  const borderWidth = config.borderThickness;
  const clearButtonWidth = 32;

  // Get color based on amount (matches React getAmountColors)
  const getAmountColor = (amt: number): number => {
    if (amt === 0) return 0x9ca3af; // gray-400
    if (amt < 5) return 0xd1d5db; // gray-300
    if (amt < 25) return 0xef4444; // red-500
    if (amt < 100) return 0x22c55e; // green-500
    if (amt < 500) return 0x6b7280; // gray-500
    if (amt < 1000) return 0x9333ea; // purple-500
    if (amt < 5000) return 0xf97316; // orange-500
    if (amt < 10000) return 0xb45309; // amber-700
    return 0xf59e0b; // amber-500
  };

  // Background box
  const bg = new Graphics();
  const drawBg = (borderColor: number, borderAlpha: number) => {
    bg.clear();
    bg.roundRect(-width / 2, -height / 2, width, height, 8);
    bg.fill({ color: 0x000000 });
    bg.stroke({ color: borderColor, width: borderWidth, alpha: borderAlpha });
  };

  // Amount text container
  const amountTextContainer = new Container();
  const symbolStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: config.fontSize,
    fontWeight: 'normal',
    fill: 0xffffff,
  });
  const amountStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: config.fontSize,
    fontWeight: 'bold',
    fill: 0xffffff,
  });
  const symbolText = new Text({ text: 'BB ', style: symbolStyle });
  const numberText = new Text({ text: amount.toLocaleString(), style: amountStyle });
  numberText.x = symbolText.width;
  amountTextContainer.addChild(symbolText);
  amountTextContainer.addChild(numberText);
  amountTextContainer.pivot.y = amountTextContainer.height / 2;

  // Clear button (initially null, created when amount > 0)
  let clearBtn: Container | null = null;

  // Pulsing placeholder for empty state
  let placeholderText: Text | null = null;
  let pulseAnimationId: number | null = null;

  // Update function for reactive amount changes
  const updateAmount = (newAmount: number, displayedAmount: number) => {
    const amountColor = getAmountColor(newAmount);
    const showClear = newAmount > 0 && onClear;

    // Update background border
    drawBg(newAmount > 0 ? amountColor : 0x000000, newAmount > 0 ? 1 : 0);

    if (newAmount === 0) {
      // Show "Build your bet." placeholder with pulsing animation
      amountTextContainer.visible = false;

      if (!placeholderText) {
        const GRAY_COLOR = 0x9ca3af;
        const GOLD_COLOR = 0xfbbf24;
        const PULSE_DURATION = getClientAnimationTiming('buildYourBetPulse', 'updateStagedBetAmount');

        const fontSize = config.buildYourBetFontSize ?? 28;
        console.log('[DEBUG BYB FONT] Creating placeholder text with fontSize:', fontSize, 'config:', config);
        placeholderText = new Text({
          text: 'Build your bet.',
          style: new TextStyle({ fontFamily: 'Arial', fontSize, fill: GRAY_COLOR }),
        });
        placeholderText.anchor.set(0.5);
        container.addChild(placeholderText);

        const lerpColor = (color1: number, color2: number, t: number): number => {
          const r1 = (color1 >> 16) & 0xff, g1 = (color1 >> 8) & 0xff, b1 = color1 & 0xff;
          const r2 = (color2 >> 16) & 0xff, g2 = (color2 >> 8) & 0xff, b2 = color2 & 0xff;
          return (Math.round(r1 + (r2 - r1) * t) << 16) |
                 (Math.round(g1 + (g2 - g1) * t) << 8) |
                 Math.round(b1 + (b2 - b1) * t);
        };

        const startTime = performance.now();
        const animatePulse = () => {
          if (container.destroyed || !placeholderText) return;
          const elapsed = (performance.now() - startTime) % PULSE_DURATION;
          const t = (Math.sin((elapsed / PULSE_DURATION) * Math.PI * 2 - Math.PI / 2) + 1) / 2;
          const currentColor = lerpColor(GRAY_COLOR, GOLD_COLOR, t);
          placeholderText.style.fill = currentColor;
          drawBg(currentColor, 1);
          pulseAnimationId = requestAnimationFrame(animatePulse);
        };
        pulseAnimationId = requestAnimationFrame(animatePulse);
      }

      // Hide clear button
      if (clearBtn) {
        clearBtn.visible = false;
      }
    } else {
      // Show amount text
      amountTextContainer.visible = true;

      // Remove placeholder and stop animation
      if (placeholderText) {
        container.removeChild(placeholderText);
        placeholderText.destroy();
        placeholderText = null;
        if (pulseAnimationId !== null) {
          cancelAnimationFrame(pulseAnimationId);
          pulseAnimationId = null;
        }
      }

      // Update text color and value
      symbolText.style.fill = amountColor;
      (amountStyle as TextStyle).fill = amountColor;
      numberText.style = amountStyle;
      numberText.text = displayedAmount.toLocaleString();
      numberText.x = symbolText.width;
      amountTextContainer.pivot.y = amountTextContainer.height / 2;

      // Position amount text
      const textRightEdge = width / 2 - borderWidth - 8 - (showClear ? clearButtonWidth : 0);
      amountTextContainer.x = textRightEdge - amountTextContainer.width;

      // Show/create clear button
      if (showClear) {
        if (!clearBtn) {
          clearBtn = new Container();
          clearBtn.eventMode = 'static';
          clearBtn.cursor = 'pointer';
          clearBtn.x = width / 2 - clearButtonWidth / 2;

          const clearBg = new Graphics();
          clearBg.roundRect(-clearButtonWidth / 2, -height / 2, clearButtonWidth, height, 8);
          clearBg.fill({ color: amountColor });
          clearBtn.addChild(clearBg);

          const clearStyle = new TextStyle({
            fontFamily: 'Arial',
            fontSize: config.clearFontSize,
            fontWeight: 'bold',
            fill: 0xffffff,
            letterSpacing: 1,
          });
          const clearText = new Text({ text: 'CLEAR', style: clearStyle });
          clearText.anchor.set(0.5);
          clearText.rotation = Math.PI / 2;
          clearBtn.addChild(clearText);

          clearBtn.on('pointerdown', (e) => {
            e.stopPropagation();
            onClear?.();
          });

          container.addChild(clearBtn);
        } else {
          clearBtn.visible = true;
          // Update clear button color
          const clearBg = clearBtn.children[0] as Graphics;
          clearBg.clear();
          clearBg.roundRect(-clearButtonWidth / 2, -height / 2, clearButtonWidth, height, 8);
          clearBg.fill({ color: amountColor });
        }
      } else if (clearBtn) {
        clearBtn.visible = false;
      }
    }
  };

  // Initial render
  container.addChild(bg);
  container.addChild(amountTextContainer);
  updateAmount(amount, amount);

  return {
    container,
    background: bg,
    amountText: amountTextContainer,
    clearButton: clearBtn,
    updateAmount,
  };
}

/** Creates a single clickable chip for the rail (matches React FieldChipRail styling) */
function createRailChip(
  denomination: number,
  textures: Map<string, Texture>,
  enabled: boolean,
  onClick?: () => void,
  labelYOffset: number = 0,
  labelPillOpacity: number = 0,
  rotation?: number  // Optional rotation - if provided, use it; otherwise chip has no rotation
): Container {
  const container = new Container();
  container.eventMode = 'static';
  container.cursor = enabled ? 'pointer' : 'not-allowed';

  const size = 48; // Matches React FieldChipRail w-12 h-12
  const borderWidth = 6;

  // Try to use chip texture, fallback to graphics
  const chipTexture = textures.get(`chip-${denomination}`);
  if (chipTexture) {
    const sprite = new Sprite(chipTexture);
    sprite.width = size;
    sprite.height = size;
    sprite.anchor.set(0.5);
    sprite.alpha = enabled ? 1 : 0.4;
    // Use provided rotation (persisted) for stable appearance across re-renders
    if (rotation !== undefined) {
      sprite.rotation = rotation;
    }
    container.addChild(sprite);

    // Border ring (thicker, matching React version)
    const border = new Graphics();
    border.circle(0, 0, size / 2 + borderWidth / 2);
    border.stroke({ color: CHIP_RAIL_COLORS[denomination] || 0xffffff, width: borderWidth });
    border.alpha = enabled ? 1 : 0.4;
    container.addChild(border);
  } else {
    // Fallback: draw chip as graphics
    const chipColor = CHIP_RAIL_COLORS[denomination] || 0x888888;
    const chip = new Graphics();
    chip.circle(0, 0, size / 2);
    chip.fill({ color: chipColor, alpha: enabled ? 1 : 0.4 });
    chip.stroke({ color: 0xffffff, width: borderWidth });
    container.addChild(chip);

    // Denomination label
    const labelText = denomination >= 1000 ? `${denomination / 1000}K` : denomination.toString();
    const labelStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: denomination >= 1000 ? 12 : 14,
      fontWeight: 'bold',
      fill: denomination === 100 ? 0xffffff : (denomination === 1 ? 0x111827 : 0xffffff),
    });
    const label = new Text({ text: labelText, style: labelStyle });
    label.anchor.set(0.5);
    label.alpha = enabled ? 1 : 0.4;
    container.addChild(label);
  }

  // Denomination label below chip (font size matches chip counter)
  const labelFontSize = 14;
  const denomText = denomination >= 1000 ? `${denomination / 1000}K` : denomination.toString();
  const denomStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: labelFontSize,
    fontWeight: 'bold',
    fill: 0xffffff,
  });
  const denomLabel = new Text({ text: denomText, style: denomStyle });
  denomLabel.anchor.set(0.5, 0);
  const baseLabelY = size / 2 + 4;
  denomLabel.y = baseLabelY + labelYOffset;
  denomLabel.alpha = enabled ? 0.9 : 0.4;

  // Pill background for label (if opacity > 0)
  // Use uniform width for all pills (sized to fit longest label "10K")
  if (labelPillOpacity > 0) {
    const pillPadding = { x: 6, y: 2 };
    const uniformPillWidth = 36; // Fixed width to accommodate all denominations uniformly
    const pillHeight = labelFontSize + pillPadding.y * 2;
    const pill = new Graphics();
    pill.roundRect(-uniformPillWidth / 2, baseLabelY + labelYOffset - pillPadding.y, uniformPillWidth, pillHeight, 6);
    pill.fill({ color: 0x000000, alpha: (labelPillOpacity / 100) * (enabled ? 1 : 0.4) });
    container.addChild(pill);
  }

  container.addChild(denomLabel);

  // Click handler
  if (onClick && enabled) {
    container.on('pointerdown', onClick);
  }

  return container;
}

// ===========================================
// DEALER AREA RENDERING
// ===========================================

function createDealerArea(
  dealer: DealerState,
  dealerPersona: DealerPersona | null | undefined,
  textures: Map<string, Texture>,
  cardConfig: CardConfig = DEFAULT_CARD_CONFIG,
  seatConfig: { x: number; y: number; rot: number; scale: number } = DEFAULT_SEAT_CONFIG,
  baseSizeScale: number = 100,
  scoreboxConfig: ScoreboxConfig = DEFAULT_SCOREBOX_CONFIG,
  scoreboxOffset: { x: number; y: number } = { x: 0, y: 0 }
): Container {
  const container = new Container();
  // Apply per-seat config offsets to dealer position
  container.x = DEALER_POSITION.x + seatConfig.x;
  container.y = DEALER_POSITION.y + seatConfig.y;
  container.rotation = degreesToRadians(seatConfig.rot);
  const effectiveScale = (baseSizeScale / 100) * (seatConfig.scale / 100);
  container.scale.set(effectiveScale);

  const width = 144;
  const height = 140;

  // Enable z-index sorting
  container.sortableChildren = true;

  // Seat box (lowest z-index)
  const box = new Graphics();
  box.roundRect(-width / 2, -height / 2, width, height, 8);
  box.stroke({ color: 0x6b7280, width: 2, alpha: 0.5 });
  box.fill({ color: 0x166534, alpha: 0.4 });
  box.zIndex = 0;
  container.addChild(box);

  // Dealer label at top of seat - use persona name if available
  const dealerName = dealerPersona
    ? `${dealerPersona.name} ${dealerPersona.lastName}`
    : 'Dealer';
  const nameStyle = new TextStyle({
    fontFamily: 'Arial',
    fontSize: 11,
    fill: 0xffffff,
  });
  const nameText = new Text({ text: dealerName, style: nameStyle });
  nameText.anchor.set(0.5, 0);
  nameText.y = -height / 2 + 1; // Raised 5px from original +6
  nameText.zIndex = 4;
  container.addChild(nameText);

  // Dealer avatar - positioned at bottom-right corner (matching player seats)
  // Avatar center is exactly on the corner (border intersects avatar in half)
  const avatarSize = 48;
  const avatarX = width / 2;
  const avatarY = height / 2;

  // Check if we have a loaded avatar texture
  const avatarTextureKey = dealerPersona?.avatarUrl ? `avatar-${dealerPersona.avatarUrl}` : null;
  const avatarTexture = avatarTextureKey ? textures.get(avatarTextureKey) : null;

  if (avatarTexture) {
    // Use avatar image with circular mask
    const avatarSprite = new Sprite(avatarTexture);
    avatarSprite.anchor.set(0.5);
    avatarSprite.width = avatarSize;
    avatarSprite.height = avatarSize;
    avatarSprite.x = avatarX;
    avatarSprite.y = avatarY;
    avatarSprite.zIndex = 20;

    // Create circular mask
    const avatarMask = new Graphics();
    avatarMask.circle(avatarX, avatarY, avatarSize / 2);
    avatarMask.fill({ color: 0xffffff });
    avatarSprite.mask = avatarMask;

    // Border ring
    const avatarBorder = new Graphics();
    avatarBorder.circle(avatarX, avatarY, avatarSize / 2);
    avatarBorder.stroke({ color: 0xffffff, width: 2 });
    avatarBorder.zIndex = 21;

    container.addChild(avatarMask);
    container.addChild(avatarSprite);
    container.addChild(avatarBorder);
  } else {
    // Fallback: "D" initial on red background
    const avatarBg = new Graphics();
    avatarBg.circle(avatarX, avatarY, avatarSize / 2);
    avatarBg.fill({ color: 0xdc2626 }); // Red for dealer
    avatarBg.stroke({ color: 0xffffff, width: 2 });
    avatarBg.zIndex = 20;
    container.addChild(avatarBg);

    // Dealer "D" initial
    const avatarTextStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fontWeight: 'bold',
      fill: 0xffffff,
    });
    const avatarText = new Text({ text: 'D', style: avatarTextStyle });
    avatarText.anchor.set(0.5);
    avatarText.x = avatarX;
    avatarText.y = avatarY;
    avatarText.zIndex = 21;
    container.addChild(avatarText);
  }

  // NOTE: Cards are NOT rendered here. The animation layer handles all card rendering
  // from deal to rake. This avoids duplicate cards and simplifies the architecture.

  // Score box - ALWAYS visible (persistent element), shows "--" when no cards
  // Only count cards that have finished their deal animation (landed)
  const dealtCardCount = MODULE_DEALT_CARD_COUNTS.get('dealer-0') ?? 0;
  const visibleCardCount = Math.min(dealer.cards.length, dealtCardCount);
  const hasCards = visibleCardCount > 0;
  const dealerCacheKey = 'dealer';

  let displayScore = 0;
  let displaySoft = false;
  let showBlackjack = false;
  let showBusted = false;
  let scoreDisplayOverride: string | undefined = undefined;

  if (hasCards) {
    // Calculate score based on visible cards only
    // Need to recalculate since dealer.score includes all cards
    let visibleScore = 0;
    let aces = 0;
    for (let i = 0; i < visibleCardCount; i++) {
      const card = dealer.cards[i];
      // Skip face-down cards (hole card at index 1 before reveal)
      if (!card.faceUp) continue;
      if (card.rank === 'A') {
        visibleScore += 11;
        aces++;
      } else if (['K', 'Q', 'J'].includes(card.rank)) {
        visibleScore += 10;
      } else {
        visibleScore += parseInt(card.rank);
      }
    }
    // Reduce aces while busted
    while (visibleScore > 21 && aces > 0) {
      visibleScore -= 10;
      aces--;
    }
    const isSoft = aces > 0 && visibleScore <= 21;

    // Use full score if hole card is revealed and all cards have landed
    const showFullScore = dealer.holeCardRevealed && visibleCardCount === dealer.cards.length;
    displayScore = showFullScore ? dealer.score : visibleScore;
    displaySoft = showFullScore ? (dealer.isSoft && dealer.holeCardRevealed) : isSoft;
    showBlackjack = dealer.isBlackjack && showFullScore;
    showBusted = dealer.isBusted && showFullScore;

    // Cache the score text for persistence until rake
    const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();
    const cacheText = showBlackjack ? 'BJ' : showBusted ? 'BUST' : padScore(displayScore);
    MODULE_SCOREBOX_CACHE.set(dealerCacheKey, cacheText);
  } else {
    // No cards - use cached score if available (persists until rake clears it)
    const cachedScore = MODULE_SCOREBOX_CACHE.get(dealerCacheKey);
    if (cachedScore) {
      scoreDisplayOverride = cachedScore;
    } else {
      scoreDisplayOverride = '--';
    }
  }

  // Create scorebox - shows cached score or "--" when hasCards is false
  // Use config minWidth to match player scoreboxes
  const scoreBox = createScoreBox(
    displayScore,
    displaySoft,
    showBlackjack,
    showBusted,
    scoreboxConfig,
    scoreDisplayOverride,
    scoreboxConfig.minWidth  // Pass minWidth from config
  );
  scoreBox.x = -width / 2 + scoreboxOffset.x;
  scoreBox.y = height / 2 - 20 + scoreboxOffset.y;
  scoreBox.zIndex = 6;
  // Counter-scale the scorebox so it maintains fixed size despite container scaling
  // This ensures the dealer scorebox matches the size of player scoreboxes
  scoreBox.scale.set(1 / effectiveScale);
  container.addChild(scoreBox);

  // Monitor for height changes (DEBUG)
  monitorScoreboxHeight('dealer', scoreBox, `createDealerArea scale=${(1/effectiveScale).toFixed(3)}`);

  return container;
}

// ===========================================
// COMPONENT
// ===========================================

export const PixiGameTableV1 = forwardRef<PixiGameTableV1Ref, PixiGameTableV1Props>(function PixiGameTableV1(
  { dealer, dealerPersona, players, mySeatPosition, currentTurnSeat, currentHandIndex, onSeatClick, seatRadiusScale = 100, seatGroupOffset = { x: 0, y: 0 }, seatSizeScale = 100, shoeConfig, trayConfig, shoeCardsConfig, seatConfigs = {}, dealerScoreboxOffset = { x: 0, y: 0 }, betChipConfigs = {}, insuranceChipConfigs = {}, scoreboxConfig = DEFAULT_SCOREBOX_CONFIG, outcomeLabelConfig = DEFAULT_OUTCOME_CONFIG, chipBorderConfig = DEFAULT_CHIP_BORDER_CONFIG, chipCounterConfig = DEFAULT_CHIP_COUNTER_CONFIG, kittyConfig = DEFAULT_KITTY_CONFIG, sidebetIconConfig, opponentKittyConfig, opponentKittyConfigs, opponentSidebetIconConfig, sidebetIconConfigs, shoeCardOffsets = DEFAULT_SHOE_CARD_OFFSETS, dealerButtonConfig, dealerButtonSeat = 4, onChipClick, onClearBet, stagedAmount = 0, stagedChips = [], displayedAmount, balance = 0, chipRailConfig = DEFAULT_CHIP_RAIL_CONFIG, stagedBetConfig = DEFAULT_STAGED_BET_CONFIG, trayChipsOffset = { x: 0, y: 0 }, cardConfig = DEFAULT_CARD_CONFIG, onChipAnimationStart, onChipAnimationEnd, onStraightenCards, showBetChips = false, showInsuranceChips = false, showHandLabels = false, handLabelConfig = { x: 0, y: 12 }, handLabelConfigs = {}, devPopulateTable = false, chipYOffsets = {}, chipXOffsets = {}, chipLabelYOffsets = {}, chipLabelPillOpacity = 0, usernameY = 0, headerHeight = 56, availableWidth, availableHeight, usePixiControlPanel = false, tableId, onHit, onStand, onDouble, onSplit, onSurrender, onBet, onAutobet, onInsuranceAccept, onInsuranceDecline, onHowToBetClose, panelTextSizes, beamConfig, devShowBeam = false, devBeamSeat = 4, kittyAmount = 0, onSidebetPlace, onSidebetRemove, onSidebetIconClick, isConnected = true, onControlPanelPositionChange, buddyActive = false, penetration = 0, trueCount = 0, buddyHudConfig, perSeatCardConfigs = {}, devCardSeat = null, leftCornerCardConfig, rightCornerCardConfig },
  ref
) {
  // Dynamic seat radius based on scale prop
  const effectiveSeatRadiusScale = SEAT_RADIUS_SCALE * (seatRadiusScale / 100);

  // Effective BUDDY HUD config - merge props with defaults (locked-in values)
  const effectiveBuddyHudConfig = useMemo(() => ({
    dpX: buddyHudConfig?.dpX ?? 247,
    tcX: buddyHudConfig?.tcX ?? 1065,
    y: buddyHudConfig?.y ?? 30,
    barY: buddyHudConfig?.barY ?? 0,
    barWidth: buddyHudConfig?.barWidth ?? 189,
    barHeight: buddyHudConfig?.barHeight ?? 4,
    labelFontSize: buddyHudConfig?.labelFontSize ?? 36,
    valueFontSize: buddyHudConfig?.valueFontSize ?? 16,
    tickLabelFontSize: buddyHudConfig?.tickLabelFontSize ?? 12,
  }), [buddyHudConfig]);
  const wrapperRef = useRef<HTMLDivElement>(null); // Outer container for measuring available space
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const texturesRef = useRef<Map<string, Texture>>(new Map());
  const initStartedRef = useRef(false);
  const destroyRequestedRef = useRef(false); // Set by destroy() to abort in-progress init
  const [loading, setLoading] = useState(true);
  // Trigger re-render when avatar textures finish loading (refs don't cause re-renders)
  const [avatarTexturesVersion, setAvatarTexturesVersion] = useState(0);
  const loadingRef = useRef(true); // Ref to avoid closure issues in imperative handles
  const pendingResyncRef = useRef(false); // Track if fullVisualResync was requested but couldn't complete
  const cardConfigRef = useRef(cardConfig); // Ref to avoid closure issues in imperative handles
  const perSeatCardConfigsRef = useRef(perSeatCardConfigs); // Ref for per-seat card positioning (DEV CONTROLS)
  const handLabelConfigRef = useRef(handLabelConfig); // Ref for global hand label positioning
  const handLabelConfigsRef = useRef(handLabelConfigs); // Ref for per-seat hand label positioning
  const kittyAmountRef = useRef(kittyAmount); // Ref for sidebet modal calculations
  const onSidebetPlaceRef = useRef(onSidebetPlace); // Ref for sidebet callback
  const onSidebetRemoveRef = useRef(onSidebetRemove); // Ref for sidebet removal callback
  const buddyHudConfigRef = useRef(effectiveBuddyHudConfig); // Ref for BUDDY HUD config
  // Effective sidebet config - merges prop values with defaults
  const sidebetConfigRef = useRef({
    baseRadiusOffset: sidebetIconConfig?.baseRadiusOffset ?? -217,
    scale: sidebetIconConfig?.scale ?? 100,
    slotPositions: [
      { localX: sidebetIconConfig?.slot0?.x ?? -45, localY: sidebetIconConfig?.slot0?.y ?? 0 },
      { localX: sidebetIconConfig?.slot1?.x ?? 0, localY: sidebetIconConfig?.slot1?.y ?? -28 },
      { localX: sidebetIconConfig?.slot2?.x ?? 45, localY: sidebetIconConfig?.slot2?.y ?? 0 },
    ],
    iconSize: SIDEBET_ICON_SIZE * ((sidebetIconConfig?.scale ?? 100) / 100),
    getPositionsForCount: SIDEBET_CONFIG.getPositionsForCount,
  });
  // Opponent-specific sidebet config - falls back to sidebetIconConfig if not provided
  const opponentSidebetConfigRef = useRef({
    baseRadiusOffset: opponentSidebetIconConfig?.baseRadiusOffset ?? sidebetIconConfig?.baseRadiusOffset ?? -217,
    scale: opponentSidebetIconConfig?.scale ?? sidebetIconConfig?.scale ?? 100,
    slotPositions: [
      { localX: opponentSidebetIconConfig?.slot0?.x ?? sidebetIconConfig?.slot0?.x ?? -45, localY: opponentSidebetIconConfig?.slot0?.y ?? sidebetIconConfig?.slot0?.y ?? 0 },
      { localX: opponentSidebetIconConfig?.slot1?.x ?? sidebetIconConfig?.slot1?.x ?? 0, localY: opponentSidebetIconConfig?.slot1?.y ?? sidebetIconConfig?.slot1?.y ?? -28 },
      { localX: opponentSidebetIconConfig?.slot2?.x ?? sidebetIconConfig?.slot2?.x ?? 45, localY: opponentSidebetIconConfig?.slot2?.y ?? sidebetIconConfig?.slot2?.y ?? 0 },
    ],
    iconSize: SIDEBET_ICON_SIZE * ((opponentSidebetIconConfig?.scale ?? sidebetIconConfig?.scale ?? 100) / 100),
    getPositionsForCount: SIDEBET_CONFIG.getPositionsForCount,
  });
  const showSidebetModalRef = useRef<((sidebetType: string) => void) | null>(null); // Ref for show function
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  // Track actual canvas dimensions for dynamic sizing
  const [canvasDimensions, setCanvasDimensions] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });

  // Track panel position for layout adjustments - initialize from stored position
  const [pixiPanelPosition, setPixiPanelPosition] = useState<ControlPanelPosition>(() => {
    if (tableId) {
      const stored = getStoredPosition(tableId);
      if (stored) {
        console.log('[PixiGameTableV1] Initializing panel position from storage:', stored);
        return stored;
      }
    }
    return 'bottom';
  });

  // Notify parent when control panel position changes (for SidePanel mirroring)
  useEffect(() => {
    onControlPanelPositionChange?.(pixiPanelPosition);
  }, [pixiPanelPosition, onControlPanelPositionChange]);

  // Scene containers
  const sceneRef = useRef<{
    gameContentContainer: Container | null;  // Wrapper for all game content (shifted for panel)
    tableLayer: Container | null;
    tableSprite: Sprite | null;  // Table image sprite for bounds calculation
    seatsLayer: Container | null;
    outcomeLabelLayer: Container | null;
    scoreboxLayer: Container | null;
    highlightLayer: Container | null;
    avatarLayer: Container | null;
    cardsLayer: Container | null;
    badgeLayer: Container | null;
    animationLayer: Container | null;
    uiLayer: Container | null;
    scorecardOverlay: Container | null;  // Stage-level overlay for scorecard (above control panel)
    seatContainers: Map<number, Container>;
    dealerContainer: Container | null;
    shoeSprite: Sprite | null;
    traySprite: Sprite | null;
    shoeCardSprites: Sprite[];
    shoeMask: Graphics | null;
    dealerButtonContainer: Container | null;
    trayChipsContainer: Container | null;
    trayChipSprites: Map<number, Sprite>;  // Denomination -> tray chip sprite (for rotation updates)
    // UI elements (migrated from React)
    stagedBetContainer: Container | null;
    chipRailContainer: Container | null;
    chipRailChips: Map<number, Container>;
    // Persistent chip system - chips live throughout game session
    chipPool: Map<number, PersistentChip[]>;        // Available chips per denomination
    stagedChipSprites: PersistentChip[];            // Chips currently in staged area
    stagedBetFrame: Container | null;               // Static frame/background
    stagedBetAmountText: Text | null;               // Amount display (updates reactively)
    stagedBetClearButton: Container | null;         // CLEAR button
    forceChipResync: boolean;                       // Flag to force chip recreation after fullVisualResync
    // Kitty display system - tracks chips bet into kitty per seat
    kittyContainers: Map<number, Container>;        // Container per visual seat position
    kittyChipsWrappers: Map<number, Container>;     // Wrapper for chips/borders - rotates independently of counter
    kittyData: Map<number, { denominations: number[], total: number }>; // Chip data per seat
    kittyChipSprites: Map<number, Map<number, KittyChipData>>; // visualPos -> (denomination -> KittyChipData)
    kittyCounterTexts: Map<number, Text>;           // Counter text per seat
    kittyPills: Map<number, Graphics>;              // Counter pill background per seat
    kittyBasePositions: Map<number, { x: number; y: number; rotRad: number }>; // Base position before centering offset
    // PixiJS Control Panel
    controlPanel: PixiControlPanel | null;
    // PixiJS Insurance Panel
    insurancePanel: PixiInsurancePanel | null;
    // PixiJS How To Bet Modal
    howToBetModal: PixiHowToBetModal | null;
    // Avatar countdown rings (for betting and turn timers)
    countdownRings: Map<number, PixiAvatarCountdownRing>;  // seatPosition -> countdown ring
    countdownRingLayer: Container | null;  // Layer for countdown rings (above avatars)
    // Insurance badges per seat (pulsing indicator when insurance pending)
    insuranceBadges: Map<number, Sprite>;         // seatPosition -> badge sprite
    // Insurance kitty (chip display for insurance bet)
    insuranceKittyContainers: Map<number, Container>; // seatPosition -> container
    insuranceKittyData: Map<number, { denominations: number[], total: number }>; // Chip data per seat
    insuranceKittyChipSprites: Map<number, Sprite[]>; // seatPosition -> chip sprites
    insuranceKittyCounterTexts: Map<number, Text>;    // Counter text per seat
    insuranceKittyPills: Map<number, Graphics>;       // Counter pill background per seat
    // Persistent scoreboxes - one per seat, updated imperatively
    persistentScoreboxes: Map<number, Container>;  // seatPosition -> scorebox container
    // Turn indicator beam
    beamContainer: Container | null;              // Container holding beam + mask
    beamGraphics: Graphics | null;                // The beam wedge shape
    beamMask: Graphics | Sprite | null;           // Mask to clip beam to table edge
    beamState: {
      currentRotation: number;                    // Current rotation in degrees
      targetRotation: number;                     // Target rotation in degrees
      velocity: number;                           // Angular velocity for spring physics
      targetAlpha: number;                        // Target alpha (0 or 1)
      isAnimating: boolean;                       // Whether animation loop is active
    };
    // Sidebet layer (persistent - not cleared between rounds)
    sidebetLayer: Container | null;
    // Per-seat sidebet icons (keyed by `${seatPosition}-${slotIndex}`, e.g., "3-0", "3-1", "3-2")
    sidebetSprites: Map<string, Sprite>;
    // Per-seat sidebet overlay containers for glow/active states (keyed by `${seatPosition}-${slotIndex}`)
    sidebetOverlays: Map<string, Container>;
    // Per-seat sidebet chip counters (Text elements showing chip count)
    sidebetCounters: Map<string, Text>;
    // Per-seat sidebet counter pill backgrounds (Graphics elements)
    sidebetCounterPills: Map<string, Graphics>;
    // Per-seat sidebet chip counts (actual numeric values)
    sidebetChipCounts: Map<string, number>;
    // Sidebet containers per seat (holds up to 3 icons each)
    seatSidebetContainers: Map<number, Container>;
    // Active sidebet states for visual indicators
    activeSidebets: Map<string, {
      amount: number;
      rounds: number;
      resolved?: boolean;
      outcome?: 'win' | 'lose';
      winType?: string;
    }>;
    // Track sidebets that have been played at this table (for gold "previously played" indicator)
    previouslyPlayedSidebets: Set<string>;
    // Track sidebets marked for removal during next card rake (keyed by `${seatPosition}-${sidebetType}`)
    sidebetsMarkedForRemoval: Set<string>;
    // Server-authoritative rounds remaining (keyed by `${seatPosition}-${sidebetType}`)
    // Survives across rounds - only cleared on true retire (isFinal, removal), NOT on per-round collect
    sidebetServerRounds: Map<string, number>;
    // Sidebet modal
    sidebetModal: {
      container: Container | null;
      backdrop: Graphics | null;
      isOpen: boolean;
      currentSidebet: string | null;
      selectedPercentage: number | null;
      selectedRounds: number;  // positive number for stepper count, -1 for infinite/ongoing
      isOngoing: boolean;  // true = ongoing mode, false = stepper mode
      percentButtons: Map<number, Container>;
      roundButtons: Map<string, Container>;  // 'stepper' and 'ongoing'
      roundCountText: Text | null;  // Text showing current stepper count
      roundsLabel: Text | null;  // "round" or "rounds" label
      placeButton: Container | null;
      cancelButton: Container | null;
      cancelBtnBg: Graphics | null;
      cancelBtnText: Text | null;
      amountText: Text | null;
      // Over/Under 13 selection
      selectedOverUnder: 'over' | 'under' | 'exactly' | null;
      overUnderButtons: Map<string, { container: Container; radio: Graphics }>;
    };
    // BUDDY HUD elements (DP and TC indicators)
    buddyHUD: {
      container: Container | null;
      visible: boolean;
      // Deck Penetration (left side)
      dpContainer: Container | null;
      dpLabel: Text | null;
      dpValue: Text | null;
      dpBarBg: Graphics | null;
      dpBarFill: Graphics | null;
      dpTickMarks: Graphics | null;
      dpTickLabels: Text[];
      // True Count (right side)
      tcContainer: Container | null;
      tcLabel: Text | null;
      tcValue: Text | null;
      tcBarBg: Graphics | null;
      tcIndicator: Graphics | null;
      tcTickMarks: Graphics | null;
      tcTickLabels: Text[];
    };
    // Flag to disable card straightening during rake animation
    rakeInProgress: boolean;
    // Split hand carousel system - masked seat card viewports
    // Each seat has a viewport (masked container) that clips cards at seat boundaries
    // Cards are dealt to a content container inside the viewport
    // Carousel navigation slides the content container to show different hands
    seatCardViewports: Map<number, Container>;    // seatPosition -> viewport (masked)
    seatCardContents: Map<number, Container>;     // seatPosition -> content (slides for carousel)
    seatCardMasks: Map<number, Graphics>;         // seatPosition -> mask graphics
    // Dynamic mask state for iris effect (only enabled when hands > 1)
    // Uses asymmetric left/right edges for smooth carousel animations
    seatMaskState: Map<number, MaskState>;
    seatMaskDebugBorders: Map<number, Graphics>;  // Debug: red border showing mask bounds
    // Split hand carousel state per seat
    splitHandState: Map<number, {
      totalHands: number;           // Total number of hands (1 = no split)
      viewedHandIndex: number;      // Currently visible hand
      isAnimating: boolean;         // Whether carousel is animating
      handContainers: Map<number, Container>;  // Container per hand for positioning
    }>;
    // Per-hand kitty tracking (keyed by "seatPosition-handIndex")
    splitKittyContainers: Map<string, Container>;
    splitKittyData: Map<string, { denominations: number[], total: number }>;
    splitKittyChipSprites: Map<string, Map<number, KittyChipData>>;
    splitKittyCounterTexts: Map<string, Text>;
    splitKittyPills: Map<string, Graphics>;
    // Split hand labels ("Hand1", "Hand2", etc.) - displayed during split and rake
    // Now uses wrapper-based masking for fly-in/fly-out clipping
    // label can be Text (legacy) or Sprite (using pre-loaded texture)
    splitHandLabels: Map<string, {
      label: Text | Sprite;
      bg: Graphics;
      wrapper?: Container; // Wrapper container with mask for clipping
      seatRotRad?: number;
      finalX?: number; // Legacy: final world X (used when no wrapper)
      finalY?: number; // Legacy: final world Y (used when no wrapper)
      finalLocalX?: number; // Final local X for bg (used with wrapper)
      finalTextLocalX?: number; // Final local X for text (used with wrapper)
      finalLocalY?: number; // Final local Y (used with wrapper)
    }>;
    // Corner cards (scorekeeper left, modal right)
    leftCornerCard: Container | null;
    rightCornerCard: Container | null;
    // Scorecard (flipping card that shows round results)
    scorecardContainer: Container | null;    // Parent container at corner card position
    scorecardBack: Container | null;         // Card back (initially visible)
    scorecardFront: Container | null;        // Results display (initially hidden via scaleX=0)
    scorecardIsShowingFront: boolean;        // Track current state
    scorecardResultLines: Text[];            // Text elements for bet LABELS (left-justified)
    scorecardValueLines: Text[];             // Text elements for bet VALUES (right-justified)
    scorecardTotalText: Text | null;         // Total amount text
    scorecardScaleFactor: number;            // Scale factor for vertical centering calculations
    scorecardContentHeight: number;          // Content height for vertical centering calculations
    // Dev mode: test card hand for positioning
    devCardSprites: Container[];
    // Dev mode: hand label visualization container
    devHandLabelsContainer: Container | null;
  }>({
    gameContentContainer: null,
    tableLayer: null,
    tableSprite: null,
    seatsLayer: null,
    outcomeLabelLayer: null,
    scoreboxLayer: null,
    highlightLayer: null,
    avatarLayer: null,
    cardsLayer: null,
    badgeLayer: null,
    animationLayer: null,
    uiLayer: null,
    scorecardOverlay: null,
    seatContainers: new Map(),
    dealerContainer: null,
    shoeSprite: null,
    traySprite: null,
    shoeCardSprites: [],
    shoeMask: null,
    dealerButtonContainer: null,
    trayChipsContainer: null,
    trayChipSprites: new Map(),
    // UI elements
    stagedBetContainer: null,
    chipRailContainer: null,
    chipRailChips: new Map(),
    // Persistent chip system
    chipPool: new Map(),
    stagedChipSprites: [],
    stagedBetFrame: null,
    stagedBetAmountText: null,
    stagedBetClearButton: null,
    forceChipResync: false,
    // Kitty display system
    kittyContainers: new Map(),
    kittyChipsWrappers: new Map(),
    kittyData: new Map(),
    kittyChipSprites: new Map(),
    kittyCounterTexts: new Map(),
    kittyPills: new Map(),
    kittyBasePositions: new Map(),
    // PixiJS Control Panel
    controlPanel: null,
    // PixiJS Insurance Panel
    insurancePanel: null,
    // PixiJS How To Bet Modal
    howToBetModal: null,
    // Avatar countdown rings
    countdownRings: new Map(),
    countdownRingLayer: null,
    // Insurance badges
    insuranceBadges: new Map(),
    // Insurance kitty
    insuranceKittyContainers: new Map(),
    insuranceKittyData: new Map(),
    insuranceKittyChipSprites: new Map(),
    insuranceKittyCounterTexts: new Map(),
    insuranceKittyPills: new Map(),
    // Persistent scoreboxes
    persistentScoreboxes: new Map(),
    // Turn indicator beam
    beamContainer: null,
    beamGraphics: null,
    beamMask: null,
    beamState: {
      currentRotation: 180,  // Start pointing down (hidden)
      targetRotation: 180,
      velocity: 0,
      targetAlpha: 0,
      isAnimating: false,
    },
    // Sidebet layer (persistent)
    sidebetLayer: null,
    // Per-seat sidebet icons (dynamic, keyed by `${seatPosition}-${slotIndex}`)
    sidebetSprites: new Map(),
    sidebetOverlays: new Map(),
    sidebetCounters: new Map(),
    sidebetCounterPills: new Map(),
    sidebetChipCounts: new Map(),
    seatSidebetContainers: new Map(),
    activeSidebets: new Map(),
    previouslyPlayedSidebets: new Set(),
    sidebetsMarkedForRemoval: new Set(),
    sidebetServerRounds: new Map(),
    // Sidebet modal
    sidebetModal: {
      container: null,
      backdrop: null,
      isOpen: false,
      currentSidebet: null,
      selectedPercentage: null,
      selectedRounds: 1,
      isOngoing: false,
      percentButtons: new Map(),
      roundButtons: new Map(),
      roundCountText: null,
      roundsLabel: null,
      placeButton: null,
      cancelButton: null,
      cancelBtnBg: null,
      cancelBtnText: null,
      amountText: null,
      selectedOverUnder: null,
      overUnderButtons: new Map(),
    },
    // BUDDY HUD (initialized as hidden)
    buddyHUD: {
      container: null,
      visible: false,
      dpContainer: null,
      dpLabel: null,
      dpValue: null,
      dpBarBg: null,
      dpBarFill: null,
      dpTickMarks: null,
      dpTickLabels: [],
      tcContainer: null,
      tcLabel: null,
      tcValue: null,
      tcBarBg: null,
      tcIndicator: null,
      tcTickMarks: null,
      tcTickLabels: [],
    },
    // Flag to disable card straightening during rake animation
    rakeInProgress: false,
    // Seat card viewport system for split hand carousel
    seatCardViewports: new Map(),
    seatCardContents: new Map(),
    seatCardMasks: new Map(),
    seatMaskState: new Map(),
    seatMaskDebugBorders: new Map(),
    // Split hand state
    splitHandState: new Map(),
    splitKittyContainers: new Map(),
    splitKittyData: new Map(),
    splitKittyChipSprites: new Map(),
    splitKittyCounterTexts: new Map(),
    splitKittyPills: new Map(),
    splitHandLabels: new Map(),
    // Corner cards
    leftCornerCard: null,
    rightCornerCard: null,
    // Scorecard
    scorecardContainer: null,
    scorecardBack: null,
    scorecardFront: null,
    scorecardIsShowingFront: false,
    scorecardResultLines: [],
    scorecardValueLines: [],
    scorecardTotalText: null,
    scorecardScaleFactor: 1,
    scorecardContentHeight: 112,
    // Dev mode cards
    devCardSprites: [],
    // Dev mode: hand label visualization container
    devHandLabelsContainer: null,
  });

  // ===========================================
  // CARD STATE TRACKING (for randomness/straightening)
  // ===========================================
  // Uses module-level maps that survive HMR and component remounts
  // (see MODULE_CARD_STATES, MODULE_STRAIGHTENED, and CardSpriteState at top of file)

  // Keep loadingRef in sync with loading state (synchronous update during render)
  // This MUST be synchronous, not in useEffect, to avoid race conditions where
  // imperative handle methods are called after render but before useEffect runs
  loadingRef.current = loading;

  // Keep perSeatCardConfigsRef in sync with prop (avoids closure issues in imperative handles)
  perSeatCardConfigsRef.current = perSeatCardConfigs;

  // Keep cardConfigRef in sync with cardConfig prop (avoids closure issues in imperative handles)
  // Track previous dealer cards offset to calculate delta for live updates
  const prevDealerCardsOffsetRef = useRef({ x: cardConfig.dealerCardsX, y: cardConfig.dealerCardsY });

  useEffect(() => {
    const prevX = prevDealerCardsOffsetRef.current.x;
    const prevY = prevDealerCardsOffsetRef.current.y;
    const newX = cardConfig.dealerCardsX;
    const newY = cardConfig.dealerCardsY;

    // Calculate delta
    const deltaX = newX - prevX;
    const deltaY = newY - prevY;

    // Update ref for next comparison
    prevDealerCardsOffsetRef.current = { x: newX, y: newY };
    cardConfigRef.current = cardConfig;

    // If no change, skip
    if (deltaX === 0 && deltaY === 0) return;

    // Update all dealer cards in MODULE_CARD_STATES
    MODULE_CARD_STATES.forEach((state, key) => {
      if (key.startsWith('dealer-') && state.isDealer) {
        // Update base positions
        state.baseX += deltaX;
        state.baseY += deltaY;

        // Move the sprite immediately
        state.sprite.x += deltaX;
        state.sprite.y += deltaY;
      }
    });
  }, [cardConfig]);

  // Keep sidebet-related refs in sync
  useEffect(() => {
    kittyAmountRef.current = kittyAmount;
  }, [kittyAmount]);

  useEffect(() => {
    onSidebetPlaceRef.current = onSidebetPlace;
  }, [onSidebetPlace]);

  useEffect(() => {
    onSidebetRemoveRef.current = onSidebetRemove;
  }, [onSidebetRemove]);

  // Keep hand label config refs in sync
  useEffect(() => {
    handLabelConfigRef.current = handLabelConfig;
  }, [handLabelConfig]);

  useEffect(() => {
    handLabelConfigsRef.current = handLabelConfigs;
  }, [handLabelConfigs]);

  // Keep BUDDY HUD config ref in sync and update all HUD elements when config changes
  useEffect(() => {
    buddyHudConfigRef.current = effectiveBuddyHudConfig;

    // Update HUD elements if they exist
    const scene = sceneRef.current;
    if (!scene.buddyHUD.container || loading) return;

    const cfg = effectiveBuddyHudConfig;
    const hud = scene.buddyHUD;

    // Update DP container position
    if (hud.dpContainer) {
      hud.dpContainer.x = cfg.dpX;
      hud.dpContainer.y = cfg.y;
    }

    // Update TC container position
    if (hud.tcContainer) {
      hud.tcContainer.x = cfg.tcX;
      hud.tcContainer.y = cfg.y;
    }

    // Update label font sizes
    if (hud.dpLabel) {
      hud.dpLabel.style.fontSize = cfg.labelFontSize;
    }
    if (hud.tcLabel) {
      hud.tcLabel.style.fontSize = cfg.labelFontSize;
    }

    // Update value font sizes
    if (hud.dpValue) {
      hud.dpValue.style.fontSize = cfg.valueFontSize;
    }
    if (hud.tcValue) {
      hud.tcValue.style.fontSize = cfg.valueFontSize;
    }

    // Apply barY offset to bar elements
    if (hud.dpBarBg) hud.dpBarBg.y = cfg.barY;
    if (hud.dpBarFill) hud.dpBarFill.y = cfg.barY;
    if (hud.dpTickMarks) hud.dpTickMarks.y = cfg.barY;
    if (hud.tcBarBg) hud.tcBarBg.y = cfg.barY;
    if (hud.tcIndicator) hud.tcIndicator.y = cfg.barY;
    if (hud.tcTickMarks) hud.tcTickMarks.y = cfg.barY;
    // Apply barY offset to tick labels (original y was: -barHeight/2 - tickHeight - 4)
    const tickLabelBaseY = -cfg.barHeight / 2 - BUDDY_HUD_CONFIG.tickHeight - 4;
    hud.dpTickLabels.forEach(label => { label.y = tickLabelBaseY + cfg.barY; });
    hud.tcTickLabels.forEach(label => { label.y = tickLabelBaseY + cfg.barY; });

    // Redraw bar backgrounds with new dimensions (no rounded corners)
    if (hud.dpBarBg) {
      hud.dpBarBg.clear();
      hud.dpBarBg.rect(-cfg.barWidth / 2, -cfg.barHeight / 2, cfg.barWidth, cfg.barHeight);
      hud.dpBarBg.fill({ color: BUDDY_HUD_CONFIG.barBgColor, alpha: BUDDY_HUD_CONFIG.barBgAlpha });
    }
    if (hud.tcBarBg) {
      hud.tcBarBg.clear();
      hud.tcBarBg.rect(-cfg.barWidth / 2, -cfg.barHeight / 2, cfg.barWidth, cfg.barHeight);
      hud.tcBarBg.fill({ color: BUDDY_HUD_CONFIG.barBgColor, alpha: BUDDY_HUD_CONFIG.barBgAlpha });
    }

    // Reposition labels based on new bar width
    if (hud.dpLabel) {
      hud.dpLabel.x = -cfg.barWidth / 2 - 8;
    }
    if (hud.dpValue) {
      hud.dpValue.x = cfg.barWidth / 2 + 8;
    }
    if (hud.tcLabel) {
      hud.tcLabel.x = -cfg.barWidth / 2 - 8;
    }
    if (hud.tcValue) {
      hud.tcValue.x = cfg.barWidth / 2 + 8;
    }

    // Update tick label font sizes and reposition based on bar width
    const dpTicks = [0, 25, 50, 75, 100];
    hud.dpTickLabels.forEach((label, i) => {
      label.style.fontSize = cfg.tickLabelFontSize;
      label.x = -cfg.barWidth / 2 + (dpTicks[i] / 100) * cfg.barWidth;
    });
    const tcTicks = [-5, -2.5, 0, 2.5, 5];
    const tcMin = -5, tcMax = 5;
    hud.tcTickLabels.forEach((label, i) => {
      label.style.fontSize = cfg.tickLabelFontSize;
      label.x = -cfg.barWidth / 2 + ((tcTicks[i] - tcMin) / (tcMax - tcMin)) * cfg.barWidth;
    });

    // Redraw tick marks based on new bar width (capture Graphics reference for closure safety)
    const tickColor = BUDDY_HUD_CONFIG.tickColor;
    const tickHeight = BUDDY_HUD_CONFIG.tickHeight;
    if (hud.dpTickMarks) {
      const dpTickMarks = hud.dpTickMarks; // Capture reference for forEach closure
      dpTickMarks.clear();
      dpTicks.forEach(tick => {
        const tickX = -cfg.barWidth / 2 + (tick / 100) * cfg.barWidth;
        dpTickMarks.moveTo(tickX, -cfg.barHeight / 2 - 2);
        dpTickMarks.lineTo(tickX, -cfg.barHeight / 2 - 2 - tickHeight);
        dpTickMarks.stroke({ color: tickColor, width: 1 });
      });
    }
    if (hud.tcTickMarks) {
      const tcTickMarks = hud.tcTickMarks; // Capture reference for forEach closure
      tcTickMarks.clear();
      tcTicks.forEach(tick => {
        const tickX = -cfg.barWidth / 2 + ((tick - tcMin) / (tcMax - tcMin)) * cfg.barWidth;
        tcTickMarks.moveTo(tickX, -cfg.barHeight / 2 - 2);
        tcTickMarks.lineTo(tickX, -cfg.barHeight / 2 - 2 - tickHeight);
        tcTickMarks.stroke({ color: tickColor, width: 1 });
      });
    }

    // Trigger a value update to redraw fills with new dimensions
    if (hud.dpBarFill && hud.dpValue && hud.tcIndicator && hud.tcValue) {
      updateBuddyHUDValues(hud.dpBarFill, hud.dpValue, hud.tcIndicator, hud.tcValue, penetration, trueCount);
    }

    console.log('[BUDDY HUD] Config updated - DP:', cfg.dpX, 'TC:', cfg.tcX, 'Y:', cfg.y, 'barWidth:', cfg.barWidth);
  }, [effectiveBuddyHudConfig, loading, penetration, trueCount]);

  // Update sidebet config refs when props change (ref update only - visual updates happen later)
  useEffect(() => {
    sidebetConfigRef.current = {
      baseRadiusOffset: sidebetIconConfig?.baseRadiusOffset ?? -217,
      scale: sidebetIconConfig?.scale ?? 100,
      slotPositions: [
        { localX: sidebetIconConfig?.slot0?.x ?? -45, localY: sidebetIconConfig?.slot0?.y ?? 0 },
        { localX: sidebetIconConfig?.slot1?.x ?? 0, localY: sidebetIconConfig?.slot1?.y ?? -28 },
        { localX: sidebetIconConfig?.slot2?.x ?? 45, localY: sidebetIconConfig?.slot2?.y ?? 0 },
      ],
      iconSize: SIDEBET_ICON_SIZE * ((sidebetIconConfig?.scale ?? 100) / 100),
      getPositionsForCount: SIDEBET_CONFIG.getPositionsForCount,
    };
    // Update opponent config - falls back to player config if not provided
    opponentSidebetConfigRef.current = {
      baseRadiusOffset: opponentSidebetIconConfig?.baseRadiusOffset ?? sidebetIconConfig?.baseRadiusOffset ?? -217,
      scale: opponentSidebetIconConfig?.scale ?? sidebetIconConfig?.scale ?? 100,
      slotPositions: [
        { localX: opponentSidebetIconConfig?.slot0?.x ?? sidebetIconConfig?.slot0?.x ?? -45, localY: opponentSidebetIconConfig?.slot0?.y ?? sidebetIconConfig?.slot0?.y ?? 0 },
        { localX: opponentSidebetIconConfig?.slot1?.x ?? sidebetIconConfig?.slot1?.x ?? 0, localY: opponentSidebetIconConfig?.slot1?.y ?? sidebetIconConfig?.slot1?.y ?? -28 },
        { localX: opponentSidebetIconConfig?.slot2?.x ?? sidebetIconConfig?.slot2?.x ?? 45, localY: opponentSidebetIconConfig?.slot2?.y ?? sidebetIconConfig?.slot2?.y ?? 0 },
      ],
      iconSize: SIDEBET_ICON_SIZE * ((opponentSidebetIconConfig?.scale ?? sidebetIconConfig?.scale ?? 100) / 100),
      getPositionsForCount: SIDEBET_CONFIG.getPositionsForCount,
    };
  }, [sidebetIconConfig, opponentSidebetIconConfig]);

  // Track previous seatCardsY for position delta calculation
  const prevSeatCardsYRef = useRef(cardConfig.seatCardsY);

  // Rebuild all card sprites when cardConfig changes (DEV CONTROLS)
  useEffect(() => {
    if (loading || !appRef.current) return;
    const scene = sceneRef.current;
    const textures = texturesRef.current;
    if (!scene.animationLayer) return;

    // Calculate seatCardsY delta for position adjustment
    const oldSeatCardsY = prevSeatCardsYRef.current;
    const newSeatCardsY = cardConfig.seatCardsY;
    const seatCardsYDelta = newSeatCardsY - oldSeatCardsY;
    prevSeatCardsYRef.current = newSeatCardsY;

    // Iterate through all stored card states and rebuild sprites
    MODULE_CARD_STATES.forEach((state, key) => {
      const { sprite: oldSprite, card, baseX, baseY, baseRotation, randomness, isDealer } = state;

      // Determine if this is an opponent card by parsing seat from key
      // Key format: "seatPosition-handIndex-cardIndex" for players, "dealer-0-cardIndex" for dealer
      let effectiveConfig = cardConfig;
      if (!isDealer) {
        const seatPosition = parseInt(key.split('-')[0], 10);
        const isOpponent = !isNaN(seatPosition) && seatPosition !== mySeatPosition;
        if (isOpponent) {
          effectiveConfig = applyOpponentCardScale(cardConfig);
        }
      }

      // Create new sprite with updated config
      const newSprite = createCardSprite(card, textures, 0, 0, 0, effectiveConfig);

      // For player cards (not dealer), apply seatCardsY position delta
      // seatCardsY offset is along seat's local Y axis (perpendicular to rotation)
      if (!isDealer && seatCardsYDelta !== 0) {
        // Calculate world-space delta from local Y offset change
        const deltaX = seatCardsYDelta * -Math.sin(baseRotation);
        const deltaY = seatCardsYDelta * Math.cos(baseRotation);
        newSprite.x = oldSprite.x + deltaX;
        newSprite.y = oldSprite.y + deltaY;
        newSprite.rotation = oldSprite.rotation;

        // Also update stored baseX/baseY so future deltas work correctly
        state.baseX = baseX + deltaX;
        state.baseY = baseY + deltaY;
      } else {
        // Dealer cards or no Y change - preserve position
        newSprite.x = oldSprite.x;
        newSprite.y = oldSprite.y;
        newSprite.rotation = oldSprite.rotation;
      }

      // Replace old sprite with new one in animation layer
      const parent = oldSprite.parent;
      if (parent) {
        const index = parent.getChildIndex(oldSprite);
        parent.removeChild(oldSprite);
        parent.addChildAt(newSprite, index);
      }
      oldSprite.destroy();

      // Update the stored state with new sprite
      MODULE_CARD_STATES.set(key, {
        ...state,
        sprite: newSprite,
      });
    });
  }, [loading, cardConfig]);

  // Track animation card count - used to trigger re-renders when cards are added/removed
  const [animationCardCount, setAnimationCardCount] = useState(0);

  // Visual position rotation
  // NOTE: When a spectator joins a seat, the store updates immediately but the prop
  // may be stale until React re-renders. We check the store value as a fallback to
  // ensure correct visual positioning during the transition.
  const getVisualPositionForSeat = useCallback((actualSeat: number): number => {
    // Check if prop is stale - use store value if prop is null but store has a value
    const effectiveMySeat = mySeatPosition ?? useGameStateStore.getState().mySeatPosition;
    if (effectiveMySeat === null) return actualSeat;
    return ((actualSeat - effectiveMySeat + 4 - 1 + 7) % 7) + 1;
  }, [mySeatPosition]);

  // Dynamic seat positions based on radius scale and group offset
  const dynamicSeatPositions = useMemo(() => {
    return SEAT_POLAR_POSITIONS.map((seat) => {
      const radians = degreesToRadians(seat.angle);
      return {
        visualPos: seat.visualPos,
        x: TABLE_CENTER.x + seat.radius * effectiveSeatRadiusScale * Math.cos(radians) + seatGroupOffset.x,
        y: TABLE_CENTER.y + seat.radius * effectiveSeatRadiusScale * Math.sin(radians) + seatGroupOffset.y,
        rotation: seat.rotation,
      };
    });
  }, [effectiveSeatRadiusScale, seatGroupOffset.x, seatGroupOffset.y]);

  // ===========================================
  // SIDEBET CONFIG VISUAL UPDATES
  // ===========================================
  // Reposition existing sidebet containers when config changes (debug borders disabled)
  useEffect(() => {
    if (loading || !sceneRef.current.sidebetLayer) return;

    const scene = sceneRef.current;

    // Remove old debug container if it exists
    const oldDebug = (scene as unknown as { sidebetDebugContainer?: Container }).sidebetDebugContainer;
    if (oldDebug) {
      scene.sidebetLayer?.removeChild(oldDebug);
      oldDebug.destroy({ children: true });
      (scene as unknown as { sidebetDebugContainer?: Container }).sidebetDebugContainer = undefined;
    }

    // Reposition existing sidebet containers
    scene.seatSidebetContainers.forEach((seatContainer, seatPosition) => {
      const visualPos = getVisualPositionForSeat(seatPosition);
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) return;

      // Use opponent config for opponent seats, player config for player seat
      const isOpponent = seatPosition !== mySeatPosition;
      const config = isOpponent ? opponentSidebetConfigRef.current : sidebetConfigRef.current;

      // Per-seat offset (additive on top of base config) - use VISUAL position for DEV CONTROLS alignment
      const perSeatOffset = sidebetIconConfigs?.[visualPos] || { radiusOffset: 0, x: 0, y: 0 };
      const baseLocalOffsetY = config.baseRadiusOffset + perSeatOffset.radiusOffset;
      const rotRad = degreesToRadians(seatData.rotation);
      const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);

      seatContainer.x = basePos.x + perSeatOffset.x;
      seatContainer.y = basePos.y + perSeatOffset.y;
      seatContainer.rotation = rotRad;

      // Update icon sizes
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          sprite.width = config.iconSize;
          sprite.height = config.iconSize;
        }
      }

      // Reposition icons within container (including counters, pills, and border rings)
      const activeSlots: { sprite: Sprite; overlay: Container | null; counter: Text | null; counterPill: Graphics | null; borderRing: Graphics | null }[] = [];
      for (let i = 0; i < 3; i++) {
        const k = `${seatPosition}-${i}`;
        const s = scene.sidebetSprites.get(k);
        const o = scene.sidebetOverlays.get(k);
        const c = scene.sidebetCounters.get(k);
        const cp = scene.sidebetCounterPills.get(k);
        if (s && !s.destroyed) {
          activeSlots.push({
            sprite: s,
            overlay: o || null,
            counter: c || null,
            counterPill: cp || null,
            borderRing: (s as unknown as { borderRing?: Graphics }).borderRing || null,
          });
        }
      }
      const posIndices = config.getPositionsForCount(activeSlots.length);
      activeSlots.forEach((slot, idx) => {
        const posIdx = posIndices[idx];
        const pos = config.slotPositions[posIdx];
        slot.sprite.x = pos.localX;
        slot.sprite.y = pos.localY;
        if (slot.borderRing && !slot.borderRing.destroyed) {
          slot.borderRing.x = pos.localX;
          slot.borderRing.y = pos.localY;
        }
        if (slot.overlay) {
          slot.overlay.x = pos.localX;
          slot.overlay.y = pos.localY;
        }
        if (slot.counter && !slot.counter.destroyed) {
          slot.counter.x = pos.localX;
          slot.counter.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
        }
        if (slot.counterPill && !slot.counterPill.destroyed) {
          slot.counterPill.x = pos.localX;
          slot.counterPill.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
        }
      });
    });

    console.log('[Sidebet Config] Repositioned sidebet containers');
  }, [sidebetIconConfig, opponentSidebetIconConfig, sidebetIconConfigs, mySeatPosition, loading, dynamicSeatPositions, getVisualPositionForSeat]);

  // ===========================================
  // VISIBILITY CHANGE RECONCILIATION
  // ===========================================
  // When tab becomes visible again, reconcile visual state with game state
  // This fixes stuck animations and creates missing elements without flicker
  // Also called from syncDealtCounts for mid-hand joins
  const hasInitialReconciliationRef = useRef(false);
  const reconcileCardsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleVisibilityChange = (isInitialMount = false, reason?: string) => {
      // Only run on visibility change OR initial mount OR explicit call, not on regular state changes
      const explicitCall = !!reason;
      if (!explicitCall && !isInitialMount && document.visibilityState !== 'visible') return;
      if (loading || !appRef.current) return;

      const scene = sceneRef.current;
      const textures = texturesRef.current;
      const currentCardConfig = cardConfigRef.current;
      if (!scene.animationLayer || !scene.outcomeLabelLayer) return;

      const logReason = reason || (isInitialMount ? 'Initial mount' : 'Tab visible');
      console.log(`[PixiGameTableV1] ${logReason} - reconciling state...`);

      // CRITICAL FIX: Clear in-flight tracking when returning from hidden tab.
      // When tab is hidden, browser throttles/pauses animations. Animation callbacks
      // may never fire, leaving stale entries in IN_FLIGHT. By clearing it here,
      // we force reconciliation to process ALL elements and snap them to final positions.
      // This fixes ALT+TAB visual corruption (duplicate cards, wrong positions, etc.)
      if (!isInitialMount && !explicitCall && MODULE_ELEMENTS_IN_FLIGHT.size > 0) {
        console.log(`[PixiGameTableV1] Clearing ${MODULE_ELEMENTS_IN_FLIGHT.size} stale in-flight elements due to tab switch`);
        MODULE_ELEMENTS_IN_FLIGHT.clear();
      }

      // CRITICAL FIX #2: Clean up orphaned sprites in animationLayer.
      // During deal animation, sprites exist in the layer but aren't in MODULE_CARD_STATES
      // until the animation completes. If ALT+TAB interrupts, those sprites become orphaned.
      // We need to remove all card-like sprites that aren't tracked in MODULE_CARD_STATES.
      // NOTE: We also skip chip sprites that are currently in-flight animations.
      if (!isInitialMount && scene.animationLayer) {
        const trackedSprites = new Set<Container>();
        MODULE_CARD_STATES.forEach(state => trackedSprites.add(state.sprite));

        const orphanedSprites: Container[] = [];
        scene.animationLayer.children.forEach(child => {
          // Skip if it's a tracked card
          if (trackedSprites.has(child)) return;
          // Skip outcome label wrappers (they have their own registry)
          if ((child as any).isOutcomeLabelWrapper) return;
          // Skip control panel
          if (child.label === 'controlPanel' || child === scene.controlPanel) return;
          // Skip beam container
          if (child.label === 'beamContainer' || child === scene.beamContainer) return;
          // Skip kitty containers (they're managed separately via scene.kittyContainers/splitKittyContainers)
          if (child.label === 'kittyContainer') return;
          // Skip chip-related containers (they're managed separately)
          if (child.name?.includes('chip') || child.name?.includes('Chip')) return;
          // Skip chip sprites currently in-flight animation
          if (MODULE_CHIP_SPRITES_IN_FLIGHT.has(child as Sprite)) return;

          // This is likely an orphaned card sprite from interrupted animation
          orphanedSprites.push(child);
        });

        if (orphanedSprites.length > 0) {
          console.log(`[PixiGameTableV1] Removing ${orphanedSprites.length} orphaned sprites from animationLayer`);
          orphanedSprites.forEach(sprite => {
            scene.animationLayer?.removeChild(sprite);
            sprite.destroy({ children: true });
          });
        }
      }

      // Build expected cards map from game state (key -> card data)
      const expectedCards = new Map<string, { card: CardType; seatPos: number; handIndex: number; cardIndex: number; isDealer: boolean }>();

      // Player cards
      players.forEach((player, seatPos) => {
        player.hands.forEach((hand, handIndex) => {
          hand.cards.forEach((card, cardIndex) => {
            expectedCards.set(`${seatPos}-${handIndex}-${cardIndex}`, { card, seatPos, handIndex, cardIndex, isDealer: false });
          });
        });
      });

      // Dealer cards
      dealer.cards.forEach((card, cardIndex) => {
        expectedCards.set(`dealer-0-${cardIndex}`, { card, seatPos: -1, handIndex: 0, cardIndex, isDealer: true });
      });

      // Reconcile cards
      let cardsFixed = 0;
      let cardsRemoved = 0;
      let cardsCreated = 0;

      // Log in-flight elements (these will be skipped)
      if (MODULE_ELEMENTS_IN_FLIGHT.size > 0) {
        console.log(`[Reconciliation] Skipping in-flight elements: ${Array.from(MODULE_ELEMENTS_IN_FLIGHT).join(', ')}`);
      }

      // Check for orphaned cards (in registry but shouldn't exist)
      MODULE_CARD_STATES.forEach((state, key) => {
        // Skip in-flight elements - don't touch them
        if (MODULE_ELEMENTS_IN_FLIGHT.has(key)) {
          return;
        }

        if (!expectedCards.has(key)) {
          // DISABLED: Don't remove "orphaned" cards during reconciliation
          // The game state might be stale when visibility changes, causing valid cards
          // to be incorrectly removed. Cards should only be removed via clearTable().
          // Previously: removed card, destroyed sprite, deleted from MODULE_CARD_STATES
          console.log(`[PixiGameTableV1] Reconciliation skipped orphan removal: ${key} (state may be stale)`);
        } else {
          // Card should exist - snap to final position if animating
          if (state.sprite && !state.sprite.destroyed) {
            // Check if straightened - keys are "seatPos-all" or "seatPos-handIndex" or "dealer"
            const parts = key.split('-');
            const seatPos = parts[0];
            const handIdx = parts[1];
            const isStraightened = state.isDealer
              ? MODULE_STRAIGHTENED.get('dealer')
              : (MODULE_STRAIGHTENED.get(`${seatPos}-all`) || MODULE_STRAIGHTENED.get(`${seatPos}-${handIdx}`));
            if (isStraightened) {
              state.sprite.x = state.baseX;
              state.sprite.y = state.baseY;
              state.sprite.rotation = state.baseRotation;
            } else {
              state.sprite.x = state.baseX + state.randomness.offsetX;
              state.sprite.y = state.baseY + state.randomness.offsetY;
              state.sprite.rotation = state.baseRotation + state.randomness.rotation;
            }
            cardsFixed++;
          }
        }
      });

      // DISABLED: Don't create "missing" cards during reconciliation
      // The game state might be stale when visibility changes, causing cards to be
      // created based on wrong data (wrong rank/suit). Cards should only be created
      // from actual CARD_DEALT events which have the correct card data.
      //
      // The original code would:
      // 1. Find cards in game state but not in MODULE_CARD_STATES
      // 2. Create sprites for them at calculated positions
      // 3. This caused random cards to appear after ALT+TAB because game state was stale
      //
      // If cards are truly missing after reconnection, the server will resend events
      // or the user can refresh the page.
      const missingCards = Array.from(expectedCards.entries())
        .filter(([key]) => !MODULE_CARD_STATES.has(key) && !MODULE_ELEMENTS_IN_FLIGHT.has(key));

      if (missingCards.length > 0) {
        console.log(`[PixiGameTableV1] Reconciliation found ${missingCards.length} missing cards but NOT creating them (state may be stale):`, missingCards.map(([k]) => k));
      }

      // Build expected outcome labels from game state
      // If no dealer cards, we're in betting phase - don't expect any outcome labels
      // This prevents stale labels from being recreated after reconnection
      const expectedOutcomes = new Map<string, { outcome: string; seatPos: number; handIndex: number }>();
      if (dealer.cards.length > 0) {
        players.forEach((player, seatPos) => {
          const handIdx = player.currentHandIndex ?? 0;
          const hand = player.hands[handIdx];
          if (hand && hand.cards.length > 0 && hand.outcome) {
            expectedOutcomes.set(`${seatPos}-${handIdx}`, { outcome: hand.outcome, seatPos, handIndex: handIdx });
          }
        });
      }

      // Reconcile outcome labels
      let labelsFixed = 0;
      let labelsRemoved = 0;
      let labelsCreated = 0;

      // Check for orphaned labels (in registry but shouldn't exist)
      // DISABLED: Don't remove "orphaned" outcome labels during reconciliation
      // The game state might be stale when visibility changes, causing valid labels
      // to be incorrectly removed. Labels should only be removed via clearTable().
      MODULE_OUTCOME_LABELS.forEach((state, key) => {
        if (!expectedOutcomes.has(key)) {
          // DISABLED: Skip orphan removal - state may be stale
          console.log(`[PixiGameTableV1] Reconciliation skipped orphan label removal: ${key} (state may be stale)`);
        } else if (state.state === 'animating-in' || state.state === 'animating-out') {
          // Still fix stuck animations - this is safe
          if (state.state === 'animating-in') {
            state.state = 'static';
            labelsFixed++;
          } else if (state.state === 'animating-out') {
            // DISABLED: Don't remove animating-out labels - let animation complete naturally
            console.log(`[PixiGameTableV1] Reconciliation skipped animating-out label: ${key}`);
          }
        }
      });

      // DISABLED: Don't create "missing" outcome labels during reconciliation
      // The game state might be stale when visibility changes, causing labels to be
      // created based on wrong data. Labels should only be created from actual game events.
      const missingLabels = Array.from(expectedOutcomes.entries())
        .filter(([key]) => !MODULE_OUTCOME_LABELS.has(key));

      if (missingLabels.length > 0) {
        console.log(`[PixiGameTableV1] Reconciliation found ${missingLabels.length} missing outcome labels but NOT creating them (state may be stale):`, missingLabels.map(([k]) => k));
      }

      if (cardsFixed || cardsRemoved || cardsCreated || labelsFixed || labelsRemoved || labelsCreated) {
        console.log(`[PixiGameTableV1] Reconciliation complete: cards(fixed=${cardsFixed}, removed=${cardsRemoved}, created=${cardsCreated}), labels(fixed=${labelsFixed}, removed=${labelsRemoved}, created=${labelsCreated})`);
      } else {
        console.log('[PixiGameTableV1] Reconciliation complete: no issues found');
      }
    };

    // Store reconciliation function in ref for use by syncDealtCounts (mid-hand joins)
    reconcileCardsRef.current = () => handleVisibilityChange(false, 'Mid-hand join sync');

    // Wrap for event listener (no isInitialMount param)
    const visibilityHandler = () => handleVisibilityChange(false);
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler);
      reconcileCardsRef.current = null;
    };
  }, [loading, players, dealer, getVisualPositionForSeat, dynamicSeatPositions, seatSizeScale, outcomeLabelConfig, scoreboxConfig]);

  // ===========================================
  // POST-LOAD RECONCILIATION (PAGE REFRESH FIX)
  // ===========================================
  // When loading completes and there's game state, reconcile cards immediately.
  // This handles page refresh scenarios where onSyncDealtCounts was called
  // before PixiJS was ready (loading=true), causing cards to be missed.
  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    // Detect loading transition: true -> false
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (wasLoading && !loading && appRef.current) {
      // Check if there's game state that needs reconciliation
      const hasPlayerCards = Array.from(players.values()).some(p => p.hands.some(h => h.cards.length > 0));
      const hasDealerCards = dealer.cards.length > 0;

      if (hasPlayerCards || hasDealerCards) {
        console.log('[PixiGameTableV1] Post-load reconciliation: game state exists, running reconciliation');
        // Small delay to ensure all state is stable
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (reconcileCardsRef.current) {
              reconcileCardsRef.current();
            }
          });
        });
      }
    }
  }, [loading, players, dealer]);

  // ===========================================
  // TURN INDICATOR BEAM ANIMATION
  // ===========================================
  // Animate beam rotation and visibility based on currentTurnSeat
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    if (!scene.beamContainer || !scene.beamGraphics) return;

    const beamState = scene.beamState;

    // Determine target state based on devShowBeam (dev override) or currentTurnSeat
    if (devShowBeam) {
      // DEV MODE: Force beam visible at specified visual seat
      const visualPos = devBeamSeat;
      const targetRotationDeg = beamConfig?.rotations?.[visualPos] ?? BEAM_CONFIG.rotations[visualPos] ?? 180;
      beamState.targetRotation = targetRotationDeg;
      beamState.targetAlpha = 1;
    } else if (currentTurnSeat !== null) {
      // Show beam and point to seat
      const visualPos = getVisualPositionForSeat(currentTurnSeat);
      const targetRotationDeg = beamConfig?.rotations?.[visualPos] ?? BEAM_CONFIG.rotations[visualPos] ?? 180;
      beamState.targetRotation = targetRotationDeg;
      beamState.targetAlpha = 1;
    } else {
      // Hide beam (retract to pointing down)
      beamState.targetRotation = 180;
      beamState.targetAlpha = 0;
    }

    // Start animation if not already running
    if (beamState.isAnimating) return;
    beamState.isAnimating = true;

    const animate = () => {
      const scene = sceneRef.current;
      if (!scene.beamContainer || !scene.beamGraphics) {
        beamState.isAnimating = false;
        return;
      }

      const { stiffness, damping } = BEAM_CONFIG.spring;
      const currentRot = beamState.currentRotation;
      const targetRot = beamState.targetRotation;

      // Spring physics for rotation
      // Handle angle wrapping (find shortest path)
      let diff = targetRot - currentRot;
      // Normalize to -180 to 180 range for shortest path
      while (diff > 180) diff -= 360;
      while (diff < -180) diff += 360;

      // Spring force: F = -k * displacement
      const force = diff * stiffness;
      beamState.velocity += force;
      beamState.velocity *= damping;
      beamState.currentRotation += beamState.velocity;

      // Normalize rotation to 0-360
      while (beamState.currentRotation < 0) beamState.currentRotation += 360;
      while (beamState.currentRotation >= 360) beamState.currentRotation -= 360;

      // Apply rotation to graphics (convert to radians)
      scene.beamGraphics.rotation = degreesToRadians(beamState.currentRotation);

      // Animate alpha (fade in/out)
      const currentAlpha = scene.beamContainer.alpha;
      const targetAlpha = beamState.targetAlpha;
      const alphaDiff = targetAlpha - currentAlpha;
      if (Math.abs(alphaDiff) > 0.01) {
        scene.beamContainer.alpha += alphaDiff * BEAM_CONFIG.fadeSpeed * 2;
      } else {
        scene.beamContainer.alpha = targetAlpha;
      }

      // Check if animation is complete (rotation settled and alpha reached target)
      const rotationSettled = Math.abs(diff) < 0.5 && Math.abs(beamState.velocity) < 0.1;
      const alphaSettled = Math.abs(scene.beamContainer.alpha - targetAlpha) < 0.01;

      if (rotationSettled && alphaSettled) {
        // Snap to final values
        beamState.currentRotation = targetRot;
        beamState.velocity = 0;
        scene.beamGraphics.rotation = degreesToRadians(targetRot);
        scene.beamContainer.alpha = targetAlpha;
        beamState.isAnimating = false;
      } else {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [loading, currentTurnSeat, getVisualPositionForSeat, devShowBeam, devBeamSeat, beamConfig]);

  // ===========================================
  // BEAM CONFIG UPDATE - Redraw when color/opacity/width changes
  // ===========================================
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    if (!scene.beamGraphics) return;

    // Get config values with fallbacks
    const color = beamConfig?.color ?? BEAM_CONFIG.color;
    const opacity = (beamConfig?.opacity ?? (BEAM_CONFIG.opacity * 100)) / 100;
    const width = beamConfig?.width ?? BEAM_CONFIG.width;
    const radius = BEAM_CONFIG.radius;

    // Parse hex color to number
    const colorNum = typeof color === 'string'
      ? parseInt(color.replace('#', ''), 16)
      : color;

    // Clear and redraw the beam with new config
    const beamWidthRad = (width / 2) * (Math.PI / 180);

    scene.beamGraphics.clear();
    scene.beamGraphics.moveTo(0, 0);
    scene.beamGraphics.arc(0, 0, radius, -Math.PI / 2 - beamWidthRad, -Math.PI / 2 + beamWidthRad);
    scene.beamGraphics.lineTo(0, 0);
    scene.beamGraphics.fill({ color: colorNum, alpha: opacity });

  }, [loading, beamConfig]);

  // ===========================================
  // AVATAR TEXTURE LOADING
  // ===========================================
  // Load avatar images asynchronously when players or dealer persona changes
  // Store in texturesRef with key `avatar-{url}`
  useEffect(() => {
    if (loading) return;

    const textures = texturesRef.current;

    // Collect all unique avatar URLs from players AND dealer
    const avatarUrls = new Set<string>();
    players.forEach((player) => {
      if (player.avatarUrl) {
        avatarUrls.add(player.avatarUrl);
      }
    });

    // Add dealer persona avatar
    if (dealerPersona?.avatarUrl) {
      avatarUrls.add(dealerPersona.avatarUrl);
    }

    // Load any avatars not already cached
    avatarUrls.forEach(async (url) => {
      const key = `avatar-${url}`;
      if (textures.has(key)) return; // Already loaded or loading

      // Mark as loading to prevent duplicate requests
      textures.set(key, null as unknown as Texture);

      try {
        const texture = await Assets.load({
          src: url,
          loadParser: 'loadTextures',
          data: {
            // CORS settings for external avatar URLs (Discord, Google, etc.)
            crossOrigin: 'anonymous',
          },
        });
        textures.set(key, texture);
        // Remove from failed tracking on success (in case this was a retry)
        MODULE_FAILED_AVATARS.delete(url);
        // Trigger re-render so seat/dealer areas update with the loaded avatar
        setAvatarTexturesVersion(v => v + 1);
      } catch {
        // Track failed avatar for per-round retry (initials fallback will display)
        if (!MODULE_FAILED_AVATARS.has(url)) {
          MODULE_FAILED_AVATARS.set(url, 0);
          console.log(`[Avatar] Load failed, will retry: ${url.substring(0, 60)}...`);
        }
      }
    });
  }, [loading, players, dealerPersona]);

  // Dynamic canvas resizing - resize PixiJS canvas to fill available space
  useEffect(() => {
    console.log('[PixiGameTableV1] Resize effect triggered:', {
      loading,
      availableWidth,
      availableHeight,
      hasApp: !!appRef.current,
      hasGameContent: !!sceneRef.current.gameContentContainer,
      currentCanvasDimensions: canvasDimensions,
    });

    if (loading) return; // Wait for app to finish initializing
    if (!availableWidth || !availableHeight) return;
    if (!appRef.current) return;
    if (!sceneRef.current.gameContentContainer) return; // Wait for scene to be set up

    const app = appRef.current;
    const newWidth = Math.round(availableWidth);
    const newHeight = Math.round(availableHeight);

    // Skip if dimensions haven't changed and panel position hasn't changed
    const dimensionsChanged = newWidth !== canvasDimensions.width || newHeight !== canvasDimensions.height;
    console.log('[PixiGameTableV1] Resize check:', { newWidth, newHeight, canvasDimensions, dimensionsChanged });

    if (dimensionsChanged) {
      console.log(`[PixiGameTableV1] Resizing canvas: ${newWidth}x${newHeight}`);

      // Resize the PixiJS renderer
      app.renderer.resize(newWidth, newHeight);
      setCanvasDimensions({ width: newWidth, height: newHeight });

    }

    // Panel dimensions
    const HORIZONTAL_PANEL_HEIGHT = 160;
    const VERTICAL_PANEL_WIDTH = 160;

    // Step 1: Calculate NATURAL scale and position (as if no panel exists)
    // This is the table's preferred size and centered position
    let gameContentScale = Math.min(newWidth / CANVAS_WIDTH, newHeight / CANVAS_HEIGHT);
    let scaledContentWidth = CANVAS_WIDTH * gameContentScale;
    let scaledContentHeight = CANVAS_HEIGHT * gameContentScale;
    let offsetX = (newWidth - scaledContentWidth) / 2;  // Natural centered X
    let offsetY = (newHeight - scaledContentHeight) / 2; // Natural centered Y

    // Step 2: For top/bottom panels, we must reduce available height (table shrinks)
    // For left/right panels, table stays natural size but shifts only if overlap occurs
    if (usePixiControlPanel) {
      if (pixiPanelPosition === 'top') {
        // Panel at top - reduce available height, shift content down
        const availableHeight = newHeight - HORIZONTAL_PANEL_HEIGHT;
        gameContentScale = Math.min(newWidth / CANVAS_WIDTH, availableHeight / CANVAS_HEIGHT);
        scaledContentWidth = CANVAS_WIDTH * gameContentScale;
        scaledContentHeight = CANVAS_HEIGHT * gameContentScale;
        offsetX = (newWidth - scaledContentWidth) / 2;
        offsetY = HORIZONTAL_PANEL_HEIGHT + (availableHeight - scaledContentHeight) / 2;
      } else if (pixiPanelPosition === 'bottom') {
        // Panel at bottom - reduce available height
        const availableHeight = newHeight - HORIZONTAL_PANEL_HEIGHT;
        gameContentScale = Math.min(newWidth / CANVAS_WIDTH, availableHeight / CANVAS_HEIGHT);
        scaledContentWidth = CANVAS_WIDTH * gameContentScale;
        scaledContentHeight = CANVAS_HEIGHT * gameContentScale;
        offsetX = (newWidth - scaledContentWidth) / 2;
        offsetY = (availableHeight - scaledContentHeight) / 2;
      } else if (pixiPanelPosition === 'viewport-left' || pixiPanelPosition === 'table-left') {
        // Panel on left side - only shift if natural position overlaps panel
        // Panel occupies x: 0 to VERTICAL_PANEL_WIDTH
        if (offsetX < VERTICAL_PANEL_WIDTH) {
          // Natural left edge overlaps panel - shift right to be flush
          offsetX = VERTICAL_PANEL_WIDTH;
          // Check if this pushes right edge off screen
          if (offsetX + scaledContentWidth > newWidth) {
            // Need to scale down to fit
            const availableWidth = newWidth - VERTICAL_PANEL_WIDTH;
            gameContentScale = Math.min(availableWidth / CANVAS_WIDTH, newHeight / CANVAS_HEIGHT);
            scaledContentWidth = CANVAS_WIDTH * gameContentScale;
            scaledContentHeight = CANVAS_HEIGHT * gameContentScale;
            offsetX = VERTICAL_PANEL_WIDTH; // Flush with panel
            offsetY = (newHeight - scaledContentHeight) / 2;
          }
        }
      } else if (pixiPanelPosition === 'viewport-right' || pixiPanelPosition === 'table-right') {
        // Panel on right side - only shift if natural position overlaps panel
        // Panel occupies x: (newWidth - VERTICAL_PANEL_WIDTH) to newWidth
        const panelLeftEdge = newWidth - VERTICAL_PANEL_WIDTH;
        const contentRightEdge = offsetX + scaledContentWidth;
        if (contentRightEdge > panelLeftEdge) {
          // Natural right edge overlaps panel - shift left to be flush
          offsetX = panelLeftEdge - scaledContentWidth;
          // Check if this pushes left edge off screen
          if (offsetX < 0) {
            // Need to scale down to fit
            const availableWidth = newWidth - VERTICAL_PANEL_WIDTH;
            gameContentScale = Math.min(availableWidth / CANVAS_WIDTH, newHeight / CANVAS_HEIGHT);
            scaledContentWidth = CANVAS_WIDTH * gameContentScale;
            scaledContentHeight = CANVAS_HEIGHT * gameContentScale;
            offsetX = panelLeftEdge - scaledContentWidth; // Flush with panel
            offsetY = (newHeight - scaledContentHeight) / 2;
          }
        }
      }
    }

    // Apply scale and positioning to game content container
    const scene = sceneRef.current;
    if (scene.gameContentContainer) {
      scene.gameContentContainer.scale.set(gameContentScale);
      scene.gameContentContainer.x = offsetX;
      scene.gameContentContainer.y = offsetY;
    }

    // Also scale the UI layer (chip rail, staged bet)
    if (scene.uiLayer) {
      scene.uiLayer.scale.set(gameContentScale);
      scene.uiLayer.x = offsetX;
      scene.uiLayer.y = offsetY;
    }

    // Also scale the scorecard overlay (mirrors uiLayer transform)
    if (scene.scorecardOverlay) {
      scene.scorecardOverlay.scale.set(gameContentScale);
      scene.scorecardOverlay.x = offsetX;
      scene.scorecardOverlay.y = offsetY;
    }

    // Update control panel with new canvas dimensions and table bounds
    if (scene.controlPanel && scene.tableSprite) {
      const sprite = scene.tableSprite;
      // Table bounds in canvas coordinates (scaled and offset)
      const scaledTableWidth = sprite.width * gameContentScale;
      const scaledTableHeight = sprite.height * gameContentScale;
      const tableBounds = {
        x: (sprite.x - sprite.width * sprite.anchor.x) * gameContentScale + offsetX,
        y: (sprite.y - sprite.height * sprite.anchor.y) * gameContentScale + offsetY,
        width: scaledTableWidth,
        height: scaledTableHeight,
      };

      scene.controlPanel.updateConfig({
        canvasWidth: newWidth,
        canvasHeight: newHeight,
        tableBounds,
        scale: 1, // No CSS transform scaling when using dynamic sizing
      });

      console.log('[PixiGameTableV1] Dynamic sizing - updated panel with tableBounds:', tableBounds);
    }
  }, [loading, availableWidth, availableHeight, canvasDimensions.width, canvasDimensions.height, usePixiControlPanel, pixiPanelPosition]);

  // Calculate viewport scale - measure actual container dimensions (like TableLayoutDemoPage)
  useEffect(() => {
    // If availableWidth/availableHeight are provided, use 1:1 scale (no CSS transform)
    // because the canvas is already sized to fill the space
    if (availableWidth && availableHeight) {
      setScale(1);
      return;
    }

    const calculateScale = () => {
      // Measure actual container dimensions instead of window dimensions
      // This is more accurate as it reflects the actual available space
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportWidth = rect.width;
      const containerHeight = rect.height;

      // Skip if dimensions aren't valid yet (layout not settled)
      if (viewportWidth <= 0 || containerHeight <= 0) return;

      const scaleX = viewportWidth / SCALE_WIDTH;
      const scaleY = containerHeight / SCALE_HEIGHT;
      const newScale = Math.min(scaleX, scaleY);

      setScale(newScale);
    };

    // Use ResizeObserver for accurate container size tracking
    // This handles: initial layout, header height changes, panel position changes
    const resizeObserver = new ResizeObserver(() => {
      calculateScale();
    });

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    // Initial calculation after layout settles
    requestAnimationFrame(calculateScale);

    // Also listen to window resize for viewport changes
    window.addEventListener('resize', calculateScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateScale);
    };
  }, [availableWidth, availableHeight]); // Re-run if dynamic sizing mode changes

  // Initialize PixiJS Application
  useEffect(() => {
    if (!containerRef.current) return;
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    // Reset destroy flag for fresh init
    destroyRequestedRef.current = false;

    // Track if this effect instance is still active (for strict mode cleanup)
    let isActive = true;

    const initPixi = async () => {
      console.log('[PixiGameTableV1] Initializing...');

      // Use dynamic dimensions if provided, otherwise use fixed dimensions
      const initialWidth = availableWidth && availableWidth > 0 ? Math.round(availableWidth) : CANVAS_WIDTH;
      const initialHeight = availableHeight && availableHeight > 0 ? Math.round(availableHeight) : CANVAS_HEIGHT;
      console.log(`[PixiGameTableV1] Initial canvas size: ${initialWidth}x${initialHeight}`);

      const app = new Application();
      await app.init({
        width: initialWidth,
        height: initialHeight,
        backgroundColor: 0x1a4731,
        antialias: true,
      });

      // Enable explicit z-ordering on the stage so addChild order doesn't matter
      app.stage.sortableChildren = true;

      // Update canvas dimensions state
      setCanvasDimensions({ width: initialWidth, height: initialHeight });

      // Check if component unmounted OR destroy() was called during async init
      if (!isActive || destroyRequestedRef.current) {
        console.log('[PixiGameTableV1] Init aborted - component unmounted or destroy requested');
        app.destroy(true, { children: true });
        return;
      }

      appRef.current = app;
      _diagnosticAppRef.current = app; // Set for texture diagnostics
      // Set CSS background on canvas to prevent white flash before first PixiJS render
      app.canvas.style.backgroundColor = '#1a4731';
      // Hide canvas until fully loaded to prevent white flash during strict mode remount
      app.canvas.style.visibility = 'hidden';
      containerRef.current!.appendChild(app.canvas);

      // ===========================================
      // WEBGL CONTEXT LOSS HANDLING
      // ===========================================
      // When GPU runs out of memory or browser reclaims resources,
      // WebGL context is lost and the canvas goes blank (green background visible).
      // We need to detect this and trigger a full re-initialization.
      const canvas = app.canvas as HTMLCanvasElement;
      const handleContextLost = (event: Event) => {
        event.preventDefault(); // Allows context restoration
        console.error('[PixiGameTableV1] WebGL CONTEXT LOST - canvas will appear blank');
        console.error('[PixiGameTableV1] This usually indicates GPU memory exhaustion');
        // Set a flag that can be checked to trigger recovery
        (window as unknown as { __webglContextLost?: boolean }).__webglContextLost = true;
      };
      const handleContextRestored = () => {
        console.log('[PixiGameTableV1] WebGL context RESTORED - attempting recovery');
        (window as unknown as { __webglContextLost?: boolean }).__webglContextLost = false;
        // Force a full re-render by toggling loading state
        // This will cause the useEffect to re-run and rebuild the scene
        setLoading(true);
        setTimeout(() => setLoading(false), 100);
      };
      canvas.addEventListener('webglcontextlost', handleContextLost);
      canvas.addEventListener('webglcontextrestored', handleContextRestored);
      // Store handlers for cleanup
      (canvas as unknown as { __contextLostHandler?: EventListener }).__contextLostHandler = handleContextLost;
      (canvas as unknown as { __contextRestoredHandler?: EventListener }).__contextRestoredHandler = handleContextRestored;

      // ===========================================
      // PERIODIC WEBGL HEALTH CHECK WITH DIAGNOSTICS
      // ===========================================
      // Some browsers don't fire contextlost event - poll for context validity
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
      let healthCheckCount = 0;
      const checkContextHealth = () => {
        if (!gl) return;
        healthCheckCount++;

        // Check WebGL context status
        const isLost = gl.isContextLost();
        if (isLost) {
          console.error('[PixiGameTableV1] HEALTH CHECK: WebGL context is LOST (detected by polling)');
          (window as unknown as { __webglContextLost?: boolean }).__webglContextLost = true;
        }

        // Log diagnostics every 12 checks (every minute) or on context loss
        if (healthCheckCount % 12 === 0 || isLost) {
          // Memory diagnostics (if available - Chrome only)
          const perfMemory = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
          if (perfMemory) {
            const usedMB = (perfMemory.usedJSHeapSize / 1024 / 1024).toFixed(1);
            const totalMB = (perfMemory.totalJSHeapSize / 1024 / 1024).toFixed(1);
            const limitMB = (perfMemory.jsHeapSizeLimit / 1024 / 1024).toFixed(1);
            console.log(`[PixiGameTableV1] MEMORY: ${usedMB}MB used / ${totalMB}MB allocated / ${limitMB}MB limit`);
          }

          // WebGL resource diagnostics
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo && healthCheckCount === 12) {
            // Only log GPU info once (first minute)
            const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
            const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            console.log(`[PixiGameTableV1] GPU: ${vendor} - ${renderer}`);
          }

          // PixiJS texture count
          if (app && app.renderer && !(app.renderer as unknown as { destroyed?: boolean }).destroyed) {
            const texGC = (app.renderer as unknown as { textureGC?: { count?: number } }).textureGC;
            const textureCount = texGC?.count ?? 'N/A';
            console.log(`[PixiGameTableV1] PIXI: managedTextures=${textureCount}, contextLost=${isLost}`);
          }
        }
      };
      const healthCheckInterval = setInterval(checkContextHealth, 5000); // Check every 5 seconds
      (canvas as unknown as { __healthCheckInterval?: ReturnType<typeof setInterval> }).__healthCheckInterval = healthCheckInterval;

      // Load textures
      const textures = new Map<string, Texture>();

      // Table
      const tableTexture = await Assets.load(tableImageUrl);
      textures.set('table', tableTexture);

      // Shoe and tray
      try {
        const shoeTexture = await Assets.load(shoeImageUrl);
        textures.set('shoe', shoeTexture);
      } catch (e) { console.warn('Shoe texture failed:', e); }

      try {
        const trayTexture = await Assets.load(trayImageUrl);
        textures.set('tray', trayTexture);
      } catch (e) { console.warn('Tray texture failed:', e); }

      // Card back
      try {
        const cardBackTexture = await Assets.load(cardBackUrl);
        textures.set('cardBack', cardBackTexture);
      } catch (e) { console.warn('Card back failed:', e); }

      // Face cards
      for (const [rank, suits] of Object.entries(FACE_CARD_URLS)) {
        for (const [suit, url] of Object.entries(suits)) {
          try {
            const texture = await Assets.load(url);
            textures.set(`${rank}-${suit}`, texture);
          } catch (e) { /* Skip failed */ }
        }
      }

      // Chips
      for (const [denom, url] of Object.entries(CHIP_URLS)) {
        try {
          const texture = await Assets.load(url);
          textures.set(`chip-${denom}`, texture);
        } catch (e) { /* Skip failed */ }
      }

      // Sidebets
      for (let i = 0; i < SIDEBET_URLS.length; i++) {
        try {
          const texture = await Assets.load(SIDEBET_URLS[i]);
          textures.set(`sidebet-${i + 1}`, texture);
        } catch (e) { console.warn(`Sidebet ${i + 1} texture failed:`, e); }
      }

      // Insurance badge
      try {
        const insuranceBadgeTexture = await Assets.load(insuranceBadgeUrl);
        textures.set('insuranceBadge', insuranceBadgeTexture);
      } catch (e) { console.warn('Insurance badge texture failed:', e); }

      // Beam mask (clips beam to table edge)
      try {
        const beamMaskTexture = await Assets.load(beamMaskUrl);
        textures.set('beamMask', beamMaskTexture);
      } catch (e) { console.warn('Beam mask texture failed:', e); }

      // ===========================================
      // PRE-RENDERED TEXT TEXTURES
      // ===========================================
      // Create textures for commonly used text to avoid creating new Text objects during gameplay
      // This prevents texture accumulation and WebGL context loss
      console.log('[PixiGameTableV1] Pre-rendering text textures...');

      // Helper to create a text texture
      const createTextTexture = (text: string, style: Partial<TextStyle>): Texture => {
        const textObj = new Text({ text, style: style as TextStyle });
        const texture = app.renderer.generateTexture(textObj);
        textObj.destroy(); // Destroy the temporary Text object
        return texture;
      };

      // Hand labels: "Hand1", "Hand2", "Hand3", "Hand4"
      const handLabelStyle = {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 14,
        fontWeight: '700' as const,
        fill: 0x000000,
      };
      for (let i = 1; i <= 4; i++) {
        textures.set(`handLabel-${i}`, createTextTexture(`Hand${i}`, handLabelStyle));
      }

      // Outcome labels: WINNER!, LOSER!, BUSTED!, BLACKJACK!, PUSH, SURRENDER
      // Pre-render at a base fontSize (24px) - scaled at runtime to match config
      // Base font size for outcome labels - used for scaling calculation at runtime
      const OUTCOME_BASE_FONT_SIZE = 24;

      // Outcome display text and colors (matching createOutcomeLabel)
      const outcomeVariants: Array<{ key: string; text: string; fill: number }> = [
        { key: 'win', text: 'WINNER!', fill: 0xffffff },      // white text on green bg
        { key: 'lose', text: 'LOSER!', fill: 0xffffff },      // white text on red bg
        { key: 'bust', text: 'BUSTED!', fill: 0xffffff },     // white text on red bg
        { key: 'push', text: 'PUSH', fill: 0xffffff },        // white text on gray bg
        { key: 'surrender', text: 'SURRENDER', fill: 0xffffff }, // white text on gray bg
        { key: 'blackjack', text: 'BLACKJACK!', fill: 0x000000 }, // black text on yellow bg
      ];

      for (const variant of outcomeVariants) {
        const style = {
          fontFamily: 'Arial',  // Match DEFAULT_OUTCOME_CONFIG
          fontSize: OUTCOME_BASE_FONT_SIZE,
          fontWeight: 'bold' as const,
          fill: variant.fill,
        };
        textures.set(`outcome-${variant.key}`, createTextTexture(variant.text, style));
      }

      // ===========================================
      // SCORE TEXT WIDTH CACHE
      // ===========================================
      // Pre-measure score text widths to avoid creating Text objects at runtime
      // Scores 0-30 cover all blackjack possibilities (including busts up to 30)
      console.log('[PixiGameTableV1] Pre-measuring score text widths...');
      const scoreTextStyle = new TextStyle({
        fontFamily: 'Arial',
        fontSize: SCOREBOX_BASE_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
      });
      for (let score = 0; score <= 30; score++) {
        const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();
        const scoreStr = padScore(score);
        const tempText = new Text({ text: scoreStr, style: scoreTextStyle });
        MODULE_SCORE_WIDTH_CACHE.set(scoreStr, tempText.width);
        tempText.destroy();
      }
      console.log(`[PixiGameTableV1] Cached ${MODULE_SCORE_WIDTH_CACHE.size} score text widths`);

      console.log(`[PixiGameTableV1] Pre-rendered ${4 + outcomeVariants.length} text textures`);
      logTextureCount('PRELOAD_COMPLETE');

      // Check if component unmounted OR destroy() was called during texture loading
      if (!isActive || destroyRequestedRef.current) {
        console.log('[PixiGameTableV1] Init aborted after texture load - component unmounted or destroy requested');
        app.destroy(true, { children: true });
        return;
      }

      texturesRef.current = textures;

      // Create scene layers
      const gameContentContainer = new Container(); // Wrapper for all game content (can be shifted for panel)
      const tableLayer = new Container();
      const seatsLayer = new Container();
      const sidebetLayer = new Container(); // Sidebet icons - persistent, above seats
      sidebetLayer.eventMode = 'passive'; // Allow events to propagate to children (sidebet icons)
      const outcomeLabelLayer = new Container(); // Outcome labels - between seats and scoreboxes
      outcomeLabelLayer.eventMode = 'none'; // Don't block clicks
      const scoreboxLayer = new Container(); // Scoreboxes - above outcome labels
      scoreboxLayer.eventMode = 'none'; // Don't block clicks
      const highlightLayer = new Container(); // Turn highlight borders - above scoreboxes
      highlightLayer.eventMode = 'none'; // Don't block clicks
      const countdownRingLayer = new Container(); // Countdown rings - below avatars
      countdownRingLayer.eventMode = 'none'; // Don't block clicks
      const avatarLayer = new Container(); // Avatars - above countdown rings
      avatarLayer.eventMode = 'none'; // Don't block clicks
      const cardsLayer = new Container();
      cardsLayer.sortableChildren = true; // Enable zIndex sorting for mask debug borders
      const badgeLayer = new Container(); // Insurance badges - above cards so they're visible
      badgeLayer.eventMode = 'none'; // Don't block clicks
      const animationLayer = new Container();
      animationLayer.eventMode = 'none'; // Don't block clicks - let them pass to seats below
      animationLayer.sortableChildren = true; // Enable zIndex sorting for chip departure order
      const uiLayer = new Container(); // UI elements layer (on top, NOT in gameContent - stays fixed)
      uiLayer.sortableChildren = true; // Enable zIndex sorting for flying chip animations

      // Add all game layers to gameContentContainer (so they shift together)
      gameContentContainer.addChild(tableLayer);
      gameContentContainer.addChild(seatsLayer);
      gameContentContainer.addChild(sidebetLayer);  // Sidebet icons - persistent, above seats
      gameContentContainer.addChild(outcomeLabelLayer);
      gameContentContainer.addChild(scoreboxLayer);
      gameContentContainer.addChild(highlightLayer);
      gameContentContainer.addChild(countdownRingLayer);  // Countdown rings - below avatars
      gameContentContainer.addChild(avatarLayer);
      gameContentContainer.addChild(cardsLayer);       // Player/dealer card filmstrips
      gameContentContainer.addChild(animationLayer);  // Deal animations & chip flights - ABOVE cards so dealing cards appear in front
      gameContentContainer.addChild(badgeLayer);      // Insurance badges - above cards

      // Add containers to stage with explicit z-order
      gameContentContainer.zIndex = STAGE_Z.gameContent;
      app.stage.addChild(gameContentContainer);
      uiLayer.zIndex = STAGE_Z.uiLayer;
      app.stage.addChild(uiLayer); // UI layer stays separate (control panel positioning is absolute)

      // Scorecard overlay - stage-level container for the scorecard so it renders above control panel
      // Mirrors the game content transform (same as uiLayer) to keep the card positioned correctly
      const scorecardOverlay = new Container();
      scorecardOverlay.zIndex = STAGE_Z.scorecardOverlay;
      app.stage.addChild(scorecardOverlay);

      // If using dynamic dimensions, scale and center game content (accounting for panel)
      if (availableWidth && availableHeight) {
        // Calculate available space for game content (panel is always at bottom on init)
        const HORIZONTAL_PANEL_HEIGHT = 160;
        const HORIZONTAL_PANEL_MARGIN = 10;
        const availableGameHeight = initialHeight - HORIZONTAL_PANEL_HEIGHT - HORIZONTAL_PANEL_MARGIN;
        const availableGameWidth = initialWidth;

        // Calculate scale to fit game content within available space
        const gameContentScale = Math.min(availableGameWidth / CANVAS_WIDTH, availableGameHeight / CANVAS_HEIGHT);

        // Calculate centering offset
        const scaledContentWidth = CANVAS_WIDTH * gameContentScale;
        const scaledContentHeight = CANVAS_HEIGHT * gameContentScale;
        const horizontalCenterOffset = (availableGameWidth - scaledContentWidth) / 2;
        const verticalCenterOffset = (availableGameHeight - scaledContentHeight) / 2;

        // Apply centering
        gameContentContainer.scale.set(gameContentScale);
        gameContentContainer.x = horizontalCenterOffset;
        gameContentContainer.y = verticalCenterOffset;
        uiLayer.scale.set(gameContentScale);
        uiLayer.x = horizontalCenterOffset;
        uiLayer.y = verticalCenterOffset;
        // Scorecard overlay mirrors the same transform so the card stays positioned
        scorecardOverlay.scale.set(gameContentScale);
        scorecardOverlay.x = horizontalCenterOffset;
        scorecardOverlay.y = verticalCenterOffset;
      }

      // Reset module-level dealer button state on scene creation
      MODULE_DEALER_BUTTON_CURRENT_SEAT = 0;
      MODULE_DEALER_BUTTON_PENDING_SEAT = null;

      sceneRef.current = {
        gameContentContainer,
        tableLayer,
        tableSprite: null,  // Will be set when table sprite is created
        seatsLayer,
        outcomeLabelLayer,
        scoreboxLayer,
        highlightLayer,
        avatarLayer,
        cardsLayer,
        badgeLayer,
        animationLayer,
        uiLayer,
        scorecardOverlay,
        seatContainers: new Map(),
        dealerContainer: null,
        shoeSprite: null,
        traySprite: null,
        shoeCardSprites: [],
        shoeMask: null,
        dealerButtonContainer: null,
        trayChipsContainer: null,
        trayChipSprites: new Map(),
        // UI elements
        stagedBetContainer: null,
        chipRailContainer: null,
        chipRailChips: new Map(),
        // Persistent chip system
        chipPool: new Map(),
        stagedChipSprites: [],
        stagedBetFrame: null,
        stagedBetAmountText: null,
        stagedBetClearButton: null,
        forceChipResync: false,
        // Kitty display system
        kittyContainers: new Map(),
        kittyChipsWrappers: new Map(),
        kittyData: new Map(),
        kittyChipSprites: new Map(),
        kittyCounterTexts: new Map(),
        kittyPills: new Map(),
        kittyBasePositions: new Map(),
        // PixiJS Control Panel
        controlPanel: null,
        // PixiJS Insurance Panel
        insurancePanel: null,
        // PixiJS How To Bet Modal
        howToBetModal: null,
        // Avatar countdown rings
        countdownRings: new Map(),
        countdownRingLayer,
        // Insurance badges
        insuranceBadges: new Map(),
        // Insurance kitty
        insuranceKittyContainers: new Map(),
        insuranceKittyData: new Map(),
        insuranceKittyChipSprites: new Map(),
        insuranceKittyCounterTexts: new Map(),
        insuranceKittyPills: new Map(),
        // Persistent scoreboxes
        persistentScoreboxes: new Map(),
        // Turn indicator beam
        beamContainer: null,
        beamGraphics: null,
        beamMask: null,
        beamState: {
          currentRotation: 180,
          targetRotation: 180,
          velocity: 0,
          targetAlpha: 0,
          isAnimating: false,
        },
        // Sidebet layer (persistent)
        sidebetLayer,
        // Per-seat sidebet icons (dynamic)
        sidebetSprites: new Map(),
        sidebetOverlays: new Map(),
        sidebetCounters: new Map(),
        sidebetCounterPills: new Map(),
        sidebetChipCounts: new Map(),
        seatSidebetContainers: new Map(),
        activeSidebets: new Map(),
        previouslyPlayedSidebets: new Set(),
    sidebetsMarkedForRemoval: new Set(),
    sidebetServerRounds: new Map(),
        // Sidebet modal
        sidebetModal: {
          container: null,
          backdrop: null,
          isOpen: false,
          currentSidebet: null,
          selectedPercentage: null,
          selectedRounds: 1,
          isOngoing: false,
          percentButtons: new Map(),
          roundButtons: new Map(),
          roundCountText: null,
          roundsLabel: null,
          placeButton: null,
          cancelButton: null,
          cancelBtnBg: null,
          cancelBtnText: null,
          amountText: null,
          selectedOverUnder: null,
          overUnderButtons: new Map(),
        },
        // BUDDY HUD (initialized as hidden)
        buddyHUD: {
          container: null,
          visible: false,
          dpContainer: null,
          dpLabel: null,
          dpValue: null,
          dpBarBg: null,
          dpBarFill: null,
          dpTickMarks: null,
          dpTickLabels: [],
          tcContainer: null,
          tcLabel: null,
          tcValue: null,
          tcBarBg: null,
          tcIndicator: null,
          tcTickMarks: null,
          tcTickLabels: [],
        },
        // Flag to disable card straightening during rake animation
        rakeInProgress: false,
        // Seat card viewport system for split hand carousel
        seatCardViewports: new Map(),
        seatCardContents: new Map(),
        seatCardMasks: new Map(),
        seatMaskState: new Map(),
        seatMaskDebugBorders: new Map(),
        // Split hand state
        splitHandState: new Map(),
        splitKittyContainers: new Map(),
        splitKittyData: new Map(),
        splitKittyChipSprites: new Map(),
        splitKittyCounterTexts: new Map(),
        splitKittyPills: new Map(),
        splitHandLabels: new Map(),
        // Corner cards
        leftCornerCard: null,
        rightCornerCard: null,
        // Scorecard
        scorecardContainer: null,
        scorecardBack: null,
        scorecardFront: null,
        scorecardIsShowingFront: false,
        scorecardResultLines: [],
        scorecardValueLines: [],
        scorecardTotalText: null,
        scorecardScaleFactor: 1,
        scorecardContentHeight: 112,
        // Dev mode cards
        devCardSprites: [],
        // Dev mode: hand label visualization container
        devHandLabelsContainer: null,
      };

      // ===========================================
      // INITIALIZE PERSISTENT CHIP POOL
      // ===========================================
      // Create pool of chip sprites that will be reused throughout the session
      for (const denom of ALL_DENOMINATIONS) {
        const chipTexture = textures.get(`chip-${denom}`);
        if (!chipTexture) continue;

        const pool: PersistentChip[] = [];
        for (let i = 0; i < CHIP_POOL_INITIAL_SIZE; i++) {
          const sprite = new Sprite(chipTexture);
          sprite.anchor.set(0.5);
          sprite.visible = false; // Hidden until needed
          sprite.width = CHIP_SIZES.staged;
          sprite.height = CHIP_SIZES.staged;
          uiLayer.addChild(sprite);

          pool.push({
            sprite,
            denomination: denom,
            state: 'pool',
          });
        }
        sceneRef.current.chipPool.set(denom, pool);
      }

      // Table background - positioned at top of canvas, flush with header
      const tableSprite = new Sprite(textures.get('table')!);
      tableSprite.anchor.set(0.5, 0); // Center horizontally, anchor at top
      tableSprite.x = CANVAS_WIDTH / 2;
      tableSprite.y = 0; // Flush with top of canvas
      tableLayer.addChild(tableSprite);
      sceneRef.current.tableSprite = tableSprite;  // Store for bounds calculation

      // ===========================================
      // BUDDY HUD (DP and TC indicators)
      // ===========================================
      // Create HUD elements (hidden by default, shown for BUDDY subscribers)
      const buddyHUD = createBuddyHUD(buddyHudConfigRef.current);
      tableLayer.addChild(buddyHUD.container);
      sceneRef.current.buddyHUD = {
        ...buddyHUD,
        visible: false,
      };
      console.log('[BUDDY HUD] Created and added to tableLayer at positions DP:', BUDDY_HUD_CONFIG.dpX, 'TC:', BUDDY_HUD_CONFIG.tcX, 'Y:', BUDDY_HUD_CONFIG.y);

      // ===========================================
      // SIDEBET ICONS (per-seat, dynamic)
      // ===========================================
      // Sidebet icons now render dynamically at each player seat
      // See: createSidebetIconAtSeat() and removeSidebetIconFromSeat()
      // The old dealer rail has been removed - icons appear above each player's main bet
      // Maximum 3 active sidebets per player, positioned in an arch

      // ===========================================
      // DEBUG: SIDEBET POSITION BORDERS
      // ===========================================
      // Draw red bordered rectangles at each sidebet slot position for all seats
      // Uses sidebetConfigRef for current config values (updated via DEV controls)
      const DEBUG_SIDEBET_BORDERS = false; // Toggle debug visualization
      if (DEBUG_SIDEBET_BORDERS) {
        const debugContainer = new Container();
        debugContainer.eventMode = 'none';
        // Store reference for dynamic updates
        (sceneRef.current as unknown as { sidebetDebugContainer?: Container }).sidebetDebugContainer = debugContainer;

        const config = sidebetConfigRef.current;

        // Iterate through all 7 visual seat positions
        for (let visualPos = 1; visualPos <= 7; visualPos++) {
          const seatData = dynamicSeatPositions[visualPos - 1];
          if (!seatData) continue;

          // Sidebets use their own config, NOT betChipConfigs
          const baseLocalOffsetY = config.baseRadiusOffset;
          const rotRad = degreesToRadians(seatData.rotation);
          const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);

          // Calculate container position (same as addSidebetToSeat)
          const containerX = basePos.x;
          const containerY = basePos.y;

          // Draw border for each of the 3 slot positions
          for (let slotIndex = 0; slotIndex < 3; slotIndex++) {
            const slotPos = config.slotPositions[slotIndex];

            const graphics = new Graphics();
            graphics.rect(
              -config.iconSize / 2,
              -config.iconSize / 2,
              config.iconSize,
              config.iconSize
            );
            graphics.stroke({ width: 2, color: 0xff0000 }); // Red border

            // Position the graphics relative to container, then rotate
            const rotatedX = slotPos.localX * Math.cos(rotRad) - slotPos.localY * Math.sin(rotRad);
            const rotatedY = slotPos.localX * Math.sin(rotRad) + slotPos.localY * Math.cos(rotRad);

            graphics.x = containerX + rotatedX;
            graphics.y = containerY + rotatedY;
            graphics.rotation = rotRad;

            debugContainer.addChild(graphics);
          }
        }

        sidebetLayer.addChild(debugContainer);
        console.log('[DEBUG] Sidebet position borders added for all seats');
      }

      // ===========================================
      // DEV CONTROL: HAND LABEL POSITION VISUALIZATION
      // ===========================================
      // Container for debug hand labels. Populated via useEffect when showHandLabels is true.
      const devHandLabelsContainer = new Container();
      devHandLabelsContainer.eventMode = 'none';
      outcomeLabelLayer.addChild(devHandLabelsContainer);
      sceneRef.current.devHandLabelsContainer = devHandLabelsContainer;

      // ===========================================
      // TURN INDICATOR BEAM
      // ===========================================
      const beamContainer = new Container();
      beamContainer.x = DEALER_POSITION.x;
      beamContainer.y = DEALER_POSITION.y;
      beamContainer.alpha = 0;
      beamContainer.zIndex = 1;

      const beamGraphics = new Graphics();
      const beamRadius = BEAM_CONFIG.radius;
      const beamWidthRad = (BEAM_CONFIG.width / 2) * (Math.PI / 180);

      beamGraphics.moveTo(0, 0);
      beamGraphics.arc(0, 0, beamRadius, -Math.PI / 2 - beamWidthRad, -Math.PI / 2 + beamWidthRad);
      beamGraphics.lineTo(0, 0);
      beamGraphics.fill({ color: BEAM_CONFIG.color, alpha: BEAM_CONFIG.opacity });

      beamContainer.addChild(beamGraphics);
      beamGraphics.rotation = Math.PI;

      tableLayer.addChild(beamContainer);
      sceneRef.current.beamContainer = beamContainer;
      sceneRef.current.beamGraphics = beamGraphics;

      // Apply beam mask to clip beam to table edge
      const beamMaskTexture = textures.get('beamMask');
      if (beamMaskTexture) {
        const beamMask = new Sprite(beamMaskTexture);
        beamMask.anchor.set(0.5);
        // Center mask on table image (accounting for horizontal margin)
        beamMask.x = UI_HORIZONTAL_MARGIN + TABLE_IMAGE_WIDTH / 2;
        beamMask.y = TABLE_IMAGE_HEIGHT / 2;
        // Mask must be added to scene to work in PixiJS
        tableLayer.addChild(beamMask);
        beamContainer.mask = beamMask;
        sceneRef.current.beamMask = beamMask;
        console.log('[PixiGameTableV1] Beam mask applied');
      }

      // Shoe with fanned cards behind it (matching /table-demo)
      // Card configuration from DEFAULT_SHOE_CONFIG
      const SHOE_CARD_CONFIG = {
        rotation: 90, // Base rotation for all cards
        cardSize: { width: 53, height: 75 },
        cards: [
          { top: 16, left: 10, rotationOffset: -5 },
          { top: 12, left: 14, rotationOffset: 0 },
          { top: 8, left: 18, rotationOffset: 5 },
        ],
      };

      // Get shoe cards config offsets (default to 0)
      const shoeCardsX = shoeCardsConfig?.x ?? 0;
      const shoeCardsY = shoeCardsConfig?.y ?? 0;
      const shoeCardsRot = shoeCardsConfig?.rotation ?? 0;
      const shoeCardsScale = (shoeCardsConfig?.scale ?? 100) / 100;

      // Add fanned card backs BEHIND the shoe
      const cardBackTexture = textures.get('cardBack');
      sceneRef.current.shoeCardSprites = []; // Clear previous sprites
      if (cardBackTexture) {
        SHOE_CARD_CONFIG.cards.forEach((cardPos, index) => {
          const cardSprite = new Sprite(cardBackTexture);
          cardSprite.anchor.set(0.5);
          // Get per-card offset (with defaults for rotation/scale)
          const perCardOffset = shoeCardOffsets[index as 0 | 1 | 2] || { x: 0, y: 0, rotation: 0, scale: 100 };
          // Generate random rotation and Y offset for this card
          const randomRotation = generateShoeCardRotation();
          const randomY = generateShoeCardRandomY();
          // Store base offset and random values in sprite for later updates
          (cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseLeft = cardPos.left;
          (cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseTop = cardPos.top;
          (cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseRotOffset = cardPos.rotationOffset;
          (cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).cardIndex = index;
          (cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).randomRotation = randomRotation;
          (cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).randomY = randomY;
          // Position relative to shoe, offset by top/left values + group offset + per-card offset + random Y
          cardSprite.x = SHOE_POSITION.x + cardPos.left + shoeCardsX + perCardOffset.x;
          cardSprite.y = SHOE_POSITION.y + cardPos.top + shoeCardsY + perCardOffset.y + randomY;
          // Scale card to target size * group scale * per-card scale
          const perCardScale = (perCardOffset.scale ?? 100) / 100;
          const cardScaleX = (SHOE_CARD_CONFIG.cardSize.width / cardBackTexture.width) * shoeCardsScale * perCardScale;
          const cardScaleY = (SHOE_CARD_CONFIG.cardSize.height / cardBackTexture.height) * shoeCardsScale * perCardScale;
          cardSprite.scale.set(cardScaleX, cardScaleY);
          // Apply rotation (base + offset + group rotation + per-card rotation + random)
          const perCardRotation = perCardOffset.rotation ?? 0;
          cardSprite.rotation = degreesToRadians(SHOE_CARD_CONFIG.rotation + cardPos.rotationOffset + shoeCardsRot + perCardRotation + randomRotation);

          // Add 0.5px border around the card (as child, in local texture coords)
          // Line width is divided by scale so it appears as 0.5px after scaling
          const borderGraphics = new Graphics();
          const texW = cardBackTexture.width;
          const texH = cardBackTexture.height;
          const avgScale = (cardScaleX + cardScaleY) / 2;
          borderGraphics.rect(-texW / 2, -texH / 2, texW, texH);
          borderGraphics.stroke({ color: 0x000000, width: 1 / avgScale }); // Black 1px
          cardSprite.addChild(borderGraphics);
          // Ensure border renders on top by setting zIndex (if container has sortableChildren)
          borderGraphics.zIndex = 1;

          tableLayer.addChild(cardSprite);
          sceneRef.current.shoeCardSprites.push(cardSprite);
        });
      }

      // Shoe sprite - added to cardsLayer (above animationLayer) so dealt cards appear from behind
      const shoeTexture = textures.get('shoe');
      if (shoeTexture) {
        const shoeSprite = new Sprite(shoeTexture);
        shoeSprite.anchor.set(0.5);
        shoeSprite.x = SHOE_POSITION.x;
        shoeSprite.y = SHOE_POSITION.y;
        // Use scale to preserve aspect ratio - target ~86px on longest side
        const shoeScaleFactor = 86 / Math.max(shoeTexture.width, shoeTexture.height);
        shoeSprite.scale.set(shoeScaleFactor);
        // Render ABOVE animationLayer (dealt cards + chip flights) so a newly-dealt card
        // slides out from BEHIND the shoe. cardsLayer sits below animationLayer, so the shoe
        // must live above it — attach to gameContentContainer (topmost of the game layers).
        gameContentContainer.addChild(shoeSprite);
        sceneRef.current.shoeSprite = shoeSprite;

        // Create mask to clip shoe cards that peek beyond the shoe's visual top edge
        // The mask defines the VISIBLE area - a large rectangle covering everything
        // EXCEPT the area beyond the shoe's left edge (visual top)
        const shoeMask = new Graphics();
        const localShoeWidth = shoeTexture.width;
        const localShoeHeight = shoeTexture.height;

        // Mask covers from shoe's left edge (visual top) extending right/down
        // Large enough to cover all possible card positions
        const maskX = -localShoeWidth / 2;  // Start at shoe's left edge (visual top)
        const maskY = -localShoeHeight;     // Extend up
        const maskW = localShoeWidth * 3;   // Wide enough to cover cards
        const maskH = localShoeHeight * 3;  // Tall enough to cover cards
        shoeMask.rect(maskX, maskY, maskW, maskH);
        shoeMask.fill({ color: 0xffffff });
        shoeSprite.addChild(shoeMask);


        // Apply mask to each shoe card sprite
        sceneRef.current.shoeCardSprites.forEach(cardSprite => {
          cardSprite.mask = shoeMask;
        });

        // Store mask reference so new shoe cards can use it
        sceneRef.current.shoeMask = shoeMask;
        console.log(`[SHOE] Mask applied to clip cards at visual top edge`);
      }

      // Tray
      const trayTexture = textures.get('tray');
      if (trayTexture) {
        const traySprite = new Sprite(trayTexture);
        traySprite.anchor.set(0.5);
        traySprite.x = KITTY_POSITION.x;
        traySprite.y = KITTY_POSITION.y;
        // Use scale to preserve aspect ratio - target ~120px on longest side
        const trayScaleFactor = 120 / Math.max(trayTexture.width, trayTexture.height);
        traySprite.scale.set(trayScaleFactor);
        tableLayer.addChild(traySprite);
        sceneRef.current.traySprite = traySprite;
      }

      console.log('[PixiGameTableV1] Initialization complete');
    };

    initPixi()
      .catch(err => {
        if (!isActive || destroyRequestedRef.current) return; // Ignore errors from aborted init
        console.error('[PixiGameTableV1] Init failed:', err);
        setError(err?.message || String(err));
      })
      .finally(() => {
        if (!isActive || destroyRequestedRef.current) {
          console.log('[PixiGameTableV1] Init finished but component unmounted or destroy requested - skipping state update');
          return;
        }
        console.log('[PixiGameTableV1] Init complete, setting loading=false');
        // Make canvas visible now that initialization is complete
        if (appRef.current?.canvas) {
          appRef.current.canvas.style.visibility = 'visible';
        }
        setLoading(false);

        // Check if a visual resync was requested while loading
        // If so, notify parent component to trigger resync after initialization
        if (pendingResyncRef.current) {
          console.log('[PixiGameTableV1] Pending visual resync detected - scene now ready');
          // The useEffect watching loading state will handle the resync
        }
      });

    return () => {
      console.log('[PixiGameTableV1] Cleanup - marking inactive and resetting init flag');
      isActive = false;
      destroyRequestedRef.current = true; // Signal to any in-progress init to abort
      initStartedRef.current = false; // Allow next mount to reinitialize
      // Clear module-level state to prevent stale sprite references on remount
      clearModuleState();
      if (appRef.current) {
        // Store canvas reference before destroy
        const canvas = appRef.current.canvas as HTMLCanvasElement & {
          __contextLostHandler?: EventListener;
          __contextRestoredHandler?: EventListener;
        };
        // Remove WebGL context loss event listeners
        if (canvas.__contextLostHandler) {
          canvas.removeEventListener('webglcontextlost', canvas.__contextLostHandler);
        }
        if (canvas.__contextRestoredHandler) {
          canvas.removeEventListener('webglcontextrestored', canvas.__contextRestoredHandler);
        }
        // Clear health check interval
        const canvasWithInterval = canvas as unknown as { __healthCheckInterval?: ReturnType<typeof setInterval> };
        if (canvasWithInterval.__healthCheckInterval) {
          clearInterval(canvasWithInterval.__healthCheckInterval);
        }
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
        // Explicitly remove canvas from DOM if it still exists (fallback for clean navigation)
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      }
    };
  }, []);

  // ===========================================
  // PENDING RESYNC HANDLER
  // ===========================================
  // When loading completes and a resync is pending, request fresh table state
  // This handles the case where table_joined arrived before PixiJS was ready
  useEffect(() => {
    if (loading) return; // Still loading, nothing to do

    // Check if a resync was requested but couldn't complete
    const checkPendingResync = () => {
      if (pendingResyncRef.current) {
        console.log('[PixiGameTableV1] Pending resync detected after loading - requesting fresh table state');
        pendingResyncRef.current = false; // Clear immediately to prevent re-triggering

        // Request fresh table state from server - this will trigger fullVisualResync via table_joined
        const socket = useSocketStore.getState().socket;
        const tableId = useGameStateStore.getState().tableId;
        if (socket?.connected && tableId) {
          console.log('[PixiGameTableV1] Emitting join_table for pending resync:', tableId);
          socket.emit('client_message', { type: 'join_table', tableId, mode: 'play' });
        } else {
          console.warn('[PixiGameTableV1] Cannot request fresh state - socket not connected or no tableId');
        }
      }
    };

    // Small delay to ensure all state is settled
    const timerId = setTimeout(checkPendingResync, 100);
    return () => clearTimeout(timerId);
  }, [loading]);

  // ===========================================
  // PIXI CONTROL PANEL
  // ===========================================

  // Helper to get actual table bounds from the sprite (in canvas/stage coordinates)
  const getActualTableBounds = () => {
    const scene = sceneRef.current;
    const sprite = scene.tableSprite;
    const container = scene.gameContentContainer;

    if (sprite && container) {
      // Get container transform (scale and offset)
      const containerScale = container.scale.x; // Assuming uniform scaling
      const containerOffsetX = container.x;
      const containerOffsetY = container.y;

      // Calculate bounds in container's local coordinates
      const localX = sprite.x - (sprite.width * sprite.anchor.x);
      const localY = sprite.y - (sprite.height * sprite.anchor.y);
      const localWidth = sprite.width;
      const localHeight = sprite.height;

      // Transform to canvas/stage coordinates
      const x = localX * containerScale + containerOffsetX;
      const y = localY * containerScale + containerOffsetY;
      const width = localWidth * containerScale;
      const height = localHeight * containerScale;

      return { x, y, width, height };
    }
    // Fallback to constants if sprite not available
    return {
      x: UI_HORIZONTAL_MARGIN,
      y: 0,
      width: TABLE_IMAGE_WIDTH,
      height: TABLE_IMAGE_HEIGHT,
    };
  };

  // Create/destroy PixiJS control panel based on prop
  useEffect(() => {
    if (loading || !usePixiControlPanel || !tableId) return;

    const app = appRef.current;
    if (!app) return;

    // Get actual table bounds from sprite (accounting for scaling in dynamic mode)
    const tableBounds = getActualTableBounds();

    // Use actual canvas dimensions (dynamic or fixed)
    const actualCanvasWidth = canvasDimensions.width || CANVAS_WIDTH;
    const actualCanvasHeight = canvasDimensions.height || CANVAS_HEIGHT;

    // Create control panel
    const controlPanel = new PixiControlPanel({
      tableId,
      canvasWidth: actualCanvasWidth,
      canvasHeight: actualCanvasHeight,
      tableBounds,
      scale: 1, // Panel uses canvas coordinates directly, no CSS transform scaling
      onHit: onHit || (() => {}),
      onStand: onStand || (() => {}),
      onDouble: onDouble || (() => {}),
      onSplit: onSplit || (() => {}),
      onSurrender: onSurrender || (() => {}),
      onBet: onBet || (() => {}),
      onAutobet: onAutobet || (() => {}),
      onInsuranceAccept: onInsuranceAccept || (() => {}),
      onInsuranceDecline: onInsuranceDecline || (() => {}),
      onPositionChange: (position) => {
        console.log('[PixiGameTableV1] Control panel position changed to:', position);
        setPixiPanelPosition(position);
      },
      textSizes: panelTextSizes,
      isConnected,
    });

    // Add directly to stage (NOT uiLayer which gets scaled/offset)
    // This keeps the panel at canvas coordinates
    controlPanel.zIndex = STAGE_Z.controlPanel;
    app.stage.addChild(controlPanel);
    sceneRef.current.controlPanel = controlPanel;

    // Create insurance panel (centered on canvas)
    const insurancePanel = new PixiInsurancePanel({
      canvasWidth: actualCanvasWidth,
      canvasHeight: actualCanvasHeight,
      onAccept: onInsuranceAccept || (() => {}),
      onDecline: onInsuranceDecline || (() => {}),
    });

    // Add to stage (above control panel via zIndex)
    insurancePanel.zIndex = STAGE_Z.insurancePanel;
    app.stage.addChild(insurancePanel);
    sceneRef.current.insurancePanel = insurancePanel;

    // Create How To Bet modal (centered on canvas)
    const howToBetModal = new PixiHowToBetModal({
      canvasWidth: actualCanvasWidth,
      canvasHeight: actualCanvasHeight,
      onClose: onHowToBetClose || (() => {}),
    });

    // Add to stage (above insurance panel via zIndex)
    howToBetModal.zIndex = STAGE_Z.howToBet;
    app.stage.addChild(howToBetModal);
    sceneRef.current.howToBetModal = howToBetModal;

    return () => {
      const scene = sceneRef.current;
      if (scene.controlPanel) {
        scene.controlPanel.dispose();
        // Check if app.stage still exists before trying to remove child
        if (app.stage && app.stage.children.includes(scene.controlPanel)) {
          app.stage.removeChild(scene.controlPanel);
        }
        scene.controlPanel = null;
      }
      if (scene.insurancePanel) {
        scene.insurancePanel.dispose();
        if (app.stage && app.stage.children.includes(scene.insurancePanel)) {
          app.stage.removeChild(scene.insurancePanel);
        }
        scene.insurancePanel = null;
      }
      if (scene.howToBetModal) {
        scene.howToBetModal.dispose();
        if (app.stage && app.stage.children.includes(scene.howToBetModal)) {
          app.stage.removeChild(scene.howToBetModal);
        }
        scene.howToBetModal = null;
      }
      // Cleanup countdown rings
      scene.countdownRings.forEach((ring) => {
        if (!ring.destroyed) {
          ring.dispose();
        }
      });
      scene.countdownRings.clear();
    };
  // Note: canvasDimensions intentionally NOT in deps - dimension changes are handled by the update effect below
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, usePixiControlPanel, tableId, onHit, onStand, onDouble, onSplit, onSurrender, onBet, onAutobet, onInsuranceAccept, onInsuranceDecline, onHowToBetClose, panelTextSizes, isConnected]);

  // Update control panel and insurance panel when scale or canvas dimensions change
  useEffect(() => {
    const scene = sceneRef.current;

    // Get actual table bounds from sprite
    const tableBounds = getActualTableBounds();

    // Use actual canvas dimensions (dynamic or fixed)
    const actualCanvasWidth = canvasDimensions.width || CANVAS_WIDTH;
    const actualCanvasHeight = canvasDimensions.height || CANVAS_HEIGHT;

    if (scene.controlPanel) {
      scene.controlPanel.updateConfig({
        canvasWidth: actualCanvasWidth,
        canvasHeight: actualCanvasHeight,
        tableBounds,
        scale,
      });
    }

    if (scene.insurancePanel) {
      scene.insurancePanel.updateConfig({
        canvasWidth: actualCanvasWidth,
        canvasHeight: actualCanvasHeight,
      });
    }

    if (scene.howToBetModal) {
      scene.howToBetModal.updateConfig({
        canvasWidth: actualCanvasWidth,
        canvasHeight: actualCanvasHeight,
      });
    }
  }, [scale, canvasDimensions.width, canvasDimensions.height]);

  // Update control panel connection state when isConnected changes
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene.controlPanel) return;

    scene.controlPanel.setIsConnected(isConnected);
  }, [isConnected]);

  // Update BUDDY HUD visibility when buddyActive prop changes
  useEffect(() => {
    const scene = sceneRef.current;
    if (loading || !scene.buddyHUD.container) return;

    console.log('[BUDDY HUD] useEffect - buddyActive:', buddyActive);
    scene.buddyHUD.container.visible = buddyActive;
    scene.buddyHUD.visible = buddyActive;
  }, [loading, buddyActive]);

  // Update BUDDY HUD values when penetration or trueCount change
  useEffect(() => {
    const scene = sceneRef.current;
    if (loading || !buddyActive || !scene.buddyHUD.container) return;

    const hud = scene.buddyHUD;
    if (!hud.dpBarFill || !hud.dpValue || !hud.tcIndicator || !hud.tcValue) return;

    console.log('[BUDDY HUD] Updating values - penetration:', penetration, 'trueCount:', trueCount);
    updateBuddyHUDValues(hud.dpBarFill, hud.dpValue, hud.tcIndicator, hud.tcValue, penetration, trueCount);
  }, [loading, buddyActive, penetration, trueCount]);

  // ===========================================
  // SIDEBET MODAL
  // ===========================================
  // Creates the PixiJS sidebet modal UI
  useEffect(() => {
    if (loading || !appRef.current) return;

    const app = appRef.current;
    const textures = texturesRef.current;
    const scene = sceneRef.current;

    // Sidebet config data
    const SIDEBET_CONFIG: Record<string, {
      title: string;
      textureKey: string;
      description: string;
      twoColumn?: boolean; // For two-column payout display (MEGA HAND)
      columnHeaders?: string[]; // Headers for two-column layout
      payouts: { name: string; odds: string; odds2?: string; color: number }[];
    }> = {
      'perfect-pairs': {
        title: 'Perfect Pairs',
        textureKey: 'sidebet-1',
        description: 'Bet that your first two cards form a pair (same rank).',
        payouts: [
          { name: 'Mixed Pair', odds: '5:1', color: 0x94a3b8 },
          { name: 'Colored Pair', odds: '10:1', color: 0xfbbf24 },
          { name: 'Perfect Pair', odds: '25:1', color: 0x22c55e },
        ],
      },
      'twenty-one-plus-three': {
        title: 'Twenty-One +3',
        textureKey: 'sidebet-2',
        description: 'Your first two cards + dealer upcard form a poker hand.',
        payouts: [
          { name: 'Flush', odds: '5:1', color: 0x94a3b8 },
          { name: 'Straight', odds: '10:1', color: 0x3b82f6 },
          { name: 'Three of a Kind', odds: '25:1', color: 0xfbbf24 },
          { name: 'Straight Flush', odds: '50:1', color: 0xf97316 },
          { name: 'Suited Three of a Kind', odds: '250:1', color: 0x22c55e },
        ],
      },
      'top-three': {
        title: 'Top Three',
        textureKey: 'sidebet-3',
        description: 'Your first 2 cards + dealer upcard form a poker hand.',
        payouts: [
          { name: 'Straight or Flush', odds: '3:1', color: 0x94a3b8 },
          { name: 'Three of a Kind', odds: '33:1', color: 0xfbbf24 },
          { name: 'Straight Flush', odds: '333:1', color: 0xf97316 },
          { name: 'Suited Three of a Kind', odds: '3333:1', color: 0x22c55e },
        ],
      },
      'lucky-ladies': {
        title: 'Lucky Ladies',
        textureKey: 'sidebet-4',
        description: 'Win big if your first two cards total 20.',
        payouts: [
          { name: 'Any 20', odds: '4:1', color: 0x94a3b8 },
          { name: 'Suited 20', odds: '10:1', color: 0xfbbf24 },
          { name: 'Matched 20', odds: '25:1', color: 0xf97316 },
          { name: 'Queen of Hearts Pair', odds: '200:1', color: 0xec4899 },
        ],
      },
      'royal-match': {
        title: 'Royal Match',
        textureKey: 'sidebet-5',
        description: 'Win if your first two cards are suited, bonus for K+Q.',
        payouts: [
          { name: 'Suited', odds: '3:1', color: 0x94a3b8 },
          { name: 'Royal Match (K+Q)', odds: '25:1', color: 0xfbbf24 },
        ],
      },
      'lucky-lucky': {
        title: 'Lucky Lucky',
        textureKey: 'sidebet-6',
        description: 'Win based on your cards + dealer upcard totaling 19-21.',
        payouts: [
          { name: '19 or 20', odds: '2:1', color: 0x94a3b8 },
          { name: '21 (unsuited)', odds: '3:1', color: 0xfbbf24 },
          { name: '21 (suited)', odds: '15:1', color: 0xf97316 },
          { name: '6-7-8 suited', odds: '50:1', color: 0x22c55e },
          { name: '7-7-7 suited', odds: '200:1', color: 0xec4899 },
        ],
      },
      'blazing-sevens': {
        title: 'Blazing Sevens',
        textureKey: 'sidebet-7',
        description: 'Win if your first cards contain one or more 7s.',
        payouts: [
          { name: 'One 7', odds: '2:1', color: 0x94a3b8 },
          { name: 'Two 7s (unsuited)', odds: '25:1', color: 0xfbbf24 },
          { name: 'Two 7s (suited)', odds: '50:1', color: 0xf97316 },
          { name: 'Three 7s', odds: '500:1', color: 0x22c55e },
        ],
      },
      'busted-bet': {
        title: 'BUSTED! BET',
        textureKey: 'sidebet-8',
        description: 'Win if the dealer busts! Higher payout for specific bust totals.',
        payouts: [
          { name: 'Bust (22-24)', odds: '2:1', color: 0x94a3b8 },
          { name: 'Bust (25-26)', odds: '4:1', color: 0xfbbf24 },
          { name: 'Bust with 8+ cards', odds: '50:1', color: 0xf97316 },
          { name: 'Bust with 8-8-8', odds: '200:1', color: 0x22c55e },
        ],
      },
      'over-under-13': {
        title: 'Over/Under 13',
        textureKey: 'sidebet-9',
        description: 'Bet whether your first two cards total over or under 13.',
        payouts: [
          { name: 'Under 13', odds: '1:1', color: 0x3b82f6 },
          { name: 'Over 13', odds: '1:1', color: 0xf97316 },
          { name: 'Exactly 13', odds: '10:1', color: 0x22c55e },
        ],
      },
      'mega-hand': {
        title: 'MEGA HAND',
        textureKey: 'sidebet-10',
        description: 'Win big if your hand reaches 4+ cards totaling 21, or 5+ cards without busting!',
        twoColumn: true, // Special two-column payout display
        columnHeaders: ['<21', '=21'],
        payouts: [
          { name: '4 Cards', odds: '—', odds2: '5:1', color: 0x64748b },
          { name: '5 Cards', odds: '5:1', odds2: '25:1', color: 0x94a3b8 },
          { name: '6 Cards', odds: '50:1', odds2: '250:1', color: 0xfbbf24 },
          { name: '7 Cards', odds: '500:1', odds2: '2,500:1', color: 0xf97316 },
          { name: '8 Cards', odds: '5,000:1', odds2: '25,000:1', color: 0x22c55e },
          { name: '9 Cards', odds: '50,000:1', odds2: '250,000:1', color: 0xec4899 },
        ],
      },
      'straight': {
        title: 'Straight',
        textureKey: 'sidebet-11',
        description: 'Win if your hand forms a five-card straight without busting!',
        payouts: [
          { name: '5-Card Straight (Bust)', odds: '100:1', color: 0x94a3b8 },
          { name: '5-Card Straight', odds: '1,000:1', color: 0xfbbf24 },
          { name: '5-Card Straight Flush', odds: '100,000:1', color: 0xf97316 },
          { name: '5-Card Straight Flush w/Ace', odds: '250,000:1', color: 0x22c55e },
        ],
      },
      'flush': {
        title: 'Flush',
        textureKey: 'sidebet-12',
        description: 'Win if your hand forms a five-card flush without busting!',
        payouts: [
          { name: '5-Card Flush', odds: '1,000:1', color: 0x94a3b8 },
          { name: '5-Card Flush w/Ace', odds: '2,500:1', color: 0xfbbf24 },
          { name: '5-Card Straight Flush', odds: '100,000:1', color: 0xf97316 },
          { name: '5-Card Straight Flush w/Ace', odds: '250,000:1', color: 0x22c55e },
        ],
      },
    };

    const BET_PERCENTAGES = [10, 25, 50, 100];
    const MODAL_WIDTH = 380;
    const MODAL_HEIGHT = 520;

    // Create backdrop (with zIndex above control panel)
    const backdrop = new Graphics();
    backdrop.rect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    backdrop.fill({ color: 0x000000, alpha: 0.8 });
    backdrop.eventMode = 'static';
    backdrop.cursor = 'pointer';
    backdrop.visible = false;
    backdrop.on('pointerdown', () => hideSidebetModal());
    backdrop.zIndex = STAGE_Z.sidebetBackdrop;
    app.stage.addChild(backdrop);

    // Create modal container (with zIndex above backdrop)
    const modalContainer = new Container();
    modalContainer.x = CANVAS_WIDTH / 2;
    modalContainer.y = CANVAS_HEIGHT / 2;
    modalContainer.visible = false;
    modalContainer.eventMode = 'static'; // Block clicks from going to backdrop
    modalContainer.zIndex = STAGE_Z.sidebetModal;
    app.stage.addChild(modalContainer);

    // Store in sceneRef
    scene.sidebetModal.container = modalContainer;
    scene.sidebetModal.backdrop = backdrop;

    // Modal background
    const modalBg = new Graphics();
    modalBg.roundRect(-MODAL_WIDTH / 2, -MODAL_HEIGHT / 2, MODAL_WIDTH, MODAL_HEIGHT, 16);
    modalBg.fill({ color: 0x1f2937 });
    modalBg.stroke({ color: 0xa855f7, width: 2 });
    modalContainer.addChild(modalBg);

    // Header bar (solid purple - taller to accommodate large avatar)
    const HEADER_HEIGHT = 110;
    const headerBg = new Graphics();
    // Draw rounded rect for top corners, then cover bottom part with rect
    headerBg.roundRect(-MODAL_WIDTH / 2, -MODAL_HEIGHT / 2, MODAL_WIDTH, HEADER_HEIGHT, 16);
    headerBg.fill({ color: 0x9333ea });
    // Cover bottom corners with square rect
    headerBg.rect(-MODAL_WIDTH / 2, -MODAL_HEIGHT / 2 + HEADER_HEIGHT - 16, MODAL_WIDTH, 16);
    headerBg.fill({ color: 0x9333ea });
    modalContainer.addChild(headerBg);

    // Sidebet image - LARGE (3x original size for prominence)
    // Positioned to straddle the left edge of the modal
    const AVATAR_SIZE = 150;
    const AVATAR_X = -MODAL_WIDTH / 2;  // Center on left edge (half inside, half outside)
    const AVATAR_Y = -MODAL_HEIGHT / 2 + HEADER_HEIGHT / 2;

    // Gold circular border around the icon
    const avatarBorder = new Graphics();
    avatarBorder.circle(AVATAR_X, AVATAR_Y, AVATAR_SIZE / 2 + 4);
    avatarBorder.stroke({ color: 0xd4a54a, width: 5 });  // Gold color
    modalContainer.addChild(avatarBorder);

    const sidebetImage = new Sprite();
    sidebetImage.anchor.set(0.5);
    sidebetImage.x = AVATAR_X;
    sidebetImage.y = AVATAR_Y;
    sidebetImage.width = AVATAR_SIZE;
    sidebetImage.height = AVATAR_SIZE;
    modalContainer.addChild(sidebetImage);

    // Title text (left-justified with consistent padding from icon's visible edge)
    // Icon straddles left edge, so visible portion inside modal is AVATAR_SIZE / 2
    const TITLE_LEFT_PADDING = 15;
    const titleText = new Text({
      text: 'Sidebet',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 28,
        fontWeight: 'bold',
        fill: 0xffffff,
      }),
    });
    titleText.anchor.set(0, 0.5);  // Left-aligned
    titleText.x = -MODAL_WIDTH / 2 + AVATAR_SIZE / 2 + TITLE_LEFT_PADDING;  // Right edge of visible icon + padding
    titleText.y = -MODAL_HEIGHT / 2 + 38;  // Upper portion of header
    modalContainer.addChild(titleText);

    // Close button
    const closeBtn = new Graphics();
    closeBtn.circle(0, 0, 14);
    closeBtn.fill({ color: 0x374151 });
    closeBtn.x = MODAL_WIDTH / 2 - 30;
    closeBtn.y = -MODAL_HEIGHT / 2 + 25;
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.on('pointerdown', () => hideSidebetModal());
    const closeBtnX = new Text({
      text: 'X',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 16, fontWeight: 'bold', fill: 0xffffff }),
    });
    closeBtnX.anchor.set(0.5);
    closeBtn.addChild(closeBtnX);
    modalContainer.addChild(closeBtn);

    // Description text - inside header, below title (on purple background)
    const descText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        fill: 0xe9d5ff,  // Light purple/lavender for visibility on purple bg
        wordWrap: true,
        wordWrapWidth: MODAL_WIDTH - AVATAR_SIZE / 2 - TITLE_LEFT_PADDING - 30,  // Account for icon overlap
      }),
    });
    descText.x = -MODAL_WIDTH / 2 + AVATAR_SIZE / 2 + TITLE_LEFT_PADDING;  // Same x as title
    descText.y = -MODAL_HEIGHT / 2 + 60;  // Below title, still in header
    modalContainer.addChild(descText);

    // Payouts section header
    const payoutsHeader = new Text({
      text: 'PAYOUTS',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        fontWeight: 'bold',
        fill: 0xa855f7,
        letterSpacing: 1,
      }),
    });
    payoutsHeader.x = -MODAL_WIDTH / 2 + 20;
    payoutsHeader.y = -MODAL_HEIGHT / 2 + 133;  // Just below header (description moved into header)
    modalContainer.addChild(payoutsHeader);

    // Payouts container (will be populated when modal opens)
    const payoutsContainer = new Container();
    payoutsContainer.x = -MODAL_WIDTH / 2 + 20;
    payoutsContainer.y = -MODAL_HEIGHT / 2 + 148;  // Below PAYOUTS header
    modalContainer.addChild(payoutsContainer);

    // Bet amount section header
    const betHeader = new Text({
      text: 'PERCENTAGE OF MAIN BET',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        fontWeight: 'bold',
        fill: 0xa855f7,
        letterSpacing: 1,
      }),
    });
    betHeader.x = -MODAL_WIDTH / 2 + 20;
    betHeader.y = -MODAL_HEIGHT / 2 + 275;  // Moved down for more payout space
    modalContainer.addChild(betHeader);

    // Percentage buttons (no dollar amounts - percentage is the commitment)
    const percentBtnWidth = (MODAL_WIDTH - 60) / 4;
    const percentBtnHeight = 40;
    scene.sidebetModal.percentButtons.clear();

    for (let i = 0; i < BET_PERCENTAGES.length; i++) {
      const pct = BET_PERCENTAGES[i];
      const btnContainer = new Container();
      btnContainer.x = -MODAL_WIDTH / 2 + 20 + i * (percentBtnWidth + 8) + percentBtnWidth / 2;
      btnContainer.y = -MODAL_HEIGHT / 2 + 315;  // Moved down for more payout space
      btnContainer.eventMode = 'static';
      btnContainer.cursor = 'pointer';

      const btnBg = new Graphics();
      btnBg.roundRect(-percentBtnWidth / 2, -percentBtnHeight / 2, percentBtnWidth, percentBtnHeight, 8);
      btnBg.fill({ color: 0x374151 });
      btnContainer.addChild(btnBg);

      const btnText = new Text({
        text: `${pct}%`,
        style: new TextStyle({ fontFamily: 'Arial', fontSize: 18, fontWeight: 'bold', fill: 0xffffff }),
      });
      btnText.anchor.set(0.5);
      btnContainer.addChild(btnText);

      // Set explicit hit area
      btnContainer.hitArea = new Rectangle(-percentBtnWidth / 2, -percentBtnHeight / 2, percentBtnWidth, percentBtnHeight);
      btnContainer.on('pointerdown', () => {
        console.log(`[Sidebet Modal] Percentage button ${pct}% CLICKED`);
        selectPercentage(pct);
      });
      modalContainer.addChild(btnContainer);
      scene.sidebetModal.percentButtons.set(pct, btnContainer);
    }

    // Duration section header
    const durationHeader = new Text({
      text: 'BET DURATION',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        fontWeight: 'bold',
        fill: 0xa855f7,
        letterSpacing: 1,
      }),
    });
    durationHeader.x = -MODAL_WIDTH / 2 + 20;
    durationHeader.y = -MODAL_HEIGHT / 2 + 355;  // Moved down for more payout space
    modalContainer.addChild(durationHeader);

    // Duration buttons - Stepper (left) and Ongoing (right)
    const durBtnWidth = (MODAL_WIDTH - 50) / 2;
    const durBtnHeight = 55;
    scene.sidebetModal.roundButtons.clear();

    // LEFT: Stepper counter button (replaces "Once")
    const stepperContainer = new Container();
    stepperContainer.x = -MODAL_WIDTH / 2 + 20 + durBtnWidth / 2;
    stepperContainer.y = -MODAL_HEIGHT / 2 + 405;  // Moved down

    const stepperBg = new Graphics();
    stepperBg.roundRect(-durBtnWidth / 2, -durBtnHeight / 2, durBtnWidth, durBtnHeight, 8);
    stepperBg.fill({ color: 0x9333ea });  // Selected by default
    stepperBg.stroke({ color: 0xa855f7, width: 2 });
    stepperContainer.addChild(stepperBg);

    // Minus button
    const minusBtn = new Container();
    minusBtn.x = -durBtnWidth / 2 + 25;
    minusBtn.eventMode = 'static';
    minusBtn.cursor = 'pointer';
    const minusBg = new Graphics();
    minusBg.circle(0, 0, 14);
    minusBg.fill({ color: 0x6b21a8 });
    minusBtn.addChild(minusBg);
    const minusText = new Text({
      text: '−',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 20, fontWeight: 'bold', fill: 0xffffff }),
    });
    minusText.anchor.set(0.5);
    minusBtn.addChild(minusText);
    minusBtn.on('pointerdown', () => adjustRounds(-1));
    stepperContainer.addChild(minusBtn);

    // Round count display
    const roundCountText = new Text({
      text: '1',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 22, fontWeight: 'bold', fill: 0xffffff }),
    });
    roundCountText.anchor.set(0.5);
    roundCountText.y = -6;
    stepperContainer.addChild(roundCountText);
    scene.sidebetModal.roundCountText = roundCountText;

    // "rounds" label
    const roundsLabel = new Text({
      text: 'round',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 10, fill: 0xe9d5ff }),
    });
    roundsLabel.anchor.set(0.5);
    roundsLabel.y = 14;
    stepperContainer.addChild(roundsLabel);
    scene.sidebetModal.roundsLabel = roundsLabel;

    // Plus button
    const plusBtn = new Container();
    plusBtn.x = durBtnWidth / 2 - 25;
    plusBtn.eventMode = 'static';
    plusBtn.cursor = 'pointer';
    const plusBg = new Graphics();
    plusBg.circle(0, 0, 14);
    plusBg.fill({ color: 0x6b21a8 });
    plusBtn.addChild(plusBg);
    const plusText = new Text({
      text: '+',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 20, fontWeight: 'bold', fill: 0xffffff }),
    });
    plusText.anchor.set(0.5);
    plusBtn.addChild(plusText);
    plusBtn.on('pointerdown', () => adjustRounds(1));
    stepperContainer.addChild(plusBtn);

    // Click on stepper selects it (vs ongoing)
    stepperBg.eventMode = 'static';
    stepperBg.cursor = 'pointer';
    stepperBg.on('pointerdown', () => selectRoundsMode('stepper'));

    modalContainer.addChild(stepperContainer);
    scene.sidebetModal.roundButtons.set('stepper', stepperContainer);

    // RIGHT: Ongoing button
    const ongoingContainer = new Container();
    ongoingContainer.x = MODAL_WIDTH / 2 - 20 - durBtnWidth / 2;
    ongoingContainer.y = -MODAL_HEIGHT / 2 + 405;  // Moved down
    ongoingContainer.eventMode = 'static';
    ongoingContainer.cursor = 'pointer';

    const ongoingBg = new Graphics();
    ongoingBg.roundRect(-durBtnWidth / 2, -durBtnHeight / 2, durBtnWidth, durBtnHeight, 8);
    ongoingBg.fill({ color: 0x374151 });
    ongoingContainer.addChild(ongoingBg);

    const ongoingText = new Text({
      text: 'Ongoing',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 14, fontWeight: 'bold', fill: 0xffffff }),
    });
    ongoingText.anchor.set(0.5);
    ongoingText.y = -8;
    ongoingContainer.addChild(ongoingText);

    const ongoingDesc = new Text({
      text: 'Every round',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 10, fill: 0x9ca3af }),
    });
    ongoingDesc.anchor.set(0.5);
    ongoingDesc.y = 10;
    ongoingContainer.addChild(ongoingDesc);

    ongoingContainer.on('pointerdown', () => selectRoundsMode('ongoing'));
    modalContainer.addChild(ongoingContainer);
    scene.sidebetModal.roundButtons.set('ongoing', ongoingContainer);

    // Action buttons
    const actionBtnWidth = (MODAL_WIDTH - 50) / 2;
    const actionBtnHeight = 45;

    // Cancel/Terminate button (dynamic based on sidebet state)
    const cancelBtn = new Container();
    cancelBtn.x = -MODAL_WIDTH / 2 + 20 + actionBtnWidth / 2;
    cancelBtn.y = MODAL_HEIGHT / 2 - 35;
    cancelBtn.eventMode = 'static';
    cancelBtn.cursor = 'pointer';

    const cancelBtnBg = new Graphics();
    cancelBtnBg.roundRect(-actionBtnWidth / 2, -actionBtnHeight / 2, actionBtnWidth, actionBtnHeight, 8);
    cancelBtnBg.fill({ color: 0x374151 });
    cancelBtn.addChild(cancelBtnBg);

    const cancelBtnText = new Text({
      text: 'Cancel',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 12, fontWeight: 'bold', fill: 0xffffff, align: 'center' }),
    });
    cancelBtnText.anchor.set(0.5);
    cancelBtn.addChild(cancelBtnText);
    cancelBtn.on('pointerdown', () => {
      const modal = sceneRef.current.sidebetModal;
      // Check if this sidebet is configured (terminate mode)
      // For over-under-13, also check variant keys
      import('../../../stores/gameStateStore').then(({ useGameStateStore }) => {
        const configs = useGameStateStore.getState().mySidebetConfigs;
        const baseType = modal.currentSidebet || '';
        let existingConfig = configs?.get(baseType);

        // For over-under-13, also check the variant keys
        if (!existingConfig && baseType === 'over-under-13') {
          existingConfig = configs?.get('over-under-13-over') || configs?.get('over-under-13-under');
        }

        if (existingConfig) {
          // Terminate mode - remove the sidebet
          removeSidebetConfig();
        } else {
          // Cancel mode - just close the modal
          hideSidebetModal();
        }
      });
    });
    modalContainer.addChild(cancelBtn);

    // Store cancel button references for dynamic updates
    scene.sidebetModal.cancelButton = cancelBtn;
    scene.sidebetModal.cancelBtnBg = cancelBtnBg;
    scene.sidebetModal.cancelBtnText = cancelBtnText;

    // Place Bet button
    const placeBtn = new Container();
    placeBtn.x = MODAL_WIDTH / 2 - 20 - actionBtnWidth / 2;
    placeBtn.y = MODAL_HEIGHT / 2 - 35;
    placeBtn.eventMode = 'static';
    placeBtn.cursor = 'pointer';

    const placeBtnBg = new Graphics();
    placeBtnBg.roundRect(-actionBtnWidth / 2, -actionBtnHeight / 2, actionBtnWidth, actionBtnHeight, 8);
    placeBtnBg.fill({ color: 0x6b7280 }); // Disabled color initially
    placeBtn.addChild(placeBtnBg);

    const placeBtnText = new Text({
      text: 'Set Sidebet',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 14, fontWeight: 'bold', fill: 0xffffff }),
    });
    placeBtnText.anchor.set(0.5);
    placeBtn.addChild(placeBtnText);
    // Set explicit hit area to ensure click detection
    placeBtn.hitArea = new Rectangle(-actionBtnWidth / 2, -actionBtnHeight / 2, actionBtnWidth, actionBtnHeight);
    placeBtn.on('pointerdown', () => {
      console.log('[Sidebet Modal] Place button CLICKED');
      placeSidebetBet();
    });
    modalContainer.addChild(placeBtn);
    scene.sidebetModal.placeButton = placeBtn;

    // Helper functions
    function selectPercentage(pct: number) {
      console.log(`[Sidebet Modal] selectPercentage called: ${pct}%`);
      const modal = sceneRef.current.sidebetModal;
      modal.selectedPercentage = pct;
      console.log(`[Sidebet Modal] selectedPercentage set to: ${modal.selectedPercentage}`);

      // Update button visuals
      modal.percentButtons.forEach((btn, btnPct) => {
        const bg = btn.children[0] as Graphics;
        bg.clear();
        const w = (MODAL_WIDTH - 60) / 4;
        const h = 40;
        bg.roundRect(-w / 2, -h / 2, w, h, 8);
        if (btnPct === pct) {
          bg.fill({ color: 0x9333ea });
          bg.stroke({ color: 0xa855f7, width: 2 });
        } else {
          bg.fill({ color: 0x374151 });
        }
      });

      updatePlaceButton();
    }

    function adjustRounds(delta: number) {
      const modal = sceneRef.current.sidebetModal;
      // Also select stepper mode when adjusting
      modal.isOngoing = false;
      const newValue = Math.max(1, modal.selectedRounds + delta);
      modal.selectedRounds = newValue;

      // Update count text
      if (modal.roundCountText) {
        modal.roundCountText.text = String(newValue);
      }
      // Update label (singular/plural)
      if (modal.roundsLabel) {
        modal.roundsLabel.text = newValue === 1 ? 'round' : 'rounds';
      }

      updateDurationButtonVisuals();
    }

    function selectRoundsMode(mode: 'stepper' | 'ongoing') {
      const modal = sceneRef.current.sidebetModal;
      modal.isOngoing = mode === 'ongoing';

      updateDurationButtonVisuals();
    }

    function updateDurationButtonVisuals() {
      const modal = sceneRef.current.sidebetModal;
      const durBtnWidth = (MODAL_WIDTH - 50) / 2;
      const durBtnHeight = 55;

      // Update stepper button
      const stepperBtn = modal.roundButtons.get('stepper');
      if (stepperBtn) {
        const bg = stepperBtn.children[0] as Graphics;
        bg.clear();
        bg.roundRect(-durBtnWidth / 2, -durBtnHeight / 2, durBtnWidth, durBtnHeight, 8);
        if (!modal.isOngoing) {
          bg.fill({ color: 0x9333ea });
          bg.stroke({ color: 0xa855f7, width: 2 });
        } else {
          bg.fill({ color: 0x374151 });
        }
      }

      // Update ongoing button
      const ongoingBtn = modal.roundButtons.get('ongoing');
      if (ongoingBtn) {
        const bg = ongoingBtn.children[0] as Graphics;
        bg.clear();
        bg.roundRect(-durBtnWidth / 2, -durBtnHeight / 2, durBtnWidth, durBtnHeight, 8);
        if (modal.isOngoing) {
          bg.fill({ color: 0x9333ea });
          bg.stroke({ color: 0xa855f7, width: 2 });
        } else {
          bg.fill({ color: 0x374151 });
        }
        // Update desc text color
        const descText = ongoingBtn.children[2] as Text;
        if (descText) {
          descText.style.fill = modal.isOngoing ? 0xe9d5ff : 0x9ca3af;
        }
      }
    }

    function updatePlaceButton() {
      const modal = sceneRef.current.sidebetModal;
      const placeBtn = modal.placeButton;
      if (!placeBtn) return;

      const hasPercentage = modal.selectedPercentage !== null;
      // For Over/Under 13, also require a selection
      const isOverUnder13 = modal.currentSidebet === 'over-under-13';
      const hasOverUnderSelection = !isOverUnder13 || modal.selectedOverUnder !== null;
      const hasSelection = hasPercentage && hasOverUnderSelection;

      const bg = placeBtn.children[0] as Graphics;
      bg.clear();
      const w = (MODAL_WIDTH - 50) / 2;
      const h = 45;
      bg.roundRect(-w / 2, -h / 2, w, h, 8);
      bg.fill({ color: hasSelection ? 0x9333ea : 0x6b7280 });

      const text = placeBtn.children[1] as Text;
      if (hasSelection) {
        text.text = `Set ${modal.selectedPercentage}%`;
      } else if (isOverUnder13 && !hasOverUnderSelection) {
        text.text = 'Select Over/Under';
      } else {
        text.text = 'Set Sidebet';
      }

      placeBtn.cursor = hasSelection ? 'pointer' : 'not-allowed';
    }

    function removeSidebetConfig() {
      const modal = sceneRef.current.sidebetModal;
      const baseSidebetType = modal.currentSidebet;

      if (!baseSidebetType) {
        console.warn('[Sidebet Modal] No sidebet type to remove');
        hideSidebetModal();
        return;
      }

      // For over-under-13, find the actual config key (includes -over or -under suffix)
      import('../../../stores/gameStateStore').then(({ useGameStateStore }) => {
        const configs = useGameStateStore.getState().mySidebetConfigs;
        let sidebetType = baseSidebetType;

        // For over-under-13, find which variant is configured
        if (baseSidebetType === 'over-under-13') {
          if (configs?.has('over-under-13-over')) {
            sidebetType = 'over-under-13-over';
          } else if (configs?.has('over-under-13-under')) {
            sidebetType = 'over-under-13-under';
          }
        }

        console.log(`[Sidebet Modal] Removing sidebet config: ${sidebetType}`);

        // Check if this sidebet is PENDING (not yet committed) or LIVE (committed this hand)
        // NO-REFUNDS MODEL: LIVE bets ride to conclusion, only optimistically destroy PENDING icons
        const config = useGameStateStore.getState().mySidebetConfigs.get(sidebetType);
        const mySidebets = useGameStateStore.getState().mySidebets;
        const isLive = config?.chipsPlacedForCountdown === true || mySidebets.has(sidebetType);

        // Remove from game state store (local config only - not the placed bet if live)
        useGameStateStore.getState().removeSidebetConfig(sidebetType);

        // CRITICAL: Send remove_sidebet message to server
        // This triggers SIDEBET_REMOVED event which handles the rest
        onSidebetRemoveRef.current?.(sidebetType);

        // Only optimistically destroy the icon if PENDING (not yet committed)
        // LIVE bets: leave icon solid, let round-end resolution (removeSidebetFromSeat) handle cleanup
        if (isLive) {
          console.log(`[Sidebet Modal] ${sidebetType} is LIVE - icon stays until round-end resolution`);
        } else {
          // PENDING: Clean delete - destroy the icon immediately
          console.log(`[Sidebet Modal] ${sidebetType} is PENDING - destroying icon optimistically`);
          const scene = sceneRef.current;
          const mySeatPosition = useGameStateStore.getState().mySeatPosition;

          if (mySeatPosition !== null && mySeatPosition !== undefined) {
            // Find the slot with this sidebet type
            for (let i = 0; i < 3; i++) {
              const key = `${mySeatPosition}-${i}`;
              const sprite = scene.sidebetSprites.get(key);
              if (sprite && !sprite.destroyed) {
                const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
                if (storedType === sidebetType ||
                    (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
                  console.log(`[Sidebet Modal] Destroying sprite at ${key}`);

                  // Destroy border ring
                  const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
                  if (borderRing && !borderRing.destroyed) {
                    borderRing.destroy();
                  }

                  // Destroy sprite
                  sprite.destroy();
                  scene.sidebetSprites.delete(key);

                  // Destroy overlay
                  const overlay = scene.sidebetOverlays.get(key);
                  if (overlay && !overlay.destroyed) {
                    overlay.destroy();
                  }
                  scene.sidebetOverlays.delete(key);

                  // Destroy counter and pill
                  const counter = scene.sidebetCounters.get(key);
                  if (counter && !counter.destroyed) {
                    counter.destroy();
                  }
                  scene.sidebetCounters.delete(key);

                  const counterPill = scene.sidebetCounterPills.get(key);
                  if (counterPill && !counterPill.destroyed) {
                    counterPill.destroy();
                  }
                  scene.sidebetCounterPills.delete(key);
                  scene.sidebetChipCounts.delete(key);

                  break;
                }
              }
            }
          }

          // Also clear from active sidebets (only if pending - live bets need to resolve)
          scene.activeSidebets.delete(sidebetType);
        }
      });

      hideSidebetModal();
    }

    function placeSidebetBet() {
      console.log('[Sidebet Modal] placeSidebetBet called');
      const modal = sceneRef.current.sidebetModal;
      console.log('[Sidebet Modal] state:', {
        selectedPercentage: modal.selectedPercentage,
        currentSidebet: modal.currentSidebet,
        selectedRounds: modal.selectedRounds,
        isOngoing: modal.isOngoing,
        selectedOverUnder: modal.selectedOverUnder,
      });

      if (!modal.selectedPercentage) {
        console.warn('[Sidebet Modal] Early return: no percentage selected');
        return;
      }

      if (!modal.currentSidebet) {
        console.warn('[Sidebet Modal] Early return: no sidebet type');
        return;
      }

      // For Over/Under 13, require a selection
      if (modal.currentSidebet === 'over-under-13' && !modal.selectedOverUnder) {
        console.warn('[Sidebet Modal] Early return: Over/Under 13 requires over or under selection');
        return;
      }

      // "Set and forget" - save configuration to store (not immediate placement)
      // The sidebet will be auto-placed when the main bet is confirmed
      // IMPORTANT: Capture values BEFORE async operation since hideSidebetModal clears them
      // For Over/Under 13, append the selection to the sidebet type
      let sidebetType = modal.currentSidebet;
      if (modal.currentSidebet === 'over-under-13' && modal.selectedOverUnder) {
        sidebetType = `over-under-13-${modal.selectedOverUnder}`;
      }
      const percentage = modal.selectedPercentage!;
      const rounds = modal.isOngoing ? -1 : modal.selectedRounds;
      console.log(`[Sidebet Modal] Saving config: ${sidebetType}, ${percentage}%, ${rounds === -1 ? 'ongoing' : rounds + ' rounds'}`);

      // Import store and save config (async - uses captured values)
      import('../../../stores/gameStateStore').then(({ useGameStateStore }) => {
        useGameStateStore.getState().setSidebetConfig(sidebetType, percentage, rounds);
      });

      // Show visual indicator on sidebet icon (configured, waiting for bet)
      // Use full sidebetType so it matches the config and server events
      // (texture lookup handles variants - all over-under-13 variants use same icon)
      showSidebetConfigured(sidebetType, percentage, rounds);

      hideSidebetModal();
    }

    // Show visual indicator that a sidebet is configured and ACTIVE
    // GREEN border with rounds badge - active state (will trigger next hand)
    function showSidebetConfigured(sidebetType: string, percentage: number, rounds: number) {
      console.log(`[SIDEBET DEBUG] showSidebetConfigured: ${sidebetType}, ${percentage}%, ${rounds} rounds`);
      const scene = sceneRef.current;
      const textures = texturesRef.current;

      // Get mySeatPosition from store (note: this shadows the prop)
      const storeMySeatPosition = useGameStateStore.getState().mySeatPosition;
      if (storeMySeatPosition === null || storeMySeatPosition === undefined) {
        console.warn('[SIDEBET DEBUG] showSidebetConfigured: No seat position in store, cannot show sidebet');
        return;
      }

      // GUARD: Prevent showing sidebet visuals when mySeatPosition prop is stale
      // If the store has mySeatPosition set but the prop is still null, the component
      // needs to re-render first so getVisualPositionForSeat has the correct closure.
      if (mySeatPosition === null) {
        console.warn(`[SIDEBET DEBUG] showSidebetConfigured: SKIPPED - mySeatPosition prop is stale (null) but store has ${storeMySeatPosition}. Component needs to re-render first.`);
        return;
      }

      // First, search for existing overlay by scanning sprites (like restoreConfiguredSidebetVisuals)
      let overlayKey: string | null = null;
      for (const [key, sprite] of scene.sidebetSprites) {
        if (sprite && !sprite.destroyed) {
          const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
          if (storedType === sidebetType ||
              (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
            overlayKey = key;
            console.log(`[SIDEBET DEBUG] Found existing sprite for ${sidebetType} at key: ${key}`);
            break;
          }
        }
      }

      // If no existing icon, create one at the user's seat
      if (!overlayKey) {
        console.log(`[SIDEBET DEBUG] No existing icon for ${sidebetType}, creating at seat ${mySeatPosition}`);

        const visualPos = getVisualPositionForSeat(mySeatPosition);
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData || !scene.sidebetLayer) {
          console.warn('[SIDEBET DEBUG] Missing seat data or sidebet layer');
          return;
        }

        // Get or create sidebet container for this seat
        let seatContainer = scene.seatSidebetContainers.get(mySeatPosition);
        if (!seatContainer) {
          seatContainer = new Container();
          seatContainer.sortableChildren = true;
          seatContainer.eventMode = 'passive'; // Allow events to propagate to children
          const config = sidebetConfigRef.current;
          // Per-seat offset (additive on top of base config) - use VISUAL position for DEV CONTROLS alignment
          const perSeatOffset = sidebetIconConfigs?.[visualPos] || { radiusOffset: 0, x: 0, y: 0 };
          const baseLocalOffsetY = config.baseRadiusOffset + perSeatOffset.radiusOffset;
          const rotRad = degreesToRadians(seatData.rotation);
          const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);
          seatContainer.x = basePos.x + perSeatOffset.x;
          seatContainer.y = basePos.y + perSeatOffset.y;
          seatContainer.rotation = rotRad;
          scene.sidebetLayer.addChild(seatContainer);
          scene.seatSidebetContainers.set(mySeatPosition, seatContainer);
        }

        // Find next available slot
        let slotIndex = -1;
        for (let i = 0; i < 3; i++) {
          const key = `${mySeatPosition}-${i}`;
          if (!scene.sidebetSprites.has(key)) {
            slotIndex = i;
            break;
          }
        }
        if (slotIndex === -1) {
          console.warn('[SIDEBET DEBUG] No available slot for sidebet icon');
          return;
        }

        // Get texture for this sidebet type
        const textureIndex = SIDEBET_TYPE_TO_INDEX[sidebetType];
        const texture = textures.get(`sidebet-${textureIndex}`);
        if (!texture) {
          console.warn(`[SIDEBET DEBUG] No texture for sidebet type ${sidebetType}`);
          return;
        }

        // Create the icon
        const config = sidebetConfigRef.current;

        // Gold border ring behind the icon
        const borderRing = new Graphics();
        borderRing.circle(0, 0, config.iconSize / 2 + 3);
        borderRing.stroke({ color: 0xd4a54a, width: 3 });
        seatContainer.addChild(borderRing);

        const iconSprite = new Sprite(texture);
        iconSprite.anchor.set(0.5);
        iconSprite.width = config.iconSize;
        iconSprite.height = config.iconSize;
        (iconSprite as unknown as { sidebetType: string }).sidebetType = sidebetType;
        (iconSprite as unknown as { borderRing: Graphics }).borderRing = borderRing;

        // Pending state: configured but not yet staked this hand — dim the ICON only.
        // Keep the gold ring fully solid so the pending sidebet stays easy to spot on the felt.
        // Icon flips to alpha 1 when chips actually land via animateSidebetPlacement.
        iconSprite.alpha = 0.5;
        borderRing.alpha = 1;

        // Make clickable for current user's sidebet icons
        iconSprite.eventMode = 'static';
        iconSprite.cursor = 'pointer';
        iconSprite.on('pointerdown', () => {
          console.log('[DEBUG SIDEBET CLICK] Icon clicked:', { sidebetType, slotIndex, mySeatPosition });
          if (onSidebetIconClick) {
            onSidebetIconClick(sidebetType);
          }
        });

        seatContainer.addChild(iconSprite);

        overlayKey = `${mySeatPosition}-${slotIndex}`;
        scene.sidebetSprites.set(overlayKey, iconSprite);

        // Create overlay for glow effects
        const newOverlay = new Container();
        newOverlay.alpha = 0;
        seatContainer.addChild(newOverlay);
        scene.sidebetOverlays.set(overlayKey, newOverlay);

        // Create counter pill and text (hidden for configured state)
        const counterPill = new Graphics();
        counterPill.zIndex = 50;
        seatContainer.addChild(counterPill);
        scene.sidebetCounterPills.set(overlayKey, counterPill);

        const counterStyle = new TextStyle({
          fontFamily: 'Arial, sans-serif',
          fontSize: SIDEBET_COUNTER_CONFIG.fontSize,
          fontWeight: 'bold',
          fill: SIDEBET_COUNTER_CONFIG.fontColor,
        });
        const counterText = new Text({ text: '', style: counterStyle });
        counterText.anchor.set(0.5, 0.5);
        counterText.zIndex = 51;
        counterText.visible = false;
        seatContainer.addChild(counterText);
        scene.sidebetCounters.set(overlayKey, counterText);
        scene.sidebetChipCounts.set(overlayKey, 0);

        // Reposition all icons in arch
        const posConfig = sidebetConfigRef.current;
        const activeSlots: { sprite: Sprite; overlay: Container | null; counter: Text | null; counterPill: Graphics | null; borderRing: Graphics | null }[] = [];
        for (let i = 0; i < 3; i++) {
          const k = `${mySeatPosition}-${i}`;
          const s = scene.sidebetSprites.get(k);
          if (s && !s.destroyed) {
            activeSlots.push({
              sprite: s,
              overlay: scene.sidebetOverlays.get(k) || null,
              counter: scene.sidebetCounters.get(k) || null,
              counterPill: scene.sidebetCounterPills.get(k) || null,
              borderRing: (s as unknown as { borderRing?: Graphics }).borderRing || null,
            });
          }
        }
        const posIndices = posConfig.getPositionsForCount(activeSlots.length);
        activeSlots.forEach((slot, idx) => {
          const posIdx = posIndices[idx];
          const pos = posConfig.slotPositions[posIdx];
          slot.sprite.x = pos.localX;
          slot.sprite.y = pos.localY;
          if (slot.borderRing) {
            slot.borderRing.x = pos.localX;
            slot.borderRing.y = pos.localY;
          }
          if (slot.overlay) {
            slot.overlay.x = pos.localX;
            slot.overlay.y = pos.localY;
          }
          if (slot.counter) {
            slot.counter.x = pos.localX;
            slot.counter.y = pos.localY + posConfig.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
          }
          if (slot.counterPill) {
            slot.counterPill.x = pos.localX;
            slot.counterPill.y = pos.localY + posConfig.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
          }
        });

        console.log(`[SIDEBET DEBUG] Created icon at ${overlayKey}`);
      }

      // Now get the overlay and add the configured badge
      const overlay = scene.sidebetOverlays.get(overlayKey);
      console.log(`[SIDEBET DEBUG] overlay found for "${overlayKey}":`, overlay ? 'yes' : 'NO');

      if (overlay) {
        overlay.removeChildren();

        // Badge showing rounds remaining (no green ring - icon on table = active)
        const badgeSize = 18;
        const badgeX = SIDEBET_ICON_SIZE / 2 - 4;
        const badgeY = SIDEBET_ICON_SIZE / 2 - 4;

        const badgeBg = new Graphics();
        badgeBg.circle(badgeX, badgeY, badgeSize / 2);
        badgeBg.fill({ color: 0x22c55e }); // Green
        badgeBg.stroke({ color: 0xffffff, width: 1.5 });
        overlay.addChild(badgeBg);

        const badgeText = new Text({
          text: rounds === -1 ? '∞' : String(rounds),
          style: {
            fontFamily: 'Arial',
            fontSize: rounds === -1 ? 14 : 11,
            fontWeight: 'bold',
            fill: 0xffffff
          }
        });
        badgeText.anchor.set(0.5);
        badgeText.x = badgeX;
        badgeText.y = badgeY + (rounds === -1 ? 1 : 0);
        overlay.addChild(badgeText);

        overlay.alpha = 1;
      }
    }

    // Show gold border for sidebets that were previously played at this table
    // Encourages re-activation
    function showSidebetPreviouslyPlayed(sidebetType: string) {
      const scene = sceneRef.current;
      const overlay = scene.sidebetOverlays.get(sidebetType);

      if (overlay) {
        overlay.removeChildren();

        // GOLD/AMBER FAT ring to show "previously played" state
        const ring = new Graphics();
        ring.circle(0, 0, SIDEBET_ICON_SIZE / 2 + 4);
        ring.stroke({ color: 0xfbbf24, width: 4, alpha: 0.7 }); // Gold/amber ring
        overlay.addChild(ring);

        overlay.alpha = 1;
      }
    }

    // Clear sidebet indicator (no border)
    function clearSidebetIndicator(sidebetType: string) {
      const scene = sceneRef.current;
      const overlay = scene.sidebetOverlays.get(sidebetType);
      if (overlay) {
        overlay.removeChildren();
        overlay.alpha = 0;
      }
    }

    function showSidebetModal(sidebetType: string) {
      const modal = sceneRef.current.sidebetModal;
      // Handle variant types (e.g., 'over-under-13-over' -> 'over-under-13')
      let configKey = sidebetType;
      if (sidebetType.startsWith('over-under-13-')) {
        configKey = 'over-under-13';
      }
      const config = SIDEBET_CONFIG[configKey];
      if (!config) {
        console.warn(`[PixiGameTableV1] Unknown sidebet type: ${sidebetType} (looked up: ${configKey})`);
        return;
      }

      // Get actual canvas dimensions for centering
      const actualWidth = appRef.current?.screen.width || CANVAS_WIDTH;
      const actualHeight = appRef.current?.screen.height || CANVAS_HEIGHT;

      // Reposition backdrop and modal for current canvas size
      if (modal.backdrop) {
        modal.backdrop.clear();
        modal.backdrop.rect(0, 0, actualWidth, actualHeight);
        modal.backdrop.fill({ color: 0x000000, alpha: 0.8 });
      }
      if (modal.container) {
        modal.container.x = actualWidth / 2;
        modal.container.y = actualHeight / 2;
      }

      // For variants, set currentSidebet to base type so config lookup works correctly
      modal.currentSidebet = configKey;
      modal.selectedPercentage = null;
      modal.selectedRounds = 1;
      modal.isOngoing = false;
      // Pre-select over/under option if opening a variant
      if (sidebetType === 'over-under-13-over') {
        modal.selectedOverUnder = 'over';
      } else if (sidebetType === 'over-under-13-under') {
        modal.selectedOverUnder = 'under';
      } else {
        modal.selectedOverUnder = null;
      }
      modal.overUnderButtons.clear();
      modal.isOpen = true;

      // Update title
      titleText.text = config.title;

      // Update image
      const texture = textures.get(config.textureKey);
      if (texture) {
        sidebetImage.texture = texture;
      }

      // Update description
      descText.text = config.description;

      // Update payouts
      payoutsContainer.removeChildren();

      // Clean up any existing column headers from previous sidebet
      if ((modal as any).columnHeaders) {
        for (const header of (modal as any).columnHeaders) {
          if (header.parent) header.parent.removeChild(header);
          header.destroy();
        }
        (modal as any).columnHeaders = null;
      }

      // Helper to update Over/Under radio button visuals
      const updateOverUnderVisuals = () => {
        modal.overUnderButtons.forEach((btnData, key) => {
          const isSelected = modal.selectedOverUnder === key;
          btnData.radio.clear();
          btnData.radio.circle(0, 0, 8);
          btnData.radio.stroke({ color: isSelected ? 0x22c55e : 0x6b7280, width: 2 });
          if (isSelected) {
            btnData.radio.circle(0, 0, 4);
            btnData.radio.fill({ color: 0x22c55e });
          }
        });
        updatePlaceButton();
      };

      // Check if this is a two-column payout display (MEGA HAND)
      const isTwoColumn = config.twoColumn === true;
      const columnHeaders = config.columnHeaders;

      if (isTwoColumn && columnHeaders) {
        // Two-column layout for MEGA HAND
        // Column headers on same line as "PAYOUTS" - add directly to modal container
        // Position relative to payoutsHeader (which is at y = -MODAL_HEIGHT / 2 + 118)
        const col1Header = new Text({
          text: columnHeaders[0],
          style: new TextStyle({ fontFamily: 'Arial', fontSize: 12, fontWeight: 'bold', fill: 0x9ca3af }),
        });
        col1Header.anchor.set(1, 0);
        col1Header.x = MODAL_WIDTH / 2 - 130 + 20; // Right side of modal - 130px
        col1Header.y = -MODAL_HEIGHT / 2 + 133; // Same y as PAYOUTS header
        modalContainer.addChild(col1Header);

        const col2Header = new Text({
          text: columnHeaders[1],
          style: new TextStyle({ fontFamily: 'Arial', fontSize: 12, fontWeight: 'bold', fill: 0x9ca3af }),
        });
        col2Header.anchor.set(1, 0);
        col2Header.x = MODAL_WIDTH / 2 - 60 + 20; // Right side of modal - 60px
        col2Header.y = -MODAL_HEIGHT / 2 + 133; // Same y as PAYOUTS header
        modalContainer.addChild(col2Header);

        // Store references so we can remove them when modal closes/changes
        (modal as any).columnHeaders = [col1Header, col2Header];

        // Add payout rows with two columns - no header row offset needed
        config.payouts.forEach((payout, idx) => {
          const row = new Container();
          row.y = idx * 18; // No +1 offset - headers are outside payoutsContainer

          // Name column
          const nameText = new Text({
            text: payout.name,
            style: new TextStyle({ fontFamily: 'Arial', fontSize: 13, fill: payout.color }),
          });
          row.addChild(nameText);

          // Odds column 1 (<21)
          const odds1Text = new Text({
            text: payout.odds,
            style: new TextStyle({ fontFamily: 'Arial', fontSize: 13, fontWeight: 'bold', fill: 0x22c55e }),
          });
          odds1Text.anchor.set(1, 0);
          odds1Text.x = MODAL_WIDTH - 130;
          row.addChild(odds1Text);

          // Odds column 2 (=21) - use odds2 if available
          const odds2 = payout.odds2 || payout.odds;
          const odds2Text = new Text({
            text: odds2,
            style: new TextStyle({ fontFamily: 'Arial', fontSize: 13, fontWeight: 'bold', fill: 0xfbbf24 }), // Gold color for =21
          });
          odds2Text.anchor.set(1, 0);
          odds2Text.x = MODAL_WIDTH - 60;
          row.addChild(odds2Text);

          payoutsContainer.addChild(row);
        });
      } else {
        // Standard single-column layout
        config.payouts.forEach((payout, idx) => {
          const row = new Container();
          row.y = idx * 18; // Single-spaced to fit more payouts

          // For Over/Under 13, add radio buttons for Under, Over, and Exactly 13 options
          const isOverUnder13 = sidebetType === 'over-under-13';
          const isSelectableRow = isOverUnder13 && (payout.name === 'Under 13' || payout.name === 'Over 13' || payout.name === 'Exactly 13');

          if (isSelectableRow) {
            // Make row interactive
            row.eventMode = 'static';
            row.cursor = 'pointer';

            // Radio button circle
            const radioBtn = new Graphics();
            radioBtn.circle(0, 0, 8);
            radioBtn.stroke({ color: 0x6b7280, width: 2 });
            radioBtn.x = 10;
            radioBtn.y = 8;
            row.addChild(radioBtn);

            // Store reference for updates
            const key = payout.name === 'Under 13' ? 'under' : (payout.name === 'Over 13' ? 'over' : 'exactly');
            modal.overUnderButtons.set(key, { container: row, radio: radioBtn });

            // Click handler
            row.on('pointerdown', () => {
              modal.selectedOverUnder = key as 'over' | 'under' | 'exactly';
              updateOverUnderVisuals();
            });

            // Name text (offset for radio button)
            const nameText = new Text({
              text: payout.name,
              style: new TextStyle({ fontFamily: 'Arial', fontSize: 13, fill: payout.color }),
            });
            nameText.x = 26;
            row.addChild(nameText);
          } else {
            // Standard payout row (no radio button)
            const nameText = new Text({
              text: payout.name,
              style: new TextStyle({ fontFamily: 'Arial', fontSize: 13, fill: payout.color }),
            });
            row.addChild(nameText);
          }

          const oddsText = new Text({
            text: payout.odds,
            style: new TextStyle({ fontFamily: 'Arial', fontSize: 13, fontWeight: 'bold', fill: 0x22c55e }),
          });
          oddsText.anchor.set(1, 0);
          oddsText.x = MODAL_WIDTH - 60;
          row.addChild(oddsText);

          payoutsContainer.addChild(row);
        });
      }

      // Reset percentage buttons
      modal.percentButtons.forEach((btn) => {
        const bg = btn.children[0] as Graphics;
        bg.clear();
        const w = (MODAL_WIDTH - 60) / 4;
        const h = 40;
        bg.roundRect(-w / 2, -h / 2, w, h, 8);
        bg.fill({ color: 0x374151 });
      });

      // Reset stepper to 1 and select stepper mode (not ongoing)
      modal.selectedRounds = 1;
      modal.isOngoing = false;
      if (modal.roundCountText) {
        modal.roundCountText.text = '1';
      }
      if (modal.roundsLabel) {
        modal.roundsLabel.text = 'round';
      }
      updateDurationButtonVisuals();

      updatePlaceButton();

      // Check if this sidebet is already configured
      // For over-under-13, check both variants since config is stored with suffix
      import('../../../stores/gameStateStore').then(({ useGameStateStore }) => {
        const configs = useGameStateStore.getState().mySidebetConfigs;
        let existingConfig = configs?.get(sidebetType);
        let configKey = sidebetType;

        // For over-under-13, also check the variant keys
        if (!existingConfig && sidebetType === 'over-under-13') {
          existingConfig = configs?.get('over-under-13-over');
          if (existingConfig) {
            configKey = 'over-under-13-over';
            modal.selectedOverUnder = 'over';
          } else {
            existingConfig = configs?.get('over-under-13-under');
            if (existingConfig) {
              configKey = 'over-under-13-under';
              modal.selectedOverUnder = 'under';
            }
          }
        }
        const isConfigured = !!existingConfig;

        // Update Cancel/Terminate button based on existing config
        if (modal.cancelBtnText && modal.cancelBtnBg) {
          const actionBtnWidth = (MODAL_WIDTH - 50) / 2;
          const actionBtnHeight = 45;

          modal.cancelBtnBg.clear();
          modal.cancelBtnBg.roundRect(-actionBtnWidth / 2, -actionBtnHeight / 2, actionBtnWidth, actionBtnHeight, 8);

          if (isConfigured && existingConfig) {
            // Terminate mode - show red button with duration info
            modal.cancelBtnBg.fill({ color: 0xdc2626 }); // Red
            if (existingConfig.rounds === -1) {
              modal.cancelBtnText.text = 'Terminate\nOngoing bet';
            } else {
              modal.cancelBtnText.text = `Terminate\n${existingConfig.rounds} more rounds`;
            }
          } else {
            // Cancel mode - show gray button
            modal.cancelBtnBg.fill({ color: 0x374151 }); // Gray
            modal.cancelBtnText.text = 'Cancel';
          }
        }

        // If configured, pre-populate the modal with existing values
        if (isConfigured && existingConfig) {
          modal.selectedPercentage = existingConfig.percentage;
          modal.selectedRounds = existingConfig.rounds === -1 ? 1 : existingConfig.rounds;
          modal.isOngoing = existingConfig.rounds === -1;

          // Update percentage button visuals
          modal.percentButtons.forEach((btn, btnPct) => {
            const bg = btn.children[0] as Graphics;
            bg.clear();
            const w = (MODAL_WIDTH - 60) / 4;
            const h = 40;
            bg.roundRect(-w / 2, -h / 2, w, h, 8);
            if (btnPct === existingConfig.percentage) {
              bg.fill({ color: 0x9333ea });
              bg.stroke({ color: 0xa855f7, width: 2 });
            } else {
              bg.fill({ color: 0x374151 });
            }
          });

          // Update duration controls
          if (modal.roundCountText) {
            modal.roundCountText.text = String(modal.selectedRounds);
          }
          if (modal.roundsLabel) {
            modal.roundsLabel.text = modal.selectedRounds === 1 ? 'round' : 'rounds';
          }
          updateDurationButtonVisuals();

          // Update Over/Under radio button visuals if applicable
          if (modal.selectedOverUnder && modal.overUnderButtons.size > 0) {
            updateOverUnderVisuals();
          }

          updatePlaceButton();
        }
      });

      // Show modal
      backdrop.visible = true;
      modalContainer.visible = true;
    }

    function hideSidebetModal() {
      const modal = sceneRef.current.sidebetModal;
      modal.isOpen = false;
      modal.currentSidebet = null;
      backdrop.visible = false;
      modalContainer.visible = false;
    }

    // Store show function in ref
    showSidebetModalRef.current = showSidebetModal;

    // Cleanup
    return () => {
      showSidebetModalRef.current = null;
      if (backdrop.parent) backdrop.parent.removeChild(backdrop);
      if (modalContainer.parent) modalContainer.parent.removeChild(modalContainer);
      backdrop.destroy();
      modalContainer.destroy({ children: true });
    };
  }, [loading]);

  // ===========================================
  // SHIFT GAME CONTENT FOR PANEL POSITION (fixed canvas mode only)
  // ===========================================
  // When PixiJS panel is on a side position, shift game content to make room
  // NOTE: When using dynamic canvas sizing, this is handled by the dynamic resize effect
  useEffect(() => {
    // Skip if using dynamic sizing - that effect handles positioning
    if (availableWidth && availableHeight) return;

    if (loading) return;
    const scene = sceneRef.current;
    if (!scene.gameContentContainer) return;

    // Panel width in canvas coordinates (unscaled - we want consistent spacing)
    const PANEL_WIDTH = 160;
    const PANEL_MARGIN = 10; // Small gap between panel and table

    // Calculate horizontal offset based on panel position
    let offsetX = 0;
    if (pixiPanelPosition === 'viewport-left' || pixiPanelPosition === 'table-left') {
      // Panel on left - shift game content right
      offsetX = PANEL_WIDTH + PANEL_MARGIN;
    } else if (pixiPanelPosition === 'viewport-right' || pixiPanelPosition === 'table-right') {
      // Panel on right - shift game content left
      offsetX = -(PANEL_WIDTH + PANEL_MARGIN);
    }
    // 'top' and 'bottom' positions - no horizontal shift

    // Apply offset to game content container
    scene.gameContentContainer.x = offsetX;

    console.log('[PixiGameTableV1] Game content shifted for panel position:', {
      position: pixiPanelPosition,
      offsetX,
    });

    // Update control panel with new effective table bounds
    if (scene.controlPanel && scene.tableSprite) {
      const sprite = scene.tableSprite;
      const width = sprite.width;
      const height = sprite.height;
      // Table bounds in stage coordinates (account for container offset)
      const tableBounds = {
        x: sprite.x - (width * sprite.anchor.x) + offsetX,
        y: sprite.y - (height * sprite.anchor.y),
        width,
        height,
      };

      scene.controlPanel.updateConfig({
        canvasWidth: CANVAS_WIDTH,
        canvasHeight: CANVAS_HEIGHT,
        tableBounds,
        scale,
      });

      console.log('[PixiGameTableV1] Updated panel with shifted tableBounds:', tableBounds);
    }
  }, [loading, pixiPanelPosition, scale, availableWidth, availableHeight]);

  // Update shoe and tray positions when config changes
  useEffect(() => {
    if (loading) return;
    const scene = sceneRef.current;

    // Update shoe
    if (scene.shoeSprite && shoeConfig) {
      scene.shoeSprite.x = SHOE_POSITION.x + shoeConfig.x;
      scene.shoeSprite.y = SHOE_POSITION.y + shoeConfig.y;
      scene.shoeSprite.rotation = degreesToRadians(shoeConfig.rotation);
      // Get base scale from texture, then apply config scale
      const shoeTexture = texturesRef.current.get('shoe');
      if (shoeTexture) {
        const baseScale = 86 / Math.max(shoeTexture.width, shoeTexture.height);
        scene.shoeSprite.scale.set(baseScale * (shoeConfig.scale / 100));
      }
    }

    // Update tray
    if (scene.traySprite && trayConfig) {
      scene.traySprite.x = KITTY_POSITION.x + trayConfig.x;
      scene.traySprite.y = KITTY_POSITION.y + trayConfig.y;
      scene.traySprite.rotation = degreesToRadians(trayConfig.rotation);
      // Get base scale from texture, then apply config scale
      const trayTexture = texturesRef.current.get('tray');
      if (trayTexture) {
        const baseScale = 120 / Math.max(trayTexture.width, trayTexture.height);
        scene.traySprite.scale.set(baseScale * (trayConfig.scale / 100));
      }
    }

    // Update shoe cards (fanned cards behind shoe)
    if (scene.shoeCardSprites.length > 0) {
      const shoeCardsX = shoeCardsConfig?.x ?? 0;
      const shoeCardsY = shoeCardsConfig?.y ?? 0;
      const shoeCardsRot = shoeCardsConfig?.rotation ?? 0;
      const shoeCardsScale = (shoeCardsConfig?.scale ?? 100) / 100;
      const cardBackTexture = texturesRef.current.get('cardBack');
      const BASE_CARD_SIZE = { width: 53, height: 75 };
      const BASE_ROTATION = 90;

      scene.shoeCardSprites.forEach((cardSprite) => {
        const spriteData = cardSprite as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number };
        // Get per-card offset (with defaults for rotation/scale)
        const perCardOffset = shoeCardOffsets[spriteData.cardIndex as 0 | 1 | 2] || { x: 0, y: 0, rotation: 0, scale: 100 };
        const randomY = spriteData.randomY ?? 0;
        // Update position (group offset + per-card offset + random Y)
        cardSprite.x = SHOE_POSITION.x + spriteData.baseLeft + shoeCardsX + perCardOffset.x;
        cardSprite.y = SHOE_POSITION.y + spriteData.baseTop + shoeCardsY + perCardOffset.y + randomY;
        // Update scale (group scale * per-card scale)
        if (cardBackTexture) {
          const perCardScale = (perCardOffset.scale ?? 100) / 100;
          const cardScaleX = (BASE_CARD_SIZE.width / cardBackTexture.width) * shoeCardsScale * perCardScale;
          const cardScaleY = (BASE_CARD_SIZE.height / cardBackTexture.height) * shoeCardsScale * perCardScale;
          cardSprite.scale.set(cardScaleX, cardScaleY);
        }
        // Update rotation (group rotation + per-card rotation + random)
        const perCardRotation = perCardOffset.rotation ?? 0;
        const randomRot = spriteData.randomRotation ?? 0;
        cardSprite.rotation = degreesToRadians(BASE_ROTATION + spriteData.baseRotOffset + shoeCardsRot + perCardRotation + randomRot);
      });
    }
  }, [loading, shoeConfig, trayConfig, shoeCardsConfig, shoeCardOffsets]);

  // ===========================================
  // CORNER CARDS (Scorekeeper & Modal)
  // ===========================================
  // Create/update corner card sprites when config changes
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    const textures = texturesRef.current;
    const cardBackTexture = textures.get('cardBack');

    if (!cardBackTexture || !scene.tableLayer) return;

    // Base card dimensions
    const BASE_CARD_SIZE = { width: 80, height: 112 };
    const CORNER_RADIUS = 6;

    // Helper to create a corner card with rounded corners (matches createCardSprite style)
    const createCornerCard = (
      config: { x: number; y: number; rotation: number; scale: number }
    ): Container => {
      const scaleFactor = config.scale / 100;
      const cardWidth = BASE_CARD_SIZE.width * scaleFactor;
      const cardHeight = BASE_CARD_SIZE.height * scaleFactor;
      const cornerRadius = CORNER_RADIUS * scaleFactor;

      const container = new Container();
      container.x = TABLE_CENTER.x + config.x;
      container.y = TABLE_CENTER.y + config.y;
      container.rotation = degreesToRadians(config.rotation);
      container.zIndex = 5;

      // Shadow
      const shadow = new Graphics();
      shadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, cornerRadius + 2);
      shadow.fill({ color: 0x000000, alpha: 0.3 });
      container.addChild(shadow);

      // White background
      const bg = new Graphics();
      bg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      bg.fill({ color: 0xfefefe });
      container.addChild(bg);

      // Rounded rectangle mask
      const mask = new Graphics();
      mask.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      mask.fill({ color: 0xffffff });
      container.addChild(mask);

      // Card back sprite with mask
      const backSprite = new Sprite(cardBackTexture);
      backSprite.width = cardWidth;
      backSprite.height = cardHeight;
      backSprite.anchor.set(0.5);
      backSprite.mask = mask;
      container.addChild(backSprite);

      // Border overlay
      const border = new Graphics();
      border.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      border.stroke({ color: 0x374151, width: 1 });
      container.addChild(border);

      return container;
    };

    // Left corner card (Scorecard - flippable to show round results)
    // NOTE: Scorecard is added to scorecardOverlay (stage-level) instead of tableLayer
    // so it renders above the control panel per STAGE_Z ordering.
    if (leftCornerCardConfig) {
      // Remove existing scorecard components from scorecardOverlay (not tableLayer)
      if (scene.scorecardContainer && scene.scorecardOverlay) {
        scene.scorecardOverlay.removeChild(scene.scorecardContainer);
        scene.scorecardContainer.destroy({ children: true });
      }
      if (scene.leftCornerCard && scene.scorecardOverlay) {
        scene.scorecardOverlay.removeChild(scene.leftCornerCard);
        scene.leftCornerCard.destroy({ children: true });
      }

      const scaleFactor = leftCornerCardConfig.scale / 100;
      const cardWidth = BASE_CARD_SIZE.width * scaleFactor;
      const cardHeight = BASE_CARD_SIZE.height * scaleFactor;
      const cornerRadius = CORNER_RADIUS * scaleFactor;

      // Create parent container for the scorecard
      const scorecardContainer = new Container();
      scorecardContainer.x = TABLE_CENTER.x + leftCornerCardConfig.x;
      scorecardContainer.y = TABLE_CENTER.y + leftCornerCardConfig.y;
      scorecardContainer.rotation = degreesToRadians(leftCornerCardConfig.rotation);
      scorecardContainer.zIndex = 5;

      // === BACK SIDE (card back - initially visible) ===
      const backContainer = new Container();

      // Shadow
      const backShadow = new Graphics();
      backShadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, cornerRadius + 2);
      backShadow.fill({ color: 0x000000, alpha: 0.3 });
      backContainer.addChild(backShadow);

      // White background
      const backBg = new Graphics();
      backBg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      backBg.fill({ color: 0xfefefe });
      backContainer.addChild(backBg);

      // Rounded rectangle mask
      const backMask = new Graphics();
      backMask.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      backMask.fill({ color: 0xffffff });
      backContainer.addChild(backMask);

      // Card back sprite with mask
      const backSprite = new Sprite(cardBackTexture);
      backSprite.width = cardWidth;
      backSprite.height = cardHeight;
      backSprite.anchor.set(0.5);
      backSprite.mask = backMask;
      backContainer.addChild(backSprite);

      // Border overlay
      const backBorder = new Graphics();
      backBorder.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      backBorder.stroke({ color: 0x374151, width: 1 });
      backContainer.addChild(backBorder);

      scorecardContainer.addChild(backContainer);
      scene.scorecardBack = backContainer;

      // === FRONT SIDE (results - initially hidden via scaleX=0) ===
      const frontContainer = new Container();
      frontContainer.scale.x = 0; // Hidden initially

      // Shadow
      const frontShadow = new Graphics();
      frontShadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, cornerRadius + 2);
      frontShadow.fill({ color: 0x000000, alpha: 0.3 });
      frontContainer.addChild(frontShadow);

      // Dark background (house style)
      const frontBg = new Graphics();
      frontBg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      frontBg.fill({ color: 0x1F2937 }); // gray-800
      frontContainer.addChild(frontBg);

      // Gold border
      const frontBorder = new Graphics();
      frontBorder.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, cornerRadius);
      frontBorder.stroke({ color: 0xFFD700, width: 2 }); // Gold
      frontContainer.addChild(frontBorder);

      // Content container - rotated to counter the card's rotation so text is readable
      // The card is rotated 90 degrees, so we rotate content -90 degrees
      const contentContainer = new Container();
      contentContainer.rotation = degreesToRadians(-leftCornerCardConfig.rotation);
      frontContainer.addChild(contentContainer);

      // For rotated content, swap width/height for layout calculations
      // Since card is rotated 90°, the "visual width" is cardHeight and "visual height" is cardWidth
      const contentWidth = cardHeight;
      const contentHeight = cardWidth;

      // Padding values (halved from original)
      const topPadding = 4 * scaleFactor;
      const bottomPadding = 4 * scaleFactor;
      const sidePadding = 4 * scaleFactor;
      const dividerColor = 0x9CA3AF; // gray-400 (lighter)

      // Header text
      const headerStyle = new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: Math.round(10 * scaleFactor),
        fontWeight: 'bold',
        fill: 0xFFD700, // Gold
      });
      const headerText = new Text({ text: 'NET RESULTS', style: headerStyle });
      headerText.anchor.set(0.5, 0);
      headerText.y = -contentHeight / 2 + topPadding;
      contentContainer.addChild(headerText);

      // Divider line under header
      const headerDivider = new Graphics();
      headerDivider.moveTo(-contentWidth / 2 + sidePadding, -contentHeight / 2 + 18 * scaleFactor);
      headerDivider.lineTo(contentWidth / 2 - sidePadding, -contentHeight / 2 + 18 * scaleFactor);
      headerDivider.stroke({ color: dividerColor, width: 1 });
      contentContainer.addChild(headerDivider);

      // Result lines (will be populated dynamically)
      // Store references for updating - separate label and value texts
      scene.scorecardResultLines = [];
      scene.scorecardValueLines = [];
      const labelStyle = new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: Math.round(8 * scaleFactor),
        fill: 0xD1D5DB, // gray-300
      });
      const valueStyle = new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: Math.round(8 * scaleFactor),
        fill: 0xD1D5DB, // gray-300
      });

      // Create 5 placeholder lines (main bet + up to 4 sidebets)
      // Each line has a label (left) and value (right)
      for (let i = 0; i < 5; i++) {
        // Label text (left-justified)
        const labelText = new Text({ text: '', style: labelStyle.clone() });
        labelText.anchor.set(0, 0.5);
        labelText.x = -contentWidth / 2 + sidePadding;
        labelText.y = -contentHeight / 2 + (26 + i * 10) * scaleFactor;
        labelText.visible = false;
        contentContainer.addChild(labelText);
        scene.scorecardResultLines.push(labelText);

        // Value text (right-justified)
        const valueText = new Text({ text: '', style: valueStyle.clone() });
        valueText.anchor.set(1, 0.5);
        valueText.x = contentWidth / 2 - sidePadding;
        valueText.y = -contentHeight / 2 + (26 + i * 10) * scaleFactor;
        valueText.visible = false;
        contentContainer.addChild(valueText);
        scene.scorecardValueLines.push(valueText);
      }

      // Divider line above total
      const totalDivider = new Graphics();
      totalDivider.moveTo(-contentWidth / 2 + sidePadding, contentHeight / 2 - 18 * scaleFactor);
      totalDivider.lineTo(contentWidth / 2 - sidePadding, contentHeight / 2 - 18 * scaleFactor);
      totalDivider.stroke({ color: dividerColor, width: 1 });
      contentContainer.addChild(totalDivider);

      // Total text
      const totalStyle = new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: Math.round(10 * scaleFactor),
        fontWeight: 'bold',
        fill: 0xFFD700, // Gold
      });
      const totalText = new Text({ text: 'TOTAL: BB 0', style: totalStyle });
      totalText.anchor.set(0.5, 1);
      totalText.y = contentHeight / 2 - bottomPadding;
      contentContainer.addChild(totalText);
      scene.scorecardTotalText = totalText;

      // Store layout dimensions for vertical centering in updateScorecardContent
      scene.scorecardScaleFactor = scaleFactor;
      scene.scorecardContentHeight = contentHeight;

      scorecardContainer.addChild(frontContainer);
      scene.scorecardFront = frontContainer;
      scene.scorecardContainer = scorecardContainer;
      scene.scorecardIsShowingFront = false;
      scene.leftCornerCard = scorecardContainer; // Also store as leftCornerCard for compatibility

      // Add to scorecardOverlay (stage-level) so it renders above control panel
      scene.scorecardOverlay?.addChild(scorecardContainer);
    }

    // Right corner card (Modal replacement)
    if (rightCornerCardConfig) {
      // Remove existing
      if (scene.rightCornerCard) {
        scene.tableLayer.removeChild(scene.rightCornerCard);
        scene.rightCornerCard.destroy({ children: true });
      }

      const rightCard = createCornerCard(rightCornerCardConfig);
      scene.tableLayer.addChild(rightCard);
      scene.rightCornerCard = rightCard;
    }
  }, [loading, leftCornerCardConfig, rightCornerCardConfig]);

  // ===========================================
  // DEV CARD HAND DISPLAY
  // ===========================================
  // Show a 4-card test hand at selected seat for positioning
  useEffect(() => {
    console.log('[DEV CARDS] useEffect triggered, devCardSeat:', devCardSeat, 'loading:', loading, 'appRef:', !!appRef.current);

    if (loading || !appRef.current) {
      console.log('[DEV CARDS] Early return - loading or no app');
      return;
    }

    const scene = sceneRef.current;
    const textures = texturesRef.current;

    // Clear existing dev cards
    scene.devCardSprites.forEach(container => {
      scene.cardsLayer?.removeChild(container);
      container.destroy({ children: true });
    });
    scene.devCardSprites = [];

    // If no seat selected, we're done
    if (devCardSeat === null) {
      console.log('[DEV CARDS] No seat selected, cleared cards');
      return;
    }

    // Get visual position for this seat
    const getVisualPos = (seatPos: number): number => {
      if (mySeatPosition === null) return seatPos;
      return ((seatPos - mySeatPosition + 10) % 7) + 1;
    };
    const visualPos = getVisualPos(devCardSeat);
    const seatData = dynamicSeatPositions[visualPos - 1];
    console.log('[DEV CARDS] visualPos:', visualPos, 'seatData:', seatData, 'cardsLayer:', !!scene.cardsLayer);
    if (!seatData) {
      console.log('[DEV CARDS] No seat data found');
      return;
    }

    // Get per-seat card config (uses VISUAL position - configs are about screen location, not physical seat)
    const seatCardConfig = perSeatCardConfigs[visualPos] || { x: 0, y: 0, radiusOffset: 0 };

    // Apply opponent scale if this is not the player's seat
    const isOpponentSeat = devCardSeat !== mySeatPosition;
    const effectiveCardConfig = isOpponentSeat
      ? applyOpponentCardScale(cardConfig)
      : cardConfig;
    console.log('[DEV CARDS] Creating cards at seat', devCardSeat, 'isOpponent:', isOpponentSeat, 'effectiveScale:', effectiveCardConfig.scale);

    // Card configuration - use scaled overlap (same as game cards)
    const scaleFactor = effectiveCardConfig.scale / 100;
    const scaledOverlap = CARD_OVERLAP * scaleFactor;

    // Position calculation (using existing card positioning logic)
    const rotRad = degreesToRadians(seatData.rotation);
    const baseCardsY = effectiveCardConfig.seatCardsY + seatCardConfig.radiusOffset;

    // Create 4 random cards
    const RANKS = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'] as const;
    const SUITS = ['spades', 'hearts', 'diamonds', 'clubs'] as const;

    for (let i = 0; i < 4; i++) {
      const rank = RANKS[Math.floor(Math.random() * RANKS.length)];
      const suit = SUITS[Math.floor(Math.random() * SUITS.length)];

      // Calculate centered position for this card in the hand
      const centeredOffset = (i - (4 - 1) / 2) * scaledOverlap;

      // Transform local position to world position
      const localX = centeredOffset + seatCardConfig.x;
      const localY = baseCardsY + seatCardConfig.y;
      const worldX = seatData.x + localX * Math.cos(rotRad) - localY * Math.sin(rotRad);
      const worldY = seatData.y + localX * Math.sin(rotRad) + localY * Math.cos(rotRad);

      // Use createCardSprite to create the card with proper rendering
      const card: CardType = { rank, suit, faceUp: true };
      const cardSprite = createCardSprite(card, textures, worldX, worldY, seatData.rotation, effectiveCardConfig);
      cardSprite.zIndex = 100 + i;

      console.log('[DEV CARDS] Card', i, ':', rank, suit, 'at', worldX.toFixed(1), worldY.toFixed(1), 'children:', cardSprite.children.length, 'visible:', cardSprite.visible, 'alpha:', cardSprite.alpha);

      // Add to scene
      scene.cardsLayer?.addChild(cardSprite);
      scene.devCardSprites.push(cardSprite);
    }
    console.log('[DEV CARDS] Created', scene.devCardSprites.length, 'cards, cardsLayer children:', scene.cardsLayer?.children.length, 'cardsLayer visible:', scene.cardsLayer?.visible, 'cardsLayer alpha:', scene.cardsLayer?.alpha);
  }, [loading, devCardSeat, perSeatCardConfigs, cardConfig, mySeatPosition, dynamicSeatPositions]);

  // ===========================================
  // DEV HAND LABEL VISUALIZATION
  // ===========================================
  // Show static hand labels at all seats for positioning verification
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    const container = scene.devHandLabelsContainer;
    if (!container) return;

    // Clear existing labels
    while (container.children.length > 0) {
      const child = container.children[0];
      container.removeChild(child);
      child.destroy();
    }

    // If not showing, we're done
    if (!showHandLabels) return;

    console.log('[DEV HAND LABELS] Creating static hand label for player seat only');

    // Iterate through all 7 visual seat positions
    for (let visualPos = 1; visualPos <= 7; visualPos++) {
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) continue;

      // ONLY show hand labels for the player's seat (visual position 4)
      // Skip opponent seats entirely
      const isPlayerSeat = mySeatPosition !== null && visualPos === 4;
      if (!isPlayerSeat) continue;

      // Get seat config for scaling - use actual seat position (mySeatPosition) to match production code
      const seatConfig = seatConfigs[mySeatPosition!] || { x: 0, y: 0, rot: 0, scale: 100 };
      const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
      const scaledSeatHeight = Math.round(168 * seatScale);
      const scaledSeatWidth = Math.round(144 * seatScale);

      // Create label using pre-loaded texture (prevents texture accumulation)
      const handLabelTexture = texturesRef.current.get('handLabel-1');
      let handLabel: Sprite | Text;
      if (handLabelTexture) {
        handLabel = new Sprite(handLabelTexture);
      } else {
        // Fallback to Text if texture not found
        handLabel = new Text({
          text: 'Hand1',
          style: {
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 14,
            fontWeight: '700',
            fill: 0x000000,
            align: 'left',
          }
        });
        MODULE_TEXT_CREATED_COUNT++;
      }
      handLabel.anchor.set(0, 0.5); // Left-justified, vertically centered

      // Position BELOW seat border, on RIGHT side (near avatar)
      // X: positive = toward avatar (right side), Y: positive = below seat border
      // Combines global config with per-seat config
      const seatRotRad = degreesToRadians(seatData.rotation);
      const labelLocalY = scaledSeatHeight / 2 + 8 + handLabelConfig.y; // Baseline +8px Y + config Y
      const labelLocalX = scaledSeatWidth * 0.2 - 38 + handLabelConfig.x; // Baseline -38px X + config X

      // Calculate world position
      const labelFinalWorld = localToWorld(seatData.x, seatData.y, labelLocalX, labelLocalY, seatRotRad);

      // Create background with ONLY bottom-left corner rounded (3 squared corners)
      const handLabelBg = new Graphics();
      const leftPadding = 8;
      const rightPadding = 16;
      const pillWidth = (handLabel.width + leftPadding + rightPadding) * 1.2; // 20% longer
      const pillHeight = handLabel.height + 8;
      const r = 4; // Corner radius for bottom-left only
      const x = 0; // Left edge at origin (text will be offset by leftPadding)
      const y = -pillHeight / 2;
      const w = pillWidth;
      const h = pillHeight;

      // Draw pill with only bottom-left corner rounded
      handLabelBg.moveTo(x, y);                        // Top-left (SQUARED)
      handLabelBg.lineTo(x + w, y);                    // Top edge to top-right (SQUARED)
      handLabelBg.lineTo(x + w, y + h);                // Right edge to bottom-right (SQUARED)
      handLabelBg.lineTo(x + r, y + h);                // Bottom edge to before bottom-left
      handLabelBg.arcTo(x, y + h, x, y + h - r, r);    // Bottom-left corner (ROUNDED)
      handLabelBg.lineTo(x, y);                        // Left edge back to top-left
      handLabelBg.closePath();
      handLabelBg.fill({ color: 0xFFFFFF });

      // Position background at final world position
      handLabelBg.x = labelFinalWorld.x;
      handLabelBg.y = labelFinalWorld.y;
      handLabelBg.rotation = seatRotRad;

      // Position text with left padding inside the pill
      // Text anchor is (0, 0.5) so we offset by leftPadding in local coords
      const textLocalX = labelLocalX + leftPadding;
      const textFinalWorld = localToWorld(seatData.x, seatData.y, textLocalX, labelLocalY, seatRotRad);
      handLabel.x = textFinalWorld.x;
      handLabel.y = textFinalWorld.y;
      handLabel.rotation = seatRotRad;

      // Add to container
      container.addChild(handLabelBg);
      container.addChild(handLabel);
    }

    console.log('[DEV HAND LABELS] Static hand labels added to all 7 seats');
  }, [loading, showHandLabels, handLabelConfig, handLabelConfigs, dynamicSeatPositions, seatConfigs, seatSizeScale]);

  // Update tray chips display (dealer chip rail - one chip per denomination)
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    const textures = texturesRef.current;

    // Remove existing tray chips
    if (scene.trayChipsContainer) {
      scene.tableLayer?.removeChild(scene.trayChipsContainer);
      scene.trayChipsContainer = null;
      scene.trayChipSprites.clear();
    }

    // Get tray position (with config offsets if any)
    const trayX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
    const trayY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
    const trayScale = (trayConfig?.scale || 100) / 100;

    // Create container for tray chip rail
    const trayChipsContainer = new Container();
    trayChipsContainer.x = trayX;
    trayChipsContainer.y = trayY;

    // Tray chip positions for each denomination (curved line following tray shape)
    // Scaled for canvas coordinates (base tray is ~120px, canvas is 1920x1080)
    const TRAY_CHIP_POSITIONS: Record<number, { x: number; y: number }> = {
      1:     { x: -50, y: 29 },
      5:     { x: -37, y: 29 },
      25:    { x: -21, y: 28 },
      100:   { x: -8, y: 29 },
      500:   { x: 6, y: 28 },
      1000:  { x: 24, y: 30 },
      5000:  { x: 38, y: 30 },
      10000: { x: 50, y: 30 },
    };

    const chipSize = 18 * trayScale; // Small chips for tray

    // Create one chip per denomination
    CHIP_DENOMS.forEach((denom) => {
      const pos = TRAY_CHIP_POSITIONS[denom];
      if (!pos) return;

      const texture = textures.get(`chip-${denom}`);
      if (!texture) return;

      const chip = new Sprite(texture);
      chip.width = chipSize;
      chip.height = chipSize;
      chip.anchor.set(0.5);
      chip.x = pos.x * trayScale;
      chip.y = pos.y * trayScale;
      // Random rotation for natural chip appearance (0-359 degrees)
      chip.rotation = Math.random() * 2 * Math.PI;
      trayChipsContainer.addChild(chip);
      // Store reference for rotation updates when chips land
      scene.trayChipSprites.set(denom, chip);
    });

    scene.tableLayer?.addChild(trayChipsContainer);
    scene.trayChipsContainer = trayChipsContainer;
  }, [loading, trayChipsOffset, trayConfig]);

  // Update scene when game state changes
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    const textures = texturesRef.current;

    // Clear and rebuild seats layer and associated layers
    // IMPORTANT: destroy() removes event listeners and frees GPU resources
    const oldSeats = scene.seatsLayer?.removeChildren() ?? [];
    oldSeats.forEach(child => child.destroy({ children: true }));
    // Preserve scoreboxes with wipe animations in progress
    const oldScoreboxes = scene.scoreboxLayer?.removeChildren() ?? [];
    const preservedScoreboxes: Container[] = [];
    oldScoreboxes.forEach(child => {
      // Check if this scorebox has a wipe animation in progress
      const label = child.label;
      if (label && label.startsWith('scorebox-')) {
        const seatPos = parseInt(label.replace('scorebox-', ''), 10);
        if (MODULE_SCOREBOX_WIPE_IN_PROGRESS.has(seatPos)) {
          // Preserve this scorebox - animation is running
          preservedScoreboxes.push(child as Container);
          return;
        }
      }
      child.destroy({ children: true });
    });
    // Re-add preserved scoreboxes
    preservedScoreboxes.forEach(scorebox => {
      scene.scoreboxLayer?.addChild(scorebox);
    });
    const oldHighlights = scene.highlightLayer?.removeChildren() ?? [];
    oldHighlights.forEach(child => child.destroy({ children: true }));
    // PRESERVE dealer button when clearing avatarLayer - it needs to persist for smooth animation
    const dealerButtonRef = scene.dealerButtonContainer;
    const oldAvatars = scene.avatarLayer?.removeChildren() ?? [];
    oldAvatars.forEach(child => {
      // Don't destroy the dealer button - we want to animate it, not recreate it
      if (child !== dealerButtonRef) {
        child.destroy({ children: true });
      }
    });
    // Re-add dealer button if it exists and wasn't destroyed
    if (dealerButtonRef && !dealerButtonRef.destroyed && scene.avatarLayer) {
      scene.avatarLayer.addChild(dealerButtonRef);
    }
    scene.seatContainers.clear();

    // Dealer area (cards are rendered in animation layer, not here)
    // Dealer uses index 0 in the seat configs (DEV controls use 0 for dealer)
    const dealerSeatConfig = seatConfigs[0] || DEFAULT_SEAT_CONFIG;
    const dealerContainer = createDealerArea(dealer, dealerPersona, textures, cardConfig, dealerSeatConfig, seatSizeScale, scoreboxConfig, dealerScoreboxOffset);
    scene.seatsLayer?.addChild(dealerContainer);
    scene.dealerContainer = dealerContainer;

    // Player seats
    for (let actualSeat = 1; actualSeat <= 7; actualSeat++) {
      const visualPos = getVisualPositionForSeat(actualSeat);
      const basePos = dynamicSeatPositions[visualPos - 1];
      const player = players.get(actualSeat);
      const isCurrentUser = mySeatPosition === actualSeat;
      const isPlayerTurn = currentTurnSeat === actualSeat;

      // Get per-seat configs
      // seatConfigs uses visualPos (for screen position adjustments)
      // betChipConfigs uses actualSeat, insuranceChipConfigs uses visualPos (DEV CONTROLS alignment)
      const seatConfig = seatConfigs[visualPos] || DEFAULT_SEAT_CONFIG;
      const betChipConfig = betChipConfigs[actualSeat] || DEFAULT_CHIP_CONFIG;
      const insuranceChipConfig = insuranceChipConfigs[visualPos] || DEFAULT_CHIP_CONFIG;

      // Apply per-seat position adjustments (x, y offsets and rotation)
      const seatX = basePos.x + seatConfig.x;
      const seatY = basePos.y + seatConfig.y;
      const seatRotation = basePos.rotation + seatConfig.rot;
      const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);

      // Determine seat click handler:
      // - Empty seat + no user seat → onSeatClick (join table)
      // - User's own seat → onStraightenCards (straighten cards)
      let seatClickHandler: ((seatPosition: number) => void) | undefined;
      if (!player && mySeatPosition === null) {
        seatClickHandler = onSeatClick;
      } else if (isCurrentUser && onStraightenCards) {
        seatClickHandler = onStraightenCards;
      }

      const seatContainer = createSeatContainer(
        actualSeat,
        seatX,
        seatY,
        seatRotation,
        player,
        isCurrentUser,
        isPlayerTurn,
        textures,
        currentHandIndex,
        seatClickHandler,
        seatScale * 100, // Convert back to percentage for createSeatContainer
        betChipConfig,
        insuranceChipConfig,
        scoreboxConfig,
        outcomeLabelConfig,
        chipBorderConfig,
        chipCounterConfig,
        kittyConfig,
        cardConfig,
        showBetChips,
        showInsuranceChips,
        usernameY,
        scene.outcomeLabelLayer,
        scene.scoreboxLayer,
        scene.highlightLayer,
        scene.avatarLayer
      );

      scene.seatsLayer?.addChild(seatContainer);
      scene.seatContainers.set(actualSeat, seatContainer);
    }

    // Dealer button - SMOOTH ANIMATION approach
    // Instead of destroying/recreating, we track pending position and animate during clearTable
    const buttonConfig = dealerButtonConfig || DEFAULT_DEALER_BUTTON_CONFIG;

    // Helper to calculate button position for a given visual seat
    // IMPORTANT: Must apply seat config offsets (x, y, rot) to match where seat is actually rendered
    const calcButtonPosition = (visualSeat: number) => {
      const seatPos = dynamicSeatPositions[visualSeat - 1];
      const seatConfig = seatConfigs[visualSeat] || DEFAULT_SEAT_CONFIG;
      // Apply seat config offsets to get actual seat position
      const adjustedX = seatPos.x + seatConfig.x;
      const adjustedY = seatPos.y + seatConfig.y;
      const seatRotation = seatPos.rotation + seatConfig.rot;
      const seatWidth = Math.round(144 * (seatSizeScale / 100) * (seatConfig.scale / 100));
      const seatHeight = Math.round(168 * (seatSizeScale / 100) * (seatConfig.scale / 100));
      const localOffsetX = seatWidth / 2 + buttonConfig.x;
      const localOffsetY = -seatHeight / 2 + buttonConfig.y;
      const rotationRad = degreesToRadians(seatRotation);
      const rotatedOffsetX = localOffsetX * Math.cos(rotationRad) - localOffsetY * Math.sin(rotationRad);
      const rotatedOffsetY = localOffsetX * Math.sin(rotationRad) + localOffsetY * Math.cos(rotationRad);
      return {
        x: adjustedX + rotatedOffsetX,
        y: adjustedY + rotatedOffsetY,
        rotation: rotationRad,
      };
    };

    // Check if button container exists and is valid (not destroyed)
    const buttonExists = scene.dealerButtonContainer && !scene.dealerButtonContainer.destroyed;

    // Sync module state with actual scene state (handles component remount)
    if (!buttonExists) {
      MODULE_DEALER_BUTTON_CURRENT_SEAT = 0;
    }

    // Case 1: Need to show button but doesn't exist - CREATE it
    if (!buttonExists && dealerButtonSeat >= 1 && dealerButtonSeat <= 7) {
      // Clean up any stale reference
      if (scene.dealerButtonContainer && scene.dealerButtonContainer.destroyed) {
        scene.dealerButtonContainer = null;
      }

      const buttonContainer = createDealerButton(buttonConfig);
      const pos = calcButtonPosition(dealerButtonSeat);
      buttonContainer.x = pos.x;
      buttonContainer.y = pos.y;
      buttonContainer.rotation = pos.rotation;
      scene.avatarLayer?.addChild(buttonContainer);
      scene.dealerButtonContainer = buttonContainer;
      MODULE_DEALER_BUTTON_CURRENT_SEAT = dealerButtonSeat;
      MODULE_DEALER_BUTTON_PENDING_SEAT = null;
      console.log(`[DealerButton] Created at visual seat ${dealerButtonSeat}`);
    }
    // Case 2: Button exists but should be hidden - DESTROY it
    else if (buttonExists && dealerButtonSeat === 0) {
      scene.avatarLayer?.removeChild(scene.dealerButtonContainer!);
      scene.dealerButtonContainer!.destroy({ children: true });
      scene.dealerButtonContainer = null;
      MODULE_DEALER_BUTTON_CURRENT_SEAT = 0;
      MODULE_DEALER_BUTTON_PENDING_SEAT = null;
      console.log(`[DealerButton] Hidden (destroyed)`);
    }
    // Case 3: Button exists and seat changed - SET PENDING for mid-rake animation
    else if (buttonExists && dealerButtonSeat >= 1 && dealerButtonSeat !== MODULE_DEALER_BUTTON_CURRENT_SEAT) {
      MODULE_DEALER_BUTTON_PENDING_SEAT = dealerButtonSeat;
      console.log(`[DealerButton] Pending move: ${MODULE_DEALER_BUTTON_CURRENT_SEAT} → ${dealerButtonSeat}`);
    }
    // Case 4: No button needed and none exists - ensure state is clean
    else if (!buttonExists && dealerButtonSeat === 0) {
      MODULE_DEALER_BUTTON_CURRENT_SEAT = 0;
      MODULE_DEALER_BUTTON_PENDING_SEAT = null;
    }
  }, [loading, dealer, dealerPersona, players, mySeatPosition, currentTurnSeat, currentHandIndex, getVisualPositionForSeat, onSeatClick, onStraightenCards, dynamicSeatPositions, seatSizeScale, seatConfigs, dealerScoreboxOffset, betChipConfigs, insuranceChipConfigs, effectiveSeatRadiusScale, scoreboxConfig, outcomeLabelConfig, dealerButtonSeat, dealerButtonConfig, animationCardCount, showBetChips, showInsuranceChips, avatarTexturesVersion]);

  // ===========================================
  // UI ELEMENTS UPDATE (Staged Bet + Chip Rail)
  // ===========================================
  // Store update function ref for staged bet frame
  const stagedBetUpdateRef = useRef<((amount: number, displayed: number) => void) | null>(null);
  // Track previous staged chips for consolidation detection
  const prevStagedChipsRef = useRef<number[]>([]);
  // Ref for swap timeout
  const swapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to store getRailChipPosition function for use by imperative handle
  const getRailChipPositionRef = useRef<((denom: number) => { x: number; y: number }) | null>(null);

  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    const textures = texturesRef.current;

    // Only show betting UI when player is seated
    const showBettingUI = mySeatPosition !== null;

    // Calculate common positions
    const railScale = (chipRailConfig?.scale ?? 100) / 100;
    const railX = UI_CHIP_RAIL_POS.x + (chipRailConfig?.x ?? 0);
    const railY = UI_CHIP_RAIL_POS.y + (chipRailConfig?.y ?? 0);
    const spacing = chipRailConfig?.spacing ?? 85;
    const totalWidth = (CHIP_RAIL_DENOMS.length - 1) * spacing;
    const startX = -totalWidth / 2;
    const arcHeight = 25;

    const stagedBetX = UI_STAGED_BET_POS.x + (stagedBetConfig?.x ?? 0);
    const stagedBetY = UI_STAGED_BET_POS.y + (stagedBetConfig?.y ?? 0);
    const stagedBetScale = (stagedBetConfig?.scale ?? 100) / 100;
    const stagedBetWidth = stagedBetConfig?.width ?? DEFAULT_STAGED_BET_CONFIG.width;
    const stagedBetBorderWidth = stagedBetConfig?.borderThickness ?? DEFAULT_STAGED_BET_CONFIG.borderThickness;

    // Calculate global positions for each rail chip denomination
    const getRailChipPosition = (denom: number): { x: number; y: number } => {
      const index = CHIP_RAIL_DENOMS.indexOf(denom);
      if (index === -1) return { x: railX, y: railY };
      const baseX = startX + index * spacing;
      const normalizedPos = (index - (CHIP_RAIL_DENOMS.length - 1) / 2) / ((CHIP_RAIL_DENOMS.length - 1) / 2);
      const baseY = arcHeight * (normalizedPos * normalizedPos - 1);
      // Apply per-chip offsets
      const localX = baseX + (chipXOffsets[denom] ?? 0);
      const localY = baseY + (chipYOffsets[denom] ?? 0);
      return {
        x: railX + localX * railScale,
        y: railY + localY * railScale,
      };
    };
    // Store in ref for use by imperative handle (animateRailToKitty)
    getRailChipPositionRef.current = getRailChipPosition;

    // ===========================================
    // STAGED BET FRAME (create/recreate when config changes)
    // ===========================================
    // Debug logging removed - was causing excessive console output

    // Always remove existing frame first so config changes are applied
    if (scene.stagedBetFrame) {
      scene.uiLayer?.removeChild(scene.stagedBetFrame);
      // Destroy container and all children to clean up event listeners
      scene.stagedBetFrame.destroy({ children: true });
      scene.stagedBetFrame = null;
      stagedBetUpdateRef.current = null;
    }

    if (showBettingUI) {
      // CLEAR handler using persistent chips
      const handleClearWithAnimation = () => {
        // Sort chips by z-index descending (top of stack first, so chips remove top to bottom)
        const chipsToAnimate = [...scene.stagedChipSprites].sort((a, b) => b.sprite.zIndex - a.sprite.zIndex);
        if (chipsToAnimate.length === 0) {
          onClearBet?.();
          return;
        }
        const denomCounts = chipsToAnimate.reduce((acc, c) => { acc[c.denomination] = (acc[c.denomination] || 0) + 1; return acc; }, {} as Record<number, number>);
        console.log(`[CHIP AUDIT] Staged→Rail (CLEAR) | amount=${stagedAmount} | animated=${chipsToAnimate.length} sprites | denoms=${JSON.stringify(denomCounts)}`);

        let animationsComplete = 0;
        const totalChips = chipsToAnimate.length;

        // Get timing values from config store (client-only animations)
        const clearStagger = getClientAnimationTiming('clearChipStagger', 'handleClearWithAnimation');
        const stagedToRailDuration = getClientAnimationTiming('stagedToRail', 'handleClearWithAnimation');

        chipsToAnimate.forEach((persistentChip, chipIndex) => {
          const { sprite, denomination } = persistentChip;
          const railPos = getRailChipPosition(denomination);

          // Mark as animating
          persistentChip.state = 'animating';
          sprite.zIndex = 1000; // Above other chips during animation

          // Notify animation start
          setTimeout(() => {
            onChipAnimationStart?.(denomination, 'out');
          }, chipIndex * clearStagger);

          // Calculate end scale (staged → rail size)
          const startSize = CHIP_SIZES.staged * stagedBetScale;
          const targetSize = CHIP_SIZES.rail * railScale;
          const scaleRatio = targetSize / startSize;
          const endScaleX = sprite.scale.x * scaleRatio;
          const endScaleY = sprite.scale.y * scaleRatio;

          // Random end rotation (0-359 degrees)
          const endRotation = Math.random() * 2 * Math.PI;

          // Staggered animation
          setTimeout(() => {
            // When this chip departs, randomize rotation of the next chip in the SAME stack
            // (same denomination, next lower zIndex)
            const nextChipInStack = chipsToAnimate.find((c, idx) =>
              idx > chipIndex && c.denomination === denomination
            );
            if (nextChipInStack) {
              nextChipInStack.sprite.rotation = Math.random() * 2 * Math.PI;
            }

            animateSprite(sprite, {
              x: railPos.x,
              y: railPos.y,
              scaleX: endScaleX,
              scaleY: endScaleY,
              rotation: endRotation,
            }, {
              duration: stagedToRailDuration,
              onComplete: () => {
                // Sound: chipLand on clear bet chip arrival at rail
                playTableSound('chipLand');
                // Return chip to pool
                returnChipToPool(persistentChip);
                onChipAnimationEnd?.(denomination, 'out');
                animationsComplete++;

                if (animationsComplete === totalChips) {
                  // Clear the staged chips array
                  scene.stagedChipSprites = [];
                  onClearBet?.();
                }
              },
            });
          }, chipIndex * clearStagger);
        });
      };

      // Create the frame
      const frame = createStagedBetFrame(stagedAmount, handleClearWithAnimation, stagedBetConfig);
      frame.container.scale.set(stagedBetScale);
      frame.container.x = stagedBetX;
      frame.container.y = stagedBetY;
      frame.container.zIndex = -10; // Below persistent chips

      // Add to uiLayer
      if (scene.uiLayer) {
        scene.uiLayer.addChild(frame.container);
      }
      scene.stagedBetFrame = frame.container;
      stagedBetUpdateRef.current = frame.updateAmount;

      // Initial update
      const counterAmount = displayedAmount ?? stagedAmount;
      frame.updateAmount(stagedAmount, counterAmount);
    }
    // Note: Frame removal when !showBettingUI is handled at the start of this effect

    // Update amount display (reactive)
    if (stagedBetUpdateRef.current) {
      const counterAmount = displayedAmount ?? stagedAmount;
      stagedBetUpdateRef.current(stagedAmount, counterAmount);
    }

    // ===========================================
    // SYNC PERSISTENT CHIPS WITH stagedChips ARRAY
    // ===========================================
    // This handles consolidation (chip swaps) and external state changes
    if (showBettingUI) {
      // Check if fullVisualResync flagged a forced chip recreation
      if (scene.forceChipResync) {
        console.log('[Staged Chips Sync] forceChipResync flag detected, resetting prevStagedChipsRef');
        prevStagedChipsRef.current = [];
        scene.forceChipResync = false;
      }

      const prevChips = prevStagedChipsRef.current;
      const chipsChanged = stagedChips.length !== prevChips.length ||
        !stagedChips.every((d, i) => d === prevChips[i]);

      if (chipsChanged) {
        // Compare scene chips with new state to determine what changed
        // Include both 'staged' AND 'animating' chips to avoid race conditions
        const sceneChips = scene.stagedChipSprites
          .filter(c => c.state === 'staged' || c.state === 'animating')
          .map(c => c.denomination);

        // Calculate diff between scene and new state
        const { removed, added } = chipDiff(sceneChips, stagedChips);

        // Handle removed chips (consolidation: small chips → animate out)
        if (removed.length > 0) {
          // Get timing values from config store (client-only animations)
          const swapOutDuration = getClientAnimationTiming('chipSwapOut', 'stagedChipsSync-removed');
          const swapStagger = getStaggerValue('chipSwap', undefined, {
            fileName: 'PixiGameTableV1.tsx',
            functionName: 'stagedChipsSync-removed',
            eventType: 'CLIENT_UI',
          });

          const denomRemoveCounts = new Map<number, number>();

          removed.forEach((denom, idx) => {
            // Find a chip of this denomination in the scene
            const removeIdx = denomRemoveCounts.get(denom) || 0;
            denomRemoveCounts.set(denom, removeIdx + 1);

            // Include both 'staged' and 'animating' chips in removal search
            const isRemovableState = (state: string) => state === 'staged' || state === 'animating';
            const chipToRemove = scene.stagedChipSprites.find(
              (c, i) => c.denomination === denom && isRemovableState(c.state) &&
                scene.stagedChipSprites.slice(0, i).filter(x => x.denomination === denom && isRemovableState(x.state)).length === removeIdx
            );

            if (chipToRemove) {
              chipToRemove.state = 'animating';
              const { sprite } = chipToRemove;
              sprite.zIndex = 1000; // Above other chips during animation

              const railPos = getRailChipPosition(denom);
              const startSize = CHIP_SIZES.staged * stagedBetScale;
              const targetSize = CHIP_SIZES.rail * railScale;
              const scaleRatio = targetSize / startSize;
              const endScaleX = sprite.scale.x * scaleRatio;
              const endScaleY = sprite.scale.y * scaleRatio;

              // Staggered animation
              setTimeout(() => {
                animateSprite(sprite, {
                  x: railPos.x,
                  y: railPos.y,
                  scaleX: endScaleX,
                  scaleY: endScaleY,
                }, {
                  duration: swapOutDuration,
                  onComplete: () => {
                    // Sound: chipLand on consolidation swap-out arrival at rail
                    playTableSound('chipLand');
                    returnChipToPool(chipToRemove);
                    // Remove from scene array
                    const removeFromScene = scene.stagedChipSprites.indexOf(chipToRemove);
                    if (removeFromScene !== -1) {
                      scene.stagedChipSprites.splice(removeFromScene, 1);
                    }
                  },
                });
              }, idx * swapStagger);
            }
          });
        }

        // Handle state cleared (scene has chips, state is empty) - clear immediately without animation
        if (stagedChips.length === 0 && scene.stagedChipSprites.length > 0) {
          scene.stagedChipSprites.forEach((chip) => {
            returnChipToPool(chip);
          });
          scene.stagedChipSprites = [];
        }

        // Handle initial load (no scene chips, state has chips) - MUST CHECK BEFORE "added" block
        // This ensures chips appear instantly on page refresh/join, not with animation
        if (scene.stagedChipSprites.length === 0 && stagedChips.length > 0 && removed.length === 0) {
          // Create chips instantly (no animation) for page refresh or after resync
          console.log(`[Staged Chips Sync] Instant creation: ${stagedChips.length} chips (no scene chips, state has chips)`);
          const denomCounts = new Map<number, number>();

          stagedChips.forEach((denom) => {
            const persistentChip = getChipFromPool(scene.chipPool, denom, textures, scene.uiLayer);
            if (!persistentChip) return;

            const stackIndex = denomCounts.get(denom) || 0;
            denomCounts.set(denom, stackIndex + 1);

            const totalOfDenom = stagedChips.filter(d => d === denom).length;
            const stagedDenoms = [...new Set(stagedChips)];

            const pos = calculateStagedChipPosition(
              denom,
              stackIndex,
              totalOfDenom,
              stagedBetX,
              stagedBetY,
              stagedBetScale,
              stagedDenoms,
              stagedBetWidth,
              stagedBetBorderWidth
            );

            const { sprite } = persistentChip;
            sprite.visible = true;
            sprite.x = pos.x;
            sprite.y = pos.y;
            sprite.width = CHIP_SIZES.staged * stagedBetScale;
            sprite.height = CHIP_SIZES.staged * stagedBetScale;
            sprite.zIndex = pos.zIndex;
            persistentChip.state = 'staged';

            scene.stagedChipSprites.push(persistentChip);
          });
          console.log(`[Staged Chips Sync] Created ${scene.stagedChipSprites.length} staged chip sprites`);
        }
        // Handle added chips (consolidation: large chips animate in)
        // Only animate if this is NOT an initial load (scene already has chips or chips were removed)
        else if (added.length > 0) {
          // Get timing values from config store (client-only animations)
          const swapInDelay = getClientAnimationTiming('chipSwapInDelay', 'stagedChipsSync-added');
          const swapInDuration = getClientAnimationTiming('chipSwapIn', 'stagedChipsSync-added');
          const swapStagger = getStaggerValue('chipSwap', undefined, {
            fileName: 'PixiGameTableV1.tsx',
            functionName: 'stagedChipsSync-added',
            eventType: 'CLIENT_UI',
          });

          const denomAddCounts = new Map<number, number>();

          // Delay added chips until removed chips start leaving
          const addDelay = removed.length > 0 ? swapInDelay : 0;

          added.forEach((denom, idx) => {
            const stackIndex = denomAddCounts.get(denom) || 0;
            denomAddCounts.set(denom, stackIndex + 1);

            setTimeout(() => {
              // Guard: check if chip already exists in scene to prevent doubling
              const existingCount = scene.stagedChipSprites.filter(c => c.denomination === denom).length;
              const targetCount = stagedChips.filter(d => d === denom).length;
              if (existingCount >= targetCount) {
                return; // Already have enough chips of this denomination
              }

              const persistentChip = getChipFromPool(scene.chipPool, denom, textures, scene.uiLayer);
              if (!persistentChip) return;

              const { sprite } = persistentChip;
              const railPos = getRailChipPosition(denom);

              // Start at rail position
              sprite.visible = true;
              sprite.x = railPos.x;
              sprite.y = railPos.y;
              sprite.width = CHIP_SIZES.rail * railScale;
              sprite.height = CHIP_SIZES.rail * railScale;
              sprite.zIndex = 1000;
              persistentChip.state = 'animating';

              // Add to stagedChipSprites immediately (before animation completes)
              // This prevents race conditions with subsequent consolidation syncs
              scene.stagedChipSprites.push(persistentChip);

              // Calculate target position
              const totalOfDenom = stagedChips.filter(d => d === denom).length;
              const stagedDenoms = [...new Set(stagedChips)];
              const targetPos = calculateStagedChipPosition(
                denom,
                stackIndex,
                totalOfDenom,
                stagedBetX,
                stagedBetY,
                stagedBetScale,
                stagedDenoms,
                stagedBetWidth,
                stagedBetBorderWidth
              );

              // Calculate end scale
              const startSize = CHIP_SIZES.rail * railScale;
              const targetSize = CHIP_SIZES.staged * stagedBetScale;
              const scaleRatio = targetSize / startSize;
              const endScaleX = sprite.scale.x * scaleRatio;
              const endScaleY = sprite.scale.y * scaleRatio;

              animateSprite(sprite, {
                x: targetPos.x,
                y: targetPos.y,
                scaleX: endScaleX,
                scaleY: endScaleY,
              }, {
                duration: swapInDuration,
                onComplete: () => {
                  // Sound: chipLand on consolidation swap-in arrival at staged box
                  playTableSound('chipLand');
                  // Chip is now staged (already in stagedChipSprites from earlier)
                  persistentChip.state = 'staged';
                  sprite.zIndex = targetPos.zIndex;
                },
              });
            }, addDelay + idx * swapStagger);
          });
        }

        // Update ref
        prevStagedChipsRef.current = [...stagedChips];
      }

      // Update positions of existing staged chips (in case layout changed)
      const denomCounts = new Map<number, number>();
      scene.stagedChipSprites.forEach((persistentChip) => {
        if (persistentChip.state !== 'staged') return;

        const { sprite, denomination } = persistentChip;
        const stackIndex = denomCounts.get(denomination) || 0;
        denomCounts.set(denomination, stackIndex + 1);

        // Include 'animating' chips in position calculations for visual consistency
        const isInStaged = (state: string) => state === 'staged' || state === 'animating';
        const totalOfDenom = scene.stagedChipSprites.filter(c => c.denomination === denomination && isInStaged(c.state)).length;
        const stagedDenoms = [...new Set(scene.stagedChipSprites.filter(c => isInStaged(c.state)).map(c => c.denomination))];

        const pos = calculateStagedChipPosition(
          denomination,
          stackIndex,
          totalOfDenom,
          stagedBetX,
          stagedBetY,
          stagedBetScale,
          stagedDenoms,
          stagedBetWidth,
          stagedBetBorderWidth
        );

        sprite.x = pos.x;
        sprite.y = pos.y;
        sprite.zIndex = pos.zIndex;
      });
    }

    // ===========================================
    // CHIP RAIL
    // ===========================================
    if (scene.chipRailContainer) {
      scene.uiLayer?.removeChild(scene.chipRailContainer);
      // Destroy container and all children to clean up event listeners
      scene.chipRailContainer.destroy({ children: true });
      scene.chipRailContainer = null;
      scene.chipRailChips.clear();
    }

    if (showBettingUI) {
      const chipRail = new Container();
      const scaleFactor = (chipRailConfig?.scale ?? 100) / 100;
      chipRail.scale.set(scaleFactor);
      chipRail.x = UI_CHIP_RAIL_POS.x + (chipRailConfig?.x ?? 0);
      chipRail.y = UI_CHIP_RAIL_POS.y + (chipRailConfig?.y ?? 0);
      chipRail.zIndex = 100; // Below animated chips (zIndex 1000+) so departing chips appear in front

      CHIP_RAIL_DENOMS.forEach((denom, index) => {
        const baseX = startX + index * spacing;
        const normalizedPos = (index - (CHIP_RAIL_DENOMS.length - 1) / 2) / ((CHIP_RAIL_DENOMS.length - 1) / 2);
        const baseY = arcHeight * (normalizedPos * normalizedPos - 1);
        // Apply per-chip offsets from DEV CONTROLS
        const chipOffsetX = chipXOffsets[denom] ?? 0;
        const chipOffsetY = chipYOffsets[denom] ?? 0;
        const x = baseX + chipOffsetX;
        const y = baseY + chipOffsetY;

        const canAfford = balance >= denom + stagedAmount;
        // Get or generate persisted rotation for this denomination
        let chipRotation = MODULE_RAIL_CHIP_ROTATIONS.get(denom);
        if (chipRotation === undefined) {
          chipRotation = Math.random() * 2 * Math.PI;
          MODULE_RAIL_CHIP_ROTATIONS.set(denom, chipRotation);
        }
        const chip = createRailChip(denom, textures, canAfford, () => {
          if (canAfford && onChipClick) {
            console.log(`[CHIP AUDIT] Rail→Staged | denom=${denom} | animated=1 sprite`);
            const chipGlobalX = railX + x * railScale;
            const chipGlobalY = railY + y * railScale;

            // Get a persistent chip from the pool
            const persistentChip = getChipFromPool(scene.chipPool, denom, textures, scene.uiLayer);
            if (!persistentChip) {
              // Pool exhausted - fall back to immediate state update
              onChipClick(denom);
              return;
            }

            const { sprite } = persistentChip;

            // Notify animation start
            onChipAnimationStart?.(denom, 'in');

            // Departing chip copies current rail chip rotation (read at click time, not closure time)
            const currentRotation = MODULE_RAIL_CHIP_ROTATIONS.get(denom) ?? 0;
            sprite.rotation = currentRotation;
            // When chip departs from rail, the revealed chip underneath gets new rotation
            const newRotation = Math.random() * 2 * Math.PI;
            MODULE_RAIL_CHIP_ROTATIONS.set(denom, newRotation);
            // Also update the visible rail chip sprite immediately
            const railChipContainer = scene.chipRailChips.get(denom);
            if (railChipContainer) {
              // Find the chip sprite (first Sprite child)
              for (const child of railChipContainer.children) {
                if (child instanceof Sprite) {
                  child.rotation = newRotation;
                  break;
                }
              }
            }

            // Position at rail chip location
            sprite.visible = true;
            sprite.x = chipGlobalX;
            sprite.y = chipGlobalY;
            sprite.width = CHIP_SIZES.rail * railScale;
            sprite.height = CHIP_SIZES.rail * railScale;
            sprite.zIndex = 2000; // Above rail chips (zIndex 100) so departing chip appears on top
            persistentChip.state = 'animating';

            // Add to stagedChipSprites immediately (before animation completes)
            // This prevents race conditions with consolidation sync
            scene.stagedChipSprites.push(persistentChip);

            // Calculate target position in staged area
            // Include both 'staged' and 'animating' chips to handle rapid clicks
            const isInStaged = (state: string) => state === 'staged' || state === 'animating';
            // Count existing chips (excluding the one we just added - it's the last in the array)
            const existingChips = scene.stagedChipSprites.slice(0, -1);
            const currentStagedOfDenom = existingChips.filter(c => c.denomination === denom && isInStaged(c.state)).length;
            const stagedDenoms = [...new Set([...existingChips.filter(c => isInStaged(c.state)).map(c => c.denomination), denom])];

            const targetPos = calculateStagedChipPosition(
              denom,
              currentStagedOfDenom, // This will be the new chip's index
              currentStagedOfDenom + 1,
              stagedBetX,
              stagedBetY,
              stagedBetScale,
              stagedDenoms,
              stagedBetWidth,
              stagedBetBorderWidth
            );

            // Calculate end scale (rail → staged size)
            const startSize = CHIP_SIZES.rail * railScale;
            const targetSize = CHIP_SIZES.staged * stagedBetScale;
            const scaleRatio = targetSize / startSize;
            const endScaleX = sprite.scale.x * scaleRatio;
            const endScaleY = sprite.scale.y * scaleRatio;

            // Random end rotation (0-359 degrees)
            const endRotation = Math.random() * 2 * Math.PI;

            // Animate to staged area
            const railToStagedDuration = getClientAnimationTiming('railToStaged', 'chipRail click handler');
            animateSprite(sprite, {
              x: targetPos.x,
              y: targetPos.y,
              scaleX: endScaleX,
              scaleY: endScaleY,
              rotation: endRotation,
            }, {
              duration: railToStagedDuration,
              onComplete: () => {
                // Chip is now staged (already in stagedChipSprites from earlier)
                // Sound: chip lands/arrives in the BUILD YOUR BET box
                playTableSound('chipLand');
                persistentChip.state = 'staged';
                sprite.zIndex = targetPos.zIndex; // Use calculated z-index for proper layering
                onChipAnimationEnd?.(denom, 'in');
              },
            });

            // Update state immediately
            onChipClick(denom);
          }
        }, chipLabelYOffsets[denom] ?? 0, chipLabelPillOpacity, chipRotation);
        chip.x = x;
        chip.y = y;

        chipRail.addChild(chip);
        scene.chipRailChips.set(denom, chip);
      });

      scene.uiLayer?.addChild(chipRail);
      scene.chipRailContainer = chipRail;
    }
  // NOTE: 'players' was removed from this dependency array - it was never used in the effect body
  // and caused excessive destruction/recreation of the staged bet frame on every game state update.
  // This was identified as a potential cause of WebGL context loss (green screen) on multi-client setups.
  }, [loading, mySeatPosition, stagedAmount, stagedChips, displayedAmount, balance, onChipClick, onClearBet, chipRailConfig, stagedBetConfig, onChipAnimationStart, onChipAnimationEnd, chipXOffsets, chipYOffsets, chipLabelYOffsets, chipLabelPillOpacity]);

  // Update chip borders when chipBorderConfig changes
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;
    const { chipSize } = KITTY_CONFIG;

    // Update all existing chip borders with new config
    scene.kittyChipSprites.forEach((kittyChipMap) => {
      kittyChipMap.forEach((data) => {
        if (data.border) {
          const borderColor = parseInt(chipBorderConfig.color.replace('#', ''), 16);
          data.border.clear();
          data.border.circle(0, 0, chipSize / 2);
          data.border.stroke({
            color: borderColor,
            width: chipBorderConfig.thickness,
            alpha: chipBorderConfig.opacity / 100,
          });
        }
      });
    });
  }, [loading, chipBorderConfig]);

  // Update kitty container positions when kittyConfig changes
  // NOTE: This only repositions existing kitty containers when DEV controls change kittyConfig.
  // It does NOT affect initial kitty creation (which happens in animateRailToKitty).
  // Effective opponent kitty config - falls back to kittyConfig if not provided
  const effectiveOpponentKittyConfig = opponentKittyConfig ?? kittyConfig;
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;

    console.log('[Kitty Config] useEffect triggered, kittyContainers.size:', scene.kittyContainers.size);
    console.log('[Kitty Config] BEFORE repositioning:');
    scene.kittyContainers.forEach((c, k) => console.log(`  key=${k}: x=${c.x.toFixed(0)}, y=${c.y.toFixed(0)}`));

    // Update all existing kitty containers with new positions and rotation
    scene.kittyContainers.forEach((container, visualPos) => {
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) return;

      // Get seatPosition from container (stored when kitty was created)
      // betChipConfigs and opponentKittyConfigs are keyed by seat position, not visual position
      const seatPosition = (container as unknown as { seatPosition?: number })?.seatPosition ?? visualPos;

      // Use opponent config for opponent kitties
      const isOpponentKitty = (container as unknown as { isOpponentKitty?: boolean })?.isOpponentKitty ?? false;
      const effectiveKittyConfig = isOpponentKitty ? effectiveOpponentKittyConfig : kittyConfig;

      // Get per-seat opponent kitty offset (only for opponent kitties) - use VISUAL position for DEV CONTROLS alignment
      const perSeatOpponentOffset = isOpponentKitty
        ? (opponentKittyConfigs?.[visualPos] ?? { radiusOffset: 0, x: 0, y: 0, scale: 100 })
        : { radiusOffset: 0, x: 0, y: 0, scale: 100 };

      // Use seatPosition for betChipConfigs lookup (UI keys by seat number, not visual position)
      const betConfig = betChipConfigs[seatPosition] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
      const baseLocalOffsetY = KITTY_BASE_RADIAL_OFFSET + betConfig.radiusOffset + perSeatOpponentOffset.radiusOffset;
      const rotRad = degreesToRadians(seatData.rotation);
      const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);

      // Recalculate position with updated kittyConfig + per-seat opponent offset
      container.x = basePos.x + betConfig.x + effectiveKittyConfig.x + perSeatOpponentOffset.x;
      container.y = basePos.y + betConfig.y + effectiveKittyConfig.y + perSeatOpponentOffset.y;
      console.log(`[Kitty Config] AFTER key=${visualPos} seat=${seatPosition}: x=${container.x.toFixed(0)}, y=${container.y.toFixed(0)}`);

      // Main container rotates by seat rotation only (so counter stays oriented to seat)
      container.rotation = rotRad;

      // Apply extra rotation to chipsWrapper only (chips rotate, counter stays in place)
      const chipsWrapper = scene.kittyChipsWrappers.get(visualPos);
      if (chipsWrapper) {
        chipsWrapper.rotation = degreesToRadians(effectiveKittyConfig.rotation || 0);
      }
    });
  }, [loading, kittyConfig, opponentKittyConfig, opponentKittyConfigs, effectiveOpponentKittyConfig, betChipConfigs, dynamicSeatPositions]);

  // Update chip counter positions when chipCounterConfig changes
  useEffect(() => {
    if (loading || !appRef.current) return;

    const scene = sceneRef.current;

    // Update all existing kitty counters with new offset
    scene.kittyPills.forEach((pill, visualPos) => {
      const counterText = scene.kittyCounterTexts.get(visualPos);
      const kittyChipSprites = scene.kittyChipSprites.get(visualPos);
      const kittyData = scene.kittyData.get(visualPos);
      const kittyContainer = scene.kittyContainers.get(visualPos);

      // Check if this is an opponent kitty (they don't show counters)
      const isOpponentKitty = (kittyContainer as unknown as { isOpponentKitty?: boolean })?.isOpponentKitty ?? false;

      if (pill && counterText && kittyChipSprites && kittyData) {
        const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
        updateKittyLayout(pill, counterText, kittyChipSprites.size, kittyData.total, counterOffset, isOpponentKitty);
      }
    });
  }, [loading, chipCounterConfig]);

  // Expose imperative methods (Promise-returning for async animation control)
  useImperativeHandle(ref, () => ({
    dealCardToSeat: (seatPosition: number, card: CardType, cardIndex: number, handIndex: number = 0, duration?: number): Promise<void> => {
      return new Promise((resolve) => {
        // Debug logging
        const dbg = (msg: string) => {
          console.log(msg);
          const w = window as unknown as { pixiDebugLog?: (m: string) => void };
          w.pixiDebugLog?.(msg);
        };

        dbg(`[DEAL] seat=${seatPosition} card=${card.rank}${card.suit} idx=${cardIndex} hand=${handIndex}`);

        // === DIAGNOSTIC: Log scene state at card deal start (first card only to reduce noise) ===
        if (cardIndex === 0 && handIndex === 0) {
          logSceneDiagnostic(sceneRef.current, `dealCardToSeat(seat=${seatPosition}, card=${card.rank}${card.suit}, idx=${cardIndex})`);
        }

        // Clear rake guard when first card is dealt to this seat (new round starting)
        if (cardIndex === 0 && handIndex === 0 && MODULE_RAKED_SEATS_THIS_ROUND.has(seatPosition)) {
          MODULE_RAKED_SEATS_THIS_ROUND.delete(seatPosition);
          dbg(`[RAKE GUARD] Cleared rake flag for seat ${seatPosition} - new round starting`);
        }

        // DIAGNOSTIC: Log current dealt card count state
        const preDealtKey = `${seatPosition}-${handIndex}`;
        const preDealtCount = MODULE_DEALT_CARD_COUNTS.get(preDealtKey) ?? 0;
        dbg(`[DEAL DIAG] PRE: dealtKey=${preDealtKey} count=${preDealtCount} (cardIndex=${cardIndex})`);
        dbg(`[DEAL DIAG] MODULE_DEALT_CARD_COUNTS state: ${JSON.stringify(Array.from(MODULE_DEALT_CARD_COUNTS.entries()))}`);

        if (!appRef.current || loadingRef.current) {
          dbg(`[DEAL] SKIPPED - app not ready or loading`);
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);
        const pos = dynamicSeatPositions[visualPos - 1];
        const baseCardConfig = cardConfigRef.current;

        // DIAGNOSTIC: Log kitty container state when dealing to split hands
        const splitState = scene.splitHandState.get(seatPosition);
        if (splitState && splitState.totalHands > 1) {
          dbg(`[DEAL KITTY DIAG] Split hand card deal - checking all kitty containers:`);

          // Main kitty
          const mainKitty = scene.kittyContainers.get(visualPos);
          dbg(`[DEAL KITTY DIAG] Main kitty (visualPos=${visualPos}): exists=${!!mainKitty}, destroyed=${mainKitty?.destroyed}, visible=${mainKitty?.visible}, pos=(${mainKitty?.x?.toFixed(0)}, ${mainKitty?.y?.toFixed(0)})`);

          // Split kitties
          for (let h = 1; h < splitState.totalHands; h++) {
            const kittyKey = `${seatPosition}-${h}`;
            const splitKitty = scene.splitKittyContainers.get(kittyKey);
            const kittyData = scene.splitKittyData.get(kittyKey);
            dbg(`[DEAL KITTY DIAG] Split kitty ${kittyKey}: exists=${!!splitKitty}, destroyed=${splitKitty?.destroyed}, visible=${splitKitty?.visible}, pos=(${splitKitty?.x?.toFixed(0)}, ${splitKitty?.y?.toFixed(0)}), chips=${kittyData?.denominations?.length ?? 0}`);
          }
        }

        // === FILMSTRIP ARCHITECTURE ===
        // Get or create the filmstrip container for this seat.
        // Cards will be added to the filmstrip at LOCAL coordinates.
        // The filmstrip slides to show the currently viewed hand.
        const seatRotationRad = degreesToRadians(pos.rotation);
        // Calculate seat dimensions for mask (visualPos already declared above)
        const seatConfig = seatConfigs[visualPos] || { x: 0, y: 0, rot: 0, scale: 100 };
        const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
        const seatWidth = Math.round(144 * seatScale);
        const seatHeight = Math.round(168 * seatScale);
        const filmstrip = scene.cardsLayer
          ? getOrCreateFilmstrip(scene, seatPosition, pos.x, pos.y, seatRotationRad, scene.cardsLayer, seatWidth, seatHeight)
          : null;
        if (filmstrip) {
          dbg(`[DEAL] Using filmstrip for seat ${seatPosition}`);
        }

        // Apply 10% size reduction for opponent cards
        // Use store value as fallback when prop is stale (spectator joining seat)
        const effectiveMySeat = mySeatPosition ?? useGameStateStore.getState().mySeatPosition;
        const isOpponent = seatPosition !== effectiveMySeat;
        const currentCardConfig = isOpponent
          ? applyOpponentCardScale(baseCardConfig)
          : baseCardConfig;

        // Safety: ensure straightening is enabled when dealing starts
        if (scene.rakeInProgress) {
          dbg(`[DEAL] Clearing rakeInProgress flag (was still true)`);
          scene.rakeInProgress = false;
        }

        // === SHOE CARD SYSTEM ===
        // Take the top shoe card sprite and use it for the deal animation (face-down)
        // Then create a replacement shoe card with new random rotation
        const shoeCardsX = shoeCardsConfig?.x ?? 0;
        const shoeCardsY = shoeCardsConfig?.y ?? 0;
        const shoeCardsRot = shoeCardsConfig?.rotation ?? 0;
        const shoeCardsScale = (shoeCardsConfig?.scale ?? 100) / 100;

        // Get top shoe card sprite (index 2 is the top card)
        const topShoeCard = scene.shoeCardSprites[2];
        if (!topShoeCard || topShoeCard.destroyed) {
          dbg(`[DEAL] WARNING: No top shoe card available, using fallback`);
          // Fallback to old behavior if shoe card not available
          const startX = SHOE_POSITION.x + DEAL_START_OFFSET.x + shoeCardsX;
          const startY = SHOE_POSITION.y + DEAL_START_OFFSET.y + shoeCardsY;
          const cardSprite = createCardSprite(card, textures, startX, startY, DEAL_START_ROTATION, currentCardConfig);
          cardSprite.scale.set(SHOE_CARD_SCALE);
          scene.animationLayer?.addChild(cardSprite);
          // Continue with animation...
        }

        // Capture the shoe card's current rotation (includes its random rotation)
        const shoeCardRotation = topShoeCard.rotation; // Already in radians

        // Remove top shoe card from tableLayer
        scene.tableLayer?.removeChild(topShoeCard);

        // Create a face-down card Container at the shoe card's position
        // We need a Container to match the structure expected by MODULE_CARD_STATES
        const cardBackTexture = textures.get('cardBack');
        const faceDownContainer = new Container();
        faceDownContainer.x = topShoeCard.x;
        faceDownContainer.y = topShoeCard.y;
        faceDownContainer.rotation = shoeCardRotation;
        faceDownContainer.scale.set(SHOE_CARD_SCALE);
        faceDownContainer.eventMode = 'none';

        if (cardBackTexture) {
          const scaleFactor = currentCardConfig.scale / 100;
          const cardWidth = CARD_WIDTH * scaleFactor;
          const cardHeight = CARD_HEIGHT * scaleFactor;

          // Card shadow for depth (matching face-up cards)
          const shadow = new Graphics();
          shadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, 8 * scaleFactor);
          shadow.fill({ color: 0x000000, alpha: 0.3 });
          faceDownContainer.addChild(shadow);

          // White background to fill any gaps at corners
          const bg = new Graphics();
          bg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
          bg.fill({ color: 0xfefefe });
          faceDownContainer.addChild(bg);

          // Rounded rectangle mask for card back (matching face-up card corners)
          const mask = new Graphics();
          mask.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
          mask.fill({ color: 0xffffff });
          faceDownContainer.addChild(mask);

          const backSprite = new Sprite(cardBackTexture);
          backSprite.width = cardWidth;
          backSprite.height = cardHeight;
          backSprite.anchor.set(0.5);
          backSprite.mask = mask;
          faceDownContainer.addChild(backSprite);

          // Border overlay to cover any sharp corners (matching face-up cards)
          const border = new Graphics();
          border.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
          border.stroke({ color: 0x374151, width: currentCardConfig.borderThickness });
          faceDownContainer.addChild(border);
        }

        // Add face-down card to animation layer
        // Set high z-index during animation so new cards always appear ON TOP of existing cards
        // Final z-index is set when card lands (in onComplete)
        faceDownContainer.zIndex = 5000 + cardIndex;
        scene.animationLayer?.addChild(faceDownContainer);

        // Destroy the old shoe card sprite (we've extracted what we need)
        topShoeCard.destroy();

        // Create replacement shoe card at top position with new random rotation and Y
        const topCardConfig = { top: 8, left: 18, rotationOffset: 5 }; // Top card config
        const perCardOffset = shoeCardOffsets[2] || { x: 0, y: 0, rotation: 0, scale: 100 };
        const newRandomRotation = generateShoeCardRotation();
        const newRandomY = generateShoeCardRandomY();

        if (cardBackTexture) {
          const newShoeCard = new Sprite(cardBackTexture);
          newShoeCard.anchor.set(0.5);
          // Store data for updates
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseLeft = topCardConfig.left;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseTop = topCardConfig.top;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseRotOffset = topCardConfig.rotationOffset;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).cardIndex = 2;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).randomRotation = newRandomRotation;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).randomY = newRandomY;
          // Position
          newShoeCard.x = SHOE_POSITION.x + topCardConfig.left + shoeCardsX + perCardOffset.x;
          newShoeCard.y = SHOE_POSITION.y + topCardConfig.top + shoeCardsY + perCardOffset.y + newRandomY;
          // Scale (group scale * per-card scale)
          const perCardScale = (perCardOffset.scale ?? 100) / 100;
          const cardScaleX = (53 / cardBackTexture.width) * shoeCardsScale * perCardScale; // SHOE_CARD_CONFIG.cardSize.width = 53
          const cardScaleY = (75 / cardBackTexture.height) * shoeCardsScale * perCardScale; // SHOE_CARD_CONFIG.cardSize.height = 75
          newShoeCard.scale.set(cardScaleX, cardScaleY);
          // Rotation (base 90° + offset 5° + group rotation + per-card rotation + random)
          const perCardRotation = perCardOffset.rotation ?? 0;
          newShoeCard.rotation = degreesToRadians(90 + topCardConfig.rotationOffset + shoeCardsRot + perCardRotation + newRandomRotation);
          // Apply mask to clip at shoe edge
          if (scene.shoeMask) {
            newShoeCard.mask = scene.shoeMask;
          }
          scene.tableLayer?.addChild(newShoeCard);
          scene.shoeCardSprites[2] = newShoeCard;
        }

        // DEAL DEBUG
        dbg(`[DEAL] Using shoe card system: face-down card at (${faceDownContainer.x.toFixed(0)}, ${faceDownContainer.y.toFixed(0)}), rot=${(shoeCardRotation * 180 / Math.PI).toFixed(1)}°`);
        dbg(`[DEAL] Replacement shoe card created with random rotation: ${newRandomRotation.toFixed(1)}°`);
        dbg(`[DEAL] Target seat visualPos=${visualPos}, pos=(${pos?.x?.toFixed(0)}, ${pos?.y?.toFixed(0)})`);

        // Calculate CENTERED position for this card
        // For initial 2-card deal: pre-center for 2 cards (no shift needed on 2nd card)
        // For 3+ cards: recenter as each card is added
        const totalCards = cardIndex < 2 ? 2 : cardIndex + 1;
        const seatRotationDegrees = pos.rotation;
        const localSeatRotationRad = degreesToRadians(seatRotationDegrees);

        // Get per-seat card config (uses VISUAL position - configs are about screen location, not physical seat)
        // Use ref to get latest config value (avoids closure issues)
        const perSeatConfig = perSeatCardConfigsRef.current?.[visualPos] || { x: 0, y: 0, radiusOffset: 0 };
        dbg(`[DEAL] perSeatCardConfigs keys: ${Object.keys(perSeatCardConfigsRef.current || {}).join(',')}, visualPos: ${visualPos}, config: x=${perSeatConfig.x}, y=${perSeatConfig.y}, rOff=${perSeatConfig.radiusOffset}`);
        const seatCardsYOffset = currentCardConfig.seatCardsY + perSeatConfig.radiusOffset;

        // Calculate scaled overlap based on card scale for consistent spacing
        const dealScaleFactor = currentCardConfig.scale / 100;
        const scaledOverlap = CARD_OVERLAP * dealScaleFactor;

        // === FILMSTRIP LOCAL COORDINATES ===
        // Cards are positioned in LOCAL coordinates within the filmstrip.
        // Local X = centered offset for card + cell offset for hand + per-seat X offset
        // Local Y = vertical offset from seat center + per-seat Y offset
        // Local rotation = 0 (filmstrip is already rotated with seat)
        const centeredOffset = calculateCardCenteredOffset(cardIndex, totalCards, scaledOverlap);
        const localBaseX = centeredOffset + handIndex * FILMSTRIP_CELL_WIDTH + perSeatConfig.x;
        const localBaseY = seatCardsYOffset + perSeatConfig.y;
        const localBaseRotation = 0; // Cards are upright relative to filmstrip

        // Generate LOCAL randomness for card placement in filmstrip
        const randomness = generateCardRandomnessLocal();
        const localEndX = localBaseX + randomness.offsetX;
        const localEndY = localBaseY + randomness.offsetY;
        const localEndRotation = localBaseRotation + randomness.rotation;

        // === ENSURE FILMSTRIP IS POSITIONED FOR TARGET HAND ===
        // Cards must ALWAYS be dealt to the seat center. The filmstrip must be
        // positioned so the target hand's cell is centered in the viewport.
        // This is NON-NEGOTIABLE: no gameplay outside the seat.
        const targetFilmstripX = -handIndex * FILMSTRIP_CELL_WIDTH;
        if (filmstrip && !filmstrip.destroyed) {
          if (Math.abs(filmstrip.x - targetFilmstripX) > 1) {
            dbg(`[DEAL] Snapping filmstrip from x=${filmstrip.x.toFixed(0)} to x=${targetFilmstripX} for hand ${handIndex}`);
            filmstrip.x = targetFilmstripX;
          }
        }

        // === WORLD COORDINATES (for animation) ===
        // CRITICAL: Animation target is the VISUAL position (seat center), NOT the local position.
        // The filmstrip is positioned so cell N is centered. The card's visual position
        // is its local position MINUS the cell offset (since filmstrip.x = -handIndex * FILMSTRIP_CELL_WIDTH).
        // This means: visualLocalX = localEndX - handIndex * FILMSTRIP_CELL_WIDTH = centeredOffset + randomness
        const visualLocalX = centeredOffset + randomness.offsetX; // Cell offset cancels out
        const visualLocalY = localEndY;
        const basePos = localToWorld(pos.x, pos.y, visualLocalX, visualLocalY, localSeatRotationRad);
        const baseX = basePos.x;
        const baseY = basePos.y;
        const baseRotation = localSeatRotationRad; // World rotation for animation

        dbg(`[DEAL] Hand ${handIndex}: local=(${localBaseX.toFixed(0)}, ${localBaseY.toFixed(0)}), visual=(${visualLocalX.toFixed(0)}, ${visualLocalY.toFixed(0)}), world=(${baseX.toFixed(0)}, ${baseY.toFixed(0)})`);
        if (handIndex > 0) {
          dbg(`[DEAL] Split hand: filmstrip.x=${targetFilmstripX}, card animates to SEAT CENTER`);
        }

        // Prepare nudge animations for existing cards (will execute after new card lands)
        // We calculate everything now but defer the animation to onComplete
        // Note: For cardIndex 1, no shift needed - first card already pre-centered for 2-card hand
        const pendingNudges: Array<{ sprite: Container; target: { x: number; y: number; rotation: number } }> = [];

        if (cardIndex > 1) {
          // Check if this hand is currently straightened
          const isHandStraightened = MODULE_STRAIGHTENED.get(`${seatPosition}-${handIndex}`) || MODULE_STRAIGHTENED.get(`${seatPosition}-all`);

          // Physics nudge constant - simulates impact of new card landing
          const NUDGE_Z = 4;

          for (let i = 0; i < cardIndex; i++) {
            const existingKey = `${seatPosition}-${handIndex}-${i}`;
            const existingState = MODULE_CARD_STATES.get(existingKey);
            if (existingState) {
              // === FILMSTRIP LOCAL COORDINATES (with per-seat offsets) ===
              // Calculate new LOCAL position for this card within the filmstrip
              const newCenteredOffset = calculateCardCenteredOffset(i, totalCards, scaledOverlap);
              const newLocalBaseX = newCenteredOffset + handIndex * FILMSTRIP_CELL_WIDTH + perSeatConfig.x;
              const newLocalBaseY = seatCardsYOffset + perSeatConfig.y;

              // Update stored base position (LOCAL coordinates)
              existingState.baseX = newLocalBaseX;
              existingState.baseY = newLocalBaseY;

              // Generate physics nudge (in LOCAL coordinates, Y is perpendicular to card row)
              const nudgeDirection = Math.random() < 0.5 ? 1 : -1;
              const nudgeRotation = nudgeDirection * Math.random() * NUDGE_Z * (Math.PI / 180); // ±0-3° in radians
              const nudgeY = (Math.random() * 2 - 1) * NUDGE_Z; // ±0-3px perpendicular to card row (local Y)
              const nudgeX = 0; // No horizontal nudge in local coords

              // Calculate final position
              // If hand was straightened, apply small nudge only (cards become slightly un-straightened)
              // If not straightened, add nudge to existing randomness
              let newEndX: number;
              let newEndY: number;
              let newEndRotation: number;

              if (isHandStraightened) {
                // Straightened cards: apply small nudge only
                newEndX = newLocalBaseX + nudgeX;
                newEndY = newLocalBaseY + nudgeY;
                newEndRotation = existingState.baseRotation + nudgeRotation;
                // Update randomness to reflect the nudge (so future recentering preserves it)
                existingState.randomness.offsetX = nudgeX;
                existingState.randomness.offsetY = nudgeY;
                existingState.randomness.rotation = nudgeRotation;
              } else {
                // Randomized cards: add nudge to existing randomness
                existingState.randomness.offsetX += nudgeX;
                existingState.randomness.offsetY += nudgeY;
                existingState.randomness.rotation += nudgeRotation;
                newEndX = newLocalBaseX + existingState.randomness.offsetX;
                newEndY = newLocalBaseY + existingState.randomness.offsetY;
                newEndRotation = existingState.baseRotation + existingState.randomness.rotation;
              }

              // Queue the nudge animation (will execute after card lands)
              // Target is in LOCAL coordinates since card is in filmstrip
              pendingNudges.push({
                sprite: existingState.sprite,
                target: { x: newEndX, y: newEndY, rotation: newEndRotation }
              });
            }
          }
        }

        // Final position for animation (randomness already included in basePos calculation)
        const endX = baseX;
        const endY = baseY;
        const endRotation = baseRotation + randomness.rotation;

        // Store card state for later straightening
        const cardKey = `${seatPosition}-${handIndex}-${cardIndex}`;

        // Register as in-flight (reconciliation will skip this element)
        MODULE_ELEMENTS_IN_FLIGHT.add(cardKey);
        dbg(`[DEAL] Registered in-flight: ${cardKey}`);

        // Debug: show map contents BEFORE store
        const mapId = (MODULE_CARD_STATES as Map<string, CardSpriteState> & { _id?: number })._id;
        const keysBefore = Array.from(MODULE_CARD_STATES.keys());
        dbg(`[DEAL] MapID=${mapId} BEFORE: ${keysBefore.length} keys: ${keysBefore.join(', ')}`);

        // We'll update MODULE_CARD_STATES after the flip to face-up
        // For now, track the face-down container for animation

        // Debug: show map contents BEFORE store
        const keysBeforeAnim = Array.from(MODULE_CARD_STATES.keys());
        dbg(`[DEAL] MapID BEFORE animation: ${keysBeforeAnim.length} keys: ${keysBeforeAnim.join(', ')}`);

        // Clear straightened flag so player can tap to straighten again after new card
        MODULE_STRAIGHTENED.delete(`${seatPosition}-${handIndex}`);
        MODULE_STRAIGHTENED.delete(`${seatPosition}-all`);

        const dealtKey = `${seatPosition}-${handIndex}`;

        // === DURATION CALCULATION ===
        // Use server-provided duration if available, otherwise calculate based on distance
        // Calculate distance for both logging and fallback calculation
        const startX = faceDownContainer.x;
        const startY = faceDownContainer.y;
        const travelDistance = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);

        let dealDuration: number;
        if (duration !== undefined && duration > 0) {
          // Server-provided duration - use it directly
          dealDuration = duration;
          console.log(`[DEAL] seat=${seatPosition} using server duration=${duration}ms`);
        } else {
          // Fallback: Use cached config for baseline, then scale by distance
          // This logs an error because server should have provided duration
          const baselineDuration = getAnimationDuration('cardDeal', undefined, {
            fileName: 'PixiGameTableV1.tsx',
            functionName: 'dealCardToSeat',
            eventType: 'CARD_DEALT',
          });

          // Calculate based on distance - seat 4 gets baseline, others scale
          const seat4Pos = SEAT_POSITIONS.find(s => s.visualPos === 4);
          const seat4X = seat4Pos?.x ?? CANVAS_WIDTH / 2;
          const seat4Y = seat4Pos?.y ?? 400;
          const seat4Distance = Math.sqrt((seat4X - startX) ** 2 + (seat4Y - startY) ** 2);
          dealDuration = Math.round(baselineDuration * (travelDistance / seat4Distance));
        }

        // === FLIP-DURING-FLIGHT CONFIG ===
        // Note: Deal animation uses easeOutCubic, so 20% of TIME ≈ 50% of DISTANCE
        const FLIP_START_PERCENT = 0.16;  // Start flip at ~40% of visual distance
        const FLIP_DURATION = getClientAnimationTiming('cardFlipInFlight', 'dealCard');

        const flipStartDelay = dealDuration * FLIP_START_PERCENT;

        console.log(`[DEAL FLIP] seat=${seatPosition} dist=${travelDistance.toFixed(0)}px duration=${dealDuration}ms flipStart=${flipStartDelay.toFixed(0)}ms`);

        // Pre-create face-up card (will be revealed via flip)
        const faceUpCard = createCardSprite(
          { ...card, faceUp: true },
          textures,
          0, 0, 0,
          currentCardConfig
        );
        faceUpCard.visible = false; // Hidden until flip midpoint
        faceUpCard.scale.x = 0;     // Start with 0 scale for flip-in
        // Match z-index of face-down card during animation (high to stay on top)
        faceUpCard.zIndex = 5000 + cardIndex;
        scene.animationLayer?.addChild(faceUpCard);

        // Flag to track flip state
        let flipComplete = false;
        let flipStarted = false;
        let cardReparented = false; // Prevents flip loop from overwriting LOCAL position after reparent

        // Animate face-down card to destination
        // Sound: deal at animation kickoff (once per card)
        playTableSound('deal');
        animateSprite(faceDownContainer, {
          x: endX,
          y: endY,
          rotation: endRotation,
          scaleX: 1,
          scaleY: 1,
        }, {
          duration: dealDuration,
          debugName: `dealCardToSeat-${seatPosition}-${card.rank}${card.suit}`,
          onComplete: () => {
            // GUARANTEED RESOLUTION: Wrap callback in try-catch to ensure resolve() is always called
            try {
              // === DEAL COMPLETE ===
              // If flip hasn't completed yet, finish it immediately
              if (!flipComplete && faceUpCard && !faceUpCard.destroyed) {
                if (faceDownContainer && !faceDownContainer.destroyed) {
                  faceDownContainer.visible = false;
                }
                faceUpCard.visible = true;
                faceUpCard.scale.x = 1;
                faceUpCard.scale.y = faceDownContainer?.scale?.y ?? 1;
                flipComplete = true;
              }

              // Remove face-down card (safe even if already destroyed)
              if (faceDownContainer && !faceDownContainer.destroyed) {
                scene.animationLayer?.removeChild(faceDownContainer);
                faceDownContainer.destroy({ children: true });
              }

              // Store face-up card in MODULE_CARD_STATES (only if card still valid)
              if (faceUpCard && !faceUpCard.destroyed) {
                // RACE CONDITION FIX: Check if fullVisualResync created a newer sprite while we animated
                // If so, destroy our old sprite instead of overwriting the newer one
                const existingState = MODULE_CARD_STATES.get(cardKey);
                if (existingState && existingState.sprite !== faceUpCard) {
                  dbg(`[DEAL] Player card ${cardKey}: fullVisualResync created newer sprite, destroying animation sprite`);
                  scene.animationLayer?.removeChild(faceUpCard);
                  faceUpCard.destroy();
                } else {
                  // === REPARENT TO FILMSTRIP ===
                  // Move card from animationLayer to filmstrip and set LOCAL position
                  if (filmstrip && !filmstrip.destroyed) {
                    scene.animationLayer?.removeChild(faceUpCard);
                    filmstrip.addChild(faceUpCard);
                    // Set LOCAL position with randomness applied
                    // Note: randomness is already in local coords (Y perpendicular to card row)
                    faceUpCard.x = localBaseX + randomness.offsetX;
                    faceUpCard.y = localBaseY + randomness.offsetY;
                    faceUpCard.rotation = localBaseRotation + randomness.rotation;
                    // Ensure visibility and scale are correct after reparenting
                    faceUpCard.visible = true;
                    faceUpCard.alpha = 1;
                    faceUpCard.scale.x = 1;
                    faceUpCard.scale.y = 1;
                    // Mark as reparented so flip loop won't overwrite LOCAL coords with WORLD coords
                    cardReparented = true;
                    dbg(`[DEAL] Reparented to filmstrip at local (${faceUpCard.x.toFixed(0)}, ${faceUpCard.y.toFixed(0)})`);
                  } else {
                    // Fallback: keep in animationLayer with world position
                    faceUpCard.x = endX;
                    faceUpCard.y = endY;
                    faceUpCard.rotation = endRotation;
                    dbg(`[DEAL] Filmstrip unavailable, keeping in animationLayer`);
                  }

                  // Set z-index for proper overlap: higher cardIndex = on top (rightmost overlaps leftmost)
                  // handIndex * 100 gives each hand its own z-range
                  faceUpCard.zIndex = handIndex * 100 + cardIndex;

                  // Store LOCAL coordinates in MODULE_CARD_STATES
                  MODULE_CARD_STATES.set(cardKey, {
                    sprite: faceUpCard,
                    randomness,
                    baseRotation: localBaseRotation,
                    baseX: localBaseX,
                    baseY: localBaseY,
                    isDealer: false,
                    card,
                  });
                }
              }

              dbg(`[DEAL] Card flip complete: ${card.rank}${card.suit}`);

              // === ELASTIC MASK FOR SPLIT HANDS ===
              // Move mask FIRST (to final positions), THEN move cards
              // This prevents momentary clipping on the left edge during nudge animations
              const maskState = scene.seatMaskState.get(seatPosition);
              if (maskState?.isEnabled && !maskState.isAnimating && pendingNudges.length > 0) {
                // Temporarily move sprites to their FINAL positions
                const savedPositions: { sprite: Sprite; x: number; y: number }[] = [];
                for (const nudge of pendingNudges) {
                  if (nudge.sprite && !nudge.sprite.destroyed) {
                    savedPositions.push({ sprite: nudge.sprite, x: nudge.sprite.x, y: nudge.sprite.y });
                    nudge.sprite.x = nudge.target.x;
                    nudge.sprite.y = nudge.target.y;
                  }
                }

                // Sync mask to FINAL card positions
                adjustMaskToHandBounds(scene, seatPosition, handIndex);

                // Restore sprites to CURRENT positions for animation
                for (const saved of savedPositions) {
                  saved.sprite.x = saved.x;
                  saved.sprite.y = saved.y;
                }
              } else if (maskState?.isEnabled && !maskState.isAnimating) {
                // No nudges - just sync mask to current bounds
                adjustMaskToHandBounds(scene, seatPosition, handIndex);
              }

              // Execute pending nudge animations now that card has landed
              const nudgeDuration = getClientAnimationTiming('cardNudge', 'dealCardToSeat');
              for (const nudge of pendingNudges) {
                if (nudge.sprite && !nudge.sprite.destroyed) {
                  animateSprite(nudge.sprite, nudge.target, { duration: nudgeDuration, debugName: `nudge-${seatPosition}` });
                }
              }

              // Increment dealt card count when card LANDS
              const prevCount = MODULE_DEALT_CARD_COUNTS.get(dealtKey) ?? 0;
              MODULE_DEALT_CARD_COUNTS.set(dealtKey, prevCount + 1);
              const newCount = MODULE_DEALT_CARD_COUNTS.get(dealtKey);
              dbg(`[DEAL] Card landed - dealt count for ${dealtKey}: ${prevCount} -> ${newCount}`);

              // Clear rake-in-progress flag when first card lands (scorebox can now recalculate)
              if (prevCount === 0 && MODULE_RAKE_SEATS_IN_PROGRESS.has(seatPosition)) {
                MODULE_RAKE_SEATS_IN_PROGRESS.delete(seatPosition);
                MODULE_SCOREBOX_WIPE_IN_PROGRESS.delete(seatPosition);
                dbg(`[DEAL] Cleared rake-in-progress flag for seat ${seatPosition} - first card landed`);
              }

              // Unregister from in-flight (reconciliation can now touch this element)
              MODULE_ELEMENTS_IN_FLIGHT.delete(cardKey);
              dbg(`[DEAL] Unregistered in-flight: ${cardKey}`);

              setAnimationCardCount(c => c + 1);
            } catch (err) {
              // Log failure but don't let it prevent resolution
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'dealCardToSeat.onComplete',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'deal-card-to-seat',
                targetDescription: `seat=${seatPosition} card=${card.rank}${card.suit}`,
                expectedDuration: dealDuration,
              });
            }
            // ALWAYS resolve, even if callback failed
            resolve();
          },
        });

        // === START FLIP AT CONFIGURED PERCENTAGE ===
        setTimeout(() => {
          if (flipComplete) return; // Safety check
          flipStarted = true;

          const flipStartTime = performance.now();

          const animateFlip = () => {
            // Safety check: if containers were destroyed, abort
            if (faceUpCard.destroyed) return;

            // Stop loop when deal animation completes (faceDownContainer destroyed)
            // IMPORTANT: Don't overwrite position if card was reparented to filmstrip
            // (the onComplete handler already set LOCAL coords; we'd overwrite with WORLD coords)
            if (faceDownContainer.destroyed) {
              if (!cardReparented) {
                // Only set WORLD position if card wasn't reparented to filmstrip
                faceUpCard.x = endX;
                faceUpCard.y = endY;
                faceUpCard.rotation = endRotation;
                faceUpCard.scale.y = 1;
              }
              return;
            }

            const elapsed = performance.now() - flipStartTime;
            const progress = Math.min(elapsed / FLIP_DURATION, 1);

            // Sync face-up card position with face-down card (still moving)
            faceUpCard.x = faceDownContainer.x;
            faceUpCard.y = faceDownContainer.y;
            faceUpCard.rotation = faceDownContainer.rotation;
            faceUpCard.scale.y = faceDownContainer.scale.y;

            if (progress < 0.5) {
              // First half: shrink face-down card (scale X: 1 -> 0)
              const scaleX = 1 - progress * 2;
              faceDownContainer.scale.x = scaleX * faceDownContainer.scale.y; // Maintain aspect during scale-down from shoe
              faceUpCard.visible = false;
            } else {
              // Second half: grow face-up card (scale X: 0 -> 1)
              const scaleX = (progress - 0.5) * 2;
              faceDownContainer.visible = false;
              faceUpCard.visible = true;
              faceUpCard.scale.x = Math.min(scaleX, 1);

              if (progress >= 1 && !flipComplete) {
                flipComplete = true;
                faceUpCard.scale.x = 1;
              }
            }

            // Continue loop to keep syncing position until deal completes
            requestAnimationFrame(animateFlip);
          };

          requestAnimationFrame(animateFlip);
        }, flipStartDelay);
      });
    },

    dealCardToDealer: (card: CardType, cardIndex: number, duration?: number): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[DEAL DEALER] card=${card.rank}${card.suit} idx=${cardIndex}`);

        // === DIAGNOSTIC: Log scene state at dealer card deal start (first card only) ===
        if (cardIndex === 0) {
          logSceneDiagnostic(sceneRef.current, `dealCardToDealer(card=${card.rank}${card.suit}, idx=${cardIndex})`);
        }

        if (!appRef.current || loadingRef.current) {
          console.log(`[DEAL DEALER] SKIPPED - app not ready or loading`);
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const currentCardConfig = cardConfigRef.current;

        // Safety: ensure straightening is enabled when dealing starts
        if (scene.rakeInProgress) {
          console.log(`[DEAL DEALER] Clearing rakeInProgress flag (was still true)`);
          scene.rakeInProgress = false;
        }

        // === SHOE CARD SYSTEM ===
        // Take the top shoe card sprite and use it for the deal animation (face-down)
        // Then create a replacement shoe card with new random rotation
        const shoeCardsX = shoeCardsConfig?.x ?? 0;
        const shoeCardsY = shoeCardsConfig?.y ?? 0;
        const shoeCardsRot = shoeCardsConfig?.rotation ?? 0;
        const shoeCardsScale = (shoeCardsConfig?.scale ?? 100) / 100;

        // Get top shoe card sprite (index 2 is the top card)
        const topShoeCard = scene.shoeCardSprites[2];
        if (!topShoeCard || topShoeCard.destroyed) {
          console.log(`[DEAL] WARNING: No top shoe card available for dealer, using fallback`);
          resolve();
          return;
        }

        // Capture the shoe card's current rotation (includes its random rotation)
        const shoeCardRotation = topShoeCard.rotation; // Already in radians

        // Remove top shoe card from tableLayer
        scene.tableLayer?.removeChild(topShoeCard);

        // Create a face-down card Container at the shoe card's position
        const cardBackTexture = textures.get('cardBack');
        const faceDownContainer = new Container();
        faceDownContainer.x = topShoeCard.x;
        faceDownContainer.y = topShoeCard.y;
        faceDownContainer.rotation = shoeCardRotation;
        faceDownContainer.scale.set(SHOE_CARD_SCALE);
        faceDownContainer.eventMode = 'none';

        if (cardBackTexture) {
          const scaleFactor = currentCardConfig.scale / 100;
          const cardWidth = CARD_WIDTH * scaleFactor;
          const cardHeight = CARD_HEIGHT * scaleFactor;

          // Card shadow for depth (matching face-up cards)
          const shadow = new Graphics();
          shadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, 8 * scaleFactor);
          shadow.fill({ color: 0x000000, alpha: 0.3 });
          faceDownContainer.addChild(shadow);

          // White background to fill any gaps at corners
          const bg = new Graphics();
          bg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
          bg.fill({ color: 0xfefefe });
          faceDownContainer.addChild(bg);

          // Rounded rectangle mask for card back (matching face-up card corners)
          const mask = new Graphics();
          mask.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
          mask.fill({ color: 0xffffff });
          faceDownContainer.addChild(mask);

          const backSprite = new Sprite(cardBackTexture);
          backSprite.width = cardWidth;
          backSprite.height = cardHeight;
          backSprite.anchor.set(0.5);
          backSprite.mask = mask;
          faceDownContainer.addChild(backSprite);

          // Border overlay to cover any sharp corners (matching face-up cards)
          const border = new Graphics();
          border.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
          border.stroke({ color: 0x374151, width: currentCardConfig.borderThickness });
          faceDownContainer.addChild(border);
        }

        // Add face-down card to animation layer
        // Set high z-index during animation so new cards always appear ON TOP of existing cards
        faceDownContainer.zIndex = 5000 + cardIndex;
        scene.animationLayer?.addChild(faceDownContainer);

        // Destroy the old shoe card sprite
        topShoeCard.destroy();

        // Create replacement shoe card at top position with new random rotation and Y
        const topCardConfig = { top: 8, left: 18, rotationOffset: 5 }; // Top card config
        const perCardOffset = shoeCardOffsets[2] || { x: 0, y: 0, rotation: 0, scale: 100 };
        const newRandomRotation = generateShoeCardRotation();
        const newRandomY = generateShoeCardRandomY();

        if (cardBackTexture) {
          const newShoeCard = new Sprite(cardBackTexture);
          newShoeCard.anchor.set(0.5);
          // Store data for updates
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseLeft = topCardConfig.left;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseTop = topCardConfig.top;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).baseRotOffset = topCardConfig.rotationOffset;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).cardIndex = 2;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).randomRotation = newRandomRotation;
          (newShoeCard as unknown as { baseLeft: number; baseTop: number; baseRotOffset: number; cardIndex: number; randomRotation: number; randomY: number }).randomY = newRandomY;
          // Position
          newShoeCard.x = SHOE_POSITION.x + topCardConfig.left + shoeCardsX + perCardOffset.x;
          newShoeCard.y = SHOE_POSITION.y + topCardConfig.top + shoeCardsY + perCardOffset.y + newRandomY;
          // Scale (group scale * per-card scale)
          const perCardScale = (perCardOffset.scale ?? 100) / 100;
          const cardScaleX = (53 / cardBackTexture.width) * shoeCardsScale * perCardScale;
          const cardScaleY = (75 / cardBackTexture.height) * shoeCardsScale * perCardScale;
          newShoeCard.scale.set(cardScaleX, cardScaleY);
          // Rotation (base 90° + offset 5° + group rotation + per-card rotation + random)
          const perCardRotation = perCardOffset.rotation ?? 0;
          newShoeCard.rotation = degreesToRadians(90 + topCardConfig.rotationOffset + shoeCardsRot + perCardRotation + newRandomRotation);
          // Apply mask to clip at shoe edge
          if (scene.shoeMask) {
            newShoeCard.mask = scene.shoeMask;
          }
          scene.tableLayer?.addChild(newShoeCard);
          scene.shoeCardSprites[2] = newShoeCard;
        }

        console.log(`[DEAL] Dealer: Using shoe card system, replacement rotation: ${newRandomRotation.toFixed(1)}°`);

        // Generate randomness for natural card placement (dealer has 0° seat rotation)
        const randomness = generateCardRandomness(0);

        // Base position (without randomness)
        const dealerCardsX = currentCardConfig.dealerCardsX;
        const dealerCardsY = currentCardConfig.dealerCardsY;
        const gameState = useGameStateStore.getState();
        const holeCardRevealed = gameState.dealer.holeCardRevealed;
        const totalDealerCards = gameState.dealer.cards.length;

        // Scale factor - gap and overlap must account for scaled card width
        const scaleFactor = currentCardConfig.scale / 100;
        const scaledCardWidth = CARD_WIDTH * scaleFactor;
        const scaledOverlap = CARD_OVERLAP * scaleFactor;

        let baseX: number;
        if (!holeCardRevealed && totalDealerCards <= 2) {
          // Before reveal: gap between cards (no overlap)
          // 8px gap between SCALED card edges
          const twoCardCenterOffset = (scaledCardWidth + 8) / 2;
          if (cardIndex === 0) {
            baseX = DEALER_POSITION.x + dealerCardsX - twoCardCenterOffset;
          } else {
            baseX = DEALER_POSITION.x + dealerCardsX + twoCardCenterOffset;
          }
        } else {
          // After reveal or for hits: centered overlap like player hands using scaled overlap
          const centeredOffset = (cardIndex - (totalDealerCards - 1) / 2) * scaledOverlap;
          baseX = DEALER_POSITION.x + dealerCardsX + centeredOffset;
        }
        const baseY = DEALER_POSITION.y + dealerCardsY;
        const baseRotation = 0;

        // Final position with randomness applied
        const endX = baseX + randomness.offsetX;
        const endY = baseY + randomness.offsetY;
        const endRotation = baseRotation + randomness.rotation;

        // Store card state for later straightening
        const cardKey = `dealer-0-${cardIndex}`;

        // Register as in-flight (reconciliation will skip this element)
        MODULE_ELEMENTS_IN_FLIGHT.add(cardKey);
        console.log(`[DEAL DEALER] Registered in-flight: ${cardKey}`);

        // Clear straightened flag for dealer
        MODULE_STRAIGHTENED.delete('dealer');

        // === RECENTER EXISTING DEALER CARDS (for hits after reveal) ===
        // When dealer gets 3+ cards, recenter all existing cards (same pattern as player cards)
        const pendingDealerNudges: Array<{ sprite: Container; target: { x: number; y: number; rotation: number } }> = [];

        if (holeCardRevealed && cardIndex >= 2) {
          // Dealer cards are always straightened after reveal
          const isDealerStraightened = MODULE_STRAIGHTENED.get('dealer');

          // Physics nudge constant - simulates impact of new card landing
          const NUDGE_Z = 4;

          for (let i = 0; i < cardIndex; i++) {
            const existingKey = `dealer-0-${i}`;
            const existingState = MODULE_CARD_STATES.get(existingKey);
            if (existingState) {
              const newCenteredOffset = calculateCardCenteredOffset(i, totalDealerCards, scaledOverlap);
              const newBaseX = DEALER_POSITION.x + dealerCardsX + newCenteredOffset;

              // Update stored base position
              existingState.baseX = newBaseX;

              // Generate physics nudge (dealer cards spread horizontally, so nudge in Y)
              const nudgeDirection = Math.random() < 0.5 ? 1 : -1;
              const nudgeRotation = nudgeDirection * Math.random() * NUDGE_Z * (Math.PI / 180);
              const nudgeY = (Math.random() * 2 - 1) * NUDGE_Z;

              // Calculate final position
              let newEndX: number;
              let newEndY: number;
              let newEndRotation: number;

              if (isDealerStraightened) {
                // Straightened cards: apply small nudge only
                newEndX = newBaseX;
                newEndY = existingState.baseY + nudgeY;
                newEndRotation = existingState.baseRotation + nudgeRotation;
                // Update randomness to reflect the nudge
                existingState.randomness.offsetX = 0;
                existingState.randomness.offsetY = nudgeY;
                existingState.randomness.rotation = nudgeRotation;
              } else {
                // Randomized cards: add nudge to existing randomness
                existingState.randomness.offsetY += nudgeY;
                existingState.randomness.rotation += nudgeRotation;
                newEndX = newBaseX + existingState.randomness.offsetX;
                newEndY = existingState.baseY + existingState.randomness.offsetY;
                newEndRotation = existingState.baseRotation + existingState.randomness.rotation;
              }

              // Queue the nudge animation (will execute after card lands)
              pendingDealerNudges.push({
                sprite: existingState.sprite,
                target: { x: newEndX, y: newEndY, rotation: newEndRotation }
              });
            }
          }
        }

        // === FLIP-DURING-FLIGHT CONFIG (same as player cards) ===
        // Note: Deal animation uses easeOutCubic, so 20% of TIME ≈ 50% of DISTANCE
        const FLIP_START_PERCENT = 0.16;  // Start flip at ~40% of visual distance
        const FLIP_DURATION = getClientAnimationTiming('cardFlipInFlight', 'dealCard');
        // Use server-provided duration, or fallback to cached config (logs error if fallback used)
        const dealDuration = (duration !== undefined && duration > 0) ? duration : getAnimationDuration('cardDeal', undefined, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'dealCardToDealer',
          eventType: 'CARD_DEALT',
        });

        const flipStartDelay = dealDuration * FLIP_START_PERCENT;

        // Hole card (cardIndex === 1) should ALWAYS be dealt face-down
        // It will be revealed later through revealHoleCard(), even for natural blackjack
        const isHoleCard = cardIndex === 1;
        const shouldFlip = !isHoleCard && card.faceUp;

        // Pre-create face-up card for flip (if needed)
        let faceUpCard: Container | null = null;
        let flipComplete = false;
        let flipStarted = false;

        if (shouldFlip) {
          faceUpCard = createCardSprite(
            { ...card, faceUp: true },
            textures,
            0, 0, 0,
            currentCardConfig
          );
          faceUpCard.visible = false;
          faceUpCard.scale.x = 0; // Start with 0 scale for flip-in
          // Match z-index of face-down card during animation (high to stay on top)
          faceUpCard.zIndex = 5000 + cardIndex;
          scene.animationLayer?.addChild(faceUpCard);
        }

        // Animate face-down card to destination
        // Sound: deal at animation kickoff (once per card)
        playTableSound('deal');
        animateSprite(faceDownContainer, { x: endX, y: endY, rotation: endRotation, scaleX: 1, scaleY: 1 }, {
          duration: dealDuration,
          debugName: `dealCardToDealer-${card.rank}${card.suit}`,
          onComplete: () => {
            // GUARANTEED RESOLUTION: Wrap callback in try-catch to ensure resolve() is always called
            try {
              if (shouldFlip && faceUpCard && !faceUpCard.destroyed) {
                // If flip hasn't completed yet, finish it immediately
                if (!flipComplete && faceDownContainer && !faceDownContainer.destroyed) {
                  faceDownContainer.visible = false;
                  faceUpCard.visible = true;
                  faceUpCard.scale.x = 1;
                  faceUpCard.scale.y = faceDownContainer.scale.y;
                  flipComplete = true;
                }

                // ALWAYS ensure face-up card is at final position
                // (flip animation stops syncing position when it completes)
                faceUpCard.x = endX;
                faceUpCard.y = endY;
                faceUpCard.rotation = endRotation;

                // Remove face-down card (safe even if already destroyed)
                if (faceDownContainer && !faceDownContainer.destroyed) {
                  scene.animationLayer?.removeChild(faceDownContainer);
                  faceDownContainer.destroy({ children: true });
                }

                // RACE CONDITION FIX: Check if fullVisualResync created a newer sprite while we animated
                // If so, destroy our old sprite instead of overwriting the newer one
                const existingState = MODULE_CARD_STATES.get(cardKey);
                if (existingState && existingState.sprite !== faceUpCard) {
                  console.log(`[DEAL] Dealer card ${cardKey}: fullVisualResync created newer sprite, destroying animation sprite`);
                  scene.animationLayer?.removeChild(faceUpCard);
                  faceUpCard.destroy();
                } else {
                  MODULE_CARD_STATES.set(cardKey, {
                    sprite: faceUpCard,
                    randomness,
                    baseRotation,
                    baseX,
                    baseY,
                    isDealer: true,
                    card,
                  });
                  console.log(`[DEAL] Dealer card flip complete: ${card.rank}${card.suit}`);
                }

                // Execute pending nudge animations now that card has landed
                const dealerNudgeDuration = getClientAnimationTiming('cardNudge', 'dealCardToDealer');
                for (const nudge of pendingDealerNudges) {
                  if (nudge.sprite && !nudge.sprite.destroyed) {
                    animateSprite(nudge.sprite, nudge.target, { duration: dealerNudgeDuration, debugName: 'dealer-nudge' });
                  }
                }
              } else if (faceDownContainer && !faceDownContainer.destroyed) {
                // Hole card stays face-down
                // RACE CONDITION FIX: Check if fullVisualResync created a newer sprite while we animated
                const existingState = MODULE_CARD_STATES.get(cardKey);
                if (existingState && existingState.sprite !== faceDownContainer) {
                  console.log(`[DEAL] Dealer hole card ${cardKey}: fullVisualResync created newer sprite, destroying animation sprite`);
                  scene.animationLayer?.removeChild(faceDownContainer);
                  faceDownContainer.destroy({ children: true });
                } else {
                  MODULE_CARD_STATES.set(cardKey, {
                    sprite: faceDownContainer,
                    randomness,
                    baseRotation,
                    baseX,
                    baseY,
                    isDealer: true,
                    card,
                  });
                  console.log(`[DEAL] Dealer hole card stays face-down`);
                }
              }

              // Increment dealt card count when card LANDS
              const dealtKey = 'dealer-0';
              MODULE_DEALT_CARD_COUNTS.set(dealtKey, (MODULE_DEALT_CARD_COUNTS.get(dealtKey) ?? 0) + 1);
              console.log(`[DEAL] Dealer card landed - dealt count: ${MODULE_DEALT_CARD_COUNTS.get(dealtKey)}`);

              // Unregister from in-flight (reconciliation can now touch this element)
              MODULE_ELEMENTS_IN_FLIGHT.delete(cardKey);
              console.log(`[DEAL DEALER] Unregistered in-flight: ${cardKey}`);

              // Trigger re-render so scorebox updates
              setAnimationCardCount(c => c + 1);
            } catch (err) {
              // Log failure but don't let it prevent resolution
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'dealCardToDealer.onComplete',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'deal-card-to-dealer',
                targetDescription: `card=${card.rank}${card.suit} idx=${cardIndex}`,
                expectedDuration: dealDuration,
              });
            }
            // ALWAYS resolve, even if callback failed
            resolve();
          },
        });

        // === START FLIP AT CONFIGURED PERCENTAGE (for face-up cards only) ===
        if (shouldFlip && faceUpCard) {
          const faceUpCardRef = faceUpCard;

          setTimeout(() => {
            if (flipComplete) return; // Safety check
            flipStarted = true;

            const flipStartTime = performance.now();

            const animateFlip = () => {
              // Safety check: if containers were destroyed, abort
              if (faceUpCardRef.destroyed) return;

              // Stop loop when deal animation completes (faceDownContainer destroyed)
              if (faceDownContainer.destroyed) {
                // Ensure final position
                faceUpCardRef.x = endX;
                faceUpCardRef.y = endY;
                faceUpCardRef.rotation = endRotation;
                faceUpCardRef.scale.y = 1;
                return;
              }

              const elapsed = performance.now() - flipStartTime;
              const progress = Math.min(elapsed / FLIP_DURATION, 1);

              // Sync face-up card position with face-down card (still moving)
              faceUpCardRef.x = faceDownContainer.x;
              faceUpCardRef.y = faceDownContainer.y;
              faceUpCardRef.rotation = faceDownContainer.rotation;
              faceUpCardRef.scale.y = faceDownContainer.scale.y;

              if (progress < 0.5) {
                // First half: shrink face-down card (scale X: 1 -> 0)
                const scaleX = 1 - progress * 2;
                faceDownContainer.scale.x = scaleX * faceDownContainer.scale.y;
                faceUpCardRef.visible = false;
              } else {
                // Second half: grow face-up card (scale X: 0 -> 1)
                const scaleX = (progress - 0.5) * 2;
                faceDownContainer.visible = false;
                faceUpCardRef.visible = true;
                faceUpCardRef.scale.x = Math.min(scaleX, 1);

                if (progress >= 1 && !flipComplete) {
                  flipComplete = true;
                  faceUpCardRef.scale.x = 1;
                }
              }

              // Continue loop to keep syncing position until deal completes
              requestAnimationFrame(animateFlip);
            };

            requestAnimationFrame(animateFlip);
          }, flipStartDelay);
        }
      });
    },

    // ===========================================
    // CHIP ANIMATIONS - Using Layout Store Endpoints
    // ===========================================

    /**
     * Animate chip from player's rail to kitty.
     * Used when bet is confirmed - chips depart from rail (player's balance).
     * Flow: Rail → Player Kitty
     *
     * Chips land in a persistent kitty display that builds up with each chip.
     * Counter upticks as each chip lands.
     */
    animateRailToKitty: (seatPosition: number, denomination: number, serverDuration?: number): Promise<void> => {
      // EPIC GAMES FIX: Skip animation when tab is hidden, but still add chip to kitty
      const tabHidden = document.hidden;
      if (tabHidden) {
        console.log('[animateRailToKitty] Tab hidden - adding chip instantly (no animation)');
      }

      // TIMING TEST: Measure time since BET press
      const now = performance.now();
      const betPressTime = (window as unknown as { __BET_PRESS_TIME__?: number }).__BET_PRESS_TIME__ || 0;
      const delta = betPressTime ? (now - betPressTime).toFixed(2) : 'N/A';
      console.log(`[TIMING TEST] ⏱️ CHIP ANIMATION START at ${now.toFixed(2)}ms | Delta from BET: ${delta}ms | duration=${serverDuration ?? 'default'}`);

      console.log('[animateRailToKitty] ========== START ==========');
      console.log('[animateRailToKitty] seat:', seatPosition, 'denom:', denomination, 'duration:', serverDuration ?? 'default');
      console.log(`[CHIP AUDIT] Rail→Kitty (bet confirm) | seat=${seatPosition} | denom=${denomination} | animated=1 sprite`);
      console.log('[animateRailToKitty] loading:', loadingRef.current, 'appRef:', !!appRef.current);
      console.log('[animateRailToKitty] mySeatPosition:', mySeatPosition);

      return new Promise(async (resolve) => {
        // Wait for loading to complete if necessary (max 2 seconds)
        // This handles race conditions where bet is confirmed before PixiJS finishes loading
        let waitAttempts = 0;
        while (loadingRef.current && waitAttempts < 40) {
          console.log('[animateRailToKitty] Waiting for loading... attempt', waitAttempts);
          await new Promise(r => setTimeout(r, 50));
          waitAttempts++;
        }

        if (waitAttempts > 0) {
          console.log('[animateRailToKitty] Waited', waitAttempts * 50, 'ms for loading to complete');
        }

        if (!appRef.current || loadingRef.current) {
          console.warn('[animateRailToKitty] EARLY RETURN - app not ready after waiting. loading:', loadingRef.current, 'appRef:', !!appRef.current);
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        console.log('[animateRailToKitty] visualPos:', visualPos);
        console.log('[animateRailToKitty] scene.uiLayer:', !!scene.uiLayer);
        console.log('[animateRailToKitty] scene.animationLayer:', !!scene.animationLayer);
        console.log('[animateRailToKitty] scene.chipRailChips.size:', scene.chipRailChips.size);
        console.log('[animateRailToKitty] scene.chipRailContainer:', !!scene.chipRailContainer);

        // Get rail position from actual rail chip sprite (adapts to layout changes including X/Y offsets)
        const railChip = scene.chipRailChips.get(denomination);
        let railPos: { x: number; y: number };

        if (railChip && scene.chipRailContainer && scene.uiLayer) {
          // Get global position and convert to uiLayer local coordinates
          // This ensures positions are correct regardless of container scale/offset
          const globalPos = railChip.getGlobalPosition();
          const localPos = scene.uiLayer.toLocal(globalPos);
          railPos = { x: localPos.x, y: localPos.y };
        } else {
          // Fallback: calculate position if rail chip not found (includes X/Y offsets)
          const railCalcScale = (chipRailConfig?.scale ?? 100) / 100;
          const railCalcX = UI_CHIP_RAIL_POS.x + (chipRailConfig?.x ?? 0);
          const railCalcY = UI_CHIP_RAIL_POS.y + (chipRailConfig?.y ?? 0);
          const railSpacing = chipRailConfig?.spacing ?? 85;
          const railTotalWidth = (CHIP_RAIL_DENOMS.length - 1) * railSpacing;
          const railStartX = -railTotalWidth / 2;
          const railArcHeight = 25;

          const denomIndex = CHIP_RAIL_DENOMS.indexOf(denomination);
          const railLocalX = railStartX + (denomIndex === -1 ? 0 : denomIndex * railSpacing);
          const railNormalizedPos = denomIndex === -1 ? 0 : (denomIndex - (CHIP_RAIL_DENOMS.length - 1) / 2) / ((CHIP_RAIL_DENOMS.length - 1) / 2);
          const railLocalY = railArcHeight * (railNormalizedPos * railNormalizedPos - 1);
          // Apply per-chip X/Y offsets
          const xOffset = chipXOffsets[denomination] ?? 0;
          const yOffset = chipYOffsets[denomination] ?? 0;
          railPos = {
            x: railCalcX + (railLocalX + xOffset) * railCalcScale,
            y: railCalcY + (railLocalY + yOffset) * railCalcScale,
          };
        }
        console.log('[animateRailToKitty] railPos:', railPos, 'from sprite:', !!railChip);

        // Calculate kitty position from dynamicSeatPositions (same as slug stack)
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn('[PixiGameTableV1] animateRailToKitty: No seat data for visualPos', visualPos);
          resolve();
          return;
        }

        // Get betChipConfig for this seat (applies DEV CONTROLS offsets)
        // Use seatPosition for config lookup (UI keys by seat number, not visual position)
        const betConfig = betChipConfigs[seatPosition] || REGISTRY_DEFAULT_CHIP_CONFIG;

        // Calculate kitty position using registry
        const kittyPosResult = calculateKittyPosition(seatData, {
          betChipConfig: betConfig,
          kittyOffsetX: kittyConfig.x,
          kittyOffsetY: kittyConfig.y,
        });
        const rotRad = kittyPosResult.rotationRad;
        const kittyPos = { x: kittyPosResult.x, y: kittyPosResult.y };

        // Get or create kitty display for this visual position
        let kittyContainer = scene.kittyContainers.get(visualPos);
        let kittyData = scene.kittyData.get(visualPos);
        let kittyChipSprites = scene.kittyChipSprites.get(visualPos);
        let kittyCounterText = scene.kittyCounterTexts.get(visualPos);
        let kittyPill = scene.kittyPills.get(visualPos);

        if (!kittyContainer) {
          // Create new kitty display with DEV CONTROLS counter config
          console.log('[animateRailToKitty] Creating NEW kitty display for visualPos:', visualPos);
          console.log('[animateRailToKitty] kittyPos:', kittyPos, 'rotation:', rotRad);

          // Store base position (before any centering offset)
          scene.kittyBasePositions.set(visualPos, { x: kittyPos.x, y: kittyPos.y, rotRad });

          // Check if insurance kitty exists for this seat - if so, apply centering offset
          // Note: seatPosition and visualPos may differ; insurance is keyed by seatPosition
          // We need to find if any seatPosition maps to this visualPos
          let insuranceAmount = 0;
          scene.insuranceKittyData.forEach((insData, insSeatPos) => {
            const insVisualPos = getVisualPositionForSeat(insSeatPos);
            if (insVisualPos === visualPos) {
              insuranceAmount = insData.total;
            }
          });

          // Calculate centering offset if insurance exists
          // Use insurance x config as the padding between pill edge and centerline
          // Use visualPos for config lookup (DEV CONTROLS alignment)
          const insConfigForCentering = insuranceChipConfigs[visualPos] || { x: KITTY_PAIR_GAP };
          const padding = KITTY_PAIR_GAP;
          const centeringOffset = insuranceAmount > 0
            ? calculateKittyCenteringOffset(denomination, padding, true, true)
            : 0;
          const adjustedPos = applyTangentOffset(kittyPos, centeringOffset, rotRad);

          const kittyDisplay = createKittyDisplay(textures, chipCounterConfig);
          kittyContainer = kittyDisplay.container;
          kittyContainer.x = adjustedPos.x;
          kittyContainer.y = adjustedPos.y;
          kittyContainer.rotation = rotRad; // Seat rotation only - counter stays oriented to seat
          kittyContainer.zIndex = 1500; // Above seats

          // Apply extra rotation to chipsWrapper only (chips rotate, counter stays in place)
          const extraRotRad = degreesToRadians(kittyConfig.rotation || 0);
          kittyDisplay.chipsWrapper.rotation = extraRotRad;

          // Mark as kitty element so clearTable doesn't rake it
          // Store seatPosition for config lookup (betChipConfigs is keyed by seat, not visual position)
          (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).isKittyElement = true;
          (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).seatPosition = seatPosition;

          scene.animationLayer?.addChild(kittyContainer);
          scene.kittyContainers.set(visualPos, kittyContainer);
          scene.kittyChipsWrappers.set(visualPos, kittyDisplay.chipsWrapper);

          console.log('[animateRailToKitty] Kitty container added to animationLayer at:', kittyContainer.x, kittyContainer.y);

          kittyData = { denominations: [], total: 0 };
          scene.kittyData.set(visualPos, kittyData);

          kittyChipSprites = new Map<number, KittyChipData>();
          scene.kittyChipSprites.set(visualPos, kittyChipSprites);

          kittyCounterText = kittyDisplay.counterText;
          scene.kittyCounterTexts.set(visualPos, kittyCounterText);

          kittyPill = kittyDisplay.pill;
          scene.kittyPills.set(visualPos, kittyPill);

          // Store references for layout updates
          (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).borderContainer = kittyDisplay.borderContainer;
          (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).chipContainer = kittyDisplay.chipContainer;
        }

        // ===== INSTANT PATH: Tab hidden - add chip directly without animation =====
        if (tabHidden) {
          console.log('[animateRailToKitty] INSTANT PATH - tab hidden, adding chip directly');

          // Create chip sprite at kitty position (no animation needed)
          const chipSprite = createChipSprite(denomination, textures, 0, 0);
          if (!chipSprite) {
            console.warn('[animateRailToKitty] INSTANT: Failed to create chip sprite');
            resolve();
            return;
          }

          // Set to kitty size immediately
          chipSprite.width = CHIP_SIZES.kittyMedium;
          chipSprite.height = CHIP_SIZES.kittyMedium;
          chipSprite.rotation = Math.random() * 2 * Math.PI;

          // Update kitty data
          if (kittyData) {
            kittyData.denominations.push(denomination);
            kittyData.denominations.sort((a, b) => a - b);
            kittyData.total += denomination;
          }

          // Add chip to kitty container
          const containerData = kittyContainer as unknown as { borderContainer: Container; chipContainer: Container };
          const chipContainer = containerData.chipContainer;
          const borderContainer = containerData.borderContainer;

          if (chipContainer && borderContainer && kittyChipSprites && chipSprite) {
            addChipToKitty(chipContainer, borderContainer, denomination, chipSprite, kittyChipSprites, chipBorderConfig);
          }

          // Update layout (counter display)
          if (kittyCounterText && !kittyCounterText.destroyed && kittyPill && !kittyPill.destroyed && kittyData && kittyChipSprites) {
            const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
            updateKittyLayout(kittyPill, kittyCounterText, kittyChipSprites.size, kittyData.total, counterOffset);
          }

          // Make kitty visible
          if (kittyContainer && !kittyContainer.destroyed) {
            kittyContainer.visible = true;
          }

          console.log('[animateRailToKitty] INSTANT: Chip added, total:', kittyData?.total);
          resolve();
          return;
        }
        // ===== END INSTANT PATH =====

        // Create chip at rail position (player's balance)
        console.log('[animateRailToKitty] Creating chip sprite at rail:', railPos.x, railPos.y);
        console.log('[animateRailToKitty] textures size:', textures.size);
        const chipSprite = createChipSprite(denomination, textures, railPos.x, railPos.y);
        if (!chipSprite) {
          console.warn('[animateRailToKitty] FAILED to create chip sprite - textures may not be loaded');
          resolve();
          return;
        }
        console.log('[animateRailToKitty] Chip sprite created successfully');

        // Start at rail size (chips depart rail at rail chip size)
        chipSprite.width = CHIP_SIZES.rail;
        chipSprite.height = CHIP_SIZES.rail;
        chipSprite.zIndex = 2000; // Above rail chips so departing chip appears on top

        // Add to UI layer (so chip appears in front of rail stacks)
        if (!scene.uiLayer) {
          console.warn('[animateRailToKitty] NO UI LAYER - chip will not be visible!');
        }
        scene.uiLayer?.addChild(chipSprite);
        console.log('[animateRailToKitty] Chip added to uiLayer, starting animation...');

        // Departing chip copies current rail chip rotation (looks like same chip leaving)
        const currentRailRotation = MODULE_RAIL_CHIP_ROTATIONS.get(denomination) ?? 0;
        chipSprite.rotation = currentRailRotation;
        // When chip departs from rail, the revealed chip underneath gets new rotation
        const newRailRotation = Math.random() * 2 * Math.PI;
        MODULE_RAIL_CHIP_ROTATIONS.set(denomination, newRailRotation);
        // Also update the visible rail chip sprite immediately (prevents rotation jump on next re-render)
        const railChipContainer = scene.chipRailChips.get(denomination);
        if (railChipContainer) {
          for (const child of railChipContainer.children) {
            if (child instanceof Sprite) {
              child.rotation = newRailRotation;
              break;
            }
          }
        }

        // Calculate denomination-specific target position in kitty
        const existingDenoms = kittyData?.denominations || [];
        const chipLocalY = calculateKittyChipYOffset(denomination, existingDenoms);

        // Transform local Y offset to global coordinates using kitty rotation
        const endPos = kittyLocalToWorld(kittyPos.x, kittyPos.y, chipLocalY, rotRad);
        console.log('[animateRailToKitty] Target position:', endPos.x, endPos.y, 'localY offset:', chipLocalY);

        // Use server-provided duration, or fallback to cached config
        const duration = getAnimationDuration('chipFlight', serverDuration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateRailToKitty',
          eventType: 'BET_CONFIRMED',
        });

        // Track if counter has been updated (at 50% progress)
        let counterUpdated = false;
        // Track if chip land sound has been played (at 25% progress)
        let chipSoundPlayed = false;

        console.log('[animateRailToKitty] Starting animateChipFlight for denom:', denomination);

        // Use shared animateChipFlight helper for consistent animation behavior
        animateChipFlight({
          sprite: chipSprite,
          animationLayer: scene.uiLayer!, // Chip is already parented to uiLayer
          startPos: { x: railPos.x, y: railPos.y },
          endPos,
          startSize: CHIP_SIZES.rail,
          endSize: CHIP_SIZES.kittyMedium,
          duration,
          zIndex: 2000,
          startRotation: currentRailRotation,
          endRotation: Math.random() * 2 * Math.PI,
          destroyOnComplete: false, // We'll add the chip to kitty instead
          debugLabel: `rail-to-kitty-${denomination}`,

          // Sound and counter updates at progress thresholds
          onProgress: (progress) => {
            // Sound: chipLand on ARRIVAL in the kitty (progress 1.0 — the precise landing)
            if (progress >= 1 && !chipSoundPlayed) {
              chipSoundPlayed = true;
              playTableSound('chipLand');
            }
            if (progress >= 0.5 && !counterUpdated) {
              counterUpdated = true;
              // DEFENSIVE: Check elements aren't destroyed before accessing
              if (kittyData && kittyCounterText && !kittyCounterText.destroyed && kittyPill && !kittyPill.destroyed && kittyChipSprites) {
                // Update tracking data
                kittyData.denominations.push(denomination);
                kittyData.denominations.sort((a, b) => a - b);
                kittyData.total += denomination;

                // Update layout - use denominations.length since chip hasn't been added to map yet
                const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
                updateKittyLayout(kittyPill, kittyCounterText, kittyData.denominations.length, kittyData.total, counterOffset);

                // Make kitty visible (check container still exists)
                if (kittyContainer && !kittyContainer.destroyed) {
                  kittyContainer.visible = true;
                }

                // Counter uptick animation (scale pulse) - only if text still valid
                if (!kittyCounterText.destroyed) {
                  kittyCounterText.scale.set(1.3, 1.3);
                  const scaleDuration = getClientAnimationTiming('chipLandingScale', 'animateRailToKitty');
                  animateSprite(kittyCounterText, {
                    scaleX: 1.0,
                    scaleY: 1.0,
                  }, {
                    duration: scaleDuration,
                    easing: easeOutCubic,
                    debugName: 'kitty-counter-pulse',
                  });
                }
              }
            }
          },

          // Add chip to kitty on completion
          onComplete: () => {
            try {
              // Remove from uiLayer (animateChipFlight doesn't destroy with destroyOnComplete: false)
              scene.uiLayer?.removeChild(chipSprite);

              // DEFENSIVE: Check if kittyContainer was destroyed during animation
              if (kittyContainer && kittyContainer.destroyed) {
                console.error('[animateRailToKitty] KITTY DESTROYED during animation! denom:', denomination, 'visualPos:', visualPos);
                console.error('[animateRailToKitty] This is likely caused by fullVisualResync running during chip animation');
                chipSprite.destroy();
                return;
              }

              // Get kitty elements
              const containerData = kittyContainer as unknown as { borderContainer: Container; chipContainer: Container };
              const chipContainer = containerData.chipContainer;
              const borderContainer = containerData.borderContainer;

              // DIAGNOSTIC: Log if we can't add the chip
              if (!chipContainer || !borderContainer || !kittyChipSprites || !chipSprite || chipSprite.destroyed) {
                console.error('[animateRailToKitty] CANNOT ADD CHIP TO KITTY:', {
                  chipContainer: !!chipContainer,
                  borderContainer: !!borderContainer,
                  kittyChipSprites: !!kittyChipSprites,
                  chipSprite: !!chipSprite,
                  chipSpriteDestroyed: chipSprite?.destroyed,
                  kittyContainerDestroyed: kittyContainer?.destroyed,
                  visualPos,
                  denomination,
                });
                chipSprite.destroy();
                return;
              }

              // Add chip to kitty using Map-based system
              addChipToKitty(chipContainer, borderContainer, denomination, chipSprite, kittyChipSprites, chipBorderConfig);
              console.log('[animateRailToKitty] Chip ADDED to kitty - denom:', denomination, 'kittyChipSprites.size:', kittyChipSprites.size);

              // DIAGNOSTIC: Verify kitty container state after adding chip
              console.log('[animateRailToKitty] KITTY STATE after add:', {
                kittyVisible: kittyContainer?.visible,
                kittyDestroyed: kittyContainer?.destroyed,
                kittyParent: !!kittyContainer?.parent,
                kittyPosition: kittyContainer ? { x: kittyContainer.x, y: kittyContainer.y } : null,
                chipContainerChildren: chipContainer?.children?.length,
                chipSpriteInKitty: chipSprite?.parent === chipContainer,
                animationLayerHasKitty: scene.animationLayer?.children.includes(kittyContainer),
              });

              console.log('[animateRailToKitty] Animation COMPLETE for denom:', denomination);
            } catch (err) {
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'animateRailToKitty.completion',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'chip-rail-to-kitty',
                targetDescription: `seat=${seatPosition} denom=${denomination}`,
                expectedDuration: duration,
              });
              chipSprite.destroy();
            }
          },
        }).then(resolve);
      });
    },

    /**
     * Animate chips from opponent's avatar to their kitty (for opponent bet placement).
     * This is the Pixi equivalent of animateOpponentBetToKitty in chipAnimations.ts (which uses DOM).
     * Flow: Opponent Avatar → Opponent Kitty
     */
    animateOpponentBetToKitty: (seatPosition: number, amount: number, betChips: number[]): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[animateOpponentBetToKitty] seat=${seatPosition}, amount=$${amount}, chips=${betChips.length}`);

        const scene = sceneRef.current;
        const textures = texturesRef.current;

        if (!scene.animationLayer || !textures) {
          console.warn('[animateOpponentBetToKitty] Scene not ready, skipping');
          resolve();
          return;
        }

        // Clear any existing kitty for this seat first
        const visualPos = getVisualPositionForSeat(seatPosition);
        console.log(`[animateOpponentBetToKitty] seat=${seatPosition} → visualPos=${visualPos}`);
        const existingKitty = scene.kittyContainers.get(visualPos);
        if (existingKitty && !existingKitty.destroyed) {
          console.log(`[animateOpponentBetToKitty] Clearing existing kitty for visualPos=${visualPos}`);
          existingKitty.destroy({ children: true });
          scene.kittyContainers.delete(visualPos);
          scene.kittyChipsWrappers.delete(visualPos);
          scene.kittyData.delete(visualPos);
          scene.kittyChipSprites.delete(visualPos);
          scene.kittyCounterTexts.delete(visualPos);
          scene.kittyPills.delete(visualPos);
          scene.kittyBasePositions.delete(visualPos);
        }

        // Get seat data
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn('[animateOpponentBetToKitty] No seat data for visualPos:', visualPos);
          resolve();
          return;
        }

        // Calculate avatar position (source) - uses registry function for consistency
        // IMPORTANT: Must apply seat config offsets (x, y, rot) to match where avatar is actually rendered
        const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const adjustedSeatData = calculateAdjustedSeatPosition(seatData, seatConfig);
        const avatarPos = calculateAvatarPosition(adjustedSeatData, seatConfig, seatSizeScale);

        // Calculate kitty position (target) - use same signature as animateRailToKitty
        // Use seatPosition for betChipConfigs, but VISUAL position for opponentKittyConfigs (DEV CONTROLS alignment)
        const betConfig = betChipConfigs[seatPosition] || REGISTRY_DEFAULT_CHIP_CONFIG;
        // Get per-seat opponent kitty offset (this is an opponent kitty) - use visual position
        const perSeatOpponentConfig = opponentKittyConfigs?.[visualPos] ?? { radiusOffset: 0, x: 0, y: 0, scale: 100 };
        // Create a modified betConfig that includes the per-seat radiusOffset
        const adjustedBetConfig = {
          ...betConfig,
          radiusOffset: betConfig.radiusOffset + perSeatOpponentConfig.radiusOffset,
        };
        const kittyPosResult = calculateKittyPosition(seatData, {
          betChipConfig: adjustedBetConfig,
          kittyOffsetX: kittyConfig.x + perSeatOpponentConfig.x,
          kittyOffsetY: kittyConfig.y + perSeatOpponentConfig.y,
        });
        const kittyPos = { x: kittyPosResult.x, y: kittyPosResult.y };

        // Create kitty display (without counter for opponents)
        const kittyDisplay = createKittyDisplay(textures, chipCounterConfig);
        const kittyContainer = kittyDisplay.container;
        kittyContainer.x = kittyPos.x;
        kittyContainer.y = kittyPos.y;
        kittyContainer.rotation = kittyPosResult.rotationRad; // Seat rotation from position calculation
        kittyContainer.zIndex = 1500; // Above seats (same as player kitty)
        // ChipsWrapper gets extra rotation from config only (not seat rotation - that's on container)
        const extraRotRad = degreesToRadians(kittyConfig.rotation || 0);
        kittyDisplay.chipsWrapper.rotation = extraRotRad;

        // Hide counter for opponent kitties (pill background + counter text)
        kittyDisplay.pill.visible = false;
        kittyDisplay.counterText.visible = false;

        // Mark as kitty element so clearTable doesn't rake it
        // Also mark as opponent kitty so we don't update the counter
        // Store seatPosition for config lookup (betChipConfigs is keyed by seat, not visual position)
        (kittyContainer as unknown as { isKittyElement: boolean; isOpponentKitty: boolean; seatPosition: number }).isKittyElement = true;
        (kittyContainer as unknown as { isKittyElement: boolean; isOpponentKitty: boolean; seatPosition: number }).isOpponentKitty = true;
        (kittyContainer as unknown as { isKittyElement: boolean; isOpponentKitty: boolean; seatPosition: number }).seatPosition = seatPosition;

        scene.animationLayer!.addChild(kittyContainer);
        console.log(`[animateOpponentBetToKitty] Kitty created for visualPos=${visualPos} at (${kittyPos.x.toFixed(0)}, ${kittyPos.y.toFixed(0)}) zIndex=${kittyContainer.zIndex} rotation=${kittyPosResult.rotationRad.toFixed(2)} parent=${!!kittyContainer.parent} animLayerChildren=${scene.animationLayer!.children.length}`);

        // Store kitty references
        scene.kittyContainers.set(visualPos, kittyContainer);
        scene.kittyChipsWrappers.set(visualPos, kittyDisplay.chipsWrapper);
        const kittyDataEntry = { denominations: [] as number[], total: 0 };
        scene.kittyData.set(visualPos, kittyDataEntry);
        const kittyChipSprites = new Map<number, KittyChipData>();
        scene.kittyChipSprites.set(visualPos, kittyChipSprites);
        scene.kittyCounterTexts.set(visualPos, kittyDisplay.counterText);
        scene.kittyPills.set(visualPos, kittyDisplay.pill);
        scene.kittyBasePositions.set(visualPos, { x: kittyPos.x, y: kittyPos.y, rotRad: kittyPosResult.rotationRad });

        // Get chip and border containers AND store as properties on kittyContainer
        // This is CRITICAL - animatePayoutToOpponentKitty needs to access these later
        const chipContainer = kittyDisplay.chipContainer;
        const borderContainer = kittyDisplay.borderContainer;
        (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).borderContainer = borderContainer;
        (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).chipContainer = chipContainer;

        // Cap and sort chips
        const { chips: cappedChips } = capChipsForAnimation(betChips);
        const sortedChips = [...cappedChips].sort((a, b) => a - b); // Ascending for stacking

        if (sortedChips.length === 0) {
          resolve();
          return;
        }

        // Animation settings
        const STAGGER_DELAY = 80;
        const DURATION = 200;
        const AVATAR_SIZE = 16; // Small starting size at avatar
        const KITTY_SIZE = KITTY_CONFIG.chipSize;

        let completedCount = 0;

        // Animate each chip with stagger
        sortedChips.forEach((denom, index) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) return;

          // Create chip sprite at avatar position (in animation layer)
          const chipSprite = new Sprite(chipTexture);
          chipSprite.label = 'chipSprite';
          chipSprite.anchor.set(0.5);
          chipSprite.width = AVATAR_SIZE;
          chipSprite.height = AVATAR_SIZE;
          chipSprite.x = avatarPos.x;
          chipSprite.y = avatarPos.y;
          chipSprite.rotation = Math.random() * Math.PI * 2;
          scene.animationLayer!.addChild(chipSprite);
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

          setTimeout(() => {
            const startTime = performance.now();
            const startX = chipSprite.x;
            const startY = chipSprite.y;

            const animate = () => {
              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / DURATION, 1);
              const eased = easeOutCubic(progress);

              chipSprite.x = startX + (kittyPos.x - startX) * eased;
              chipSprite.y = startY + (kittyPos.y - startY) * eased;
              chipSprite.width = AVATAR_SIZE + (KITTY_SIZE - AVATAR_SIZE) * eased;
              chipSprite.height = AVATAR_SIZE + (KITTY_SIZE - AVATAR_SIZE) * eased;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Sound: chipLand on opponent bet chip arrival at opponent kitty
                playTableSound('chipLand');
                // Animation complete - remove from flight tracking
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);

                // Add chip to kitty
                if (chipContainer && borderContainer && !chipSprite.destroyed) {
                  // Transfer sprite from animation layer to kitty
                  scene.animationLayer!.removeChild(chipSprite);
                  addChipToKitty(chipContainer, borderContainer, denom, chipSprite, kittyChipSprites, chipBorderConfig);
                  kittyDataEntry.denominations.push(denom);
                }

                // Update counter (opponent kitties don't show counters)
                kittyDataEntry.total += denom;
                updateKittyLayout(
                  kittyDisplay.pill,
                  kittyDisplay.counterText,
                  kittyChipSprites.size,
                  kittyDataEntry.total,
                  { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
                  true // isOpponentKitty - hide counter
                );

                completedCount++;
                if (completedCount === sortedChips.length) {
                  console.log(`[animateOpponentBetToKitty] Complete: seat ${seatPosition}, $${kittyDataEntry.total}`);
                  resolve();
                }
              }
            };

            requestAnimationFrame(animate);
          }, index * STAGGER_DELAY);
        });
      });
    },

    /**
     * Animate chips from dealer tray to opponent's kitty.
     * Used for opponent win payouts (dealer pays opponent).
     * Flow: Dealer Tray → Opponent Kitty
     */
    animatePayoutToOpponentKitty: (seatPosition: number, chips: number[]): Promise<void> => {
      console.log(`[CHIP AUDIT] Tray→OpponentKitty | seat=${seatPosition} | chips=${chips.join(',')} | count=${chips.length}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current || chips.length === 0) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Get existing opponent kitty
        const kittyContainer = scene.kittyContainers.get(visualPos);
        const kittyData = scene.kittyData.get(visualPos);
        const kittyChipSprites = scene.kittyChipSprites.get(visualPos);

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn(`[animatePayoutToOpponentKitty] No kitty found for visualPos=${visualPos}, seat=${seatPosition}`);
          resolve();
          return;
        }

        // Get kitty position and total rotation from container
        // Total rotation = container rotation (seat orientation) + chipsWrapper rotation (config offset)
        const kittyPos = { x: kittyContainer.x, y: kittyContainer.y };
        const containerRot = kittyContainer.rotation;
        const chipsWrapper = (kittyContainer as unknown as { chipsWrapper?: Container }).chipsWrapper;
        const wrapperRot = chipsWrapper?.rotation ?? 0;
        const rotRad = containerRot + wrapperRot;

        // Calculate tray position (same as animateChipFromDealer)
        const trayCalcX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
        const trayCalcY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
        const trayCalcScale = (trayConfig?.scale || 100) / 100;

        const TRAY_CHIP_OFFSETS: Record<number, { x: number; y: number }> = {
          1:     { x: -50, y: 29 },
          5:     { x: -37, y: 29 },
          25:    { x: -21, y: 28 },
          100:   { x: -8, y: 29 },
          500:   { x: 6, y: 28 },
          1000:  { x: 24, y: 30 },
          5000:  { x: 38, y: 30 },
          10000: { x: 50, y: 30 },
        };

        // Animation settings
        const STAGGER_DELAY = 80;
        const DURATION = 200;
        const TRAY_SIZE = CHIP_SIZES.dealerPile;
        const KITTY_SIZE = KITTY_CONFIG.chipSize;

        // Sort chips ascending for arrival animation (small first)
        const sortedChips = [...chips].sort((a, b) => a - b);
        let completedCount = 0;

        // Get chip and border containers from kitty
        const chipContainer = (kittyContainer as unknown as { chipContainer?: Container }).chipContainer;
        const borderContainer = (kittyContainer as unknown as { borderContainer?: Container }).borderContainer;

        // Track denominations for calculating chip stack positions
        // Start with existing denominations from kitty data
        const existingDenoms = [...(kittyData.denominations || [])];

        sortedChips.forEach((denom, index) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) {
            completedCount++;
            if (completedCount === sortedChips.length) resolve();
            return;
          }

          // Calculate tray start position for this denomination
          const denomOffset = TRAY_CHIP_OFFSETS[denom] || { x: 0, y: 30 };
          const trayPos = {
            x: trayCalcX + denomOffset.x * trayCalcScale,
            y: trayCalcY + denomOffset.y * trayCalcScale,
          };

          // Calculate denomination-specific target position in kitty stack
          // Include denominations from previous chips in this batch that will have landed by this chip's arrival
          const denomsForThisChip = [...existingDenoms, ...sortedChips.slice(0, index)];
          const chipLocalY = calculateKittyChipYOffset(denom, denomsForThisChip);
          const targetPos = kittyLocalToWorld(kittyPos.x, kittyPos.y, chipLocalY, rotRad);

          // Create chip sprite at tray position
          const chipSprite = new Sprite(chipTexture);
          chipSprite.label = 'opponentPayoutChip';
          chipSprite.anchor.set(0.5);
          chipSprite.width = TRAY_SIZE;
          chipSprite.height = TRAY_SIZE;
          chipSprite.x = trayPos.x;
          chipSprite.y = trayPos.y;
          chipSprite.rotation = Math.random() * Math.PI * 2;
          chipSprite.zIndex = 2000;
          scene.animationLayer!.addChild(chipSprite);
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

          setTimeout(() => {
            const startTime = performance.now();
            const startX = chipSprite.x;
            const startY = chipSprite.y;
            const startRotation = chipSprite.rotation;
            const endRotation = Math.random() * Math.PI * 2;

            const animate = () => {
              if (chipSprite.destroyed) {
                completedCount++;
                if (completedCount === sortedChips.length) resolve();
                return;
              }

              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / DURATION, 1);
              const eased = easeOutCubic(progress);

              chipSprite.x = startX + (targetPos.x - startX) * eased;
              chipSprite.y = startY + (targetPos.y - startY) * eased;
              chipSprite.width = TRAY_SIZE + (KITTY_SIZE - TRAY_SIZE) * eased;
              chipSprite.height = TRAY_SIZE + (KITTY_SIZE - TRAY_SIZE) * eased;
              chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Sound: chipLand on payout chip arrival at opponent kitty
                playTableSound('chipLand');
                // Animation complete - ALWAYS remove from animation layer first
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
                if (!chipSprite.destroyed) {
                  scene.animationLayer?.removeChild(chipSprite);
                }

                // Add chip to opponent's kitty if containers exist
                if (chipContainer && borderContainer && !chipSprite.destroyed) {
                  addChipToKitty(chipContainer, borderContainer, denom, chipSprite, kittyChipSprites, chipBorderConfig);
                  kittyData.denominations.push(denom);
                  kittyData.total += denom;
                } else if (!chipSprite.destroyed) {
                  // Containers missing - destroy the orphaned sprite
                  chipSprite.destroy();
                }

                completedCount++;
                if (completedCount === sortedChips.length) {
                  console.log(`[animatePayoutToOpponentKitty] Complete: seat ${seatPosition}, added $${chips.reduce((a, b) => a + b, 0)}`);
                  resolve();
                }
              }
            };

            requestAnimationFrame(animate);
          }, index * STAGGER_DELAY);
        });
      });
    },

    /**
     * Animate all chips from opponent's kitty to their avatar.
     * Uses the reusable animateChipFlight registry function.
     * Flow: Opponent Kitty → Avatar
     */
    animateOpponentKittyToAvatar: (seatPosition: number, chips: number[]): Promise<void> => {
      console.log(`[CHIP AUDIT] OpponentKitty→Avatar | seat=${seatPosition} | chips=${chips.join(',')} | count=${chips.length}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current || chips.length === 0) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Get seat data for avatar position calculation
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn(`[animateOpponentKittyToAvatar] No seat data for visualPos=${visualPos}`);
          resolve();
          return;
        }

        // Get existing opponent kitty
        const kittyContainer = scene.kittyContainers.get(visualPos);
        const kittyData = scene.kittyData.get(visualPos);
        const kittyChipSprites = scene.kittyChipSprites.get(visualPos);

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn(`[animateOpponentKittyToAvatar] No kitty found for visualPos=${visualPos}`);
          resolve();
          return;
        }

        // Calculate avatar position (END)
        const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const adjustedSeatData = calculateAdjustedSeatPosition(seatData, seatConfig);
        const endPos = calculateAvatarPosition(adjustedSeatData, seatConfig, seatSizeScale);

        // Calculate unified departure position (START) - top of kitty stack
        let startPos: { x: number; y: number };
        const existingDenoms = [...kittyChipSprites.keys()].sort((a, b) => a - b);
        if (existingDenoms.length > 0) {
          const topDenom = existingDenoms[existingDenoms.length - 1];
          const topChipData = kittyChipSprites.get(topDenom);
          if (topChipData) {
            const worldPos = topChipData.sprite.getGlobalPosition();
            startPos = scene.animationLayer!.toLocal(worldPos);
          } else {
            const kittyWorld = kittyContainer.getGlobalPosition();
            startPos = scene.animationLayer!.toLocal(kittyWorld);
          }
        } else {
          const kittyWorld = kittyContainer.getGlobalPosition();
          startPos = scene.animationLayer!.toLocal(kittyWorld);
        }

        // Animation settings
        const STAGGER_DELAY = 80;
        const DURATION = 200;
        const START_SIZE = KITTY_CONFIG.chipSize;
        const END_SIZE = 40; // Avatar chip size

        // Sort chips descending for departure (large first = top of stack first)
        const sortedChips = [...chips].sort((a, b) => b - a);

        // Get containers for kitty cleanup
        const containerData = kittyContainer as unknown as { chipContainer?: Container; borderContainer?: Container };

        // Track completion
        let completedCount = 0;
        const totalChips = sortedChips.length;

        // Cleanup function for each chip (called via onComplete)
        const cleanupKittyChip = (denom: number) => {
          const chipData = kittyChipSprites.get(denom);
          if (!chipData) return;

          if (chipData.count <= 1) {
            // Last chip of this denom - remove sprite and border
            containerData.chipContainer?.removeChild(chipData.sprite);
            containerData.borderContainer?.removeChild(chipData.border);
            if (!chipData.sprite.destroyed) chipData.sprite.destroy();
            if (!chipData.border.destroyed) chipData.border.destroy();
            kittyChipSprites.delete(denom);
          } else {
            // Decrement count, randomize rotation of revealed chip
            chipData.count--;
            chipData.sprite.rotation = Math.random() * 2 * Math.PI;
          }

          // Update kitty data tracking
          const denomIndex = kittyData.denominations.lastIndexOf(denom);
          if (denomIndex !== -1) {
            kittyData.denominations.splice(denomIndex, 1);
            kittyData.total -= denom;
          }
        };

        // Final cleanup when ALL chips done
        const finalizeAllChips = () => {
          // Reposition remaining chips once
          if (containerData.chipContainer && containerData.borderContainer && kittyChipSprites.size > 0) {
            repositionKittyChips(containerData.chipContainer, containerData.borderContainer, kittyChipSprites);
          }
          // Update kitty layout
          const pill = scene.kittyPills.get(visualPos);
          const counterText = scene.kittyCounterTexts.get(visualPos);
          if (pill && counterText) {
            updateKittyLayout(
              pill,
              counterText,
              kittyChipSprites.size,
              kittyData.total,
              { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
              true // isOpponentKitty
            );
          }
          console.log(`[animateOpponentKittyToAvatar] Complete: seat ${seatPosition}`);
          resolve();
        };

        // Animate each chip with stagger
        sortedChips.forEach((denom, index) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) {
            completedCount++;
            if (completedCount === totalChips) finalizeAllChips();
            return;
          }

          // Create clone sprite for animation
          const chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          chipSprite.label = 'opponentKittyToAvatar';

          // Stagger the animation start
          setTimeout(() => {
            animateChipFlight({
              sprite: chipSprite,
              animationLayer: scene.animationLayer!,
              startPos,
              endPos,
              startSize: START_SIZE,
              endSize: END_SIZE,
              duration: DURATION,
              zIndex: 3000 - index, // First chip highest, descending
              destroyOnComplete: true,
              debugLabel: `opponentKitty→Avatar-seat${seatPosition}`,
              onComplete: () => {
                // Sound: chipLand on opponent kitty to avatar arrival
                playTableSound('chipLand');
                // Clean up this chip from kitty tracking
                cleanupKittyChip(denom);

                completedCount++;
                if (completedCount === totalChips) {
                  finalizeAllChips();
                }
              },
            });
          }, index * STAGGER_DELAY);
        });
      });
    },

    /**
     * Animate chips from opponent's kitty to dealer tray.
     * Used for opponent losses (dealer collects).
     * Flow: Opponent Kitty → Dealer Tray
     */
    animateOpponentLossToDealer: (seatPosition: number, chips: number[]): Promise<void> => {
      console.log(`[CHIP AUDIT] OpponentKitty→Tray | seat=${seatPosition} | chips=${chips.join(',')} | count=${chips.length}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current || chips.length === 0) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Get existing opponent kitty
        const kittyContainer = scene.kittyContainers.get(visualPos);
        const kittyData = scene.kittyData.get(visualPos);
        const kittyChipSprites = scene.kittyChipSprites.get(visualPos);

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn(`[animateOpponentLossToDealer] No kitty found for visualPos=${visualPos}`);
          resolve();
          return;
        }

        // Get kitty position
        const kittyPos = { x: kittyContainer.x, y: kittyContainer.y };

        // Calculate tray position (same as animateChipToDealer)
        const trayCalcX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
        const trayCalcY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
        const trayCalcScale = (trayConfig?.scale || 100) / 100;

        const TRAY_CHIP_OFFSETS: Record<number, { x: number; y: number }> = {
          1:     { x: -50, y: 29 },
          5:     { x: -37, y: 29 },
          25:    { x: -21, y: 28 },
          100:   { x: -8, y: 29 },
          500:   { x: 6, y: 28 },
          1000:  { x: 24, y: 30 },
          5000:  { x: 38, y: 30 },
          10000: { x: 50, y: 30 },
        };

        // Animation settings
        const STAGGER_DELAY = 80;
        const DURATION = 200;
        const KITTY_SIZE = KITTY_CONFIG.chipSize;
        const TRAY_SIZE = CHIP_SIZES.dealerPile;

        // Sort chips descending for departure animation (large first)
        const sortedChips = [...chips].sort((a, b) => b - a);
        let completedCount = 0;

        // FIX: Calculate a UNIFIED departure position for ALL chips (top of stack)
        // This prevents staggering where chips depart from different Y positions
        let unifiedDeparturePos: { x: number; y: number } | null = null;
        const existingDenoms = [...kittyChipSprites.keys()].sort((a, b) => a - b);
        if (existingDenoms.length > 0) {
          const topDenom = existingDenoms[existingDenoms.length - 1]; // Largest denom is at top
          const topChipData = kittyChipSprites.get(topDenom);
          if (topChipData) {
            const worldPos = topChipData.sprite.getGlobalPosition();
            unifiedDeparturePos = {
              x: scene.animationLayer!.toLocal(worldPos).x,
              y: scene.animationLayer!.toLocal(worldPos).y
            };
          }
        }
        // Fallback to kitty container position if no chips found
        if (!unifiedDeparturePos) {
          const kittyWorld = kittyContainer.getGlobalPosition();
          unifiedDeparturePos = {
            x: scene.animationLayer!.toLocal(kittyWorld).x,
            y: scene.animationLayer!.toLocal(kittyWorld).y
          };
        }

        // Get chip container from kitty for deferred cleanup
        const containerData = kittyContainer as unknown as { chipContainer?: Container; borderContainer?: Container };

        sortedChips.forEach((denom, index) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) {
            completedCount++;
            if (completedCount === sortedChips.length) resolve();
            return;
          }

          // Calculate tray destination for this denomination
          const denomOffset = TRAY_CHIP_OFFSETS[denom] || { x: 0, y: 30 };
          const trayPos = {
            x: trayCalcX + denomOffset.x * trayCalcScale,
            y: trayCalcY + denomOffset.y * trayCalcScale,
          };

          // Check if chip exists in kitty (for deferred cleanup tracking)
          const existingChipData = kittyChipSprites.get(denom);
          const needsKittyCleanup = existingChipData && existingChipData.count > 0;

          // ALWAYS create a clone for animation - never take actual sprites
          // This keeps the kitty visually intact until each chip's animation completes
          const chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          chipSprite.x = unifiedDeparturePos!.x;
          chipSprite.y = unifiedDeparturePos!.y;
          chipSprite.width = KITTY_SIZE;
          chipSprite.height = KITTY_SIZE;
          chipSprite.label = 'opponentLossToDealer';
          chipSprite.zIndex = 3000 - index;
          chipSprite.rotation = Math.random() * Math.PI * 2;
          scene.animationLayer!.addChild(chipSprite);
          scene.animationLayer!.sortChildren();
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

          setTimeout(() => {
            const startTime = performance.now();
            const startX = chipSprite.x;
            const startY = chipSprite.y;
            const startRotation = chipSprite.rotation;
            const endRotation = Math.random() * Math.PI * 2;

            const animate = () => {
              if (chipSprite.destroyed) {
                completedCount++;
                if (completedCount === sortedChips.length) resolve();
                return;
              }

              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / DURATION, 1);
              const eased = easeOutCubic(progress);

              chipSprite.x = startX + (trayPos.x - startX) * eased;
              chipSprite.y = startY + (trayPos.y - startY) * eased;
              chipSprite.width = KITTY_SIZE + (TRAY_SIZE - KITTY_SIZE) * eased;
              chipSprite.height = KITTY_SIZE + (TRAY_SIZE - KITTY_SIZE) * eased;
              chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Sound: chipLand on opponent loss chip arrival at dealer tray
                playTableSound('chipLand');
                // Animation complete - fade out at dealer tray
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);

                const fadeOutDuration = 100;
                const fadeStart = performance.now();
                const fadeAnimate = () => {
                  if (chipSprite.destroyed) {
                    completedCount++;
                    if (completedCount === sortedChips.length) resolve();
                    return;
                  }

                  const fadeElapsed = performance.now() - fadeStart;
                  const fadeProgress = Math.min(fadeElapsed / fadeOutDuration, 1);
                  chipSprite.alpha = 1 - fadeProgress;

                  if (fadeProgress < 1) {
                    requestAnimationFrame(fadeAnimate);
                  } else {
                    scene.animationLayer?.removeChild(chipSprite);
                    chipSprite.destroy();

                    // DEFERRED: Update kitty visuals - but DON'T reposition until all done
                    if (needsKittyCleanup) {
                      const currentChipData = kittyChipSprites.get(denom);
                      if (currentChipData) {
                        if (currentChipData.count <= 1) {
                          // Last chip of this denom - remove sprite and border from kitty
                          containerData.chipContainer?.removeChild(currentChipData.sprite);
                          containerData.borderContainer?.removeChild(currentChipData.border);
                          if (!currentChipData.sprite.destroyed) currentChipData.sprite.destroy();
                          if (!currentChipData.border.destroyed) currentChipData.border.destroy();
                          kittyChipSprites.delete(denom);
                          // NOTE: Don't reposition here - wait until all animations complete
                        } else {
                          // Decrement count, randomize rotation of revealed chip
                          currentChipData.count--;
                          currentChipData.sprite.rotation = Math.random() * 2 * Math.PI;
                        }
                      }
                      // Update kitty data tracking
                      if (kittyData) {
                        const denomIndex = kittyData.denominations.lastIndexOf(denom);
                        if (denomIndex !== -1) {
                          kittyData.denominations.splice(denomIndex, 1);
                          kittyData.total -= denom;
                        }
                      }
                    }

                    completedCount++;
                    if (completedCount === sortedChips.length) {
                      // ALL animations complete - NOW reposition remaining chips once
                      if (containerData.chipContainer && containerData.borderContainer && kittyChipSprites.size > 0) {
                        repositionKittyChips(containerData.chipContainer, containerData.borderContainer, kittyChipSprites);
                      }
                      // Update kitty layout once at the end
                      const pill = scene.kittyPills.get(visualPos);
                      const counterText = scene.kittyCounterTexts.get(visualPos);
                      if (pill && counterText && kittyData) {
                        updateKittyLayout(
                          pill,
                          counterText,
                          kittyChipSprites.size,
                          kittyData.total,
                          { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
                          true // isOpponentKitty - hide counter
                        );
                      }
                      console.log(`[animateOpponentLossToDealer] Complete: seat ${seatPosition}`);
                      resolve();
                    }
                  }
                };
                requestAnimationFrame(fadeAnimate);
              }
            };

            requestAnimationFrame(animate);
          }, index * STAGGER_DELAY);
        });
      });
    },

    /**
     * Animate opponent double down - chips from avatar to EXISTING kitty.
     * Unlike animateOpponentBetToKitty, this does NOT clear the existing kitty.
     * Flow: Opponent Avatar → Existing Kitty (adds to bet)
     */
    animateOpponentDoubleDownChips: (seatPosition: number, chips: number[]): Promise<void> => {
      console.log(`[CHIP AUDIT] OpponentDoubleDown | seat=${seatPosition} | chips=${chips.join(',')} | count=${chips.length}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current || chips.length === 0) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Get EXISTING kitty - do NOT clear it
        const kittyContainer = scene.kittyContainers.get(visualPos);
        const kittyData = scene.kittyData.get(visualPos);
        const kittyChipSprites = scene.kittyChipSprites.get(visualPos);

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn(`[animateOpponentDoubleDownChips] No existing kitty for visualPos=${visualPos}, seat=${seatPosition}`);
          resolve();
          return;
        }

        // Get seat data for avatar position calculation
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn('[animateOpponentDoubleDownChips] No seat data for visualPos:', visualPos);
          resolve();
          return;
        }

        // Calculate avatar position (source)
        const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const adjustedSeatData = calculateAdjustedSeatPosition(seatData, seatConfig);
        const avatarPos = calculateAvatarPosition(adjustedSeatData, seatConfig, seatSizeScale);

        // Get kitty position and rotation for target calculation
        const kittyPos = { x: kittyContainer.x, y: kittyContainer.y };
        const containerRot = kittyContainer.rotation;
        const chipsWrapper = (kittyContainer as unknown as { chipsWrapper?: Container }).chipsWrapper;
        const wrapperRot = chipsWrapper?.rotation ?? 0;
        const rotRad = containerRot + wrapperRot;

        // Get chip and border containers from existing kitty
        const chipContainer = (kittyContainer as unknown as { chipContainer?: Container }).chipContainer;
        const borderContainer = (kittyContainer as unknown as { borderContainer?: Container }).borderContainer;

        // Animation settings
        const STAGGER_DELAY = 80;
        const DURATION = 200;
        const AVATAR_SIZE = 16;
        const KITTY_SIZE = KITTY_CONFIG.chipSize;

        // Sort chips ascending for arrival animation (small first)
        const sortedChips = [...chips].sort((a, b) => a - b);
        let completedCount = 0;

        // Track existing denominations for calculating chip stack positions
        const existingDenoms = [...(kittyData.denominations || [])];

        sortedChips.forEach((denom, index) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) {
            completedCount++;
            if (completedCount === sortedChips.length) resolve();
            return;
          }

          // Calculate target position in kitty stack (same logic as animatePayoutToOpponentKitty)
          const denomsForThisChip = [...existingDenoms, ...sortedChips.slice(0, index)];
          const chipLocalY = calculateKittyChipYOffset(denom, denomsForThisChip);
          const targetPos = kittyLocalToWorld(kittyPos.x, kittyPos.y, chipLocalY, rotRad);

          // Create chip sprite at avatar position
          const chipSprite = new Sprite(chipTexture);
          chipSprite.label = 'opponentDoubleDownChip';
          chipSprite.anchor.set(0.5);
          chipSprite.width = AVATAR_SIZE;
          chipSprite.height = AVATAR_SIZE;
          chipSprite.x = avatarPos.x;
          chipSprite.y = avatarPos.y;
          chipSprite.rotation = Math.random() * Math.PI * 2;
          chipSprite.zIndex = 2000;
          scene.animationLayer!.addChild(chipSprite);
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

          setTimeout(() => {
            const startTime = performance.now();
            const startX = chipSprite.x;
            const startY = chipSprite.y;
            const startRotation = chipSprite.rotation;
            const endRotation = Math.random() * Math.PI * 2;

            const animate = () => {
              if (chipSprite.destroyed) {
                completedCount++;
                if (completedCount === sortedChips.length) resolve();
                return;
              }

              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / DURATION, 1);
              const eased = easeOutCubic(progress);

              chipSprite.x = startX + (targetPos.x - startX) * eased;
              chipSprite.y = startY + (targetPos.y - startY) * eased;
              chipSprite.width = AVATAR_SIZE + (KITTY_SIZE - AVATAR_SIZE) * eased;
              chipSprite.height = AVATAR_SIZE + (KITTY_SIZE - AVATAR_SIZE) * eased;
              chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Sound: chipLand on opponent double down chip arrival at opponent kitty
                playTableSound('chipLand');
                // Animation complete - add chip to kitty
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);

                if (chipContainer && borderContainer && !chipSprite.destroyed) {
                  scene.animationLayer!.removeChild(chipSprite);
                  addChipToKitty(chipContainer, borderContainer, denom, chipSprite, kittyChipSprites, chipBorderConfig);
                  kittyData.denominations.push(denom);
                  kittyData.total += denom;
                } else {
                  chipSprite.destroy();
                }

                // Update layout (opponent kitties don't show counters)
                const pill = scene.kittyPills.get(visualPos);
                const counterText = scene.kittyCounterTexts.get(visualPos);
                if (pill && counterText) {
                  updateKittyLayout(
                    pill,
                    counterText,
                    kittyChipSprites.size,
                    kittyData.total,
                    { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
                    true // isOpponentKitty
                  );
                }

                completedCount++;
                if (completedCount === sortedChips.length) {
                  console.log(`[animateOpponentDoubleDownChips] Complete: seat ${seatPosition}, added $${chips.reduce((a, b) => a + b, 0)}`);
                  resolve();
                }
              }
            };

            requestAnimationFrame(animate);
          }, index * STAGGER_DELAY);
        });
      });
    },

    /**
     * Animate opponent surrender - simultaneous animation to dealer AND avatar.
     * Creates clone sprites so both animations run from the same source.
     * Flow: Opponent Kitty → Dealer Tray (loss) + Avatar (refund) SIMULTANEOUSLY
     */
    animateOpponentSurrenderChips: (seatPosition: number, lossChips: number[], refundChips: number[]): Promise<void> => {
      console.log(`[CHIP AUDIT] OpponentSurrender | seat=${seatPosition} | loss=${lossChips.length} | refund=${refundChips.length}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        const kittyContainer = scene.kittyContainers.get(visualPos);
        const kittyData = scene.kittyData.get(visualPos);
        const kittyChipSprites = scene.kittyChipSprites.get(visualPos);

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn('[animateOpponentSurrenderChips] No kitty for visualPos', visualPos);
          resolve();
          return;
        }

        // Get seat data for avatar position calculation
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn('[animateOpponentSurrenderChips] No seat data for visualPos:', visualPos);
          resolve();
          return;
        }

        // Calculate avatar position (refund destination)
        const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const adjustedSeatData = calculateAdjustedSeatPosition(seatData, seatConfig);
        const avatarPos = calculateAvatarPosition(adjustedSeatData, seatConfig, seatSizeScale);

        // Calculate dealer tray positions (loss destination)
        const trayCalcX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
        const trayCalcY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
        const trayCalcScale = (trayConfig?.scale || 100) / 100;

        const TRAY_CHIP_OFFSETS: Record<number, { x: number; y: number }> = {
          1:     { x: -50, y: 29 },
          5:     { x: -37, y: 29 },
          25:    { x: -21, y: 28 },
          100:   { x: -8, y: 29 },
          500:   { x: 6, y: 28 },
          1000:  { x: 24, y: 30 },
          5000:  { x: 38, y: 30 },
          10000: { x: 50, y: 30 },
        };

        // Combine all chips for processing (loss + refund)
        const allChips = [...lossChips, ...refundChips];
        const sortedChips = [...allChips].sort((a, b) => b - a); // Largest first

        const staggerDelay = 60;
        const duration = 300;
        const allPromises: Promise<void>[] = [];

        // Track denom counts for proper kitty management
        const denomCounts = new Map<number, number>();
        for (const denom of sortedChips) {
          denomCounts.set(denom, (denomCounts.get(denom) || 0) + 1);
        }

        // Determine which chips go where (maintain order)
        const lossSet = new Set(lossChips);
        const refundSet = new Set(refundChips);
        let lossCount = new Map<number, number>();
        let refundCount = new Map<number, number>();
        for (const denom of lossChips) {
          lossCount.set(denom, (lossCount.get(denom) || 0) + 1);
        }
        for (const denom of refundChips) {
          refundCount.set(denom, (refundCount.get(denom) || 0) + 1);
        }

        const containerData = kittyContainer as unknown as { chipContainer?: Container; borderContainer?: Container };

        // FIX: Calculate a UNIFIED departure position for ALL chips (top of stack)
        // This prevents staggering where chips depart from different Y positions
        let unifiedDeparturePos: { x: number; y: number } | null = null;
        const existingDenoms = [...kittyChipSprites.keys()].sort((a, b) => a - b);
        if (existingDenoms.length > 0) {
          const topDenom = existingDenoms[existingDenoms.length - 1]; // Largest denom is at top
          const topChipData = kittyChipSprites.get(topDenom);
          if (topChipData) {
            const worldPos = topChipData.sprite.getGlobalPosition();
            unifiedDeparturePos = {
              x: scene.animationLayer!.toLocal(worldPos).x,
              y: scene.animationLayer!.toLocal(worldPos).y
            };
          }
        }
        // Fallback to kitty container position if no chips found
        if (!unifiedDeparturePos) {
          const kittyWorld = kittyContainer.getGlobalPosition();
          unifiedDeparturePos = {
            x: scene.animationLayer!.toLocal(kittyWorld).x,
            y: scene.animationLayer!.toLocal(kittyWorld).y
          };
        }

        sortedChips.forEach((denom, index) => {
          const chipPromise = new Promise<void>((chipResolve) => {
            setTimeout(() => {
              const chipData = kittyChipSprites.get(denom);
              if (!chipData) {
                chipResolve();
                return;
              }

              const chipTexture = textures.get(`chip-${denom}`);
              if (!chipTexture) {
                chipResolve();
                return;
              }

              if (!chipData.sprite.parent) {
                chipResolve();
                return;
              }

              const sourceRotation = chipData.sprite.rotation;

              // Determine destination: is this chip going to dealer (loss) or avatar (refund)?
              const remainingLoss = lossCount.get(denom) || 0;
              const remainingRefund = refundCount.get(denom) || 0;
              let goesToDealer = false;

              if (remainingLoss > 0) {
                goesToDealer = true;
                lossCount.set(denom, remainingLoss - 1);
              } else if (remainingRefund > 0) {
                goesToDealer = false;
                refundCount.set(denom, remainingRefund - 1);
              } else {
                chipResolve();
                return;
              }

              // ALWAYS create a clone for animation - never modify kitty visuals until animation completes
              const chipSprite = new Sprite(chipTexture);
              chipSprite.label = 'opponentSurrenderChip';
              chipSprite.anchor.set(0.5);
              chipSprite.width = CHIP_SIZES.kittyMedium;
              chipSprite.height = CHIP_SIZES.kittyMedium;
              chipSprite.x = unifiedDeparturePos!.x;
              chipSprite.y = unifiedDeparturePos!.y;
              chipSprite.rotation = sourceRotation;
              chipSprite.zIndex = 3000 - index;
              scene.animationLayer?.addChild(chipSprite);
              MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

              // Calculate target position
              let targetPos: { x: number; y: number };
              let endSize: number;

              if (goesToDealer) {
                const denomOffset = TRAY_CHIP_OFFSETS[denom] || { x: 0, y: 30 };
                targetPos = {
                  x: trayCalcX + denomOffset.x * trayCalcScale,
                  y: trayCalcY + denomOffset.y * trayCalcScale,
                };
                endSize = CHIP_SIZES.dealerPile;
              } else {
                targetPos = { x: avatarPos.x, y: avatarPos.y };
                endSize = 40; // Avatar size
              }

              // Animate chip
              const startTime = performance.now();
              const startX = chipSprite.x;
              const startY = chipSprite.y;
              const startSize = CHIP_SIZES.kittyMedium;
              const startRotation = chipSprite.rotation;
              const endRotation = Math.random() * Math.PI * 2;

              const animate = () => {
                if (chipSprite.destroyed) {
                  chipResolve();
                  return;
                }

                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeOutCubic(progress);

                chipSprite.x = startX + (targetPos.x - startX) * eased;
                chipSprite.y = startY + (targetPos.y - startY) * eased;
                const size = startSize + (endSize - startSize) * eased;
                chipSprite.width = size;
                chipSprite.height = size;
                chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  // Sound: chipLand on opponent surrender chip arrival (shared branch for loss→tray and refund→avatar)
                  playTableSound('chipLand');
                  // Animation complete - destroy animation sprite
                  MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
                  scene.animationLayer?.removeChild(chipSprite);
                  chipSprite.destroy();

                  // DEFERRED: Update kitty visuals - but DON'T reposition until all done
                  const currentChipData = kittyChipSprites.get(denom);
                  if (currentChipData) {
                    const currentCount = denomCounts.get(denom) || 0;
                    if (currentCount <= 1) {
                      // Last chip of this denom - remove sprite and border from kitty
                      containerData.chipContainer?.removeChild(currentChipData.sprite);
                      containerData.borderContainer?.removeChild(currentChipData.border);
                      if (!currentChipData.sprite.destroyed) currentChipData.sprite.destroy();
                      if (!currentChipData.border.destroyed) currentChipData.border.destroy();
                      kittyChipSprites.delete(denom);
                      // NOTE: Don't reposition here - wait until all animations complete
                    } else {
                      // Decrement count, randomize rotation of revealed chip
                      currentChipData.count--;
                      currentChipData.sprite.rotation = Math.random() * 2 * Math.PI;
                    }
                    denomCounts.set(denom, (currentCount || 1) - 1);
                  }

                  // Update kitty data tracking
                  const kittyDenomIndex = kittyData.denominations.indexOf(denom);
                  if (kittyDenomIndex !== -1) {
                    kittyData.denominations.splice(kittyDenomIndex, 1);
                    kittyData.total -= denom;
                  }

                  chipResolve();
                }
              };
              requestAnimationFrame(animate);
            }, index * staggerDelay);
          });
          allPromises.push(chipPromise);
        });

        // Master timeout for safety
        const masterTimeout = setTimeout(() => {
          console.warn('[animateOpponentSurrenderChips] Master timeout reached');
          resolve();
        }, (sortedChips.length * staggerDelay) + duration + 1000);

        Promise.all(allPromises).then(() => {
          clearTimeout(masterTimeout);
          // ALL animations complete - NOW reposition remaining chips once
          if (containerData.chipContainer && containerData.borderContainer && kittyChipSprites.size > 0) {
            repositionKittyChips(containerData.chipContainer, containerData.borderContainer, kittyChipSprites);
          }
          // Update kitty layout once at the end
          const pill = scene.kittyPills.get(visualPos);
          const counterText = scene.kittyCounterTexts.get(visualPos);
          if (pill && counterText) {
            updateKittyLayout(
              pill,
              counterText,
              kittyChipSprites.size,
              kittyData.total,
              { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
              true // isOpponentKitty - hide counter
            );
          }
          if (kittyChipSprites.size === 0) {
            kittyContainer.visible = false;
          }
          resolve();
        });
      });
    },

    /**
     * Animate chip from player's kitty to rail.
     * Used for wins and pushes (chips return to player).
     * Flow: Player Kitty → Rail
     * @param handIndex Hand index (0 = main hand, 1+ = split hands)
     * @param zIndex Optional z-index for stacking order during departure (higher = on top)
     */
    animateChipFromKitty: (seatPosition: number, denomination: number, handIndex: number = 0, zIndex?: number, serverDuration?: number): Promise<void> => {
      console.log(`[CHIP AUDIT] Kitty→Rail | seat=${seatPosition} | hand=${handIndex} | denom=${denomination} | animated=1 sprite | duration=${serverDuration ?? 'default'}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Get the kitty container and data for this seat (main vs split)
        let kittyContainer: Container | undefined;
        let kittyData: { denominations: number[]; total: number } | undefined;
        let kittyChipSprites: Map<number, KittyChipData> | undefined;
        let pill: Graphics | undefined;
        let counterText: Text | undefined;

        if (handIndex === 0) {
          // Main hand - use regular kitty
          kittyContainer = scene.kittyContainers.get(visualPos);
          kittyData = scene.kittyData.get(visualPos);
          kittyChipSprites = scene.kittyChipSprites.get(visualPos);
          pill = scene.kittyPills.get(visualPos);
          counterText = scene.kittyCounterTexts.get(visualPos);
        } else {
          // Split hand - use split kitty
          const kittyKey = `${seatPosition}-${handIndex}`;
          kittyContainer = scene.splitKittyContainers.get(kittyKey);
          kittyData = scene.splitKittyData.get(kittyKey);
          kittyChipSprites = scene.splitKittyChipSprites.get(kittyKey);
          pill = scene.splitKittyPills.get(kittyKey);
          counterText = scene.splitKittyCounterTexts.get(kittyKey);
        }

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn(`[PixiGameTableV1] animateChipFromKitty: No kitty for seat=${seatPosition} hand=${handIndex}`);
          resolve();
          return;
        }

        // Get the KittyChipData for this denomination from the Map
        const chipData = kittyChipSprites.get(denomination);
        if (!chipData) {
          console.warn(`[PixiGameTableV1] animateChipFromKitty: No chip data for denom ${denomination} in hand ${handIndex}`);
          resolve();
          return;
        }

        const containerData = kittyContainer as unknown as { chipContainer?: Container; borderContainer?: Container };
        let chipSprite: Sprite;

        if (chipData.count > 1) {
          // More than one chip of this denomination - create a new sprite for animation
          // The visual chip stays in place
          chipData.count--;
          const chipTexture = textures.get(`chip-${denomination}`);
          if (!chipTexture) {
            console.warn('[PixiGameTableV1] animateChipFromKitty: No texture for denom', denomination);
            resolve();
            return;
          }
          // Check if source sprite still has a parent (may have been destroyed by React re-render)
          if (!chipData.sprite.parent) {
            console.warn('[PixiGameTableV1] animateChipFromKitty: Source sprite has no parent, skipping');
            resolve();
            return;
          }
          chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          chipSprite.width = CHIP_SIZES.kittyMedium;
          chipSprite.height = CHIP_SIZES.kittyMedium;
          // Position at the existing chip's position, converted to animationLayer local coordinates
          // (must match the layer where chip is added - animationLayer, for proper z-ordering with kitty)
          const existingWorldPos = chipData.sprite.getGlobalPosition();
          const existingLocalPos = scene.animationLayer!.toLocal(existingWorldPos);
          chipSprite.x = existingLocalPos.x;
          chipSprite.y = existingLocalPos.y;
          // Give each departing chip its own random rotation (copy from kitty sprite)
          chipSprite.rotation = chipData.sprite.rotation;
          // Randomize the remaining kitty sprite rotation (the chip revealed underneath)
          chipData.sprite.rotation = Math.random() * 2 * Math.PI;
        } else {
          // Last chip of this denomination - use the actual sprite and remove border
          chipSprite = chipData.sprite;
          // Check if sprite still has a parent (may have been destroyed by React re-render)
          if (!chipSprite.parent) {
            console.warn('[PixiGameTableV1] animateChipFromKitty: Last sprite has no parent, skipping');
            resolve();
            return;
          }
          // Get world position before removing from container, convert to animationLayer local
          // (must match the layer where chip is added - animationLayer, for proper z-ordering with kitty)
          const worldPos = chipSprite.getGlobalPosition();
          const localPos = scene.animationLayer!.toLocal(worldPos);
          containerData.chipContainer?.removeChild(chipSprite);
          containerData.borderContainer?.removeChild(chipData.border);
          chipData.border.destroy();
          chipSprite.x = localPos.x;
          chipSprite.y = localPos.y;
          // Remove from Map and reposition remaining chips and borders
          kittyChipSprites.delete(denomination);
          if (containerData.chipContainer && containerData.borderContainer) {
            repositionKittyChips(containerData.chipContainer, containerData.borderContainer, kittyChipSprites);
            // Randomize rotation of remaining revealed chips
            for (const [, data] of kittyChipSprites) {
              data.sprite.rotation = Math.random() * 2 * Math.PI;
            }
          }
        }

        // Get rail position from actual rail chip sprite (adapts to layout changes)
        const railChip = scene.chipRailChips.get(denomination);
        let railPos: { x: number; y: number };

        if (railChip && scene.chipRailContainer && scene.uiLayer) {
          // Get global position and convert to uiLayer local coordinates
          const globalPos = railChip.getGlobalPosition();
          const localPos = scene.uiLayer.toLocal(globalPos);
          railPos = { x: localPos.x, y: localPos.y };
        } else {
          // Fallback: calculate position if rail chip not found
          const railCalcScale = (chipRailConfig?.scale ?? 100) / 100;
          const railCalcX = UI_CHIP_RAIL_POS.x + (chipRailConfig?.x ?? 0);
          const railCalcY = UI_CHIP_RAIL_POS.y + (chipRailConfig?.y ?? 0);
          const railSpacing = chipRailConfig?.spacing ?? 85;
          const railTotalWidth = (CHIP_RAIL_DENOMS.length - 1) * railSpacing;
          const railStartX = -railTotalWidth / 2;
          const railArcHeight = 25;

          const railDenomIndex = CHIP_RAIL_DENOMS.indexOf(denomination);
          const railLocalX = railStartX + (railDenomIndex === -1 ? 0 : railDenomIndex * railSpacing);
          const railNormalizedPos = railDenomIndex === -1 ? 0 : (railDenomIndex - (CHIP_RAIL_DENOMS.length - 1) / 2) / ((CHIP_RAIL_DENOMS.length - 1) / 2);
          const railLocalY = railArcHeight * (railNormalizedPos * railNormalizedPos - 1);
          railPos = {
            x: railCalcX + railLocalX * railCalcScale,
            y: railCalcY + railLocalY * railCalcScale,
          };
        }

        // Remove chip from kitty data tracking
        const kittyDenomIndex = kittyData.denominations.indexOf(denomination);
        if (kittyDenomIndex !== -1) {
          kittyData.denominations.splice(kittyDenomIndex, 1);
          kittyData.total -= denomination;
        }

        // Add to animation layer for the flight (same layer as kitty, matching animateChipToDealer)
        // Use provided zIndex or default to 2000 (higher = rendered on top)
        chipSprite.zIndex = zIndex ?? 2000;
        scene.animationLayer?.addChild(chipSprite);

        // Update kitty layout (visual chip count is Map size)
        // (pill and counterText already captured above for correct main/split handling)
        const isOpponentKitty = (kittyContainer as unknown as { isOpponentKitty?: boolean })?.isOpponentKitty ?? false;
        if (pill && counterText) {
          const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
          updateKittyLayout(pill, counterText, kittyChipSprites.size, kittyData.total, counterOffset, isOpponentKitty);
        }

        const startX = chipSprite.x;
        const startY = chipSprite.y;
        const endX = railPos.x;
        const endY = railPos.y;

        // Scale: kittyMedium (28px) → rail (48px)
        const startSize = CHIP_SIZES.kittyMedium;
        const endSize = CHIP_SIZES.rail;

        // Rotation: start from current, end at random rotation (0-359 degrees)
        const startRotation = chipSprite.rotation;
        const endRotation = Math.random() * 2 * Math.PI;

        const startTime = performance.now();
        // Use server-provided duration, or fallback to cached config
        const duration = getAnimationDuration('chipFlight', serverDuration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateChipFromKitty',
          eventType: 'CHIPS_PAID',
        });

        // GUARANTEED RESOLUTION: Track if we've already resolved
        let resolved = false;

        // GUARANTEED RESOLUTION: Single finalize function
        const finalize = (reason: 'complete' | 'timeout' | 'destroyed') => {
          if (resolved) return;
          resolved = true;
          clearTimeout(safetyTimeout);

          if (reason !== 'complete') {
            recordAnimationFailure({
              fileName: 'PixiGameTableV1.tsx',
              functionName: 'animateChipFromKitty',
              reason: reason,
              animationType: 'chip-kitty-to-rail',
              targetDescription: `seat=${seatPosition} denom=${denomination}`,
              expectedDuration: duration,
            });
          }

          // Clean up sprite if it still exists
          if (chipSprite && !chipSprite.destroyed) {
            scene.animationLayer?.removeChild(chipSprite);
            chipSprite.destroy();
          }

          resolve();
        };

        // GUARANTEED RESOLUTION: Safety timeout (duration + fade + buffer)
        const safetyTimeout = setTimeout(() => {
          if (!resolved) finalize('timeout');
        }, duration + 150 + 500);

        const animateLinear = () => {
          // Already resolved - stop
          if (resolved) return;

          // Check if sprite was destroyed during animation
          if (!chipSprite || chipSprite.destroyed) {
            finalize('destroyed');
            return;
          }

          const elapsed = performance.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutCubic(progress);

          chipSprite.x = startX + (endX - startX) * eased;
          chipSprite.y = startY + (endY - startY) * eased;

          const currentSize = startSize + (endSize - startSize) * eased;
          chipSprite.width = currentSize;
          chipSprite.height = currentSize;

          // Rotate during flight
          chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

          if (progress < 1) {
            requestAnimationFrame(animateLinear);
          } else {
            // Animation complete - wrap in try-catch for guaranteed resolution
            try {
              // Sound: chipLand on landing (payout chip reaches rail)
              playTableSound('chipLand');
              // Update persistent rail chip rotation to match landed chip
              MODULE_RAIL_CHIP_ROTATIONS.set(denomination, endRotation);
              // Also update the visible rail chip sprite immediately (prevents rotation jump on next re-render)
              const railChipContainer = scene.chipRailChips.get(denomination);
              if (railChipContainer) {
                for (const child of railChipContainer.children) {
                  if (child instanceof Sprite) {
                    child.rotation = endRotation;
                    break;
                  }
                }
              }
              // Fade out after landing at rail
              const fadeDuration = getClientAnimationTiming('chipFadeOut', 'animateChipFromKitty');
              animateSprite(chipSprite, { alpha: 0 }, {
                duration: fadeDuration,
                debugName: `chipFromKitty-fade-${denomination}`,
                onComplete: () => {
                  if (resolved) return;
                  resolved = true;
                  clearTimeout(safetyTimeout);
                  if (chipSprite && !chipSprite.destroyed) {
                    scene.animationLayer?.removeChild(chipSprite);
                    chipSprite.destroy();
                  }
                  resolve();
                },
              });
            } catch (err) {
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'animateChipFromKitty.completion',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'chip-kitty-to-rail',
                targetDescription: `seat=${seatPosition} denom=${denomination}`,
                expectedDuration: duration,
              });
              finalize('destroyed');
            }
          }
        };

        requestAnimationFrame(animateLinear);
      });
    },

    /**
     * Animate chip from player's kitty to dealer tray.
     * Used for losses (dealer collects chips).
     * Flow: Player Kitty → Dealer Tray (denomination-specific slot)
     * @param handIndex Hand index (0 = main hand, 1+ = split hands)
     * @param zIndex Optional z-index for stacking order during departure (higher = on top)
     */
    animateChipToDealer: (seatPosition: number, denomination: number, handIndex: number = 0, zIndex?: number, serverDuration?: number): Promise<void> => {
      console.log(`[CHIP AUDIT] Kitty→Tray | seat=${seatPosition} | hand=${handIndex} | denom=${denomination} | animated=1 sprite | duration=${serverDuration ?? 'default'}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Get the kitty container and data for this seat (main vs split)
        let kittyContainer: Container | undefined;
        let kittyData: { denominations: number[]; total: number } | undefined;
        let kittyChipSprites: Map<number, KittyChipData> | undefined;
        let pill: Graphics | undefined;
        let counterText: Text | undefined;

        if (handIndex === 0) {
          // Main hand - use regular kitty
          kittyContainer = scene.kittyContainers.get(visualPos);
          kittyData = scene.kittyData.get(visualPos);
          kittyChipSprites = scene.kittyChipSprites.get(visualPos);
          pill = scene.kittyPills.get(visualPos);
          counterText = scene.kittyCounterTexts.get(visualPos);
        } else {
          // Split hand - use split kitty
          const kittyKey = `${seatPosition}-${handIndex}`;
          kittyContainer = scene.splitKittyContainers.get(kittyKey);
          kittyData = scene.splitKittyData.get(kittyKey);
          kittyChipSprites = scene.splitKittyChipSprites.get(kittyKey);
          pill = scene.splitKittyPills.get(kittyKey);
          counterText = scene.splitKittyCounterTexts.get(kittyKey);
        }

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn(`[PixiGameTableV1] animateChipToDealer: No kitty for seat=${seatPosition} hand=${handIndex}`);
          resolve();
          return;
        }

        // Get the KittyChipData for this denomination from the Map
        const chipData = kittyChipSprites.get(denomination);
        if (!chipData) {
          console.warn(`[PixiGameTableV1] animateChipToDealer: No chip data for denom ${denomination} in hand ${handIndex}`);
          resolve();
          return;
        }

        // Calculate tray chip position directly (same as tray chip rendering)
        const trayCalcX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
        const trayCalcY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
        const trayCalcScale = (trayConfig?.scale || 100) / 100;

        const TRAY_CHIP_OFFSETS: Record<number, { x: number; y: number }> = {
          1:     { x: -50, y: 29 },
          5:     { x: -37, y: 29 },
          25:    { x: -21, y: 28 },
          100:   { x: -8, y: 29 },
          500:   { x: 6, y: 28 },
          1000:  { x: 24, y: 30 },
          5000:  { x: 38, y: 30 },
          10000: { x: 50, y: 30 },
        };

        const denomOffset = TRAY_CHIP_OFFSETS[denomination] || { x: 0, y: 30 };
        const trayPos = {
          x: trayCalcX + denomOffset.x * trayCalcScale,
          y: trayCalcY + denomOffset.y * trayCalcScale,
        };

        const containerData = kittyContainer as unknown as { chipContainer?: Container; borderContainer?: Container };
        let chipSprite: Sprite;

        if (chipData.count > 1) {
          // More than one chip of this denomination - create a new sprite for animation
          // The visual chip stays in place
          chipData.count--;
          const chipTexture = textures.get(`chip-${denomination}`);
          if (!chipTexture) {
            console.warn('[PixiGameTableV1] animateChipToDealer: No texture for denom', denomination);
            resolve();
            return;
          }
          // Check if source sprite still has a parent (may have been destroyed by React re-render)
          if (!chipData.sprite.parent) {
            console.warn('[PixiGameTableV1] animateChipToDealer: Source sprite has no parent, skipping');
            resolve();
            return;
          }
          chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          chipSprite.width = CHIP_SIZES.kittyMedium;
          chipSprite.height = CHIP_SIZES.kittyMedium;
          // Position at the existing chip's position, converted to animationLayer local coordinates
          // (must match the layer where chip is added - animationLayer, for proper z-ordering with kitty)
          const existingWorldPos = chipData.sprite.getGlobalPosition();
          const existingLocalPos = scene.animationLayer!.toLocal(existingWorldPos);
          chipSprite.x = existingLocalPos.x;
          chipSprite.y = existingLocalPos.y;
          // Give each departing chip its own random rotation (copy from kitty sprite)
          chipSprite.rotation = chipData.sprite.rotation;
          // Randomize the remaining kitty sprite rotation (the chip revealed underneath)
          chipData.sprite.rotation = Math.random() * 2 * Math.PI;
        } else {
          // Last chip of this denomination - use the actual sprite and remove border
          chipSprite = chipData.sprite;
          // Check if sprite still has a parent (may have been destroyed by React re-render)
          if (!chipSprite.parent) {
            console.warn('[PixiGameTableV1] animateChipToDealer: Last sprite has no parent, skipping');
            resolve();
            return;
          }
          // Get world position before removing from container, convert to animationLayer local
          // (must match the layer where chip is added - animationLayer, for proper z-ordering with kitty)
          const worldPos = chipSprite.getGlobalPosition();
          const localPos = scene.animationLayer!.toLocal(worldPos);
          containerData.chipContainer?.removeChild(chipSprite);
          containerData.borderContainer?.removeChild(chipData.border);
          chipData.border.destroy();
          chipSprite.x = localPos.x;
          chipSprite.y = localPos.y;
          // Remove from Map and reposition remaining chips and borders
          kittyChipSprites.delete(denomination);
          if (containerData.chipContainer && containerData.borderContainer) {
            repositionKittyChips(containerData.chipContainer, containerData.borderContainer, kittyChipSprites);
            // Randomize rotation of remaining revealed chips
            for (const [, data] of kittyChipSprites) {
              data.sprite.rotation = Math.random() * 2 * Math.PI;
            }
          }
        }

        // Remove chip from kitty data tracking
        const denomIndex = kittyData.denominations.indexOf(denomination);
        if (denomIndex !== -1) {
          kittyData.denominations.splice(denomIndex, 1);
          kittyData.total -= denomination;
        }

        // Add to animation layer for the flight
        // Use provided zIndex or default to 2000 (higher = rendered on top)
        chipSprite.zIndex = zIndex ?? 2000;
        scene.animationLayer?.addChild(chipSprite);
        MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

        // Update kitty layout (visual chip count is Map size)
        // (pill and counterText already captured above for correct main/split handling)
        const isOpponentKitty = (kittyContainer as unknown as { isOpponentKitty?: boolean })?.isOpponentKitty ?? false;
        if (pill && counterText) {
          const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
          updateKittyLayout(pill, counterText, kittyChipSprites.size, kittyData.total, counterOffset, isOpponentKitty);
        }

        const startX = chipSprite.x;
        const startY = chipSprite.y;
        const endX = trayPos.x;
        const endY = trayPos.y;

        // Scale: kittyMedium (28px) → dealerPile (20px)
        const startSize = CHIP_SIZES.kittyMedium;
        const endSize = CHIP_SIZES.dealerPile;

        // Rotation: start from current, end at random rotation (0-359 degrees)
        const startRotation = chipSprite.rotation;
        const endRotation = Math.random() * 2 * Math.PI;

        const startTime = performance.now();
        // Use server-provided duration, or fallback to cached config
        const duration = getAnimationDuration('chipFlight', serverDuration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateChipToDealer',
          eventType: 'CHIPS_COLLECTED',
        });

        // GUARANTEED RESOLUTION: Track if we've already resolved
        let resolved = false;

        // GUARANTEED RESOLUTION: Single finalize function
        const finalize = (reason: 'complete' | 'timeout' | 'destroyed') => {
          if (resolved) return;
          resolved = true;
          clearTimeout(safetyTimeout);

          if (reason !== 'complete') {
            recordAnimationFailure({
              fileName: 'PixiGameTableV1.tsx',
              functionName: 'animateChipToDealer',
              reason: reason,
              animationType: 'chip-kitty-to-dealer',
              targetDescription: `seat=${seatPosition} denom=${denomination}`,
              expectedDuration: duration,
            });
          }

          // Clean up sprite if it still exists
          if (chipSprite && !chipSprite.destroyed) {
            MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
            scene.animationLayer?.removeChild(chipSprite);
            chipSprite.destroy();
          }

          resolve();
        };

        // GUARANTEED RESOLUTION: Safety timeout (duration + fade + buffer)
        const safetyTimeout = setTimeout(() => {
          if (!resolved) finalize('timeout');
        }, duration + 150 + 500);

        const animateLinear = () => {
          // Already resolved - stop
          if (resolved) return;

          // Check if sprite was destroyed during animation
          if (!chipSprite || chipSprite.destroyed) {
            finalize('destroyed');
            return;
          }

          const elapsed = performance.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutCubic(progress);

          chipSprite.x = startX + (endX - startX) * eased;
          chipSprite.y = startY + (endY - startY) * eased;

          const currentSize = startSize + (endSize - startSize) * eased;
          chipSprite.width = currentSize;
          chipSprite.height = currentSize;

          // Rotate during flight
          chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

          if (progress < 1) {
            requestAnimationFrame(animateLinear);
          } else {
            // Animation complete - wrap in try-catch for guaranteed resolution
            try {
              // Sound: chipLand on landing (chip swept to dealer)
              playTableSound('chipLand');
              // Update persistent tray chip to match landing rotation
              const trayChip = scene.trayChipSprites.get(denomination);
              if (trayChip) {
                trayChip.rotation = endRotation;
              }
              // Fade out after landing at dealer tray
              const fadeOutDuration = getClientAnimationTiming('chipFadeOut', 'animateChipToDealer');
              animateSprite(chipSprite, { alpha: 0 }, {
                duration: fadeOutDuration,
                debugName: `chipToDealer-fade-${denomination}`,
                onComplete: () => {
                  if (resolved) return;
                  resolved = true;
                  clearTimeout(safetyTimeout);
                  if (chipSprite && !chipSprite.destroyed) {
                    MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
                    scene.animationLayer?.removeChild(chipSprite);
                    chipSprite.destroy();
                  }
                  resolve();
                },
              });
            } catch (err) {
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'animateChipToDealer.completion',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'chip-kitty-to-dealer',
                targetDescription: `seat=${seatPosition} denom=${denomination}`,
                expectedDuration: duration,
              });
              finalize('destroyed');
            }
          }
        };

        requestAnimationFrame(animateLinear);
      });
    },

    /**
     * Animate chip from dealer tray to player's kitty.
     * Used for payouts (dealer pays player).
     * Flow: Dealer Tray (denomination-specific slot) → Player Kitty
     * Counter upticks as each chip lands.
     * @param handIndex Hand index (0 = main hand, 1+ = split hands)
     */
    animateChipFromDealer: (seatPosition: number, denomination: number, handIndex: number = 0, serverDuration?: number): Promise<void> => {
      console.log(`[CHIP AUDIT] Tray→Kitty | seat=${seatPosition} | hand=${handIndex} | denom=${denomination} | animated=1 sprite | duration=${serverDuration ?? 'default'}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        // Calculate tray chip position directly (same as tray chip rendering)
        // This ensures animation starts from the actual rendered tray chips
        const trayCalcX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
        const trayCalcY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
        const trayCalcScale = (trayConfig?.scale || 100) / 100;

        // Tray chip positions for each denomination (same as rendering)
        const TRAY_CHIP_OFFSETS: Record<number, { x: number; y: number }> = {
          1:     { x: -50, y: 29 },
          5:     { x: -37, y: 29 },
          25:    { x: -21, y: 28 },
          100:   { x: -8, y: 29 },
          500:   { x: 6, y: 28 },
          1000:  { x: 24, y: 30 },
          5000:  { x: 38, y: 30 },
          10000: { x: 50, y: 30 },
        };

        const denomOffset = TRAY_CHIP_OFFSETS[denomination] || { x: 0, y: 30 };
        const trayPos = {
          x: trayCalcX + denomOffset.x * trayCalcScale,
          y: trayCalcY + denomOffset.y * trayCalcScale,
        };

        console.log('[animateChipFromDealer] Tray position for denom', denomination, ':', trayPos);

        // Calculate kitty position from dynamicSeatPositions
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn('[PixiGameTableV1] animateChipFromDealer: No seat data for visualPos', visualPos);
          resolve();
          return;
        }

        // Get betChipConfig for this seat (applies DEV CONTROLS offsets)
        // Use seatPosition for config lookup (UI keys by seat number, not visual position)
        const betConfig = betChipConfigs[seatPosition] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };

        // Base local offset with DEV CONTROLS offsets applied + kittyConfig offsets
        const baseLocalOffsetY = KITTY_BASE_RADIAL_OFFSET + betConfig.radiusOffset;
        const rotRad = degreesToRadians(seatData.rotation);
        const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);
        const kittyPos = {
          x: basePos.x + betConfig.x + kittyConfig.x,
          y: basePos.y + betConfig.y + kittyConfig.y,
        };

        // Get the kitty container and data for this seat (main vs split)
        let kittyContainer: Container | undefined;
        let kittyData: { denominations: number[]; total: number } | undefined;
        let kittyChipSprites: Map<number, KittyChipData> | undefined;
        let kittyCounterText: Text | undefined;
        let kittyPill: Graphics | undefined;

        if (handIndex === 0) {
          // Main hand - use regular kitty
          kittyContainer = scene.kittyContainers.get(visualPos);
          kittyData = scene.kittyData.get(visualPos);
          kittyChipSprites = scene.kittyChipSprites.get(visualPos);
          kittyCounterText = scene.kittyCounterTexts.get(visualPos);
          kittyPill = scene.kittyPills.get(visualPos);
        } else {
          // Split hand - use split kitty (should already exist from the split action)
          const kittyKey = `${seatPosition}-${handIndex}`;
          kittyContainer = scene.splitKittyContainers.get(kittyKey);
          kittyData = scene.splitKittyData.get(kittyKey);
          kittyChipSprites = scene.splitKittyChipSprites.get(kittyKey);
          kittyCounterText = scene.splitKittyCounterTexts.get(kittyKey);
          kittyPill = scene.splitKittyPills.get(kittyKey);

          if (!kittyContainer) {
            console.warn(`[animateChipFromDealer] Split kitty not found for key=${kittyKey}, falling back to main kitty`);
            // Fallback to main kitty if split kitty doesn't exist (shouldn't happen in normal flow)
            kittyContainer = scene.kittyContainers.get(visualPos);
            kittyData = scene.kittyData.get(visualPos);
            kittyChipSprites = scene.kittyChipSprites.get(visualPos);
            kittyCounterText = scene.kittyCounterTexts.get(visualPos);
            kittyPill = scene.kittyPills.get(visualPos);
          }
        }

        if (!kittyContainer) {
          // Create new kitty display with DEV CONTROLS counter config (only for main hand)
          console.log('[animateChipFromDealer] Creating NEW kitty display for visualPos:', visualPos);

          // Store base position (before any centering offset) for centerline calculations
          scene.kittyBasePositions.set(visualPos, { x: kittyPos.x, y: kittyPos.y, rotRad });

          const kittyDisplay = createKittyDisplay(textures, chipCounterConfig);
          kittyContainer = kittyDisplay.container;
          kittyContainer.x = kittyPos.x;
          kittyContainer.y = kittyPos.y;
          kittyContainer.rotation = rotRad; // Seat rotation only - counter stays oriented to seat
          kittyContainer.zIndex = 1500;

          // Apply extra rotation to chipsWrapper only (chips rotate, counter stays in place)
          const extraRotRad = degreesToRadians(kittyConfig.rotation || 0);
          kittyDisplay.chipsWrapper.rotation = extraRotRad;

          // Store seatPosition for config lookup (betChipConfigs is keyed by seat, not visual position)
          (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).isKittyElement = true;
          (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).seatPosition = seatPosition;

          scene.animationLayer?.addChild(kittyContainer);
          scene.kittyContainers.set(visualPos, kittyContainer);
          scene.kittyChipsWrappers.set(visualPos, kittyDisplay.chipsWrapper);

          kittyData = { denominations: [], total: 0 };
          scene.kittyData.set(visualPos, kittyData);

          kittyChipSprites = new Map<number, KittyChipData>();
          scene.kittyChipSprites.set(visualPos, kittyChipSprites);

          kittyCounterText = kittyDisplay.counterText;
          scene.kittyCounterTexts.set(visualPos, kittyCounterText);

          kittyPill = kittyDisplay.pill;
          scene.kittyPills.set(visualPos, kittyPill);

          (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).borderContainer = kittyDisplay.borderContainer;
          (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).chipContainer = kittyDisplay.chipContainer;
        }

        // Create chip at tray position (start at dealer pile size)
        const chipSprite = createChipSprite(
          denomination,
          textures,
          trayPos.x,
          trayPos.y
        );
        if (!chipSprite) {
          resolve();
          return;
        }

        // Start at dealer pile size, scale up to kitty size
        chipSprite.width = CHIP_SIZES.dealerPile;
        chipSprite.height = CHIP_SIZES.dealerPile;
        chipSprite.zIndex = 2000;

        scene.animationLayer?.addChild(chipSprite);
        MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

        const startX = chipSprite.x;
        const startY = chipSprite.y;

        // Calculate denomination-specific target position in kitty
        // Get existing denominations to determine where this chip will land
        const existingDenoms = kittyData?.denominations || [];
        const chipLocalY = calculateKittyChipYOffset(denomination, existingDenoms);

        // For split hands, use the actual kitty container's position (not calculated base position)
        // This ensures chips animate to the correct split kitty location
        const targetKittyPos = (handIndex > 0 && kittyContainer)
          ? { x: kittyContainer.x, y: kittyContainer.y }
          : kittyPos;

        // Transform local Y offset to global coordinates using kitty rotation
        const endPos = kittyLocalToWorld(targetKittyPos.x, targetKittyPos.y, chipLocalY, rotRad);

        // Scale: dealerPile (20px) → kittyMedium (28px)
        const startSize = CHIP_SIZES.dealerPile;
        const endSize = CHIP_SIZES.kittyMedium;

        // Rotation: departing chip copies tray chip's current rotation
        const trayChip = scene.trayChipSprites.get(denomination);
        const startRotation = trayChip?.rotation ?? 0;
        chipSprite.rotation = startRotation;
        // Randomize the tray chip rotation (the chip revealed underneath)
        if (trayChip) {
          trayChip.rotation = Math.random() * 2 * Math.PI;
        }
        const endRotation = Math.random() * 2 * Math.PI;

        const startTime = performance.now();
        // Use server-provided duration, or fallback to cached config
        const duration = getAnimationDuration('chipFlight', serverDuration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateChipFromDealer',
          eventType: 'CHIPS_PAID',
        });
        let counterUpdated = false; // Track if counter has been updated (at 60% progress)

        // GUARANTEED RESOLUTION: Track if we've already resolved
        let resolved = false;

        // GUARANTEED RESOLUTION: Single finalize function
        const finalize = (reason: 'complete' | 'timeout' | 'destroyed') => {
          if (resolved) return;
          resolved = true;
          clearTimeout(safetyTimeout);

          if (reason !== 'complete') {
            recordAnimationFailure({
              fileName: 'PixiGameTableV1.tsx',
              functionName: 'animateChipFromDealer',
              reason: reason,
              animationType: 'chip-dealer-to-kitty',
              targetDescription: `seat=${seatPosition} denom=${denomination}`,
              expectedDuration: duration,
            });
          }

          // Clean up sprite if it still exists
          if (chipSprite && !chipSprite.destroyed) {
            MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
            scene.animationLayer?.removeChild(chipSprite);
            chipSprite.destroy();
          }

          resolve();
        };

        // GUARANTEED RESOLUTION: Safety timeout
        const safetyTimeout = setTimeout(() => {
          if (!resolved) finalize('timeout');
        }, duration + 500);

        const animateLinear = () => {
          // Already resolved - stop
          if (resolved) return;

          // Check if sprite was destroyed during animation
          if (!chipSprite || chipSprite.destroyed) {
            finalize('destroyed');
            return;
          }

          // Check if kitty container was destroyed (can happen during fullVisualResync)
          if (kittyContainer && kittyContainer.destroyed) {
            finalize('destroyed');
            return;
          }

          const elapsed = performance.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutCubic(progress);

          // Straight line from dealer tray to kitty
          chipSprite.x = startX + (endPos.x - startX) * eased;
          chipSprite.y = startY + (endPos.y - startY) * eased;

          // Scale up during flight
          const currentSize = startSize + (endSize - startSize) * eased;
          chipSprite.width = currentSize;
          chipSprite.height = currentSize;

          // Rotate during flight
          chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

          // Update counter at 60% progress (ensures last chip registers before completion)
          if (progress >= 0.5 && !counterUpdated) {
            counterUpdated = true;
            // Guard against destroyed objects (can happen if fullVisualResync runs mid-animation)
            if (kittyData && kittyCounterText && !kittyCounterText.destroyed && kittyPill && !kittyPill.destroyed && kittyChipSprites) {
              // Update tracking data
              kittyData.denominations.push(denomination);
              kittyData.denominations.sort((a, b) => a - b);
              kittyData.total += denomination;

              // Update layout - use denominations.length since chip hasn't been added to map yet
              const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
              updateKittyLayout(kittyPill, kittyCounterText, kittyData.denominations.length, kittyData.total, counterOffset);

              // Make kitty visible
              if (kittyContainer && !kittyContainer.destroyed) {
                kittyContainer.visible = true;
              }

              // Counter uptick animation (scale pulse) - only if text still valid
              if (!kittyCounterText.destroyed) {
                kittyCounterText.scale.set(1.3, 1.3);
                const scaleDuration = getClientAnimationTiming('chipLandingScale', 'animateChipFromDealer');
                animateSprite(kittyCounterText, {
                  scaleX: 1.0,
                  scaleY: 1.0,
                }, {
                  duration: scaleDuration,
                  easing: easeOutCubic,
                  debugName: `chipFromDealer-counter-pulse`,
                });
              }
            }
          }

          if (progress < 1) {
            requestAnimationFrame(animateLinear);
          } else {
            // GUARANTEED RESOLUTION: Wrap completion in try-catch
            if (resolved) return;
            resolved = true;
            clearTimeout(safetyTimeout);

            try {
              // Chip has landed - add to persistent kitty display (like animateRailToKitty)
              // Sound: chip arrives in the player kitty from the dealer tray (payout)
              playTableSound('chipLand');
              MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
              scene.animationLayer?.removeChild(chipSprite);

              const containerData = kittyContainer as unknown as { borderContainer: Container; chipContainer: Container };
              const chipContainer = containerData.chipContainer;
              const borderContainer = containerData.borderContainer;

              if (chipContainer && borderContainer && kittyChipSprites && chipSprite && !chipSprite.destroyed) {
                // Add chip to kitty using Map-based system (max 8 visual chips, one per denomination)
                addChipToKitty(chipContainer, borderContainer, denomination, chipSprite, kittyChipSprites, chipBorderConfig);
              }
            } catch (err) {
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'animateChipFromDealer.completion',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'chip-dealer-to-kitty',
                targetDescription: `seat=${seatPosition} denom=${denomination}`,
                expectedDuration: duration,
              });
            }
            resolve();
          }
        };

        requestAnimationFrame(animateLinear);
      });
    },

    /**
     * Animate surrender - FULL chip stack flies to BOTH rail AND dealer simultaneously.
     * Creates clone sprites so both animations can run from the same source.
     * The original chips are hidden/removed, clones animate to both destinations.
     */
    animateSurrenderChips: (seatPosition: number, denominations: number[]): Promise<void> => {
      console.log(`[CHIP AUDIT] Surrender | seat=${seatPosition} | chips=${denominations.length}`);
      return new Promise((resolve) => {
        if (!appRef.current || loadingRef.current) {
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);

        const kittyContainer = scene.kittyContainers.get(visualPos);
        const kittyData = scene.kittyData.get(visualPos);
        const kittyChipSprites = scene.kittyChipSprites.get(visualPos);

        if (!kittyContainer || !kittyData || !kittyChipSprites) {
          console.warn('[PixiGameTableV1] animateSurrenderChips: No kitty for visualPos', visualPos);
          resolve();
          return;
        }

        // Sort chips largest to smallest for animation order (largest depart first)
        const sortedDenoms = [...denominations].sort((a, b) => b - a);

        // Pre-calculate target positions
        // Rail positions
        const railPositions = new Map<number, { x: number; y: number }>();
        for (const denom of sortedDenoms) {
          const railChip = scene.chipRailChips.get(denom);
          if (railChip && scene.uiLayer) {
            const globalPos = railChip.getGlobalPosition();
            const localPos = scene.uiLayer.toLocal(globalPos);
            railPositions.set(denom, { x: localPos.x, y: localPos.y });
          }
        }

        // Dealer tray positions
        const trayCalcX = KITTY_POSITION.x + (trayConfig?.x || 0) + trayChipsOffset.x;
        const trayCalcY = KITTY_POSITION.y + (trayConfig?.y || 0) + trayChipsOffset.y;
        const trayCalcScale = (trayConfig?.scale || 100) / 100;

        const TRAY_CHIP_OFFSETS: Record<number, { x: number; y: number }> = {
          1:     { x: -50, y: 29 },
          5:     { x: -37, y: 29 },
          25:    { x: -21, y: 28 },
          100:   { x: -8, y: 29 },
          500:   { x: 6, y: 28 },
          1000:  { x: 24, y: 30 },
          5000:  { x: 38, y: 30 },
          10000: { x: 50, y: 30 },
        };

        const dealerPositions = new Map<number, { x: number; y: number }>();
        for (const denom of sortedDenoms) {
          const denomOffset = TRAY_CHIP_OFFSETS[denom] || { x: 0, y: 30 };
          dealerPositions.set(denom, {
            x: trayCalcX + denomOffset.x * trayCalcScale,
            y: trayCalcY + denomOffset.y * trayCalcScale,
          });
        }

        const staggerDelay = 60;
        const duration = 300;
        const allPromises: Promise<void>[] = [];

        // Track which denoms we've processed to handle count properly
        const denomCounts = new Map<number, number>();
        for (const denom of sortedDenoms) {
          denomCounts.set(denom, (denomCounts.get(denom) || 0) + 1);
        }

        // Get references for counter updates
        const pill = scene.kittyPills.get(visualPos);
        const counterText = scene.kittyCounterTexts.get(visualPos);
        const containerData = kittyContainer as unknown as { chipContainer?: Container; borderContainer?: Container };

        // Process each chip with staggered departure
        sortedDenoms.forEach((denom, index) => {
          const chipPromise = new Promise<void>((chipResolve) => {
            // Stagger the departure of each chip
            setTimeout(() => {
              const chipData = kittyChipSprites.get(denom);
              if (!chipData) {
                chipResolve();
                return;
              }

              const chipTexture = textures.get(`chip-${denom}`);
              if (!chipTexture) {
                chipResolve();
                return;
              }

              if (!chipData.sprite.parent) {
                chipResolve();
                return;
              }

              // Get source position from kitty chip
              const sourceWorldPos = chipData.sprite.getGlobalPosition();
              const sourceLocalPos = scene.uiLayer!.toLocal(sourceWorldPos);
              const sourceRotation = chipData.sprite.rotation;

              // Create TWO clone sprites - one for rail, one for dealer
              const railClone = new Sprite(chipTexture);
              railClone.label = 'chipSprite'; // Mark as chip for reconciliation skip
              railClone.anchor.set(0.5);
              railClone.width = CHIP_SIZES.kittyMedium;
              railClone.height = CHIP_SIZES.kittyMedium;
              railClone.x = sourceLocalPos.x;
              railClone.y = sourceLocalPos.y;
              railClone.rotation = sourceRotation;
              scene.animationLayer?.addChild(railClone);
              MODULE_CHIP_SPRITES_IN_FLIGHT.add(railClone);

              const dealerClone = new Sprite(chipTexture);
              dealerClone.label = 'chipSprite'; // Mark as chip for reconciliation skip
              dealerClone.anchor.set(0.5);
              dealerClone.width = CHIP_SIZES.kittyMedium;
              dealerClone.height = CHIP_SIZES.kittyMedium;
              dealerClone.x = sourceLocalPos.x;
              dealerClone.y = sourceLocalPos.y;
              dealerClone.rotation = sourceRotation;
              scene.animationLayer?.addChild(dealerClone);
              MODULE_CHIP_SPRITES_IN_FLIGHT.add(dealerClone);

              // Handle original chip - hide/remove and update counter NOW (when chip departs)
              const currentCount = denomCounts.get(denom) || 0;
              if (currentCount === 1) {
                // Last chip of this denom - remove sprite and border, delete from Map
                containerData.chipContainer?.removeChild(chipData.sprite);
                containerData.borderContainer?.removeChild(chipData.border);
                chipData.sprite.destroy();
                chipData.border.destroy();
                kittyChipSprites.delete(denom);
                // Reposition remaining chips
                if (containerData.chipContainer && containerData.borderContainer) {
                  repositionKittyChips(containerData.chipContainer, containerData.borderContainer, kittyChipSprites);
                  // Randomize rotation of remaining revealed chips
                  for (const [, data] of kittyChipSprites) {
                    data.sprite.rotation = Math.random() * 2 * Math.PI;
                  }
                }
              } else {
                // More chips of this denom remain
                // Randomize the remaining kitty sprite rotation (the chip revealed underneath)
                chipData.sprite.rotation = Math.random() * 2 * Math.PI;
              }
              denomCounts.set(denom, currentCount - 1);

              // Update kitty data tracking (decrement total)
              const kittyDenomIndex = kittyData.denominations.indexOf(denom);
              if (kittyDenomIndex !== -1) {
                kittyData.denominations.splice(kittyDenomIndex, 1);
                kittyData.total -= denom;
              }

              // Update counter display
              if (pill && counterText) {
                const counterOffset = { x: chipCounterConfig.x ?? 0, y: chipCounterConfig.y ?? 0 };
                updateKittyLayout(pill, counterText, kittyChipSprites.size, kittyData.total, counterOffset);
              }

              // GUARANTEED RESOLUTION: Track when both animations complete
              let railDone = false;
              let dealerDone = false;
              let chipResolved = false;

              const checkComplete = () => {
                if (chipResolved) return;
                if (railDone && dealerDone) {
                  chipResolved = true;
                  clearTimeout(chipSafetyTimeout);
                  chipResolve();
                }
              };

              // GUARANTEED RESOLUTION: Safety timeout for this chip's animations
              const chipSafetyTimeout = setTimeout(() => {
                if (chipResolved) return;
                chipResolved = true;
                recordAnimationFailure({
                  fileName: 'PixiGameTableV1.tsx',
                  functionName: 'animateSurrenderChips.chip',
                  reason: 'timeout',
                  animationType: 'surrender-chip',
                  targetDescription: `seat=${seatPosition} denom=${denom}`,
                  expectedDuration: duration,
                });
                // Clean up any remaining sprites
                if (railClone && !railClone.destroyed) {
                  MODULE_CHIP_SPRITES_IN_FLIGHT.delete(railClone);
                  scene.animationLayer?.removeChild(railClone);
                  railClone.destroy();
                }
                if (dealerClone && !dealerClone.destroyed) {
                  MODULE_CHIP_SPRITES_IN_FLIGHT.delete(dealerClone);
                  scene.animationLayer?.removeChild(dealerClone);
                  dealerClone.destroy();
                }
                chipResolve();
              }, duration + 500);

              // Animate rail clone
              const railTarget = railPositions.get(denom);
              if (railTarget) {
                const startTime = performance.now();
                const startX = railClone.x;
                const startY = railClone.y;
                const startSize = CHIP_SIZES.kittyMedium;
                const endSize = CHIP_SIZES.rail;

                const animateRail = () => {
                  if (chipResolved) return;
                  if (!railClone || railClone.destroyed) {
                    railDone = true;
                    checkComplete();
                    return;
                  }

                  const elapsed = performance.now() - startTime;
                  const progress = Math.min(elapsed / duration, 1);
                  const eased = easeOutCubic(progress);

                  railClone.x = startX + (railTarget.x - startX) * eased;
                  railClone.y = startY + (railTarget.y - startY) * eased;
                  const size = startSize + (endSize - startSize) * eased;
                  railClone.width = size;
                  railClone.height = size;

                  if (progress < 1) {
                    requestAnimationFrame(animateRail);
                  } else {
                    // Sound: chipLand on surrender refund half arrival at rail
                    playTableSound('chipLand');
                    MODULE_CHIP_SPRITES_IN_FLIGHT.delete(railClone);
                    scene.animationLayer?.removeChild(railClone);
                    railClone.destroy();
                    railDone = true;
                    checkComplete();
                  }
                };
                requestAnimationFrame(animateRail);
              } else {
                railDone = true;
              }

              // Animate dealer clone
              const dealerTarget = dealerPositions.get(denom);
              if (dealerTarget) {
                const startTime = performance.now();
                const startX = dealerClone.x;
                const startY = dealerClone.y;
                const startSize = CHIP_SIZES.kittyMedium;
                const endSize = CHIP_SIZES.dealerPile;

                const animateDealer = () => {
                  if (chipResolved) return;
                  if (!dealerClone || dealerClone.destroyed) {
                    dealerDone = true;
                    checkComplete();
                    return;
                  }

                  const elapsed = performance.now() - startTime;
                  const progress = Math.min(elapsed / duration, 1);
                  const eased = easeOutCubic(progress);

                  dealerClone.x = startX + (dealerTarget.x - startX) * eased;
                  dealerClone.y = startY + (dealerTarget.y - startY) * eased;
                  const size = startSize + (endSize - startSize) * eased;
                  dealerClone.width = size;
                  dealerClone.height = size;

                  if (progress < 1) {
                    requestAnimationFrame(animateDealer);
                  } else {
                    // Sound: chipLand on surrender loss half arrival at dealer
                    playTableSound('chipLand');
                    MODULE_CHIP_SPRITES_IN_FLIGHT.delete(dealerClone);
                    scene.animationLayer?.removeChild(dealerClone);
                    dealerClone.destroy();
                    dealerDone = true;
                    checkComplete();
                  }
                };
                requestAnimationFrame(animateDealer);
              } else {
                dealerDone = true;
              }

              // If neither animation started, resolve immediately
              if (!railTarget && !dealerTarget) {
                chipResolved = true;
                clearTimeout(chipSafetyTimeout);
                chipResolve();
              }
            }, index * staggerDelay);
          });
          allPromises.push(chipPromise);
        });

        // GUARANTEED RESOLUTION: Master timeout for entire surrender operation
        const masterTimeout = setTimeout(() => {
          recordAnimationFailure({
            fileName: 'PixiGameTableV1.tsx',
            functionName: 'animateSurrenderChips.master',
            reason: 'timeout',
            animationType: 'surrender-chips-all',
            targetDescription: `seat=${seatPosition} chips=${denominations.length}`,
            expectedDuration: (sortedDenoms.length * staggerDelay) + duration,
          });
          resolve();
        }, (sortedDenoms.length * staggerDelay) + duration + 1000);

        // Wait for all animations to complete, then finalize cleanup
        Promise.all(allPromises).then(() => {
          clearTimeout(masterTimeout);

          // Kitty data and chip sprites were already updated progressively
          // Just ensure container is hidden if empty
          if (kittyChipSprites.size === 0) {
            kittyContainer.visible = false;
          }

          resolve();
        });
      });
    },

    revealHoleCard: (flipDuration?: number): Promise<void> => {
      // Guard against duplicate calls AFTER completion (e.g., duplicate events)
      // MODULE_HOLE_CARD_REVEALED is set when reveal completes and cleared on DEALING_STARTED
      // This is separate from MODULE_STRAIGHTENED which tracks card positioning
      if (MODULE_HOLE_CARD_REVEALED) {
        console.log('[revealHoleCard] Already completed this round, skipping duplicate call');
        return Promise.resolve();
      }

      const orchestrator = getAnimationOrchestrator();

      // Use animateSingleton for synchronous registration (mutual exclusion with straightenDealerCards)
      return orchestrator.animateSingleton('hole-card-reveal', 'HOLE_CARD_REVEAL', async () => {
        console.log('[PixiGameTableV1] Hole card reveal triggered');

        // Wait for any in-progress dealer straightening to complete first
        // This ensures we don't have competing animations
        await orchestrator.waitForType('DEALER_STRAIGHTEN');

        const scene = sceneRef.current;
        const textures = texturesRef.current;
        const currentCardConfig = cardConfigRef.current;

        // Get dealer state for the revealed hole card
        const gameState = useGameStateStore.getState();
        const dealerCards = gameState.dealer.cards;
        const totalDealerCards = dealerCards.length;

        // Find both dealer card states
        const card0State = MODULE_CARD_STATES.get('dealer-0-0');
        const card1State = MODULE_CARD_STATES.get('dealer-0-1');

        if (!card0State || !card1State) {
          console.warn('[revealHoleCard] Missing dealer card states');
          await new Promise(r => setTimeout(r, 400));
          return;
        }

        // Calculate new overlap positions (centered like player hands)
        const dealerCardsX = currentCardConfig.dealerCardsX;
        const dealerCardsY = currentCardConfig.dealerCardsY;

        // Scale overlap based on card scale for consistent spacing
        const revealScaleFactor = currentCardConfig.scale / 100;
        const scaledOverlap = CARD_OVERLAP * revealScaleFactor;

        // Centered offset for 2 cards with scaled overlap
        const card0OverlapX = DEALER_POSITION.x + dealerCardsX + (0 - (totalDealerCards - 1) / 2) * scaledOverlap;
        const card1OverlapX = DEALER_POSITION.x + dealerCardsX + (1 - (totalDealerCards - 1) / 2) * scaledOverlap;

        // Get hole card data for face-up rendering
        const holeCard = dealerCards[1];
        if (!holeCard) {
          console.warn('[revealHoleCard] No hole card in dealer state');
          await new Promise(r => setTimeout(r, 400));
          return;
        }

        // Register both dealer cards as in-flight (both move during reveal)
        MODULE_ELEMENTS_IN_FLIGHT.add('dealer-0-0');
        MODULE_ELEMENTS_IN_FLIGHT.add('dealer-0-1');
        console.log('[revealHoleCard] Registered in-flight: dealer-0-0, dealer-0-1');

        // Create face-up card sprite for the hole card
        // Force faceUp: true since we're explicitly revealing the card
        // (handles DEALER_BLACKJACK case where state might not have faceUp set yet)
        const faceUpCard = createCardSprite(
          { ...holeCard, faceUp: true },
          textures,
          card1State.baseX,
          card1State.baseY,
          card1State.baseRotation,
          currentCardConfig
        );
        // Set z-index higher than card 0 (5000) so hole card flips ON TOP of face-up card
        faceUpCard.zIndex = 5001;
        scene.animationLayer?.addChild(faceUpCard);

        // === ATOMIC FLIP + CENTER ANIMATION ===
        // Single unified animation: flip and slide happen SIMULTANEOUSLY
        // This prevents any race conditions between separate operations
        await new Promise<void>((resolveAnimation) => {
          // Use server-provided duration, or fallback to cached config (logs error if fallback used)
          const animationDuration = getAnimationDuration('cardFlip', flipDuration, {
            fileName: 'PixiGameTableV1.tsx',
            functionName: 'revealHoleCard',
            eventType: 'DEALER_REVEALS_HOLE',
          });
          console.log(`[revealHoleCard] Using duration: ${animationDuration}ms (server=${flipDuration ?? 'none'})`);
          const startTime = Date.now();
          const card0StartX = card0State.sprite.x;
          const card1StartX = card1State.sprite.x;
          let faceDownDestroyed = false;
          let flipSoundPlayed = false; // Sound latch for hole-card reveal

          const unifiedTicker = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / animationDuration, 1);
          const eased = easeOutCubic(progress);

          // === SLIDE (throughout entire animation) ===
          // Both cards slide toward overlap positions
          card0State.sprite.x = card0StartX + (card0OverlapX - card0StartX) * eased;
          if (!faceDownDestroyed) {
            card1State.sprite.x = card1StartX + (card1OverlapX - card1StartX) * eased;
          }
          faceUpCard.x = card1StartX + (card1OverlapX - card1StartX) * eased;

          // === FLIP (first 60% of animation) ===
          // Flip completes at 60% progress, giving slide time to finish smoothly
          const flipProgress = Math.min(progress / 0.6, 1);

          if (flipProgress < 0.5) {
            // First half: shrink face-down card
            const scaleX = 1 - flipProgress * 2;
            card1State.sprite.scale.x = scaleX;
            faceUpCard.scale.x = 0;
            faceUpCard.visible = false;
          } else if (!faceDownDestroyed) {
            // Sound: deal at 50% flip progress (one-shot latch)
            if (!flipSoundPlayed) {
              flipSoundPlayed = true;
              playTableSound('deal');
            }
            // Second half: grow face-up card, hide face-down
            const scaleX = (flipProgress - 0.5) * 2;
            card1State.sprite.visible = false;
            faceUpCard.visible = true;
            faceUpCard.scale.x = scaleX;

            // At 100% flip progress, destroy the face-down card
            if (flipProgress >= 1) {
              scene.animationLayer?.removeChild(card1State.sprite);
              card1State.sprite.destroy({ children: true });
              faceDownDestroyed = true;

              // Update MODULE_CARD_STATES with face-up card
              MODULE_CARD_STATES.set('dealer-0-1', {
                ...card1State,
                sprite: faceUpCard,
                card: holeCard,
              });
            }
          }

          // === ANIMATION COMPLETE ===
          if (progress >= 1) {
            // Ensure final positions are exact
            card0State.sprite.x = card0OverlapX;
            faceUpCard.x = card1OverlapX;
            faceUpCard.scale.x = 1;

            // Update base positions for future operations
            card0State.baseX = card0OverlapX;
            card1State.baseX = card1OverlapX;
            const updatedCard1State = MODULE_CARD_STATES.get('dealer-0-1');
            if (updatedCard1State) {
              updatedCard1State.baseX = card1OverlapX;
            }

            // Mark dealer as straightened (centering is complete)
            // This prevents straightenDealerCards() from re-centering
            MODULE_STRAIGHTENED.set('dealer', true);
            // Mark hole card as revealed for this round (prevents duplicate reveal calls)
            MODULE_HOLE_CARD_REVEALED = true;
            console.log('[revealHoleCard] Atomic flip+center complete, marked dealer as straightened');

            // Unregister from in-flight (reconciliation can now touch these elements)
            MODULE_ELEMENTS_IN_FLIGHT.delete('dealer-0-0');
            MODULE_ELEMENTS_IN_FLIGHT.delete('dealer-0-1');

            resolveAnimation();
            return;
          }

          requestAnimationFrame(unifiedTicker);
        };

        requestAnimationFrame(unifiedTicker);
      });
    });
    },

    clearTable: (): Promise<void> => {
      // Debug logging
      const dbg = (msg: string) => {
        console.log(msg);
        const w = window as unknown as { pixiDebugLog?: (m: string) => void };
        w.pixiDebugLog?.(msg);
      };

      const orchestrator = getAnimationOrchestrator();
      console.log(`[OOO DEBUG] clearTable - CHECKING FOR WORK, cards: ${MODULE_CARD_STATES.size}`);

      // Helper function to clean up card/label state (NOT kitty - that persists through rounds)
      // Kitty is only cleared on reconnection via clearModuleState()
      const cleanupState = () => {
        MODULE_CARD_STATES.clear();
        MODULE_STRAIGHTENED.clear();
        MODULE_DEALT_CARD_COUNTS.clear();
        MODULE_SCOREBOX_CACHE.clear();
        MODULE_ELEMENTS_IN_FLIGHT.clear();
        // NOTE: MODULE_HOLE_CARD_REVEAL_PROMISE and MODULE_DEALER_STRAIGHTEN_PROMISE are now
        // managed by AnimationOrchestrator - no need to manually clear here
        MODULE_OUTCOME_LABELS.forEach((state) => {
          if (!state.wrapper.destroyed) state.wrapper.destroy();
        });
        MODULE_OUTCOME_LABELS.clear();
        // Destroy any in-flight chip sprites (prevents stuck chips after tab switch)
        MODULE_CHIP_SPRITES_IN_FLIGHT.forEach((sprite) => {
          if (!sprite.destroyed) {
            sprite.parent?.removeChild(sprite);
            sprite.destroy();
          }
        });
        MODULE_CHIP_SPRITES_IN_FLIGHT.clear();

        // NOTE: Kitty containers are NOT cleared here - they persist through rounds
        // and are only cleared on reconnection (clearModuleState) or fullVisualResync

        setAnimationCardCount(0);
      };

      // ============================================================
      // Early return checks - before using orchestrator
      // ============================================================

      const scene = sceneRef.current;
      if (!scene || !scene.animationLayer) {
        // Clear state even if no scene or animation layer
        dbg(`[CLEAR TABLE] No scene/animation layer - clearing ${MODULE_CARD_STATES.size} cards`);
        cleanupState();
        console.log(`[OOO DEBUG] clearTable - NO SCENE, returning resolved promise`);
        return Promise.resolve();
      }

      // Disable card straightening during rake
      scene.rakeInProgress = true;
      dbg(`[CLEAR TABLE] rakeInProgress = true (straightening disabled)`);

      // Gather all sprites to animate (snapshot before we start removing)
      // Filter out kitty elements and outcome labels - they have their own animations
      const children = [...scene.animationLayer.children].filter(
        child => {
          const c = child as unknown as { isKittyElement?: boolean; isOutcomeLabelWrapper?: boolean };
          return !c.isKittyElement && !c.isOutcomeLabelWrapper;
        }
      ) as Container[];
      const total = children.length;

      if (total === 0) {
        console.log(`[OOO DEBUG] clearTable - NO CARDS TO RAKE, returning resolved promise`);
        dbg(`[CLEAR TABLE] No children - clearing ${MODULE_CARD_STATES.size} cards`);
        cleanupState();
        return Promise.resolve();
      }

      // Now we know we have cards to rake - use orchestrator.animateSingleton for deduplication
      console.log(`[OOO DEBUG] clearTable - CREATING ANIMATION, raking ${total} cards...`);

      return orchestrator.animateSingleton('clear-table', 'CLEAR_TABLE', async () => {
        await new Promise<void>((resolveAnimation) => {
          try {
            dbg(`[CLEAR TABLE] triggered! Cards before clear: ${MODULE_CARD_STATES.size}`);

        // NOTE: Kitty containers are NOT cleared during normal clearTable - they persist
        // through rounds. Only cleared on reconnection via clearModuleState() or fullVisualResync()

        // Animate all outcome labels out (drop down) in parallel with card rake
        // Labels are always in outcomeLabelLayer (no re-parenting needed)
        const outcomeFlyOutDuration = getClientAnimationTiming('outcomeLabelFly', 'clearTable');
        MODULE_OUTCOME_LABELS.forEach((state, outcomeKey) => {
          if (state.state !== 'animating-out' && !state.wrapper.destroyed) {
            dbg(`[CLEAR TABLE] Animating outcome label fly-out for: ${outcomeKey}`);

            // Mark as animating out
            state.state = 'animating-out';

            // The outcome label is the second child (first is the mask)
            const outcomeLabel = state.wrapper.children[1] as Container;
            if (outcomeLabel) {
              animateSprite(outcomeLabel, { y: outcomeLabel.y + 30 }, {
                duration: outcomeFlyOutDuration,
                easing: easeInCubic,
                onComplete: () => {
                  // Clean up after animation
                  scene.outcomeLabelLayer?.removeChild(state.wrapper);
                  state.wrapper.destroy();
                  MODULE_OUTCOME_LABELS.delete(outcomeKey);
                }
              });
            }
          }
        });

        // NOTE: Scorecard flip-back removed from here.
        // Scorecard now stays visible until dealer rake completes.
        // Flip-back happens in onDealingStarted after rakeDealerCards().

        // Rake destination: dealer center, 200px above
        const rakeDestination = {
          x: DEALER_POSITION.x,
          y: DEALER_POSITION.y + RAKE_CONFIG.destinationYOffset,
        };

        // TIMING TEST: Measure time since BET press for rake animation
        const rakeNow = performance.now();
        const betPressTime = (window as unknown as { __BET_PRESS_TIME__?: number }).__BET_PRESS_TIME__ || 0;
        const rakeDelta = betPressTime ? (rakeNow - betPressTime).toFixed(2) : 'N/A';
        console.log(`[TIMING TEST] ⏱️ RAKE ANIMATION START at ${rakeNow.toFixed(2)}ms | Delta from BET: ${rakeDelta}ms | Cards: ${total}`);

        // Reset all sidebet icon borders to gold - synchronized with outcome label fly-out
        console.log(`[SIDEBET BORDER] clearTable: Fading out all sidebet borders, keys:`, Array.from(scene.sidebetSprites.keys()));
        for (const [key, sprite] of scene.sidebetSprites) {
          if (sprite && !sprite.destroyed) {
            const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
            if (borderRing && !borderRing.destroyed) {
              console.log(`[SIDEBET BORDER] clearTable: Fading out border for ${key}`);
              const iconSize = sidebetConfigRef.current?.iconSize ?? SIDEBET_ICON_SIZE;
              fadeOutSidebetBorder(borderRing, iconSize);
            }
          }
        }

        let completed = 0;

        // Reverse children order so top-of-stack (last dealt) cards rake first
        // This creates a more natural visual of peeling cards from the top
        children.reverse();

        // Set high zIndex on all cards so they fly IN FRONT of dealer cards during rake
        // Dealer cards have zIndex ~5000, so use 10000 to ensure raking cards are on top
        // IMPORTANT: Use the same zIndex for all cards to preserve their dealt z-order
        // (different zIndex values would reorder them visually before rake starts)
        children.forEach((child) => {
          child.zIndex = 10000;
        });

        // DEALER BUTTON ANIMATION - trigger mid-rake if position changed
        // Calculate mid-rake timing: when ~40% of cards have started their animation
        const midRakeDelay = Math.floor(total * 0.4) * RAKE_CONFIG.staggerDelay;

        // Read button position directly from game state store (always current, not stale from props)
        // This avoids timing issues where clearTable runs before React re-renders with new props
        const gameState = useGameStateStore.getState();
        const actualButtonSeat = gameState.buttonPosition;
        const mySeat = gameState.mySeatPosition;

        // Convert actual seat to visual seat (rotated so player is at position 4)
        let targetButtonSeat = 0;
        if (actualButtonSeat >= 1 && actualButtonSeat <= 7 && mySeat !== null) {
          targetButtonSeat = ((actualButtonSeat - mySeat + 10) % 7) + 1;
        } else if (actualButtonSeat >= 1 && actualButtonSeat <= 7) {
          targetButtonSeat = actualButtonSeat; // No rotation if not seated
        }

        const shouldAnimateButton = targetButtonSeat >= 1 && targetButtonSeat !== MODULE_DEALER_BUTTON_CURRENT_SEAT;

        console.log(`[DealerButton] clearTable check: actualSeat=${actualButtonSeat}, mySeat=${mySeat}, visualTarget=${targetButtonSeat}, current=${MODULE_DEALER_BUTTON_CURRENT_SEAT}, shouldAnimate=${shouldAnimateButton}`);

        if (shouldAnimateButton) {
          const pendingSeat = targetButtonSeat;
          console.log(`[DealerButton] Scheduling mid-rake animation: ${MODULE_DEALER_BUTTON_CURRENT_SEAT} → ${pendingSeat} in ${midRakeDelay}ms`);
          setTimeout(() => {
            // Access the moveDealerButton method from the current ref
            // We need to animate the button here since the ref might not be available
            const button = scene.dealerButtonContainer;
            if (!button || button.destroyed) {
              MODULE_DEALER_BUTTON_PENDING_SEAT = null;
              return;
            }

            // Calculate target position
            // IMPORTANT: Must apply seat config offsets (x, y, rot) to match where seat is actually rendered
            const targetSeatPos = dynamicSeatPositions[pendingSeat - 1];
            if (!targetSeatPos) {
              MODULE_DEALER_BUTTON_PENDING_SEAT = null;
              return;
            }

            const btnConfig = dealerButtonConfig || DEFAULT_DEALER_BUTTON_CONFIG;
            const targetSeatConfig = seatConfigs[pendingSeat] || DEFAULT_SEAT_CONFIG;
            // Apply seat config offsets to get actual seat position
            const adjustedTargetX = targetSeatPos.x + targetSeatConfig.x;
            const adjustedTargetY = targetSeatPos.y + targetSeatConfig.y;
            const targetRotation = targetSeatPos.rotation + targetSeatConfig.rot;
            const targetSeatWidth = Math.round(144 * (seatSizeScale / 100) * (targetSeatConfig.scale / 100));
            const targetSeatHeight = Math.round(168 * (seatSizeScale / 100) * (targetSeatConfig.scale / 100));
            const targetLocalOffsetX = targetSeatWidth / 2 + (btnConfig.x || 0);
            const targetLocalOffsetY = -targetSeatHeight / 2 + (btnConfig.y || 0);
            const targetRotationRad = degreesToRadians(targetRotation);
            const targetRotatedOffsetX = targetLocalOffsetX * Math.cos(targetRotationRad) - targetLocalOffsetY * Math.sin(targetRotationRad);
            const targetRotatedOffsetY = targetLocalOffsetX * Math.sin(targetRotationRad) + targetLocalOffsetY * Math.cos(targetRotationRad);

            const targetX = adjustedTargetX + targetRotatedOffsetX;
            const targetY = adjustedTargetY + targetRotatedOffsetY;

            // Animate
            const startX = button.x;
            const startY = button.y;
            const startRotation = button.rotation;

            // Handle rotation wraparound (shortest path)
            let deltaRotation = targetRotationRad - startRotation;
            if (deltaRotation > Math.PI) deltaRotation -= 2 * Math.PI;
            if (deltaRotation < -Math.PI) deltaRotation += 2 * Math.PI;

            console.log(`[DealerButton] Mid-rake animation starting: (${startX.toFixed(0)}, ${startY.toFixed(0)}) → (${targetX.toFixed(0)}, ${targetY.toFixed(0)})`);

            const buttonAnimDuration = 350; // Slightly longer than chip animations for visual appeal
            const animStartTime = performance.now();
            const animateButton = () => {
              const elapsed = performance.now() - animStartTime;
              const progress = Math.min(elapsed / buttonAnimDuration, 1);
              const eased = easeOutCubic(progress);

              button.x = startX + (targetX - startX) * eased;
              button.y = startY + (targetY - startY) * eased;
              button.rotation = startRotation + deltaRotation * eased;

              if (progress < 1) {
                requestAnimationFrame(animateButton);
              } else {
                MODULE_DEALER_BUTTON_CURRENT_SEAT = pendingSeat;
                MODULE_DEALER_BUTTON_PENDING_SEAT = null;
                console.log(`[DealerButton] Mid-rake animation complete, now at seat ${pendingSeat}`);
              }
            };
            requestAnimationFrame(animateButton);
          }, midRakeDelay);
        }

        children.forEach((child, index) => {
          // Stagger start time
          setTimeout(() => {
            // Safety check: if child was destroyed before animation started, skip it
            if (child.destroyed) {
              completed++;
              if (completed >= total) {
                MODULE_CARD_STATES.clear();
                MODULE_STRAIGHTENED.clear();
                MODULE_DEALT_CARD_COUNTS.clear();
                MODULE_SCOREBOX_CACHE.clear();
                MODULE_OUTCOME_LABELS.forEach((state) => {
                  if (!state.wrapper.destroyed) state.wrapper.destroy();
                });
                MODULE_OUTCOME_LABELS.clear();
                setAnimationCardCount(0);
                // Re-enable card straightening
                scene.rakeInProgress = false;
                resolveAnimation();
              }
              return;
            }

            const startX = child.x;
            const startY = child.y;
            const startRotation = child.rotation;

            // Calculate per-card control point for arc
            const controlPoint = calculateRakeControlPoint(startX, startY, DEALER_POSITION.x);

            // Animate along Bezier arc
            animateSpriteArc(
              child,
              { x: startX, y: startY, rotation: startRotation },
              rakeDestination,
              controlPoint,
              {
                duration: RAKE_CONFIG.duration,
                easing: easeInOutCubic,
                onComplete: () => {
                  // Safety: only clean up if not already destroyed
                  if (!child.destroyed) {
                    scene.animationLayer?.removeChild(child);
                    child.destroy();
                  }
                  completed++;
                  if (completed >= total) {
                    console.log(`[OOO DEBUG] clearTable - RAKE ANIMATION COMPLETE (${total} cards)`);
                    MODULE_CARD_STATES.clear();
                    MODULE_STRAIGHTENED.clear();
                    MODULE_DEALT_CARD_COUNTS.clear();
                    MODULE_SCOREBOX_CACHE.clear();
                    // Clear outcome labels registry (destroy wrappers)
                    MODULE_OUTCOME_LABELS.forEach((state) => {
                      if (!state.wrapper.destroyed) state.wrapper.destroy();
                    });
                    MODULE_OUTCOME_LABELS.clear();
                    setAnimationCardCount(0);
                    // Re-enable card straightening
                    scene.rakeInProgress = false;
                    resolveAnimation();
                  }
                },
              }
            );
          }, index * RAKE_CONFIG.staggerDelay);
        });

        // Safety timeout
        const maxTime = total * RAKE_CONFIG.staggerDelay + RAKE_CONFIG.duration + 100;
        setTimeout(() => {
          MODULE_CARD_STATES.clear();
          MODULE_STRAIGHTENED.clear();
          MODULE_DEALT_CARD_COUNTS.clear();
          MODULE_SCOREBOX_CACHE.clear();
          // Clear outcome labels registry (destroy wrappers)
          MODULE_OUTCOME_LABELS.forEach((state) => {
            if (!state.wrapper.destroyed) state.wrapper.destroy();
          });
          MODULE_OUTCOME_LABELS.clear();
          setAnimationCardCount(0);
          // Re-enable card straightening
          scene.rakeInProgress = false;
          resolveAnimation();
        }, maxTime);
          } catch (error) {
            // If any error occurs, ensure we clean up and resolve
            console.error('[CLEAR TABLE] Error during clear, forcing cleanup:', error);
            MODULE_CARD_STATES.clear();
            MODULE_STRAIGHTENED.clear();
            MODULE_DEALT_CARD_COUNTS.clear();
            MODULE_SCOREBOX_CACHE.clear();
            MODULE_OUTCOME_LABELS.clear();
            setAnimationCardCount(0);
            // Re-enable card straightening
            scene.rakeInProgress = false;
            resolveAnimation();
          }
        });
      });
    },

    /**
     * Animate the dealer button to a new visual seat position.
     * This is called when buttonPosition changes between rounds.
     */
    moveDealerButton: (targetVisualSeat: number): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[DealerButton] moveDealerButton called: target=${targetVisualSeat}, current=${MODULE_DEALER_BUTTON_CURRENT_SEAT}`);

        const scene = sceneRef.current;
        const button = scene.dealerButtonContainer;

        // Skip if no change needed
        if (targetVisualSeat === MODULE_DEALER_BUTTON_CURRENT_SEAT) {
          console.log(`[DealerButton] Already at seat ${targetVisualSeat}, skipping animation`);
          resolve();
          return;
        }

        // Skip if no button exists
        if (!button || button.destroyed) {
          console.log(`[DealerButton] No button exists, cannot animate`);
          resolve();
          return;
        }

        // Skip if hiding (target = 0)
        if (targetVisualSeat === 0) {
          console.log(`[DealerButton] Target is 0 (hide), skipping animation`);
          resolve();
          return;
        }

        // Get target seat position
        const targetSeatPos = dynamicSeatPositions[targetVisualSeat - 1];
        if (!targetSeatPos) {
          console.warn(`[DealerButton] No seat data for visual seat ${targetVisualSeat}`);
          resolve();
          return;
        }

        // Calculate target position with seat config offsets
        const btnConfig = dealerButtonConfig || DEFAULT_DEALER_BUTTON_CONFIG;
        const targetSeatConfig = seatConfigs[targetVisualSeat] || DEFAULT_SEAT_CONFIG;
        const adjustedTargetX = targetSeatPos.x + targetSeatConfig.x;
        const adjustedTargetY = targetSeatPos.y + targetSeatConfig.y;
        const targetRotation = targetSeatPos.rotation + targetSeatConfig.rot;
        const targetSeatWidth = Math.round(144 * (seatSizeScale / 100) * (targetSeatConfig.scale / 100));
        const targetSeatHeight = Math.round(168 * (seatSizeScale / 100) * (targetSeatConfig.scale / 100));
        const targetLocalOffsetX = targetSeatWidth / 2 + (btnConfig.x || 0);
        const targetLocalOffsetY = -targetSeatHeight / 2 + (btnConfig.y || 0);
        const targetRotationRad = degreesToRadians(targetRotation);
        const targetRotatedOffsetX = targetLocalOffsetX * Math.cos(targetRotationRad) - targetLocalOffsetY * Math.sin(targetRotationRad);
        const targetRotatedOffsetY = targetLocalOffsetX * Math.sin(targetRotationRad) + targetLocalOffsetY * Math.cos(targetRotationRad);

        const targetX = adjustedTargetX + targetRotatedOffsetX;
        const targetY = adjustedTargetY + targetRotatedOffsetY;

        // Animate
        const startX = button.x;
        const startY = button.y;
        const startRotation = button.rotation;

        // Handle rotation wraparound (shortest path)
        let deltaRotation = targetRotationRad - startRotation;
        if (deltaRotation > Math.PI) deltaRotation -= 2 * Math.PI;
        if (deltaRotation < -Math.PI) deltaRotation += 2 * Math.PI;

        console.log(`[DealerButton] Animating: (${startX.toFixed(0)}, ${startY.toFixed(0)}) → (${targetX.toFixed(0)}, ${targetY.toFixed(0)})`);

        const buttonAnimDuration = 350;
        const animStartTime = performance.now();
        const animateButton = () => {
          if (button.destroyed) {
            resolve();
            return;
          }

          const elapsed = performance.now() - animStartTime;
          const progress = Math.min(elapsed / buttonAnimDuration, 1);
          const eased = easeOutCubic(progress);

          button.x = startX + (targetX - startX) * eased;
          button.y = startY + (targetY - startY) * eased;
          button.rotation = startRotation + deltaRotation * eased;

          if (progress < 1) {
            requestAnimationFrame(animateButton);
          } else {
            MODULE_DEALER_BUTTON_CURRENT_SEAT = targetVisualSeat;
            MODULE_DEALER_BUTTON_PENDING_SEAT = null;
            console.log(`[DealerButton] Animation complete, now at seat ${targetVisualSeat}`);
            resolve();
          }
        };
        requestAnimationFrame(animateButton);
      });
    },

    rakeCardsForSeat: (seatPosition: number): Promise<void> => {
      return new Promise(async (resolve) => {
        console.log('[PixiGameTableV1] Rake cards for seat:', seatPosition);
        logTextureCount(`RAKE_START seat=${seatPosition}`);

        // === DIAGNOSTIC: Log scene state at start of rake ===
        const diagSplitState = sceneRef.current?.splitHandState.get(seatPosition);
        const diagIsSplitHand = diagSplitState && diagSplitState.totalHands > 1;
        logSceneDiagnostic(sceneRef.current, `rakeCardsForSeat_START(seat=${seatPosition}, split=${diagIsSplitHand})`);

        // DUPLICATE RAKE GUARD: Skip if this seat was already raked this round
        // This prevents texture accumulation from duplicate SEAT_RAKE events
        if (MODULE_RAKED_SEATS_THIS_ROUND.has(seatPosition)) {
          console.log(`[RAKE GUARD] Skipping duplicate rake for seat ${seatPosition} - already raked this round`);
          resolve();
          return;
        }

        const scene = sceneRef.current;
        if (!scene.animationLayer) {
          resolve();
          return;
        }

        // Disable card straightening during rake
        scene.rakeInProgress = true;

        // Mark this seat as raked for this round - prevents duplicate processing
        MODULE_RAKED_SEATS_THIS_ROUND.add(seatPosition);
        console.log(`[RAKE GUARD] Marked seat ${seatPosition} as raked for this round`);

        // Find all cards for this seat (any hand index)
        const cardsToRake: { key: string; state: CardSpriteState }[] = [];
        MODULE_CARD_STATES.forEach((state, key) => {
          if (key.startsWith(`${seatPosition}-`) && !state.isDealer) {
            cardsToRake.push({ key, state });
          }
        });

        if (cardsToRake.length === 0) {
          scene.rakeInProgress = false;
          resolve();
          return;
        }

        // Sound: rake ONCE at start of rake operation (not per card)
        playTableSound('rake');

        // Group cards by hand index
        const cardsByHand = new Map<number, { key: string; state: CardSpriteState }[]>();
        cardsToRake.forEach(card => {
          const parts = card.key.split('-');
          const handIndex = parseInt(parts[1], 10) || 0;
          if (!cardsByHand.has(handIndex)) {
            cardsByHand.set(handIndex, []);
          }
          cardsByHand.get(handIndex)!.push(card);
        });

        // Get total hands count
        const splitState = scene.splitHandState.get(seatPosition);
        const totalHands = splitState?.totalHands ?? cardsByHand.size;
        const isSplitHand = totalHands > 1;

        console.log(`[RAKE] Seat ${seatPosition}: totalHands=${totalHands}, cardsByHand keys=[${Array.from(cardsByHand.keys()).join(',')}]`);

        // Hide persistent "Hand1" label during split hand rake (to avoid overlap with animated labels)
        if (isSplitHand && scene.devHandLabelsContainer && !scene.devHandLabelsContainer.destroyed) {
          scene.devHandLabelsContainer.visible = false;
          console.log(`[RAKE] Hiding persistent hand label for split hand rake`);
        }

        // NOTE: Scorecard flip-back removed from here.
        // Scorecard now stays visible until dealer rake completes.
        // Flip-back happens in onDealingStarted after rakeDealerCards().

        // Reset sidebet icon borders at start
        console.log(`[SIDEBET BORDER] Fading out borders for seat ${seatPosition}`);
        for (let i = 0; i < 3; i++) {
          const sidebetKey = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(sidebetKey);
          if (sprite && !sprite.destroyed) {
            const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
            if (borderRing && !borderRing.destroyed) {
              const iconSize = sidebetConfigRef.current?.iconSize ?? SIDEBET_ICON_SIZE;
              fadeOutSidebetBorder(borderRing, iconSize);
            }
          }
        }

        const viewport = scene.seatCardViewports.get(seatPosition);
        const filmstrip = scene.seatCardContents.get(seatPosition);
        const viewportX = viewport?.x ?? 0;
        const viewportY = viewport?.y ?? 0;
        const viewportRotation = viewport?.rotation ?? 0;

        // === SEQUENTIAL SPLIT HAND RAKE (OOO: Forward order 1→2→3→4) ===
        if (isSplitHand && viewport && filmstrip && !viewport.destroyed && !filmstrip.destroyed) {
          console.log(`[SPLIT RAKE] Sequential rake for ${totalHands} hands (forward order)`);
          console.log(`[SPLIT RAKE DIAG] splitState?.totalHands=${splitState?.totalHands}, cardsByHand.size=${cardsByHand.size}, cardsByHand keys=[${Array.from(cardsByHand.keys()).join(',')}]`);

          // Build order: always forward (Hand1 → Hand2 → Hand3 → Hand4)
          // IMPORTANT: Use the game state to determine actual hand count, not just cards
          const gameState = useGameStateStore.getState();
          const player = gameState.players.get(seatPosition);
          const gameStateHandCount = player?.hands.length ?? 0;
          const actualTotalHands = Math.max(totalHands, gameStateHandCount);
          console.log(`[SPLIT RAKE DIAG] gameStateHandCount=${gameStateHandCount}, actualTotalHands=${actualTotalHands}`);

          const handOrder: number[] = [];
          for (let i = 0; i < actualTotalHands; i++) {
            handOrder.push(i);  // 0, 1, 2, 3 (Hand1 → Hand4)
          }
          console.log(`[SPLIT RAKE] Forward order: ${handOrder.map(h => h + 1).join(' -> ')}`);

          // CRITICAL: Capture hand outcomes NOW before the loop, because game state
          // may be cleared mid-rake (e.g., after processing some hands)
          const capturedOutcomes = new Map<number, string>();
          if (player?.hands) {
            player.hands.forEach((hand, idx) => {
              if (hand.outcome) {
                capturedOutcomes.set(idx, hand.outcome);
                console.log(`[SPLIT RAKE] Captured outcome for hand ${idx}: ${hand.outcome}`);
              }
            });
          }

          // Calculate iris config for rake carousel navigation
          const rakeVisualPos = getVisualPositionForSeat(seatPosition);
          const rakeSeatConfig = rakeVisualPos !== null ? (seatConfigs[rakeVisualPos] || { x: 0, y: 0, rot: 0, scale: 100 }) : { x: 0, y: 0, rot: 0, scale: 100 };
          const rakeSeatScale = (seatSizeScale / 100) * (rakeSeatConfig.scale / 100);
          const rakeCardConfig = cardConfigRef.current;
          const rakeScaleFactor = rakeCardConfig.scale / 100;

          // Pre-rake: Navigate to Hand1 if not already there
          const currentViewedHand = splitState?.viewedHandIndex ?? 0;
          if (currentViewedHand !== 0) {
            const hand0CardCount = cardsByHand.get(0)?.length ?? 2;
            console.log(`[SPLIT RAKE] Pre-rake: moving to Hand1 from Hand${currentViewedHand + 1}`);
            await slideFilmstripToHand(scene, seatPosition, 0, true, 600, {
              targetHandCardCount: hand0CardCount,
              scaleFactor: rakeScaleFactor,
              seatScale: rakeSeatScale,
            });
            if (splitState) splitState.viewedHandIndex = 0;
          }

          // Rake each hand sequentially
          for (let seq = 0; seq < handOrder.length; seq++) {
            const handIndex = handOrder[seq];
            const handCards = cardsByHand.get(handIndex);
            const hasCardsToRake = handCards && handCards.length > 0;

            console.log(`[SPLIT RAKE] Processing hand ${handIndex + 1}/${totalHands} (${handCards?.length ?? 0} cards, hasCards=${hasCardsToRake})`);

            // Create "Hand{n}" label - positioned BELOW seat border, on RIGHT side (near avatar)
            // Mirrors outcome label behavior: flies in from right (behind avatar), flies out UPWARD
            // ONLY show hand labels for the player's seat (not opponents)
            // NOTE: We create labels for ALL hands, even if they have no cards to rake
            const isPlayerSeat = seatPosition === mySeatPosition;
            const visualPos = getVisualPositionForSeat(seatPosition);
            const seatData = visualPos !== null ? dynamicSeatPositions[visualPos - 1] : null;

            // Navigate filmstrip to bring this hand into the seat (only if we have cards or need visual consistency)
            if (seq > 0) {
              const handCardCount = handCards?.length ?? 2;
              await slideFilmstripToHand(scene, seatPosition, handIndex, true, 600, {
                targetHandCardCount: handCardCount,
                scaleFactor: rakeScaleFactor,
                seatScale: rakeSeatScale,
              });
            }
            let handLabel: Text | Sprite | null = null;
            let bgPill: Graphics | null = null;
            let existingWrapper: Container | null = null;
            let reusingExistingLabel = false;

            // === HANDLE EXISTING HAND LABEL ===
            const existingLabelKey = `${seatPosition}-handLabel`;
            const existingLabel = scene.splitHandLabels?.get(existingLabelKey);

            // For Hand1 (seq === 0): PRESERVE the existing label, don't create a new one
            // This avoids the unnecessary destroy-and-recreate animation
            if (seq === 0 && existingLabel) {
              console.log(`[RAKE] Hand1: Preserving existing hand label (no fly-in needed)`);
              const { label: oldLabel, bg: oldBg, wrapper: oldWrapper } = existingLabel;
              if (oldLabel && !oldLabel.destroyed) {
                handLabel = oldLabel;
                bgPill = oldBg ?? null;
                existingWrapper = oldWrapper ?? null;
                reusingExistingLabel = true;
                // Store rotation and wrapper for fly-out animation
                const seatRotRad = seatData ? degreesToRadians(seatData.rotation) : 0;
                (handLabel as any)._seatRotRad = existingLabel.seatRotRad ?? seatRotRad;
                (handLabel as any)._bgPill = bgPill;
                (handLabel as any)._wrapper = existingWrapper;
              }
              // Clear from map since we're taking ownership
              scene.splitHandLabels?.delete(existingLabelKey);
            } else if (existingLabel) {
              // For other hands: destroy existing label (and wrapper) before creating new one
              console.log(`[RAKE] Hand${handIndex + 1}: Destroying existing hand label before creating rake label`);
              const { label: oldLabel, bg: oldBg, wrapper: oldWrapper } = existingLabel;
              // If there's a wrapper, destroy it (which destroys children too)
              if (oldWrapper && !oldWrapper.destroyed) {
                scene.outcomeLabelLayer?.removeChild(oldWrapper);
                oldWrapper.destroy({ children: true });
                MODULE_TEXT_DESTROYED_COUNT++; // Track wrapper destruction (contains text label)
              } else {
                // No wrapper, destroy label and bg individually
                if (oldLabel && !oldLabel.destroyed) {
                  scene.outcomeLabelLayer?.removeChild(oldLabel);
                  oldLabel.destroy();
                  MODULE_TEXT_DESTROYED_COUNT++; // Track label destruction
                }
                if (oldBg && !oldBg.destroyed) {
                  scene.outcomeLabelLayer?.removeChild(oldBg);
                  oldBg.destroy();
                }
              }
              scene.splitHandLabels?.delete(existingLabelKey);
            }

            // Only create new label if we're not reusing an existing one
            if (!reusingExistingLabel && seatData && scene.animationLayer && isPlayerSeat) {
              const labelText = `Hand${handIndex + 1}`;
              console.log(`[RAKE LABEL DIAG] Creating label for hand ${handIndex + 1}: text="${labelText}"`);

              // Use pre-loaded texture instead of creating new Text (prevents texture accumulation)
              const handLabelTexture = texturesRef.current.get(`handLabel-${handIndex + 1}`);
              if (handLabelTexture) {
                handLabel = new Sprite(handLabelTexture);
                console.log(`[RAKE LABEL DIAG] Using pre-loaded Sprite: width=${handLabel.width.toFixed(1)}, height=${handLabel.height.toFixed(1)}, text="${labelText}"`);
              } else {
                // Fallback to Text if texture not found (shouldn't happen)
                console.warn(`[RAKE LABEL DIAG] Pre-loaded texture not found for handLabel-${handIndex + 1}, falling back to Text`);
                handLabel = new Text({
                  text: labelText,
                  style: {
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 14,
                    fontWeight: '700',
                    fill: 0x000000,
                    align: 'left',
                  }
                });
                MODULE_TEXT_CREATED_COUNT++;
              }
              handLabel.anchor.set(0, 0.5); // Left-justified, vertically centered

              // Get visualPos config for mask dimensions (matches actual seat rendering)
              const seatConfigForMask = seatConfigs[visualPos ?? 0] || { x: 0, y: 0, rot: 0, scale: 100 };
              const seatScaleForMask = (seatSizeScale / 100) * (seatConfigForMask.scale / 100);
              const scaledSeatHeight = Math.round(168 * seatScaleForMask);
              const scaledSeatWidth = Math.round(144 * seatScaleForMask);

              // Get seatPosition config for label positioning (consistent with other code paths)
              const seatConfigForLabel = seatConfigs[seatPosition] || { x: 0, y: 0, rot: 0, scale: 100 };
              const seatScaleForLabel = (seatSizeScale / 100) * (seatConfigForLabel.scale / 100);
              const scaledSeatHeightForLabel = Math.round(168 * seatScaleForLabel);
              const scaledSeatWidthForLabel = Math.round(144 * seatScaleForLabel);

              // Position BELOW seat border (positive Y offset in local coords = toward player/away from dealer)
              // and on RIGHT side (positive X offset in local coords = toward avatar)
              // Combines global config with per-seat config from refs
              const seatRotRad = degreesToRadians(seatData.rotation);
              const hlConfig = handLabelConfigRef.current;
              const labelLocalY = scaledSeatHeightForLabel / 2 + 8 + hlConfig.y; // Baseline +8px Y + config Y
              const labelLocalX = scaledSeatWidthForLabel * 0.2 - 38 + hlConfig.x; // Baseline -38px X + config X

              // === WRAPPER-BASED MASKING FOR FLY-IN CLIPPING ===
              const handLabelWrapper = new Container();
              handLabelWrapper.label = 'handLabelWrapper';
              handLabelWrapper.sortableChildren = true;
              handLabelWrapper.x = seatData.x;
              handLabelWrapper.y = seatData.y;
              handLabelWrapper.rotation = seatRotRad;

              // Create mask that clips on LEFT side (behind scorebox)
              // Use visualPos-based dimensions for mask (matches actual seat rendering)
              const maskTopY = scaledSeatHeight / 2; // Seat border bottom
              const maskBottomY = scaledSeatHeight / 2 + 60; // Extend below seat
              const maskHeight = maskBottomY - maskTopY;
              const maskLeftX = -scaledSeatWidth / 2 + 40; // Clip 40px from left (behind scorebox)
              const maskRightX = scaledSeatWidth / 2; // Full right side
              const maskWidth = maskRightX - maskLeftX;

              const handLabelMask = new Graphics();
              handLabelMask.roundRect(maskLeftX, maskTopY, maskWidth, maskHeight, 8);
              handLabelMask.fill({ color: 0xffffff });
              handLabelWrapper.addChild(handLabelMask);
              handLabelWrapper.mask = handLabelMask;


              // Create background with ONLY bottom-left corner rounded (3 squared corners)
              bgPill = new Graphics();
              const leftPadding = 8;
              const rightPadding = 16;
              const pillWidth = (handLabel.width + leftPadding + rightPadding) * 1.2; // 20% longer
              const pillHeight = handLabel.height + 8;
              const r = 4;
              const bx = 0; // Left edge at origin
              const by = -pillHeight / 2;
              const bw = pillWidth;
              const bh = pillHeight;

              // Draw with only bottom-left corner rounded
              bgPill.moveTo(bx, by);                        // Top-left (SQUARED)
              bgPill.lineTo(bx + bw, by);                   // Top edge to top-right (SQUARED)
              bgPill.lineTo(bx + bw, by + bh);              // Right edge to bottom-right (SQUARED)
              bgPill.lineTo(bx + r, by + bh);               // Bottom edge to before bottom-left
              bgPill.arcTo(bx, by + bh, bx, by + bh - r, r); // Bottom-left corner (ROUNDED)
              bgPill.lineTo(bx, by);                        // Left edge back to top-left
              bgPill.closePath();
              bgPill.fill({ color: 0xFFFFFF });

              // Start position (80px to the right, behind avatar) in LOCAL coords
              const flyInOffset = 80;
              const textLocalX = labelLocalX + leftPadding;

              // Position bg in LOCAL coords (starts outside mask on right)
              bgPill.x = labelLocalX + flyInOffset;
              bgPill.y = labelLocalY;
              bgPill.zIndex = 0;

              // Position text in LOCAL coords (starts outside mask)
              handLabel.x = textLocalX + flyInOffset;
              handLabel.y = labelLocalY;
              handLabel.zIndex = 1;

              // Add bg FIRST, then label (z-index ordering)
              handLabelWrapper.addChild(bgPill);
              handLabelWrapper.addChild(handLabel);

              // Add wrapper to outcomeLabelLayer
              scene.outcomeLabelLayer?.addChild(handLabelWrapper);
              existingWrapper = handLabelWrapper;

              // Animate in LOCAL coordinates - clipping happens automatically at mask edge
              animateSprite(handLabel, { x: textLocalX }, {
                duration: 300,
                easing: easeOutCubic,
                debugName: `handLabel-flyIn-${handIndex}`
              });
              animateSprite(bgPill, { x: labelLocalX }, {
                duration: 300,
                easing: easeOutCubic,
                debugName: `handLabelBg-flyIn-${handIndex}`
              });

              // Store wrapper and rotation for fly-out animation
              (handLabel as any)._seatRotRad = seatRotRad;
              (handLabel as any)._bgPill = bgPill;
              (handLabel as any)._wrapper = existingWrapper;
            }

            // === OUTCOME LABEL FLY-IN FOR ALL HANDS DURING RAKE ===
            // During resolution, outcome labels for other hands were destroyed when navigating away.
            // We need to recreate them during rake so each hand shows its outcome while being raked.
            // For the last hand resolved (which still has its outcome label), we destroy and recreate
            // to get the consistent fly-in animation.
            console.log(`[RAKE OUTCOME GUARD] hand=${handIndex}: seatData=${!!seatData}, outcomeLabelLayer=${!!scene.outcomeLabelLayer}, isPlayerSeat=${isPlayerSeat} (seatPos=${seatPosition}, myPos=${mySeatPosition})`);
            if (seatData && scene.outcomeLabelLayer && isPlayerSeat) {
              const outcomeKey = `${seatPosition}-${handIndex}`;
              const existingOutcome = MODULE_OUTCOME_LABELS.get(outcomeKey);

              // Use captured outcome (game state may be cleared mid-rake)
              const handOutcome = capturedOutcomes.get(handIndex);

              console.log(`[RAKE OUTCOME DIAG] seq=${seq} hand=${handIndex}: existingOutcome=${!!existingOutcome}, handOutcome=${handOutcome}, capturedOutcomes.size=${capturedOutcomes.size}`);

              // For seq=0 (first hand in rake): KEEP existing outcome label (already visible, no fly-in needed)
              // For seq>0: Destroy existing and recreate with fly-in animation
              if (seq === 0 && existingOutcome && !existingOutcome.wrapper.destroyed) {
                // First hand already has visible outcome label from resolution - keep it
                console.log(`[RAKE] Hand 0: Keeping existing outcome label (no fly-in needed)`);
              } else if (existingOutcome && !existingOutcome.wrapper.destroyed) {
                // Other hands: destroy existing to recreate with fly-in
                console.log(`[RAKE] Destroying existing outcome label for hand ${handIndex} to recreate with fly-in`);
                scene.outcomeLabelLayer.removeChild(existingOutcome.wrapper);
                existingOutcome.wrapper.destroy();
                MODULE_OUTCOME_LABELS.delete(outcomeKey);
              }

              // Create outcome label with fly-in animation (skip for seq=0 if already exists)
              const needsCreation = !(seq === 0 && existingOutcome && !existingOutcome.wrapper.destroyed);
              if (handOutcome && needsCreation) {
                console.log(`[RAKE] Creating outcome label for hand ${handIndex}: ${handOutcome}`);

                // Calculate dimensions (matches showOutcomeLabel)
                const seatConfigLocal = seatConfigs[visualPos ?? 0] || DEFAULT_SEAT_CONFIG;
                const seatScaleLocal = (seatSizeScale / 100) * (seatConfigLocal.scale / 100);
                const widthLocal = Math.round(144 * seatScaleLocal);
                const heightLocal = Math.round(168 * seatScaleLocal);

                // Create wrapper at seat position
                const seatX = seatData.x + seatConfigLocal.x;
                const seatY = seatData.y + seatConfigLocal.y;
                const seatRotation = seatData.rotation + seatConfigLocal.rot;

                const outcomeLabelWrapper = new Container();
                outcomeLabelWrapper.label = 'outcomeLabelWrapper';
                (outcomeLabelWrapper as unknown as { isOutcomeLabelWrapper: boolean }).isOutcomeLabelWrapper = true;
                outcomeLabelWrapper.x = seatX;
                outcomeLabelWrapper.y = seatY;
                outcomeLabelWrapper.rotation = degreesToRadians(seatRotation);

                // Create mask matching seat box dimensions
                const outcomeMask = new Graphics();
                outcomeMask.roundRect(-widthLocal / 2, -heightLocal / 2, widthLocal, heightLocal, 8);
                outcomeMask.fill({ color: 0xffffff });
                outcomeLabelWrapper.addChild(outcomeMask);
                outcomeLabelWrapper.mask = outcomeMask;

                // Create the label
                const outcomeLabel = createOutcomeLabel(handOutcome, outcomeLabelConfig, texturesRef.current);
                const outcomeBounds = outcomeLabel.getLocalBounds();
                const labelScaleLocal = outcomeLabel.scale.y;
                const visualHeightLocal = outcomeBounds.height * labelScaleLocal;

                // Calculate scorebox width for positioning
                let scoreboxWidthLocal = scoreboxConfig.minWidth ?? 35;
                const latestScore = getLatestScore(seatPosition, handIndex);
                if (latestScore) {
                  const boxScale = scoreboxConfig.scale / 100;
                  const textWidth = getScoreTextWidth(latestScore.score, scoreboxConfig.fontSize);
                  const calculatedWidth = Math.max(12, textWidth) * boxScale;
                  scoreboxWidthLocal = scoreboxConfig.minWidth
                    ? Math.max(scoreboxConfig.minWidth, calculatedWidth)
                    : calculatedWidth;
                }

                // Position outcome label flush with scorebox
                const scoreboxLocalX = -widthLocal / 2 + scoreboxConfig.x;
                const finalX = scoreboxLocalX + scoreboxWidthLocal;
                const scoreboxBottom = heightLocal / 2 + scoreboxConfig.y;
                outcomeLabel.y = scoreboxBottom - visualHeightLocal;
                outcomeLabelWrapper.addChild(outcomeLabel);

                // Add to layer
                scene.outcomeLabelLayer.addChild(outcomeLabelWrapper);

                // Register in module-level map
                MODULE_OUTCOME_LABELS.set(outcomeKey, {
                  wrapper: outcomeLabelWrapper,
                  outcome: handOutcome,
                  state: 'animating-in'
                });

                // Animate fly-in from left (same duration as hand label so they END together)
                const flyInOffsetOutcome = 80;
                outcomeLabel.x = finalX - flyInOffsetOutcome;
                animateSprite(outcomeLabel, { x: finalX }, {
                  duration: 300, // Same as hand label
                  easing: easeOutCubic,
                  debugName: `rakeOutcomeLabel-flyIn-${handIndex}`,
                  onComplete: () => {
                    const labelState = MODULE_OUTCOME_LABELS.get(outcomeKey);
                    if (labelState) labelState.state = 'static';
                    console.log(`[RAKE] Outcome label '${handOutcome}' fly-in complete for hand ${handIndex}`);
                  }
                });
              }
            }

            // Wait for label fly-in to settle
            await new Promise(r => setTimeout(r, 350));

            // Animate outcome label fly-out for this hand
            const outcomeKey = `${seatPosition}-${handIndex}`;
            const seatOutcomeFlyDuration = getClientAnimationTiming('outcomeLabelFly', 'rakeSeatCards');
            const outcomeState = MODULE_OUTCOME_LABELS.get(outcomeKey);
            if (outcomeState && outcomeState.state !== 'animating-out' && !outcomeState.wrapper.destroyed) {
              outcomeState.state = 'animating-out';
              const outcomeLabel = outcomeState.wrapper.children[1] as Container;
              if (outcomeLabel) {
                animateSprite(outcomeLabel, { y: outcomeLabel.y + 30 }, {
                  duration: seatOutcomeFlyDuration,
                  easing: easeInCubic,
                  onComplete: () => {
                    scene.outcomeLabelLayer?.removeChild(outcomeState.wrapper);
                    outcomeState.wrapper.destroy();
                    MODULE_OUTCOME_LABELS.delete(outcomeKey);
                  }
                });
              }
            }

            // === SCOREBOX WIPE FOR EACH HAND ===
            // Trigger scorebox DOWN wipe when each hand is raked (same timing as single hand rake)
            {
              const RAKE_SCOREBOX_WIPE_DELAY = 225;
              const RAKE_SCOREBOX_WIPE_DURATION = 300;
              console.log(`[SPLIT RAKE SCOREBOX] Hand ${handIndex + 1} - scheduling wipe in ${RAKE_SCOREBOX_WIPE_DELAY}ms`);
              setTimeout(() => {
                // Only add to MODULE_RAKE_SEATS_IN_PROGRESS on last hand (controls final "--" persistence)
                if (seq === handOrder.length - 1) {
                  MODULE_RAKE_SEATS_IN_PROGRESS.add(seatPosition);
                }
                if (scene.scoreboxLayer) {
                  animateScoreboxWipe(scene.scoreboxLayer, seatPosition, 'down', RAKE_SCOREBOX_WIPE_DURATION);
                  console.log(`[SPLIT RAKE] Scorebox wipe DOWN started for hand ${handIndex + 1} (seat ${seatPosition})`);
                }
              }, RAKE_SCOREBOX_WIPE_DELAY);
            }

            // === CARD OPERATIONS (only if we have cards to rake) ===
            if (hasCardsToRake) {
              // CRITICAL: Remove ALL cards for this hand from MODULE_CARD_STATES BEFORE reparenting
              // Reparenting changes sprite.x/y to world coordinates, which would cause
              // getHandBounds() to return wildly incorrect values (mask goes "crazy wide")
              handCards!.forEach(({ key, state }) => {
                const currentState = MODULE_CARD_STATES.get(key);
                if (currentState && currentState.sprite === state.sprite) {
                  MODULE_CARD_STATES.delete(key);
                }
              });

              // Contract mask to seat bounds now that all cards are removed from tracking
              const rakeMaskState = scene.seatMaskState.get(seatPosition);
              if (rakeMaskState?.isEnabled && !rakeMaskState.isAnimating) {
                // No cards remain for this hand in MODULE_CARD_STATES, so getHandBounds returns null
                // which causes adjustMaskToHandBounds to return early. Instead, reset to seat bounds.
                const mask = scene.seatCardMasks.get(seatPosition);
                const debugBorder = scene.seatMaskDebugBorders.get(seatPosition);
                if (mask && !mask.destroyed) {
                  rakeMaskState.leftEdge = rakeMaskState.seatLeftEdge;
                  rakeMaskState.rightEdge = rakeMaskState.seatRightEdge;
                  rakeMaskState.topEdge = rakeMaskState.seatTopEdge;
                  rakeMaskState.bottomEdge = rakeMaskState.seatBottomEdge;
                  redrawMaskElastic(mask, rakeMaskState.leftEdge, rakeMaskState.rightEdge, rakeMaskState.topEdge, rakeMaskState.bottomEdge);
                  if (MASK_DEBUG_ENABLED && debugBorder && !debugBorder.destroyed) {
                    updateMaskDebugBorderElastic(debugBorder, rakeMaskState.leftEdge, rakeMaskState.rightEdge, rakeMaskState.topEdge, rakeMaskState.bottomEdge);
                  }
                  console.log(`[RAKE MASK] Contracted to seat bounds for hand ${handIndex} (all cards removed from tracking)`);
                }
              }

              // Reparent cards for this hand from filmstrip to animationLayer
              handCards!.forEach(({ state }) => {
                const sprite = state.sprite;
                if (sprite.destroyed) return;

                // Get current local position (already at correct cell position due to filmstrip slide)
                const localX = sprite.x - handIndex * FILMSTRIP_CELL_WIDTH;
                const localY = sprite.y;
                const localRotation = sprite.rotation;

                const worldPos = localToWorld(viewportX, viewportY, localX, localY, viewportRotation);
                const worldRotation = viewportRotation + localRotation;

                filmstrip.removeChild(sprite);
                scene.animationLayer?.addChild(sprite);

                sprite.x = worldPos.x;
                sprite.y = worldPos.y;
                sprite.rotation = worldRotation;
              });

              // Sort cards for this hand (highest index first)
              handCards!.sort((a, b) => {
                const aIdx = parseInt(a.key.split('-')[2]) || 0;
                const bIdx = parseInt(b.key.split('-')[2]) || 0;
                return bIdx - aIdx;
              });

              // Set z-index
              handCards!.forEach(({ key, state }, idx) => {
                const cardIdx = parseInt(key.split('-')[2]) || 0;
                state.sprite.zIndex = 10000 + handIndex * 100 + cardIdx;
              });
            }

            // Animate hand label flying OUT UPWARD (toward dealer) while cards rake
            // Uses unified animateHandLabelFlyOut function for consistent behavior
            if (handLabel && seatData && scene.outcomeLabelLayer) {
              const bgPill = (handLabel as any)._bgPill as Graphics;
              const labelWrapper = (handLabel as any)._wrapper as Container | undefined;

              // Calculate seat dimensions for mask (use visualPos to match actual seat rendering)
              const seatConfigLocal = seatConfigs[visualPos ?? 0] || { x: 0, y: 0, rot: 0, scale: 100 };
              const seatScaleLocal = (seatSizeScale / 100) * (seatConfigLocal.scale / 100);
              const scaledSeatHeightLocal = Math.round(168 * seatScaleLocal);
              const scaledSeatWidthLocal = Math.round(144 * seatScaleLocal);

              animateHandLabelFlyOut({
                label: handLabel,
                bg: bgPill,
                seatData: { x: seatData.x, y: seatData.y, rotation: seatData.rotation },
                scaledSeatWidth: scaledSeatWidthLocal,
                scaledSeatHeight: scaledSeatHeightLocal,
                outcomeLabelLayer: scene.outcomeLabelLayer,
                duration: 300,
                debugLabel: `rake-seq${seq}-hand${handIndex}`,
                existingWrapper: labelWrapper ?? existingWrapper ?? undefined
              });
            }

            // Rake cards for this hand (only if we have cards)
            if (hasCardsToRake) {
              const rakeDestination = {
                x: DEALER_POSITION.x,
                y: DEALER_POSITION.y + RAKE_CONFIG.destinationYOffset,
              };

              await new Promise<void>((rakeResolve) => {
                let completed = 0;
                const totalHandCards = handCards!.length;

                handCards!.forEach(({ key, state }, index) => {
                  setTimeout(() => {
                    const sprite = state.sprite;
                    if (sprite.destroyed) {
                      completed++;
                      if (completed >= totalHandCards) rakeResolve();
                      return;
                    }

                    // NOTE: Cards already removed from MODULE_CARD_STATES and mask contracted
                    // before reparenting (see "CARD OPERATIONS" block above)

                    const startX = sprite.x;
                    const startY = sprite.y;
                    const startRotation = sprite.rotation;
                    const controlPoint = calculateRakeControlPoint(startX, startY, DEALER_POSITION.x);

                    animateSpriteArc(
                      sprite,
                      { x: startX, y: startY, rotation: startRotation },
                      rakeDestination,
                      controlPoint,
                      {
                        duration: RAKE_CONFIG.duration,
                        easing: easeInOutCubic,
                        onComplete: () => {
                          scene.animationLayer?.removeChild(sprite);
                          sprite.destroy();
                          setAnimationCardCount(c => c - 1);
                          completed++;
                          if (completed >= totalHandCards) rakeResolve();
                        },
                      }
                    );
                  }, index * RAKE_CONFIG.staggerDelay);
                });

                // Safety timeout for this hand
                setTimeout(rakeResolve, handCards!.length * RAKE_CONFIG.staggerDelay + RAKE_CONFIG.duration + 100);
              });
            } else {
              // No cards to rake - just a brief pause to allow labels to animate
              await new Promise(r => setTimeout(r, 300));
            }

            // Brief pause before next hand
            if (seq < handOrder.length - 1) {
              await new Promise(r => setTimeout(r, 100));
            }
          }

          // Cleanup after all hands raked
          for (let h = 0; h < totalHands; h++) {
            MODULE_STRAIGHTENED.delete(`${seatPosition}-${h}`);
            MODULE_DEALT_CARD_COUNTS.delete(`${seatPosition}-${h}`);
          }
          MODULE_STRAIGHTENED.delete(`${seatPosition}-all`);
          MODULE_SCOREBOX_CACHE.delete(`${seatPosition}`);

          // Reset split state and invisible snap to cell 1
          console.log(`[SPLIT RAKE] Final reset: snap to cell 1 (invisible)`);
          if (splitState) {
            splitState.viewedHandIndex = 0;
            splitState.totalHands = 1;
          }
          slideFilmstripToHand(scene, seatPosition, 0, false);  // false = no animation

          scene.rakeInProgress = false;

          // === DIAGNOSTIC: Log scene state at end of split hand rake ===
          logSceneDiagnostic(scene, `rakeCardsForSeat_COMPLETE(seat=${seatPosition}, split=true)`);

          logTextureCount(`RAKE_END seat=${seatPosition} (split, no cards)`);
          resolve();
          return;
        }

        // === SINGLE HAND RAKE (existing logic, but don't collapse) ===
        // Animate scorebox wipe DOWN to "--" starting 225ms after rake starts
        // Also add seat to MODULE_RAKE_SEATS_IN_PROGRESS to prevent re-renders from recalculating score
        const RAKE_SCOREBOX_WIPE_DELAY = 225;
        const RAKE_SCOREBOX_WIPE_DURATION = 300; // Match outcomeLabelFly duration
        setTimeout(() => {
          MODULE_RAKE_SEATS_IN_PROGRESS.add(seatPosition);
          // Use animated wipe instead of direct text change
          if (scene.scoreboxLayer) {
            animateScoreboxWipe(scene.scoreboxLayer, seatPosition, 'down', RAKE_SCOREBOX_WIPE_DURATION);
            console.log(`[RAKE] Scorebox wipe DOWN started at ${RAKE_SCOREBOX_WIPE_DELAY}ms (seat ${seatPosition} marked as raking)`);
          }
        }, RAKE_SCOREBOX_WIPE_DELAY);

        // Animate outcome label out
        const seatOutcomeFlyDuration = getClientAnimationTiming('outcomeLabelFly', 'rakeSeatCards');
        MODULE_OUTCOME_LABELS.forEach((state, outcomeKey) => {
          if (outcomeKey.startsWith(`${seatPosition}-`) && state.state !== 'animating-out' && !state.wrapper.destroyed) {
            state.state = 'animating-out';
            const outcomeLabel = state.wrapper.children[1] as Container;
            if (outcomeLabel) {
              animateSprite(outcomeLabel, { y: outcomeLabel.y + 30 }, {
                duration: seatOutcomeFlyDuration,
                easing: easeInCubic,
                onComplete: () => {
                  scene.outcomeLabelLayer?.removeChild(state.wrapper);
                  state.wrapper.destroy();
                  MODULE_OUTCOME_LABELS.delete(outcomeKey);
                }
              });
            }
          }
        });

        // Sort by card index (highest first)
        cardsToRake.sort((a, b) => {
          const aIndex = parseInt(a.key.split('-')[2]) || 0;
          const bIndex = parseInt(b.key.split('-')[2]) || 0;
          return bIndex - aIndex;
        });

        // CRITICAL: Remove ALL cards from MODULE_CARD_STATES BEFORE reparenting
        // Reparenting changes sprite.x/y to world coordinates, which would cause
        // getHandBounds() to return wildly incorrect values (mask goes "crazy wide")
        cardsToRake.forEach(({ key, state }) => {
          const currentState = MODULE_CARD_STATES.get(key);
          if (currentState && currentState.sprite === state.sprite) {
            MODULE_CARD_STATES.delete(key);
          }
        });

        // Contract mask to seat bounds now that all cards are removed from tracking
        const rakeMaskState = scene.seatMaskState.get(seatPosition);
        if (rakeMaskState?.isEnabled && !rakeMaskState.isAnimating) {
          const mask = scene.seatCardMasks.get(seatPosition);
          const debugBorder = scene.seatMaskDebugBorders.get(seatPosition);
          if (mask && !mask.destroyed) {
            rakeMaskState.leftEdge = rakeMaskState.seatLeftEdge;
            rakeMaskState.rightEdge = rakeMaskState.seatRightEdge;
            rakeMaskState.topEdge = rakeMaskState.seatTopEdge;
            rakeMaskState.bottomEdge = rakeMaskState.seatBottomEdge;
            redrawMaskElastic(mask, rakeMaskState.leftEdge, rakeMaskState.rightEdge, rakeMaskState.topEdge, rakeMaskState.bottomEdge);
            if (MASK_DEBUG_ENABLED && debugBorder && !debugBorder.destroyed) {
              updateMaskDebugBorderElastic(debugBorder, rakeMaskState.leftEdge, rakeMaskState.rightEdge, rakeMaskState.topEdge, rakeMaskState.bottomEdge);
            }
            console.log(`[RAKE MASK] Contracted to seat bounds (all cards removed from tracking)`);
          }
        }

        // Reparent cards to animation layer
        if (viewport && filmstrip && !viewport.destroyed && !filmstrip.destroyed) {
          cardsToRake.forEach(({ key, state }) => {
            const sprite = state.sprite;
            if (sprite.destroyed) return;

            const parts = key.split('-');
            const handIndex = parseInt(parts[1], 10) || 0;

            const localX = sprite.x - handIndex * FILMSTRIP_CELL_WIDTH;
            const localY = sprite.y;
            const localRotation = sprite.rotation;

            const worldPos = localToWorld(viewportX, viewportY, localX, localY, viewportRotation);
            const worldRotation = viewportRotation + localRotation;

            filmstrip.removeChild(sprite);
            scene.animationLayer?.addChild(sprite);

            sprite.x = worldPos.x;
            sprite.y = worldPos.y;
            sprite.rotation = worldRotation;
          });
        }

        // Set z-index
        cardsToRake.forEach(({ key, state }) => {
          const parts = key.split('-');
          const handIdx = parseInt(parts[1], 10) || 0;
          const cardIdx = parseInt(parts[2], 10) || 0;
          state.sprite.zIndex = 10000 + handIdx * 100 + cardIdx;
        });

        const rakeDestination = {
          x: DEALER_POSITION.x,
          y: DEALER_POSITION.y + RAKE_CONFIG.destinationYOffset,
        };

        let completed = 0;
        const totalCards = cardsToRake.length;

        cardsToRake.forEach(({ key, state }, index) => {
          setTimeout(() => {
            const sprite = state.sprite;
            if (sprite.destroyed) {
              completed++;
              if (completed >= totalCards) resolve();
              return;
            }

            // NOTE: Cards already removed from MODULE_CARD_STATES and mask contracted
            // before reparenting (see above)

            const startX = sprite.x;
            const startY = sprite.y;
            const startRotation = sprite.rotation;

            const controlPoint = calculateRakeControlPoint(startX, startY, DEALER_POSITION.x);

            animateSpriteArc(
              sprite,
              { x: startX, y: startY, rotation: startRotation },
              rakeDestination,
              controlPoint,
              {
                duration: RAKE_CONFIG.duration,
                easing: easeInOutCubic,
                onComplete: () => {
                  scene.animationLayer?.removeChild(sprite);
                  sprite.destroy();
                  setAnimationCardCount(c => c - 1);

                  completed++;
                  if (completed >= totalCards) {
                    MODULE_STRAIGHTENED.delete(`${seatPosition}-0`);
                    MODULE_STRAIGHTENED.delete(`${seatPosition}-all`);
                    MODULE_STRAIGHTENED.delete(`${seatPosition}-1`);
                    MODULE_STRAIGHTENED.delete(`${seatPosition}-2`);
                    MODULE_STRAIGHTENED.delete(`${seatPosition}-3`);
                    MODULE_SCOREBOX_CACHE.delete(`${seatPosition}`);
                    MODULE_DEALT_CARD_COUNTS.delete(`${seatPosition}-0`);
                    MODULE_DEALT_CARD_COUNTS.delete(`${seatPosition}-1`);
                    MODULE_DEALT_CARD_COUNTS.delete(`${seatPosition}-2`);
                    MODULE_DEALT_CARD_COUNTS.delete(`${seatPosition}-3`);
                    slideFilmstripToHand(scene, seatPosition, 0, false);
                    scene.rakeInProgress = false;

                    // === DIAGNOSTIC: Log scene state at end of single hand rake ===
                    logSceneDiagnostic(scene, `rakeCardsForSeat_COMPLETE(seat=${seatPosition}, split=false)`);

                    logTextureCount(`RAKE_END seat=${seatPosition} (single hand)`);
                    resolve();
                  }
                },
              }
            );
          }, index * RAKE_CONFIG.staggerDelay);
        });

        // Safety timeout
        const maxTime = totalCards * RAKE_CONFIG.staggerDelay + RAKE_CONFIG.duration + 100;
        setTimeout(() => {
          slideFilmstripToHand(scene, seatPosition, 0, false);
          scene.rakeInProgress = false;
          logTextureCount(`RAKE_END seat=${seatPosition} (safety timeout)`);
          resolve();
        }, maxTime);
      });
    },

    // Rake ONLY dealer cards (new multiplayer architecture)
    // Fires after ALL player rakes complete, before dealing starts
    rakeDealerCards: (): Promise<void> => {
      return new Promise((resolve) => {
        console.log('[PixiGameTableV1] Rake dealer cards only');

        const scene = sceneRef.current;
        if (!scene.animationLayer) {
          resolve();
          return;
        }

        // Disable card straightening during rake
        scene.rakeInProgress = true;

        // DIAGNOSTIC: Log all keys in MODULE_CARD_STATES to trace rake issue
        console.log('[DEALER_RAKE_DEBUG] MODULE_CARD_STATES keys:', Array.from(MODULE_CARD_STATES.keys()));
        console.log('[DEALER_RAKE_DEBUG] MODULE_CARD_STATES size:', MODULE_CARD_STATES.size);

        // Find all dealer cards
        const dealerCardsToRake: { key: string; state: CardSpriteState }[] = [];
        MODULE_CARD_STATES.forEach((state, key) => {
          const startsWithDealer = key.startsWith('dealer-');
          const hasIsDealer = state.isDealer;
          if (startsWithDealer || hasIsDealer) {
            console.log(`[DEALER_RAKE_DEBUG] Key: ${key}, startsWithDealer=${startsWithDealer}, hasIsDealer=${hasIsDealer}, destroyed=${state.sprite?.destroyed}`);
          }
          if (startsWithDealer && hasIsDealer) {
            dealerCardsToRake.push({ key, state });
          }
        });

        if (dealerCardsToRake.length === 0) {
          console.log('[PixiGameTableV1] No dealer cards to rake');
          console.log('[DEALER_RAKE_DEBUG] PROBLEM: No dealer cards found but DEALER_RAKE was triggered!');
          scene.rakeInProgress = false;
          resolve();
          return;
        }

        // Sound: rake ONCE at start of dealer rake operation (not per card)
        playTableSound('rake');

        console.log(`[PixiGameTableV1] Raking ${dealerCardsToRake.length} dealer cards: ${dealerCardsToRake.map(c => c.key).join(', ')}`);

        // Sort by card index in REVERSE order - top of stack rakes first
        dealerCardsToRake.sort((a, b) => {
          const aIndex = parseInt(a.key.split('-')[2]) || 0;
          const bIndex = parseInt(b.key.split('-')[2]) || 0;
          return bIndex - aIndex;
        });

        // Set high zIndex on all cards to be raked
        dealerCardsToRake.forEach(({ state }) => {
          state.sprite.zIndex = 10000;
        });

        // Rake destination: dealer center, 200px above
        const rakeDestination = {
          x: DEALER_POSITION.x,
          y: DEALER_POSITION.y + RAKE_CONFIG.destinationYOffset,
        };

        let completedCount = 0;
        const totalCards = dealerCardsToRake.length;

        // Animate each card
        dealerCardsToRake.forEach(({ key, state }, index) => {
          setTimeout(() => {
            animateSprite(
              state.sprite,
              { x: rakeDestination.x, y: rakeDestination.y },
              {
                duration: RAKE_CONFIG.duration,
                easing: easeInOutCubic,
                onComplete: () => {
                  console.log(`[DEALER_RAKE_DEBUG] onComplete for ${key} - removing and destroying sprite`);

                  // Remove from animation layer and destroy
                  scene.animationLayer?.removeChild(state.sprite);
                  if (!state.sprite.destroyed) {
                    state.sprite.destroy();
                  }

                  // RACE CONDITION FIX: Only delete from MODULE_CARD_STATES if the sprite
                  // we animated is still the one tracked there. If fullVisualResync ran during
                  // our animation, it created NEW sprites with the same keys - don't orphan them!
                  const currentState = MODULE_CARD_STATES.get(key);
                  if (currentState && currentState.sprite === state.sprite) {
                    console.log(`[DEALER_RAKE_DEBUG] Deleting ${key} from MODULE_CARD_STATES`);
                    MODULE_CARD_STATES.delete(key);
                  } else {
                    console.log(`[DEALER_RAKE_DEBUG] NOT deleting ${key} - sprite mismatch (resync occurred)`);
                  }

                  completedCount++;
                  if (completedCount === totalCards) {
                    // Clear dealer-related state
                    MODULE_STRAIGHTENED.delete('dealer');
                    MODULE_SCOREBOX_CACHE.delete('dealer');
                    MODULE_DEALT_CARD_COUNTS.delete('dealer-0');
                    // Re-enable card straightening
                    scene.rakeInProgress = false;
                    resolve();
                  }
                },
              }
            );
          }, index * RAKE_CONFIG.staggerDelay);
        });

        // Safety timeout
        const maxTime = totalCards * RAKE_CONFIG.staggerDelay + RAKE_CONFIG.duration + 100;
        setTimeout(() => {
          scene.rakeInProgress = false;
          resolve();
        }, maxTime);
      });
    },

    // ===========================================
    // CARD STRAIGHTENING METHODS
    // ===========================================

    straightenSeatCards: (seatPosition: number, handIndex: number = -1): Promise<void> => {
      return new Promise((resolve) => {
        // Helper to log to both console and on-screen debug panel
        const dbg = (msg: string) => {
          console.log(msg);
          const w = window as unknown as { pixiDebugLog?: (m: string) => void };
          w.pixiDebugLog?.(msg);
        };

        dbg(`=== STRAIGHTEN seat=${seatPosition} hand=${handIndex} ===`);

        // Guard: Don't allow straightening during rake animation
        if (sceneRef.current?.rakeInProgress) {
          dbg(`[STRAIGHTEN] Blocked - rake in progress`);
          resolve();
          return;
        }

        dbg(`[MODULE] Card states: ${MODULE_CARD_STATES.size} entries`);

        // When handIndex is -1, straighten ALL hands for this seat
        const straightenAllHands = handIndex === -1;

        // Log all card states for debugging
        const allKeys = Array.from(MODULE_CARD_STATES.keys());
        dbg(`Total cards: ${allKeys.length} | Keys: ${allKeys.join(', ')}`);

        // Find all cards for this seat (all hands if handIndex is -1, specific hand otherwise)
        const searchPattern = straightenAllHands ? `${seatPosition}-` : `${seatPosition}-${handIndex}-`;
        dbg(`Pattern: "${searchPattern}"`);

        const cardsToStraighten: { key: string; state: CardSpriteState; cardIndex: number; handIdx: number }[] = [];

        for (const [key, state] of MODULE_CARD_STATES.entries()) {
          const matches = key.startsWith(searchPattern) && !state.isDealer;
          if (matches) {
            const parts = key.split('-');
            const handIdx = parseInt(parts[1], 10);
            const cardIdx = parseInt(parts[2], 10);
            dbg(`MATCH: ${key} → hand=${handIdx} cardIdx=${cardIdx}`);
            cardsToStraighten.push({ key, state, cardIndex: cardIdx, handIdx });
          }
        }

        dbg(`Found ${cardsToStraighten.length} cards to straighten`);

        if (cardsToStraighten.length === 0) {
          dbg('No cards found!');
          resolve();
          return;
        }

        // Sort by hand index, then card index for proper ordering
        cardsToStraighten.sort((a, b) => {
          if (a.handIdx !== b.handIdx) return a.handIdx - b.handIdx;
          return a.cardIndex - b.cardIndex;
        });

        dbg(`Found ${cardsToStraighten.length} cards to straighten`);

        // Mark as straightened
        const straightenKey = straightenAllHands ? `${seatPosition}-all` : `${seatPosition}-${handIndex}`;
        MODULE_STRAIGHTENED.set(straightenKey, true);

        // Get seat position data for recalculating uniform positions
        const visualPos = getVisualPositionForSeat(seatPosition);
        const pos = dynamicSeatPositions[visualPos - 1];
        const seatRotationRad = degreesToRadians(pos.rotation);
        const currentCardConfig = cardConfigRef.current;

        // Get per-seat card config (uses VISUAL position - configs are about screen location, not physical seat)
        // Use ref to get latest config value (avoids closure issues)
        const perSeatConfig = perSeatCardConfigsRef.current?.[visualPos] || { x: 0, y: 0, radiusOffset: 0 };
        const seatCardsYOffset = currentCardConfig.seatCardsY + perSeatConfig.radiusOffset;

        // Calculate scaled card overlap based on card scale
        // This ensures proper spacing regardless of card size configuration
        const scaleFactor = currentCardConfig.scale / 100;
        const scaledOverlap = CARD_OVERLAP * scaleFactor;
        dbg(`Card scale: ${currentCardConfig.scale}%, scaledOverlap: ${scaledOverlap.toFixed(1)}px`);

        // Group cards by hand for proper centering calculation
        const cardsByHand = new Map<number, typeof cardsToStraighten>();
        for (const card of cardsToStraighten) {
          const handCards = cardsByHand.get(card.handIdx) || [];
          handCards.push(card);
          cardsByHand.set(card.handIdx, handCards);
        }

        // === FILMSTRIP LOCAL COORDINATES ===
        // Cards are positioned in LOCAL coordinates within the filmstrip.
        // Straightening animates to the correct local position (centered, no randomness).
        let completed = 0;
        for (const [handIdx, handCards] of cardsByHand) {
          const totalCards = handCards.length;
          // Base z-index for this hand (different hands get different ranges to avoid conflicts)
          const handBaseZIndex = handIdx * 100;

          for (const { key, state, cardIndex } of handCards) {
            // Calculate LOCAL position within filmstrip (with per-seat offsets)
            const centeredOffset = calculateCardCenteredOffset(cardIndex, totalCards, scaledOverlap);
            const localBaseX = centeredOffset + handIdx * FILMSTRIP_CELL_WIDTH + perSeatConfig.x;
            const localBaseY = seatCardsYOffset + perSeatConfig.y;
            const localBaseRotation = 0; // Upright relative to filmstrip

            // Update stored base position (LOCAL coordinates)
            state.baseX = localBaseX;
            state.baseY = localBaseY;
            state.baseRotation = localBaseRotation;

            // Clear randomness since we're straightening
            state.randomness = { offsetX: 0, offsetY: 0, rotation: 0 };

            // Set z-index so cards overlap properly: higher cardIndex = on top (rightmost overlaps leftmost)
            state.sprite.zIndex = handBaseZIndex + cardIndex;

            const curX = state.sprite.x.toFixed(0);
            const curY = state.sprite.y.toFixed(0);
            dbg(`${key}: (${curX},${curY}) → (${localBaseX.toFixed(0)},${localBaseY.toFixed(0)}) z=${state.sprite.zIndex}`);

            const straightenDuration = getClientAnimationTiming('cardStraighten', 'straightenPlayerHand');
            // Animate in LOCAL coordinates (card is in filmstrip container)
            animateSprite(state.sprite, {
              x: localBaseX,
              y: localBaseY,
              rotation: localBaseRotation,
            }, {
              duration: straightenDuration,
              easing: linear, // Linear for player straightening (matches React)
              onComplete: () => {
                completed++;
                if (completed === cardsToStraighten.length) {
                  dbg('=== ALL COMPLETE ===');

                  // Adjust mask to new hand bounds after straightening
                  // Cards are now upright (rotation=0), so the hand bounds may have changed
                  const scene = sceneRef.current;
                  if (scene) {
                    const filmstrip = scene.seatCardContents.get(seatPosition);
                    if (filmstrip) {
                      const activeHandIndex = Math.round(-filmstrip.x / FILMSTRIP_CELL_WIDTH);
                      adjustMaskToHandBounds(scene, seatPosition, activeHandIndex);
                    }
                  }

                  resolve();
                }
              },
            });
          }
        }
      });
    },

    straightenDealerCards: (): Promise<void> => {
      const orchestrator = getAnimationOrchestrator();

      // Use animateSingleton for synchronous registration (mutual exclusion with revealHoleCard)
      return orchestrator.animateSingleton('dealer-straighten', 'DEALER_STRAIGHTEN', async () => {
        // Safety timeout: resolve after 2 seconds no matter what
        // This prevents the promise from blocking forever if animations fail
        const SAFETY_TIMEOUT_MS = 2000;

        await new Promise<void>(async (resolveAnimation) => {
          let resolved = false;
          const safeResolve = () => {
            if (!resolved) {
              resolved = true;
              resolveAnimation();
            }
          };

          // Safety timeout
          const safetyTimer = setTimeout(() => {
            if (!resolved) {
              console.warn('[straightenDealerCards] Safety timeout - resolving to unblock event chain');
              safeResolve();
            }
          }, SAFETY_TIMEOUT_MS);
          const straightenKey = 'dealer';

          // CRITICAL: If hole card reveal is in progress, wait for it to complete
          // revealHoleCard() does flip + center atomically, so we must not interfere
          await orchestrator.waitForType('HOLE_CARD_REVEAL');

          // Check if already straightened (reveal may have done it)
          if (MODULE_STRAIGHTENED.get(straightenKey)) {
            console.log('[straightenDealerCards] Already straightened, skipping');
            clearTimeout(safetyTimer);
            safeResolve();
            return;
          }

        // Get expected dealer card count from game state
        const gameState = useGameStateStore.getState();
        const expectedDealerCards = gameState.dealer.cards.length;
        const currentCardConfig = cardConfigRef.current;

        // Helper to find all dealer cards currently in MODULE_CARD_STATES
        const findDealerCards = () => {
          const cards: { key: string; state: CardSpriteState; cardIndex: number }[] = [];
          MODULE_CARD_STATES.forEach((state, key) => {
            if (key.startsWith('dealer-') && state.isDealer) {
              // Extract card index from key (e.g., "dealer-0-1" -> 1)
              const parts = key.split('-');
              const cardIndex = parseInt(parts[2], 10);
              cards.push({ key, state, cardIndex });
            }
          });
          // Sort by card index for consistent ordering
          return cards.sort((a, b) => a.cardIndex - b.cardIndex);
        };

        // Wait for all expected dealer cards to be present (handles timing where
        // PLAYER_TURNS_STARTED arrives before hole card animation completes)
        const maxWaitTime = 500; // Max 500ms wait
        const pollInterval = 16; // ~60fps
        let waitedTime = 0;

        const waitForCards = () => {
          const cardsToStraighten = findDealerCards();

          if (cardsToStraighten.length >= expectedDealerCards || waitedTime >= maxWaitTime) {
            // All cards present or timeout - proceed with straightening
            if (cardsToStraighten.length === 0) {
              console.log('[straightenDealerCards] No cards found, resolving');
              clearTimeout(safetyTimer);
              safeResolve();
              return;
            }

            if (cardsToStraighten.length < expectedDealerCards) {
              console.warn(`[straightenDealerCards] Timeout waiting for dealer cards. Found ${cardsToStraighten.length}, expected ${expectedDealerCards}`);
            }

            // Mark as straightened
            MODULE_STRAIGHTENED.set(straightenKey, true);

            // Calculate uniform positions for all cards (enforce consistent fanning)
            const totalCards = cardsToStraighten.length;
            const dealerCardsX = currentCardConfig.dealerCardsX;
            const dealerCardsY = currentCardConfig.dealerCardsY;
            const baseY = DEALER_POSITION.y + dealerCardsY;
            const baseRotation = 0;

            // Check if hole card has been revealed
            const holeCardRevealed = gameState.dealer.holeCardRevealed;

            // Scale factor for gap calculation
            const scaleFactor = currentCardConfig.scale / 100;
            const scaledCardWidth = CARD_WIDTH * scaleFactor;
            const scaledOverlap = CARD_OVERLAP * scaleFactor;

            // Before hole card reveal with 2 cards: use 8px gap positioning
            // After reveal or with more cards: use overlap positioning
            const useGapPositioning = !holeCardRevealed && totalCards === 2;

            // Gap positioning: 8px gap between scaled card edges
            const twoCardCenterOffset = (scaledCardWidth + 8) / 2;

            // Animate all cards to their RECALCULATED positions (not stored baseX)
            let completed = 0;
            cardsToStraighten.forEach(({ state, cardIndex }) => {
              // Calculate the correct X position for this card index
              let correctBaseX: number;

              if (useGapPositioning) {
                // Gap positioning for initial 2 cards before reveal
                correctBaseX = cardIndex === 0
                  ? DEALER_POSITION.x + dealerCardsX - twoCardCenterOffset
                  : DEALER_POSITION.x + dealerCardsX + twoCardCenterOffset;
              } else {
                // Overlap positioning for revealed/3+ cards using scaled overlap
                correctBaseX = DEALER_POSITION.x + dealerCardsX + calculateCardCenteredOffset(cardIndex, totalCards, scaledOverlap);
              }

              // Update stored base position to the correct value
              state.baseX = correctBaseX;
              state.baseY = baseY;
              state.baseRotation = baseRotation;

              const dealerStraightenDuration = getClientAnimationTiming('cardStraighten', 'straightenDealerCards');
              animateSprite(state.sprite, {
                x: correctBaseX,
                y: baseY,
                rotation: baseRotation,
              }, {
                duration: dealerStraightenDuration,
                easing: easeOutCubic, // Ease-out for dealer straightening (matches React)
                onComplete: () => {
                  completed++;
                  if (completed === cardsToStraighten.length) {
                    console.log('[straightenDealerCards] All animations complete');
                    clearTimeout(safetyTimer);
                    safeResolve();
                  }
                },
              });
            });
          } else {
            // Not all cards present yet - continue polling
            waitedTime += pollInterval;
            setTimeout(waitForCards, pollInterval);
          }
        };

          // Start polling for cards
          waitForCards();
        });
      });
    },

    /**
     * Clear kitty display for a specific seat or all seats.
     * Used at round end to reset kitty state for new bets.
     * @param seatPosition - Server seat position (0-6), or undefined to clear all
     * @param handIndex - Hand index (0 = main hand, 1+ = split hands). If undefined, clears main hand only.
     */
    clearKitty: (seatPosition?: number, handIndex?: number): void => {
      const scene = sceneRef.current;

      const clearMainKitty = (visualPos: number) => {
        // Remove container from layer
        const container = scene.kittyContainers.get(visualPos);
        if (container) {
          console.log(`[clearKitty] Destroying main kitty for visualPos=${visualPos}`);
          scene.animationLayer?.removeChild(container);
          container.destroy({ children: true });
        }

        // Clear all tracking data
        scene.kittyContainers.delete(visualPos);
        scene.kittyData.delete(visualPos);
        scene.kittyChipSprites.delete(visualPos);
        scene.kittyCounterTexts.delete(visualPos);
        scene.kittyPills.delete(visualPos);
        scene.kittyChipsWrappers.delete(visualPos);
      };

      const clearSplitKitty = (seatPos: number, hIndex: number) => {
        const kittyKey = `${seatPos}-${hIndex}`;
        const container = scene.splitKittyContainers.get(kittyKey);
        if (container) {
          console.log(`[clearKitty] Destroying split kitty for key=${kittyKey}`);
          scene.animationLayer?.removeChild(container);
          container.destroy({ children: true });
        }

        // Clear all tracking data
        scene.splitKittyContainers.delete(kittyKey);
        scene.splitKittyData.delete(kittyKey);
        scene.splitKittyChipSprites.delete(kittyKey);
        scene.splitKittyCounterTexts.delete(kittyKey);
        scene.splitKittyPills.delete(kittyKey);
      };

      if (seatPosition !== undefined) {
        if (handIndex !== undefined && handIndex > 0) {
          // Clear specific split hand kitty
          clearSplitKitty(seatPosition, handIndex);
        } else {
          // Clear main hand kitty (handIndex = 0 or undefined)
          const visualPos = getVisualPositionForSeat(seatPosition);
          clearMainKitty(visualPos);
        }
      } else {
        // Clear all seats (main kitties only - split kitties cleared via resetSplitHandState)
        for (let vp = 1; vp <= 7; vp++) {
          clearMainKitty(vp);
        }
      }
    },

    /**
     * Recenter split kitties after one is removed (e.g., mid-round bust).
     * Calculates new positions based on remaining kitties and animates them to fill the gap.
     * @param seatPosition - Server seat position
     */
    recenterSplitKitties: (seatPosition: number): void => {
      const scene = sceneRef.current;
      const RECENTER_DURATION = 200;

      // Get visual position for seat
      const visualPos = getVisualPositionForSeat(seatPosition);
      if (visualPos === null) {
        console.warn(`[recenterSplitKitties] No visual position for seat ${seatPosition}`);
        return;
      }

      // Get seat position data
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) {
        console.warn(`[recenterSplitKitties] No seat data for visualPos ${visualPos}`);
        return;
      }

      const seatRotRad = degreesToRadians(seatData.rotation);
      const betConfig = betChipConfigs[seatPosition] || REGISTRY_DEFAULT_CHIP_CONFIG;
      const kittyPosResult = calculateKittyPosition(seatData, {
        betChipConfig: betConfig,
        kittyOffsetX: kittyConfig?.x ?? 0,
        kittyOffsetY: kittyConfig?.y ?? 0,
      });
      const baseKittyPos = { x: kittyPosResult.x, y: kittyPosResult.y };

      // Gather existing kitties and their pill widths
      const existingKitties: { handIndex: number; container: Container; pillWidth: number }[] = [];

      // Check main kitty (hand 0)
      const mainKitty = scene.kittyContainers.get(visualPos);
      const mainKittyData = scene.kittyData.get(visualPos);
      if (mainKitty && !mainKitty.destroyed && mainKittyData && mainKittyData.denominations.length > 0) {
        const mainPill = scene.kittyPills.get(visualPos);
        existingKitties.push({
          handIndex: 0,
          container: mainKitty,
          pillWidth: getKittyPillWidth(mainPill),
        });
      }

      // Check split kitties (hands 1+)
      scene.splitKittyContainers.forEach((container, key) => {
        if (container.destroyed) return;
        const parts = key.split('-');
        if (parseInt(parts[0], 10) !== seatPosition) return;

        const handIndex = parseInt(parts[1], 10);
        const splitData = scene.splitKittyData.get(key);
        if (splitData && splitData.denominations.length > 0) {
          const splitPill = scene.splitKittyPills.get(key);
          existingKitties.push({
            handIndex,
            container,
            pillWidth: getKittyPillWidth(splitPill),
          });
        }
      });

      // Sort by hand index
      existingKitties.sort((a, b) => a.handIndex - b.handIndex);

      if (existingKitties.length === 0) {
        console.log(`[recenterSplitKitties] No kitties to recenter for seat ${seatPosition}`);
        return;
      }

      // Calculate dynamic offsets based on pill widths
      const pillWidths = existingKitties.map(k => k.pillWidth);
      const dynamicOffsets = calculateDynamicKittyOffsets(pillWidths, SPLIT_KITTY_PILL_PADDING);

      console.log(`[recenterSplitKitties] Recentering ${existingKitties.length} kitties for seat ${seatPosition}`);

      // Animate each kitty to its new position
      existingKitties.forEach((kittyInfo, index) => {
        const targetPos = registryApplyTangentOffset(baseKittyPos, dynamicOffsets[index], seatRotRad);
        console.log(`[recenterSplitKitties] Hand ${kittyInfo.handIndex}: offset=${dynamicOffsets[index].toFixed(0)} -> (${targetPos.x.toFixed(0)}, ${targetPos.y.toFixed(0)})`);

        animateSprite(kittyInfo.container, { x: targetPos.x, y: targetPos.y }, {
          duration: RECENTER_DURATION,
          easing: easeOutCubic,
          debugName: `recenter-kitty-${seatPosition}-${kittyInfo.handIndex}`,
        });
      });
    },

    /**
     * Get all chip denominations currently in a seat's kitty.
     * Returns array of denominations (e.g., [25, 25, 100] for $150).
     * Used by payout handlers to know what chips to animate out.
     * @param seatPosition - Server seat position
     * @param handIndex - Hand index (0 = main hand, 1+ = split hands)
     */
    getKittyChips: (seatPosition: number, handIndex: number = 0): number[] => {
      const scene = sceneRef.current;

      if (handIndex === 0) {
        // Main hand - use regular kitty
        const visualPos = getVisualPositionForSeat(seatPosition);
        const kittyData = scene.kittyData.get(visualPos);
        console.log(`[getKittyChips] seat=${seatPosition} hand=${handIndex} (main) -> ${kittyData?.denominations?.length ?? 0} chips`);
        return kittyData?.denominations ? [...kittyData.denominations] : [];
      } else {
        // Split hand - use split kitty
        const kittyKey = `${seatPosition}-${handIndex}`;
        const kittyData = scene.splitKittyData.get(kittyKey);
        console.log(`[getKittyChips] seat=${seatPosition} hand=${handIndex} (split key=${kittyKey}) -> ${kittyData?.denominations?.length ?? 0} chips`);
        return kittyData?.denominations ? [...kittyData.denominations] : [];
      }
    },

    /**
     * IMPERATIVE: Show outcome label for a seat. Bypasses React render cycle.
     * Returns promise that resolves when fly-in animation completes.
     * This is the EVENT-DRIVEN approach - call directly, await completion.
     */
    showOutcomeLabel: (seatPosition: number, handIndex: number, outcome: string): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[OUTCOME-LABEL] showOutcomeLabel called imperatively: seat=${seatPosition} hand=${handIndex} outcome=${outcome}`);

        const scene = sceneRef.current;
        if (!scene.outcomeLabelLayer) {
          console.log(`[OUTCOME-LABEL] No outcomeLabelLayer, resolving immediately`);
          resolve();
          return;
        }

        const visualPos = getVisualPositionForSeat(seatPosition);
        const pos = dynamicSeatPositions[visualPos - 1];
        if (!pos) {
          console.log(`[OUTCOME-LABEL] No position for seat ${seatPosition}, resolving immediately`);
          resolve();
          return;
        }

        const outcomeKey = `${seatPosition}-${handIndex}`;
        const existingState = MODULE_OUTCOME_LABELS.get(outcomeKey);

        // Skip if already exists with same outcome
        if (existingState && existingState.outcome === outcome && existingState.state !== 'animating-out') {
          console.log(`[OUTCOME-LABEL] Already exists for ${outcomeKey}, resolving immediately`);
          resolve();
          return;
        }

        // Calculate dimensions (must match createSeatContainer)
        const seatConfig = seatConfigs[visualPos] || DEFAULT_SEAT_CONFIG;
        const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
        const width = Math.round(144 * seatScale);
        const height = Math.round(168 * seatScale);

        // Create wrapper at seat position (must match createSeatContainer's adjusted coords)
        const seatX = pos.x + seatConfig.x;
        const seatY = pos.y + seatConfig.y;
        const seatRotation = pos.rotation + seatConfig.rot;

        const outcomeLabelWrapper = new Container();
        outcomeLabelWrapper.label = 'outcomeLabelWrapper';
        (outcomeLabelWrapper as unknown as { isOutcomeLabelWrapper: boolean }).isOutcomeLabelWrapper = true;
        outcomeLabelWrapper.x = seatX;
        outcomeLabelWrapper.y = seatY;
        outcomeLabelWrapper.rotation = degreesToRadians(seatRotation);

        // Create mask matching seat box dimensions
        const outcomeMask = new Graphics();
        outcomeMask.roundRect(-width / 2, -height / 2, width, height, 8);
        outcomeMask.fill({ color: 0xffffff });
        outcomeLabelWrapper.addChild(outcomeMask);
        outcomeLabelWrapper.mask = outcomeMask;

        // Create the label
        const outcomeLabel = createOutcomeLabel(outcome, outcomeLabelConfig, texturesRef.current);
        const outcomeBounds = outcomeLabel.getLocalBounds();
        // Account for scale applied in createOutcomeLabel
        const labelScale = outcomeLabel.scale.y;
        const visualHeight = outcomeBounds.height * labelScale;

        // Calculate collapsed scorebox width for resolved score
        // When outcome label shows, the hand is resolved, so score displays as "XX" not "XX/XX"
        // We calculate this width directly rather than reading the scorebox (which may not
        // have re-rendered yet from soft format to collapsed format)
        let scoreboxWidth = scoreboxConfig.minWidth ?? 35; // Fallback
        const latestScore = getLatestScore(seatPosition, handIndex);
        if (latestScore) {
          // Calculate width for collapsed score display using cached measurements
          const boxScale = scoreboxConfig.scale / 100;
          const textWidth = getScoreTextWidth(latestScore.score, scoreboxConfig.fontSize);
          const calculatedWidth = Math.max(12, textWidth) * boxScale;
          // Apply minWidth if configured (matches createScoreBox logic)
          scoreboxWidth = scoreboxConfig.minWidth
            ? Math.max(scoreboxConfig.minWidth, calculatedWidth)
            : calculatedWidth;
        }

        // Position outcome label:
        // - Left edge flush with right edge of scorebox
        // - Bottom edge aligned with scorebox bottom (which is at height/2 + scoreboxConfig.y)
        const scoreboxLocalX = -width / 2 + scoreboxConfig.x;
        const finalX = scoreboxLocalX + scoreboxWidth;
        const scoreboxBottom = height / 2 + scoreboxConfig.y;
        outcomeLabel.y = scoreboxBottom - visualHeight;
        outcomeLabelWrapper.addChild(outcomeLabel);

        // Add to layer
        scene.outcomeLabelLayer.addChild(outcomeLabelWrapper);

        // Register in module-level map
        MODULE_OUTCOME_LABELS.set(outcomeKey, {
          wrapper: outcomeLabelWrapper,
          outcome,
          state: 'animating-in'
        });

        // Animate fly-in - resolve promise when animation completes
        outcomeLabel.x = finalX - 80; // Start off-screen left
        console.log(`[OUTCOME-LABEL] Starting fly-in animation for ${outcomeKey}`);

        // Sound: outcome at fly-in, with opponent ducking (0.4x volume for opponents)
        const outcomeSoundKey = getOutcomeSoundKey(outcome);
        if (outcomeSoundKey) {
          const isOpponent = seatPosition !== (mySeatPosition ?? useGameStateStore.getState().mySeatPosition);
          playTableSound(outcomeSoundKey, { multiplier: isOpponent ? 0.4 : 1.0 });
        }

        const flyInDuration = getClientAnimationTiming('outcomeLabelFly', 'showOutcomeLabel');
        animateSprite(outcomeLabel, { x: finalX }, {
          duration: flyInDuration,
          easing: easeOutCubic,
          onComplete: () => {
            const state = MODULE_OUTCOME_LABELS.get(outcomeKey);
            if (state) state.state = 'static';
            console.log(`[OUTCOME-LABEL] Animation complete for ${outcomeKey}, resolving promise`);
            resolve();
          }
        });
      });
    },

    /**
     * Health check - verify PixiJS scene integrity and repair if needed.
     * Called during rake phase each round to catch scene corruption early.
     */
    ensurePixiSceneHealth: () => {
      const issues: string[] = [];
      let checked = 0;
      let fixed = 0;

      const app = appRef.current;
      const scene = sceneRef.current;
      const textures = texturesRef.current;

      // Check 1: App exists and has stage
      checked++;
      if (!app || !app.stage) {
        issues.push('PixiJS app or stage is null');
        return { checked, fixed, issues };
      }

      // Check 2: Core scene layers exist
      const coreLayers = [
        { name: 'gameContentContainer', ref: scene.gameContentContainer },
        { name: 'tableLayer', ref: scene.tableLayer },
        { name: 'seatsLayer', ref: scene.seatsLayer },
        { name: 'animationLayer', ref: scene.animationLayer },
        { name: 'cardsLayer', ref: scene.cardsLayer },
        { name: 'uiLayer', ref: scene.uiLayer },
      ];
      for (const layer of coreLayers) {
        checked++;
        if (!layer.ref) {
          issues.push(`Missing core layer: ${layer.name}`);
        } else if (layer.ref.destroyed) {
          issues.push(`Destroyed core layer: ${layer.name}`);
        }
      }

      // Check 3: Table sprite exists and is valid
      checked++;
      if (!scene.tableSprite) {
        issues.push('Table sprite is null');
      } else if (scene.tableSprite.destroyed) {
        issues.push('Table sprite is destroyed');
      }

      // Check 4: Shoe card sprites exist (3 expected)
      checked++;
      if (scene.shoeCardSprites.length !== 3) {
        issues.push(`Shoe card sprites: expected 3, found ${scene.shoeCardSprites.length}`);
      } else {
        const destroyedShoeCards = scene.shoeCardSprites.filter(s => s.destroyed).length;
        if (destroyedShoeCards > 0) {
          issues.push(`${destroyedShoeCards} shoe card sprite(s) destroyed`);
        }
      }

      // Check 5: Control panel exists (if usePixiControlPanel is enabled)
      checked++;
      if (!scene.controlPanel) {
        issues.push('Control panel is null');
      } else if ((scene.controlPanel as unknown as { destroyed?: boolean }).destroyed) {
        issues.push('Control panel is destroyed');
      }

      // Check 6: Key textures are loaded
      const requiredTextures = ['table', 'cardBack', 'shoe'];
      for (const texKey of requiredTextures) {
        checked++;
        const tex = textures.get(texKey);
        if (!tex) {
          issues.push(`Missing texture: ${texKey}`);
        } else if ((tex as unknown as { destroyed?: boolean }).destroyed) {
          issues.push(`Destroyed texture: ${texKey}`);
        }
      }

      // Check 7: Face card textures (A, J, Q, K for each suit)
      const faceRanks = ['A', 'J', 'Q', 'K'];
      const suits = ['hearts', 'diamonds', 'clubs', 'spades'];
      let missingFaceCards = 0;
      for (const rank of faceRanks) {
        for (const suit of suits) {
          checked++;
          const tex = textures.get(`${rank}-${suit}`);
          if (!tex || (tex as unknown as { destroyed?: boolean }).destroyed) {
            missingFaceCards++;
          }
        }
      }
      if (missingFaceCards > 0) {
        issues.push(`${missingFaceCards} face card texture(s) missing or destroyed`);
      }

      // Check 8: Chip textures
      const chipDenoms = [1, 5, 25, 100, 500, 1000, 5000, 10000];
      let missingChips = 0;
      for (const denom of chipDenoms) {
        checked++;
        const tex = textures.get(`chip-${denom}`);
        if (!tex || (tex as unknown as { destroyed?: boolean }).destroyed) {
          missingChips++;
        }
      }
      if (missingChips > 0) {
        issues.push(`${missingChips} chip texture(s) missing or destroyed`);
      }

      // Check 9: Kitty container positions (ensure they're not offscreen or NaN)
      checked++;
      const kittyPositions: { visualPos: number; x: number; y: number; visible: boolean; destroyed: boolean }[] = [];
      scene.kittyContainers.forEach((container, visualPos) => {
        // Guard against null/undefined containers (can happen if container was destroyed)
        if (!container) {
          issues.push(`Kitty ${visualPos}: Container is null/undefined`);
          return;
        }
        // Check destroyed FIRST - accessing properties on destroyed Pixi objects can throw
        if (container.destroyed) {
          issues.push(`Kitty ${visualPos}: Container destroyed`);
          kittyPositions.push({ visualPos, x: NaN, y: NaN, visible: false, destroyed: true });
          return;
        }
        const pos = {
          visualPos,
          x: container.x,
          y: container.y,
          visible: container.visible,
          destroyed: false,
        };
        kittyPositions.push(pos);

        // Check for invalid positions
        if (isNaN(container.x) || isNaN(container.y)) {
          issues.push(`Kitty ${visualPos}: NaN position (x=${container.x}, y=${container.y})`);
        } else if (Math.abs(container.x) > 3000 || Math.abs(container.y) > 3000) {
          issues.push(`Kitty ${visualPos}: Offscreen position (x=${container.x}, y=${container.y})`);
        }
      });
      console.log('[PixiSceneHealth] Kitty positions:', kittyPositions);

      // === SPLIT KITTY DEBUG LOGGING ===
      const splitKittyPositions: Array<{
        key: string;
        x: number;
        y: number;
        visible: boolean;
        destroyed: boolean;
        chipCount: number;
        total: number;
      }> = [];
      scene.splitKittyContainers.forEach((container, key) => {
        if (!container) {
          splitKittyPositions.push({ key, x: NaN, y: NaN, visible: false, destroyed: true, chipCount: 0, total: 0 });
          issues.push(`Split kitty ${key}: Container is null/undefined`);
          return;
        }
        if (container.destroyed) {
          splitKittyPositions.push({ key, x: NaN, y: NaN, visible: false, destroyed: true, chipCount: 0, total: 0 });
          issues.push(`Split kitty ${key}: Container destroyed`);
          return;
        }
        const data = scene.splitKittyData.get(key);
        splitKittyPositions.push({
          key,
          x: container.x,
          y: container.y,
          visible: container.visible,
          destroyed: false,
          chipCount: data?.denominations.length ?? 0,
          total: data?.total ?? 0,
        });
      });
      if (splitKittyPositions.length > 0) {
        console.log('[PixiSceneHealth] Split kitty positions:', splitKittyPositions);
      }

      // Log results
      if (issues.length > 0) {
        console.warn(`[PixiSceneHealth] Found ${issues.length} issues:`, issues);
      } else {
        console.log(`[PixiSceneHealth] All ${checked} checks passed`);
      }

      return { checked, fixed, issues };
    },

    /**
     * Sync dealt card counts and scores for mid-hand join.
     * When joining a table with an active game, cards are already dealt but
     * MODULE_DEALT_CARD_COUNTS and MODULE_SCORE_HISTORY are empty. This initializes
     * them so scoreboxes display correctly and control panel enables.
     */
    syncDealtCounts: (
      seatCardCounts: Record<number, number[]>,
      dealerCardCount: number,
      seatScores?: Record<number, Array<{ score: number; isSoft: boolean }>>,
      dealerScore?: { score: number; isSoft: boolean }
    ) => {
      console.log('[syncDealtCounts] Syncing dealt counts for mid-hand join:', { seatCardCounts, dealerCardCount, seatScores, dealerScore });

      // Clear existing counts first
      MODULE_DEALT_CARD_COUNTS.clear();

      // Set counts for each seat/hand
      // seatCardCounts is Record<seatPosition, cardCountPerHand[]>
      // e.g., { 1: [3], 3: [2, 2] } means seat 1 has 3 cards in hand 0, seat 3 has 2 cards each in hands 0 and 1
      for (const [seatPosStr, handCounts] of Object.entries(seatCardCounts)) {
        const seatPosition = parseInt(seatPosStr, 10);
        handCounts.forEach((cardCount, handIndex) => {
          const key = `${seatPosition}-${handIndex}`;
          MODULE_DEALT_CARD_COUNTS.set(key, cardCount);
          console.log(`[syncDealtCounts] Set ${key} = ${cardCount}`);

          // Also record score history for this hand (if provided)
          const handScore = seatScores?.[seatPosition]?.[handIndex];
          if (handScore && cardCount > 0) {
            recordScoreSnapshot(seatPosition, handIndex, cardCount, handScore.score, handScore.isSoft);
            console.log(`[syncDealtCounts] Recorded score for ${key}: ${handScore.score} (soft=${handScore.isSoft})`);
          }
        });
      }

      // Set dealer count and score
      if (dealerCardCount > 0) {
        MODULE_DEALT_CARD_COUNTS.set('dealer-0', dealerCardCount);
        console.log(`[syncDealtCounts] Set dealer-0 = ${dealerCardCount}`);

        // Record dealer score if provided
        if (dealerScore) {
          recordScoreSnapshot(-1, 0, dealerCardCount, dealerScore.score, dealerScore.isSoft);
          console.log(`[syncDealtCounts] Recorded dealer score: ${dealerScore.score} (soft=${dealerScore.isSoft})`);
        }
      }

      // Trigger re-render so scoreboxes update
      setAnimationCardCount(c => c + 1);

      // Reconcile cards - create sprites for cards that exist in Zustand but not MODULE_CARD_STATES
      // This is critical for mid-hand joins where syncFromTableState populated Zustand with cards
      // but no dealing animation ran to create sprites
      requestAnimationFrame(() => {
        if (reconcileCardsRef.current) {
          console.log('[syncDealtCounts] Triggering card reconciliation for mid-hand join');
          reconcileCardsRef.current();
        }
      });
    },

    /**
     * Full visual resync after keyed state refresh.
     * Creates card sprites for all cards in the current zustand state.
     * Called after accepting a keyed resync - state is guaranteed fresh from server.
     */
    fullVisualResync: async () => {
      console.log('[fullVisualResync] Starting full visual resync from fresh server state');
      console.log('[fullVisualResync] Call stack:', new Error().stack);

      // Wait for loading to complete if necessary (max 2 seconds)
      // This handles page refresh where table_joined arrives before PixiJS finishes loading
      let waitAttempts = 0;
      while (loadingRef.current && waitAttempts < 40) {
        console.log('[fullVisualResync] Waiting for loading... attempt', waitAttempts);
        await new Promise(r => setTimeout(r, 50));
        waitAttempts++;
      }

      if (waitAttempts > 0) {
        console.log('[fullVisualResync] Waited', waitAttempts * 50, 'ms for loading to complete');
      }

      const scene = sceneRef.current;
      const textures = texturesRef.current;
      const currentCardConfig = cardConfigRef.current;

      if (!scene?.animationLayer || !textures) {
        console.warn('[fullVisualResync] Scene or textures not ready after waiting - scheduling retry');
        // Schedule a retry in 500ms - initialization might still be completing
        setTimeout(() => {
          console.log('[fullVisualResync] Retry after scene initialization');
          // Check again if scene is now ready
          const retryScene = sceneRef.current;
          const retryTextures = texturesRef.current;
          if (retryScene?.animationLayer && retryTextures) {
            console.log('[fullVisualResync] Scene now ready, triggering resync via ref');
            // The scene is ready, but we can't easily call ourselves recursively
            // Mark pending so the effect can handle it
            pendingResyncRef.current = true;
          } else {
            console.warn('[fullVisualResync] Scene still not ready after retry - giving up');
          }
        }, 500);
        return;
      }

      // Clear pending flag since we're proceeding with resync
      pendingResyncRef.current = false;

      // Read fresh state directly from zustand (not from closure)
      const gameState = useGameStateStore.getState();
      const { players, dealer } = gameState;

      // Clear any existing card sprites and state before recreating
      // This ensures no orphaned sprites remain from before tab switch
      MODULE_CARD_STATES.forEach((state, cardKey) => {
        if (state.sprite && !state.sprite.destroyed) {
          scene.animationLayer?.removeChild(state.sprite);
          state.sprite.destroy();
        }
      });
      MODULE_CARD_STATES.clear();
      MODULE_DEALT_CARD_COUNTS.clear();
      MODULE_STRAIGHTENED.clear();
      console.log('[fullVisualResync] Cleared existing card sprites');

      // Destroy any in-flight chip animations (prevents orphaned chips during resync)
      const inFlightChipCount = MODULE_CHIP_SPRITES_IN_FLIGHT.size;
      MODULE_CHIP_SPRITES_IN_FLIGHT.forEach((sprite) => {
        if (!sprite.destroyed) {
          sprite.parent?.removeChild(sprite);
          sprite.destroy();
        }
      });
      MODULE_CHIP_SPRITES_IN_FLIGHT.clear();
      if (inFlightChipCount > 0) {
        console.log(`[fullVisualResync] Destroyed ${inFlightChipCount} in-flight chip sprites`);
      }

      let cardsCreated = 0;

      // === CREATE PLAYER CARDS ===
      const mySeat = gameState.mySeatPosition;
      players.forEach((player, seatPosition) => {
        const visualPos = getVisualPositionForSeat(seatPosition);
        const pos = dynamicSeatPositions[visualPos - 1];
        if (!pos) {
          console.warn(`[fullVisualResync] No position for seat ${seatPosition}`);
          return;
        }

        // Apply 10% size reduction for opponent cards
        const isOpponent = seatPosition !== mySeat;
        const effectiveCardConfig = isOpponent
          ? applyOpponentCardScale(currentCardConfig)
          : currentCardConfig;

        // Get per-seat card config (uses VISUAL position - configs are about screen location, not physical seat)
        // Use ref to get latest config value (avoids closure issues)
        const perSeatConfig = perSeatCardConfigsRef.current?.[visualPos] || { x: 0, y: 0, radiusOffset: 0 };
        const seatCardsYOffset = effectiveCardConfig.seatCardsY + perSeatConfig.radiusOffset;

        // === FILMSTRIP ARCHITECTURE ===
        // Get or create filmstrip for this seat
        const seatRotationRad = degreesToRadians(pos.rotation);
        // Calculate seat dimensions for mask
        const seatConfig = seatConfigs[visualPos] || { x: 0, y: 0, rot: 0, scale: 100 };
        const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
        const seatWidth = Math.round(144 * seatScale);
        const seatHeight = Math.round(168 * seatScale);
        const filmstrip = scene.cardsLayer
          ? getOrCreateFilmstrip(scene, seatPosition, pos.x, pos.y, seatRotationRad, scene.cardsLayer, seatWidth, seatHeight)
          : null;

        // Calculate scaled overlap for card positioning
        const scaleFactor = effectiveCardConfig.scale / 100;
        const scaledOverlap = CARD_OVERLAP * scaleFactor;

        player.hands.forEach((hand, handIndex) => {
          const totalCards = hand.cards.length;
          if (totalCards === 0) return;

          // Record dealt count for this hand
          const dealtKey = `${seatPosition}-${handIndex}`;
          MODULE_DEALT_CARD_COUNTS.set(dealtKey, totalCards);

          // Mark as straightened (cards appear neat after resync)
          MODULE_STRAIGHTENED.set(`${seatPosition}-${handIndex}`, true);

          // Record score history
          if (hand.score > 0) {
            recordScoreSnapshot(seatPosition, handIndex, totalCards, hand.score, hand.isSoft);
          }

          hand.cards.forEach((card, cardIndex) => {
            // === FILMSTRIP LOCAL COORDINATES (with per-seat offsets) ===
            const centeredOffset = calculateCardCenteredOffset(cardIndex, totalCards, scaledOverlap);
            const localBaseX = centeredOffset + handIndex * FILMSTRIP_CELL_WIDTH + perSeatConfig.x;
            const localBaseY = seatCardsYOffset + perSeatConfig.y;
            const localBaseRotation = 0; // Upright relative to filmstrip

            // Create card sprite at LOCAL position (cards are in filmstrip)
            const cardSprite = createCardSprite(
              card,
              textures,
              localBaseX,
              localBaseY,
              0,  // Local rotation (filmstrip is already rotated)
              effectiveCardConfig
            );
            // Set z-index for proper overlap: higher cardIndex = on top (rightmost overlaps leftmost)
            // handIndex * 100 gives each hand its own z-range
            cardSprite.zIndex = handIndex * 100 + cardIndex;

            // Add to filmstrip if available, otherwise fall back to animationLayer
            if (filmstrip && !filmstrip.destroyed) {
              filmstrip.addChild(cardSprite);
            } else {
              scene.animationLayer?.addChild(cardSprite);
            }

            // Store in MODULE_CARD_STATES (LOCAL coordinates)
            const cardKey = `${seatPosition}-${handIndex}-${cardIndex}`;
            MODULE_CARD_STATES.set(cardKey, {
              sprite: cardSprite,
              card,
              baseX: localBaseX,
              baseY: localBaseY,
              baseRotation: localBaseRotation,
              randomness: { offsetX: 0, offsetY: 0, rotation: 0 },
              isDealer: false,
            });

            cardsCreated++;
          });
        });
      });

      // === CREATE DEALER CARDS ===
      const dealerCards = dealer.cards;
      if (dealerCards.length > 0) {
        const dealerCardsX = currentCardConfig.dealerCardsX;
        const dealerCardsY = currentCardConfig.dealerCardsY;
        const totalDealerCards = dealerCards.length;
        const holeCardRevealed = dealer.holeCardRevealed;
        const scaleFactor = currentCardConfig.scale / 100;
        const scaledCardWidth = CARD_WIDTH * scaleFactor;
        const scaledOverlap = CARD_OVERLAP * scaleFactor;

        // Record dealt count for dealer
        MODULE_DEALT_CARD_COUNTS.set('dealer-0', totalDealerCards);

        // Mark dealer as straightened
        MODULE_STRAIGHTENED.set('dealer', true);

        // Record dealer score
        if (dealer.visibleScore > 0) {
          recordScoreSnapshot(-1, 0, totalDealerCards, dealer.visibleScore, dealer.isSoft);
        }

        // DIAGNOSTIC: Log dealer card state for debugging hole card issue
        console.log(`[fullVisualResync] Dealer cards: ${dealerCards.map((c, i) => `[${i}]${c.rank}${c.suit} faceUp=${c.faceUp}`).join(', ')}`);
        console.log(`[fullVisualResync] holeCardRevealed=${holeCardRevealed}`);

        dealerCards.forEach((card, cardIndex) => {
          // Calculate dealer card position using registry with scaled overlap
          const dealerCardPos = calculateDealerCardPosition(
            DEALER_POSITION.x,
            DEALER_POSITION.y,
            cardIndex,
            totalDealerCards,
            dealerCardsX,
            dealerCardsY,
            holeCardRevealed,
            scaledCardWidth,
            scaledOverlap
          );
          const baseX = dealerCardPos.x;
          const baseY = dealerCardPos.y;
          const baseRotation = 0;

          // Create card sprite at final position
          const cardSprite = createCardSprite(
            card,
            textures,
            baseX,
            baseY,
            0, // Dealer cards have 0° rotation
            currentCardConfig
          );
          scene.animationLayer?.addChild(cardSprite);

          // Store in MODULE_CARD_STATES
          const cardKey = `dealer-0-${cardIndex}`;
          MODULE_CARD_STATES.set(cardKey, {
            sprite: cardSprite,
            card,
            baseX,
            baseY,
            baseRotation,
            randomness: { offsetX: 0, offsetY: 0, rotation: 0 },
            isDealer: true,
          });

          cardsCreated++;
        });
      }

      // === CREATE KITTY CHIPS FOR ALL PLAYERS WITH BETS ===
      // First, clear any existing kitty containers from previous state
      const existingKittyCount = scene.kittyContainers.size;
      if (existingKittyCount > 0) {
        console.warn(`[fullVisualResync] CLEARING ${existingKittyCount} existing kitty containers - this may interrupt animations!`);
        const kittyVisualPositions = Array.from(scene.kittyContainers.keys());
        console.warn(`[fullVisualResync] Kitty visual positions being cleared: [${kittyVisualPositions.join(', ')}]`);
      }
      scene.kittyContainers.forEach((container, visualPos) => {
        if (container && !container.destroyed) {
          scene.animationLayer?.removeChild(container);
          container.destroy();
        }
      });
      scene.kittyContainers.clear();
      scene.kittyChipSprites.clear();
      scene.kittyData.clear();
      scene.kittyCounterTexts.clear();
      scene.kittyPills.clear();
      scene.kittyBasePositions.clear();
      console.log('[fullVisualResync] Cleared existing kitty containers, count was:', existingKittyCount);

      let kittiesCreated = 0;
      // Log all player bets for diagnostic purposes
      const playerBets: string[] = [];
      players.forEach((player, seatPos) => {
        playerBets.push(`seat${seatPos}=$${player.confirmedBet}`);
      });
      console.log(`[fullVisualResync] Player bets: [${playerBets.join(', ')}]`);

      players.forEach((player, seatPosition) => {
        const confirmedBet = player.confirmedBet;
        if (confirmedBet <= 0) {
          console.log(`[fullVisualResync] Skipping kitty for seat ${seatPosition}: confirmedBet=${confirmedBet}`);
          return;
        }

        const visualPos = getVisualPositionForSeat(seatPosition);
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) return;

        // Calculate kitty position using registry
        // Use seatPosition for betChipConfigs, but VISUAL position for opponentKittyConfigs (DEV CONTROLS alignment)
        const betConfig = betChipConfigs[seatPosition] || REGISTRY_DEFAULT_CHIP_CONFIG;
        // Check if this is an opponent seat and apply per-seat opponent config
        const isOpponentSeat = seatPosition !== mySeatPosition;
        const perSeatOpponentConfig = isOpponentSeat
          ? (opponentKittyConfigs?.[visualPos] ?? { radiusOffset: 0, x: 0, y: 0, scale: 100 })
          : { radiusOffset: 0, x: 0, y: 0, scale: 100 };
        const adjustedBetConfig = {
          ...betConfig,
          radiusOffset: betConfig.radiusOffset + perSeatOpponentConfig.radiusOffset,
        };
        // Use opponent kitty config for opponent seats, regular kittyConfig for player's seat
        const effectiveConfig = isOpponentSeat ? effectiveOpponentKittyConfig : kittyConfig;
        const kittyPosResult = calculateKittyPosition(seatData, {
          betChipConfig: adjustedBetConfig,
          kittyOffsetX: effectiveConfig.x + perSeatOpponentConfig.x,
          kittyOffsetY: effectiveConfig.y + perSeatOpponentConfig.y,
        });

        // Create kitty display
        const kittyDisplay = createKittyDisplay(textures, chipCounterConfig);
        const kittyContainer = kittyDisplay.container;
        kittyContainer.x = kittyPosResult.x;
        kittyContainer.y = kittyPosResult.y;
        kittyContainer.rotation = kittyPosResult.rotationRad; // Seat rotation only - counter stays oriented to seat
        kittyContainer.zIndex = 1500;

        // Apply extra rotation to chipsWrapper only (chips rotate, counter stays in place)
        const extraRotRad = degreesToRadians(effectiveConfig.rotation || 0);
        kittyDisplay.chipsWrapper.rotation = extraRotRad;

        // Store seatPosition for config lookup (betChipConfigs is keyed by seat, not visual position)
        (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).isKittyElement = true;
        (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).seatPosition = seatPosition;
        scene.animationLayer?.addChild(kittyContainer);
        scene.kittyContainers.set(visualPos, kittyContainer);
        scene.kittyChipsWrappers.set(visualPos, kittyDisplay.chipsWrapper);

        // Store base position for centerline calculations
        scene.kittyBasePositions.set(visualPos, { x: kittyPosResult.x, y: kittyPosResult.y, rotRad: kittyPosResult.rotationRad });

        // Initialize kitty data
        const kittyData = { denominations: [] as number[], total: 0 };
        scene.kittyData.set(visualPos, kittyData);

        const kittyChipSprites = new Map<number, KittyChipData>();
        scene.kittyChipSprites.set(visualPos, kittyChipSprites);

        scene.kittyCounterTexts.set(visualPos, kittyDisplay.counterText);
        scene.kittyPills.set(visualPos, kittyDisplay.pill);

        // Store border/chip containers on kitty container for later access
        (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).borderContainer = kittyDisplay.borderContainer;
        (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).chipContainer = kittyDisplay.chipContainer;

        // Break down bet into chip denominations and add to kitty
        const chipBreakdown = breakdownChips(confirmedBet);
        const cappedResult = capChipsForAnimation(chipBreakdown);

        for (const denom of cappedResult.chips) {
          // Create chip sprite at kitty position (no animation)
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) continue;

          const chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          chipSprite.width = KITTY_CHIP_SIZE;
          chipSprite.height = KITTY_CHIP_SIZE;
          chipSprite.rotation = Math.random() * 2 * Math.PI;
          chipSprite.x = 0;

          // Add to kitty using the system (handles positioning and borders)
          addChipToKitty(
            kittyDisplay.chipContainer,
            kittyDisplay.borderContainer,
            denom,
            chipSprite,
            kittyChipSprites,
            chipBorderConfig
          );

          // Track denomination in kitty data
          if (!kittyData.denominations.includes(denom)) {
            kittyData.denominations.push(denom);
          }
        }

        // Update total and counter display
        // Opponents don't show chip counters
        const isOpponent = seatPosition !== mySeatPosition;
        kittyData.total = confirmedBet;
        updateKittyLayout(
          kittyDisplay.pill,
          kittyDisplay.counterText,
          kittyChipSprites.size,
          confirmedBet,
          { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
          isOpponent
        );

        // Mark opponent kitties so other functions know to skip counter updates
        if (isOpponent) {
          (kittyContainer as unknown as { isOpponentKitty: boolean }).isOpponentKitty = true;
        }

        kittiesCreated++;
        console.log(`[fullVisualResync] Created kitty for seat ${seatPosition} (visual ${visualPos}): $${confirmedBet}, isOpponent=${isOpponent}`);
      });

      // === CREATE OUTCOME LABELS FOR RESOLVED HANDS ===
      // First, clear any existing outcome labels from previous state
      MODULE_OUTCOME_LABELS.forEach((state, outcomeKey) => {
        if (state.wrapper && !state.wrapper.destroyed) {
          scene.outcomeLabelLayer?.removeChild(state.wrapper);
          state.wrapper.destroy();
        }
      });
      MODULE_OUTCOME_LABELS.clear();
      console.log('[fullVisualResync] Cleared existing outcome labels');

      let outcomeLabelsCreated = 0;
      players.forEach((player, seatPosition) => {
        const visualPos = getVisualPositionForSeat(seatPosition);
        const pos = dynamicSeatPositions[visualPos - 1];
        if (!pos) return;

        player.hands.forEach((hand, handIndex) => {
          const outcome = hand.outcome;
          if (!outcome) return;

          const outcomeKey = `${seatPosition}-${handIndex}`;

          // Skip if already exists
          if (MODULE_OUTCOME_LABELS.has(outcomeKey)) return;

          // Calculate seat dimensions (must match createSeatContainer)
          const seatConfig = seatConfigs[visualPos] || DEFAULT_SEAT_CONFIG;
          const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
          const width = Math.round(144 * seatScale);
          const height = Math.round(168 * seatScale);

          // Create wrapper at seat position
          const seatX = pos.x + seatConfig.x;
          const seatY = pos.y + seatConfig.y;
          const seatRotation = pos.rotation + seatConfig.rot;

          const outcomeLabelWrapper = new Container();
          outcomeLabelWrapper.label = 'outcomeLabelWrapper';
          (outcomeLabelWrapper as unknown as { isOutcomeLabelWrapper: boolean }).isOutcomeLabelWrapper = true;
          outcomeLabelWrapper.x = seatX;
          outcomeLabelWrapper.y = seatY;
          outcomeLabelWrapper.rotation = degreesToRadians(seatRotation);

          // Create mask matching seat box dimensions
          const outcomeMask = new Graphics();
          outcomeMask.roundRect(-width / 2, -height / 2, width, height, 8);
          outcomeMask.fill({ color: 0xffffff });
          outcomeLabelWrapper.addChild(outcomeMask);
          outcomeLabelWrapper.mask = outcomeMask;

          // Create the label
          const outcomeLabel = createOutcomeLabel(outcome, outcomeLabelConfig, texturesRef.current);
          const outcomeBounds = outcomeLabel.getLocalBounds();
          const labelScale = outcomeLabel.scale.y;
          const visualHeight = outcomeBounds.height * labelScale;

          // Calculate scorebox width for positioning using cached measurements
          let scoreboxWidth = scoreboxConfig.minWidth ?? 35;
          const latestScore = getLatestScore(seatPosition, handIndex);
          if (latestScore) {
            const boxScale = scoreboxConfig.scale / 100;
            const textWidth = getScoreTextWidth(latestScore.score, scoreboxConfig.fontSize);
            const calculatedWidth = Math.max(12, textWidth) * boxScale;
            scoreboxWidth = scoreboxConfig.minWidth
              ? Math.max(scoreboxConfig.minWidth, calculatedWidth)
              : calculatedWidth;
          }

          // Position outcome label (static, no animation)
          const scoreboxLocalX = -width / 2 + scoreboxConfig.x;
          const finalX = scoreboxLocalX + scoreboxWidth;
          const scoreboxBottom = height / 2 + scoreboxConfig.y;
          outcomeLabel.x = finalX;
          outcomeLabel.y = scoreboxBottom - visualHeight;
          outcomeLabelWrapper.addChild(outcomeLabel);

          // Add to layer
          scene.outcomeLabelLayer?.addChild(outcomeLabelWrapper);

          // Register in module-level map (static state)
          MODULE_OUTCOME_LABELS.set(outcomeKey, {
            wrapper: outcomeLabelWrapper,
            outcome,
            state: 'static'
          });

          outcomeLabelsCreated++;
          console.log(`[fullVisualResync] Created outcome label for seat ${seatPosition} hand ${handIndex}: ${outcome}`);
        });
      });

      // === POSITION TURN BEAM INSTANTLY ===
      const { currentTurnSeat: gameTurnSeat } = gameState;
      if (scene.beamContainer && scene.beamGraphics) {
        if (gameTurnSeat !== null) {
          // Show beam pointed at current turn seat (instant, no animation)
          const visualPos = getVisualPositionForSeat(gameTurnSeat);
          const targetRotationDeg = beamConfig?.rotations?.[visualPos] ?? BEAM_CONFIG.rotations[visualPos] ?? 180;

          scene.beamState.currentRotation = targetRotationDeg;
          scene.beamState.targetRotation = targetRotationDeg;
          scene.beamState.velocity = 0;
          scene.beamState.targetAlpha = 1;
          scene.beamState.isAnimating = false;

          scene.beamGraphics.rotation = degreesToRadians(targetRotationDeg);
          scene.beamContainer.alpha = 1;

          console.log(`[fullVisualResync] Beam positioned to seat ${gameTurnSeat} (visual ${visualPos}, rot ${targetRotationDeg}°)`);
        } else {
          // Hide beam (instant)
          scene.beamState.currentRotation = 180;
          scene.beamState.targetRotation = 180;
          scene.beamState.velocity = 0;
          scene.beamState.targetAlpha = 0;
          scene.beamState.isAnimating = false;

          scene.beamGraphics.rotation = Math.PI;
          scene.beamContainer.alpha = 0;

          console.log('[fullVisualResync] Beam hidden (no active turn)');
        }
      }

      // === CREATE INSURANCE KITTY FOR PLAYERS WITH INSURANCE BETS ===
      // First, clear any existing insurance kitty containers from previous state
      scene.insuranceKittyContainers.forEach((container, seatPosition) => {
        if (container && !container.destroyed) {
          scene.animationLayer?.removeChild(container);
          container.destroy();
        }
      });
      scene.insuranceKittyContainers.clear();
      scene.insuranceKittyData.clear();
      scene.insuranceKittyChipSprites.clear();
      console.log('[fullVisualResync] Cleared existing insurance kitty containers');

      // === CLEAR SPLIT KITTY CONTAINERS ===
      // Split kitties are keyed by "seatPosition-handIndex"
      const splitKittyCount = scene.splitKittyContainers.size;
      scene.splitKittyContainers.forEach((container, key) => {
        if (container && !container.destroyed) {
          scene.animationLayer?.removeChild(container);
          container.destroy();
        }
      });
      scene.splitKittyContainers.clear();
      scene.splitKittyData.clear();
      scene.splitKittyChipSprites.clear();
      scene.splitKittyCounterTexts.clear();
      scene.splitKittyPills.clear();
      if (splitKittyCount > 0) {
        console.log(`[fullVisualResync] Cleared ${splitKittyCount} split kitty containers`);
      }

      // === CLEAR SPLIT HAND STATE AND LABELS ===
      scene.splitHandState.clear();
      const splitLabelCount = scene.splitHandLabels.size;
      scene.splitHandLabels.forEach((labelData, key) => {
        if (labelData.wrapper && !labelData.wrapper.destroyed) {
          scene.outcomeLabelLayer?.removeChild(labelData.wrapper);
          labelData.wrapper.destroy({ children: true });
        } else {
          if (labelData.label && !labelData.label.destroyed) {
            scene.outcomeLabelLayer?.removeChild(labelData.label);
            labelData.label.destroy();
          }
          if (labelData.bg && !labelData.bg.destroyed) {
            scene.outcomeLabelLayer?.removeChild(labelData.bg);
            labelData.bg.destroy();
          }
        }
      });
      scene.splitHandLabels.clear();
      if (splitLabelCount > 0) {
        console.log(`[fullVisualResync] Cleared ${splitLabelCount} split hand labels`);
      }

      let insuranceKittiesCreated = 0;
      players.forEach((player, seatPosition) => {
        const insuranceBet = player.insuranceBet;
        if (!insuranceBet || insuranceBet <= 0) return;

        const visualPos = getVisualPositionForSeat(seatPosition);
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) return;

        // Get existing insurance kitty or create new one
        let insKittyContainer = scene.insuranceKittyContainers.get(seatPosition);
        if (insKittyContainer) return; // Already exists

        // Calculate insurance kitty position (matching animateRailToInsuranceKitty logic)
        // Use visualPos for config lookup (DEV CONTROLS alignment)
        const insConfig = insuranceChipConfigs[visualPos] || { x: 35, y: 0, scale: 100 };
        const rotRad = degreesToRadians(seatData.rotation);

        // Calculate base position similar to main kitty
        const radialOffset = KITTY_BASE_RADIAL_OFFSET + (betChipConfigs[seatPosition]?.radiusOffset ?? 0);
        const basePos = applyRadialOffset(seatData.x, seatData.y, radialOffset, rotRad);
        const baseX = basePos.x + (kittyConfig?.x ?? 0);
        const baseY = basePos.y;

        // Insurance kitty uses same Y (radial distance) as main kitty
        // Only tangent offset is applied later to shift horizontally
        const baseTargetX = baseX;
        const baseTargetY = baseY;

        // Use insConfig.x as the padding between pill edge and centerline
        const padding = KITTY_PAIR_GAP;

        // Calculate centering offset for insurance (shifts right based on its OWN pill width)
        const insCenterOffset = calculateKittyCenteringOffset(insuranceBet, padding, false, true);
        const adjustedInsPos = applyTangentOffset({ x: baseTargetX, y: baseTargetY }, insCenterOffset, rotRad);

        const insKittyDisplay = createKittyDisplay(textures, chipCounterConfig);
        insKittyContainer = insKittyDisplay.container;
        insKittyContainer.x = adjustedInsPos.x;
        insKittyContainer.y = adjustedInsPos.y;
        insKittyContainer.rotation = rotRad;
        insKittyContainer.zIndex = 1500;
        (insKittyContainer as unknown as { isKittyElement: boolean }).isKittyElement = true;
        (insKittyContainer as unknown as { isInsuranceKitty: boolean }).isInsuranceKitty = true;
        scene.animationLayer?.addChild(insKittyContainer);
        scene.insuranceKittyContainers.set(seatPosition, insKittyContainer);

        // Store border/chip container references
        (insKittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).borderContainer = insKittyDisplay.borderContainer;
        (insKittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).chipContainer = insKittyDisplay.chipContainer;

        // Initialize insurance kitty data
        const insKittyData = { denominations: [] as number[], total: 0 };
        scene.insuranceKittyData.set(seatPosition, insKittyData);

        // Insurance kitty uses array-based chip system (not KittyChipData map)
        const insKittySprites: Sprite[] = [];
        scene.insuranceKittyChipSprites.set(seatPosition, insKittySprites);

        scene.insuranceKittyCounterTexts.set(seatPosition, insKittyDisplay.counterText);
        scene.insuranceKittyPills.set(seatPosition, insKittyDisplay.pill);

        // Break down insurance bet into chips
        const insChipBreakdown = breakdownChips(insuranceBet);
        const insCappedResult = capChipsForAnimation(insChipBreakdown);

        for (const denom of insCappedResult.chips) {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) continue;

          // Create chip sprite directly in chipContainer (matching animateRailToInsuranceKitty pattern)
          const kittyChip = new Sprite(chipTexture);
          kittyChip.anchor.set(0.5);
          kittyChip.width = CHIP_SIZES.kittyMedium;
          kittyChip.height = CHIP_SIZES.kittyMedium;
          // Position relative to container origin (chips stack upward)
          kittyChip.x = 0;
          kittyChip.y = -insKittySprites.length * 4; // Stack offset
          kittyChip.rotation = Math.random() * 2 * Math.PI;
          kittyChip.zIndex = 10 + insKittySprites.length;
          insKittyDisplay.chipContainer.addChild(kittyChip);
          insKittySprites.push(kittyChip);

          insKittyData.denominations.push(denom);
        }

        // Update total and counter display
        // Opponents don't show chip counters
        const isOpponent = seatPosition !== mySeatPosition;
        insKittyData.total = insuranceBet;
        updateKittyLayout(
          insKittyDisplay.pill,
          insKittyDisplay.counterText,
          insKittySprites.length,
          insuranceBet,
          { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
          isOpponent
        );

        // Mark opponent insurance kitties
        if (isOpponent) {
          (insKittyContainer as unknown as { isOpponentKitty: boolean }).isOpponentKitty = true;
        }

        // Also shift the main kitty to make room for insurance kitty
        const mainKittyContainer = scene.kittyContainers.get(visualPos);
        const mainKittyBasePos = scene.kittyBasePositions.get(visualPos);
        const mainKittyData = scene.kittyData.get(visualPos);
        const mainBetAmount = mainKittyData?.total ?? insuranceBet;

        if (mainKittyContainer && mainKittyBasePos && !mainKittyContainer.destroyed) {
          const mainCenterOffset = calculateKittyCenteringOffset(mainBetAmount, padding, true, true);
          const newMainPos = applyTangentOffset(
            { x: mainKittyBasePos.x, y: mainKittyBasePos.y },
            mainCenterOffset,
            mainKittyBasePos.rotRad
          );
          // Set position instantly (no animation for resync)
          mainKittyContainer.x = newMainPos.x;
          mainKittyContainer.y = newMainPos.y;
        }

        insuranceKittiesCreated++;
        console.log(`[fullVisualResync] Created insurance kitty for seat ${seatPosition}: $${insuranceBet}`);
      });

      // === CLEAR STAGED CHIPS (betting UI) ===
      // Return all staged chips to pool and clear the array
      // The sync effect will recreate them from stagedChipsStore
      if (scene.stagedChipSprites.length > 0) {
        console.log(`[fullVisualResync] Clearing ${scene.stagedChipSprites.length} staged chips`);
        scene.stagedChipSprites.forEach((chip) => {
          if (chip.sprite && !chip.sprite.destroyed) {
            chip.sprite.visible = false;
            // Return to pool
            const pool = scene.chipPool.get(chip.denomination);
            if (pool) {
              chip.state = 'pool';
              pool.push(chip);
            } else {
              chip.sprite.destroy();
            }
          }
        });
        scene.stagedChipSprites = [];
      }
      // Set flag so sync effect knows to force recreation of staged chips
      // NOTE: We do NOT destroy stagedBetFrame here - it's a UI element that persists
      // and will update reactively when stagedAmount changes
      scene.forceChipResync = true;
      console.log('[fullVisualResync] Staged chips cleared, forceChipResync flag set');

      // Trigger re-render so scoreboxes update
      setAnimationCardCount(c => c + 1);

      console.log(`[fullVisualResync] Complete: ${cardsCreated} cards, ${kittiesCreated} kitties, ${outcomeLabelsCreated} outcome labels, ${insuranceKittiesCreated} insurance kitties`);
    },

    showSidebetModal: (sidebetType: string) => {
      if (showSidebetModalRef.current) {
        showSidebetModalRef.current(sidebetType);
      } else {
        console.warn('[showSidebetModal] Modal not ready yet');
      }
    },

    // ===========================================
    // PER-SEAT SIDEBET ICON MANAGEMENT
    // ===========================================

    addSidebetToSeat: (seatPosition: number, sidebetType: string, amount: number, rounds: number): number => {
      console.log(`[Sidebet Icon] Adding ${sidebetType} to seat ${seatPosition}, amount: ${amount}, rounds: ${rounds}`);

      // GUARD: Prevent adding sidebet icons when mySeatPosition prop is stale
      // If the store has mySeatPosition set but the prop is still null, the component
      // needs to re-render first so getVisualPositionForSeat has the correct closure.
      // This prevents icons from appearing at the wrong visual position.
      const storeMySeatPosition = useGameStateStore.getState().mySeatPosition;
      if (mySeatPosition === null && storeMySeatPosition !== null && seatPosition === storeMySeatPosition) {
        console.warn(`[Sidebet Icon] SKIPPED: mySeatPosition prop is stale (null) but store has ${storeMySeatPosition}. Component needs to re-render first.`);
        return -1;
      }

      const scene = sceneRef.current;
      const textures = texturesRef.current;
      const visualPos = getVisualPositionForSeat(seatPosition);
      const seatData = dynamicSeatPositions[visualPos - 1];

      if (!seatData || !scene.sidebetLayer) {
        console.warn('[Sidebet Icon] Missing seat data or sidebet layer');
        return -1;
      }

      // Determine if this is an opponent seat
      const isOpponentSeat = seatPosition !== mySeatPosition;

      // Get or create sidebet container for this seat
      let seatContainer = scene.seatSidebetContainers.get(seatPosition);
      if (!seatContainer) {
        seatContainer = new Container();
        seatContainer.sortableChildren = true;
        seatContainer.eventMode = 'passive'; // Allow events to propagate to children

        // Position container at seat's sidebet area (above main bet)
        // Use opponent config for opponent seats - sidebets use their own config, NOT betChipConfigs
        const config = isOpponentSeat ? opponentSidebetConfigRef.current : sidebetConfigRef.current;
        // Per-seat offset (additive on top of base config) - use VISUAL position for DEV CONTROLS alignment
        const perSeatOffset = sidebetIconConfigs?.[visualPos] || { radiusOffset: 0, x: 0, y: 0 };
        const baseLocalOffsetY = config.baseRadiusOffset + perSeatOffset.radiusOffset;
        const rotRad = degreesToRadians(seatData.rotation);
        const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);

        seatContainer.x = basePos.x + perSeatOffset.x;
        seatContainer.y = basePos.y + perSeatOffset.y;
        seatContainer.rotation = rotRad;

        scene.sidebetLayer.addChild(seatContainer);  // Add to sidebetLayer (persistent)
        scene.seatSidebetContainers.set(seatPosition, seatContainer);
      }

      // Check how many sidebets are already at this seat
      let existingCount = 0;
      const existingTypes: string[] = [];
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          existingCount++;
          existingTypes.push((sprite as unknown as { sidebetType?: string }).sidebetType || '');
        }
      }

      // Check if this sidebet type already exists at this seat
      // Use partial matching for over-under-13 variants (e.g., over-under-13-over matches over-under-13)
      const alreadyExists = existingTypes.some(existing =>
        existing === sidebetType ||
        (existing && (existing.startsWith(sidebetType) || sidebetType.startsWith(existing)))
      );
      if (alreadyExists) {
        console.log(`[Sidebet Icon] ${sidebetType} already exists at seat ${seatPosition}`);
        return -1;
      }

      // Check if seat is full
      if (existingCount >= 3) {
        console.warn(`[Sidebet Icon] Seat ${seatPosition} already has 3 sidebets`);
        return -1;
      }

      // Find next available slot
      let slotIndex = -1;
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        if (!scene.sidebetSprites.has(key)) {
          slotIndex = i;
          break;
        }
      }

      if (slotIndex === -1) {
        console.warn(`[Sidebet Icon] No available slot at seat ${seatPosition}`);
        return -1;
      }

      // Get texture for this sidebet type
      const textureIndex = SIDEBET_TYPE_TO_INDEX[sidebetType];
      const texture = textures.get(`sidebet-${textureIndex}`);
      if (!texture) {
        console.warn(`[Sidebet Icon] No texture for sidebet type ${sidebetType}`);
        return -1;
      }

      // Create the icon sprite - use opponent config for opponent seats
      const config = isOpponentSeat ? opponentSidebetConfigRef.current : sidebetConfigRef.current;

      // Gold border ring behind the icon
      const borderRing = new Graphics();
      borderRing.circle(0, 0, config.iconSize / 2 + 3);
      borderRing.stroke({ color: 0xd4a54a, width: 3 }); // Gold color
      seatContainer.addChild(borderRing);

      const iconSprite = new Sprite(texture);
      iconSprite.anchor.set(0.5);
      iconSprite.width = config.iconSize;
      iconSprite.height = config.iconSize;
      (iconSprite as unknown as { sidebetType: string }).sidebetType = sidebetType;
      (iconSprite as unknown as { borderRing: Graphics }).borderRing = borderRing; // Store reference for cleanup

      // Make clickable for current user's sidebet icons only
      if (seatPosition === mySeatPosition && onSidebetIconClick) {
        iconSprite.eventMode = 'static';
        iconSprite.cursor = 'pointer';
        iconSprite.on('pointerdown', () => {
          console.log('[DEBUG SIDEBET CLICK] addSidebetIcon clicked:', { sidebetType, slotIndex, seatPosition, mySeatPosition });
          onSidebetIconClick(sidebetType);
        });
      }

      // Position will be set by repositionSidebetIcons after adding
      seatContainer.addChild(iconSprite);

      const key = `${seatPosition}-${slotIndex}`;
      scene.sidebetSprites.set(key, iconSprite);

      // Create overlay for glow effects
      const overlay = new Container();
      overlay.alpha = 0;
      seatContainer.addChild(overlay);
      scene.sidebetOverlays.set(key, overlay);

      // Create counter pill background (matches kitty counter style)
      // Only for local player - opponents don't show chip counters
      const counterPill = new Graphics();
      counterPill.zIndex = 50;
      seatContainer.addChild(counterPill);
      scene.sidebetCounterPills.set(key, counterPill);

      // Create chip counter text (styled to match kitty counter)
      const counterStyle = new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: SIDEBET_COUNTER_CONFIG.fontSize,
        fontWeight: 'bold',
        fill: SIDEBET_COUNTER_CONFIG.fontColor,
      });
      const counterText = new Text({ text: '', style: counterStyle });
      counterText.anchor.set(0.5, 0.5); // Center anchor for vertical centering in pill
      counterText.zIndex = 51;
      counterText.visible = false; // Hidden until chips arrive
      seatContainer.addChild(counterText);
      scene.sidebetCounters.set(key, counterText);
      scene.sidebetChipCounts.set(key, amount); // Initialize with passed amount (for restoration)

      // Hide counter for opponent sidebet icons (only local player sees counters)
      const isOpponentSidebet = seatPosition !== mySeatPosition;
      if (isOpponentSidebet) {
        counterPill.visible = false;
        counterText.visible = false;
        // Mark as opponent sidebet so counter stays hidden when updating
        (iconSprite as unknown as { isOpponentSidebet: boolean }).isOpponentSidebet = true;
      }

      // Store active sidebet state
      scene.activeSidebets.set(`${seatPosition}-${sidebetType}`, { amount, rounds });
      // Store server-authoritative rounds (survives across rounds, cleared only on true retire)
      scene.sidebetServerRounds.set(`${seatPosition}-${sidebetType}`, rounds);

      // If amount > 0 (restoration case), update the counter display immediately
      // Skip for opponents - they don't show chip counters
      if (amount > 0 && !isOpponentSidebet) {
        console.log(`[Sidebet Icon] Restoring counter display for ${sidebetType} at seat ${seatPosition}: $${amount}`);
        updateSidebetCounterLayout(counterPill, counterText, amount, config.iconSize);
      }

      // Reposition all icons to maintain balanced arch
      {
        // Use opponent config for opponent seats
        const posConfig = isOpponentSidebet ? opponentSidebetConfigRef.current : sidebetConfigRef.current;
        const activeSlots: { sprite: Sprite; overlay: Container | null; counter: Text | null; counterPill: Graphics | null }[] = [];
        for (let i = 0; i < 3; i++) {
          const k = `${seatPosition}-${i}`;
          const s = scene.sidebetSprites.get(k);
          const o = scene.sidebetOverlays.get(k);
          const c = scene.sidebetCounters.get(k);
          const p = scene.sidebetCounterPills.get(k);
          if (s && !s.destroyed) {
            activeSlots.push({ sprite: s, overlay: o || null, counter: c || null, counterPill: p || null });
          }
        }
        const posIndices = posConfig.getPositionsForCount(activeSlots.length);
        activeSlots.forEach((slot, idx) => {
          const posIdx = posIndices[idx];
          const pos = posConfig.slotPositions[posIdx];
          slot.sprite.x = pos.localX;
          slot.sprite.y = pos.localY;
          // Also move the border ring
          const borderRing = (slot.sprite as unknown as { borderRing?: Graphics }).borderRing;
          if (borderRing && !borderRing.destroyed) {
            borderRing.x = pos.localX;
            borderRing.y = pos.localY;
          }
          if (slot.overlay) {
            slot.overlay.x = pos.localX;
            slot.overlay.y = pos.localY;
          }
          if (slot.counter) {
            slot.counter.x = pos.localX;
            slot.counter.y = pos.localY + posConfig.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
          }
          if (slot.counterPill) {
            slot.counterPill.x = pos.localX;
            slot.counterPill.y = pos.localY + posConfig.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
          }
        });
      }

      console.log(`[Sidebet Icon] Added ${sidebetType} at slot ${slotIndex} for seat ${seatPosition}`);
      return slotIndex;
    },

    repositionSidebetIcons: (seatPosition: number) => {
      const scene = sceneRef.current;
      const seatContainer = scene.seatSidebetContainers.get(seatPosition);
      if (!seatContainer) return;

      // Collect active sprites and their slot indices
      const activeSlots: { index: number; sprite: Sprite; overlay: Container; counter: Text | null; counterPill: Graphics | null }[] = [];
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        const overlay = scene.sidebetOverlays.get(key);
        const counter = scene.sidebetCounters.get(key);
        const counterPill = scene.sidebetCounterPills.get(key);
        if (sprite && !sprite.destroyed) {
          activeSlots.push({ index: i, sprite, overlay: overlay || new Container(), counter: counter || null, counterPill: counterPill || null });
        }
      }

      // Use opponent config for opponent seats, player config for player seat
      const isOpponent = seatPosition !== mySeatPosition;
      const config = isOpponent ? opponentSidebetConfigRef.current : sidebetConfigRef.current;
      const positionIndices = config.getPositionsForCount(activeSlots.length);

      // Reposition each active sprite, overlay, counter, pill, and border ring
      activeSlots.forEach((slot, idx) => {
        const posIndex = positionIndices[idx];
        const pos = config.slotPositions[posIndex];
        slot.sprite.x = pos.localX;
        slot.sprite.y = pos.localY;
        // Also move the border ring
        const borderRing = (slot.sprite as unknown as { borderRing?: Graphics }).borderRing;
        if (borderRing && !borderRing.destroyed) {
          borderRing.x = pos.localX;
          borderRing.y = pos.localY;
        }
        if (slot.overlay) {
          slot.overlay.x = pos.localX;
          slot.overlay.y = pos.localY;
        }
        if (slot.counter) {
          slot.counter.x = pos.localX;
          slot.counter.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
        }
        if (slot.counterPill) {
          slot.counterPill.x = pos.localX;
          slot.counterPill.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
        }
      });
    },

    removeSidebetFromSeat: (seatPosition: number, sidebetType: string) => {
      console.log(`[Sidebet Icon] Removing ${sidebetType} from seat ${seatPosition}`);

      const scene = sceneRef.current;

      // Find the slot with this sidebet type (exact match first, then partial)
      let foundSlot = -1;

      // Pass 1: Exact match
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
          if (storedType === sidebetType) {
            foundSlot = i;
            break;
          }
        }
      }

      // Pass 2: Partial match for over-under-13 variants
      if (foundSlot === -1) {
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType))) {
              foundSlot = i;
              break;
            }
          }
        }
      }

      if (foundSlot >= 0) {
        const key = `${seatPosition}-${foundSlot}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite) {
          // Remove border ring first (stored on sprite)
          const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
          if (borderRing && !borderRing.destroyed) {
            borderRing.destroy();
          }
          // Remove sprite
          if (!sprite.destroyed) {
            sprite.destroy();
          }
          scene.sidebetSprites.delete(key);

          // Remove overlay
          const overlay = scene.sidebetOverlays.get(key);
          if (overlay && !overlay.destroyed) {
            overlay.destroy();
          }
          scene.sidebetOverlays.delete(key);

          // Remove counter and pill
          const counter = scene.sidebetCounters.get(key);
          if (counter && !counter.destroyed) {
            counter.destroy();
          }
          scene.sidebetCounters.delete(key);
          const counterPill = scene.sidebetCounterPills.get(key);
          if (counterPill && !counterPill.destroyed) {
            counterPill.destroy();
          }
          scene.sidebetCounterPills.delete(key);
          scene.sidebetChipCounts.delete(key);

          // Remove from active sidebets
          scene.activeSidebets.delete(`${seatPosition}-${sidebetType}`);
          // Clear server rounds on true retire (icon actually removed)
          scene.sidebetServerRounds.delete(`${seatPosition}-${sidebetType}`);

          // Reposition remaining icons
          {
            const activeSlots: { sprite: Sprite; overlay: Container | null; counter: Text | null; counterPill: Graphics | null }[] = [];
            for (let j = 0; j < 3; j++) {
              const k = `${seatPosition}-${j}`;
              const s = scene.sidebetSprites.get(k);
              const o = scene.sidebetOverlays.get(k);
              const c = scene.sidebetCounters.get(k);
              const cp = scene.sidebetCounterPills.get(k);
              if (s && !s.destroyed) {
                activeSlots.push({ sprite: s, overlay: o || null, counter: c || null, counterPill: cp || null });
              }
            }
            const config = sidebetConfigRef.current;
            const posIndices = config.getPositionsForCount(activeSlots.length);
            activeSlots.forEach((slot, idx) => {
              const posIdx = posIndices[idx];
              const pos = config.slotPositions[posIdx];
              slot.sprite.x = pos.localX;
              slot.sprite.y = pos.localY;
              // Also move the border ring
              const borderRing = (slot.sprite as unknown as { borderRing?: Graphics }).borderRing;
              if (borderRing && !borderRing.destroyed) {
                borderRing.x = pos.localX;
                borderRing.y = pos.localY;
              }
              if (slot.overlay) {
                slot.overlay.x = pos.localX;
                slot.overlay.y = pos.localY;
              }
              if (slot.counter) {
                slot.counter.x = pos.localX;
                slot.counter.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
              }
              if (slot.counterPill) {
                slot.counterPill.x = pos.localX;
                slot.counterPill.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
              }
            });
          }

          console.log(`[Sidebet Icon] Removed ${sidebetType} from slot ${foundSlot} at seat ${seatPosition}`);
          return;
        }
      }

      console.warn(`[Sidebet Icon] ${sidebetType} not found at seat ${seatPosition}`);
    },

    updateSidebetBadge: (seatPosition: number, sidebetType: string, newRounds: number) => {
      console.log(`[BADGE DEBUG] updateSidebetBadge called: seat ${seatPosition}, type ${sidebetType}, newRounds ${newRounds}`);
      const scene = sceneRef.current;

      // Find the sprite with matching sidebetType to get the correct overlay key
      let overlayKey: string | null = null;
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
          if (storedType === sidebetType ||
              (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
            overlayKey = key;
            break;
          }
        }
      }

      if (!overlayKey) {
        console.warn(`[BADGE DEBUG] No sprite found for ${sidebetType} at seat ${seatPosition}`);
        return;
      }

      const overlay = scene.sidebetOverlays.get(overlayKey);
      if (!overlay || overlay.destroyed) {
        console.warn(`[BADGE DEBUG] No overlay found for key ${overlayKey}`);
        return;
      }

      console.log(`[BADGE DEBUG] Found overlay at key ${overlayKey}, updating badge to ${newRounds}`);

      // Clear and recreate the badge with new value
      overlay.removeChildren();

      const badgeSize = 18;
      const badgeX = SIDEBET_ICON_SIZE / 2 - 4;
      const badgeY = SIDEBET_ICON_SIZE / 2 - 4;

      const badgeBg = new Graphics();
      badgeBg.circle(badgeX, badgeY, badgeSize / 2);
      badgeBg.fill({ color: newRounds === 0 ? 0xef4444 : 0x22c55e }); // Red if expired, green otherwise
      badgeBg.stroke({ color: 0xffffff, width: 1.5 });
      overlay.addChild(badgeBg);

      const badgeText = new Text({
        text: newRounds === -1 ? '∞' : String(newRounds),
        style: {
          fontFamily: 'Arial',
          fontSize: newRounds === -1 ? 14 : 11,
          fontWeight: 'bold',
          fill: 0xffffff
        }
      });
      badgeText.anchor.set(0.5);
      badgeText.x = badgeX;
      badgeText.y = badgeY + (newRounds === -1 ? 1 : 0);
      overlay.addChild(badgeText);

      overlay.alpha = 1;
      console.log(`[BADGE DEBUG] Badge updated successfully`);
    },

    markSidebetForRemoval: (seatPosition: number, sidebetType: string) => {
      const key = `${seatPosition}-${sidebetType}`;
      console.log(`[BADGE DEBUG] Marking sidebet for removal during rake: ${key}`);
      sceneRef.current.sidebetsMarkedForRemoval.add(key);
    },

    removeExpiredSidebets: () => {
      const scene = sceneRef.current;
      const toRemove = Array.from(scene.sidebetsMarkedForRemoval);
      console.log(`[BADGE DEBUG] removeExpiredSidebets called, marked for removal: ${toRemove.length}`, toRemove);

      if (toRemove.length === 0) return;

      for (const key of toRemove) {
        const [seatPosStr, ...typeParts] = key.split('-');
        const seatPosition = parseInt(seatPosStr, 10);
        const sidebetType = typeParts.join('-'); // Rejoin in case type has hyphens

        console.log(`[BADGE DEBUG] Removing expired sidebet: seat ${seatPosition}, type ${sidebetType}`);

        // Find and remove the sprite
        for (let i = 0; i < 3; i++) {
          const spriteKey = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(spriteKey);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType === sidebetType ||
                (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
              // Destroy border ring first
              const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
              if (borderRing && !borderRing.destroyed) {
                borderRing.destroy();
              }
              sprite.destroy();
              scene.sidebetSprites.delete(spriteKey);

              // Remove overlay
              const overlay = scene.sidebetOverlays.get(spriteKey);
              if (overlay && !overlay.destroyed) {
                overlay.destroy();
              }
              scene.sidebetOverlays.delete(spriteKey);

              // Remove counter and pill
              const counter = scene.sidebetCounters.get(spriteKey);
              if (counter && !counter.destroyed) {
                counter.destroy();
              }
              scene.sidebetCounters.delete(spriteKey);
              const counterPill = scene.sidebetCounterPills.get(spriteKey);
              if (counterPill && !counterPill.destroyed) {
                counterPill.destroy();
              }
              scene.sidebetCounterPills.delete(spriteKey);
              scene.sidebetChipCounts.delete(spriteKey);

              console.log(`[BADGE DEBUG] Removed expired sidebet from slot ${i}`);
              break;
            }
          }
        }

        // Clear from active sidebets
        scene.activeSidebets.delete(`${seatPosition}-${sidebetType}`);
        // Clear server rounds on true retire (expired sidebet removed)
        scene.sidebetServerRounds.delete(`${seatPosition}-${sidebetType}`);
      }

      // Clear the removal set
      scene.sidebetsMarkedForRemoval.clear();

      // Reposition remaining icons for affected seats (including counterPill)
      const affectedSeats = new Set(toRemove.map(key => parseInt(key.split('-')[0], 10)));
      for (const seatPos of affectedSeats) {
        // Inline repositioning for this seat
        const activeSlots: { sprite: Sprite; overlay: Container | null; counter: Text | null; counterPill: Graphics | null; borderRing: Graphics | null }[] = [];
        for (let i = 0; i < 3; i++) {
          const k = `${seatPos}-${i}`;
          const s = scene.sidebetSprites.get(k);
          const o = scene.sidebetOverlays.get(k);
          const c = scene.sidebetCounters.get(k);
          const cp = scene.sidebetCounterPills.get(k);
          if (s && !s.destroyed) {
            activeSlots.push({
              sprite: s,
              overlay: o || null,
              counter: c || null,
              counterPill: cp || null,
              borderRing: (s as unknown as { borderRing?: Graphics }).borderRing || null,
            });
          }
        }
        const config = sidebetConfigRef.current;
        const posIndices = config.getPositionsForCount(activeSlots.length);
        activeSlots.forEach((slot, idx) => {
          const posIdx = posIndices[idx];
          const pos = config.slotPositions[posIdx];
          slot.sprite.x = pos.localX;
          slot.sprite.y = pos.localY;
          if (slot.borderRing && !slot.borderRing.destroyed) {
            slot.borderRing.x = pos.localX;
            slot.borderRing.y = pos.localY;
          }
          if (slot.overlay) {
            slot.overlay.x = pos.localX;
            slot.overlay.y = pos.localY;
          }
          if (slot.counter && !slot.counter.destroyed) {
            slot.counter.x = pos.localX;
            slot.counter.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
          }
          if (slot.counterPill && !slot.counterPill.destroyed) {
            slot.counterPill.x = pos.localX;
            slot.counterPill.y = pos.localY + config.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
          }
        });
      }

      console.log(`[BADGE DEBUG] removeExpiredSidebets complete`);
    },

    clearSidebetsFromSeat: (seatPosition: number) => {
      console.log(`[Sidebet Icon] Clearing all sidebets from seat ${seatPosition}`);

      const scene = sceneRef.current;

      // Remove all sprites, border rings, and overlays for this seat
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          // Destroy border ring first (stored on sprite)
          const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
          if (borderRing && !borderRing.destroyed) {
            borderRing.destroy();
          }
          sprite.destroy();
        }
        scene.sidebetSprites.delete(key);

        const overlay = scene.sidebetOverlays.get(key);
        if (overlay && !overlay.destroyed) {
          overlay.destroy();
        }
        scene.sidebetOverlays.delete(key);
      }

      // Clear active sidebets for this seat
      for (const key of Array.from(scene.activeSidebets.keys())) {
        if (key.startsWith(`${seatPosition}-`)) {
          scene.activeSidebets.delete(key);
        }
      }
      // Clear server rounds for this seat on true retire (seat cleared)
      for (const key of Array.from(scene.sidebetServerRounds.keys())) {
        if (key.startsWith(`${seatPosition}-`)) {
          scene.sidebetServerRounds.delete(key);
        }
      }

      // Remove the container
      const container = scene.seatSidebetContainers.get(seatPosition);
      if (container && !container.destroyed) {
        container.destroy();
      }
      scene.seatSidebetContainers.delete(seatPosition);
    },

    // Clear ALL visuals for a player seat (cards, kitty, sidebets) - used when player leaves
    clearSeatVisuals: async (seatPosition: number): Promise<void> => {
      console.log(`[PixiGameTableV1] clearSeatVisuals for seat ${seatPosition}`);

      const scene = sceneRef.current;
      if (!scene.animationLayer) {
        console.log(`[clearSeatVisuals] No animation layer, skipping`);
        return;
      }

      // 1. Clear cards for this seat (no animation - just destroy immediately)
      const cardsToRemove: string[] = [];
      MODULE_CARD_STATES.forEach((state, key) => {
        // Keys are like "3-0-0" (seat-hand-card) or "dealer-0-0"
        const parts = key.split('-');
        const keySeat = parseInt(parts[0]);
        if (keySeat === seatPosition) {
          cardsToRemove.push(key);
        }
      });

      for (const key of cardsToRemove) {
        const state = MODULE_CARD_STATES.get(key);
        if (state?.sprite && !state.sprite.destroyed) {
          scene.animationLayer?.removeChild(state.sprite);
          state.sprite.destroy({ children: true });
        }
        MODULE_CARD_STATES.delete(key);
      }
      console.log(`[clearSeatVisuals] Cleared ${cardsToRemove.length} cards for seat ${seatPosition}`);

      // Clear straightened state for this seat
      MODULE_STRAIGHTENED.delete(`${seatPosition}`);
      for (let h = 0; h < 5; h++) {
        MODULE_STRAIGHTENED.delete(`${seatPosition}-${h}`);
      }

      // Clear dealt counts for this seat
      for (let h = 0; h < 5; h++) {
        MODULE_DEALT_CARD_COUNTS.delete(`${seatPosition}-${h}`);
      }

      // Clear scorebox cache for this seat
      for (let h = 0; h < 5; h++) {
        MODULE_SCOREBOX_CACHE.delete(`${seatPosition}-${h}`);
      }

      // 2. Clear kitty for this seat
      const visualPos = getVisualPositionForSeat(seatPosition);
      const kittyContainer = scene.kittyContainers.get(visualPos);
      if (kittyContainer && !kittyContainer.destroyed) {
        // Remove all children (chip sprites)
        while (kittyContainer.children.length > 0) {
          const child = kittyContainer.children[0];
          kittyContainer.removeChild(child);
          if (!child.destroyed) {
            child.destroy({ children: true });
          }
        }
        // Remove container itself
        kittyContainer.parent?.removeChild(kittyContainer);
        kittyContainer.destroy();
      }
      scene.kittyContainers.delete(visualPos);
      scene.kittyChipSprites.delete(visualPos);
      console.log(`[clearSeatVisuals] Cleared kitty for seat ${seatPosition} (visualPos ${visualPos})`);

      // 3. Clear sidebet icons for this seat
      // Remove all sprites, border rings, and overlays
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          const borderRing = (sprite as unknown as { borderRing?: Graphics }).borderRing;
          if (borderRing && !borderRing.destroyed) {
            borderRing.destroy();
          }
          sprite.destroy();
        }
        scene.sidebetSprites.delete(key);

        const overlay = scene.sidebetOverlays.get(key);
        if (overlay && !overlay.destroyed) {
          overlay.destroy();
        }
        scene.sidebetOverlays.delete(key);

        const counter = scene.sidebetCounters.get(key);
        if (counter && !counter.destroyed) {
          counter.destroy();
        }
        scene.sidebetCounters.delete(key);
      }

      // Clear active sidebets for this seat
      for (const key of Array.from(scene.activeSidebets.keys())) {
        if (key.startsWith(`${seatPosition}-`)) {
          scene.activeSidebets.delete(key);
        }
      }

      // Remove sidebet container
      const sidebetContainer = scene.seatSidebetContainers.get(seatPosition);
      if (sidebetContainer && !sidebetContainer.destroyed) {
        sidebetContainer.destroy();
      }
      scene.seatSidebetContainers.delete(seatPosition);
      console.log(`[clearSeatVisuals] Cleared sidebets for seat ${seatPosition}`);

      // 4. Clear outcome labels for this seat
      for (let h = 0; h < 5; h++) {
        const labelKey = `${seatPosition}-${h}`;
        const labelState = MODULE_OUTCOME_LABELS.get(labelKey);
        if (labelState && !labelState.wrapper.destroyed) {
          labelState.wrapper.destroy();
        }
        MODULE_OUTCOME_LABELS.delete(labelKey);
      }
      console.log(`[clearSeatVisuals] Cleared outcome labels for seat ${seatPosition}`);

      // 5. Destroy seat container (avatar, username, score box)
      const seatContainer = scene.seatContainers.get(visualPos);
      if (seatContainer && !seatContainer.destroyed) {
        seatContainer.destroy({ children: true });
      }
      scene.seatContainers.delete(visualPos);
      console.log(`[clearSeatVisuals] Cleared seat container for seat ${seatPosition} (visualPos ${visualPos})`);

      console.log(`[clearSeatVisuals] Complete for seat ${seatPosition}`);
    },

    getSidebetIconPosition: (seatPosition: number, sidebetType: string): { x: number; y: number } | null => {
      const scene = sceneRef.current;

      // Find the slot with this sidebet type (partial match for over-under-13 variants)
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite) {
          const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
          if (storedType === sidebetType ||
              (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
            // Get global position for animation targeting
            const globalPos = sprite.getGlobalPosition();
            if (scene.animationLayer) {
              const localPos = scene.animationLayer.toLocal(globalPos);
              return { x: localPos.x, y: localPos.y };
            }
            return { x: globalPos.x, y: globalPos.y };
          }
        }
      }

      return null;
    },

    // ===========================================
    // SIDEBET ANIMATIONS
    // ===========================================

    animateSidebetPlacement: (seatPosition: number, sidebetType: string, amount: number, rounds: number, duration?: number, stagger?: number): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[Sidebet Anim] Placement: seat ${seatPosition}, ${sidebetType}, $${amount}, rounds: ${rounds}, duration: ${duration ?? 'default'}, stagger: ${stagger ?? 'default'}`);

        const scene = sceneRef.current;

        if (!scene.animationLayer) {
          console.warn('[Sidebet Anim] Missing animation layer');
          resolve();
          return;
        }

        // =====================================================
        // STEP 1: Find existing sidebet icon at seat
        // =====================================================
        // Icons should already exist from addSidebetToSeat (called when configured)
        // This function only animates chips to the existing icon

        // Find the slot index for this sidebet type at this seat
        // Use partial matching for over-under-13 variants (icon may have base type)
        let slotIndex = -1;
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const existing = scene.sidebetSprites.get(key);
          if (existing) {
            const storedType = (existing as unknown as { sidebetType?: string }).sidebetType;
            // Exact match or partial match for over-under-13 variants
            if (storedType === sidebetType ||
                (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
              slotIndex = i;
              break;
            }
          }
        }

        // If icon not found, it should have been created by addSidebetToSeat
        if (slotIndex === -1) {
          console.warn(`[Sidebet Anim] No icon found for ${sidebetType} at seat ${seatPosition}. Call addSidebetToSeat first.`);
          resolve();
          return;
        }

        const sidebetKey = `${seatPosition}-${slotIndex}`;
        const sidebetSprite = scene.sidebetSprites.get(sidebetKey);
        const sidebetOverlay = scene.sidebetOverlays.get(sidebetKey);

        if (!sidebetSprite) {
          console.warn('[Sidebet Anim] Missing sprite for', sidebetKey);
          resolve();
          return;
        }

        // Store active sidebet state (keyed by seatPosition-sidebetType for lookup)
        scene.activeSidebets.set(`${seatPosition}-${sidebetType}`, { amount, rounds });
        // Store server-authoritative rounds (survives across rounds, cleared only on true retire)
        scene.sidebetServerRounds.set(`${seatPosition}-${sidebetType}`, rounds);

        // Reset chip count for this sidebet (will count up as chips arrive)
        // Skip counter updates for opponents - they don't show chip counters
        scene.sidebetChipCounts.set(sidebetKey, 0);
        const isOpponentSidebet = (sidebetSprite as unknown as { isOpponentSidebet?: boolean }).isOpponentSidebet ?? false;
        const counterText = scene.sidebetCounters.get(sidebetKey);
        const counterPill = scene.sidebetCounterPills.get(sidebetKey);
        if (counterText && !counterText.destroyed && counterPill && !counterPill.destroyed && !isOpponentSidebet) {
          updateSidebetCounterLayout(counterPill, counterText, 0, SIDEBET_ICON_SIZE);
        }

        // Break down amount into chips, cap $10k chips at 100, sort ASCENDING (smallest first for arriving animation)
        const rawChips = breakdownChips(amount);
        const { chips: cappedChips, tenKValue } = capChipsForAnimation(rawChips);
        const sortedChips = [...cappedChips].sort((a, b) => a - b);

        if (sortedChips.length === 0) {
          resolve();
          return;
        }

        // Get sidebet icon position - convert global to animationLayer local coordinates
        const sidebetGlobalPos = sidebetSprite.getGlobalPosition();
        const sidebetLocalPos = scene.animationLayer.toLocal(sidebetGlobalPos);

        // Animation constants (matching main bet placement pattern)
        // Use server-provided stagger, or fallback to cached config (logs error if fallback used)
        const STAGGER_DELAY = getStaggerValue('sidebetChip', stagger, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateSidebetPlacement',
          eventType: 'SIDEBET_PLACED',
        });
        // Use server-provided duration, or fallback to cached config (logs error if fallback used)
        const DURATION = getAnimationDuration('chipFlight', duration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateSidebetPlacement',
          eventType: 'SIDEBET_PLACED',
        });
        console.log(`[Sidebet Anim] Using duration: ${DURATION}ms, stagger: ${STAGGER_DELAY}ms (server: dur=${duration ?? 'none'}, stag=${stagger ?? 'none'}), isOpponent: ${isOpponentSidebet}, source: ${isOpponentSidebet ? 'avatar' : 'rail'}`);
        // Standardized: No arc - matches standard chip-to-dealer style

        // Calculate chip sizes:
        // SOURCE: Player rail chips are 48px * railScale (container is scaled)
        //         Opponent avatar chips are 16px (small chips behind avatar)
        // TARGET: Sidebet icons are SIDEBET_ICON_SIZE (40px, no additional scaling)
        const railScale = (chipRailConfig?.scale ?? 100) / 100;
        const AVATAR_CHIP_SIZE = 16; // Small chips for opponent animation
        const SOURCE_SIZE = isOpponentSidebet ? AVATAR_CHIP_SIZE : CHIP_SIZES.rail * railScale;
        const TARGET_SIZE = SIDEBET_ICON_SIZE;

        // For opponent sidebets, calculate avatar position - uses registry function for consistency
        // IMPORTANT: Must apply seat config offsets (x, y, rot) to match where avatar is actually rendered
        let opponentAvatarPos: { x: number; y: number } | null = null;
        if (isOpponentSidebet) {
          const visualPos = getVisualPositionForSeat(seatPosition);
          const seatData = dynamicSeatPositions[visualPos - 1];
          if (seatData) {
            const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
            const adjustedSeatData = calculateAdjustedSeatPosition(seatData, seatConfig);
            opponentAvatarPos = calculateAvatarPosition(adjustedSeatData, seatConfig, seatSizeScale);
          }
        }

        const chipSprites: Sprite[] = [];
        let completedCount = 0;

        // Create and animate each chip with stagger
        sortedChips.forEach((denom, index) => {
          const chipTexture = texturesRef.current.get(`chip-${denom}`);
          if (!chipTexture) {
            console.warn('[Sidebet Anim] No chip texture for', denom);
            return;
          }

          // Determine chip source position:
          // - For opponents: use avatar position (chips appear from behind avatar)
          // - For player: use actual rail chip position from rendered sprite
          let sourcePos: { x: number; y: number };

          if (isOpponentSidebet && opponentAvatarPos) {
            // Opponent: chips come from behind avatar
            sourcePos = opponentAvatarPos;
          } else {
            // Player: get ACTUAL rail chip position from the rendered sprite
            // Rail chips are in uiLayer, animation happens in animationLayer (inside gameContentContainer)
            // Both have the same scale/position transforms, so we convert global → animationLayer local
            const railChip = scene.chipRailChips.get(denom);

            if (railChip && scene.animationLayer) {
              // Use actual rendered position - convert global to animationLayer local coordinates
              const globalPos = railChip.getGlobalPosition();
              const localPos = scene.animationLayer.toLocal(globalPos);
              sourcePos = { x: localPos.x, y: localPos.y };
            } else {
              // Fallback to calculated position
              const calcPos = getRailChipPositionRef.current?.(denom);
              if (!calcPos) {
                console.warn('[Sidebet Anim] No rail position for', denom);
                return;
              }
              sourcePos = calcPos;
            }
          }

          // Create chip sprite at source size
          const chipSprite = new Sprite(chipTexture);
          chipSprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
          chipSprite.anchor.set(0.5);
          chipSprite.width = SOURCE_SIZE;
          chipSprite.height = SOURCE_SIZE;
          chipSprite.x = sourcePos.x;
          chipSprite.y = sourcePos.y;
          chipSprite.alpha = 0; // Start invisible, reveal on stagger
          // Random starting rotation for natural chip appearance
          chipSprite.rotation = Math.random() * Math.PI * 2;
          // zIndex must be above kitty stacks (1500) so chips fly over them
          chipSprite.zIndex = 2000;
          scene.animationLayer!.addChild(chipSprite);
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);
          chipSprites.push(chipSprite);

          // Delayed start for stagger effect
          setTimeout(() => {
            chipSprite.alpha = 1;
            const startTime = performance.now();
            const startX = sourcePos.x;
            const startY = sourcePos.y;
            const endX = sidebetLocalPos.x;
            const endY = sidebetLocalPos.y;
            let counterUpdated = false; // Track if counter has been updated (at 60% progress)

            // Random end rotation for natural chip appearance
            const startRotation = chipSprite.rotation;
            const endRotation = Math.random() * Math.PI * 2;

            const animateFrame = () => {
              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / DURATION, 1);
              const eased = easeOutCubic(progress);

              // Linear interpolation (standardized - matches chip-to-dealer style)
              chipSprite.x = startX + (endX - startX) * eased;
              chipSprite.y = startY + (endY - startY) * eased;

              // Interpolate size from rail to sidebet icon (use width/height, not scale.set)
              const currentSize = SOURCE_SIZE + (TARGET_SIZE - SOURCE_SIZE) * eased;
              chipSprite.width = currentSize;
              chipSprite.height = currentSize;

              // Rotate during flight (standardized - matches chip-to-dealer style)
              chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

              // Update counter at 60% progress (ensures last chip registers before completion)
              // Skip counter updates for opponents - they don't show chip counters
              if (progress >= 0.5 && !counterUpdated) {
                counterUpdated = true;
                // Increment sidebet total by this chip's value
                // For $10k chips, use adjusted tenKValue to account for capping
                const chipValue = denom === 10000 ? tenKValue : denom;
                const counterText = scene.sidebetCounters.get(sidebetKey);
                const counterPill = scene.sidebetCounterPills.get(sidebetKey);
                const currentTotal = (scene.sidebetChipCounts.get(sidebetKey) || 0) + chipValue;
                scene.sidebetChipCounts.set(sidebetKey, currentTotal);
                if (counterText && !counterText.destroyed && counterPill && !counterPill.destroyed && !isOpponentSidebet) {
                  updateSidebetCounterLayout(counterPill, counterText, currentTotal, SIDEBET_ICON_SIZE);
                }
              }

              if (progress < 1) {
                requestAnimationFrame(animateFrame);
              } else {
                // Sound: chipLand on sidebet placement arrival at sidebet icon
                playTableSound('chipLand');
                // This chip finished
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
                chipSprite.destroy();
                completedCount++;

                // All chips done?
                if (completedCount === chipSprites.length) {
                  // Reveal the sidebet icon (flip from pending to active)
                  if (sidebetSprite && !sidebetSprite.destroyed) {
                    sidebetSprite.alpha = 1;
                    // Also flip the border ring to fully opaque
                    const borderRing = (sidebetSprite as unknown as { borderRing?: Graphics }).borderRing;
                    if (borderRing && !borderRing.destroyed) {
                      borderRing.alpha = 1;
                    }
                  }

                  // Show badge on sidebet icon (rounds remaining)
                  // Skip badges for opponent sidebets - they don't need to see rounds/amounts
                  if (sidebetOverlay && !isOpponentSidebet) {
                    sidebetOverlay.removeChildren();

                    // Badge showing rounds remaining (bottom-right corner)
                    const badgeSize = 18;
                    const badgeX = SIDEBET_ICON_SIZE / 2 - 4;
                    const badgeY = SIDEBET_ICON_SIZE / 2 - 4;

                    const badgeBg = new Graphics();
                    badgeBg.circle(badgeX, badgeY, badgeSize / 2);
                    badgeBg.fill({ color: 0x22c55e });
                    badgeBg.stroke({ color: 0xffffff, width: 1.5 });
                    sidebetOverlay.addChild(badgeBg);

                    const badgeText = new Text({
                      text: rounds === -1 ? '∞' : String(rounds),
                      style: {
                        fontFamily: 'Arial, sans-serif',
                        fontSize: rounds === -1 ? 14 : 11,
                        fontWeight: 'bold',
                        fill: 0xffffff,
                      }
                    });
                    badgeText.anchor.set(0.5);
                    badgeText.x = badgeX;
                    badgeText.y = badgeY + (rounds === -1 ? 1 : 0);
                    sidebetOverlay.addChild(badgeText);

                    // Amount label below icon
                    const amountText = new Text({
                      text: `$${amount.toLocaleString()}`,
                      style: {
                        fontFamily: 'Arial, sans-serif',
                        fontSize: 10,
                        fontWeight: 'bold',
                        fill: 0xffffff,
                        stroke: { color: 0x000000, width: 2 },
                      }
                    });
                    amountText.anchor.set(0.5, 0);
                    amountText.x = 0;
                    amountText.y = SIDEBET_ICON_SIZE / 2 + 2;
                    sidebetOverlay.addChild(amountText);

                    sidebetOverlay.alpha = 1;
                  }

                  console.log(`[Sidebet Anim] Placement complete: ${chipSprites.length} chips`);
                  resolve();
                }
              }
            };

            requestAnimationFrame(animateFrame);
          }, index * STAGGER_DELAY);
        });

        // Safety: if no valid chips, resolve immediately
        if (chipSprites.length === 0) {
          resolve();
        }
      });
    },

    showSidebetWin: (seatPosition: number, sidebetType: string, winType?: string) => {
      console.log(`[Sidebet Anim] Win: seat ${seatPosition}, ${sidebetType}, ${winType || 'win'}`);

      const scene = sceneRef.current;

      // Find the slot index for this sidebet type at this seat (exact then partial match)
      let slotIndex = -1;

      // Pass 1: Exact match
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
          if (storedType === sidebetType) {
            slotIndex = i;
            break;
          }
        }
      }

      // Pass 2: Partial match for over-under-13 variants
      if (slotIndex === -1) {
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType))) {
              slotIndex = i;
              break;
            }
          }
        }
      }

      const sidebetKey = slotIndex >= 0 ? `${seatPosition}-${slotIndex}` : null;
      const activeKey = `${seatPosition}-${sidebetType}`;

      const sidebet = scene.activeSidebets.get(activeKey);
      if (sidebet) {
        sidebet.resolved = true;
        sidebet.outcome = 'win';
        sidebet.winType = winType;
      }

      // Mark as previously played at this table
      scene.previouslyPlayedSidebets.add(sidebetType);

      // Check if still configured - if so, keep green; otherwise show gold "previously played"
      const config = useGameStateStore.getState().mySidebetConfigs.get(sidebetType as SidebetType);
      const overlay = sidebetKey ? scene.sidebetOverlays.get(sidebetKey) : null;
      const sidebetSprite = sidebetKey ? scene.sidebetSprites.get(sidebetKey) : null;

      // Check if this is an opponent's sidebet
      const isOpponentSidebet = sidebetSprite ? (sidebetSprite as unknown as { isOpponentSidebet?: boolean }).isOpponentSidebet ?? false : false;

      // Update border ring to GREEN for win (player only - opponents don't show outcome borders)
      if (sidebetSprite && !sidebetSprite.destroyed && !isOpponentSidebet) {
        const borderRing = (sidebetSprite as unknown as { borderRing?: Graphics }).borderRing;
        if (borderRing) {
          borderRing.clear();
          borderRing.circle(0, 0, (sidebetConfigRef.current?.iconSize ?? SIDEBET_ICON_SIZE) / 2 + 3);
          borderRing.stroke({ color: 0x22c55e, width: 8 }); // Green border for win
        }
      }

      // Skip badge for opponent sidebets - they don't need to see rounds
      if (overlay && !isOpponentSidebet) {
        overlay.removeChildren();

        if (config) {
          // Still configured - show badge with rounds remaining
          const badgeSize = 18;
          const badgeX = SIDEBET_ICON_SIZE / 2 - 4;
          const badgeY = SIDEBET_ICON_SIZE / 2 - 4;
          const badgeBg = new Graphics();
          badgeBg.circle(badgeX, badgeY, badgeSize / 2);
          badgeBg.fill({ color: 0x22c55e });
          badgeBg.stroke({ color: 0xffffff, width: 1.5 });
          overlay.addChild(badgeBg);

          // SERVER-AUTHORITATIVE: read rounds from the persistent map (survives resolution);
          // fall back to config.rounds only pre-first-placement. Prevents the badge bouncing back
          // to the frozen config value when a round resolves (win/loss).
          const _resolveRounds = scene.sidebetServerRounds.get(activeKey) ?? config.rounds;
          const badgeText = new Text({
            text: _resolveRounds === -1 ? '∞' : String(_resolveRounds),
            style: { fontFamily: 'Arial', fontSize: _resolveRounds === -1 ? 14 : 11, fontWeight: 'bold', fill: 0xffffff }
          });
          badgeText.anchor.set(0.5);
          badgeText.x = badgeX;
          badgeText.y = badgeY + (_resolveRounds === -1 ? 1 : 0);
          overlay.addChild(badgeText);

          overlay.alpha = 1;
        } else {
          // No longer configured - hide overlay (icon will be removed)
          overlay.alpha = 0;
        }
      }
    },

    showSidebetLoss: (seatPosition: number, sidebetType: string) => {
      console.log(`[Sidebet Anim] showSidebetLoss called: seat ${seatPosition}, ${sidebetType}`);

      const scene = sceneRef.current;
      console.log(`[Sidebet Anim] Available sprites: ${Array.from(scene.sidebetSprites.keys()).join(', ')}`);

      // Find the slot index for this sidebet type at this seat (exact then partial match)
      let slotIndex = -1;

      // Pass 1: Exact match
      for (let i = 0; i < 3; i++) {
        const key = `${seatPosition}-${i}`;
        const sprite = scene.sidebetSprites.get(key);
        if (sprite && !sprite.destroyed) {
          const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
          console.log(`[Sidebet Anim] Checking slot ${i}: storedType=${storedType}, looking for=${sidebetType}`);
          if (storedType === sidebetType) {
            slotIndex = i;
            break;
          }
        }
      }

      // Pass 2: Partial match for over-under-13 variants
      if (slotIndex === -1) {
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType))) {
              slotIndex = i;
              break;
            }
          }
        }
      }

      const sidebetKey = slotIndex >= 0 ? `${seatPosition}-${slotIndex}` : null;
      console.log(`[Sidebet Anim] showSidebetLoss: slotIndex=${slotIndex}, sidebetKey=${sidebetKey}`);
      const activeKey = `${seatPosition}-${sidebetType}`;

      const sidebet = scene.activeSidebets.get(activeKey);
      if (sidebet) {
        sidebet.resolved = true;
        sidebet.outcome = 'lose';
      }

      // Mark as previously played at this table
      scene.previouslyPlayedSidebets.add(sidebetType);

      // Check if still configured - if so, keep green; otherwise show gold "previously played"
      const config = useGameStateStore.getState().mySidebetConfigs.get(sidebetType as SidebetType);
      const overlay = sidebetKey ? scene.sidebetOverlays.get(sidebetKey) : null;
      const sidebetSprite = sidebetKey ? scene.sidebetSprites.get(sidebetKey) : null;

      // Check if this is an opponent's sidebet
      const isOpponentSidebet = sidebetSprite ? (sidebetSprite as unknown as { isOpponentSidebet?: boolean }).isOpponentSidebet ?? false : false;

      // Update border ring to RED for loss (player only - opponents don't show outcome borders)
      if (sidebetSprite && !sidebetSprite.destroyed && !isOpponentSidebet) {
        const borderRing = (sidebetSprite as unknown as { borderRing?: Graphics }).borderRing;
        if (borderRing) {
          console.log(`[Sidebet Anim] showSidebetLoss: Updating border to RED for ${sidebetKey}`);
          borderRing.clear();
          borderRing.circle(0, 0, (sidebetConfigRef.current?.iconSize ?? SIDEBET_ICON_SIZE) / 2 + 3);
          borderRing.stroke({ color: 0xef4444, width: 8 }); // Red border for loss
        } else {
          console.log(`[Sidebet Anim] showSidebetLoss: No borderRing found for ${sidebetKey}`);
        }
      } else if (!isOpponentSidebet) {
        console.log(`[Sidebet Anim] showSidebetLoss: Sprite not found or destroyed for ${sidebetKey}`);
      }

      // Skip badge for opponent sidebets - they don't need to see rounds
      if (overlay && !isOpponentSidebet) {
        overlay.removeChildren();

        if (config) {
          // Still configured - show badge with rounds remaining
          const badgeSize = 18;
          const badgeX = SIDEBET_ICON_SIZE / 2 - 4;
          const badgeY = SIDEBET_ICON_SIZE / 2 - 4;
          const badgeBg = new Graphics();
          badgeBg.circle(badgeX, badgeY, badgeSize / 2);
          badgeBg.fill({ color: 0x22c55e });
          badgeBg.stroke({ color: 0xffffff, width: 1.5 });
          overlay.addChild(badgeBg);

          // SERVER-AUTHORITATIVE: read rounds from the persistent map (survives resolution);
          // fall back to config.rounds only pre-first-placement. Prevents the badge bouncing back
          // to the frozen config value when a round resolves (win/loss).
          const _resolveRounds = scene.sidebetServerRounds.get(activeKey) ?? config.rounds;
          const badgeText = new Text({
            text: _resolveRounds === -1 ? '∞' : String(_resolveRounds),
            style: { fontFamily: 'Arial', fontSize: _resolveRounds === -1 ? 14 : 11, fontWeight: 'bold', fill: 0xffffff }
          });
          badgeText.anchor.set(0.5);
          badgeText.x = badgeX;
          badgeText.y = badgeY + (_resolveRounds === -1 ? 1 : 0);
          overlay.addChild(badgeText);

          overlay.alpha = 1;
        } else {
          // No longer configured - hide overlay (icon will be removed)
          overlay.alpha = 0;
        }
      }
    },

    animateSidebetWin: (seatPosition: number, sidebetType: string, amount: number, chipBreakdown: ChipBreakdown, duration?: number): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[Sidebet Anim] Win payout: seat ${seatPosition}, ${sidebetType}, $${amount}, duration: ${duration ?? 'default'}, serverChips: ${chipBreakdown?.totalChips ?? 'N/A'}`);

        const scene = sceneRef.current;
        // Determine if this is an opponent seat for config selection
        const isOpponent = seatPosition !== mySeatPosition;
        const effectiveSidebetConfig = isOpponent ? opponentSidebetConfigRef.current : sidebetConfigRef.current;

        // Debug: Log all sidebet sprites at this seat
        const storedTypes: { slot: number; type: string | undefined }[] = [];
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            storedTypes.push({ slot: i, type: storedType });
          }
        }
        console.log(`[Sidebet Anim] Looking for "${sidebetType}" at seat ${seatPosition}. Stored sprites:`, JSON.stringify(storedTypes));

        // Find the slot index for this sidebet type at this seat
        // First try exact match, then partial match for over-under-13 variants
        let slotIndex = -1;

        // Pass 1: Exact match
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType === sidebetType) {
              slotIndex = i;
              console.log(`[Sidebet Anim] Exact match found at slot ${i}`);
              break;
            }
          }
        }

        // Pass 2: Partial match for over-under-13 variants
        if (slotIndex === -1) {
          for (let i = 0; i < 3; i++) {
            const key = `${seatPosition}-${i}`;
            const sprite = scene.sidebetSprites.get(key);
            if (sprite && !sprite.destroyed) {
              const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
              // Match if either starts with the other (handles over-under-13 vs over-under-13-over)
              if (storedType && (
                storedType.startsWith(sidebetType) ||
                sidebetType.startsWith(storedType)
              )) {
                slotIndex = i;
                console.log(`[Sidebet Anim] Partial match found at slot ${i}: stored="${storedType}" vs event="${sidebetType}"`);
                break;
              }
            }
          }
        }

        const sidebetKey = slotIndex >= 0 ? `${seatPosition}-${slotIndex}` : null;
        const sidebetSprite = sidebetKey ? scene.sidebetSprites.get(sidebetKey) : null;
        const isOpponentSidebet = sidebetSprite ? (sidebetSprite as unknown as { isOpponentSidebet?: boolean }).isOpponentSidebet ?? false : false;

        if (!sidebetSprite || !scene.animationLayer) {
          console.warn(`[Sidebet Anim] No sprite found for ${sidebetType} at seat ${seatPosition}. Check stored types above.`);
          resolve();
          return;
        }

        // Get the ORIGINAL bet amount before payout chips arrive
        // The counter currently shows the original sidebet amount
        const originalBet = sidebetKey ? (scene.sidebetChipCounts.get(sidebetKey) || 0) : 0;

        // Backend sends `amount` = full payout (original bet returned + profit)
        // For 1:1 payout: $4k bet wins $4k profit, so amount = $8k
        // We only want to animate PROFIT chips from tray to icon
        const profit = Math.max(0, amount - originalBet);

        console.log(`[Sidebet Anim] Win math: originalBet=$${originalBet}, fullPayout=$${amount}, profit=$${profit}`);

        // Phase 1 chips: Only the PROFIT (what dealer pays out)
        // Still calculated locally since server only provides payout breakdown
        const phase1Raw = breakdownChips(profit);
        const { chips: phase1ChipDenoms, tenKValue: phase1TenKValue } = capChipsForAnimation(phase1Raw);

        // Phase 2 chips: The FULL payout (original bet + profit = amount)
        // SERVER-AUTHORITATIVE: Use chipBreakdown from server instead of calculating locally
        // This ensures client animation matches server timing exactly
        const phase2ChipDenoms: number[] = [];

        // Track the largest denomination's face value and adjusted counter value
        // When capping occurs, only the LARGEST denomination gets capped/adjusted
        let largestDenomFaceValue = 0;
        let largestDenomCounterValue = 0;

        // Convert server's ChipBreakdown to array format for animation
        // Order: smallest denominations first (matches existing animation logic)
        const denomMappings = [
          { key: 'denom1', faceValue: 1 },
          { key: 'denom5', faceValue: 5 },
          { key: 'denom25', faceValue: 25 },
          { key: 'denom100', faceValue: 100 },
          { key: 'denom500', faceValue: 500 },
          { key: 'denom1000', faceValue: 1000 },
          { key: 'denom5000', faceValue: 5000 },
          { key: 'denom10000', faceValue: 10000 },
          { key: 'denom50000', faceValue: 50000 },
          { key: 'denom100000', faceValue: 100000 },
          { key: 'denom500000', faceValue: 500000 },
          { key: 'denom1000000', faceValue: 1000000 },
        ];

        for (const { key, faceValue } of denomMappings) {
          const denomData = chipBreakdown[key as keyof ChipBreakdown] as { qty: number; value: number } | undefined;
          if (denomData && typeof denomData === 'object' && denomData.qty > 0) {
            for (let i = 0; i < denomData.qty; i++) {
              phase2ChipDenoms.push(faceValue);
            }
            // Track the largest denomination (last one with chips, since we iterate ascending)
            // Its `value` field contains the counter increment (adjusted if capped)
            largestDenomFaceValue = faceValue;
            largestDenomCounterValue = denomData.value;
          }
        }

        // For backwards compatibility, use tenKValue naming but now it's actually the largest denom's value
        const phase2TenKValue = largestDenomCounterValue || 10000;

        console.log(`[Sidebet Anim] SERVER breakdown: ${phase2ChipDenoms.length} chips, largest denom: $${largestDenomFaceValue}, counter value: ${largestDenomCounterValue}`);

        if (phase1ChipDenoms.length === 0 && phase2ChipDenoms.length === 0) {
          resolve();
          return;
        }

        // Get sidebet icon position - convert global to animationLayer local coordinates
        const sidebetGlobalPos = sidebetSprite.getGlobalPosition();
        const sidebetLocalPos = scene.animationLayer.toLocal(sidebetGlobalPos);

        // Animation constants - use config values (stagger no longer server-provided)
        const STAGGER_DELAY = getStaggerValue('sidebetChip', undefined, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateSidebetWin',
          eventType: 'SIDEBET_CHIPS_PAID',
        });
        // Server sends total duration for both phases (tray→icon + icon→rail)
        // Split evenly: 50% for Phase 1, 50% for Phase 2
        const totalDuration = getAnimationDuration('sidebetChipsPaid', duration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateSidebetWin',
          eventType: 'SIDEBET_CHIPS_PAID',
        });
        const PHASE1_DURATION = Math.round(totalDuration / 2); // Dealer → sidebet icon
        const PHASE2_DURATION = Math.round(totalDuration / 2); // Sidebet icon → rail
        const PAUSE_DURATION = 0; // No pause between phases
        console.log(`[Sidebet Anim] Win using duration: total=${totalDuration}ms, Phase1=${PHASE1_DURATION}ms, Phase2=${PHASE2_DURATION}ms, stagger: ${STAGGER_DELAY}ms, serverChips: ${chipBreakdown?.totalChips ?? 'N/A'}`);
        // Size progression: dealer tray → sidebet icon → rail (all dynamic/scalable)
        const DEALER_SIZE = CHIP_SIZES.dealerPile; // 20px base
        const SIDEBET_SIZE = effectiveSidebetConfig.iconSize; // Dynamic from DEV controls
        const railScale = (chipRailConfig?.scale ?? 100) / 100;
        const RAIL_SIZE = CHIP_SIZES.rail * railScale; // Dynamic rail size

        // NOTE: Don't reset counter - PROFIT chips ADD to the existing sidebet amount
        // Counter should show: original bet → original + profit = total
        // Then Phase 2 takes ALL chips to rail, counter → 0

        // ===========================================
        // PHASE 1: Dealer tray → Sidebet icon (ASCENDING - smallest first)
        // Only animates PROFIT chips (not the original bet)
        // ===========================================
        const phase1ChipsSorted = [...phase1ChipDenoms].sort((a, b) => a - b);
        const phase1Sprites: { sprite: Sprite; denom: number; trayLocalPos: { x: number; y: number } }[] = [];

        phase1ChipsSorted.forEach((denom) => {
          const chipTexture = texturesRef.current.get(`chip-${denom}`);
          if (!chipTexture) return;

          // Get ACTUAL tray chip position from rendered sprite (not layoutStore)
          const trayChip = scene.trayChipSprites.get(denom);
          let trayLocalPos: { x: number; y: number };

          if (trayChip && scene.animationLayer) {
            // Use actual rendered position - convert global to animationLayer local
            const trayGlobalPos = trayChip.getGlobalPosition();
            trayLocalPos = scene.animationLayer.toLocal(trayGlobalPos);
          } else {
            // Fallback to layoutStore position
            const fallbackPos = getTrayChipPosition(denom);
            if (!fallbackPos) return;
            trayLocalPos = fallbackPos;
          }

          const chipSprite = new Sprite(chipTexture);
          chipSprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
          chipSprite.anchor.set(0.5);
          chipSprite.width = DEALER_SIZE;
          chipSprite.height = DEALER_SIZE;
          chipSprite.x = trayLocalPos.x;
          chipSprite.y = trayLocalPos.y;
          chipSprite.alpha = 0;
          // Random starting rotation for natural chip appearance
          chipSprite.rotation = Math.random() * Math.PI * 2;
          scene.animationLayer!.addChild(chipSprite);
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

          phase1Sprites.push({ sprite: chipSprite, denom, trayLocalPos });
        });

        // If no profit chips to animate (profit = 0), skip directly to Phase 2
        if (phase1Sprites.length === 0) {
          if (phase2ChipDenoms.length === 0) {
            resolve();
            return;
          }
          // No profit chips, but still need to animate original bet to rail
          startPhase2();
          return;
        }

        let phase1Completed = 0;

        // Animate each chip in Phase 1
        phase1Sprites.forEach(({ sprite, denom, trayLocalPos }, index) => {
          setTimeout(() => {
            sprite.alpha = 1;
            const startTime = performance.now();
            let counterUpdated = false; // Track if counter has been updated (at 60% progress)
            // Random end rotation for natural chip appearance (standardized - matches chip-to-dealer style)
            const startRotation = sprite.rotation;
            const endRotation = Math.random() * Math.PI * 2;

            const animatePhase1 = () => {
              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / PHASE1_DURATION, 1);
              const eased = easeOutCubic(progress);

              // Use local coordinates (both trayLocalPos and sidebetLocalPos are in animationLayer space)
              sprite.x = trayLocalPos.x + (sidebetLocalPos.x - trayLocalPos.x) * eased;
              sprite.y = trayLocalPos.y + (sidebetLocalPos.y - trayLocalPos.y) * eased;

              // Interpolate width/height from dealer size to sidebet size (20 → 40)
              const currentSize = DEALER_SIZE + (SIDEBET_SIZE - DEALER_SIZE) * eased;
              sprite.width = currentSize;
              sprite.height = currentSize;

              // Rotate during flight (standardized - matches chip-to-dealer style)
              sprite.rotation = startRotation + (endRotation - startRotation) * eased;

              // Update counter at 60% progress (ensures last chip registers before completion)
              // For $10k chips, use adjusted phase1TenKValue to account for capping
              // Skip counter updates for opponents - they don't show chip counters
              if (progress >= 0.5 && !counterUpdated) {
                counterUpdated = true;
                if (sidebetKey) {
                  const chipValue = denom === 10000 ? phase1TenKValue : denom;
                  const counterText = scene.sidebetCounters.get(sidebetKey);
                  const counterPill = scene.sidebetCounterPills.get(sidebetKey);
                  const currentTotal = (scene.sidebetChipCounts.get(sidebetKey) || 0) + chipValue;
                  scene.sidebetChipCounts.set(sidebetKey, currentTotal);
                  if (counterText && !counterText.destroyed && counterPill && !counterPill.destroyed && !isOpponentSidebet) {
                    updateSidebetCounterLayout(counterPill, counterText, currentTotal, SIDEBET_ICON_SIZE);
                  }
                }
              }

              if (progress < 1) {
                requestAnimationFrame(animatePhase1);
              } else {
                // Sound: chipLand on sidebet win Phase 1 arrival at sidebet icon
                playTableSound('chipLand');
                phase1Completed++;
                // Counter already updated at 60%

                // All Phase 1 chips arrived at sidebet icon?
                if (phase1Completed === phase1Sprites.length) {
                  // Destroy Phase 1 sprites (they've merged into the icon visually)
                  phase1Sprites.forEach(({ sprite }) => {
                    MODULE_CHIP_SPRITES_IN_FLIGHT.delete(sprite);
                    if (!sprite.destroyed) sprite.destroy();
                  });

                  // Brief pause, then start Phase 2
                  setTimeout(() => {
                    startPhase2();
                  }, PAUSE_DURATION);
                }
              }
            };

            requestAnimationFrame(animatePhase1);
          }, index * STAGGER_DELAY);
        });

        // ===========================================
        // PHASE 2: Sidebet icon → Player rail/avatar (DESCENDING - largest first)
        // Creates NEW sprites for the FULL total (original bet + profit)
        // For current user: pays out to player's rail
        // For opponents: pays out to opponent's avatar
        // ===========================================
        function startPhase2() {
          // Determine if this is an opponent's sidebet
          const mySeat = useGameStateStore.getState().mySeatPosition;
          const isOpponent = mySeat !== null && mySeat !== seatPosition;

          // Calculate opponent avatar position if needed - uses registry function for consistency
          // IMPORTANT: Must apply seat config offsets (x, y, rot) to match where avatar is actually rendered
          let opponentAvatarPos: { x: number; y: number } | null = null;
          if (isOpponent) {
            const visualPos = getVisualPositionForSeat(seatPosition);
            const seatData = dynamicSeatPositions[visualPos - 1];
            if (seatData) {
              const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
              const adjustedSeatData = calculateAdjustedSeatPosition(seatData, seatConfig);
              opponentAvatarPos = calculateAvatarPosition(adjustedSeatData, seatConfig, seatSizeScale);
              console.log(`[Sidebet Anim] Opponent avatar target: seat ${seatPosition} → (${opponentAvatarPos.x.toFixed(1)}, ${opponentAvatarPos.y.toFixed(1)})`);
            }
          }

          // Sort denominations descending (largest first) for leaving animation
          const phase2ChipsSorted = [...phase2ChipDenoms].sort((a, b) => b - a);

          // Create new sprites for Phase 2 at the sidebet icon position
          const phase2Sprites: { sprite: Sprite; denom: number }[] = [];

          phase2ChipsSorted.forEach((denom, index) => {
            const chipTexture = texturesRef.current.get(`chip-${denom}`);
            if (!chipTexture) return;

            // For current user: use rail position. For opponents: use avatar position
            const targetPos = isOpponent ? opponentAvatarPos : getRailChipPositionRef.current?.(denom);
            if (!targetPos) return;

            const chipSprite = new Sprite(chipTexture);
            chipSprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
            chipSprite.anchor.set(0.5);
            chipSprite.width = SIDEBET_SIZE;
            chipSprite.height = SIDEBET_SIZE;
            // Start at sidebet icon position
            chipSprite.x = sidebetLocalPos.x;
            chipSprite.y = sidebetLocalPos.y;
            chipSprite.alpha = 1;
            // Random starting rotation for natural chip appearance
            chipSprite.rotation = Math.random() * Math.PI * 2;
            // Set zIndex so chips departing first (largest, index 0) are on TOP visually
            // This prevents the "dealing from bottom of stack" appearance
            chipSprite.zIndex = phase2ChipsSorted.length - index;
            scene.animationLayer!.addChild(chipSprite);
            MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);

            phase2Sprites.push({ sprite: chipSprite, denom });
          });

          if (phase2Sprites.length === 0) {
            // No chips to animate, just resolve
            scene.activeSidebets.delete(sidebetType);
            resolve();
            return;
          }

          let phase2Completed = 0;

          phase2Sprites.forEach(({ sprite, denom }, index) => {
            // For current user: use rail position. For opponents: use avatar position
            const targetPos = isOpponent ? opponentAvatarPos : getRailChipPositionRef.current?.(denom);
            if (!targetPos) {
              MODULE_CHIP_SPRITES_IN_FLIGHT.delete(sprite);
              sprite.destroy();
              phase2Completed++;
              if (phase2Completed === phase2Sprites.length) {
                scene.activeSidebets.delete(sidebetType);
                resolve();
              }
              return;
            }

            setTimeout(() => {
              const startTime = performance.now();
              const startX = sprite.x;
              const startY = sprite.y;
              // Random end rotation for natural chip appearance (standardized - matches chip-to-dealer style)
              const startRotation = sprite.rotation;
              const endRotation = Math.random() * Math.PI * 2;

              const animatePhase2 = () => {
                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / PHASE2_DURATION, 1);
                const eased = easeOutCubic(progress);

                sprite.x = startX + (targetPos.x - startX) * eased;
                sprite.y = startY + (targetPos.y - startY) * eased;

                // Interpolate width/height from sidebet size to target size
                // For current user: grow to rail size. For opponents: match avatar size (40px)
                // Must use width/height (not scale.set) to match Phase 1's approach
                const OPPONENT_AVATAR_SIZE = 40;
                const targetSize = isOpponent ? OPPONENT_AVATAR_SIZE : RAIL_SIZE;
                const currentSize = SIDEBET_SIZE + (targetSize - SIDEBET_SIZE) * eased;
                sprite.width = currentSize;
                sprite.height = currentSize;

                // Rotate during flight (standardized - matches chip-to-dealer style)
                sprite.rotation = startRotation + (endRotation - startRotation) * eased;

                if (progress < 1) {
                  requestAnimationFrame(animatePhase2);
                } else {
                  // Sound: chipLand on sidebet win Phase 2 arrival (player rail or opponent avatar)
                  playTableSound('chipLand');
                  MODULE_CHIP_SPRITES_IN_FLIGHT.delete(sprite);
                  sprite.destroy();
                  phase2Completed++;

                  // Decrement sidebet total by this chip's value
                  // For $10k chips, use adjusted phase2TenKValue to account for capping
                  // Skip counter updates for opponents - they don't show chip counters
                  if (sidebetKey) {
                    const chipValue = denom === 10000 ? phase2TenKValue : denom;
                    const counterText = scene.sidebetCounters.get(sidebetKey);
                    const counterPill = scene.sidebetCounterPills.get(sidebetKey);
                    const currentTotal = Math.max(0, (scene.sidebetChipCounts.get(sidebetKey) || 0) - chipValue);
                    scene.sidebetChipCounts.set(sidebetKey, currentTotal);
                    if (counterText && !counterText.destroyed && counterPill && !counterPill.destroyed && !isOpponentSidebet) {
                      updateSidebetCounterLayout(counterPill, counterText, currentTotal, SIDEBET_ICON_SIZE);
                    }
                  }

                  // All Phase 2 chips arrived at rail?
                  if (phase2Completed === phase2Sprites.length) {
                    scene.activeSidebets.delete(sidebetType);

                    // NOTE: Don't clear overlay here - badge is now updated at START of animation
                    // and should persist until sidebet icon is removed during rake

                    console.log(`[Sidebet Anim] Win payout complete: ${phase2Sprites.length} chips ($${amount} total)`);
                    resolve();
                  }
                }
              };

              requestAnimationFrame(animatePhase2);
            }, index * STAGGER_DELAY);
          });
        }
      });
    },

    animateSidebetLoss: (seatPosition: number, sidebetType: string, amount: number, duration?: number, stagger?: number): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[Sidebet Anim] Loss collection: seat ${seatPosition}, ${sidebetType}, $${amount}, duration: ${duration ?? 'default'}, stagger: ${stagger ?? 'default'}`);

        const scene = sceneRef.current;
        // Determine if this is an opponent seat for config selection
        const isOpponent = seatPosition !== mySeatPosition;
        const effectiveSidebetConfig = isOpponent ? opponentSidebetConfigRef.current : sidebetConfigRef.current;

        // Debug: Log all sidebet sprites at this seat
        const storedTypes: { slot: number; type: string | undefined }[] = [];
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            storedTypes.push({ slot: i, type: storedType });
          }
        }
        console.log(`[Sidebet Anim] Looking for "${sidebetType}" at seat ${seatPosition}. Stored sprites:`, JSON.stringify(storedTypes));

        // Find the slot index for this sidebet type at this seat
        // First try exact match, then partial match for over-under-13 variants
        let slotIndex = -1;

        // Pass 1: Exact match
        for (let i = 0; i < 3; i++) {
          const key = `${seatPosition}-${i}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType === sidebetType) {
              slotIndex = i;
              console.log(`[Sidebet Anim] Exact match found at slot ${i}`);
              break;
            }
          }
        }

        // Pass 2: Partial match for over-under-13 variants
        if (slotIndex === -1) {
          for (let i = 0; i < 3; i++) {
            const key = `${seatPosition}-${i}`;
            const sprite = scene.sidebetSprites.get(key);
            if (sprite && !sprite.destroyed) {
              const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
              // Match if either starts with the other (handles over-under-13 vs over-under-13-over)
              if (storedType && (
                storedType.startsWith(sidebetType) ||
                sidebetType.startsWith(storedType)
              )) {
                slotIndex = i;
                console.log(`[Sidebet Anim] Partial match found at slot ${i}: stored="${storedType}" vs event="${sidebetType}"`);
                break;
              }
            }
          }
        }

        const sidebetKey = slotIndex >= 0 ? `${seatPosition}-${slotIndex}` : null;
        const sidebetSprite = sidebetKey ? scene.sidebetSprites.get(sidebetKey) : null;
        const isOpponentSidebet = sidebetSprite ? (sidebetSprite as unknown as { isOpponentSidebet?: boolean }).isOpponentSidebet ?? false : false;

        if (!sidebetSprite || !scene.animationLayer) {
          console.warn(`[Sidebet Anim] No sprite found for ${sidebetType} at seat ${seatPosition}. Check stored types above.`);
          resolve();
          return;
        }

        // Break down amount into chips, cap $10k at 100, sort DESCENDING (largest first for unstacking/leaving)
        const rawChips = breakdownChips(amount);
        const { chips: cappedChips, tenKValue } = capChipsForAnimation(rawChips);
        const sortedChips = [...cappedChips].sort((a, b) => b - a);

        if (sortedChips.length === 0) {
          resolve();
          return;
        }

        // Get sidebet icon position - convert global to animationLayer local coordinates
        const sidebetGlobalPos = sidebetSprite.getGlobalPosition();
        const sidebetLocalPos = scene.animationLayer.toLocal(sidebetGlobalPos);

        // Animation constants (matching main bet collection pattern)
        // Use server-provided stagger, or fallback to cached config (logs error if fallback used)
        const STAGGER_DELAY = getStaggerValue('sidebetChip', stagger, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateSidebetLoss',
          eventType: 'SIDEBET_CHIPS_COLLECTED',
        });
        // Use server-provided duration, or fallback to cached config (logs error if fallback used)
        const DURATION = getAnimationDuration('sidebetChipsCollected', duration, {
          fileName: 'PixiGameTableV1.tsx',
          functionName: 'animateSidebetLoss',
          eventType: 'SIDEBET_CHIPS_COLLECTED',
        });
        console.log(`[Sidebet Anim] Loss using duration: ${DURATION}ms, stagger: ${STAGGER_DELAY}ms (server: dur=${duration ?? 'none'}, stag=${stagger ?? 'none'})`);

        // Get actual sidebet icon size (may be scaled via DEV controls)
        const SOURCE_SIZE = effectiveSidebetConfig.iconSize;
        const TARGET_SIZE = CHIP_SIZES.dealerPile; // 20px - dealer tray chip size

        const chipSprites: Sprite[] = [];
        let completedCount = 0;

        // Create and animate each chip with stagger
        sortedChips.forEach((denom, index) => {
          const chipTexture = texturesRef.current.get(`chip-${denom}`);
          if (!chipTexture) {
            console.warn('[Sidebet Anim] No chip texture for', denom);
            return;
          }

          // Get ACTUAL tray chip position from rendered sprite (not layoutStore)
          // This ensures we animate to the exact visual location
          const trayChip = scene.trayChipSprites.get(denom);
          let trayLocalPos: { x: number; y: number };

          if (trayChip && scene.animationLayer) {
            // Use actual rendered position - convert global to animationLayer local
            const trayGlobalPos = trayChip.getGlobalPosition();
            trayLocalPos = scene.animationLayer.toLocal(trayGlobalPos);
          } else {
            // Fallback to layoutStore position
            const fallbackPos = getTrayChipPosition(denom);
            if (!fallbackPos) {
              console.warn('[Sidebet Anim] No tray position for', denom);
              return;
            }
            trayLocalPos = fallbackPos;
          }

          // Create chip sprite at sidebet icon position
          const chipSprite = new Sprite(chipTexture);
          chipSprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
          chipSprite.anchor.set(0.5);
          chipSprite.width = SOURCE_SIZE;
          chipSprite.height = SOURCE_SIZE;
          chipSprite.x = sidebetLocalPos.x;
          chipSprite.y = sidebetLocalPos.y;
          chipSprite.alpha = 0; // Start invisible, reveal on stagger
          // Random starting rotation for natural chip appearance
          chipSprite.rotation = Math.random() * Math.PI * 2;
          // Set zIndex so chips departing first (largest, index 0) are on TOP visually
          // This prevents the "dealing from bottom of stack" appearance
          chipSprite.zIndex = sortedChips.length - index;
          scene.animationLayer!.addChild(chipSprite);
          MODULE_CHIP_SPRITES_IN_FLIGHT.add(chipSprite);
          chipSprites.push(chipSprite);

          // Delayed start for stagger effect
          setTimeout(() => {
            chipSprite.alpha = 1;
            const startTime = performance.now();
            const startX = sidebetLocalPos.x;
            const startY = sidebetLocalPos.y;
            const endX = trayLocalPos.x;
            const endY = trayLocalPos.y;
            const startRotation = chipSprite.rotation;
            const endRotation = Math.random() * Math.PI * 2; // Random end rotation

            const animateFrame = () => {
              const elapsed = performance.now() - startTime;
              const progress = Math.min(elapsed / DURATION, 1);
              const eased = easeOutCubic(progress);

              // Linear interpolation to dealer tray (matches standard chip-to-dealer style)
              chipSprite.x = startX + (endX - startX) * eased;
              chipSprite.y = startY + (endY - startY) * eased;

              // Interpolate size using width/height (not scale.set)
              const currentSize = SOURCE_SIZE + (TARGET_SIZE - SOURCE_SIZE) * eased;
              chipSprite.width = currentSize;
              chipSprite.height = currentSize;

              // Rotate as it travels
              chipSprite.rotation = startRotation + (endRotation - startRotation) * eased;

              if (progress < 1) {
                requestAnimationFrame(animateFrame);
              } else {
                // Sound: chipLand on sidebet loss chip arrival at dealer tray
                playTableSound('chipLand');
                // Update persistent tray chip to match landing rotation
                if (trayChip) {
                  trayChip.rotation = endRotation;
                }

                // This chip finished - destroy it
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chipSprite);
                chipSprite.destroy();
                completedCount++;

                // Decrement sidebet total by this chip's value
                // For $10k chips, use adjusted tenKValue to account for capping
                // Skip counter updates for opponents - they don't show chip counters
                if (sidebetKey) {
                  const chipValue = denom === 10000 ? tenKValue : denom;
                  const counterText = scene.sidebetCounters.get(sidebetKey);
                  const counterPill = scene.sidebetCounterPills.get(sidebetKey);
                  const currentTotal = Math.max(0, (scene.sidebetChipCounts.get(sidebetKey) || 0) - chipValue);
                  scene.sidebetChipCounts.set(sidebetKey, currentTotal);
                  if (counterText && !counterText.destroyed && counterPill && !counterPill.destroyed && !isOpponentSidebet) {
                    updateSidebetCounterLayout(counterPill, counterText, currentTotal, SIDEBET_ICON_SIZE);
                  }
                }

                // All chips done?
                if (completedCount === chipSprites.length) {
                  // Clear from active sidebets tracking
                  scene.activeSidebets.delete(`${seatPosition}-${sidebetType}`);

                  // NOTE: Don't clear overlay here - badge is now updated at START of animation
                  // and should persist until sidebet icon is removed during rake

                  console.log(`[Sidebet Anim] Loss collection complete: ${chipSprites.length} chips to dealer tray`);
                  resolve();
                }
              }
            };

            requestAnimationFrame(animateFrame);
          }, index * STAGGER_DELAY);
        });

        // Safety: if no valid chips, resolve immediately
        if (chipSprites.length === 0) {
          resolve();
        }
      });
    },

    clearSidebetState: (sidebetType: string) => {
      const scene = sceneRef.current;

      // Clear active sidebet states for this type across all seats
      // Use partial matching for over-under-13 variants
      for (const key of Array.from(scene.activeSidebets.keys())) {
        const keyType = key.split('-').slice(1).join('-'); // Extract type from "seatPos-type"
        if (keyType === sidebetType ||
            keyType.startsWith(sidebetType) ||
            sidebetType.startsWith(keyType)) {
          scene.activeSidebets.delete(key);
        }
      }

      // Clear overlays for this sidebet type across all seats
      for (let seatPos = 0; seatPos <= 7; seatPos++) {
        for (let slotIdx = 0; slotIdx < 3; slotIdx++) {
          const key = `${seatPos}-${slotIdx}`;
          const sprite = scene.sidebetSprites.get(key);
          if (sprite) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType === sidebetType ||
                (storedType && (storedType.startsWith(sidebetType) || sidebetType.startsWith(storedType)))) {
              const overlay = scene.sidebetOverlays.get(key);
              if (overlay && !overlay.destroyed) {
                overlay.removeChildren();
                overlay.alpha = 0;
              }
            }
          }
        }
      }
    },

    /** Clear all active sidebet visuals - call when new round starts */
    clearAllSidebetVisuals: () => {
      console.log('[SIDEBET DEBUG] clearAllSidebetVisuals called');
      const scene = sceneRef.current;
      console.log('[SIDEBET DEBUG] sidebetOverlays count:', scene.sidebetOverlays.size);

      // Clear all active sidebets
      scene.activeSidebets.clear();

      // NOTE: Do NOT reset border rings here! Outcome borders (green/red) should persist
      // until START of cards rake +200ms. The border reset is handled in clearTable/rakeCardsForSeat.

      // Clear all overlays (badges only)
      for (const [, overlay] of scene.sidebetOverlays) {
        if (overlay && !overlay.destroyed) {
          overlay.removeChildren();
          overlay.alpha = 0;
        }
      }
    },

    /** Restore configured sidebet visuals - shows green indicators for active configs */
    restoreConfiguredSidebetVisuals: (configs: Map<string, { percentage: number; rounds: number }>) => {
      console.log('[SIDEBET DEBUG] restoreConfiguredSidebetVisuals called, configs:', Array.from(configs.entries()));

      // GUARD: Prevent restoring sidebet visuals when mySeatPosition prop is stale
      // If the store has mySeatPosition set but the prop is still null, the component
      // needs to re-render first so getVisualPositionForSeat has the correct closure.
      // This prevents icons from appearing at the wrong visual position.
      const storeMySeatPosition = useGameStateStore.getState().mySeatPosition;
      if (mySeatPosition === null && storeMySeatPosition !== null) {
        console.warn(`[SIDEBET DEBUG] SKIPPED restoreConfiguredSidebetVisuals: mySeatPosition prop is stale (null) but store has ${storeMySeatPosition}. Component needs to re-render first.`);
        return;
      }

      const scene = sceneRef.current;
      console.log('[SIDEBET DEBUG] sidebetOverlays keys:', Array.from(scene.sidebetOverlays.keys()));
      console.log('[SIDEBET DEBUG] sidebetSprites keys:', Array.from(scene.sidebetSprites.keys()));

      for (const [sidebetType, config] of configs) {
        // Find the sprite with matching sidebetType to get the correct overlay key
        // First try exact match, then partial match for over-under-13 variants
        let overlayKey: string | null = null;

        // Pass 1: Exact match
        for (const [key, sprite] of scene.sidebetSprites) {
          if (sprite && !sprite.destroyed) {
            const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
            if (storedType === sidebetType) {
              overlayKey = key;
              console.log(`[SIDEBET DEBUG] Exact match found for ${sidebetType} at key: ${key}`);
              break;
            }
          }
        }

        // Pass 2: Partial match for over-under-13 variants
        if (!overlayKey) {
          for (const [key, sprite] of scene.sidebetSprites) {
            if (sprite && !sprite.destroyed) {
              const storedType = (sprite as unknown as { sidebetType?: string }).sidebetType;
              if (storedType && (
                storedType.startsWith(sidebetType) ||
                sidebetType.startsWith(storedType)
              )) {
                overlayKey = key;
                console.log(`[SIDEBET DEBUG] Partial match found for ${sidebetType} at key: ${key} (stored: ${storedType})`);
                break;
              }
            }
          }
        }

        // If no existing icon, create one at the user's seat (same pattern as showSidebetConfigured)
        if (!overlayKey) {
          console.log(`[SIDEBET DEBUG] restoreConfiguredSidebetVisuals: No existing icon for ${sidebetType}, creating it`);

          const mySeatPosition = useGameStateStore.getState().mySeatPosition;
          if (mySeatPosition === null || mySeatPosition === undefined) {
            console.warn('[SIDEBET DEBUG] restoreConfiguredSidebetVisuals: No seat position, cannot create sidebet');
            continue;
          }

          const visualPos = getVisualPositionForSeat(mySeatPosition);
          const seatData = dynamicSeatPositions[visualPos - 1];
          if (!seatData || !scene.sidebetLayer) {
            console.warn('[SIDEBET DEBUG] restoreConfiguredSidebetVisuals: Missing seat data or sidebet layer');
            continue;
          }

          const textures = texturesRef.current;

          // Get or create sidebet container for this seat
          let seatContainer = scene.seatSidebetContainers.get(mySeatPosition);
          if (!seatContainer) {
            seatContainer = new Container();
            seatContainer.sortableChildren = true;
            const sbConfig = sidebetConfigRef.current;
            // Per-seat offset (additive on top of base config) - use VISUAL position for DEV CONTROLS alignment
            const perSeatOffset = sidebetIconConfigs?.[visualPos] || { radiusOffset: 0, x: 0, y: 0 };
            const baseLocalOffsetY = sbConfig.baseRadiusOffset + perSeatOffset.radiusOffset;
            const rotRad = degreesToRadians(seatData.rotation);
            const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);
            seatContainer.x = basePos.x + perSeatOffset.x;
            seatContainer.y = basePos.y + perSeatOffset.y;
            seatContainer.rotation = rotRad;
            seatContainer.eventMode = 'passive'; // Allow events to propagate to children
            scene.sidebetLayer.addChild(seatContainer);
            scene.seatSidebetContainers.set(mySeatPosition, seatContainer);
          }

          // Find next available slot
          let slotIndex = -1;
          for (let i = 0; i < 3; i++) {
            const key = `${mySeatPosition}-${i}`;
            if (!scene.sidebetSprites.has(key)) {
              slotIndex = i;
              break;
            }
          }
          if (slotIndex === -1) {
            console.warn('[SIDEBET DEBUG] restoreConfiguredSidebetVisuals: No available slot for sidebet icon');
            continue;
          }

          // Get texture for this sidebet type
          const textureIndex = SIDEBET_TYPE_TO_INDEX[sidebetType];
          const texture = textures.get(`sidebet-${textureIndex}`);
          if (!texture) {
            console.warn(`[SIDEBET DEBUG] restoreConfiguredSidebetVisuals: No texture for sidebet type ${sidebetType}`);
            continue;
          }

          // Create the icon
          const sbConfig = sidebetConfigRef.current;

          // Gold border ring behind the icon
          const borderRing = new Graphics();
          borderRing.circle(0, 0, sbConfig.iconSize / 2 + 3);
          borderRing.stroke({ color: 0xd4a54a, width: 3 });
          seatContainer.addChild(borderRing);

          const iconSprite = new Sprite(texture);
          iconSprite.anchor.set(0.5);
          iconSprite.width = sbConfig.iconSize;
          iconSprite.height = sbConfig.iconSize;
          (iconSprite as unknown as { sidebetType: string }).sidebetType = sidebetType;
          (iconSprite as unknown as { borderRing: Graphics }).borderRing = borderRing;

          // Pending vs active: check if chips have landed this round
          // chipsPlacedForCountdown = true means chips arrived (active), false = pending
          const storeConfig = useGameStateStore.getState().mySidebetConfigs.get(sidebetType);
          const isActive = storeConfig?.chipsPlacedForCountdown ?? false;
          iconSprite.alpha = isActive ? 1 : 0.5;
          borderRing.alpha = 1; // ring always solid — keeps the (dimmed) pending icon findable on the felt

          // Make clickable for current user's sidebet icons
          if (onSidebetIconClick) {
            iconSprite.eventMode = 'static';
            iconSprite.cursor = 'pointer';
            iconSprite.on('pointerdown', () => {
              console.log('[DEBUG SIDEBET CLICK] restoreConfiguredSidebetVisuals clicked:', { sidebetType, slotIndex, mySeatPosition });
              onSidebetIconClick(sidebetType);
            });
          }

          seatContainer.addChild(iconSprite);

          overlayKey = `${mySeatPosition}-${slotIndex}`;
          scene.sidebetSprites.set(overlayKey, iconSprite);

          // Create overlay for glow effects
          const newOverlay = new Container();
          newOverlay.alpha = 0;
          seatContainer.addChild(newOverlay);
          scene.sidebetOverlays.set(overlayKey, newOverlay);

          // Create counter pill and text (hidden for configured state)
          const counterPill = new Graphics();
          counterPill.zIndex = 50;
          seatContainer.addChild(counterPill);
          scene.sidebetCounterPills.set(overlayKey, counterPill);

          const counterStyle = new TextStyle({
            fontFamily: 'Arial, sans-serif',
            fontSize: SIDEBET_COUNTER_CONFIG.fontSize,
            fontWeight: 'bold',
            fill: SIDEBET_COUNTER_CONFIG.fontColor,
          });
          const counterText = new Text({ text: '', style: counterStyle });
          counterText.anchor.set(0.5, 0.5);
          counterText.zIndex = 51;
          counterText.visible = false;
          seatContainer.addChild(counterText);
          scene.sidebetCounters.set(overlayKey, counterText);
          scene.sidebetChipCounts.set(overlayKey, 0);

          // Reposition all icons in arch
          const posConfig = sidebetConfigRef.current;
          const activeSlots: { sprite: Sprite; overlay: Container | null; counter: Text | null; counterPill: Graphics | null; borderRing: Graphics | null }[] = [];
          for (let i = 0; i < 3; i++) {
            const k = `${mySeatPosition}-${i}`;
            const s = scene.sidebetSprites.get(k);
            if (s && !s.destroyed) {
              activeSlots.push({
                sprite: s,
                overlay: scene.sidebetOverlays.get(k) || null,
                counter: scene.sidebetCounters.get(k) || null,
                counterPill: scene.sidebetCounterPills.get(k) || null,
                borderRing: (s as unknown as { borderRing?: Graphics }).borderRing || null,
              });
            }
          }
          const posIndices = posConfig.getPositionsForCount(activeSlots.length);
          activeSlots.forEach((slot, idx) => {
            const posIdx = posIndices[idx];
            const pos = posConfig.slotPositions[posIdx];
            slot.sprite.x = pos.localX;
            slot.sprite.y = pos.localY;
            if (slot.borderRing) {
              slot.borderRing.x = pos.localX;
              slot.borderRing.y = pos.localY;
            }
            if (slot.overlay) {
              slot.overlay.x = pos.localX;
              slot.overlay.y = pos.localY;
            }
            if (slot.counter) {
              slot.counter.x = pos.localX;
              slot.counter.y = pos.localY + posConfig.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
            }
            if (slot.counterPill) {
              slot.counterPill.x = pos.localX;
              slot.counterPill.y = pos.localY + posConfig.iconSize / 2 + SIDEBET_COUNTER_CONFIG.offsetY;
            }
          });

          console.log(`[SIDEBET DEBUG] restoreConfiguredSidebetVisuals: Created icon at ${overlayKey}`);
        }

        const overlay = scene.sidebetOverlays.get(overlayKey);
        console.log(`[SIDEBET DEBUG] Looking for overlay with key "${overlayKey}":`, overlay ? 'found' : 'NOT FOUND');

        if (overlay && !overlay.destroyed) {
          console.log(`[SIDEBET DEBUG] Restoring badge for ${sidebetType}, rounds: ${config.rounds}`);
          overlay.removeChildren();

          // Badge showing rounds remaining (no ring - icon on table = active)
          const badgeSize = 18;
          const badgeX = SIDEBET_ICON_SIZE / 2 - 4;
          const badgeY = SIDEBET_ICON_SIZE / 2 - 4;

          const badgeBg = new Graphics();
          badgeBg.circle(badgeX, badgeY, badgeSize / 2);
          badgeBg.fill({ color: 0x22c55e }); // Green
          badgeBg.stroke({ color: 0xffffff, width: 1.5 });
          overlay.addChild(badgeBg);

          // SERVER-AUTHORITATIVE COUNTDOWN: Read rounds from sidebetServerRounds (survives across rounds),
          // fallback to config.rounds only on first render before SIDEBET_PLACED arrives
          const mySeat = useGameStateStore.getState().mySeatPosition;
          const serverRounds = mySeat !== null
            ? scene.sidebetServerRounds.get(`${mySeat}-${sidebetType}`)
            : undefined;
          const displayRounds = serverRounds !== undefined ? serverRounds : config.rounds;

          const badgeText = new Text({
            text: displayRounds === -1 ? '∞' : String(displayRounds),
            style: {
              fontFamily: 'Arial',
              fontSize: displayRounds === -1 ? 14 : 11,
              fontWeight: 'bold',
              fill: 0xffffff
            }
          });
          badgeText.anchor.set(0.5);
          badgeText.x = badgeX;
          badgeText.y = badgeY + (displayRounds === -1 ? 1 : 0);
          overlay.addChild(badgeText);

          overlay.alpha = 1;
        }
      }
    },

    // ===========================================
    // BUDDY HUD METHODS
    // ===========================================

    /**
     * Show or hide the BUDDY HUD (DP and TC indicators).
     * Should be called based on BUDDY subscription status.
     */
    setBuddyHUDVisible: (visible: boolean) => {
      const scene = sceneRef.current;
      console.log('[BUDDY HUD] setBuddyHUDVisible called with:', visible, 'container exists:', !!scene.buddyHUD.container);
      if (scene.buddyHUD.container && !scene.buddyHUD.container.destroyed) {
        scene.buddyHUD.container.visible = visible;
        scene.buddyHUD.visible = visible;
        console.log('[BUDDY HUD] Visibility set to:', visible);
      }
    },

    /**
     * Update the BUDDY HUD with current penetration and true count values.
     * @param penetration - Deck penetration percentage (0-100)
     * @param trueCount - Current true count value
     */
    updateBuddyHUD: (penetration: number, trueCount: number) => {
      const scene = sceneRef.current;
      const hud = scene.buddyHUD;

      // Only update if HUD is visible and elements exist
      if (!hud.visible || !hud.dpBarFill || !hud.dpValue || !hud.tcIndicator || !hud.tcValue) {
        return;
      }

      if (hud.dpBarFill.destroyed || hud.dpValue.destroyed || hud.tcIndicator.destroyed || hud.tcValue.destroyed) {
        return;
      }

      updateBuddyHUDValues(hud.dpBarFill, hud.dpValue, hud.tcIndicator, hud.tcValue, penetration, trueCount);
    },

    // ==========================================
    // INSURANCE PANEL METHODS
    // ==========================================

    /**
     * Show the insurance panel with cost and optional deadline.
     * @param cost - Insurance cost amount
     * @param deadline - Countdown deadline timestamp (null for no countdown)
     */
    showInsurancePanel: (cost: number, deadline: number | null) => {
      const scene = sceneRef.current;
      if (scene.insurancePanel) {
        console.log('[PixiGameTableV1] showInsurancePanel called, cost:', cost, 'deadline:', deadline);
        scene.insurancePanel.show(cost, deadline);
      }
    },

    /**
     * Hide the insurance panel.
     */
    hideInsurancePanel: () => {
      const scene = sceneRef.current;
      if (scene.insurancePanel) {
        console.log('[PixiGameTableV1] hideInsurancePanel called');
        scene.insurancePanel.hide();
      }
    },

    /**
     * Show the How To Bet tutorial modal.
     */
    showHowToBetModal: () => {
      const scene = sceneRef.current;
      if (scene.howToBetModal) {
        console.log('[PixiGameTableV1] showHowToBetModal called');
        scene.howToBetModal.show();
      }
    },

    /**
     * Hide the How To Bet tutorial modal.
     */
    hideHowToBetModal: () => {
      const scene = sceneRef.current;
      if (scene.howToBetModal) {
        console.log('[PixiGameTableV1] hideHowToBetModal called');
        scene.howToBetModal.hide();
      }
    },

    /**
     * Set the insurance recommendation highlighting.
     * @param rec - 'ACCEPT' to highlight YES, 'DECLINE' to highlight NO, null for no highlight
     */
    setInsuranceRecommendation: (rec: 'ACCEPT' | 'DECLINE' | null) => {
      const scene = sceneRef.current;
      if (scene.insurancePanel) {
        scene.insurancePanel.setRecommendation(rec);
      }
    },

    /**
     * Set whether player can afford insurance.
     * @param canAfford - True if player has enough balance for insurance
     */
    setInsuranceCanAfford: (canAfford: boolean) => {
      const scene = sceneRef.current;
      if (scene.insurancePanel) {
        scene.insurancePanel.setCanAfford(canAfford);
      }
    },

    // ==========================================
    // INSURANCE BADGE METHODS
    // ==========================================

    /**
     * Show insurance pending badge at a seat.
     * @param seatPosition - Seat position (1-7)
     */
    showInsuranceBadge: (seatPosition: number) => {
      console.log('[showInsuranceBadge] Called for seat:', seatPosition);
      const scene = sceneRef.current;
      const textures = texturesRef.current;
      const insuranceTexture = textures?.get('insuranceBadge');

      if (!insuranceTexture || !scene.badgeLayer || scene.badgeLayer.destroyed) {
        console.log('[showInsuranceBadge] Early return:', { hasTexture: !!insuranceTexture, hasBadgeLayer: !!scene.badgeLayer, destroyed: scene.badgeLayer?.destroyed });
        return;
      }

      // Get seat position data first - return early if not found
      const visualPos = getVisualPositionForSeat(seatPosition);
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) {
        console.warn('[showInsuranceBadge] No seat data for position:', seatPosition, 'visualPos:', visualPos);
        return;
      }

      // Check if badge already exists and is still valid (not destroyed)
      let badge = scene.insuranceBadges.get(seatPosition);

      if (!badge || badge.destroyed) {
        // Create new badge sprite (or recreate if destroyed)
        badge = new Sprite(insuranceTexture);
        badge.anchor.set(0.5);
        badge.width = 40; // Badge size
        badge.height = 40;
        badge.eventMode = 'none';
        scene.badgeLayer.addChild(badge);
        scene.insuranceBadges.set(seatPosition, badge);
      }

      // Position badge at upper-left corner of seat (opposite from avatar which is lower-right)
      // Seat base size is 144x168, scaled by seatSizeScale and per-seat scale
      const seatConfig = seatConfigs[visualPos] || { scale: 100 };
      const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
      const seatWidth = 144 * seatScale;
      const seatHeight = 168 * seatScale;

      // Local offset: upper-left corner - badge center at corner so half is inside, half outside
      const localOffsetX = -(seatWidth / 2); // Left edge
      const localOffsetY = -(seatHeight / 2); // Top edge

      // Transform local offset to world coordinates based on seat rotation
      const rotRad = degreesToRadians(seatData.rotation);
      const worldOffsetX = localOffsetX * Math.cos(rotRad) - localOffsetY * Math.sin(rotRad);
      const worldOffsetY = localOffsetX * Math.sin(rotRad) + localOffsetY * Math.cos(rotRad);

      badge.x = seatData.x + worldOffsetX;
      badge.y = seatData.y + worldOffsetY;

      badge.visible = true;
      badge.alpha = 1;
      console.log('[showInsuranceBadge] Badge positioned at:', { x: badge.x, y: badge.y, seatPos: seatData, localOffset: { x: localOffsetX, y: localOffsetY } });
    },

    /**
     * Hide insurance pending badge at a seat.
     * @param seatPosition - Seat position (1-7)
     */
    hideInsuranceBadge: (seatPosition: number) => {
      const scene = sceneRef.current;
      const badge = scene.insuranceBadges.get(seatPosition);
      if (badge && !badge.destroyed) {
        badge.visible = false;
      }
    },

    /**
     * Hide all insurance badges.
     */
    hideAllInsuranceBadges: () => {
      const scene = sceneRef.current;
      scene.insuranceBadges.forEach((badge) => {
        if (!badge.destroyed) {
          badge.visible = false;
        }
      });
    },

    // ==========================================
    // AVATAR COUNTDOWN RING METHODS
    // ==========================================

    /**
     * Show countdown ring around avatar at a seat.
     * @param seatPosition - Seat position (1-7)
     * @param deadline - Unix timestamp when countdown expires
     * @param totalDuration - Total duration in ms (for calculating progress)
     */
    showCountdownRing: (seatPosition: number, deadline: number, totalDuration: number = 10000) => {
      const scene = sceneRef.current;
      if (!scene.countdownRingLayer) return;

      const visualPos = getVisualPositionForSeat(seatPosition);
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) {
        console.warn('[showCountdownRing] No seat data for position:', seatPosition, 'visualPos:', visualPos);
        return;
      }

      // Calculate seat dimensions for avatar positioning
      const seatConfig = seatConfigs[visualPos] || { scale: 100 };
      const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
      const seatWidth = 144 * seatScale;
      const seatHeight = 168 * seatScale;

      // Get avatar position using registry function
      const avatarPos = calculateAvatarPositionRaw(seatData.x, seatData.y, seatWidth, seatHeight, seatData.rotation);

      // Determine avatar size (current user vs opponent)
      const isCurrentUser = mySeatPosition === seatPosition;
      const avatarSize = isCurrentUser ? 48 : 40;
      const avatarRadius = avatarSize / 2;

      // Get or create countdown ring
      let ring = scene.countdownRings.get(seatPosition);
      if (!ring || ring.destroyed) {
        ring = new PixiAvatarCountdownRing(avatarRadius);
        scene.countdownRingLayer.addChild(ring);
        scene.countdownRings.set(seatPosition, ring);
      }

      // Update ring radius in case seat changed (current user vs opponent)
      ring.setAvatarRadius(avatarRadius);

      // Position ring at avatar center
      ring.x = avatarPos.x;
      ring.y = avatarPos.y;

      // Start countdown
      ring.start(deadline, totalDuration);
      console.log('[showCountdownRing] Started countdown for seat', seatPosition, 'deadline:', deadline);
    },

    /**
     * Hide countdown ring at a seat.
     * @param seatPosition - Seat position (1-7)
     */
    hideCountdownRing: (seatPosition: number) => {
      const scene = sceneRef.current;
      const ring = scene.countdownRings.get(seatPosition);
      if (ring && !ring.destroyed) {
        ring.stop();
      }
    },

    /**
     * Hide all countdown rings.
     */
    hideAllCountdownRings: () => {
      const scene = sceneRef.current;
      scene.countdownRings.forEach((ring) => {
        if (!ring.destroyed) {
          ring.stop();
        }
      });
    },

    // ==========================================
    // INSURANCE CHIP ANIMATION METHODS
    // ==========================================

    /**
     * Animate chip from rail to insurance kitty (accepting insurance).
     * @param seatPosition - Seat position (1-7)
     * @param denomination - Chip denomination
     */
    animateRailToInsuranceKitty: async (seatPosition: number, denomination: number): Promise<void> => {
      const scene = sceneRef.current;
      const textures = texturesRef.current;

      const chipTexture = textures?.get(`chip-${denomination}`);
      if (!chipTexture || !scene.uiLayer || !textures) return;

      const visualPos = getVisualPositionForSeat(seatPosition);
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) return;

      // Get rail position from actual rail chip sprite (like animateRailToKitty)
      const railChip = scene.chipRailChips.get(denomination);
      let railPos: { x: number; y: number };

      if (railChip && scene.uiLayer) {
        // Get global position and convert to uiLayer local coordinates
        const globalPos = railChip.getGlobalPosition();
        const localPos = scene.uiLayer.toLocal(globalPos);
        railPos = { x: localPos.x, y: localPos.y };
      } else {
        // Fallback: calculate position if rail chip not found
        const railCalcScale = (chipRailConfig?.scale ?? 100) / 100;
        const railCalcX = UI_CHIP_RAIL_POS.x + (chipRailConfig?.x ?? 0);
        const railCalcY = UI_CHIP_RAIL_POS.y + (chipRailConfig?.y ?? 0);
        const railSpacing = chipRailConfig?.spacing ?? 85;
        const railTotalWidth = (CHIP_RAIL_DENOMS.length - 1) * railSpacing;
        const railStartX = -railTotalWidth / 2;
        const railArcHeight = 25;

        const denomIndex = CHIP_RAIL_DENOMS.indexOf(denomination);
        const railLocalX = railStartX + (denomIndex === -1 ? 0 : denomIndex * railSpacing);
        const railNormalizedPos = denomIndex === -1 ? 0 : (denomIndex - (CHIP_RAIL_DENOMS.length - 1) / 2) / ((CHIP_RAIL_DENOMS.length - 1) / 2);
        const railLocalY = railArcHeight * (railNormalizedPos * railNormalizedPos - 1);
        // Apply per-chip X/Y offsets
        const xOffset = chipXOffsets[denomination] ?? 0;
        const yOffset = chipYOffsets[denomination] ?? 0;
        railPos = {
          x: railCalcX + (railLocalX + xOffset) * railCalcScale,
          y: railCalcY + (railLocalY + yOffset) * railCalcScale,
        };
      }

      // Calculate insurance kitty position using insuranceChipConfigs (from DEV CONTROLS)
      // Insurance should be at the SAME radial level as main bet, just offset tangentially (to the right)
      // Use visualPos for config lookup (DEV CONTROLS alignment)
      const insConfig = insuranceChipConfigs[visualPos] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
      const rotRad = degreesToRadians(seatData.rotation);

      // Use same radial offset as main bet (KITTY_BASE_RADIAL_OFFSET), plus any radiusOffset from config
      const radialOffset = KITTY_BASE_RADIAL_OFFSET + insConfig.radiusOffset + (kittyConfig?.y ?? 0);

      // Base position at main bet's radial level (THE CENTERLINE)
      // Note: insConfig.x is used as padding for centerline calculation, NOT as direct offset
      // insConfig.y provides additional radial adjustment if needed
      const basePos = applyRadialOffset(seatData.x, seatData.y, radialOffset, rotRad);
      const baseX = basePos.x + (kittyConfig?.x ?? 0);
      const baseY = basePos.y;

      // Insurance kitty uses same Y (radial distance) as main kitty
      // Only tangent offset is applied later to shift horizontally
      const baseTargetX = baseX;
      const baseTargetY = baseY;

      // Get or create insurance kitty container for this seat
      let kittyContainer = scene.insuranceKittyContainers.get(seatPosition);
      let kittyCounterText = scene.insuranceKittyCounterTexts.get(seatPosition);
      let kittyPill = scene.insuranceKittyPills.get(seatPosition);
      let kittySprites = scene.insuranceKittyChipSprites.get(seatPosition);
      let kittyData = scene.insuranceKittyData.get(seatPosition);

      // Get main kitty data for centering calculation
      const mainKittyContainer = scene.kittyContainers.get(visualPos);
      const mainKittyData = scene.kittyData.get(visualPos);
      const mainKittyBasePos = scene.kittyBasePositions.get(visualPos);
      const mainBetAmount = mainKittyData?.total ?? denomination; // Estimate if no main kitty yet

      if (!kittyContainer) {
        // Create new insurance kitty display with counter
        const kittyDisplay = createKittyDisplay(textures, chipCounterConfig);
        kittyContainer = kittyDisplay.container;

        // Use insConfig.x as the padding between pill edge and centerline
        const padding = KITTY_PAIR_GAP;

        // Calculate centering offset for insurance (shifts right based on its OWN pill width)
        const insCenterOffset = calculateKittyCenteringOffset(denomination, padding, false, true);
        const adjustedInsPos = applyTangentOffset({ x: baseTargetX, y: baseTargetY }, insCenterOffset, rotRad);

        kittyContainer.x = adjustedInsPos.x;
        kittyContainer.y = adjustedInsPos.y;
        kittyContainer.rotation = rotRad;
        kittyContainer.zIndex = 1500; // Above seats

        // Mark as kitty element so clearTable doesn't rake it
        // Store seatPosition for config lookup (insurance kitties are already keyed by seat)
        (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).isKittyElement = true;
        (kittyContainer as unknown as { isKittyElement: boolean; seatPosition: number }).seatPosition = seatPosition;
        // Mark as insurance kitty for easy identification
        (kittyContainer as unknown as { isInsuranceKitty: boolean }).isInsuranceKitty = true;

        // Store references to chipContainer for adding chips
        (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).borderContainer = kittyDisplay.borderContainer;
        (kittyContainer as unknown as { borderContainer: Container; chipContainer: Container }).chipContainer = kittyDisplay.chipContainer;

        scene.animationLayer?.addChild(kittyContainer);
        scene.insuranceKittyContainers.set(seatPosition, kittyContainer);

        kittyData = { denominations: [], total: 0 };
        scene.insuranceKittyData.set(seatPosition, kittyData);

        kittySprites = [];
        scene.insuranceKittyChipSprites.set(seatPosition, kittySprites);

        kittyCounterText = kittyDisplay.counterText;
        scene.insuranceKittyCounterTexts.set(seatPosition, kittyCounterText);

        kittyPill = kittyDisplay.pill;
        scene.insuranceKittyPills.set(seatPosition, kittyPill);

        // Shift existing main kitty to center the pair (smooth animation)
        console.log('[animateRailToInsuranceKitty] Checking main kitty shift:', {
          hasMainContainer: !!mainKittyContainer,
          hasBasePos: !!mainKittyBasePos,
          destroyed: mainKittyContainer?.destroyed,
        });
        if (mainKittyContainer && mainKittyBasePos && !mainKittyContainer.destroyed) {
          const mainCenterOffset = calculateKittyCenteringOffset(mainBetAmount, padding, true, true);
          console.log('[animateRailToInsuranceKitty] Main kitty shift:', {
            mainBetAmount,
            padding,
            mainCenterOffset,
            basePos: mainKittyBasePos,
          });
          const newMainPos = applyTangentOffset(
            { x: mainKittyBasePos.x, y: mainKittyBasePos.y },
            mainCenterOffset,
            mainKittyBasePos.rotRad
          );
          console.log('[animateRailToInsuranceKitty] newMainPos:', newMainPos);

          // Animate main kitty to new position
          const startX = mainKittyContainer.x;
          const startY = mainKittyContainer.y;
          const duration = 200;
          const startTime = performance.now();

          const animateMainKitty = (currentTime: number) => {
            if (mainKittyContainer.destroyed) return;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(progress);

            mainKittyContainer.x = startX + (newMainPos.x - startX) * eased;
            mainKittyContainer.y = startY + (newMainPos.y - startY) * eased;

            if (progress < 1) {
              requestAnimationFrame(animateMainKitty);
            }
          };
          requestAnimationFrame(animateMainKitty);
        }
      }

      // Animation target is the kitty container's position (with centering offset already applied)
      const targetX = kittyContainer!.x;
      const targetY = kittyContainer!.y;

      // Create animation chip sprite at rail position
      const animChip = new Sprite(chipTexture);
      animChip.anchor.set(0.5);
      // Start at rail size (chips depart rail at rail chip size)
      animChip.width = CHIP_SIZES.rail;
      animChip.height = CHIP_SIZES.rail;
      animChip.x = railPos.x;
      animChip.y = railPos.y;
      animChip.zIndex = 2000; // Above rail chips so departing chip appears on top
      animChip.label = 'chipSprite'; // Mark as chip for reconciliation skip
      scene.uiLayer.addChild(animChip);
      MODULE_CHIP_SPRITES_IN_FLIGHT.add(animChip); // Track for cleanup during resync

      // Animation sizes: rail (48px) → kittyMedium (28px)
      const startSize = CHIP_SIZES.rail;
      const endSize = CHIP_SIZES.kittyMedium;

      // Animate to insurance kitty
      return new Promise<void>((resolve) => {
        const duration = 250; // Match standard chip animation speed
        const startTime = performance.now();
        const startX = animChip.x;
        const startY = animChip.y;

        // GUARANTEED RESOLUTION: Track if we've already resolved
        let resolved = false;

        // GUARANTEED RESOLUTION: Safety timeout
        const safetyTimeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            recordAnimationFailure({
              fileName: 'PixiGameTableV1.tsx',
              functionName: 'animateRailToInsuranceKitty',
              reason: 'timeout',
              animationType: 'chip-rail-to-insurance',
              targetDescription: `seat=${seatPosition} denom=${denomination}`,
              expectedDuration: duration,
            });
            if (animChip && !animChip.destroyed) {
              MODULE_CHIP_SPRITES_IN_FLIGHT.delete(animChip);
              scene.uiLayer?.removeChild(animChip);
              animChip.destroy();
            }
            resolve();
          }
        }, duration + 500);

        const animate = (currentTime: number) => {
          if (resolved) return;

          if (animChip.destroyed) {
            resolved = true;
            clearTimeout(safetyTimeout);
            resolve();
            return;
          }
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutCubic(progress);

          animChip.x = startX + (targetX - startX) * eased;
          animChip.y = startY + (targetY - startY) * eased;

          // Interpolate size from rail to kitty
          const currentSize = startSize + (endSize - startSize) * eased;
          animChip.width = currentSize;
          animChip.height = currentSize;

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // GUARANTEED RESOLUTION: Mark resolved and clear timeout
            if (resolved) return;
            resolved = true;
            clearTimeout(safetyTimeout);

            try {
              // Sound: insurance chip arrives in the insurance kitty
              playTableSound('chipLand');
              // Remove animation chip from tracking and scene
              MODULE_CHIP_SPRITES_IN_FLIGHT.delete(animChip);
              scene.uiLayer?.removeChild(animChip);
              animChip.destroy();

              // Create persistent chip in kitty container (only if scene is still valid)
              if (kittyContainer && !kittyContainer.destroyed) {
                const chipContainerRef = (kittyContainer as unknown as { chipContainer: Container }).chipContainer;
                const kittyChip = new Sprite(chipTexture);
                kittyChip.anchor.set(0.5);
                kittyChip.width = CHIP_SIZES.kittyMedium;
                kittyChip.height = CHIP_SIZES.kittyMedium;
                // Position relative to container origin (chips stack upward)
                kittyChip.x = 0;
                kittyChip.y = -(kittySprites?.length ?? 0) * 4; // Stack offset
                kittyChip.rotation = Math.random() * 2 * Math.PI; // Random rotation
                kittyChip.zIndex = 10 + (kittySprites?.length ?? 0); // Stack order
                chipContainerRef.addChild(kittyChip);
                kittySprites?.push(kittyChip);
              }

              // Update kitty data
              if (kittyData) {
                kittyData.denominations.push(denomination);
                kittyData.total += denomination;

                // Update counter display
                if (kittyPill && kittyCounterText) {
                  const counterOffset = { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 };
                  updateKittyLayout(kittyPill, kittyCounterText, kittySprites?.length ?? 0, kittyData.total, counterOffset);
                }
              }
            } catch (err) {
              recordAnimationFailure({
                fileName: 'PixiGameTableV1.tsx',
                functionName: 'animateRailToInsuranceKitty.completion',
                reason: 'error',
                errorMessage: err instanceof Error ? err.message : String(err),
                animationType: 'chip-rail-to-insurance',
                targetDescription: `seat=${seatPosition} denom=${denomination}`,
                expectedDuration: duration,
              });
            }
            resolve();
          }
        };

        requestAnimationFrame(animate);
      });
    },

    /**
     * Animate insurance win: payout from tray to insurance kitty, then all chips to rail.
     * @param seatPosition - Seat position (1-7)
     * @param insuranceBet - Original insurance bet amount
     * @param payoutAmount - Payout amount (2:1 = insuranceBet * 2)
     */
    animateInsuranceWin: async (seatPosition: number, insuranceBet: number, payoutAmount: number): Promise<void> => {
      const scene = sceneRef.current;
      const textures = texturesRef.current;

      // Get insurance kitty container and data
      const kittyContainer = scene.insuranceKittyContainers.get(seatPosition);
      let kittySprites = scene.insuranceKittyChipSprites.get(seatPosition) || [];
      let kittyData = scene.insuranceKittyData.get(seatPosition);
      const kittyCounterText = scene.insuranceKittyCounterTexts.get(seatPosition);
      const kittyPill = scene.insuranceKittyPills.get(seatPosition);

      // STEP 1: Animate payout chips from tray to insurance kitty
      if (payoutAmount > 0 && kittyContainer && !kittyContainer.destroyed && textures) {
        console.log('[animateInsuranceWin] Step 1: Animating payout from tray to insurance kitty:', payoutAmount);

        const payoutChips = breakdownChips(payoutAmount);
        const STAGGER_DELAY = 80;
        const duration = 300;

        // Get tray position
        const traySprite = scene.traySprite;
        let trayX = 650, trayY = 200; // Fallback
        if (traySprite && scene.animationLayer) {
          const trayGlobal = traySprite.getGlobalPosition();
          const trayLocal = scene.animationLayer.toLocal(trayGlobal);
          trayX = trayLocal.x;
          trayY = trayLocal.y;
        }

        // Get insurance kitty position
        const kittyGlobal = kittyContainer.getGlobalPosition();
        const kittyLocal = scene.animationLayer ? scene.animationLayer.toLocal(kittyGlobal) : kittyGlobal;

        const payoutAnimations: Promise<void>[] = [];

        for (let i = 0; i < payoutChips.length; i++) {
          const denom = payoutChips[i];
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) continue;

          const animPromise = new Promise<void>((resolve) => {
            setTimeout(() => {
              const chip = new Sprite(chipTexture);
              chip.label = 'chipSprite'; // Mark as chip for reconciliation skip
              chip.anchor.set(0.5);
              chip.width = CHIP_SIZES.dealerPile;
              chip.height = CHIP_SIZES.dealerPile;
              chip.x = trayX;
              chip.y = trayY;
              chip.zIndex = 2500 + i;
              scene.animationLayer?.addChild(chip);
              MODULE_CHIP_SPRITES_IN_FLIGHT.add(chip);

              const startTime = performance.now();
              const startX = chip.x;
              const startY = chip.y;
              const targetX = kittyLocal.x;
              const targetY = kittyLocal.y - (kittySprites.length + i) * 3; // Stack offset

              const animate = (currentTime: number) => {
                if (chip.destroyed) { resolve(); return; }
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeOutCubic(progress);

                chip.x = startX + (targetX - startX) * eased;
                chip.y = startY + (targetY - startY) * eased;
                const size = CHIP_SIZES.dealerPile + (CHIP_SIZES.kittyMedium - CHIP_SIZES.dealerPile) * eased;
                chip.width = size;
                chip.height = size;

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  // Sound: chipLand on insurance win Step 1 arrival at insurance kitty
                  playTableSound('chipLand');
                  // Add to kitty sprites and data
                  kittySprites.push(chip);
                  if (kittyData) {
                    kittyData.denominations.push(denom);
                    kittyData.total += denom;
                    // Update counter immediately as each chip lands
                    if (kittyCounterText) {
                      kittyCounterText.text = `$${kittyData.total.toLocaleString()}`;
                    }
                  }
                  // Move to kitty container
                  MODULE_CHIP_SPRITES_IN_FLIGHT.delete(chip);
                  scene.animationLayer?.removeChild(chip);
                  const chipContainer = (kittyContainer as unknown as { chipContainer: Container }).chipContainer;
                  if (chipContainer) {
                    chip.x = 0;
                    chip.y = -kittySprites.indexOf(chip) * 3;
                    chipContainer.addChild(chip);
                  }
                  resolve();
                }
              };
              requestAnimationFrame(animate);
            }, i * STAGGER_DELAY);
          });
          payoutAnimations.push(animPromise);
        }

        await Promise.all(payoutAnimations);

        // Small delay to show the payout landed
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Re-get kitty sprites after adding payout chips
      kittySprites = scene.insuranceKittyChipSprites.get(seatPosition) || [];
      kittyData = scene.insuranceKittyData.get(seatPosition);

      // STEP 2: Animate all chips from insurance kitty to rail
      if (kittySprites.length === 0) {
        // Clean up all maps
        if (kittyContainer && !kittyContainer.destroyed) {
          scene.animationLayer?.removeChild(kittyContainer);
          kittyContainer.destroy();
        }
        scene.insuranceKittyContainers.delete(seatPosition);
        scene.insuranceKittyChipSprites.delete(seatPosition);
        scene.insuranceKittyData.delete(seatPosition);
        scene.insuranceKittyCounterTexts.delete(seatPosition);
        scene.insuranceKittyPills.delete(seatPosition);
        return;
      }

      // Animation constants (match standard chip departures)
      const STAGGER_DELAY = 80; // 80ms between chips (standardized stagger)
      const duration = 300; // Match animateChipFromKitty duration

      // Size interpolation: kittyMedium (28px) → rail (48px)
      const startSize = CHIP_SIZES.kittyMedium;
      const endSize = CHIP_SIZES.rail;

      // Get denominations for each chip (to find correct rail position)
      const denominations = kittyData?.denominations || [];

      // Move all chips from container to animationLayer for animation
      // (need to convert from local container coords to world coords)
      const chipContainerRef = kittyContainer ?
        (kittyContainer as unknown as { chipContainer: Container }).chipContainer : null;

      // Get counter text for updating as chips leave
      const counterTextForStep2 = scene.insuranceKittyCounterTexts.get(seatPosition);
      let runningTotal = kittyData?.total || 0;

      // Animate all chips to rail with stagger
      // FIX: Iterate in REVERSE order (top of stack first, like other chip animations)
      const animations: Promise<void>[] = [];
      const reversedIndices = [...kittySprites.keys()].reverse(); // [n-1, n-2, ..., 1, 0]
      reversedIndices.forEach((originalIndex, staggerIndex) => {
        const sprite = kittySprites[originalIndex];
        if (!sprite || sprite.destroyed) return;

        // Get denomination for this chip (use original index for data lookup)
        const denomination = denominations[originalIndex] || 1; // Fallback to $1

        // Get chip's world position before removing from container
        const globalPos = sprite.getGlobalPosition();
        const animLayerPos = scene.animationLayer ? scene.animationLayer.toLocal(globalPos) : globalPos;

        // Remove from chipContainer, add to animationLayer
        chipContainerRef?.removeChild(sprite);
        sprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
        sprite.x = animLayerPos.x;
        sprite.y = animLayerPos.y;
        sprite.rotation = 0; // Reset rotation for animation
        // First chip (top of stack) gets highest zIndex, subsequent get decreasing
        sprite.zIndex = 2000 - staggerIndex;
        scene.animationLayer?.addChild(sprite);
        MODULE_CHIP_SPRITES_IN_FLIGHT.add(sprite);

        // Get rail position from actual rail chip sprite
        const railChip = scene.chipRailChips.get(denomination);
        let targetX: number, targetY: number;

        if (railChip && scene.animationLayer) {
          const railGlobalPos = railChip.getGlobalPosition();
          const railLocalPos = scene.animationLayer.toLocal(railGlobalPos);
          targetX = railLocalPos.x;
          targetY = railLocalPos.y;
        } else {
          // Fallback: use getRailChipPositionRef
          const railPos = getRailChipPositionRef.current?.(denomination);
          targetX = railPos?.x ?? 0;
          targetY = railPos?.y ?? 0;
        }

        const startX = sprite.x;
        const startY = sprite.y;
        const startSpriteSize = sprite.width;

        const animPromise = new Promise<void>((resolve) => {
          setTimeout(() => {
            if (sprite.destroyed) {
              resolve();
              return;
            }
            const startTime = performance.now();

            const animate = (currentTime: number) => {
              if (sprite.destroyed) {
                resolve();
                return;
              }
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = easeOutCubic(progress);

              sprite.x = startX + (targetX - startX) * eased;
              sprite.y = startY + (targetY - startY) * eased;

              // Interpolate size from kitty to rail
              const currentSize = startSpriteSize + (endSize - startSpriteSize) * eased;
              sprite.width = currentSize;
              sprite.height = currentSize;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Sound: chipLand on insurance win Step 2 arrival at player rail
                playTableSound('chipLand');
                // Update counter as each chip departs
                runningTotal -= denomination;
                if (counterTextForStep2 && !counterTextForStep2.destroyed) {
                  counterTextForStep2.text = runningTotal > 0 ? `$${runningTotal.toLocaleString()}` : '';
                }
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(sprite);
                scene.animationLayer?.removeChild(sprite);
                sprite.destroy();
                resolve();
              }
            };

            requestAnimationFrame(animate);
          }, staggerIndex * STAGGER_DELAY);
        });
        animations.push(animPromise);
      });

      await Promise.all(animations);

      // Get visual position before cleanup for main kitty animation
      const visualPos = getVisualPositionForSeat(seatPosition);

      // Destroy the container and clean up all maps
      if (kittyContainer && !kittyContainer.destroyed) {
        scene.animationLayer?.removeChild(kittyContainer);
        kittyContainer.destroy();
      }
      scene.insuranceKittyContainers.delete(seatPosition);
      scene.insuranceKittyChipSprites.delete(seatPosition);
      scene.insuranceKittyData.delete(seatPosition);
      scene.insuranceKittyCounterTexts.delete(seatPosition);
      scene.insuranceKittyPills.delete(seatPosition);

      // Animate main kitty back to center (base position)
      if (visualPos !== null) {
        const mainKittyContainer = scene.kittyContainers.get(visualPos);
        const mainKittyBasePos = scene.kittyBasePositions.get(visualPos);

        if (mainKittyContainer && mainKittyBasePos && !mainKittyContainer.destroyed) {
          const startX = mainKittyContainer.x;
          const startY = mainKittyContainer.y;
          const shiftDuration = 200;
          const shiftStartTime = performance.now();

          const animateMainKittyBack = (currentTime: number) => {
            if (mainKittyContainer.destroyed) return;
            const elapsed = currentTime - shiftStartTime;
            const progress = Math.min(elapsed / shiftDuration, 1);
            const eased = easeOutCubic(progress);

            mainKittyContainer.x = startX + (mainKittyBasePos.x - startX) * eased;
            mainKittyContainer.y = startY + (mainKittyBasePos.y - startY) * eased;

            if (progress < 1) {
              requestAnimationFrame(animateMainKittyBack);
            }
          };
          requestAnimationFrame(animateMainKittyBack);
        }
      }
    },

    /**
     * Animate insurance loss: chips from insurance kitty to dealer.
     * @param seatPosition - Seat position (1-7)
     * @param amount - Insurance bet amount
     */
    animateInsuranceLoss: async (seatPosition: number, amount: number): Promise<void> => {
      const scene = sceneRef.current;

      // Get insurance kitty container and data
      const kittyContainer = scene.insuranceKittyContainers.get(seatPosition);
      const kittySprites = scene.insuranceKittyChipSprites.get(seatPosition) || [];
      const kittyData = scene.insuranceKittyData.get(seatPosition);

      if (kittySprites.length === 0) {
        // Clean up all maps
        if (kittyContainer && !kittyContainer.destroyed) {
          scene.animationLayer?.removeChild(kittyContainer);
          kittyContainer.destroy();
        }
        scene.insuranceKittyContainers.delete(seatPosition);
        scene.insuranceKittyChipSprites.delete(seatPosition);
        scene.insuranceKittyData.delete(seatPosition);
        scene.insuranceKittyCounterTexts.delete(seatPosition);
        scene.insuranceKittyPills.delete(seatPosition);
        return;
      }

      // Animation constants (match standard chip departures)
      const STAGGER_DELAY = 80; // 80ms between chips (standardized stagger)
      const duration = 300; // Match animateChipFromKitty duration

      // Size interpolation: kittyMedium (28px) → dealerPile (20px)
      const endSize = CHIP_SIZES.dealerPile;

      // Get denominations for each chip (to find correct tray position)
      const denominations = kittyData?.denominations || [];

      // Get chip container reference
      const chipContainerRef = kittyContainer ?
        (kittyContainer as unknown as { chipContainer: Container }).chipContainer : null;

      // Get counter text for updating as chips leave
      const counterText = scene.insuranceKittyCounterTexts.get(seatPosition);
      let runningTotal = kittyData?.total || 0;

      // Animate all chips to dealer tray with stagger
      // FIX: Iterate in REVERSE order (top of stack first, like other chip animations)
      const animations: Promise<void>[] = [];
      const reversedIndices = [...kittySprites.keys()].reverse(); // [n-1, n-2, ..., 1, 0]
      reversedIndices.forEach((originalIndex, staggerIndex) => {
        const sprite = kittySprites[originalIndex];
        if (!sprite || sprite.destroyed) return;

        // Get denomination for this chip (use original index for data lookup)
        const denomination = denominations[originalIndex] || 1; // Fallback to $1

        // Get chip's world position before removing from container
        const globalPos = sprite.getGlobalPosition();
        const animLayerPos = scene.animationLayer ? scene.animationLayer.toLocal(globalPos) : globalPos;

        // Remove from chipContainer, add to animationLayer
        chipContainerRef?.removeChild(sprite);
        sprite.label = 'chipSprite'; // Mark as chip for reconciliation skip
        sprite.x = animLayerPos.x;
        sprite.y = animLayerPos.y;
        sprite.rotation = 0; // Reset rotation for animation
        // First chip (top of stack) gets highest zIndex, subsequent get decreasing
        sprite.zIndex = 2000 - staggerIndex;
        scene.animationLayer?.addChild(sprite);
        MODULE_CHIP_SPRITES_IN_FLIGHT.add(sprite);

        // Get tray chip position for this denomination
        const trayChip = scene.trayChipSprites.get(denomination);
        let targetX: number, targetY: number;

        if (trayChip && scene.animationLayer) {
          // Get global position and convert to animationLayer coordinates
          const trayGlobalPos = trayChip.getGlobalPosition();
          const trayLocalPos = scene.animationLayer.toLocal(trayGlobalPos);
          targetX = trayLocalPos.x;
          targetY = trayLocalPos.y;
        } else {
          // Fallback: use getTrayChipPosition from layoutStore
          const trayPos = getTrayChipPosition(denomination);
          if (trayPos) {
            targetX = trayPos.x;
            targetY = trayPos.y;
          } else {
            // Last resort: use dealer position
            targetX = DEALER_POSITION.x;
            targetY = DEALER_POSITION.y;
          }
        }

        const startX = sprite.x;
        const startY = sprite.y;
        const startSpriteSize = sprite.width;

        const animPromise = new Promise<void>((resolve) => {
          setTimeout(() => {
            if (sprite.destroyed) {
              resolve();
              return;
            }
            const startTime = performance.now();

            const animate = (currentTime: number) => {
              if (sprite.destroyed) {
                resolve();
                return;
              }
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = easeOutCubic(progress);

              sprite.x = startX + (targetX - startX) * eased;
              sprite.y = startY + (targetY - startY) * eased;

              // Interpolate size from kitty to dealer pile
              const currentSize = startSpriteSize + (endSize - startSpriteSize) * eased;
              sprite.width = currentSize;
              sprite.height = currentSize;

              // Fade out as it approaches dealer
              sprite.alpha = 1 - progress * 0.5;

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                // Sound: chipLand on insurance loss chip arrival at dealer tray
                playTableSound('chipLand');
                // Update counter as each chip departs
                runningTotal -= denomination;
                if (counterText && !counterText.destroyed) {
                  counterText.text = runningTotal > 0 ? `$${runningTotal.toLocaleString()}` : '';
                }
                // Update tray chip rotation to match landing
                if (trayChip) {
                  trayChip.rotation = Math.random() * Math.PI * 2;
                }
                MODULE_CHIP_SPRITES_IN_FLIGHT.delete(sprite);
                scene.animationLayer?.removeChild(sprite);
                sprite.destroy();
                resolve();
              }
            };

            requestAnimationFrame(animate);
          }, staggerIndex * STAGGER_DELAY);
        });
        animations.push(animPromise);
      });

      await Promise.all(animations);

      // Get visual position before cleanup for main kitty animation
      const visualPos = getVisualPositionForSeat(seatPosition);

      // Destroy the container and clean up all maps
      if (kittyContainer && !kittyContainer.destroyed) {
        scene.animationLayer?.removeChild(kittyContainer);
        kittyContainer.destroy();
      }
      scene.insuranceKittyContainers.delete(seatPosition);
      scene.insuranceKittyChipSprites.delete(seatPosition);
      scene.insuranceKittyData.delete(seatPosition);
      scene.insuranceKittyCounterTexts.delete(seatPosition);
      scene.insuranceKittyPills.delete(seatPosition);

      // Animate main kitty back to center (base position)
      if (visualPos !== null) {
        const mainKittyContainer = scene.kittyContainers.get(visualPos);
        const mainKittyBasePos = scene.kittyBasePositions.get(visualPos);

        if (mainKittyContainer && mainKittyBasePos && !mainKittyContainer.destroyed) {
          const startX = mainKittyContainer.x;
          const startY = mainKittyContainer.y;
          const shiftDuration = 200;
          const shiftStartTime = performance.now();

          const animateMainKittyBack = (currentTime: number) => {
            if (mainKittyContainer.destroyed) return;
            const elapsed = currentTime - shiftStartTime;
            const progress = Math.min(elapsed / shiftDuration, 1);
            const eased = easeOutCubic(progress);

            mainKittyContainer.x = startX + (mainKittyBasePos.x - startX) * eased;
            mainKittyContainer.y = startY + (mainKittyBasePos.y - startY) * eased;

            if (progress < 1) {
              requestAnimationFrame(animateMainKittyBack);
            }
          };
          requestAnimationFrame(animateMainKittyBack);
        }
      }
    },

    /**
     * Clear insurance kitty for a seat.
     * @param seatPosition - Seat position (1-7)
     */
    clearInsuranceKitty: (seatPosition: number) => {
      const scene = sceneRef.current;

      // Get visual position for main kitty lookup
      const visualPos = getVisualPositionForSeat(seatPosition);

      // Destroy the container (which also destroys all children including chips)
      const kittyContainer = scene.insuranceKittyContainers.get(seatPosition);
      if (kittyContainer && !kittyContainer.destroyed) {
        scene.animationLayer?.removeChild(kittyContainer);
        kittyContainer.destroy();
      }

      // Clear all maps
      scene.insuranceKittyContainers.delete(seatPosition);
      scene.insuranceKittyChipSprites.delete(seatPosition);
      scene.insuranceKittyData.delete(seatPosition);
      scene.insuranceKittyCounterTexts.delete(seatPosition);
      scene.insuranceKittyPills.delete(seatPosition);

      // Animate main kitty back to center (base position)
      if (visualPos !== null) {
        const mainKittyContainer = scene.kittyContainers.get(visualPos);
        const mainKittyBasePos = scene.kittyBasePositions.get(visualPos);

        if (mainKittyContainer && mainKittyBasePos && !mainKittyContainer.destroyed) {
          const startX = mainKittyContainer.x;
          const startY = mainKittyContainer.y;
          const duration = 200;
          const startTime = performance.now();

          const animateMainKittyBack = (currentTime: number) => {
            if (mainKittyContainer.destroyed) return;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(progress);

            mainKittyContainer.x = startX + (mainKittyBasePos.x - startX) * eased;
            mainKittyContainer.y = startY + (mainKittyBasePos.y - startY) * eased;

            if (progress < 1) {
              requestAnimationFrame(animateMainKittyBack);
            }
          };
          requestAnimationFrame(animateMainKittyBack);
        }
      }
    },

    /**
     * Force retry autoplay after tab visibility change.
     * This is called from PixiTablePage when tab becomes visible to ensure
     * autoplay resumes if it was interrupted by ALT+TAB.
     */
    retryAutoPlay: () => {
      console.log('[PixiGameTableV1] retryAutoPlay called - forwarding to control panel');
      const scene = sceneRef.current;
      if (scene.controlPanel) {
        scene.controlPanel.forceRetryAutoPlay();
      } else {
        console.warn('[PixiGameTableV1] retryAutoPlay: no control panel');
      }
    },

    getComputedRecommendation: () => {
      const scene = sceneRef.current;
      if (scene.controlPanel) {
        return scene.controlPanel.getComputedRecommendation();
      }
      return null;
    },

    // ===========================================
    // SPLIT HAND SUPPORT
    // ===========================================

    setSplitHandCount: (seatPosition: number, totalHands: number) => {
      console.log(`[SPLIT] setSplitHandCount: seat=${seatPosition} totalHands=${totalHands}`);
      const scene = sceneRef.current;

      // Get or create split state for this seat
      let state = scene.splitHandState.get(seatPosition);
      if (!state) {
        state = {
          totalHands: 1,
          viewedHandIndex: 0,
          isAnimating: false,
          handContainers: new Map(),
        };
        scene.splitHandState.set(seatPosition, state);
      }

      // Update total hands
      state.totalHands = totalHands;

      // Clamp viewed index if needed
      if (state.viewedHandIndex >= totalHands) {
        state.viewedHandIndex = totalHands - 1;
      }

      console.log(`[SPLIT] State updated: totalHands=${state.totalHands}, viewedHand=${state.viewedHandIndex}`);
    },

    navigateToHand: (seatPosition: number, handIndex: number, pendingOutcome?: string): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[SPLIT] navigateToHand: seat=${seatPosition} handIndex=${handIndex} pendingOutcome=${pendingOutcome || 'none'}`);
        const scene = sceneRef.current;

        const state = scene.splitHandState.get(seatPosition);
        if (!state || state.totalHands <= 1) {
          console.log(`[SPLIT] No split state or single hand, skipping navigation`);
          resolve();
          return;
        }

        // Clamp to valid range
        const targetIndex = Math.max(0, Math.min(handIndex, state.totalHands - 1));
        if (targetIndex === state.viewedHandIndex) {
          console.log(`[SPLIT] Already viewing hand ${targetIndex}`);
          resolve();
          return;
        }

        // Prevent concurrent animations
        if (state.isAnimating) {
          console.log(`[SPLIT] Carousel animation in progress, queuing navigation`);
          // Wait for current animation then try again
          setTimeout(() => {
            const newState = scene.splitHandState.get(seatPosition);
            if (newState && !newState.isAnimating) {
              newState.viewedHandIndex = targetIndex;
            }
            resolve();
          }, 350);
          return;
        }

        state.isAnimating = true;

        // === CHOREOGRAPHY TIMING ===
        // Carousel slide: 600ms (0 → 600)
        // HandA drops up: 300ms (0 → 300)
        // HandB flies in: 300ms (300 → 600)
        // All animations END together at 600ms
        const CAROUSEL_DURATION = 600;
        const LABEL_DURATION = 300;

        console.log(`[SPLIT] Filmstrip slide: ${state.viewedHandIndex} -> ${targetIndex} (600ms carousel, 2x300ms labels)`);

        // === PREPARE LABEL TRANSITION DATA ===
        const labelKey = `${seatPosition}-handLabel`;
        const existingLabel = scene.splitHandLabels?.get(labelKey);
        const previousHandIndex = state.viewedHandIndex;

        // Get seat data for positioning
        // Use seatPosition for LABEL position, visualPos for MASK dimensions (to match actual seat rendering)
        const visualPos = getVisualPositionForSeat(seatPosition);
        const seatData = visualPos !== null ? dynamicSeatPositions[visualPos - 1] : null;
        const seatRotRad = seatData ? degreesToRadians(seatData.rotation) : 0;

        // Mask dimensions use visualPos (matches actual seat rendering)
        const seatConfigForMask = seatConfigs[visualPos ?? 0] || { x: 0, y: 0, rot: 0, scale: 100 };
        const seatScaleForMask = (seatSizeScale / 100) * (seatConfigForMask.scale / 100);
        const scaledSeatHeight = Math.round(168 * seatScaleForMask);
        const scaledSeatWidth = Math.round(144 * seatScaleForMask);

        // Label position uses seatPosition (consistent with initial split and rake label creation)
        const seatConfigForLabel = seatConfigs[seatPosition] || { x: 0, y: 0, rot: 0, scale: 100 };
        const seatScaleForLabel = (seatSizeScale / 100) * (seatConfigForLabel.scale / 100);
        const scaledSeatHeightForLabel = Math.round(168 * seatScaleForLabel);
        const scaledSeatWidthForLabel = Math.round(144 * seatScaleForLabel);

        // === PHASE 1 (0-300ms): HandA drops up + Carousel starts + OutcomeA drops down + Scorebox flips ===
        // Start carousel slide (runs full 600ms)
        // Scorebox wipe animation is now handled by slideFilmstripToHand
        // (wipes horizontally to "--" at duration/2, snaps to new score at duration end)

        // Get target hand card count for iris animation
        const gameState = useGameStateStore.getState();
        const player = gameState.players.get(seatPosition);
        const targetHandCardCount = player?.hands[targetIndex]?.cards?.length ?? 2;
        const currentCardConfig = cardConfigRef.current;
        const irisScaleFactor = currentCardConfig.scale / 100;

        slideFilmstripToHand(scene, seatPosition, targetIndex, true, CAROUSEL_DURATION, {
          targetHandCardCount,
          scaleFactor: irisScaleFactor,
          seatScale: seatScaleForMask,
        });

        // Animate existing OUTCOME label "dropping down" (fly downward - mirrors hand label going up)
        // CLIPPING: Keep wrapper stationary (mask stays in place), animate label child in LOCAL coords
        const previousOutcomeKey = `${seatPosition}-${previousHandIndex}`;
        const existingOutcome = MODULE_OUTCOME_LABELS.get(previousOutcomeKey);
        if (existingOutcome && existingOutcome.wrapper && !existingOutcome.wrapper.destroyed) {
          console.log(`[SPLIT] Phase 1: Outcome label for hand ${previousHandIndex} dropping down`);
          const outcomeWrapper = existingOutcome.wrapper;

          // Find the label child (not the mask) - it's the second child added to wrapper
          // Structure: wrapper.children = [mask, label]
          const outcomeLabel = outcomeWrapper.children.find(
            child => child !== outcomeWrapper.mask && !child.destroyed
          );

          if (outcomeLabel) {
            // Fly-down in LOCAL coordinates (positive Y = downward in seat-rotated space)
            // The mask clips the label as it exits the seat boundary
            const flyDownOffset = 60; // Increased to ensure full exit from mask
            const targetLocalY = outcomeLabel.y + flyDownOffset;

            existingOutcome.state = 'animating-out';
            animateSprite(outcomeLabel, { y: targetLocalY }, {
              duration: LABEL_DURATION,
              easing: easeInCubic,
              onComplete: () => {
                scene.outcomeLabelLayer?.removeChild(outcomeWrapper);
                outcomeWrapper.destroy();
                MODULE_OUTCOME_LABELS.delete(previousOutcomeKey);
              }
            });
          } else {
            // Fallback: destroy immediately if label not found
            scene.outcomeLabelLayer?.removeChild(outcomeWrapper);
            outcomeWrapper.destroy();
            MODULE_OUTCOME_LABELS.delete(previousOutcomeKey);
          }
        }

        // Animate existing HAND label "dropping up" (fly upward toward dealer)
        // Uses unified animateHandLabelFlyOut function for consistent behavior
        if (existingLabel && seatData && scene.outcomeLabelLayer) {
          const { label: oldLabel, bg: oldBg, wrapper: existingWrapper } = existingLabel;

          console.log(`[SPLIT] Phase 1 (0-300ms): Hand${previousHandIndex + 1} label dropping up (hasWrapper=${!!existingWrapper})`);

          animateHandLabelFlyOut({
            label: oldLabel,
            bg: oldBg ?? null,
            seatData: { x: seatData.x, y: seatData.y, rotation: seatData.rotation },
            scaledSeatWidth,
            scaledSeatHeight,
            outcomeLabelLayer: scene.outcomeLabelLayer,
            duration: LABEL_DURATION,
            debugLabel: `navigate-hand${previousHandIndex + 1}`,
            existingWrapper: existingWrapper // Pass wrapper if it exists
          });

          // Clear from map (wrapper is destroyed in animateHandLabelFlyOut)
          scene.splitHandLabels?.delete(labelKey);
        }

        // === PHASE 2 (300-600ms): HandB flies in ===
        // Schedule fly-in to start at 300ms (after drop-up completes)
        // ONLY show hand labels for the player's seat (not opponents)
        const isPlayerSeat = seatPosition === mySeatPosition;
        setTimeout(() => {
          if (seatData && scene.animationLayer && isPlayerSeat) {
            const newHandNumber = targetIndex + 1;
            // Use pre-loaded texture for hand label (avoids runtime texture creation)
            const handLabelTexture = texturesRef.current.get(`handLabel-${newHandNumber}`);
            let newLabel: Sprite | Text;
            if (handLabelTexture) {
              newLabel = new Sprite(handLabelTexture);
              newLabel.anchor.set(0, 0.5); // Left-justified, vertically centered
            } else {
              console.warn(`[SPLIT LABEL] Pre-loaded texture not found for handLabel-${newHandNumber}, falling back to Text`);
              MODULE_TEXT_CREATED_COUNT++;
              newLabel = new Text({
                text: `Hand${newHandNumber}`,
                style: {
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 14,
                  fontWeight: '700',
                  fill: 0x000000,
                  align: 'left',
                }
              });
              newLabel.anchor.set(0, 0.5); // Left-justified, vertically centered
            }

            // === WRAPPER-BASED MASKING FOR FLY-IN CLIPPING ===
            // Same pattern as outcome label - wrapper at seat center with mask
            const handLabelWrapper = new Container();
            handLabelWrapper.label = 'handLabelWrapper';
            handLabelWrapper.sortableChildren = true;
            handLabelWrapper.x = seatData.x;
            handLabelWrapper.y = seatData.y;
            handLabelWrapper.rotation = seatRotRad;

            // Create mask that clips on LEFT side (behind scorebox)
            // Mask covers area from 40px inward on left to full right edge
            const maskTopY = scaledSeatHeight / 2; // Seat border bottom
            const maskBottomY = scaledSeatHeight / 2 + 60; // Extend below seat
            const maskHeight = maskBottomY - maskTopY;
            const maskLeftX = -scaledSeatWidth / 2 + 40; // Clip 40px from left (behind scorebox)
            const maskRightX = scaledSeatWidth / 2; // Full right side
            const maskWidth = maskRightX - maskLeftX;

            const handLabelMask = new Graphics();
            handLabelMask.roundRect(maskLeftX, maskTopY, maskWidth, maskHeight, 8);
            handLabelMask.fill({ color: 0xffffff });
            handLabelWrapper.addChild(handLabelMask);
            handLabelWrapper.mask = handLabelMask;


            // Combines global config with per-seat config from refs
            // Use scaledSeatHeightForLabel/WidthForLabel (seatPosition-based) for label positioning
            const hlConfig = handLabelConfigRef.current;
            const labelLocalY = scaledSeatHeightForLabel / 2 + 8 + hlConfig.y; // Baseline +8px Y + config Y
            const labelLocalX = scaledSeatWidthForLabel * 0.2 - 38 + hlConfig.x; // Baseline -38px X + config X

            const flyInOffset = 80;

            // Background with ONLY bottom-left corner rounded (3 squared corners)
            const newBg = new Graphics();
            const leftPadding = 8;
            const rightPadding = 16;
            const pillWidth = (newLabel.width + leftPadding + rightPadding) * 1.2; // 20% longer
            const pillHeight = newLabel.height + 8;
            const r = 4;
            const bx = 0; // Left edge at origin
            const by = -pillHeight / 2;
            const bw = pillWidth;
            const bh = pillHeight;

            // Draw with only bottom-left corner rounded
            newBg.moveTo(bx, by);                        // Top-left (SQUARED)
            newBg.lineTo(bx + bw, by);                   // Top edge to top-right (SQUARED)
            newBg.lineTo(bx + bw, by + bh);              // Right edge to bottom-right (SQUARED)
            newBg.lineTo(bx + r, by + bh);               // Bottom edge to before bottom-left
            newBg.arcTo(bx, by + bh, bx, by + bh - r, r); // Bottom-left corner (ROUNDED)
            newBg.lineTo(bx, by);                        // Left edge back to top-left
            newBg.closePath();
            newBg.fill({ color: 0xFFFFFF });

            // Position bg in LOCAL coords (starts outside mask on right)
            newBg.x = labelLocalX + flyInOffset;
            newBg.y = labelLocalY;
            newBg.zIndex = 0;

            // Position text with left padding inside the pill (LOCAL coords)
            const textLocalX = labelLocalX + leftPadding;
            newLabel.x = textLocalX + flyInOffset; // Start outside mask
            newLabel.y = labelLocalY;
            newLabel.zIndex = 1;

            // Add bg FIRST, then label (z-index ordering)
            handLabelWrapper.addChild(newBg);
            handLabelWrapper.addChild(newLabel);

            // Add wrapper to outcomeLabelLayer
            scene.outcomeLabelLayer?.addChild(handLabelWrapper);

            // Store wrapper AND local coordinates for fly-out animation
            scene.splitHandLabels = scene.splitHandLabels || new Map();
            scene.splitHandLabels.set(labelKey, {
              label: newLabel,
              bg: newBg,
              wrapper: handLabelWrapper,
              seatRotRad,
              finalLocalX: labelLocalX, // Store final LOCAL X for bg
              finalTextLocalX: textLocalX, // Store final LOCAL X for text
              finalLocalY: labelLocalY // Store final LOCAL Y
            });

            console.log(`[SPLIT] Phase 2 (300-600ms): Hand${newHandNumber} label flying in (wrapper-masked)`);

            // Animate in LOCAL coordinates - clipping happens automatically at mask edge
            animateSprite(newLabel, { x: textLocalX }, {
              duration: LABEL_DURATION,
              easing: easeOutCubic,
              debugName: `carouselHandLabel-flyIn-${seatPosition}`,
            });
            animateSprite(newBg, { x: labelLocalX }, {
              duration: LABEL_DURATION,
              easing: easeOutCubic,
              debugName: `carouselHandLabelBg-flyIn-${seatPosition}`,
            });
          }

          // === OUTCOME LABEL FLY-IN (synchronized with hand label) ===
          // If pendingOutcome is provided, create and animate the outcome label
          // Both labels should END at the same time (600ms total, this starts at 300ms, runs for 300ms)
          if (pendingOutcome && seatData && scene.outcomeLabelLayer && isPlayerSeat) {
            const outcomeKey = `${seatPosition}-${targetIndex}`;
            const existingOutcome = MODULE_OUTCOME_LABELS.get(outcomeKey);

            // Skip if already exists
            if (!existingOutcome || existingOutcome.state === 'animating-out') {
              console.log(`[SPLIT] Phase 2 (300-600ms): Outcome label '${pendingOutcome}' flying in (synchronized)`);

              // Calculate dimensions (matches createSeatContainer)
              const visualPos = getVisualPositionForSeat(seatPosition);
              const seatConfigLocal = seatConfigs[visualPos ?? 0] || DEFAULT_SEAT_CONFIG;
              const seatScaleLocal = (seatSizeScale / 100) * (seatConfigLocal.scale / 100);
              const widthLocal = Math.round(144 * seatScaleLocal);
              const heightLocal = Math.round(168 * seatScaleLocal);

              // Create wrapper at seat position
              const seatX = seatData.x + seatConfigLocal.x;
              const seatY = seatData.y + seatConfigLocal.y;
              const seatRotation = seatData.rotation + seatConfigLocal.rot;

              const outcomeLabelWrapper = new Container();
              outcomeLabelWrapper.label = 'outcomeLabelWrapper';
              (outcomeLabelWrapper as unknown as { isOutcomeLabelWrapper: boolean }).isOutcomeLabelWrapper = true;
              outcomeLabelWrapper.x = seatX;
              outcomeLabelWrapper.y = seatY;
              outcomeLabelWrapper.rotation = degreesToRadians(seatRotation);

              // Create mask matching seat box dimensions
              const outcomeMask = new Graphics();
              outcomeMask.roundRect(-widthLocal / 2, -heightLocal / 2, widthLocal, heightLocal, 8);
              outcomeMask.fill({ color: 0xffffff });
              outcomeLabelWrapper.addChild(outcomeMask);
              outcomeLabelWrapper.mask = outcomeMask;

              // Create the label
              const outcomeLabel = createOutcomeLabel(pendingOutcome, outcomeLabelConfig, texturesRef.current);
              const outcomeBounds = outcomeLabel.getLocalBounds();
              const labelScaleLocal = outcomeLabel.scale.y;
              const visualHeightLocal = outcomeBounds.height * labelScaleLocal;

              // Calculate scorebox width for positioning using cached measurements
              let scoreboxWidthLocal = scoreboxConfig.minWidth ?? 35;
              const latestScore = getLatestScore(seatPosition, targetIndex);
              if (latestScore) {
                const boxScale = scoreboxConfig.scale / 100;
                const textWidth = getScoreTextWidth(latestScore.score, scoreboxConfig.fontSize);
                const calculatedWidth = Math.max(12, textWidth) * boxScale;
                scoreboxWidthLocal = scoreboxConfig.minWidth
                  ? Math.max(scoreboxConfig.minWidth, calculatedWidth)
                  : calculatedWidth;
              }

              // Position outcome label flush with scorebox
              const scoreboxLocalX = -widthLocal / 2 + scoreboxConfig.x;
              const finalX = scoreboxLocalX + scoreboxWidthLocal;
              const scoreboxBottom = heightLocal / 2 + scoreboxConfig.y;
              outcomeLabel.y = scoreboxBottom - visualHeightLocal;
              outcomeLabelWrapper.addChild(outcomeLabel);

              // Add to layer
              scene.outcomeLabelLayer.addChild(outcomeLabelWrapper);

              // Register in module-level map
              MODULE_OUTCOME_LABELS.set(outcomeKey, {
                wrapper: outcomeLabelWrapper,
                outcome: pendingOutcome,
                state: 'animating-in'
              });

              // Animate fly-in from left (same duration as hand label so they END together)
              const flyInOffsetOutcome = 80;
              outcomeLabel.x = finalX - flyInOffsetOutcome; // Start off-screen left
              animateSprite(outcomeLabel, { x: finalX }, {
                duration: LABEL_DURATION, // Same duration as hand label = ends at 600ms
                easing: easeOutCubic,
                debugName: `carouselOutcomeLabel-flyIn-${seatPosition}`,
                onComplete: () => {
                  const labelState = MODULE_OUTCOME_LABELS.get(outcomeKey);
                  if (labelState) labelState.state = 'static';
                  console.log(`[SPLIT] Outcome label '${pendingOutcome}' fly-in complete (synchronized with hand label)`);
                }
              });
            }
          }
        }, LABEL_DURATION); // Start at 300ms

        // === FINALIZATION (at 600ms) ===
        // All animations complete together - update state
        setTimeout(() => {
          // Animation complete - finalize state
          state.viewedHandIndex = targetIndex;
          state.isAnimating = false;

          // NOTE: Split kitty containers should ALWAYS be visible during play.
          // All chip stacks display as a group above the seat, regardless of which hand is viewed.
          // (Outcome labels correctly show/hide per hand, but chip stacks do not.)

          // Update outcome labels visibility
          MODULE_OUTCOME_LABELS.forEach((labelState, key) => {
            const parts = key.split('-');
            if (parts[0] === String(seatPosition)) {
              const labelHandIndex = parseInt(parts[1], 10);
              labelState.wrapper.visible = labelHandIndex === targetIndex;
            }
          });

          // NOTE: Scorebox is now updated by slideFilmstripToHand at navigation end
          // (wipes to "--" at duration/2, snaps to new score at duration end)

          console.log(`[SPLIT] All animations complete at 600ms: now viewing hand ${targetIndex}`);
          resolve();
        }, CAROUSEL_DURATION); // Resolve at 600ms
      });
    },

    getCurrentViewedHand: (seatPosition: number): number => {
      const scene = sceneRef.current;
      const state = scene.splitHandState.get(seatPosition);
      return state?.viewedHandIndex ?? 0;
    },

    getTotalHands: (seatPosition: number): number => {
      const scene = sceneRef.current;
      const state = scene.splitHandState.get(seatPosition);
      return state?.totalHands ?? 1;
    },

    instantResetFilmstrip: (seatPosition: number): void => {
      console.log(`[SPLIT] instantResetFilmstrip: seat=${seatPosition}`);
      const scene = sceneRef.current;

      // Snap filmstrip to cell 1 (hand index 0) without animation
      slideFilmstripToHand(scene, seatPosition, 0, false);

      // Update split hand state
      const splitState = scene.splitHandState.get(seatPosition);
      if (splitState) {
        splitState.viewedHandIndex = 0;
      }
    },

    animateSplitAction: (
      seatPosition: number,
      originalHandIndex: number,
      newHandIndex: number,
      betChips: number[]
    ): Promise<void> => {
      return new Promise(async (resolve) => {
        console.log(`[SPLIT] animateSplitAction: seat=${seatPosition} original=${originalHandIndex} new=${newHandIndex} chips=[${betChips.join(',')}]`);

        const scene = sceneRef.current;
        const textures = texturesRef.current;

        if (!appRef.current || loadingRef.current) {
          console.warn('[SPLIT] App not ready, skipping animation');
          resolve();
          return;
        }

        // Initialize split hand state
        let state = scene.splitHandState.get(seatPosition);
        if (!state) {
          state = {
            totalHands: 1,
            viewedHandIndex: 0,
            isAnimating: false,
            handContainers: new Map(),
          };
          scene.splitHandState.set(seatPosition, state);
        }

        // Update total hands
        state.totalHands = Math.max(state.totalHands, newHandIndex + 1);
        console.log(`[SPLIT] Updated totalHands to ${state.totalHands}`);

        // Get visual position for seat
        const visualPos = getVisualPositionForSeat(seatPosition);
        if (visualPos === null) {
          console.warn(`[SPLIT] No visual position for seat ${seatPosition}`);
          resolve();
          return;
        }

        // Check if this is an opponent split (opponents don't show chip counters)
        const currentMySeat = useGameStateStore.getState().mySeatPosition;
        const isOpponentSplit = seatPosition !== currentMySeat;

        // Get seat position data
        const seatData = dynamicSeatPositions[visualPos - 1];
        if (!seatData) {
          console.warn(`[SPLIT] No seat data for visualPos ${visualPos}`);
          resolve();
          return;
        }

        // === ENABLE IRIS MASK FOR SPLIT HANDS (IF NOT ALREADY ENABLED) ===
        // Mask should already be enabled by animateSplitCardSeparation, but enable here as fallback
        // (e.g., for opponent splits where card separation may not be animated)
        const existingMaskState = scene.seatMaskState.get(seatPosition);
        if (!existingMaskState?.isEnabled) {
          const seatConfigForMask = seatConfigs[visualPos] || { x: 0, y: 0, rot: 0, scale: 100 };
          const seatScaleForMask = (seatSizeScale / 100) * (seatConfigForMask.scale / 100);
          const seatWidth = Math.round(144 * seatScaleForMask);
          const seatHeight = Math.round(168 * seatScaleForMask);
          enableSeatMask(scene, seatPosition, seatWidth, seatHeight);
          console.log(`[SPLIT] Enabled mask for seat ${seatPosition} (fallback): ${seatWidth}x${seatHeight}`);
        }

        // Calculate base kitty position (center of all kitties before offset)
        const seatRotRad = degreesToRadians(seatData.rotation);
        // Use seatPosition for config lookup (UI keys by seat number, not visual position)
        const betConfig = betChipConfigs[seatPosition] || REGISTRY_DEFAULT_CHIP_CONFIG;
        const kittyPosResult = calculateKittyPosition(seatData, {
          betChipConfig: betConfig,
          kittyOffsetX: kittyConfig?.x ?? 0,
          kittyOffsetY: kittyConfig?.y ?? 0,
        });
        const baseKittyPos = { x: kittyPosResult.x, y: kittyPosResult.y };

        // === RECENTER ALL KITTIES (DYNAMIC PILL-BASED) ===
        // When a split happens, ALL kitties (main + splits) need to recenter as a group.
        // Spacing is based on actual pill widths with SPLIT_KITTY_PILL_PADDING gap between edges.
        const totalHands = state.totalHands;
        const RECENTER_DURATION = 200; // Animation duration for recentering

        // Gather pill widths for all hands (main + existing splits + new split)
        const pillWidths: number[] = [];

        // Hand 0 (main kitty) pill width
        const mainPill = scene.kittyPills.get(visualPos);
        pillWidths.push(getKittyPillWidth(mainPill));

        // Existing split kitties (hands 1 to newHandIndex-1)
        for (let h = 1; h < newHandIndex; h++) {
          const splitPill = scene.splitKittyPills.get(`${seatPosition}-${h}`);
          pillWidths.push(getKittyPillWidth(splitPill));
        }

        // New split kitty (will start with minimum width, grows as chips land)
        pillWidths.push(40); // Minimum starting width for new kitty

        // Calculate dynamic offsets based on pill widths
        const dynamicOffsets = calculateDynamicKittyOffsets(pillWidths, SPLIT_KITTY_PILL_PADDING);
        console.log(`[SPLIT] Dynamic offsets for ${totalHands} hands: [${dynamicOffsets.map(o => o.toFixed(0)).join(', ')}]`);

        // Recenter the MAIN kitty (hand 0)
        const mainKittyContainer = scene.kittyContainers.get(visualPos);
        if (mainKittyContainer && !mainKittyContainer.destroyed) {
          const mainTargetPos = registryApplyTangentOffset(baseKittyPos, dynamicOffsets[0], seatRotRad);
          console.log(`[SPLIT] Recentering main kitty: offset=${dynamicOffsets[0].toFixed(0)} to (${mainTargetPos.x.toFixed(0)}, ${mainTargetPos.y.toFixed(0)})`);
          animateSprite(mainKittyContainer, { x: mainTargetPos.x, y: mainTargetPos.y }, {
            duration: RECENTER_DURATION,
            debugName: `recenter-main-kitty-${seatPosition}`,
          });
        }

        // Recenter existing SPLIT kitties (hands 1 to newHandIndex-1)
        // For opponents, all split kitties stack at the same position (no spread)
        for (let h = 1; h < newHandIndex; h++) {
          const splitKey = `${seatPosition}-${h}`;
          const splitKitty = scene.splitKittyContainers.get(splitKey);
          if (splitKitty && !splitKitty.destroyed) {
            // Opponents: stack all at base position; Players: spread using dynamic offsets
            const splitTargetPos = isOpponentSplit
              ? baseKittyPos
              : registryApplyTangentOffset(baseKittyPos, dynamicOffsets[h], seatRotRad);
            console.log(`[SPLIT] Recentering split kitty ${h}: offset=${isOpponentSplit ? 0 : dynamicOffsets[h].toFixed(0)}`);
            animateSprite(splitKitty, { x: splitTargetPos.x, y: splitTargetPos.y }, {
              duration: RECENTER_DURATION,
              debugName: `recenter-split-kitty-${splitKey}`,
            });
          }
        }

        // Calculate position for the NEW split kitty
        // Opponents: stack at base position; Players: use dynamic offset for spread
        const newKittyPos = isOpponentSplit
          ? baseKittyPos
          : registryApplyTangentOffset(baseKittyPos, dynamicOffsets[newHandIndex], seatRotRad);

        // Create kitty container for new hand
        const kittyKey = `${seatPosition}-${newHandIndex}`;
        let newKittyContainer = scene.splitKittyContainers.get(kittyKey);

        if (!newKittyContainer) {
          // Create new kitty display for this hand
          const kittyDisplay = registryCreateKittyDisplay({
            ...chipCounterConfig,
            show: true,
          });

          newKittyContainer = kittyDisplay.container;
          newKittyContainer.x = newKittyPos.x;
          newKittyContainer.y = newKittyPos.y;
          newKittyContainer.rotation = seatRotRad;
          newKittyContainer.zIndex = 1500;

          // Store chipContainer and borderContainer on the container for later chip additions
          (newKittyContainer as unknown as { chipContainer: Container; borderContainer: Container }).chipContainer = kittyDisplay.chipContainer;
          (newKittyContainer as unknown as { chipContainer: Container; borderContainer: Container }).borderContainer = kittyDisplay.borderContainer;

          scene.animationLayer?.addChild(newKittyContainer);
          scene.splitKittyContainers.set(kittyKey, newKittyContainer);
          scene.splitKittyData.set(kittyKey, { denominations: [], total: 0 });
          scene.splitKittyChipSprites.set(kittyKey, new Map());

          // Store counter text and pill references
          scene.splitKittyCounterTexts.set(kittyKey, kittyDisplay.counterText);
          scene.splitKittyPills.set(kittyKey, kittyDisplay.pill);

          // Hide counter for opponent split kitties
          if (isOpponentSplit) {
            kittyDisplay.counterText.visible = false;
            kittyDisplay.pill.visible = false;
          } else {
            kittyDisplay.counterText.text = '$0';
          }

          console.log(`[SPLIT] Created kitty for hand ${newHandIndex} at (${newKittyPos.x.toFixed(0)}, ${newKittyPos.y.toFixed(0)}), totalHands=${totalHands}, isOpponent=${isOpponentSplit}`);
          console.log(`[SPLIT DEBUG] Kitty created:`, {
            key: kittyKey,
            visible: newKittyContainer.visible,
            hasParent: !!newKittyContainer.parent,
            parentLabel: newKittyContainer.parent?.label,
            label: newKittyContainer.label,
            zIndex: newKittyContainer.zIndex,
          });
        }

        // Get rail position for chip animation
        const railContainer = scene.chipRailContainer;
        if (!railContainer) {
          console.warn('[SPLIT] No rail container, skipping chip animation');
          resolve();
          return;
        }

        // Animate chips from rail to new kitty using shared batch wrapper
        const runningTotal = betChips.reduce((a, b) => a + b, 0);

        // Sort chips ascending (1 → 10k) so smaller chips animate first
        const sortedBetChips = [...betChips].sort((a, b) => a - b);
        console.log(`[SPLIT] Animating ${sortedBetChips.length} chips to new kitty: [${sortedBetChips.join(',')}] (sorted ascending)`);

        // Build chip configs for batch animation
        const chipConfigs: RailToKittyChipConfig[] = [];
        const cardsLayer = scene.cardsLayer;

        if (!cardsLayer) {
          console.warn('[SPLIT] No cards layer, skipping chip animation');
          resolve();
          return;
        }

        // Get target position in new kitty (same for all chips)
        const targetGlobalPos = newKittyContainer.getGlobalPosition();
        const localTargetPos = cardsLayer.toLocal(targetGlobalPos);

        for (const denomination of sortedBetChips) {
          // Get chip position from rail
          const railChip = scene.chipRailChips.get(denomination);
          if (!railChip || railChip.destroyed) {
            console.warn(`[SPLIT] Rail chip not found for denom ${denomination}, available: [${Array.from(scene.chipRailChips.keys()).join(',')}]`);
            continue;
          }

          const railGlobalPos = railChip.getGlobalPosition();
          const localRailPos = cardsLayer.toLocal(railGlobalPos);

          // Create chip sprite
          const chipTexture = textures.get(`chip-${denomination}`);
          if (!chipTexture) {
            console.warn(`[SPLIT] No chip texture for denomination ${denomination}`);
            continue;
          }

          const chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);

          console.log(`[SPLIT] Created chip sprite for $${denomination}: from (${localRailPos.x.toFixed(0)}, ${localRailPos.y.toFixed(0)}) to (${localTargetPos.x.toFixed(0)}, ${localTargetPos.y.toFixed(0)})`);

          chipConfigs.push({
            sprite: chipSprite,
            denomination,
            startPos: { x: localRailPos.x, y: localRailPos.y },
            endPos: { x: localTargetPos.x, y: localTargetPos.y },
            startSize: KITTY_CHIP_SIZE,
            endSize: KITTY_CHIP_SIZE,
            onLand: (denom: number, sprite: Sprite) => {
              // Remove from cards layer (where it was animated above cards)
              cardsLayer.removeChild(sprite);

              // Get kitty components
              const kContainer = scene.splitKittyContainers.get(kittyKey);
              const chipContainer = (kContainer as unknown as { chipContainer: Container })?.chipContainer;
              const borderContainer = (kContainer as unknown as { borderContainer: Container })?.borderContainer;
              const kittyChipSprites = scene.splitKittyChipSprites.get(kittyKey);
              const pill = scene.splitKittyPills.get(kittyKey);
              const counterText = scene.splitKittyCounterTexts.get(kittyKey);

              // Add chip to kitty stack (persistent)
              if (chipContainer && borderContainer && kittyChipSprites && !sprite.destroyed) {
                // Reset chip position to local coordinates within chip container
                sprite.x = 0;
                sprite.y = 0;
                addChipToKitty(chipContainer, borderContainer, denom, sprite, kittyChipSprites, chipBorderConfig);
                console.log(`[SPLIT DEBUG] Chip $${denom} added to kitty ${kittyKey}, chipSprites.size=${kittyChipSprites.size}`);
              } else {
                console.warn(`[SPLIT DEBUG] Cannot add chip $${denom} to kitty ${kittyKey}:`, {
                  hasChipContainer: !!chipContainer,
                  hasBorderContainer: !!borderContainer,
                  hasKittyChipSprites: !!kittyChipSprites,
                  spriteDestroyed: sprite.destroyed,
                  kittyContainerDestroyed: kContainer?.destroyed,
                });
                sprite.destroy();
              }

              // Update kitty data
              const data = scene.splitKittyData.get(kittyKey)!;
              data.denominations.push(denom);
              data.total += denom;

              // Update counter text and pill (skip for opponents - they don't show counters)
              if (counterText && pill && !isOpponentSplit) {
                counterText.text = `$${data.total}`;
                // Update pill layout
                const counterOffset = { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 };
                registryUpdateKittyLayout(
                  pill,
                  counterText,
                  data.denominations.length,
                  data.total,
                  counterOffset
                );

                // === RECENTER ALL KITTIES AFTER PILL UPDATE ===
                // Pill may have grown, so recalculate offsets and animate all kitties
                const splitState = scene.splitHandState.get(seatPosition);
                const totalHands = splitState?.totalHands ?? 1;

                if (totalHands > 1) {
                  const RECENTER_DURATION = 150; // Quick slide animation

                  // Gather current pill widths for all hands
                  const pillWidths: number[] = [];
                  const mainPill = scene.kittyPills.get(visualPos);
                  pillWidths.push(getKittyPillWidth(mainPill));

                  for (let h = 1; h < totalHands; h++) {
                    const splitPill = scene.splitKittyPills.get(`${seatPosition}-${h}`);
                    pillWidths.push(getKittyPillWidth(splitPill));
                  }

                  // Calculate new dynamic offsets based on updated pill widths
                  const dynamicOffsets = calculateDynamicKittyOffsets(pillWidths, SPLIT_KITTY_PILL_PADDING);

                  // Animate main kitty to new position
                  const mainKittyContainer = scene.kittyContainers.get(visualPos);
                  if (mainKittyContainer && !mainKittyContainer.destroyed) {
                    const mainTargetPos = registryApplyTangentOffset(baseKittyPos, dynamicOffsets[0], seatRotRad);
                    animateSprite(mainKittyContainer, { x: mainTargetPos.x, y: mainTargetPos.y }, {
                      duration: RECENTER_DURATION,
                      debugName: `recenter-chip-main-${seatPosition}`,
                    });
                  }

                  // Animate split kitties to new positions
                  for (let h = 1; h < totalHands; h++) {
                    const splitKey = `${seatPosition}-${h}`;
                    const splitKitty = scene.splitKittyContainers.get(splitKey);
                    if (splitKitty && !splitKitty.destroyed) {
                      const splitTargetPos = registryApplyTangentOffset(baseKittyPos, dynamicOffsets[h], seatRotRad);
                      animateSprite(splitKitty, { x: splitTargetPos.x, y: splitTargetPos.y }, {
                        duration: RECENTER_DURATION,
                        debugName: `recenter-chip-split-${splitKey}`,
                      });
                    }
                  }
                }
              }
            },
          });
        }

        // Animate all chips using shared batch wrapper (parallel with stagger)
        await animateChipBatchRailToKitty({
          animationLayer: cardsLayer, // Use cardsLayer so chips fly ABOVE cards
          chips: chipConfigs,
          debugLabel: `split-${seatPosition}-hand${newHandIndex}`,
        });

        console.log(`[SPLIT] Split action complete: hand ${newHandIndex} kitty total = $${runningTotal}`);
        resolve();
      });
    },

    /**
     * Animate the second card sliding from original hand to new split hand.
     * This is called after HAND_SPLIT event to visually separate the cards.
     */
    animateSplitCardSeparation: (
      seatPosition: number,
      originalHandIndex: number,
      newHandIndex: number,
      duration: number = 300
    ): Promise<void> => {
      return new Promise((resolve) => {
        console.log(`[SPLIT CARD] animateSplitCardSeparation: seat=${seatPosition} ${originalHandIndex} -> ${newHandIndex}`);

        if (!appRef.current || loadingRef.current) {
          console.warn('[SPLIT CARD] App not ready, skipping animation');
          resolve();
          return;
        }

        const scene = sceneRef.current;
        const visualPos = getVisualPositionForSeat(seatPosition);
        if (visualPos === null) {
          console.warn(`[SPLIT CARD] No visual position for seat ${seatPosition}`);
          resolve();
          return;
        }

        const pos = dynamicSeatPositions[visualPos - 1];
        if (!pos) {
          console.warn(`[SPLIT CARD] No seat data for visualPos ${visualPos}`);
          resolve();
          return;
        }

        // === ENABLE IRIS MASK AT START OF SPLIT ===
        // Enable mask immediately when split card separation begins (not after chip animation)
        // This ensures the mask is active before the card slides to the new hand position
        const maskState = scene.seatMaskState.get(seatPosition);
        if (!maskState?.isEnabled) {
          const seatConfigForMask = seatConfigs[visualPos] || { x: 0, y: 0, rot: 0, scale: 100 };
          const seatScaleForMask = (seatSizeScale / 100) * (seatConfigForMask.scale / 100);
          const seatWidth = Math.round(144 * seatScaleForMask);
          const seatHeight = Math.round(168 * seatScaleForMask);
          enableSeatMask(scene, seatPosition, seatWidth, seatHeight);
          console.log(`[SPLIT CARD] Enabled mask for seat ${seatPosition} at START: ${seatWidth}x${seatHeight}`);
        }

        // Find the second card of the original hand (this will become card 0 of new hand)
        const originalCardKey = `${seatPosition}-${originalHandIndex}-1`;
        const cardState = MODULE_CARD_STATES.get(originalCardKey);

        if (!cardState || !cardState.sprite || cardState.sprite.destroyed) {
          console.warn(`[SPLIT CARD] Card not found or destroyed: ${originalCardKey}`);
          resolve();
          return;
        }

        // === FILMSTRIP LOCAL COORDINATES ===
        // Calculate target position for the new hand in LOCAL filmstrip coordinates
        const currentCardConfig = cardConfigRef.current;

        // Get per-seat card config (uses VISUAL position - configs are about screen location, not physical seat)
        // Use ref to get latest config value (avoids closure issues)
        const perSeatConfig = perSeatCardConfigsRef.current?.[visualPos] || { x: 0, y: 0, radiusOffset: 0 };
        const seatCardsYOffset = currentCardConfig.seatCardsY + perSeatConfig.radiusOffset;
        const scaledOverlap = CARD_OVERLAP * (currentCardConfig.scale / 100);

        // Target LOCAL position: centered for 2 cards (will become 2 cards when second card is dealt)
        // Apply per-seat x and y offsets
        const centeredOffset = calculateCardCenteredOffset(0, 2, scaledOverlap);
        const localTargetX = centeredOffset + newHandIndex * FILMSTRIP_CELL_WIDTH + perSeatConfig.x;
        const localTargetY = seatCardsYOffset + perSeatConfig.y;
        const localTargetRotation = 0; // Upright relative to filmstrip

        // Generate new LOCAL randomness for the split card
        const randomness = generateCardRandomnessLocal();

        // Animate the card sliding to new LOCAL position
        const sprite = cardState.sprite;
        const startX = sprite.x;
        const startY = sprite.y;
        const startRotation = sprite.rotation;
        const endX = localTargetX + randomness.offsetX;
        const endY = localTargetY + randomness.offsetY;
        const endRotation = localTargetRotation + randomness.rotation;

        const startTime = performance.now();

        // === SYNCHRONIZED "Hand1" LABEL FLY-IN ===
        // At the START of the card animation, also START the hand label fly-in
        // Card flies left → right (to new hand), Label flies right → left (from behind avatar)
        // ONLY animate if no label exists yet (avoid duplicates on subsequent splits)
        // ONLY show hand labels for the player's seat (not opponents)
        const labelKey = `${seatPosition}-handLabel`;
        const existingLabel = scene.splitHandLabels?.get(labelKey);
        const isPlayerSeat = seatPosition === mySeatPosition;

        if (scene.animationLayer && !existingLabel && isPlayerSeat) {
          // Create "Hand1" label using pre-loaded texture (prevents texture accumulation)
          const handLabelTexture = texturesRef.current.get('handLabel-1');
          let handLabel: Sprite | Text;
          if (handLabelTexture) {
            handLabel = new Sprite(handLabelTexture);
          } else {
            // Fallback to Text if texture not found (shouldn't happen)
            console.warn('[SPLIT LABEL] Pre-loaded texture not found for handLabel-1, falling back to Text');
            handLabel = new Text({
              text: 'Hand1',
              style: {
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 14,
                fontWeight: '700',
                fill: 0x000000,
                align: 'left',
              }
            });
            MODULE_TEXT_CREATED_COUNT++;
          }
          handLabel.anchor.set(0, 0.5); // Left-justified, vertically centered

          // Get visualPos for mask dimensions (matches actual seat rendering)
          const visualPos = getVisualPositionForSeat(seatPosition);
          const seatConfigForMask = seatConfigs[visualPos ?? 0] || { x: 0, y: 0, rot: 0, scale: 100 };
          const seatScaleForMask = (seatSizeScale / 100) * (seatConfigForMask.scale / 100);
          const scaledSeatHeight = Math.round(168 * seatScaleForMask);
          const scaledSeatWidth = Math.round(144 * seatScaleForMask);

          // Get seatPosition config for label positioning (consistent with other code paths)
          const seatConfigForLabel = seatConfigs[seatPosition] || { x: 0, y: 0, rot: 0, scale: 100 };
          const seatScaleForLabel = (seatSizeScale / 100) * (seatConfigForLabel.scale / 100);
          const scaledSeatHeightForLabel = Math.round(168 * seatScaleForLabel);
          const scaledSeatWidthForLabel = Math.round(144 * seatScaleForLabel);

          // Position BELOW seat border, on RIGHT side (near avatar)
          // Combines global config with per-seat config from refs
          const seatRotRad = degreesToRadians(pos.rotation);
          const hlConfig = handLabelConfigRef.current;
          const labelLocalY = scaledSeatHeightForLabel / 2 + 8 + hlConfig.y; // Baseline +8px Y + config Y
          const labelLocalX = scaledSeatWidthForLabel * 0.2 - 38 + hlConfig.x; // Baseline -38px X + config X

          // === WRAPPER-BASED MASKING FOR FLY-IN CLIPPING ===
          // Same pattern as outcome label - wrapper at seat center with mask
          const handLabelWrapper = new Container();
          handLabelWrapper.label = 'handLabelWrapper';
          handLabelWrapper.sortableChildren = true;
          handLabelWrapper.x = pos.x;
          handLabelWrapper.y = pos.y;
          handLabelWrapper.rotation = seatRotRad;

          // Create mask that clips on LEFT side (behind scorebox)
          // Use visualPos-based dimensions for mask (matches actual seat rendering)
          const maskTopY = scaledSeatHeight / 2; // Seat border bottom
          const maskBottomY = scaledSeatHeight / 2 + 60; // Extend below seat
          const maskHeight = maskBottomY - maskTopY;
          const maskLeftX = -scaledSeatWidth / 2 + 40; // Clip 40px from left (behind scorebox)
          const maskRightX = scaledSeatWidth / 2; // Full right side
          const maskWidth = maskRightX - maskLeftX;

          const handLabelMask = new Graphics();
          handLabelMask.roundRect(maskLeftX, maskTopY, maskWidth, maskHeight, 8);
          handLabelMask.fill({ color: 0xffffff });
          handLabelWrapper.addChild(handLabelMask);
          handLabelWrapper.mask = handLabelMask;


          // Start position (80px to the right, behind avatar)
          const flyInOffset = 80;

          // Create background with ONLY bottom-left corner rounded (3 squared corners)
          const handLabelBg = new Graphics();
          const leftPadding = 8;
          const rightPadding = 16;
          const pillWidth = (handLabel.width + leftPadding + rightPadding) * 1.2; // 20% longer
          const pillHeight = handLabel.height + 8;
          const r = 4; // Corner radius for bottom-left only
          const bx = 0; // Left edge at origin
          const by = -pillHeight / 2;
          const bw = pillWidth;
          const bh = pillHeight;

          // Draw pill with only bottom-left corner rounded
          handLabelBg.moveTo(bx, by);                        // Top-left (SQUARED)
          handLabelBg.lineTo(bx + bw, by);                   // Top edge to top-right (SQUARED)
          handLabelBg.lineTo(bx + bw, by + bh);              // Right edge to bottom-right (SQUARED)
          handLabelBg.lineTo(bx + r, by + bh);               // Bottom edge to before bottom-left
          handLabelBg.arcTo(bx, by + bh, bx, by + bh - r, r); // Bottom-left corner (ROUNDED)
          handLabelBg.lineTo(bx, by);                        // Left edge back to top-left
          handLabelBg.closePath();
          handLabelBg.fill({ color: 0xFFFFFF });

          // Position bg in LOCAL coords (starts outside mask on right)
          handLabelBg.x = labelLocalX + flyInOffset;
          handLabelBg.y = labelLocalY;
          handLabelBg.zIndex = 0;

          // Position text with left padding inside the pill (LOCAL coords)
          const textLocalX = labelLocalX + leftPadding;
          handLabel.x = textLocalX + flyInOffset; // Start outside mask
          handLabel.y = labelLocalY;
          handLabel.zIndex = 1;

          // Add bg FIRST, then label (z-index ordering)
          handLabelWrapper.addChild(handLabelBg);
          handLabelWrapper.addChild(handLabel);

          // Add wrapper to outcomeLabelLayer
          scene.outcomeLabelLayer?.addChild(handLabelWrapper);

          // Store wrapper AND local coordinates for fly-out animation
          scene.splitHandLabels = scene.splitHandLabels || new Map();
          scene.splitHandLabels.set(labelKey, {
            label: handLabel,
            bg: handLabelBg,
            wrapper: handLabelWrapper,
            seatRotRad,
            finalLocalX: labelLocalX,
            finalTextLocalX: textLocalX,
            finalLocalY: labelLocalY
          });

          // Animate in LOCAL coordinates - clipping happens automatically at mask edge
          animateSprite(handLabel, { x: textLocalX }, {
            duration: duration,
            easing: easeOutCubic,
            debugName: `splitHandLabel-flyIn-${seatPosition}`,
          });
          animateSprite(handLabelBg, { x: labelLocalX }, {
            duration: duration,
            easing: easeOutCubic,
            debugName: `splitHandLabelBg-flyIn-${seatPosition}`,
          });

          console.log(`[SPLIT] Hand1 label fly-in STARTED (wrapper-masked, synchronized with card separation)`);
        }

        const animateFrame = () => {
          const elapsed = performance.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutCubic(progress);

          sprite.x = startX + (endX - startX) * eased;
          sprite.y = startY + (endY - startY) * eased;
          sprite.rotation = startRotation + (endRotation - startRotation) * eased;

          if (progress < 1) {
            requestAnimationFrame(animateFrame);
          } else {
            // Animation complete - update MODULE_CARD_STATES
            // Remove from original key
            MODULE_CARD_STATES.delete(originalCardKey);

            // Add with new key (card index 0 in new hand) - LOCAL coordinates
            const newCardKey = `${seatPosition}-${newHandIndex}-0`;
            MODULE_CARD_STATES.set(newCardKey, {
              sprite: sprite,
              card: cardState.card,
              baseX: localTargetX,
              baseY: localTargetY,
              baseRotation: localTargetRotation,
              randomness: randomness,
              isDealer: false,
            });

            // Also update the original hand's card - it's now the only card (index 0)
            const firstCardKey = `${seatPosition}-${originalHandIndex}-0`;
            const firstCardState = MODULE_CARD_STATES.get(firstCardKey);
            if (firstCardState) {
              // Recenter the first card for single-card hand (will shift again when 2nd card dealt)
              // LOCAL coordinates
              const firstCenteredOffset = calculateCardCenteredOffset(0, 2, scaledOverlap);
              const firstLocalX = firstCenteredOffset + originalHandIndex * FILMSTRIP_CELL_WIDTH;
              const firstLocalY = seatCardsYOffset;

              firstCardState.baseX = firstLocalX;
              firstCardState.baseY = firstLocalY;

              // Animate first card recentering (LOCAL coordinates)
              animateSprite(firstCardState.sprite, {
                x: firstLocalX + firstCardState.randomness.offsetX,
                y: firstLocalY + firstCardState.randomness.offsetY,
              }, {
                duration: 150,
                debugName: 'splitRecenter',
              });
            }

            // Update dealt card counts
            const originalDealtKey = `${seatPosition}-${originalHandIndex}`;
            const newDealtKey = `${seatPosition}-${newHandIndex}`;
            MODULE_DEALT_CARD_COUNTS.set(originalDealtKey, 1); // Original hand now has 1 card
            MODULE_DEALT_CARD_COUNTS.set(newDealtKey, 1); // New hand has 1 card

            // === SCOREBOX UPDATE: Record score snapshots for both hands ===
            // The zustand state has already been updated by processEvent(HAND_SPLIT)
            // We need to record score snapshots so the scorebox displays update correctly
            const gameState = useGameStateStore.getState();
            const player = gameState.players.get(seatPosition);
            if (player) {
              // Original hand: now has 1 card, update its score
              const originalHand = player.hands[originalHandIndex];
              if (originalHand) {
                recordScoreSnapshot(seatPosition, originalHandIndex, 1, originalHand.score, originalHand.isSoft);
                console.log(`[SPLIT CARD] Recorded score for original hand ${originalHandIndex}: ${originalHand.score} (soft=${originalHand.isSoft})`);
              }
              // New hand: has 1 card (the split card), update its score
              const newHand = player.hands[newHandIndex];
              if (newHand) {
                recordScoreSnapshot(seatPosition, newHandIndex, 1, newHand.score, newHand.isSoft);
                console.log(`[SPLIT CARD] Recorded score for new hand ${newHandIndex}: ${newHand.score} (soft=${newHand.isSoft})`);
              }
            }

            console.log(`[SPLIT CARD] Card separation complete: ${originalCardKey} -> ${newCardKey}`);

            resolve();
          }
        };

        requestAnimationFrame(animateFrame);
      });
    },

    resetSplitHandState: (seatPosition: number) => {
      console.log(`[SPLIT] resetSplitHandState: seat=${seatPosition}`);
      const scene = sceneRef.current;

      // === DIAGNOSTIC: Log scene state at split hand reset ===
      logSceneDiagnostic(scene, `resetSplitHandState(seat=${seatPosition})`);

      // === DISABLE IRIS MASK ===
      // When split hand state is reset (after rake), disable mask since hands are no longer split
      disableSeatMask(scene, seatPosition);
      console.log(`[SPLIT] Disabled mask for seat ${seatPosition} (split hand reset)`);

      // === DEBUG: Log split kitty state BEFORE cleanup ===
      const splitKittyState: Array<{
        key: string;
        visible: boolean;
        destroyed: boolean;
        x: number;
        y: number;
        chipCount: number;
        total: number;
        hasParent: boolean;
      }> = [];
      scene.splitKittyContainers.forEach((container, key) => {
        if (key.startsWith(`${seatPosition}-`)) {
          const data = scene.splitKittyData.get(key);
          splitKittyState.push({
            key,
            visible: container?.visible ?? false,
            destroyed: container?.destroyed ?? true,
            x: container?.x ?? NaN,
            y: container?.y ?? NaN,
            chipCount: data?.denominations.length ?? 0,
            total: data?.total ?? 0,
            hasParent: !!(container?.parent),
          });
        }
      });
      if (splitKittyState.length > 0) {
        console.log(`[SPLIT DEBUG] State BEFORE cleanup:`, JSON.stringify(splitKittyState, null, 2));
      }

      // Remove split hand state
      scene.splitHandState.delete(seatPosition);

      // Clean up split kitty containers and data for this seat
      const keysToDelete: string[] = [];
      scene.splitKittyContainers.forEach((container, key) => {
        if (key.startsWith(`${seatPosition}-`)) {
          keysToDelete.push(key);
          if (!container.destroyed) {
            console.log(`[SPLIT DEBUG] Destroying kitty container: ${key}`);
            scene.animationLayer?.removeChild(container);
            container.destroy({ children: true });
          } else {
            console.log(`[SPLIT DEBUG] Kitty container already destroyed: ${key}`);
          }
        }
      });

      keysToDelete.forEach(key => {
        scene.splitKittyContainers.delete(key);
        scene.splitKittyData.delete(key);
        scene.splitKittyChipSprites.delete(key);
        scene.splitKittyCounterTexts.delete(key);
        scene.splitKittyPills.delete(key);
      });

      // Reset card visibility for all hands
      MODULE_CARD_STATES.forEach((cardState, key) => {
        const parts = key.split('-');
        if (parts[0] === String(seatPosition)) {
          if (cardState.sprite && !cardState.sprite.destroyed) {
            cardState.sprite.alpha = 1;
          }
        }
      });

      console.log(`[SPLIT] Cleaned up ${keysToDelete.length} split kitty containers`);

      // Restore persistent "Hand1" label visibility (was hidden during split rake)
      if (scene.devHandLabelsContainer && !scene.devHandLabelsContainer.destroyed) {
        scene.devHandLabelsContainer.visible = true;
        console.log(`[SPLIT] Restored persistent hand label visibility`);
      }
    },

    setActiveHandIndicator: (seatPosition: number, handIndex: number | null) => {
      // CAROUSEL SYSTEM: Active hand indication is handled by mask-based carousel
      // The carousel slides to show only the active hand, no dimming needed
      console.log(`[SPLIT] setActiveHandIndicator: seat=${seatPosition} hand=${handIndex} (carousel handles visibility)`);

      // Future: Could add subtle visual effect here (glow, outline) but NOT alpha dimming
      // The carousel mask system ensures only the active hand is visible
    },

    // ===========================================
    // DEV CONTROLS: Populate Table
    // ===========================================

    populateTableForDev: (): void => {
      console.log('[DEV] Populating table with all visual elements');
      const scene = sceneRef.current;
      const textures = texturesRef.current;

      if (!scene || !scene.animationLayer || loadingRef.current) {
        console.warn('[DEV] Cannot populate - scene not ready');
        return;
      }

      // Mark as dev populated to track state
      (scene as unknown as { devPopulated?: boolean }).devPopulated = true;

      const DENOMS = [1, 5, 25, 100, 500, 1000, 5000, 10000];
      const CARD_RANKS = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'] as const;
      const CARD_SUITS = ['spades', 'hearts', 'diamonds', 'clubs'] as const;
      type CardRank = typeof CARD_RANKS[number];
      type CardSuit = typeof CARD_SUITS[number];

      // Helper: Create and place a card sprite directly (no animation)
      const placeCard = (seatPosition: number, cardIndex: number, handIndex: number, rank: CardRank, suit: CardSuit) => {
        const visualPos = getVisualPositionForSeat(seatPosition);
        if (!visualPos) return;

        const pos = dynamicSeatPositions[visualPos - 1];
        if (!pos) return;

        const cardTexture = textures.get(`card-${rank}-${suit}`);
        if (!cardTexture) return;

        const currentCardConfig = cardConfigRef.current;
        const scaleFactor = currentCardConfig.scale / 100;
        const cardWidth = CARD_WIDTH * scaleFactor;
        const cardHeight = CARD_HEIGHT * scaleFactor;

        // Create card container
        const cardContainer = new Container();
        cardContainer.label = `dev-card-${seatPosition}-${handIndex}-${cardIndex}`;

        // Shadow
        const shadow = new Graphics();
        shadow.roundRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth, cardHeight, 8 * scaleFactor);
        shadow.fill({ color: 0x000000, alpha: 0.3 });
        cardContainer.addChild(shadow);

        // White background
        const bg = new Graphics();
        bg.roundRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 6 * scaleFactor);
        bg.fill({ color: 0xfefefe });
        cardContainer.addChild(bg);

        // Card face sprite
        const cardSprite = new Sprite(cardTexture);
        cardSprite.anchor.set(0.5);
        cardSprite.width = cardWidth;
        cardSprite.height = cardHeight;
        cardContainer.addChild(cardSprite);

        // Position at seat - straightened (no splay)
        const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const rotRad = degreesToRadians(pos.rotation + (seatConfig.rot || 0));
        const cardLocalY = -60 - cardIndex * 20; // Stack cards vertically
        const finalPos = applyRadialOffset(pos.x + (seatConfig.x || 0), pos.y + (seatConfig.y || 0), cardLocalY, rotRad);

        cardContainer.x = finalPos.x;
        cardContainer.y = finalPos.y;
        cardContainer.rotation = rotRad;
        cardContainer.zIndex = 100 + cardIndex;

        scene.cardsLayer?.addChild(cardContainer);

        // Track in MODULE_CARD_STATES for cleanup
        const key = `dev-${seatPosition}-${handIndex}-${cardIndex}`;
        MODULE_CARD_STATES.set(key, {
          sprite: cardContainer,
          randomness: { offsetX: 0, offsetY: 0, rotation: 0 },
          baseRotation: rotRad,
          baseX: cardContainer.x,
          baseY: cardContainer.y,
          isDealer: false,
          card: { rank, suit, faceUp: true },
        });
      };

      // Helper: Create kitty with chips at a seat
      const createDevKitty = (seatPosition: number, handIndex: number = 0) => {
        const visualPos = getVisualPositionForSeat(seatPosition);
        if (!visualPos) return;

        const pos = dynamicSeatPositions[visualPos - 1];
        if (!pos) return;

        // Check if this is an opponent seat
        const isOpponent = seatPosition !== mySeatPosition;

        // Use seatPosition for betChipConfigs, but VISUAL position for opponentKittyConfigs (DEV CONTROLS alignment)
        const betChipConfig = betChipConfigs[seatPosition] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
        // Get per-seat opponent kitty config (only applies to opponent seats) - use visual position
        const perSeatOpponentConfig = isOpponent
          ? (opponentKittyConfigs?.[visualPos] ?? { radiusOffset: 0, x: 0, y: 0, scale: 100 })
          : { radiusOffset: 0, x: 0, y: 0, scale: 100 };
        // Use opponent kitty config for opponents, regular kittyConfig for player
        const effectiveConfig = isOpponent ? (opponentKittyConfig ?? kittyConfig) : kittyConfig;

        const seatConfig = seatConfigs[visualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const rotRad = degreesToRadians(pos.rotation + (seatConfig.rot || 0));

        // Calculate kitty position (apply both betChipConfig and per-seat opponent offsets)
        const kittyLocalX = betChipConfig.x + (effectiveConfig?.x ?? 0) + perSeatOpponentConfig.x;
        // Match the formula used in the repositioning useEffect:
        // Only radial offsets go into applyRadialOffset, y offsets are added separately
        const baseLocalOffsetY = KITTY_BASE_RADIAL_OFFSET + betChipConfig.radiusOffset + perSeatOpponentConfig.radiusOffset;

        // Apply horizontal offset for split hands (player's right direction)
        const splitOffset = handIndex * FILMSTRIP_CELL_WIDTH; // Match filmstrip spacing
        const horizontalOffset = handIndex === 0 ? 0 : splitOffset;

        // Use pos directly (no seatConfig adjustment) to match useEffect formula
        const basePos = applyRadialOffset(pos.x, pos.y, baseLocalOffsetY, rotRad);
        // Apply horizontal offset in seat rotation direction (player's right)
        const spreadAngle = rotRad;
        // Add x/y offsets separately (matching useEffect formula)
        const finalX = basePos.x + kittyLocalX + Math.cos(spreadAngle) * horizontalOffset;
        const finalY = basePos.y + betChipConfig.y + (effectiveConfig?.y ?? 0) + perSeatOpponentConfig.y + Math.sin(spreadAngle) * horizontalOffset;

        console.log(`[createDevKitty] seat=${seatPosition} visualPos=${visualPos}`,
          `seatPos=(${pos.x.toFixed(0)}, ${pos.y.toFixed(0)}, rot=${pos.rotation})`,
          `baseLocalOffsetY=${baseLocalOffsetY.toFixed(0)}`,
          `FINAL=(${finalX.toFixed(0)}, ${finalY.toFixed(0)})`
        );

        // Create kitty display
        const kittyDisplay = createKittyDisplay(textures, chipCounterConfig);
        kittyDisplay.container.x = finalX;
        kittyDisplay.container.y = finalY;
        kittyDisplay.container.rotation = rotRad;
        kittyDisplay.container.zIndex = 50;
        kittyDisplay.container.label = `dev-kitty-${seatPosition}-${handIndex}`;

        // Set seatPosition for config lookup (betChipConfigs is keyed by seat, not visual position)
        // Also set isOpponentKitty for proper config selection
        (kittyDisplay.container as unknown as { seatPosition: number; isOpponentKitty: boolean; isKittyElement: boolean }).seatPosition = seatPosition;
        (kittyDisplay.container as unknown as { seatPosition: number; isOpponentKitty: boolean; isKittyElement: boolean }).isOpponentKitty = isOpponent;
        (kittyDisplay.container as unknown as { seatPosition: number; isOpponentKitty: boolean; isKittyElement: boolean }).isKittyElement = true;

        scene.animationLayer?.addChild(kittyDisplay.container);

        // Verify position after adding to scene
        console.log(`[createDevKitty] VERIFY seat=${seatPosition}: container.x=${kittyDisplay.container.x.toFixed(0)}, container.y=${kittyDisplay.container.y.toFixed(0)}`);

        // Add one of each chip denomination
        const kittyChipSprites = new Map<number, KittyChipData>();
        DENOMS.forEach((denom, idx) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) return;

          const chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          addChipToKitty(kittyDisplay.chipContainer, kittyDisplay.borderContainer, denom, chipSprite, kittyChipSprites, chipBorderConfig);
        });

        // Update counter
        const total = DENOMS.reduce((sum, d) => sum + d, 0);
        updateKittyLayout(
          kittyDisplay.pill,
          kittyDisplay.counterText,
          DENOMS.length,
          total,
          { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
          isOpponent
        );

        // Track in scene for cleanup
        // For main hands (handIndex=0), use visualPos as key so useEffect can find seat data
        // For split hands, use offset key to avoid collision
        const kittyKey = handIndex === 0 ? visualPos : (100 + seatPosition * 10 + handIndex);
        scene.kittyContainers.set(kittyKey, kittyDisplay.container);
        scene.kittyChipSprites.set(kittyKey, kittyChipSprites);
        scene.kittyData.set(kittyKey, { denominations: [...DENOMS], total });
        scene.kittyPills.set(kittyKey, kittyDisplay.pill);
        scene.kittyCounterTexts.set(kittyKey, kittyDisplay.counterText);
        scene.kittyChipsWrappers.set(kittyKey, kittyDisplay.chipsWrapper);
      };

      // 1. ADD CARDS TO ALL 7 SEATS (2 cards each)
      for (let seat = 1; seat <= 7; seat++) {
        const rank1 = CARD_RANKS[seat % CARD_RANKS.length];
        const rank2 = CARD_RANKS[(seat + 1) % CARD_RANKS.length];
        const suit = CARD_SUITS[seat % CARD_SUITS.length];
        placeCard(seat, 0, 0, rank1, suit);
        placeCard(seat, 1, 0, rank2, suit);
      }

      // 2. ADD MAIN KITTIES TO ALL 7 SEATS
      for (let seat = 1; seat <= 7; seat++) {
        createDevKitty(seat, 0);
      }

      // 3. ADD SPLIT HANDS (4 kitties) TO SEATS 1, 3, 5
      for (const seat of [1, 3, 5]) {
        for (let handIdx = 1; handIdx <= 3; handIdx++) {
          createDevKitty(seat, handIdx);
          // Add cards to each split hand
          const rank1 = CARD_RANKS[(seat + handIdx) % CARD_RANKS.length];
          const rank2 = CARD_RANKS[(seat + handIdx + 1) % CARD_RANKS.length];
          const suit = CARD_SUITS[(seat + handIdx) % CARD_SUITS.length];
          placeCard(seat, 0, handIdx, rank1, suit);
          placeCard(seat, 1, handIdx, rank2, suit);
        }
      }

      // 4. ADD SIDEBET ICONS (3 per seat)
      const SIDEBET_TYPES = ['21+3', 'perfect-pairs', 'bet-behind'];
      for (let seat = 1; seat <= 7; seat++) {
        SIDEBET_TYPES.forEach((type, idx) => {
          // Call addSidebetToSeat directly (it's in the same scope)
          // Get or create sidebet container for this seat
          const visualPos = getVisualPositionForSeat(seat);
          const seatData = dynamicSeatPositions[visualPos - 1];
          if (!seatData || !scene.sidebetLayer) return;

          const isOpponentSeat = seat !== mySeatPosition;
          let seatContainer = scene.seatSidebetContainers.get(seat);
          if (!seatContainer) {
            seatContainer = new Container();
            seatContainer.sortableChildren = true;
            seatContainer.eventMode = 'passive';
            seatContainer.label = `dev-sidebet-container-${seat}`;

            const config = isOpponentSeat ? opponentSidebetConfigRef.current : sidebetConfigRef.current;
            // Per-seat offset - use VISUAL position for DEV CONTROLS alignment
            const perSeatOffset = sidebetIconConfigs?.[visualPos] || { radiusOffset: 0, x: 0, y: 0 };
            const baseLocalOffsetY = config.baseRadiusOffset + perSeatOffset.radiusOffset;
            const rotRad = degreesToRadians(seatData.rotation);
            const basePos = applyRadialOffset(seatData.x, seatData.y, baseLocalOffsetY, rotRad);

            seatContainer.x = basePos.x + perSeatOffset.x;
            seatContainer.y = basePos.y + perSeatOffset.y;
            seatContainer.rotation = rotRad;

            scene.sidebetLayer.addChild(seatContainer);
            scene.seatSidebetContainers.set(seat, seatContainer);
          }

          // Get texture for sidebet
          const textureIndex = SIDEBET_TYPE_TO_INDEX[type] || 1;
          const texture = textures.get(`sidebet-${textureIndex}`);
          if (!texture) return;

          const config = isOpponentSeat ? opponentSidebetConfigRef.current : sidebetConfigRef.current;
          const iconSize = config.iconSize;
          const slotPos = config.slotPositions[idx] || { localX: 0, localY: 0 };

          // Create sprite
          const sprite = new Sprite(texture);
          sprite.width = iconSize;
          sprite.height = iconSize;
          sprite.anchor.set(0.5);
          sprite.x = slotPos.localX;
          sprite.y = slotPos.localY;
          sprite.zIndex = 10 + idx;
          sprite.label = `dev-sidebet-${seat}-${idx}`;

          seatContainer.addChild(sprite);
          scene.sidebetSprites.set(`${seat}-${idx}`, sprite);
        });
      }

      // 5. ADD INSURANCE KITTIES TO ALL SEATS
      for (let insSeat = 1; insSeat <= 7; insSeat++) {
        const insVisualPos = getVisualPositionForSeat(insSeat);
        if (!insVisualPos) continue;

        const pos = dynamicSeatPositions[insVisualPos - 1];
        if (!pos) continue;

        // Use visual position for config lookup (DEV CONTROLS alignment)
        const insChipConfig = insuranceChipConfigs[insVisualPos] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
        const seatConfig = seatConfigs[insVisualPos] || REGISTRY_DEFAULT_SEAT_CONFIG;
        const betConfig = betChipConfigs[insSeat] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
        const rotRad = degreesToRadians(pos.rotation + (seatConfig.rot || 0));

        // Insurance position: same radial level as main bet + insurance offset
        const insLocalY = KITTY_BASE_RADIAL_OFFSET + betConfig.radiusOffset + insChipConfig.radiusOffset;
        const basePos = applyRadialOffset(pos.x, pos.y, insLocalY, rotRad);
        const finalX = basePos.x + betConfig.x + insChipConfig.x;
        const finalY = basePos.y + betConfig.y + insChipConfig.y;

        const insKittyDisplay = createKittyDisplay(textures, chipCounterConfig);
        insKittyDisplay.container.x = finalX;
        insKittyDisplay.container.y = finalY;
        insKittyDisplay.container.rotation = rotRad;
        insKittyDisplay.container.zIndex = 60;
        insKittyDisplay.container.label = `dev-insurance-kitty-${insSeat}`;

        scene.animationLayer?.addChild(insKittyDisplay.container);

        // Add chips to insurance kitty
        const insChipSpriteMap = new Map<number, KittyChipData>();
        const insChipSpriteArray: Sprite[] = [];
        DENOMS.forEach((denom) => {
          const chipTexture = textures.get(`chip-${denom}`);
          if (!chipTexture) return;
          const chipSprite = new Sprite(chipTexture);
          chipSprite.anchor.set(0.5);
          addChipToKitty(insKittyDisplay.chipContainer, insKittyDisplay.borderContainer, denom, chipSprite, insChipSpriteMap, chipBorderConfig);
          insChipSpriteArray.push(chipSprite);
        });

        const insTotal = DENOMS.reduce((sum, d) => sum + d, 0);
        updateKittyLayout(
          insKittyDisplay.pill,
          insKittyDisplay.counterText,
          DENOMS.length,
          insTotal,
          { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 },
          true
        );

        scene.insuranceKittyContainers.set(insSeat, insKittyDisplay.container);
        scene.insuranceKittyChipSprites.set(insSeat, insChipSpriteArray);
      }

      // Final verification - log all kitty positions
      console.log('[DEV] FINAL KITTY POSITIONS:');
      scene.kittyContainers.forEach((container, key) => {
        console.log(`  key=${key}: x=${container.x.toFixed(0)}, y=${container.y.toFixed(0)}`);
      });

      console.log('[DEV] Table populated successfully');
    },

    clearDevPopulation: (): void => {
      console.log('[DEV] Clearing dev population');
      const scene = sceneRef.current;

      if (!scene) {
        console.warn('[DEV] Cannot clear - scene not ready');
        return;
      }

      // Check if actually populated
      if (!(scene as unknown as { devPopulated?: boolean }).devPopulated) {
        console.log('[DEV] Table was not dev-populated, nothing to clear');
        return;
      }

      // Clear dev cards
      MODULE_CARD_STATES.forEach((state, key) => {
        if (key.startsWith('dev-')) {
          if (state.sprite && !state.sprite.destroyed) {
            scene.cardsLayer?.removeChild(state.sprite);
            state.sprite.destroy({ children: true });
          }
          MODULE_CARD_STATES.delete(key);
        }
      });

      // Clear dev kitties (identified by label)
      scene.kittyContainers.forEach((container, key) => {
        if (container.label?.startsWith('dev-kitty-')) {
          scene.animationLayer?.removeChild(container);
          container.destroy({ children: true });
          scene.kittyContainers.delete(key);
          scene.kittyChipSprites.delete(key);
          scene.kittyData.delete(key);
          scene.kittyPills.delete(key);
          scene.kittyCounterTexts.delete(key);
          scene.kittyChipsWrappers.delete(key);
        }
      });

      // Clear dev sidebet containers and sprites
      scene.seatSidebetContainers.forEach((container, seat) => {
        if (container.label?.startsWith('dev-sidebet-container-')) {
          scene.sidebetLayer?.removeChild(container);
          container.destroy({ children: true });
          scene.seatSidebetContainers.delete(seat);
        }
      });

      scene.sidebetSprites.forEach((sprite, key) => {
        if (sprite.label?.startsWith('dev-sidebet-')) {
          sprite.destroy();
          scene.sidebetSprites.delete(key);
        }
      });

      // Clear dev insurance kitty
      scene.insuranceKittyContainers.forEach((container, seat) => {
        if (container.label?.startsWith('dev-insurance-kitty-')) {
          scene.animationLayer?.removeChild(container);
          container.destroy({ children: true });
          scene.insuranceKittyContainers.delete(seat);
          scene.insuranceKittyChipSprites.delete(seat);
        }
      });

      (scene as unknown as { devPopulated?: boolean }).devPopulated = false;
      console.log('[DEV] Table cleared successfully');
    },

    // ===========================================
    // SCORECARD (Round Results Display)
    // ===========================================

    /**
     * Flip the scorecard to show results (front) or hide (back).
     * Uses scaleX animation similar to dealer hole card flip.
     * @param toFront - true to show results, false to show card back
     * @param duration - total flip duration in ms (default 400)
     */
    flipScorecard: (toFront: boolean, duration: number = 400): Promise<void> => {
      return new Promise((resolve) => {
        const scene = sceneRef.current;
        const back = scene.scorecardBack;
        const front = scene.scorecardFront;

        if (!back || !front || loadingRef.current) {
          console.warn('[SCORECARD] Cannot flip - scorecard not ready', { back: !!back, front: !!front, loading: loadingRef.current });
          resolve();
          return;
        }

        // Skip if already in target state
        if (toFront === scene.scorecardIsShowingFront) {
          console.log(`[SCORECARD] Skip flip - already in target state (toFront=${toFront}, isShowingFront=${scene.scorecardIsShowingFront})`);
          resolve();
          return;
        }

        console.log(`[SCORECARD] Flipping to ${toFront ? 'show results' : 'card back'} (current state: isShowingFront=${scene.scorecardIsShowingFront})`);

        const startTime = performance.now();
        const halfDuration = duration / 2;
        // Sound latch: play the card-flip sound once at the midpoint, same pattern as the hole-card flip
        let flipSoundPlayed = false;

        // Easing functions
        const easeIn = (t: number) => t * t;
        const easeOut = (t: number) => 1 - (1 - t) * (1 - t);

        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const totalProgress = Math.min(elapsed / duration, 1);

          // Sound: fire the rake sound at the flip midpoint (card edge-on, faces swap)
          if (elapsed >= halfDuration && !flipSoundPlayed) {
            flipSoundPlayed = true;
            playTableSound('rake');
          }

          if (toFront) {
            // First half: shrink back (1 → 0)
            if (elapsed < halfDuration) {
              const halfProgress = elapsed / halfDuration;
              back.scale.x = 1 - easeIn(halfProgress);
              front.scale.x = 0;
            } else {
              // Second half: grow front (0 → 1)
              const halfProgress = (elapsed - halfDuration) / halfDuration;
              back.scale.x = 0;
              front.scale.x = easeOut(Math.min(halfProgress, 1));
            }
          } else {
            // First half: shrink front (1 → 0)
            if (elapsed < halfDuration) {
              const halfProgress = elapsed / halfDuration;
              front.scale.x = 1 - easeIn(halfProgress);
              back.scale.x = 0;
            } else {
              // Second half: grow back (0 → 1)
              const halfProgress = (elapsed - halfDuration) / halfDuration;
              front.scale.x = 0;
              back.scale.x = easeOut(Math.min(halfProgress, 1));
            }
          }

          if (totalProgress >= 1) {
            // Ensure final state
            if (toFront) {
              back.scale.x = 0;
              front.scale.x = 1;
            } else {
              front.scale.x = 0;
              back.scale.x = 1;
            }
            scene.scorecardIsShowingFront = toFront;
            resolve();
            return;
          }

          requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
      });
    },

    /**
     * Update the scorecard content with round results.
     * Call this before flipping to front.
     * @param mainBet - Main bet amount
     * @param mainOutcome - Main bet outcome ('win', 'lose', 'push', 'blackjack', 'bust', 'surrender')
     * @param sidebets - Map of sidebet results { sidebetType -> { amount, outcome, payout } }
     */
    updateScorecardContent: (
      hands: ScorecardHandResult[],
      sidebets: ScorecardSidebetResult[],
      insurance: ScorecardInsuranceResult | undefined,
      totalNetResult: number
    ): void => {
      const scene = sceneRef.current;
      const labelLines = scene.scorecardResultLines;
      const valueLines = scene.scorecardValueLines;
      const totalText = scene.scorecardTotalText;

      if (!labelLines.length || !valueLines.length || !totalText) {
        console.warn('[SCORECARD] Cannot update content - text elements not ready');
        return;
      }

      // Colors for results
      const WIN_COLOR = 0x22C55E;  // green-500
      const LOSS_COLOR = 0xEF4444; // red-500
      const PUSH_COLOR = 0x9CA3AF; // gray-400

      // Format currency with comma separators (no currency symbol)
      const formatAmount = (amt: number): string => {
        const sign = amt >= 0 ? '+' : '-';
        return `${sign}${Math.abs(amt).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
      };

      // Get color based on net result
      const getColor = (netResult: number): number => {
        if (netResult > 0) return WIN_COLOR;
        if (netResult < 0) return LOSS_COLOR;
        return PUSH_COLOR;
      };

      // Build list of all active lines (for vertical centering)
      type LineData = { label: string; value: string; color: number };
      const activeLines: LineData[] = [];

      // Add hand lines
      for (const hand of hands) {
        // When insurance is consolidated into main bet, show "Main Bet + Ins."
        let label: string;
        if (hand.handIndex === 0) {
          label = hand.includesInsurance ? 'Main Bet + Ins.' : 'Main Bet';
        } else {
          label = `Split ${hand.handIndex + 1}`;
        }
        activeLines.push({
          label,
          value: formatAmount(hand.netResult),
          color: getColor(hand.netResult),
        });
      }

      // Insurance is now consolidated into the first hand, so this is a legacy fallback
      if (insurance) {
        activeLines.push({
          label: 'Insurance',
          value: formatAmount(insurance.netResult),
          color: getColor(insurance.netResult),
        });
      }

      // Add sidebet lines
      for (const sidebet of sidebets) {
        activeLines.push({
          label: sidebet.displayName,
          value: formatAmount(sidebet.netResult),
          color: getColor(sidebet.netResult),
        });
      }

      // Calculate vertical centering
      // The bet area is between the two dividers, centered at y=0
      // Line spacing is 10 * scaleFactor
      const scaleFactor = scene.scorecardScaleFactor;
      const lineSpacing = 10 * scaleFactor;
      const totalActiveLines = Math.min(activeLines.length, labelLines.length);

      // Center the lines: first line starts at -(totalLines-1) * spacing / 2
      const startY = -((totalActiveLines - 1) * lineSpacing) / 2;

      // Update visible lines with centered positions
      for (let i = 0; i < totalActiveLines; i++) {
        const labelLine = labelLines[i];
        const valueLine = valueLines[i];
        const data = activeLines[i];

        if (labelLine && valueLine && data) {
          labelLine.text = data.label;
          labelLine.style.fill = 0xD1D5DB;
          labelLine.y = startY + i * lineSpacing;
          labelLine.visible = true;

          valueLine.text = data.value;
          valueLine.style.fill = data.color;
          valueLine.y = startY + i * lineSpacing;
          valueLine.visible = true;
        }
      }

      // Hide unused lines
      for (let i = totalActiveLines; i < labelLines.length; i++) {
        labelLines[i].visible = false;
        valueLines[i].visible = false;
      }

      // Update total (server-provided, no calculation needed)
      totalText.text = `TOTAL: ${formatAmount(totalNetResult)}`;
      totalText.style.fill = totalNetResult >= 0 ? (totalNetResult > 0 ? WIN_COLOR : 0xFFD700) : LOSS_COLOR;

      console.log(`[SCORECARD] Updated content (server-driven): hands=${hands.length}, insurance=${!!insurance}, sidebets=${sidebets.length}, total=${formatAmount(totalNetResult)}`);
    },

    /**
     * Reset the scorecard to card-back state (no animation).
     * Call this when starting a new round.
     */
    resetScorecard: (): void => {
      const scene = sceneRef.current;
      const back = scene.scorecardBack;
      const front = scene.scorecardFront;

      if (!back || !front) return;

      // Immediately reset to card-back state
      back.scale.x = 1;
      front.scale.x = 0;
      scene.scorecardIsShowingFront = false;

      // Clear label lines
      scene.scorecardResultLines.forEach((line) => {
        line.text = '';
        line.visible = false;
      });

      // Clear value lines
      scene.scorecardValueLines.forEach((line) => {
        line.text = '';
        line.visible = false;
      });

      if (scene.scorecardTotalText) {
        scene.scorecardTotalText.text = 'TOTAL: BB 0';
      }

      console.log('[SCORECARD] Reset to card-back state');
    },

    retryFailedAvatars: async (): Promise<void> => {
      // Get failed avatars that haven't exceeded retry cap
      const failedUrls = Array.from(MODULE_FAILED_AVATARS.entries())
        .filter(([, attempts]) => attempts < AVATAR_RETRY_CAP);

      if (failedUrls.length === 0) return;

      const textures = texturesRef.current;

      for (const [url, attempts] of failedUrls) {
        try {
          // Cache-bust: unload the failed cache entry before retrying
          await Assets.unload(url);

          // Retry the load
          const texture = await Assets.load({
            src: url,
            loadParser: 'loadTextures',
            data: { crossOrigin: 'anonymous' },
          });

          // Success: update texture map and remove from failed tracking
          const key = `avatar-${url}`;
          const oldTexture = textures.get(key);
          textures.set(key, texture);
          MODULE_FAILED_AVATARS.delete(url);

          // Clean up old placeholder texture reference (prevents GPU leak)
          if (oldTexture && oldTexture !== texture) {
            try {
              oldTexture.destroy(true);
            } catch {
              // Texture may already be destroyed
            }
          }

          console.log(`[Avatar] Retry success (attempt ${attempts + 1}): ${url.substring(0, 50)}...`);

          // Trigger re-render to swap in the new avatar
          setAvatarTexturesVersion(v => v + 1);
        } catch {
          // Increment retry count
          const newAttempts = attempts + 1;
          MODULE_FAILED_AVATARS.set(url, newAttempts);

          if (newAttempts < AVATAR_RETRY_CAP) {
            console.log(`[Avatar] Retry failed (${newAttempts}/${AVATAR_RETRY_CAP}): ${url.substring(0, 50)}...`);
          } else {
            console.log(`[Avatar] Retry cap reached, giving up: ${url.substring(0, 50)}...`);
          }
        }
      }
    },

    destroy: () => {
      console.log('[PixiGameTableV1] destroy() called - immediately destroying PixiJS app');
      // Signal to any in-progress init to abort
      destroyRequestedRef.current = true;
      // Reset init flag so next mount can reinitialize
      initStartedRef.current = false;
      // Clear module-level state to prevent stale sprite references
      clearModuleState();
      if (appRef.current) {
        const canvas = appRef.current.canvas;
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
        // Explicitly remove canvas from DOM
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      }
    },
  }), [loading, getVisualPositionForSeat, dynamicSeatPositions, cardConfig, kittyConfig, opponentKittyConfig, opponentKittyConfigs, chipCounterConfig, chipBorderConfig, betChipConfigs, insuranceChipConfigs, sidebetIconConfigs, trayConfig, trayChipsOffset, chipRailConfig, chipXOffsets, chipYOffsets, seatConfigs, seatSizeScale, outcomeLabelConfig, scoreboxConfig, dealerButtonConfig, dealerButtonSeat, mySeatPosition, perSeatCardConfigs]);

  // ===========================================
  // INSURANCE KITTY POSITION UPDATE (DEV CONTROLS)
  // ===========================================
  // Update persisted insurance kitty container positions when insuranceChipConfigs changes
  useEffect(() => {
    if (loading) return;
    const scene = sceneRef.current;

    // For each seat with insurance kitty container, update position
    scene.insuranceKittyContainers.forEach((container, seatPosition) => {
      if (container.destroyed) return;

      // Get visual position for this seat
      const visualPos = getVisualPositionForSeat(seatPosition);
      if (visualPos === null) return;

      // Get seat position data
      const seatData = dynamicSeatPositions[visualPos - 1];
      if (!seatData) return;

      // Calculate new insurance kitty position using the same formula as populateTableForDev
      // Direct positioning without centering - game handles centering during gameplay
      // Use visualPos for config lookup (DEV CONTROLS alignment)
      const insConfig = insuranceChipConfigs[visualPos] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
      const betConfig = betChipConfigs[seatPosition] || { radiusOffset: 0, x: 0, y: 0, scale: 100 };
      const rotRad = degreesToRadians(seatData.rotation);

      // Use same radial offset as main bet (KITTY_BASE_RADIAL_OFFSET), plus betConfig and insConfig radiusOffsets
      const radialOffset = KITTY_BASE_RADIAL_OFFSET + betConfig.radiusOffset + insConfig.radiusOffset;

      // Base position - direct, no centering offset
      const basePos = applyRadialOffset(seatData.x, seatData.y, radialOffset, rotRad);
      const finalX = basePos.x + betConfig.x + insConfig.x;
      const finalY = basePos.y + betConfig.y + insConfig.y;

      // Update container position and rotation - direct positioning
      container.x = finalX;
      container.y = finalY;
      container.rotation = rotRad;

      // Apply scale from config to chip sizes
      const scale = insConfig.scale / 100;
      const chipSize = CHIP_SIZES.kittyMedium * scale;

      // Update each chip sprite size (positions are relative to container)
      const sprites = scene.insuranceKittyChipSprites.get(seatPosition) || [];
      sprites.forEach((sprite, index) => {
        if (sprite.destroyed) return;
        sprite.width = chipSize;
        sprite.height = chipSize;
        // Chips are positioned at (0, -index*4) relative to container origin
        sprite.x = 0;
        sprite.y = -index * 4;
      });

      // Update counter position
      const kittyPill = scene.insuranceKittyPills.get(seatPosition);
      const kittyCounterText = scene.insuranceKittyCounterTexts.get(seatPosition);
      const kittyData = scene.insuranceKittyData.get(seatPosition);
      if (kittyPill && kittyCounterText && kittyData) {
        const counterOffset = { x: chipCounterConfig?.x ?? 0, y: chipCounterConfig?.y ?? 0 };
        updateKittyLayout(kittyPill, kittyCounterText, sprites.length, kittyData.total, counterOffset);
      }
    });
  }, [loading, insuranceChipConfigs, betChipConfigs, getVisualPositionForSeat, dynamicSeatPositions, kittyConfig, chipCounterConfig]);

  // Dynamic sizing mode: canvas fills available space, no CSS transform needed
  const isDynamicSizing = !!(availableWidth && availableHeight);

  return (
    <div
      ref={wrapperRef}
      className="absolute inset-0 overflow-hidden flex justify-center items-start"
      style={{ backgroundColor: '#1a4731' }} // Green felt to match canvas
    >
      {/* PixiJS canvas container */}
      <div
        ref={containerRef}
        style={isDynamicSizing ? {
          // Dynamic sizing: canvas already sized to fill space, no transform needed
          visibility: loading ? 'hidden' : 'visible',
          backgroundColor: '#1a4731',
        } : {
          // Fixed sizing: use CSS transform to scale the canvas
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          visibility: loading ? 'hidden' : 'visible',
          backgroundColor: '#1a4731',
        }}
      />

      {/* Loading overlay */}
      {loading && (
        <>
          <LoadingVideo />
          {error && (
            <div className="absolute inset-0 flex items-center justify-center z-[10000]">
              <div className="text-red-500 text-sm bg-black/80 px-4 py-2 rounded">Error: {error}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default PixiGameTableV1;
